import React from 'react';
import './App.css';
import clubLogo from './assets/blonianka-logo.png';
import footDeskLogo from './assets/footdesk-logo.svg';

type Role = 'ADMIN' | 'COORDINATOR' | 'COACH' | 'PARENT' | 'PLAYER';

type Me = {
  id: string;
  role: Role;
  roles: Role[];
  status: 'ACTIVE' | 'PENDING' | 'REJECTED';
  rodo_accepted_latest: boolean;
  rodo_accepted_version: string | null;
  rodo_accepted_at: string | null;
  rodo_latest_version: string | null;
};

function normalizeMe(value: any): Me {
  const role: Role =
    value?.role === 'ADMIN' || value?.role === 'COORDINATOR' || value?.role === 'COACH' || value?.role === 'PARENT' || value?.role === 'PLAYER'
      ? value.role
      : 'PARENT';
  const rolesRaw: any[] = Array.isArray(value?.roles) ? value.roles : [];
  const roles = rolesRaw
    .filter((r) => r === 'ADMIN' || r === 'COORDINATOR' || r === 'COACH' || r === 'PARENT' || r === 'PLAYER')
    .map((r) => r as Role);
  const status: Me['status'] =
    value?.status === 'ACTIVE' || value?.status === 'PENDING' || value?.status === 'REJECTED' ? value.status : 'PENDING';
  const rodoAcceptedRaw = value?.rodo_accepted_latest;
  const rodo_accepted_latest = typeof rodoAcceptedRaw === 'boolean' ? rodoAcceptedRaw : false;
  const rodo_accepted_version = value?.rodo_accepted_version ? String(value.rodo_accepted_version) : null;
  const rodo_accepted_at = value?.rodo_accepted_at ? String(value.rodo_accepted_at) : null;
  const rodo_latest_version = value?.rodo_latest_version ? String(value.rodo_latest_version) : null;
  return {
    id: String(value?.id ?? ''),
    role,
    roles: roles.length > 0 ? roles : [role],
    status,
    rodo_accepted_latest,
    rodo_accepted_version,
    rodo_accepted_at,
    rodo_latest_version,
  };
}

type RodoSection = {
  title: string;
  body: string;
};

type RodoPolicy = {
  version: string;
  published_at: string;
  sections: RodoSection[];
};

type RodoAcceptance = {
  latest_version: string;
  accepted_version: string | null;
  accepted_at: string | null;
  last_seen_version: string | null;
  accepted_latest: boolean;
  policy: RodoPolicy;
};

type UserProfile = {
  email: string;
  display_name: string | null;
  nickname: string | null;
  rodo_accepted_latest: boolean;
  rodo_accepted_version: string | null;
  rodo_accepted_at: string | null;
  rodo_latest_version: string | null;
};

type EventType = 'TRAINING' | 'MATCH';
type EventStatus = 'ACTIVE' | 'CANCELLED';
type AttendanceStatus = 'IN' | 'OUT' | 'LATE';
type MatchRsvp = 'PENDING' | 'ACCEPTED' | 'DECLINED';
type MatchKind = 'LEAGUE' | 'FRIENDLY' | 'TOURNAMENT' | 'OTHER';

type TrainingCoachItem = {
  id: string;
  email: string;
  is_absent: boolean;
};

type EventListItem = {
  id: string;
  type: EventType;
  status: EventStatus;
  title: string;
  group_id: string;
  is_individual?: boolean | null;
  is_individual_coach_event?: boolean | null;
  target_child_id?: string | null;
  target_child_label?: string | null;
  start_at: string;
  end_at?: string | null;
  location: string;
  opponent_name?: string | null;
  opponent_logo_url?: string | null;
  is_tbd?: boolean | null;
  match_kind?: MatchKind | null;
  match_meet_at?: string | null;
  home_score?: number | null;
  away_score?: number | null;
  match_finalized?: boolean | null;
  external_source?: string | null;
  can_take_action: boolean;
  attendance_status?: AttendanceStatus | null;
  match_rsvp?: MatchRsvp | null;
  match_responded_late?: boolean | null;
  match_deadline_at?: string | null;
  can_match_rsvp?: boolean | null;
  called_up?: boolean | null;
  called_up_count?: number | null;
  callup_confirmed_count?: number | null;
  callup_total_count?: number | null;
  transport_offers_count?: number | null;
  transport_requests_count?: number | null;
};

type EventDetails = {
  id: string;
  type: EventType;
  status: EventStatus;
  title: string;
  group_id: string;
  is_individual?: boolean | null;
  is_individual_coach_event?: boolean | null;
  target_child_id?: string | null;
  target_child_label?: string | null;
  series_id?: string | null;
  opponent_name?: string | null;
  opponent_logo_url?: string | null;
  is_tbd?: boolean | null;
  match_kind?: MatchKind | null;
  match_meet_at?: string | null;
  home_score?: number | null;
  away_score?: number | null;
  match_finalized?: boolean | null;
  external_source?: string | null;
  start_at: string;
  end_at?: string | null;
  location: string;
  description?: string | null;
  can_take_action: boolean;
  attendance_status?: AttendanceStatus | null;
  match_rsvp?: MatchRsvp | null;
  match_responded_late?: boolean | null;
  match_deadline_at?: string | null;
  can_match_rsvp?: boolean | null;
  called_up?: boolean | null;
  called_up_child_ids?: string[] | null;
  callup_rsvps?: Record<string, MatchRsvp> | null;
  callup_rsvp_reasons?: Record<string, string | null> | null;
  callup_confirmed_count?: number | null;
  callup_total_count?: number | null;
  training_coaches?: TrainingCoachItem[] | null;
  can_mark_coach_absence?: boolean | null;
  my_coach_absence?: boolean | null;
  training_absent_count?: number | null;
};

type TransportReservationItem = {
  id: string;
  child_id: string;
  child_label: string;
};

type TransportRequestItem = {
  id: string;
  child_id: string;
  child_label: string;
  note?: string | null;
  requester_label?: string | null;
  is_mine?: boolean | null;
  status?: 'OPEN' | 'DONE' | string | null;
  handled_by_label?: string | null;
};

type TransportOfferItem = {
  id: string;
  driver_label: string;
  seats_total: number;
  seats_taken: number;
  seats_free: number;
  note?: string | null;
  reservations?: TransportReservationItem[] | null;
};

type TransportResponse = {
  offers: TransportOfferItem[];
  requests_count?: number | null;
  my_offer_id?: string | null;
  my_reservation_id?: string | null;
  my_reservation_offer_id?: string | null;
  my_request?: TransportRequestItem | null;
  requests?: TransportRequestItem[] | null;
};

type TokenResponse = {
  token: string;
  status: 'ACTIVE' | 'PENDING' | 'REJECTED';
  role: Role;
};

type AdminUserItem = {
  id: string;
  email: string;
  display_name?: string | null;
  nickname?: string | null;
  role: string;
  can_parent: boolean;
  can_coach: boolean;
  can_admin: boolean;
  can_coordinator?: boolean;
  can_player?: boolean;
  status: string;
  created_at: string;
  signup_role?: string | null;
  signup_child_first_name?: string | null;
  signup_child_last_initial?: string | null;
  signup_child_birth_year?: number | null;
  signup_group_label?: string | null;
};

type PlayerAccountItem = {
  id: string;
  email: string;
};

type AdminCreateUserResponse = {
  user: AdminUserItem;
  password: string;
};

type AdminUsageUserItem = {
  id: string;
  email: string;
  role: string;
  status: string;
  login_count: number;
  last_login_at?: string | null;
  last_seen_at?: string | null;
};

type ChildItem = {
  id: string;
  first_name: string;
  last_initial: string;
  birth_year?: number | null;
  shirt_number?: number | null;
  group_id?: string | null;
  group_name?: string | null;
  group_year?: number | null;
  unavailability_kind?: 'NB' | 'KONTUZJA' | null;
  unavailability_reason?: 'CHOROBA' | 'KONTUZJA' | 'INNE' | null;
  unavailability_from?: string | null;
  unavailability_until?: string | null;
  unavailability_note?: string | null;
};

type CoachNoteVisibility = 'PRIVATE' | 'PARENT';

type ChildCoachNoteResponse = {
  child_id: string;
  text?: string | null;
  visibility?: CoachNoteVisibility | null;
  updated_at?: string | null;
};

type QuickReactionItem = {
  id: string;
  text: string;
  created_at: string;
  expires_at: string;
};

type QuickReactionVisibility = 'PARENT' | 'COACH_PRIVATE';

type QuickReactionsResponse = {
  ttl_days: number;
  items: QuickReactionItem[];
};

type QuickReactionsConfigResponse = {
  ttl_days: number;
};

type ChildPhotoResponse = {
  child_id: string;
  data_url?: string | null;
  updated_at?: string | null;
};

type PhotoCropRequest = {
  title: string;
  file: File;
  onConfirm: (cropped: File) => Promise<void> | void;
};

type GroupItem = {
  id: string;
  name: string;
  year?: number | null;
  created_at: string;
};

type CoachItem = {
  id: string;
  email: string;
};

type OpponentTeamItem = {
  id: string;
  name: string;
  logo_url?: string | null;
};

function prettyUserLabelFromBackendLabel(label: string): string {
  const v = (label || '').trim();
  if (!v) return 'Użytkownik';
  return v;
}

function userLabelWithEmail(u: { email: string; display_name?: string | null; nickname?: string | null }): string {
  const email = (u.email || '').trim();
  const nick = (u.nickname || '').trim();
  const dn = (u.display_name || '').trim();
  const base = nick || dn || email || 'Użytkownik';
  if (email && base !== email) return `${base} (${email})`;
  return base;
}

type ChatTargetItem = {
  recipient_user_id: string;
  recipient_email: string;
  child_id?: string | null;
  child_label?: string | null;
};

type ChatThreadItem = {
  id: string;
  title: string;
  child_id: string;
  child_label: string;
  last_message_at?: string | null;
  last_message_preview?: string | null;
  unread_count: number;
};

type ChatMessageItem = {
  id: string;
  sender_user_id: string;
  body: string;
  created_at: string;
  is_me: boolean;
};

type FeedReactionSummary = {
  emoji: string;
  count: number;
  is_me: boolean;
};

type FeedAttachmentItem = {
  id: string;
  kind: 'FILE' | 'LINK';
  title: string;
  url?: string | null;
  mime?: string | null;
  size_bytes: number;
  download_url?: string | null;
};

type FeedPostItem = {
  id: string;
  group_id: string;
  target_child_id?: string | null;
  kind: 'SYSTEM' | 'POST';
  category: string;
  title: string;
  body: string;
  is_important: boolean;
  ref_event_id?: string | null;
  attachments?: FeedAttachmentItem[] | null;
  author_label: string;
  created_at: string;
  my_confirmed: boolean;
  confirms_count: number;
  reactions: FeedReactionSummary[];
};

type LoungeMessageItem = {
  id: string;
  sender_user_id: string;
  sender_email: string;
  body: string;
  created_at: string;
  is_me: boolean;
};

type NotificationKind =
  | 'MATCH_RSVP'
  | 'FEED_IMPORTANT'
  | 'CHAT_UNREAD'
  | 'ADMIN_APPROVAL'
  | 'CALLUPS_MISSING_INFO'
  | 'CALLUPS_MISSING_CRITICAL'
  | 'MATCH_TBD';

type NotificationItem = {
  kind: NotificationKind;
  count: number;
  title: string;
  subtitle?: string | null;
  preview?: string | null;
  ref_event_id?: string | null;
  ref_post_id?: string | null;
  ref_thread_id?: string | null;
  ref_user_id?: string | null;
};

type NotificationsResponse = {
  total: number;
  items: NotificationItem[];
};

type PushPublicKeyResponse = {
  public_key: string | null;
};

type Tab = 'feed' | 'kalendarz' | 'druzyna' | 'wiadomosci' | 'czat' | 'profil';

const TAB_LABEL: Record<Tab, string> = {
  feed: 'Aktualności',
  kalendarz: 'Kalendarz',
  druzyna: 'Drużyna',
  wiadomosci: 'Wiadomości',
  czat: 'Czat',
  profil: 'Mój profil',
};

const TAB_LABEL_PARTS: Record<Tab, string[]> = {
  feed: ['AKTUAL', 'NOŚCI'],
  kalendarz: ['KALEN', 'DARZ'],
  druzyna: ['DRUŻYNA'],
  wiadomosci: ['WIADO', 'MOŚCI'],
  czat: ['CZAT'],
  profil: ['MÓJ', 'PROFIL'],
};

const IS_DEV = process.env.NODE_ENV !== 'production';

const APP_FE_VERSION = '0.1.0';
const APP_FE_RELEASE = 'v0.1.0-release-faza6';
const APP_FE_BUILD_ISO = (() => {
  try {
    const scripts = Array.from(document.getElementsByTagName('script') || []);
    for (const s of scripts) {
      const src = (s && typeof s.src === 'string' && s.src) || '';
      if (!src) continue;
      const m = src.match(/\/static\/js\/main\.([a-f0-9]{8,})\.js/i);
      if (m && m[1]) return m[1];
    }
  } catch { /* ignore */ }
  try {
    const html = document.documentElement ? document.documentElement.outerHTML || '' : '';
    const m = html.match(/\/static\/js\/main\.([a-f0-9]{8,})\.js/i);
    return (m && m[1]) ? m[1] : '';
  } catch {
    return '';
  }
})();

function detectFrontendBuildHash(): { hash: string; builtAt: string } {
  try {
    const scripts = Array.from(document.getElementsByTagName('script') || []);
    for (const s of scripts) {
      const src = (s && typeof s.src === 'string' && s.src) || '';
      if (!src) continue;
      const m = src.match(/\/static\/js\/main\.([a-f0-9]{8,})\.js/i);
      if (m && m[1]) return { hash: m[1], builtAt: new Date().toISOString().slice(0, 10) };
    }
  } catch { /* ignore */ }
  try {
    if (typeof window !== 'undefined' && document.documentElement) {
      const html = document.documentElement.outerHTML || '';
      const m = html.match(/\/static\/js\/main\.([a-f0-9]{8,})\.js/i);
      if (m && m[1]) return { hash: m[1], builtAt: new Date().toISOString().slice(0, 10) };
    }
  } catch { /* ignore */ }
  return { hash: APP_FE_BUILD_ISO || 'local', builtAt: new Date().toISOString().slice(0, 10) };
}

type BackendInfo = {
  version: string;
  release_name: string;
  app_env: string;
  scheduler_enabled: boolean;
  backend_build: string;
  backend_build_epoch: number;
  backend_cache_buster: string;
  uptime_seconds: number;
  startup_at: string;
};

async function apiBackendInfo(): Promise<BackendInfo | null> {
  try {
    const res = await fetch('/v1/info', {
      method: 'GET',
      headers: { Accept: 'application/json' },
    });
    if (!res || !res.ok) return null;
    const j = await res.json();
    return {
      version: String(j?.version ?? ''),
      release_name: String(j?.release_name ?? ''),
      app_env: String(j?.app_env ?? ''),
      scheduler_enabled: Boolean(j?.scheduler_enabled),
      backend_build: String(j?.backend_build ?? ''),
      backend_build_epoch: Number(j?.backend_build_epoch) || 0,
      backend_cache_buster: String(j?.backend_cache_buster ?? ''),
      uptime_seconds: Number(j?.uptime_seconds) || 0,
      startup_at: String(j?.startup_at ?? ''),
    };
  } catch {
    return null;
  }
}

function AppVersionBadge({ token }: { token?: string | null }) {
  const [feInfo] = React.useState(() => detectFrontendBuildHash());
  const [beInfo, setBeInfo] = React.useState<BackendInfo | null>(null);
  const [beLoading, setBeLoading] = React.useState(false);
  const [expanded, setExpanded] = React.useState(false);
  React.useEffect(() => {
    let cancelled = false;
    void (async () => {
      setBeLoading(true);
      try {
        const info = await apiBackendInfo();
        if (cancelled) return;
        setBeInfo(info);
      } finally {
        if (!cancelled) setBeLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);
  const feShort = (feInfo.hash || 'local').slice(0, 8);
  const beVersion = beInfo?.version || '—';
  const beRelease = beInfo?.release_name || '—';
  const envLabel = (beInfo?.app_env || 'local').toUpperCase();
  const schedLabel = beInfo ? (beInfo.scheduler_enabled ? 'ON' : 'OFF') : '—';
  const beBuiltDate = (() => {
    try {
      if (!beInfo?.backend_build_epoch) return '—';
      const d = new Date(Number(beInfo.backend_build_epoch) * 1000);
      if (Number.isNaN(d.getTime())) return '—';
      return d.toISOString().slice(0, 10);
    } catch {
      return '—';
    }
  })();
  return (
    <Card title="Wersja aplikacji">
      <div className="KeyValue">
        <div className="Key">Frontend (UI)</div>
        <div className="Value" style={{ fontFamily: 'ui-monospace, monospace' }}>
          {APP_FE_VERSION} · build <span style={{ fontWeight: 700 }}>{feShort}</span>
        </div>
      </div>
      <div className="KeyValue">
        <div className="Key">Backend (API)</div>
        <div className="Value" style={{ fontFamily: 'ui-monospace, monospace' }}>
          {beLoading ? 'Ładowanie…' : `${beVersion}`}
          {!beLoading && beRelease && beRelease !== beVersion ? ` · ${beRelease}` : ''}
        </div>
      </div>
      {expanded && (
        <>
          <div className="KeyValue">
            <div className="Key">Środowisko</div>
            <div className="Value">
              <span className={['Badge', envLabel === 'PROD' ? 'Badge--ok' : (envLabel === 'DEV' ? 'Badge--warn' : 'Badge--muted')].join(' ')}>{envLabel}</span>
            </div>
          </div>
          <div className="KeyValue">
            <div className="Key">Scheduler (BE)</div>
            <div className="Value">
              {beLoading ? '—' : (schedLabel === 'ON' ? '✅ WŁĄCZONY' : schedLabel === 'OFF' ? '⏸️ WYŁĄCZONY' : schedLabel)}
            </div>
          </div>
          <div className="KeyValue">
            <div className="Key">FE pełny hash</div>
            <div className="Value" style={{ fontFamily: 'ui-monospace, monospace', wordBreak: 'break-all' }}>main.{feInfo.hash || 'local'}.js</div>
          </div>
          <div className="KeyValue">
            <div className="Key">BE zbudowano</div>
            <div className="Value">{beBuiltDate}</div>
          </div>
          {beInfo?.startup_at && (
            <div className="KeyValue">
              <div className="Key">BE uruchomiono</div>
              <div className="Value">{new Date(beInfo.startup_at).toLocaleString('pl-PL')}</div>
            </div>
          )}
          {beInfo?.backend_cache_buster && (
            <div className="KeyValue">
              <div className="Key">BE cache-buster</div>
              <div className="Value" style={{ fontFamily: 'ui-monospace, monospace' }}>{beInfo.backend_cache_buster}</div>
            </div>
          )}
        </>
      )}
      <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <SecondaryButton type="button" onClick={() => setExpanded((v) => !v)}>
          {expanded ? 'Ukryj szczegóły' : 'Pokaż szczegóły'}
        </SecondaryButton>
        <SecondaryButton
          type="button"
          onClick={() => {
            if (window.confirm('Wymusić odświeżenie aplikacji? Spowoduje to ponowne pobranie najnowszej wersji.')) {
              try { window.location.reload(); } catch { /* ignore */ }
            }
          }}
        >
          🔄 Odśwież wersję
        </SecondaryButton>
      </div>
    </Card>
  );
}

const LAST_LOCATION_STORAGE_KEY = 'footdesk:lastLocation';
const IMPORTANT_TODAY_COLLAPSED_STORAGE_KEY = 'footdesk:importantTodayCollapsed';
const SCHEDULE_CHANGES_COLLAPSED_KEY = 'footdesk:scheduleChangesCollapsed';

function loadLastLocation(): string | null {
  try {
    const v = window.localStorage.getItem(LAST_LOCATION_STORAGE_KEY);
    return v && v.trim() ? v.trim() : null;
  } catch {
    return null;
  }
}

function saveLastLocation(value: string) {
  const v = value.trim();
  if (!v) return;
  try {
    window.localStorage.setItem(LAST_LOCATION_STORAGE_KEY, v);
  } catch {
    return;
  }
}

function loadScheduleChangesCollapsedPref(): boolean | null {
  try {
    const v = window.localStorage.getItem(SCHEDULE_CHANGES_COLLAPSED_KEY);
    if (v === '1') return true;
    if (v === '0') return false;
    return null;
  } catch {
    return null;
  }
}

function saveScheduleChangesCollapsedPref(value: boolean) {
  try {
    window.localStorage.setItem(SCHEDULE_CHANGES_COLLAPSED_KEY, value ? '1' : '0');
  } catch {
    // ignore
  }
}

function loadImportantTodayCollapsedPref(): boolean | null {
  try {
    const v = window.localStorage.getItem(IMPORTANT_TODAY_COLLAPSED_STORAGE_KEY);
    if (v === null) return null;
    if (v === '1') return true;
    if (v === '0') return false;
    return null;
  } catch {
    return null;
  }
}

function saveImportantTodayCollapsedPref(value: boolean): void {
  try {
    window.localStorage.setItem(IMPORTANT_TODAY_COLLAPSED_STORAGE_KEY, value ? '1' : '0');
  } catch {
    return;
  }
}

function devMeFromToken(token: string): Me | null {
  if (!IS_DEV) return null;
  const rodoBase = {
    rodo_accepted_latest: true,
    rodo_accepted_version: 'v1.1',
    rodo_accepted_at: null,
    rodo_latest_version: 'v1.1',
  };
  if (token === 'dev-coach') return { id: 'dev:dev-coach', role: 'COACH', roles: ['COACH'], status: 'ACTIVE', ...rodoBase };
  if (token === 'dev-admin') return { id: 'dev:dev-admin', role: 'ADMIN', roles: ['ADMIN'], status: 'ACTIVE', ...rodoBase };
  if (token === 'dev-coordinator')
    return { id: 'dev:dev-coordinator', role: 'COORDINATOR', roles: ['COORDINATOR'], status: 'ACTIVE', ...rodoBase };
  if (token === 'dev-parent') return { id: 'dev:dev-parent', role: 'PARENT', roles: ['PARENT'], status: 'ACTIVE', ...rodoBase };
  if (token === 'dev-parent-pending') return { id: 'dev:dev-parent-pending', role: 'PARENT', roles: ['PARENT'], status: 'PENDING', ...rodoBase };
  if (token === 'dev-player') return { id: 'dev:dev-player', role: 'PLAYER', roles: ['PLAYER'], status: 'ACTIVE', ...rodoBase };
  return null;
}

function createDevEvents(): Record<string, EventDetails> {
  const now = new Date();
  const todayMorning = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 9, 30, 0);
  const todayAfternoon = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 16, 30, 0);
  const todayEvening = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 18, 0, 0);
  const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 17, 30, 0);
  const dayAfterTomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 2, 18, 15, 0);
  const in3Days = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 3, 17, 0, 0);
  const in4Days = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 4, 11, 0, 0);
  const in5Days = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 5, 14, 0, 0);
  const in6Days = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 6, 9, 0, 0);
  const in7Days = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 7, 16, 0, 0);
  const in8Days = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 8, 18, 0, 0);
  const yesterdayMorning = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 9, 0, 0);
  const yesterdayEvening = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 18, 0, 0);
  const in2Days = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 2, 11, 0, 0);

  const events: EventDetails[] = [];

  const addEvent = (d: EventDetails) => events.push(d);

  addEvent({
    id: 'dev:event:training-today-morning',
    type: 'TRAINING',
    status: 'ACTIVE',
    title: 'Trening techniczny',
    group_id: 'demo-group',
    start_at: todayMorning.toISOString(),
    end_at: new Date(todayMorning.getTime() + 90 * 60 * 1000).toISOString(),
    location: 'Boisko Błonie (Orlik)',
    description: 'Zbiórka 10 min wcześniej. Ćwiczenia prowadzenia piłki i podań krótkich.',
    can_take_action: true,
    attendance_status: 'IN',
  });

  addEvent({
    id: 'dev:event:training-today-1630',
    type: 'TRAINING',
    status: 'ACTIVE',
    title: 'Trening bramkarzy',
    group_id: 'demo-group',
    target_child_id: 'dev:child:1',
    target_child_label: devChildLabel('dev:child:1'),
    is_individual: true,
    is_individual_coach_event: true,
    start_at: todayAfternoon.toISOString(),
    end_at: new Date(todayAfternoon.getTime() + 60 * 60 * 1000).toISOString(),
    location: 'Hala sportowa',
    description: 'Obuwie halowe obowiązkowe.',
    can_take_action: true,
    attendance_status: 'IN',
  });

  addEvent({
    id: 'dev:event:training-today-1800',
    type: 'TRAINING',
    status: 'ACTIVE',
    title: 'Trening główny',
    group_id: 'demo-group',
    start_at: todayEvening.toISOString(),
    end_at: new Date(todayEvening.getTime() + 105 * 60 * 1000).toISOString(),
    location: 'Boisko Błonie (Orlik)',
    description: 'Rozgrzewka + gra pozycyjna + fragmenty meczu. Zbiórka 18:00, start treningu 18:10.',
    can_take_action: true,
    attendance_status: 'IN',
  });

  addEvent({
    id: 'dev:event:match-today-sparing',
    type: 'MATCH',
    status: 'ACTIVE',
    title: 'Sparing: Błonianka vs LILAGO',
    group_id: 'demo-group',
    is_tbd: false,
    match_kind: 'FRIENDLY',
    opponent_name: 'LILAGO Warszawa',
    start_at: todayAfternoon.toISOString(),
    end_at: new Date(todayAfternoon.getTime() + 90 * 60 * 1000).toISOString(),
    location: 'Stadion KS Błonie',
    description: 'Zbiórka 45 min przed meczem. Białe stroje wyjazdowe.',
    can_take_action: true,
    match_rsvp: 'PENDING',
    match_deadline_at: new Date(todayAfternoon.getTime() - 2 * 60 * 60 * 1000).toISOString(),
    called_up: true,
  });

  addEvent({
    id: 'dev:event:training-tomorrow',
    type: 'TRAINING',
    status: 'ACTIVE',
    title: 'Trening motoryczny',
    group_id: 'demo-group',
    start_at: tomorrow.toISOString(),
    end_at: new Date(tomorrow.getTime() + 75 * 60 * 1000).toISOString(),
    location: 'Hala sportowa',
    description: 'Woda + obuwie halowe + ręcznik.',
    can_take_action: true,
    attendance_status: 'IN',
  });

  addEvent({
    id: 'dev:event:training-tomorrow-2',
    type: 'TRAINING',
    status: 'CANCELLED',
    title: 'Trening siłowy',
    group_id: 'demo-group',
    start_at: new Date(tomorrow.getTime() + 3 * 60 * 60 * 1000).toISOString(),
    end_at: new Date(tomorrow.getTime() + 4.2 * 60 * 60 * 1000).toISOString(),
    location: 'Siłownia przy hali',
    description: 'Trening odwołany z powodu nieobecności trenera siłowego.',
    can_take_action: false,
    attendance_status: null,
  });

  addEvent({
    id: 'dev:event:training-2days',
    type: 'TRAINING',
    status: 'ACTIVE',
    title: 'Trening taktyczny',
    group_id: 'demo-group',
    start_at: dayAfterTomorrow.toISOString(),
    end_at: new Date(dayAfterTomorrow.getTime() + 90 * 60 * 1000).toISOString(),
    location: 'Boisko Błonie (Orlik)',
    description: 'Obrona przejściowa i pressing.',
    can_take_action: true,
    attendance_status: 'LATE',
  });

  addEvent({
    id: 'dev:event:match-3days',
    type: 'MATCH',
    status: 'ACTIVE',
    title: 'Mecz ligowy: Błonianka vs MKS Wesoła',
    group_id: 'demo-group',
    is_tbd: false,
    match_kind: 'LEAGUE',
    opponent_name: 'MKS Wesoła',
    start_at: in3Days.toISOString(),
    end_at: new Date(in3Days.getTime() + 110 * 60 * 1000).toISOString(),
    location: 'Stadion Miejski w Błoniu',
    description: 'Zbiórka 45 min wcześniej. Czerwone stroje domowe. Po meczu krótka rozmowa z trenerem.',
    can_take_action: true,
    match_rsvp: 'ACCEPTED',
    match_deadline_at: new Date(in3Days.getTime() - 22 * 60 * 60 * 1000).toISOString(),
    called_up: true,
  });

  addEvent({
    id: 'dev:event:match-4days-tbd',
    type: 'MATCH',
    status: 'ACTIVE',
    title: 'Mecz: termin do potwierdzenia',
    group_id: 'demo-group',
    is_tbd: true,
    match_kind: 'LEAGUE',
    opponent_name: 'UKS Ursynów',
    start_at: in4Days.toISOString(),
    end_at: new Date(in4Days.getTime() + 110 * 60 * 1000).toISOString(),
    location: 'Do potwierdzenia',
    description: 'Termin i miejsce meczu zostaną potwierdzone do końca dnia poprzedniego.',
    can_take_action: true,
    match_rsvp: 'PENDING',
    called_up: null,
  });

  addEvent({
    id: 'dev:event:tournament-5days',
    type: 'MATCH',
    status: 'ACTIVE',
    title: 'Turniej Piłkarski „Jesień 2026”',
    group_id: 'demo-group',
    is_tbd: false,
    match_kind: 'TOURNAMENT',
    opponent_name: 'Turniej – 4 mecze',
    start_at: in5Days.toISOString(),
    end_at: new Date(in5Days.getTime() + 6 * 60 * 60 * 1000).toISOString(),
    location: 'Kompleks sportowy „Arena” w Ożarowie',
    description: 'Zbiórka: 13:15 pod halą. Cały turniej potrwa do około 20:00. Obiady zapewnione przez klub.',
    can_take_action: true,
    match_rsvp: 'DECLINED',
    match_deadline_at: new Date(in5Days.getTime() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    match_responded_late: false,
    called_up: true,
  });

  addEvent({
    id: 'dev:event:training-6days',
    type: 'TRAINING',
    status: 'ACTIVE',
    title: 'Trening bramkarski + 1:1',
    group_id: 'demo-group',
    target_child_id: 'dev:child:1',
    target_child_label: devChildLabel('dev:child:1'),
    is_individual: true,
    is_individual_coach_event: true,
    start_at: in6Days.toISOString(),
    end_at: new Date(in6Days.getTime() + 60 * 60 * 1000).toISOString(),
    location: 'Boisko Błonie (Orlik)',
    description: 'Rękawice obowiązkowe. Po treningu rozmowa indywidualna z trenerem.',
    can_take_action: true,
    attendance_status: 'IN',
  });

  addEvent({
    id: 'dev:event:match-7days-friendly',
    type: 'MATCH',
    status: 'ACTIVE',
    title: 'Sparing wyjazdowy: Legia Warszawa U12',
    group_id: 'demo-group',
    is_tbd: false,
    match_kind: 'FRIENDLY',
    opponent_name: 'Legia Warszawa U12',
    start_at: in7Days.toISOString(),
    end_at: new Date(in7Days.getTime() + 100 * 60 * 1000).toISOString(),
    location: 'Stadion Legii Training Ground, Warszawa',
    description: 'Wyjazd autobusem spod szkoły o 14:30. Powrót około 19:30.',
    can_take_action: true,
    match_rsvp: 'PENDING',
    match_deadline_at: new Date(in7Days.getTime() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    called_up: true,
  });

  addEvent({
    id: 'dev:event:training-8days',
    type: 'TRAINING',
    status: 'ACTIVE',
    title: 'Trening standardów',
    group_id: 'demo-group',
    start_at: in8Days.toISOString(),
    end_at: new Date(in8Days.getTime() + 90 * 60 * 1000).toISOString(),
    location: 'Boisko Błonie (Orlik)',
    description: 'Rzuty wolne, rożne, karne. Ustawienia obronne i ofensywne.',
    can_take_action: true,
    attendance_status: null,
  });

  addEvent({
    id: 'dev:event:match-yesterday-1',
    type: 'MATCH',
    status: 'ACTIVE',
    title: 'Mecz ligowy: Polonia U12 vs Błonianka',
    group_id: 'demo-group',
    is_tbd: false,
    match_kind: 'LEAGUE',
    opponent_name: 'Polonia Warszawa U12',
    start_at: yesterdayMorning.toISOString(),
    end_at: new Date(yesterdayMorning.getTime() + 110 * 60 * 1000).toISOString(),
    location: 'Stadion Polonii',
    description: 'Wynik: 2:3. Do podsumowania na treningu.',
    can_take_action: false,
    match_rsvp: 'ACCEPTED',
    match_responded_late: false,
    called_up: true,
  });

  addEvent({
    id: 'dev:event:training-yesterday',
    type: 'TRAINING',
    status: 'ACTIVE',
    title: 'Trening poprawkowy',
    group_id: 'demo-group',
    start_at: yesterdayEvening.toISOString(),
    end_at: new Date(yesterdayEvening.getTime() + 90 * 60 * 1000).toISOString(),
    location: 'Hala sportowa',
    description: 'Trening dla zawodników niepowołanych na mecz.',
    can_take_action: true,
    attendance_status: 'OUT',
  });

  addEvent({
    id: 'dev:event:match-2days-other',
    type: 'MATCH',
    status: 'ACTIVE',
    title: 'Mecz pucharowy: SP Łochów',
    group_id: 'demo-group',
    is_tbd: false,
    match_kind: 'OTHER',
    opponent_name: 'SP Łochów',
    start_at: in2Days.toISOString(),
    end_at: new Date(in2Days.getTime() + 110 * 60 * 1000).toISOString(),
    location: 'Stadion w Łochowie',
    description: '1/16 Pucharu Mazowsza. Zbiórka o 9:45 pod hotelem w Błoniu.',
    can_take_action: true,
    match_rsvp: 'PENDING',
    match_deadline_at: new Date(in2Days.getTime() - 20 * 60 * 60 * 1000).toISOString(),
    called_up: true,
  });

  const out: Record<string, EventDetails> = {};
  for (const e of events) {
    out[e.id] = e;
  }
  return out;
}

function createDevFeed(): FeedPostItem[] {
  const now = new Date();
  const out: FeedPostItem[] = [];

  const add = (p: FeedPostItem) => out.push(p);

  add({
    id: 'dev:feed:sys:training-today-morning',
    group_id: 'demo-group',
    kind: 'SYSTEM',
    category: 'EVENT',
    title: 'Trening techniczny',
    body:
      'Dzisiaj o 9:30 na boisku Orlik odbędzie się trening techniczny. Zbiórka 10 min wcześniej. Prosimy o potwierdzenie obecności (lub zgłoszenie nieobecności) przez aplikację. Trenerzy: prowadzenie piłki + podania krótkie.',
    is_important: false,
    ref_event_id: 'dev:event:training-today-morning',
    author_label: 'System',
    created_at: new Date(now.getTime() - 5 * 60 * 1000).toISOString(),
    my_confirmed: false,
    confirms_count: 11,
    reactions: [],
  });

  add({
    id: 'dev:feed:sys:match-today-sparing',
    group_id: 'demo-group',
    kind: 'SYSTEM',
    category: 'EVENT',
    title: 'Sparing: Błonianka vs LILAGO',
    body:
      'Dzisiaj o 16:30 gramy sparing z LILAGO Warszawa. Miejsce: Stadion KS Błonie. Zbiórka 45 min przed meczem. Powołani potwierdzają udział do 14:30. Stroje: białe wyjazdowe.',
    is_important: true,
    ref_event_id: 'dev:event:match-today-sparing',
    author_label: 'System',
    created_at: new Date(now.getTime() - 15 * 60 * 1000).toISOString(),
    my_confirmed: false,
    confirms_count: 8,
    reactions: [
      { emoji: '👍', count: 6, is_me: false },
      { emoji: '⚽', count: 3, is_me: true },
    ],
  });

  add({
    id: 'dev:feed:sys:training-today-1800',
    group_id: 'demo-group',
    kind: 'SYSTEM',
    category: 'EVENT',
    title: 'Trening główny',
    body:
      'Dzisiaj o 18:00 – trening główny grupy U12. Rozgrzewka, gra pozycyjna i fragmenty meczu. Prosimy o buty z korkami na deszcz, prognoza słaba. Początek treningu: 18:10.',
    is_important: false,
    ref_event_id: 'dev:event:training-today-1800',
    author_label: 'System',
    created_at: new Date(now.getTime() - 1 * 60 * 60 * 1000).toISOString(),
    my_confirmed: false,
    confirms_count: 13,
    reactions: [{ emoji: '👍', count: 9, is_me: true }],
  });

  add({
    id: 'dev:feed:coach:1',
    group_id: 'demo-group',
    kind: 'POST',
    category: 'INFO',
    title: 'Dodatkowe treningi indywidualne – wrzesień',
    body:
      'Od tego tygodnia startujemy z dodatkowymi treningami 1:1 z trenerami. Każdy zawodnik może zapisać się na 30 min raz w tygodniu. Pierwszeństwo mają zawodnicy z informacją zwrotną z ostatniego testu sprawnościowego. Zapisy przez koordynatora.',
    is_important: true,
    author_label: 'Trener Robert',
    created_at: new Date(now.getTime() - 2 * 60 * 60 * 1000).toISOString(),
    my_confirmed: false,
    confirms_count: 0,
    reactions: [
      { emoji: '🔥', count: 4, is_me: false },
      { emoji: '💪', count: 7, is_me: false },
    ],
    attachments: [
      { id: 'att:1', kind: 'LINK', title: 'Harmonogram wrzesień 2026', url: 'https://example.pl/harmonogram', mime: null, size_bytes: 0 },
    ],
  });

  add({
    id: 'dev:feed:sys:match-yesterday',
    group_id: 'demo-group',
    kind: 'SYSTEM',
    category: 'EVENT',
    title: 'Mecz ligowy: Polonia U12 vs Błonianka',
    body: 'Wczorajszy wynik: 2:3. Dziękujemy za grę. Szczegółowe podsumowanie i ćwiczenia poprawkowe zostaną omówione na dzisiejszym treningu o 18:00.',
    is_important: false,
    ref_event_id: 'dev:event:match-yesterday-1',
    author_label: 'System',
    created_at: new Date(now.getTime() - 20 * 60 * 60 * 1000).toISOString(),
    my_confirmed: false,
    confirms_count: 0,
    reactions: [
      { emoji: '👍', count: 12, is_me: false },
      { emoji: '⚽', count: 5, is_me: false },
    ],
  });

  add({
    id: 'dev:feed:coach:2',
    group_id: 'demo-group',
    kind: 'POST',
    category: 'INFO',
    title: 'Podsumowanie wczorajszego meczu z Polonią',
    body:
      'Cześć! Dzisiaj po treningu przeanalizujemy najważniejsze momenty z meczu wczorajszego. Do 17:45 polecam obejrzeć nagranie kluczowych akcji, które przesyłam w załączniku. Jeśli ktoś nie może być na treningu – proszę o info.',
    is_important: false,
    author_label: 'Trener Robert',
    created_at: new Date(now.getTime() - 22 * 60 * 60 * 1000).toISOString(),
    my_confirmed: false,
    confirms_count: 0,
    reactions: [
      { emoji: '👍', count: 8, is_me: true },
      { emoji: '📹', count: 2, is_me: false },
    ],
    attachments: [
      { id: 'att:2', kind: 'LINK', title: 'Analiza meczu – klipy', url: 'https://example.pl/match-clips', mime: null, size_bytes: 0 },
      { id: 'att:3', kind: 'FILE', title: 'Statystyka_U12_vs_Polonia.pdf', url: null, mime: 'application/pdf', size_bytes: 182341 },
    ],
  });

  add({
    id: 'dev:feed:sys:training-tomorrow-cancelled',
    group_id: 'demo-group',
    kind: 'SYSTEM',
    category: 'EVENT',
    title: '⚠️ Trening siłowy – ODWOŁANY',
    body: 'Jutrzejszy trening siłowy o 20:30 został odwołany z powodu nieobecności trenera siłowego. Następny trening siłowy: za tydzień w tym samym terminie. Przepraszamy za utrudnienia.',
    is_important: true,
    ref_event_id: 'dev:event:training-tomorrow-2',
    author_label: 'System',
    created_at: new Date(now.getTime() - 3 * 60 * 60 * 1000).toISOString(),
    my_confirmed: true,
    confirms_count: 17,
    reactions: [
      { emoji: '😞', count: 3, is_me: false },
      { emoji: '👍', count: 10, is_me: false },
    ],
  });

  add({
    id: 'dev:feed:sys:match-3days',
    group_id: 'demo-group',
    kind: 'SYSTEM',
    category: 'EVENT',
    title: 'Mecz ligowy: Błonianka vs MKS Wesoła',
    body:
      'Pojedynek ligowy w sobotę o 17:00 na Stadionie Miejskim. Zbiórka: 45 min przed meczem. Stroje domowe – czerwone. Powołani: lista zostanie opublikowana w czwartek wieczorem. Potwierdzenie udziału – do piątku godzina 12:00.',
    is_important: true,
    ref_event_id: 'dev:event:match-3days',
    author_label: 'System',
    created_at: new Date(now.getTime() - 6 * 60 * 1000).toISOString(),
    my_confirmed: false,
    confirms_count: 4,
    reactions: [{ emoji: '🔥', count: 5, is_me: false }],
  });

  add({
    id: 'dev:feed:sys:training-2days',
    group_id: 'demo-group',
    kind: 'SYSTEM',
    category: 'EVENT',
    title: 'Trening taktyczny',
    body:
      'Środa 18:15 – Orlik. Temat zajęć: obrona przejściowa i pressing grupowy. Prosimy o zeszyty trenerskie, żeby zapisać najważniejsze reguły. Ćwiczenia będzie prowadził trener Marek.',
    is_important: false,
    ref_event_id: 'dev:event:training-2days',
    author_label: 'System',
    created_at: new Date(now.getTime() - 4 * 60 * 60 * 1000).toISOString(),
    my_confirmed: false,
    confirms_count: 10,
    reactions: [{ emoji: '⚽', count: 4, is_me: false }],
  });

  add({
    id: 'dev:feed:coord:1',
    group_id: 'demo-group',
    kind: 'POST',
    category: 'ANNOUNCEMENT',
    title: 'Zbiórka na turniej Jesień 2026 – potrzebujemy kierowców',
    body:
      'W niedzielę jedziemy na turniej do Ożarowa. Potrzebujemy 5-6 samochodów do wspólnego dojazdu. Jeśli masz wolne miejsce w aucie – kliknij ikonkę auta w wydarzeniu lub napisz w wiadomości. Dziękujemy!',
    is_important: true,
    author_label: 'Koordynator Anna',
    created_at: new Date(now.getTime() - 5 * 60 * 60 * 1000).toISOString(),
    my_confirmed: false,
    confirms_count: 0,
    reactions: [
      { emoji: '🚗', count: 3, is_me: false },
      { emoji: '🙏', count: 6, is_me: true },
    ],
  });

  add({
    id: 'dev:feed:sys:match-tbd',
    group_id: 'demo-group',
    kind: 'SYSTEM',
    category: 'EVENT',
    title: 'Mecz z UKS Ursynów – TERMIN DO POTWIERDZENIA',
    body: 'Termin i miejsce meczu zostaną potwierdzone do końca dnia poprzedniego. Na razie sugerujemy potraktować mecz jako prawdopodobny. Jeśli coś się zmieni – wysyłamy powiadomienie push i wpis w feedzie.',
    is_important: false,
    ref_event_id: 'dev:event:match-4days-tbd',
    author_label: 'System',
    created_at: new Date(now.getTime() - 7 * 60 * 60 * 1000).toISOString(),
    my_confirmed: false,
    confirms_count: 0,
    reactions: [],
  });

  add({
    id: 'dev:feed:sys:match-2days-other',
    group_id: 'demo-group',
    kind: 'SYSTEM',
    category: 'EVENT',
    title: 'Mecz pucharowy: SP Łochów',
    body:
      '1/16 Pucharu Mazowsza. Wyjazd: 9:45 pod hotelem w Błoniu. Potrzebni kierowcy – deklaracje do końca dzisiaj. Obiad w drodze powrotnej dla zawodników z budżetu drużyny.',
    is_important: false,
    ref_event_id: 'dev:event:match-2days-other',
    author_label: 'System',
    created_at: new Date(now.getTime() - 8 * 60 * 60 * 1000).toISOString(),
    my_confirmed: false,
    confirms_count: 7,
    reactions: [
      { emoji: '💪', count: 8, is_me: false },
      { emoji: '🏆', count: 5, is_me: false },
    ],
  });

  add({
    id: 'dev:feed:coach:3',
    group_id: 'demo-group',
    kind: 'POST',
    category: 'INFO',
    title: 'Nowe koszulki treningowe – zamówienia',
    body:
      'Rozpoczynamy zamówienia na dodatkowy komplet koszulek treningowych w kolorze pomarańczowym. Cena: 89 zł. Wymiary i próbki dostępne po treningu w środę. Ostateczna lista zamówień do 8 września.',
    is_important: false,
    author_label: 'Trener Robert',
    created_at: new Date(now.getTime() - 26 * 60 * 60 * 1000).toISOString(),
    my_confirmed: false,
    confirms_count: 0,
    reactions: [
      { emoji: '😍', count: 3, is_me: false },
      { emoji: '👍', count: 7, is_me: false },
    ],
    attachments: [
      { id: 'att:4', kind: 'FILE', title: 'Tabela_rozmiarow.pdf', url: null, mime: 'application/pdf', size_bytes: 251880 },
    ],
  });

  add({
    id: 'dev:feed:sys:tournament',
    group_id: 'demo-group',
    kind: 'SYSTEM',
    category: 'EVENT',
    title: '🏆 Turniej „Jesień 2026”',
    body:
      'Zawody niedzielne w Ożarowie. Przewidywane 4 mecze. Start 14:00, koniec ok. 20:00. Obiady i napoje dla zawodników zapewnia klub. Dla rodziców: kawiarnia na terenie obiektu.',
    is_important: true,
    ref_event_id: 'dev:event:tournament-5days',
    author_label: 'System',
    created_at: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    my_confirmed: false,
    confirms_count: 12,
    reactions: [
      { emoji: '🏆', count: 9, is_me: true },
      { emoji: '🔥', count: 6, is_me: false },
      { emoji: '👏', count: 4, is_me: false },
    ],
  });

  add({
    id: 'dev:feed:coord:2',
    group_id: 'demo-group',
    kind: 'POST',
    category: 'ANNOUNCEMENT',
    title: 'Składki klubowe wrzesień',
    body:
      'Przypominamy o składkach klubowych za wrzesień – płatność do 10 dnia miesiąca. Wszelkie pytania dotyczące płatności proszę kierować przez wiadomości do koordynatora. Numer konta w załączniku.',
    is_important: true,
    author_label: 'Koordynator Anna',
    created_at: new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    my_confirmed: false,
    confirms_count: 0,
    reactions: [{ emoji: '👍', count: 10, is_me: false }],
    attachments: [
      { id: 'att:5', kind: 'LINK', title: 'Regulamin płatności 2026/27', url: 'https://example.pl/regulamin', mime: null, size_bytes: 0 },
    ],
  });

  add({
    id: 'dev:feed:sys:match-friendly-7days',
    group_id: 'demo-group',
    kind: 'SYSTEM',
    category: 'EVENT',
    title: 'Sparing wyjazdowy: Legia Warszawa U12',
    body:
      'Jedziemy na sparing do Warszawy. Wyjazd autobusem spod szkoły o 14:30, powrót ok. 19:30. Lista powołanych – do piątku. Potwierdzenia udziału – do wtorku 23:59.',
    is_important: false,
    ref_event_id: 'dev:event:match-7days-friendly',
    author_label: 'System',
    created_at: new Date(now.getTime() - 4 * 24 * 60 * 60 * 1000).toISOString(),
    my_confirmed: false,
    confirms_count: 3,
    reactions: [
      { emoji: '⚽', count: 7, is_me: false },
      { emoji: '🚌', count: 2, is_me: false },
    ],
  });

  add({
    id: 'dev:feed:coach:4',
    group_id: 'demo-group',
    kind: 'POST',
    category: 'INFO',
    title: 'Testy sprawnościowe – nowy cykl',
    body:
      'Za 2 tygodnie startujemy z nowym cyklem testów sprawnościowych. Szczegółowa lista zadań i standardy dla poszczególnych kategorii wiekowych pojawią się wkrótce w profilu zawodnika. Rekomendujemy 2 treningi regeneracyjne w tygodniu testów.',
    is_important: false,
    author_label: 'Trener Marek',
    created_at: new Date(now.getTime() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    my_confirmed: false,
    confirms_count: 0,
    reactions: [
      { emoji: '💪', count: 8, is_me: false },
      { emoji: '🏃', count: 5, is_me: false },
    ],
  });

  add({
    id: 'dev:feed:coord:3',
    group_id: 'demo-group',
    kind: 'POST',
    category: 'ANNOUNCEMENT',
    title: 'Dzień otwarty – zapraszamy kolegów',
    body:
      'Ostatni piątek września organizujemy Dzień Otwarty. Każdy zawodnik może zaprosić kolegę (rocznik 2014-2015) na wspólny trening. Zgłoszenia – koordynator do 20.09. Liczba miejsc ograniczona.',
    is_important: false,
    author_label: 'Koordynator Anna',
    created_at: new Date(now.getTime() - 6 * 24 * 60 * 60 * 1000).toISOString(),
    my_confirmed: false,
    confirms_count: 0,
    reactions: [
      { emoji: '🎉', count: 9, is_me: false },
      { emoji: '👍', count: 5, is_me: false },
    ],
  });

  return out.sort((a, b) => b.created_at.localeCompare(a.created_at));
}

function createDevOpponentTeams(): OpponentTeamItem[] {
  return [
    { id: 'dev:team:1', name: 'LILAGO', logo_url: null },
    { id: 'dev:team:2', name: 'KS BŁONIE', logo_url: null },
  ];
}

let DEV_OPPONENT_TEAMS: OpponentTeamItem[] = createDevOpponentTeams();

const DEV_CHILDREN: ChildItem[] = [
  {
    id: 'dev:child:1',
    first_name: 'Jan',
    last_initial: 'K.',
    birth_year: 2014,
    group_id: 'demo-group',
    group_name: 'Demo U12',
    group_year: 2014,
  },
];

type DevChatThread = {
  id: string;
  child_id: string;
  participant_ids: string[];
  messages: Array<{ id: string; sender_user_id: string; body: string; created_at: string }>;
  reads: Record<string, string>;
};

function devChildLabel(childId: string): string {
  const c = DEV_CHILDREN.find((x) => x.id === childId);
  if (!c) return childId;
  const by = c.birth_year ? ` (${c.birth_year})` : '';
  return `${c.first_name} ${c.last_initial}${by}`;
}

let DEV_EVENTS: Record<string, EventDetails> = createDevEvents();
let DEV_FEED: FeedPostItem[] = createDevFeed();

function createDevChatThreads(): DevChatThread[] {
  const now = new Date();
  const t1: DevChatThread = {
    id: 'dev:thread:1',
    child_id: 'dev:child:1',
    participant_ids: ['dev:dev-parent', 'dev:dev-coach'],
    messages: [
      {
        id: 'dev:msg:1',
        sender_user_id: 'dev:dev-coach',
        body: 'Cześć! Przypominam o treningu jutro o 17:30.',
        created_at: new Date(now.getTime() - 1000 * 60 * 60 * 6).toISOString(),
      },
      {
        id: 'dev:msg:2',
        sender_user_id: 'dev:dev-parent',
        body: 'Dzięki! Będziemy na miejscu 10 min wcześniej.',
        created_at: new Date(now.getTime() - 1000 * 60 * 60 * 5).toISOString(),
      },
    ],
    reads: {
      'dev:dev-parent': '',
      'dev:dev-coach': '',
    },
  };
  return [t1];
}

let DEV_CHAT_THREADS: DevChatThread[] = createDevChatThreads();

function toListItem(d: EventDetails): EventListItem {
  return {
    id: d.id,
    type: d.type,
    status: d.status,
    title: d.title,
    group_id: d.group_id,
    is_individual: d.is_individual ?? false,
    is_individual_coach_event: d.is_individual_coach_event ?? null,
    target_child_id: d.target_child_id ?? null,
    target_child_label: d.target_child_label ?? null,
    start_at: d.start_at,
    location: d.location,
    opponent_name: d.opponent_name ?? null,
    is_tbd: d.is_tbd ?? false,
    match_kind: d.match_kind ?? null,
    can_take_action: d.can_take_action,
    attendance_status: d.attendance_status ?? null,
    match_rsvp: d.match_rsvp ?? null,
    match_responded_late: d.match_responded_late ?? null,
    match_deadline_at: d.match_deadline_at ?? null,
    called_up: d.called_up ?? null,
    transport_offers_count: 0,
    transport_requests_count: 0,
  };
}

function matchKindLabel(k: MatchKind | null | undefined): string {
  switch (k) {
    case 'LEAGUE':
      return 'Mecz ligowy';
    case 'FRIENDLY':
      return 'Mecz sparingowy';
    case 'TOURNAMENT':
      return 'Turniej';
    case 'OTHER':
      return 'Mecz inny';
    default:
      return 'Mecz';
  }
}

function matchKindShort(k: MatchKind | null | undefined): string {
  switch (k) {
    case 'LEAGUE':
      return 'LIGA';
    case 'FRIENDLY':
      return 'SPARING';
    case 'TOURNAMENT':
      return 'TURNIEJ';
    case 'OTHER':
      return 'INNY';
    default:
      return 'MECZ';
  }
}

function matchKindClass(k: MatchKind | null | undefined): string {
  switch (k) {
    case 'LEAGUE':
      return 'is-league';
    case 'FRIENDLY':
      return 'is-friendly';
    case 'TOURNAMENT':
      return 'is-tournament';
    case 'OTHER':
      return 'is-other';
    default:
      return '';
  }
}

function matchKindEmoji(k: MatchKind | null | undefined): string {
  switch (k) {
    case 'TOURNAMENT':
      return '🏆';
    case 'FRIENDLY':
      return '🤝';
    case 'LEAGUE':
    case 'OTHER':
    default:
      return '⚽';
  }
}

function transportIconClass(e: { transport_offers_count?: number | null; transport_requests_count?: number | null } | null | undefined): string {
  const offers = Number(e?.transport_offers_count ?? 0);
  const needs = Number(e?.transport_requests_count ?? 0);
  if (offers > 0 && needs > 0) return 'is-transport-both';
  if (needs > 0) return 'is-transport-need';
  if (offers > 0) return 'is-transport-offer';
  return '';
}

function formatTopBarDateParts(d: Date): { line1: string; line2: string } {
  const df = new Intl.DateTimeFormat('pl-PL', { day: '2-digit', month: 'long' });
  return { line1: 'DZISIAJ,', line2: df.format(d).toUpperCase() };
}

function BellIcon(props: { className?: string }) {
  return (
    <svg className={props.className} viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
      <path
        d="M12 22a2.4 2.4 0 0 0 2.4-2.4H9.6A2.4 2.4 0 0 0 12 22Zm7.2-6V11a7.2 7.2 0 1 0-14.4 0v5L3 17.8v1.4h18v-1.4L19.2 16Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ClockIcon(props: { className?: string }) {
  return (
    <svg className={props.className} viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
      <circle cx="12" cy="12" r="8.5" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <path d="M12 7.6v4.8l3.2 2" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ExclamationIcon(props: { className?: string }) {
  return (
    <svg className={props.className} viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
      <path
        d="M12 3.2 2.8 20h18.4L12 3.2Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path d="M12 9v5.2" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <circle cx="12" cy="16.9" r="1" fill="currentColor" />
    </svg>
  );
}

function CheckIcon(props: { className?: string }) {
  return (
    <svg className={props.className} viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
      <path
        d="M20 7 10.5 16.5 4 10"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function XIcon(props: { className?: string }) {
  return (
    <svg className={props.className} viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
      <path
        d="M6.2 6.2 17.8 17.8M17.8 6.2 6.2 17.8"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function UndoIcon(props: { className?: string }) {
  return (
    <svg className={props.className} viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
      <path
        d="M9 9H4V4"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M4 9a8 8 0 1 1 2.4 5.7"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function TrashIcon(props: { className?: string }) {
  return (
    <svg className={props.className} viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
      <path
        d="M8.2 6.8h7.6M10 6.8V5.6c0-.9.7-1.6 1.6-1.6h.8c.9 0 1.6.7 1.6 1.6v1.2"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M6.8 6.8l.8 14.2c.1 1 1 2 2.2 2h4.4c1.2 0 2.1-1 2.2-2l.8-14.2"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path d="M10.2 10v8M13.8 10v8" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function MenuIcon(props: { className?: string }) {
  return (
    <svg className={props.className} viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
      <path d="M4 7h16M4 12h16M4 17h16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function TabIcon(props: { tab: Tab; className?: string }) {
  const common = { fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  if (props.tab === 'feed') {
    return (
      <svg className={props.className} viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
        <path {...common} d="M4 11 12 4l8 7v9a2 2 0 0 1-2 2h-4v-6h-4v6H6a2 2 0 0 1-2-2v-9Z" />
      </svg>
    );
  }
  if (props.tab === 'kalendarz') {
    return (
      <svg className={props.className} viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
        <path {...common} d="M7 3v3M17 3v3M4 8h16" />
        <path {...common} d="M6 6h12a2 2 0 0 1 2 2v12a2.5 2.5 0 0 1-2.5 2.5H6.5A2.5 2.5 0 0 1 4 20V8a2 2 0 0 1 2-2Z" />
        <circle cx="8.2" cy="12" r="0.9" fill="currentColor" />
        <circle cx="12" cy="12" r="0.9" fill="currentColor" />
        <circle cx="15.8" cy="12" r="0.9" fill="currentColor" />
        <circle cx="8.2" cy="15.8" r="0.9" fill="currentColor" />
        <circle cx="12" cy="15.8" r="0.9" fill="currentColor" />
        <circle cx="15.8" cy="15.8" r="0.9" fill="currentColor" />
      </svg>
    );
  }
  if (props.tab === 'druzyna') {
    return (
      <svg className={props.className} viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
        <path {...common} d="M9 11.4a3.4 3.4 0 1 0 0-6.8 3.4 3.4 0 0 0 0 6.8Z" />
        <path {...common} d="M16.6 12a2.8 2.8 0 1 0 0-5.6 2.8 2.8 0 0 0 0 5.6Z" />
        <path {...common} d="M2.8 21v-1a6.2 6.2 0 0 1 6.2-6.2h.1A6.2 6.2 0 0 1 15.3 20v1" />
        <path {...common} d="M15.8 21v-1a4.8 4.8 0 0 1 4.8-4.8h.1" />
      </svg>
    );
  }
  if (props.tab === 'wiadomosci') {
    return (
      <svg className={props.className} viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
        <path {...common} d="M20.8 14.4a4.6 4.6 0 0 1-4.6 4.6H9l-5.8 3V7.6A4.6 4.6 0 0 1 7.8 3h8.4a4.6 4.6 0 0 1 4.6 4.6v6.8Z" />
        <circle cx="9" cy="11.2" r="1" fill="currentColor" />
        <circle cx="12" cy="11.2" r="1" fill="currentColor" />
        <circle cx="15" cy="11.2" r="1" fill="currentColor" />
      </svg>
    );
  }
  if (props.tab === 'czat') {
    return <ChatIcon className={props.className} />;
  }
  return (
    <svg className={props.className} viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
      <path {...common} d="M12 12.2a4.2 4.2 0 1 0 0-8.4 4.2 4.2 0 0 0 0 8.4Z" />
      <path {...common} d="M20 21v-1.2A6.8 6.8 0 0 0 13.2 13h-2.4A6.8 6.8 0 0 0 4 19.8V21" />
    </svg>
  );
}

function MapPinIcon(props: { className?: string }) {
  return (
    <svg className={props.className} viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
      <path
        d="M12 21s7-4.6 7-11A7 7 0 0 0 5 10c0 6.4 7 11 7 11Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path
        d="M12 13.2a3.2 3.2 0 1 0 0-6.4 3.2 3.2 0 0 0 0 6.4Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
      />
    </svg>
  );
}

function MapIcon(props: { className?: string }) {
  return (
    <svg className={props.className} viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
      <path
        d="M9 19 3.8 21V5l5.2-2 6 2 5.2-2v16L15 21l-6-2Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path d="M9 3v16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M15 5v16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function CarIcon(props: { className?: string }) {
  return (
    <svg className={props.className} viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
      <path
        d="M5.2 16.2h13.6M6.6 16.2l1.2-5.2a2.2 2.2 0 0 1 2.1-1.7h4.2a2.2 2.2 0 0 1 2.1 1.7l1.2 5.2"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <path
        d="M6 16.2v2.2M18 16.2v2.2"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      <circle cx="7.6" cy="18.8" r="1.2" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="16.4" cy="18.8" r="1.2" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <path d="M8.6 12.2h6.8" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function UserIcon(props: { className?: string }) {
  return (
    <svg className={props.className} viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
      <path
        d="M12 12a4.2 4.2 0 1 0 0-8.4A4.2 4.2 0 0 0 12 12Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
      />
      <path
        d="M20 21v-1.2A6.8 6.8 0 0 0 13.2 13h-2.4A6.8 6.8 0 0 0 4 19.8V21"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function ChatIcon(props: { className?: string }) {
  return (
    <svg className={props.className} viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
      <path
        d="M21 15a4 4 0 0 1-4 4H9l-6 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4v8Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path
        d="M8 8.8h8M8 12h5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function SearchIcon(props: { className?: string }) {
  return (
    <svg className={props.className} viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
      <path
        d="M10.8 18.2a7.4 7.4 0 1 1 0-14.8 7.4 7.4 0 0 1 0 14.8Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
      />
      <path
        d="M21 21l-4.2-4.2"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function SlidersIcon(props: { className?: string }) {
  return (
    <svg className={props.className} viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
      <path d="M4 6h10M18 6h2" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M4 12h2M10 12h10" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M4 18h10M18 18h2" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M14 6v0" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M8 12v0" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M14 18v0" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <circle cx="14" cy="6" r="2" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="8" cy="12" r="2" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="14" cy="18" r="2" fill="none" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  );
}

function SoccerIcon(props: { className?: string }) {
  return (
    <svg className={props.className} viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
      <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="M12 7.2l3 2.2-1.1 3.5H10.1L9 9.4l3-2.2Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path d="M9 9.4 6.9 9l-1.8 3.6 2.2 2.7" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <path d="M15 9.4 17.1 9l1.8 3.6-2.2 2.7" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <path d="M10.1 12.9 8.7 16l3.3 2.3 3.3-2.3-1.4-3.1" fill="none" stroke="currentColor" strokeWidth="1.4" />
    </svg>
  );
}

function TrainingIcon(props: { className?: string }) {
  return (
    <svg className={props.className} viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
      <path
        d="M6 9.2v5.6M18 9.2v5.6M8.2 10.5v3M15.8 10.5v3M9.8 12h4.4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M4.8 8.4h1.4a.8.8 0 0 1 .8.8v5.6a.8.8 0 0 1-.8.8H4.8a.8.8 0 0 1-.8-.8V9.2a.8.8 0 0 1 .8-.8Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path
        d="M19.2 8.4h-1.4a.8.8 0 0 0-.8.8v5.6a.8.8 0 0 0 .8.8h1.4a.8.8 0 0 0 .8-.8V9.2a.8.8 0 0 0-.8-.8Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function PlusIcon(props: { className?: string }) {
  return (
    <svg className={props.className} viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
      <path
        d="M12 5v14M5 12h14"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Fab(props: { onClick?: () => void }) {
  return (
    <button className="Fab" type="button" aria-label="Szybka akcja" onClick={props.onClick} disabled>
      <ChatIcon className="FabIcon" />
    </button>
  );
}

const TOKEN_KEY = 'ac_token';
const ACTIVE_GROUP_KEY = 'ac_active_group_id';
const COACH_VIEW_MODE_KEY = 'ac_coach_view_mode';
const CLIENT_CACHE_KEY = 'footdesk:lastClientCacheBuster';
const FORCE_RELOGIN_MSG_KEY = 'footdesk:reloginMsgShown_v1';
const LAST_FORCE_RELOGIN_ISO_KEY = 'footdesk:lastForceReloginIsoApplied';

type CoachViewMode = 'GROUP' | 'INDIVIDUAL';

function getTabsForRole(role: Role): Tab[] {
  if (role === 'ADMIN' || role === 'COORDINATOR') return ['feed', 'kalendarz', 'druzyna', 'czat', 'wiadomosci', 'profil'];
  if (role === 'PLAYER') return ['kalendarz', 'profil'];
  return ['feed', 'kalendarz', 'druzyna', 'wiadomosci', 'czat', 'profil'];
}

function getBottomTabsForRole(role: Role): Tab[] {
  return getTabsForRole(role).filter((t) => t !== 'profil');
}

function getHashParts(): { path: string; params: URLSearchParams } {
  const raw = window.location.hash.replace(/^#\/?/, '').trim();
  const [path, query = ''] = raw.split('?');
  return { path, params: new URLSearchParams(query) };
}

function getTabFromHash(): Tab {
  const { path } = getHashParts();
  if (path === 'feed' || path === '') return 'feed';
  if (path === 'kalendarz') return 'kalendarz';
  if (path === 'druzyna') return 'druzyna';
  if (path === 'wiadomosci') return 'wiadomosci';
  if (path === 'czat') return 'czat';
  if (path === 'profil') return 'profil';
  return 'feed';
}

function navigate(tab: Tab, params?: Record<string, string | null | undefined>): void {
  const sp = new URLSearchParams();
  if (params) {
    Object.entries(params).forEach(([k, v]) => {
      if (v !== null && v !== undefined && v !== '') sp.set(k, v);
    });
  }
  const qs = sp.toString();
  window.location.hash = qs ? `#/${tab}?${qs}` : `#/${tab}`;
}

async function apiGetMe(token: string): Promise<Me> {
  const devMe = devMeFromToken(token);
  if (devMe) return devMe;
  const res = await fetch('/v1/me', {
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    throw new Error(`HTTP_${res.status}`);
  }
  return normalizeMe(await res.json());
}

async function apiGetProfile(token: string): Promise<UserProfile> {
  const devMe = devMeFromToken(token);
  if (IS_DEV && devMe) {
    const email =
      token === 'dev-admin'
        ? 'admin@demo.pl'
        : token === 'dev-coordinator'
          ? 'koordynator@demo.pl'
        : token === 'dev-coach'
          ? 'trener@demo.pl'
          : token === 'dev-parent' || token === 'dev-parent-pending'
            ? 'rodzic@demo.pl'
            : 'dev@demo.pl';
    return {
      email,
      display_name: null,
      nickname: null,
      rodo_accepted_latest: true,
      rodo_accepted_version: 'v1.1',
      rodo_accepted_at: null,
      rodo_latest_version: 'v1.1',
    };
  }
  const res = await fetch('/v1/profile', {
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiUpdateProfile(token: string, body: { display_name?: string | null; nickname?: string | null }): Promise<UserProfile> {
  const devMe = devMeFromToken(token);
  if (IS_DEV && devMe) {
    const email =
      token === 'dev-admin'
        ? 'admin@demo.pl'
        : token === 'dev-coordinator'
          ? 'koordynator@demo.pl'
        : token === 'dev-coach'
          ? 'trener@demo.pl'
          : token === 'dev-parent' || token === 'dev-parent-pending'
            ? 'rodzic@demo.pl'
            : 'dev@demo.pl';
    return {
      email,
      display_name: body.display_name ?? null,
      nickname: body.nickname ?? null,
      rodo_accepted_latest: true,
      rodo_accepted_version: 'v1.1',
      rodo_accepted_at: null,
      rodo_latest_version: 'v1.1',
    };
  }
  const res = await fetch('/v1/profile', {
    method: 'PATCH',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiGetRodo(token: string): Promise<RodoAcceptance> {
  const devMe = devMeFromToken(token);
  if (IS_DEV && devMe) {
    const devSections: RodoSection[] = [
      { title: '1. Administrator danych osobowych', body: 'Administratorem danych jest FootDesk / Akademia Piłkarska Connect.' },
      { title: '2. Cel i podstawa przetwarzania', body: 'Dane przetwarzane są w celu organizacji treningów, meczów, powiadomień dla rodziców oraz publikacji zdjęć z wydarzeń (turnieje, mecze, obozy, uroczystości).' },
      { title: '3. Kategorie danych', body: 'Przetwarzamy: imię i nazwisko zawodnika, rok urodzenia, numer koszulki, zdjęcie profilowe, zdjęcia i nagrania wizerunku z wydarzeń Akademii, dane kontaktowe rodzica, historię obecności, potwierdzenia udziału w meczach, deklaracje nieobecności, dane o transporcie.' },
      { title: '4. Odbiorcy danych', body: 'Dane widoczne są dla trenerów, administratorów i rodziców w tej samej grupie. Zdjęcia z wydarzeń mogą być ponadto publikowane w aplikacji FootDesk, na stronie internetowej Akademii i w mediach społecznościowych.' },
      { title: '5. Okres przechowywania', body: 'Dane zawodnika przechowywane są do końca sezonu, w którym opuszcza on Akademię. Zdjęcia z wydarzeń przechowywane są przez okres działalności promocyjnej, a po cofnięciu zgody na wizerunek usuwane lub zanonimizowane.' },
      { title: '6. Prawa użytkownika', body: 'Masz prawo do dostępu, sprostowania, usunięcia, ograniczenia, przenoszenia danych, wniesienia sprzeciwu oraz cofnięcia zgody na wizerunek w dowolnym momencie.' },
      { title: '7. Powiadomienia push', body: 'Subskrypcja push używana jest wyłącznie do powiadomień Akademii (powołania, zmiany planu, wiadomości, ważne ogłoszenia).' },
      { title: '8. Bezpieczeństwo', body: 'Dostęp do konta chroniony jest hasłem. Rekomendujemy użycie silnego hasła i nieudostępnianie go innym osobom.' },
      { title: '9. Zdjęcia z turniejów, meczów i wydarzeń Akademii', body: 'W ramach działalności Akademii mogą być wykonywane zdjęcia i nagrania wizerunku zawodników, trenerów i uczestników. Zdjęcia wykorzystywane są wyłącznie do celów promocyjnych, dokumentacyjnych i informacyjnych Akademii. Rodzic/opiekun może w dowolnym momencie wyrazić zgodę lub cofnąć ją kontaktując się z Administratorem. Użytkownicy aplikacji zobowiązują się nie publikować, nie kopiować i nie rozpowszechniać zdjęć innych osób bez ich zgody.' },
    ];
    return {
      latest_version: 'v1.1',
      accepted_version: 'v1.1',
      accepted_at: null,
      last_seen_version: 'v1.1',
      accepted_latest: true,
      policy: { version: 'v1.1', published_at: '2026-08-18', sections: devSections },
    };
  }
  const res = await fetch('/v1/rodo', {
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiAcceptRodo(token: string, version: string): Promise<RodoAcceptance> {
  const devMe = devMeFromToken(token);
  if (IS_DEV && devMe) {
    return apiGetRodo(token);
  }
  const res = await fetch('/v1/rodo/accept', {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ version }),
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiLogout(token: string | null): Promise<void> {
  if (!token) return;
  const devMe = devMeFromToken(token);
  if (IS_DEV && devMe) return;
  if (token.length < 6) return;
  try {
    await fetch('/v1/auth/logout', {
      method: 'POST',
      headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
    });
  } catch {
  }
}

async function apiChangePassword(token: string, body: { current_password: string; new_password: string }): Promise<{ ok: true }> {
  const devMe = devMeFromToken(token);
  if (IS_DEV && devMe) {
    if (body.new_password.trim().length < 8) throw new Error('HTTP_422');
    return { ok: true };
  }
  const res = await fetch('/v1/auth/change-password', {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiNotifications(token: string, childId?: string | null): Promise<NotificationsResponse> {
  const dev = devMeFromToken(token);
  if (IS_DEV && dev) {
    const items: NotificationItem[] = [];

    if (isChildScopedRole(dev.role) && childId) {
      const [posts, events, threads] = await Promise.all([
        apiFeedList(token, { childId }).catch(() => [] as FeedPostItem[]),
        apiListEvents(token, childId).catch(() => [] as EventListItem[]),
        apiChatListThreads(token).catch(() => [] as ChatThreadItem[]),
      ]);

      const important = posts.filter((p) => p.is_important && !p.my_confirmed);
      if (important.length > 0) {
        const first = important[0];
        items.push({
          kind: 'FEED_IMPORTANT',
          count: important.length,
          title: 'Ważne do przeczytania',
          preview: `${first.title} — ${first.body}`,
          ref_post_id: first.id,
        });
      }

      const pending = events.filter(
        (e) => e.type === 'MATCH' && (e.called_up ?? false) && (e.match_rsvp ?? 'PENDING') === 'PENDING' && e.can_match_rsvp !== false
      );
      if (pending.length > 0) {
        const first = pending[0];
        items.push({
          kind: 'MATCH_RSVP',
          count: pending.length,
          title: 'Mecz do potwierdzenia',
          subtitle: `${first.start_at} • ${first.location}`,
          preview: first.title,
          ref_event_id: first.id,
        });
      }

      const totalUnread = threads.reduce((acc, t) => acc + (t.unread_count || 0), 0);
      const firstUnread = threads.find((t) => t.unread_count > 0) ?? null;
      if (totalUnread > 0 && firstUnread) {
        items.push({
          kind: 'CHAT_UNREAD',
          count: totalUnread,
          title: 'Nowa wiadomość',
          preview: `${firstUnread.title} — ${firstUnread.last_message_preview ?? ''}`,
          ref_thread_id: firstUnread.id,
        });
      }
    }

    if (dev.role === 'COACH') {
      const threads = await apiChatListThreads(token);
      const totalUnread = threads.reduce((acc, t) => acc + (t.unread_count || 0), 0);
      const firstUnread = threads.find((t) => t.unread_count > 0) ?? null;
      if (totalUnread > 0 && firstUnread) {
        items.push({
          kind: 'CHAT_UNREAD',
          count: totalUnread,
          title: 'Nowa wiadomość',
          preview: `${firstUnread.title} — ${firstUnread.last_message_preview ?? ''}`,
          ref_thread_id: firstUnread.id,
        });
      }
    }

    const total = items.reduce((acc, i) => acc + (i.count > 0 ? i.count : 0), 0);
    return { total, items };
  }

  const qs = childId ? `?child_id=${encodeURIComponent(childId)}` : '';
  const res = await fetch(`/v1/notifications${qs}`, {
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const outputArray = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) {
    outputArray[i] = raw.charCodeAt(i);
  }
  return outputArray;
}

async function apiPushPublicKey(token: string): Promise<PushPublicKeyResponse> {
  const res = await fetch('/v1/push/public-key', {
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiPushSubscribe(token: string, body: { endpoint: string; keys: { p256dh: string; auth: string } }): Promise<{ ok: true }> {
  const res = await fetch('/v1/push/subscribe', {
    method: 'POST',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiPushUnsubscribe(token: string, body: { endpoint: string }): Promise<{ ok: true }> {
  const res = await fetch('/v1/push/unsubscribe', {
    method: 'POST',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiPushTest(token: string): Promise<{ ok: true }> {
  const res = await fetch('/v1/push/test', {
    method: 'POST',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiDevMkcertRootCa(token: string): Promise<Blob> {
  const res = await fetch('/v1/dev/mkcert/rootca', {
    headers: { Accept: 'application/x-pem-file', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.blob();
}

async function apiListEvents(token: string, childId?: string | null): Promise<EventListItem[]> {
  if (IS_DEV && devMeFromToken(token)) {
    return Object.values(DEV_EVENTS)
      .map((d) => toListItem(d))
      .sort((a, b) => a.start_at.localeCompare(b.start_at));
  }
  try {
    const qs = childId ? `?child_id=${encodeURIComponent(childId)}` : '';
    const res = await fetch(`/v1/events${qs}`, {
      headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(`HTTP_${res.status}`);
    return res.json();
  } catch (e) {
    if (IS_DEV) {
      return Object.values(DEV_EVENTS)
        .map((d) => toListItem(d))
        .sort((a, b) => a.start_at.localeCompare(b.start_at));
    }
    throw e;
  }
}

async function apiCoachListMyEvents(token: string): Promise<EventListItem[]> {
  if (IS_DEV && token === 'dev-coach') {
    return Object.values(DEV_EVENTS)
      .map((d) => toListItem(d))
      .sort((a, b) => a.start_at.localeCompare(b.start_at));
  }
  try {
    const res = await fetch('/v1/coach/events', {
      headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(`HTTP_${res.status}`);
    return res.json();
  } catch (e) {
    if (IS_DEV) {
      return Object.values(DEV_EVENTS)
        .map((d) => toListItem(d))
        .sort((a, b) => a.start_at.localeCompare(b.start_at));
    }
    throw e;
  }
}

async function apiCoachListChildEvents(token: string, childId: string): Promise<EventListItem[]> {
  if (IS_DEV && token === 'dev-coach') {
    return Object.values(DEV_EVENTS)
      .map((d) => toListItem(d))
      .sort((a, b) => a.start_at.localeCompare(b.start_at));
  }
  try {
    const res = await fetch(`/v1/coach/children/${encodeURIComponent(childId)}/events`, {
      headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(`HTTP_${res.status}`);
    return res.json();
  } catch (e) {
    if (IS_DEV) {
      return Object.values(DEV_EVENTS)
        .map((d) => toListItem(d))
        .sort((a, b) => a.start_at.localeCompare(b.start_at));
    }
    throw e;
  }
}

async function apiGetEvent(token: string, eventId: string, childId?: string | null): Promise<EventDetails> {
  if (IS_DEV && devMeFromToken(token)) {
    const d = DEV_EVENTS[eventId];
    if (!d) throw new Error('HTTP_404');
    return { ...d };
  }
  try {
    const qs = childId ? `?child_id=${encodeURIComponent(childId)}` : '';
    const res = await fetch(`/v1/events/${encodeURIComponent(eventId)}${qs}`, {
      headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(`HTTP_${res.status}`);
    return res.json();
  } catch (e) {
    if (IS_DEV) {
      const d = DEV_EVENTS[eventId];
      if (!d) throw new Error('HTTP_404');
      return { ...d };
    }
    throw e;
  }
}

async function apiOptOut(token: string, eventId: string, childId?: string | null): Promise<EventDetails> {
  if (IS_DEV && devMeFromToken(token)) {
    const d = DEV_EVENTS[eventId];
    if (!d) throw new Error('HTTP_404');
    DEV_EVENTS = { ...DEV_EVENTS, [eventId]: { ...d, attendance_status: 'OUT' } };
    return { ...DEV_EVENTS[eventId] };
  }
  try {
    const qs = childId ? `?child_id=${encodeURIComponent(childId)}` : '';
    const res = await fetch(`/v1/events/${encodeURIComponent(eventId)}/attendance/opt-out${qs}`, {
      method: 'POST',
      headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(`HTTP_${res.status}`);
    return res.json();
  } catch (e) {
    if (IS_DEV) {
      const d = DEV_EVENTS[eventId];
      if (!d) throw new Error('HTTP_404');
      DEV_EVENTS = { ...DEV_EVENTS, [eventId]: { ...d, attendance_status: 'OUT' } };
      return { ...DEV_EVENTS[eventId] };
    }
    throw e;
  }
}

async function apiOptIn(token: string, eventId: string, childId?: string | null): Promise<EventDetails> {
  if (IS_DEV && devMeFromToken(token)) {
    const d = DEV_EVENTS[eventId];
    if (!d) throw new Error('HTTP_404');
    DEV_EVENTS = { ...DEV_EVENTS, [eventId]: { ...d, attendance_status: 'IN' } };
    return { ...DEV_EVENTS[eventId] };
  }
  try {
    const qs = childId ? `?child_id=${encodeURIComponent(childId)}` : '';
    const res = await fetch(`/v1/events/${encodeURIComponent(eventId)}/attendance/opt-in${qs}`, {
      method: 'POST',
      headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(`HTTP_${res.status}`);
    return res.json();
  } catch (e) {
    if (IS_DEV) {
      const d = DEV_EVENTS[eventId];
      if (!d) throw new Error('HTTP_404');
      DEV_EVENTS = { ...DEV_EVENTS, [eventId]: { ...d, attendance_status: 'IN' } };
      return { ...DEV_EVENTS[eventId] };
    }
    throw e;
  }
}

async function apiMarkTrainingLate(token: string, eventId: string, childId?: string | null): Promise<EventDetails> {
  if (IS_DEV && devMeFromToken(token)) {
    const d = DEV_EVENTS[eventId];
    if (!d) throw new Error('HTTP_404');
    DEV_EVENTS = { ...DEV_EVENTS, [eventId]: { ...d, attendance_status: 'LATE' } };
    return { ...DEV_EVENTS[eventId] };
  }
  try {
    const qs = childId ? `?child_id=${encodeURIComponent(childId)}` : '';
    const res = await fetch(`/v1/events/${encodeURIComponent(eventId)}/attendance/late${qs}`, {
      method: 'POST',
      headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(`HTTP_${res.status}`);
    return res.json();
  } catch (e) {
    if (IS_DEV) {
      const d = DEV_EVENTS[eventId];
      if (!d) throw new Error('HTTP_404');
      DEV_EVENTS = { ...DEV_EVENTS, [eventId]: { ...d, attendance_status: 'LATE' } };
      return { ...DEV_EVENTS[eventId] };
    }
    throw e;
  }
}

async function apiMatchRsvpRequest(
  token: string,
  eventId: string,
  body: { response: 'ACCEPT' | 'DECLINE'; reason?: string },
  childId?: string | null
): Promise<EventDetails> {
  if (IS_DEV && devMeFromToken(token)) {
    const d = DEV_EVENTS[eventId];
    if (!d) throw new Error('HTTP_404');
    const nextRsvp: MatchRsvp = body.response === 'ACCEPT' ? 'ACCEPTED' : 'DECLINED';
    DEV_EVENTS = { ...DEV_EVENTS, [eventId]: { ...d, match_rsvp: nextRsvp } };
    return { ...DEV_EVENTS[eventId] };
  }
  const qs = childId ? `?child_id=${encodeURIComponent(childId)}` : '';
  const res = await fetch(`/v1/events/${encodeURIComponent(eventId)}/match/rsvp${qs}`, {
    method: 'POST',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiParentCreatePrivateTraining(
  token: string,
  body: { child_id: string; title?: string | null; start_at: string; end_at?: string | null; location?: string | null; description?: string | null }
): Promise<EventDetails> {
  const res = await fetch(`/v1/parent/private-trainings`, {
    method: 'POST',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiTransportGet(token: string, eventId: string, childId?: string | null): Promise<TransportResponse> {
  const qs = childId ? `?child_id=${encodeURIComponent(childId)}` : '';
  const res = await fetch(`/v1/events/${encodeURIComponent(eventId)}/transport${qs}`, {
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiTransportUpsertRequest(
  token: string,
  eventId: string,
  body: { child_id: string; note?: string | null },
  childId?: string | null
): Promise<TransportResponse> {
  const qs = childId ? `?child_id=${encodeURIComponent(childId)}` : '';
  const res = await fetch(`/v1/events/${encodeURIComponent(eventId)}/transport/requests${qs}`, {
    method: 'POST',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiTransportDeleteRequest(
  token: string,
  eventId: string,
  requestId: string,
  childId?: string | null
): Promise<TransportResponse> {
  const qs = childId ? `?child_id=${encodeURIComponent(childId)}` : '';
  const res = await fetch(`/v1/events/${encodeURIComponent(eventId)}/transport/requests/${encodeURIComponent(requestId)}${qs}`, {
    method: 'DELETE',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiTransportUpsertOffer(
  token: string,
  eventId: string,
  body: { seats_total: number; note?: string | null },
  childId?: string | null
): Promise<TransportResponse> {
  const qs = childId ? `?child_id=${encodeURIComponent(childId)}` : '';
  const res = await fetch(`/v1/events/${encodeURIComponent(eventId)}/transport/offers${qs}`, {
    method: 'POST',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiTransportDeleteOffer(token: string, eventId: string, offerId: string, childId?: string | null): Promise<TransportResponse> {
  const qs = childId ? `?child_id=${encodeURIComponent(childId)}` : '';
  const res = await fetch(`/v1/events/${encodeURIComponent(eventId)}/transport/offers/${encodeURIComponent(offerId)}${qs}`, {
    method: 'DELETE',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiTransportReserve(
  token: string,
  eventId: string,
  body: { offer_id: string; child_id: string },
  childId?: string | null
): Promise<TransportResponse> {
  const qs = childId ? `?child_id=${encodeURIComponent(childId)}` : '';
  const res = await fetch(`/v1/events/${encodeURIComponent(eventId)}/transport/reservations${qs}`, {
    method: 'POST',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiTransportCancelReservation(
  token: string,
  eventId: string,
  reservationId: string,
  childId?: string | null
): Promise<TransportResponse> {
  const qs = childId ? `?child_id=${encodeURIComponent(childId)}` : '';
  const res = await fetch(`/v1/events/${encodeURIComponent(eventId)}/transport/reservations/${encodeURIComponent(reservationId)}${qs}`, {
    method: 'DELETE',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiTransportTakeRequest(
  token: string,
  eventId: string,
  requestId: string,
  childId?: string | null
): Promise<TransportResponse> {
  const qs = childId ? `?child_id=${encodeURIComponent(childId)}` : '';
  const res = await fetch(`/v1/events/${encodeURIComponent(eventId)}/transport/requests/${encodeURIComponent(requestId)}/take${qs}`, {
    method: 'POST',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

type MatchGoalSide = 'HOME' | 'AWAY';

type MatchGoalBase = {
  team_side: MatchGoalSide;
  scorer_child_id?: string | null;
  scorer_display_name: string;
  minute?: number | null;
  is_own_goal?: boolean | null;
  is_penalty?: boolean | null;
  note?: string | null;
  assist_child_id?: string | null;
  assist_display_name?: string | null;
};

type MatchGoalItem = MatchGoalBase & {
  id: string;
  event_id: string;
  created_by_user_id: string;
  created_at: string;
};

async function apiSetMatchResult(
  token: string,
  eventId: string,
  body: { home_score?: number | null; away_score?: number | null; match_finalized?: boolean | null }
): Promise<EventDetails> {
  const res = await fetch(`/v1/matches/${encodeURIComponent(eventId)}/result`, {
    method: 'PUT',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

type LnpParseRequest = { csv_text?: string; source_kind?: 'CSV' | 'PASTE' };
type LnpParsedRowFE = {
  id?: string | null;
  date: string;
  opponent: string;
  score_home: number | null;
  score_away: number | null;
  side: 'HOME' | 'AWAY' | 'UNKNOWN';
  scorers?: string | null;
  status?: 'MATCHED' | 'NO_MATCH' | 'DUPLICATE' | 'ERROR' | 'PENDING';
  match_event_id?: string | null;
  note?: string | null;
};
type LnpParseResponse = {
  source: string;
  rows: LnpParsedRowFE[];
};
async function apiAdminLnpParse(token: string, body: LnpParseRequest): Promise<LnpParseResponse> {
  const res = await fetch('/v1/admin/lnp/parse', {
    method: 'POST',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}
type LnpApplyRequest = {
  row_ids: number[];
  rows: LnpParsedRowFE[];
  source_kind?: 'CSV' | 'PASTE';
  source_text?: string;
};
type LnpApplyResponse = {
  run_id: string;
  summary: {
    row_count: number;
    matched_count: number;
    created_count: number;
    skipped_count: number;
  };
  rows: Array<{
    idx: number;
    status: string;
    match_event_id?: string | null;
    note?: string | null;
  }>;
};
async function apiAdminLnpApply(token: string, body: LnpApplyRequest): Promise<LnpApplyResponse> {
  const res = await fetch('/v1/admin/lnp/apply', {
    method: 'POST',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}
async function apiAdminLnpHistory(token: string, limit = 50): Promise<any[]> {
  const url = `/v1/admin/lnp/history?limit=${encodeURIComponent(String(limit))}`;
  const res = await fetch(url, {
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiListMatchGoals(token: string, eventId: string): Promise<MatchGoalItem[]> {
  const res = await fetch(`/v1/matches/${encodeURIComponent(eventId)}/goals`, {
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiAddMatchGoal(
  token: string,
  eventId: string,
  body: MatchGoalBase
): Promise<MatchGoalItem[]> {
  const res = await fetch(`/v1/matches/${encodeURIComponent(eventId)}/goals`, {
    method: 'POST',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiUpdateMatchGoal(
  token: string,
  eventId: string,
  goalId: string,
  body: Partial<MatchGoalBase>
): Promise<MatchGoalItem[]> {
  const res = await fetch(`/v1/matches/${encodeURIComponent(eventId)}/goals/${encodeURIComponent(goalId)}`, {
    method: 'PUT',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiDeleteMatchGoal(token: string, eventId: string, goalId: string): Promise<MatchGoalItem[]> {
  const res = await fetch(`/v1/matches/${encodeURIComponent(eventId)}/goals/${encodeURIComponent(goalId)}`, {
    method: 'DELETE',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

type FitnessTestItem = {
  id: string;
  child_id: string;
  test_type: string;
  test_date: string;
  value: number;
  unit: string;
  note?: string | null;
  created_at: string;
  created_by_email?: string | null;
};

async function apiListFitnessTests(token: string, childId: string): Promise<FitnessTestItem[]> {
  const res = await fetch(`/v1/children/${encodeURIComponent(childId)}/fitness-tests`, {
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiCreateFitnessTest(
  token: string,
  childId: string,
  body: { test_type: string; test_date: string; value: number; unit: string; note?: string | null }
): Promise<FitnessTestItem> {
  const res = await fetch(`/v1/children/${encodeURIComponent(childId)}/fitness-tests`, {
    method: 'POST',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiUpdateFitnessTest(
  token: string,
  testId: string,
  body: { test_type: string; test_date: string; value: number; unit: string; note?: string | null }
): Promise<FitnessTestItem> {
  const res = await fetch(`/v1/fitness-tests/${encodeURIComponent(testId)}`, {
    method: 'PUT',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiDeleteFitnessTest(token: string, testId: string): Promise<{ ok: true }> {
  const res = await fetch(`/v1/fitness-tests/${encodeURIComponent(testId)}`, {
    method: 'DELETE',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiRegister(body: {
  email: string;
  password: string;
  role: 'PARENT' | 'COACH' | 'PLAYER';
  child_first_name?: string;
  child_last_initial?: string;
  child_birth_year?: number;
  group_id?: string;
}): Promise<TokenResponse> {
  const res = await fetch('/v1/auth/register', {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiPublicGroups(): Promise<GroupItem[]> {
  const res = await fetch('/v1/public/groups', {
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiLogin(email: string, password: string): Promise<TokenResponse> {
  if (IS_DEV) {
    const e = email.trim().toLowerCase();
    const p = password.trim();
    if (p === '123456789') {
      if (e === 'admin@demo.pl') return { token: 'dev-admin', status: 'ACTIVE', role: 'ADMIN' };
      if (e === 'koordynator@demo.pl') return { token: 'dev-coordinator', status: 'ACTIVE', role: 'COORDINATOR' };
      if (e === 'rodzic@demo.pl') return { token: 'dev-parent', status: 'ACTIVE', role: 'PARENT' };
      if (e === 'zawodnik@demo.pl') return { token: 'dev-player', status: 'ACTIVE', role: 'PLAYER' };
    }
  }
  const res = await fetch('/v1/auth/login', {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiAuthSwitchRole(token: string, role: Role): Promise<Me> {
  const res = await fetch('/v1/auth/switch-role', {
    method: 'POST',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ role }),
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return normalizeMe(await res.json());
}

async function apiAdminListUsers(
  token: string,
  args?: { status_filter?: string; q?: string; limit?: number; offset?: number } | null
): Promise<{ items: AdminUserItem[]; total: number | null }> {
  const qs = (() => {
    const sp = new URLSearchParams();
    if (args?.status_filter) sp.set('status_filter', args.status_filter);
    if (args?.q) sp.set('q', args.q);
    if (Number.isFinite(args?.limit as number)) sp.set('limit', String(args?.limit));
    if (Number.isFinite(args?.offset as number)) sp.set('offset', String(args?.offset));
    const s = sp.toString();
    return s ? `?${s}` : '';
  })();
  const res = await fetch(`/v1/admin/users${qs}`, {
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  const totalRaw = (res.headers.get('X-Total-Count') || '').trim();
  const totalNum = totalRaw ? Number.parseInt(totalRaw, 10) : NaN;
  const total = Number.isFinite(totalNum) && totalNum >= 0 ? totalNum : null;
  const items = (await res.json()) as AdminUserItem[];
  return { items, total };
}

async function apiAdminUsageUsers(token: string): Promise<AdminUsageUserItem[]> {
  const res = await fetch('/v1/admin/usage/users', {
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiAdminCreateUser(
  token: string,
  body: {
    email: string;
    role: Role;
    can_parent?: boolean;
    can_coach?: boolean;
    can_admin?: boolean;
    can_coordinator?: boolean;
    can_player?: boolean;
    status: 'ACTIVE' | 'PENDING';
    password?: string;
  }
): Promise<AdminCreateUserResponse> {
  const res = await fetch('/v1/admin/users', {
    method: 'POST',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiAdminDbBackup(token: string): Promise<{ blob: Blob; filename: string }> {
  const res = await fetch('/v1/admin/db/backup', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  const cd = res.headers.get('Content-Disposition') || '';
  const m = /filename="?([^";]+)"?/i.exec(cd);
  const filename = (m?.[1] || 'ac-backup.sqlite3').trim();
  const blob = await res.blob();
  return { blob, filename };
}

async function apiAdminAttendanceReportSamplePdf(token: string): Promise<{ blob: Blob; filename: string }> {
  const res = await fetch('/v1/admin/reports/attendance/sample.pdf', {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  const cd = res.headers.get('Content-Disposition') || '';
  const m = /filename="?([^";]+)"?/i.exec(cd);
  const filename = (m?.[1] || 'raport_frekwencji.pdf').trim();
  const blob = await res.blob();
  return { blob, filename };
}

type AttendanceReportRange = 'month' | 'quarter' | 'halfyear';
type AttendanceReportListItem = { id: string; group_id: string; range: AttendanceReportRange; year: number; month: number; created_at: string; filename: string };
type AttendanceReportBulkGenerateResult = { group_id: string; ok: boolean; report_id?: string | null; filename?: string | null; error?: string | null };
type AttendanceReportBulkGenerateResponse = {
  range: AttendanceReportRange;
  year: number;
  month: number;
  total_groups: number;
  ok_count: number;
  error_count: number;
  results: AttendanceReportBulkGenerateResult[];
};

async function apiAdminAttendanceReportPdf(
  token: string,
  args: { group_id: string; range: AttendanceReportRange; year: number; month: number }
): Promise<{ blob: Blob; filename: string; report_id?: string | null }> {
  const res = await fetch('/v1/admin/reports/attendance/generate.pdf', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(args),
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  const cd = res.headers.get('Content-Disposition') || '';
  const m = /filename="?([^";]+)"?/i.exec(cd);
  const filename = (m?.[1] || 'raport_frekwencji.pdf').trim();
  const report_id = res.headers.get('X-Report-Id');
  const blob = await res.blob();
  return { blob, filename, report_id };
}

async function apiAdminBulkGenerateAttendanceReports(
  token: string,
  args: { range?: AttendanceReportRange; year?: number; month?: number } = {}
): Promise<AttendanceReportBulkGenerateResponse> {
  const res = await fetch('/v1/admin/reports/attendance/generate-bulk', {
    method: 'POST',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(args),
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiAdminListAttendanceReports(token: string, groupId?: string): Promise<AttendanceReportListItem[]> {
  const qs = groupId ? `?group_id=${encodeURIComponent(groupId)}` : '';
  const res = await fetch(`/v1/admin/reports/attendance/list${qs}`, {
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiAdminDownloadAttendanceReport(token: string, reportId: string): Promise<{ blob: Blob; filename: string }> {
  const res = await fetch(`/v1/admin/reports/attendance/${encodeURIComponent(reportId)}.pdf`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  const cd = res.headers.get('Content-Disposition') || '';
  const m = /filename="?([^";]+)"?/i.exec(cd);
  const filename = (m?.[1] || 'raport_frekwencji.pdf').trim();
  const blob = await res.blob();
  return { blob, filename };
}

async function apiUserListAttendanceReports(token: string, groupId?: string): Promise<AttendanceReportListItem[]> {
  const qs = groupId ? `?group_id=${encodeURIComponent(groupId)}` : '';
  const res = await fetch(`/v1/user/attendance-reports${qs}`, {
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiUserDownloadAttendanceReport(token: string, reportId: string): Promise<{ blob: Blob; filename: string }> {
  const res = await fetch(`/v1/user/attendance-reports/${encodeURIComponent(reportId)}.pdf`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  const cd = res.headers.get('Content-Disposition') || '';
  const m = /filename="?([^";]+)"?/i.exec(cd);
  const filename = (m?.[1] || 'raport_frekwencji.pdf').trim();
  const blob = await res.blob();
  return { blob, filename };
}

type NotificationPrefs = {
  user_id?: string | null;
  silent_from: number;
  silent_until: number;
  channels: Record<string, boolean>;
  injury_alerts: 0 | 1;
};

async function apiUserGetNotificationPrefs(token: string): Promise<NotificationPrefs> {
  const res = await fetch('/v1/notification-prefs/me', {
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiUserPutNotificationPrefs(
  token: string,
  body: Partial<{
    silent_from: number;
    silent_until: number;
    channels: Record<string, boolean>;
    injury_alerts: 0 | 1;
  }>
): Promise<NotificationPrefs> {
  const res = await fetch('/v1/notification-prefs/me', {
    method: 'PUT',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiAdminDbResetStatsCalendar(
  token: string,
  body: { confirm: string; with_backup?: boolean | null }
): Promise<{ ok: boolean; backup_filename?: string | null }> {
  const res = await fetch('/v1/admin/db/reset-stats-calendar', {
    method: 'POST',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiMyGroups(token: string): Promise<GroupItem[]> {
  const demo = [{ id: 'demo-group', name: 'Demo U12', year: 2014, created_at: 'dev' }];
  if (IS_DEV && (token === 'dev-coach' || token === 'dev-admin' || token === 'dev-coordinator')) {
    return demo;
  }
  try {
    const res = await fetch('/v1/groups/me', {
      headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(`HTTP_${res.status}`);
    return res.json();
  } catch (e) {
    if (IS_DEV) return demo;
    throw e;
  }
}

async function apiCoachMyChildren(token: string): Promise<ChildItem[]> {
  const demo = [
    {
      id: 'dev:child:1',
      first_name: 'Jan',
      last_initial: 'K.',
      birth_year: 2014,
      group_id: 'demo-group',
      group_name: 'Demo U12',
      group_year: 2014,
    },
    {
      id: 'dev-child-2',
      first_name: 'Kacper',
      last_initial: 'N.',
      birth_year: 2014,
      group_id: 'demo-group',
      group_name: 'Demo U12',
      group_year: 2014,
    },
    {
      id: 'dev-child-3',
      first_name: 'Marek',
      last_initial: 'K.',
      birth_year: 2014,
      group_id: 'demo-group',
      group_name: 'Demo U12',
      group_year: 2014,
    },
  ];
  if (IS_DEV && token === 'dev-coach') {
    return demo;
  }
  try {
    const res = await fetch('/v1/coach/children', {
      headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(`HTTP_${res.status}`);
    return res.json();
  } catch (e) {
    if (IS_DEV) return demo;
    throw e;
  }
}

async function apiCoachCreateChild(
  token: string,
  body: { first_name: string; last_initial: string; birth_year?: number | null; shirt_number?: number | null }
): Promise<ChildItem[]> {
  if (IS_DEV && token === 'dev-coach') {
    return [];
  }
  const res = await fetch('/v1/coach/children', {
    method: 'POST',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiCoachRemoveChild(token: string, childId: string): Promise<ChildItem[]> {
  if (IS_DEV && token === 'dev-coach') {
    return [];
  }
  const res = await fetch(`/v1/coach/children/${encodeURIComponent(childId)}`, {
    method: 'DELETE',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiListOpponentTeams(token: string): Promise<OpponentTeamItem[]> {
  if (IS_DEV && devMeFromToken(token)) {
    return DEV_OPPONENT_TEAMS.slice();
  }
  const res = await fetch('/v1/opponent-teams', {
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiCoachCreateOpponentTeam(token: string, body: { name: string }): Promise<OpponentTeamItem> {
  if (IS_DEV && devMeFromToken(token)) {
    const normalize = (v: string) => v.trim().toLowerCase().replace(/\s+/g, ' ');
    const display = body.name.trim().replace(/\s+/g, ' ');
    const found = DEV_OPPONENT_TEAMS.find((t) => normalize(t.name) === normalize(display)) ?? null;
    if (found) return found;
    const next = { id: `dev:team:${normalize(display)}`, name: display, logo_url: null };
    DEV_OPPONENT_TEAMS = [...DEV_OPPONENT_TEAMS, next];
    return next;
  }
  const res = await fetch('/v1/coach/opponent-teams', {
    method: 'POST',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiCoachUpdateOpponentTeam(
  token: string,
  teamId: string,
  body: { name?: string | null; logo_url?: string | null }
): Promise<OpponentTeamItem> {
  if (IS_DEV && devMeFromToken(token)) {
    const id = teamId.trim();
    const normalize = (v: string) => v.trim().toLowerCase().replace(/\s+/g, ' ');
    const idx = DEV_OPPONENT_TEAMS.findIndex((t) => t.id === id);
    if (idx < 0) throw new Error('HTTP_404');
    const prev = DEV_OPPONENT_TEAMS[idx]!;
    const nextName = body.name !== undefined && body.name !== null ? body.name.trim().replace(/\s+/g, ' ') : prev.name;
    const nextLogo = body.logo_url !== undefined ? (body.logo_url && body.logo_url.trim() ? body.logo_url.trim() : null) : prev.logo_url ?? null;
    const dup = DEV_OPPONENT_TEAMS.find((t) => t.id !== id && normalize(t.name) === normalize(nextName)) ?? null;
    if (dup) throw new Error('HTTP_422');
    const next: OpponentTeamItem = { ...prev, name: nextName || prev.name, logo_url: nextLogo };
    DEV_OPPONENT_TEAMS = DEV_OPPONENT_TEAMS.map((t) => (t.id === id ? next : t));
    return next;
  }
  const res = await fetch(`/v1/coach/opponent-teams/${encodeURIComponent(teamId)}`, {
    method: 'PATCH',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiCoachDeleteOpponentTeam(token: string, teamId: string): Promise<void> {
  if (IS_DEV && devMeFromToken(token)) {
    const id = teamId.trim();
    DEV_OPPONENT_TEAMS = DEV_OPPONENT_TEAMS.filter((t) => t.id !== id);
    return;
  }
  const res = await fetch(`/v1/coach/opponent-teams/${encodeURIComponent(teamId)}`, {
    method: 'DELETE',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
}

async function apiCoachCreateMatch(
  token: string,
  body: {
    group_id: string;
    target_child_id?: string | null;
    match_kind?: MatchKind | null;
    title: string;
    opponent_team_id?: string | null;
    opponent_name?: string | null;
    is_tbd?: boolean | null;
    start_at: string;
    end_at?: string | null;
    match_meet_at?: string | null;
    location: string;
    description?: string | null;
    match_deadline_at?: string | null;
  }
): Promise<EventDetails> {
  if (IS_DEV && devMeFromToken(token)) {
    const id = `dev:event:match:${Date.now()}`;
    const coachIds = body.group_id === 'demo-group' ? ['dev:dev-coach'] : [];
    const targetChildId = (body.target_child_id ?? '').trim() || null;
    const d: EventDetails = {
      id,
      type: 'MATCH',
      status: 'ACTIVE',
      title: body.title,
      group_id: body.group_id,
      is_individual: !!targetChildId,
      target_child_id: targetChildId,
      target_child_label: targetChildId ? devChildLabel(targetChildId) : null,
      is_individual_coach_event: targetChildId ? true : null,
      opponent_name: body.opponent_name ?? null,
      is_tbd: body.is_tbd ?? false,
      match_kind: body.match_kind ?? null,
      match_meet_at: body.match_meet_at ?? null,
      start_at: body.start_at,
      end_at: body.end_at ?? null,
      location: body.location,
      description: body.description ?? null,
      can_take_action: true,
      match_rsvp: null,
      match_deadline_at: body.match_deadline_at ?? null,
      called_up_child_ids: targetChildId ? [targetChildId] : [],
      training_coaches: coachIds.map((cid) => ({
        id: cid,
        email: cid === 'dev:dev-coach' ? 'trener.robert@demo.pl' : cid,
        is_absent: false,
      })),
      can_mark_coach_absence: true,
      my_coach_absence: false,
    };
    DEV_EVENTS = { ...DEV_EVENTS, [id]: d };
    return { ...d };
  }
  const res = await fetch('/v1/coach/matches', {
    method: 'POST',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiCoachCreateTraining(
  token: string,
  body: {
    group_id: string;
    target_child_id?: string | null;
    title: string;
    start_at: string;
    end_at?: string | null;
    location: string;
    description?: string | null;
    coach_ids?: string[] | null;
    is_recurring?: boolean;
    interval_weeks?: number;
    occurrences?: number;
  }
): Promise<EventDetails> {
  if (IS_DEV && devMeFromToken(token)) {
    const id = `dev:event:training:${Date.now()}`;
    const coachIds = body.group_id === 'demo-group' ? ['dev:dev-coach'] : (body.coach_ids ?? []).filter(Boolean);
    const targetChildId = (body.target_child_id ?? '').trim() || null;
    const d: EventDetails = {
      id,
      type: 'TRAINING',
      status: 'ACTIVE',
      title: body.title,
      group_id: body.group_id,
      is_individual: !!targetChildId,
      target_child_id: targetChildId,
      target_child_label: targetChildId ? devChildLabel(targetChildId) : null,
      is_individual_coach_event: targetChildId ? true : null,
      start_at: body.start_at,
      end_at: body.end_at ?? null,
      location: body.location,
      description: body.description ?? null,
      can_take_action: true,
      attendance_status: 'IN',
      training_coaches: coachIds.map((cid, i) => ({
        id: cid,
        email: cid === 'dev:dev-coach' ? 'trener.robert@demo.pl' : cid,
        is_absent: false,
      })),
      can_mark_coach_absence: true,
      my_coach_absence: false,
    };
    DEV_EVENTS = { ...DEV_EVENTS, [id]: d };
    return { ...d };
  }
  const res = await fetch('/v1/coach/trainings', {
    method: 'POST',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let detail: string | null = null;
    try {
      const json = (await res.json()) as unknown;
      if (json && typeof json === 'object' && 'detail' in json) {
        const d = (json as { detail?: unknown }).detail;
        if (typeof d === 'string') detail = d;
        else if (Array.isArray(d)) {
          const parts: string[] = [];
          for (const it of d) {
            if (typeof it === 'string') {
              const t = it.trim();
              if (t) parts.push(t);
              continue;
            }
            if (!it || typeof it !== 'object') continue;
            const locRaw = (it as { loc?: unknown }).loc;
            const msgRaw = (it as { msg?: unknown }).msg;
            const loc = Array.isArray(locRaw) ? locRaw.map((x) => String(x)).filter(Boolean).join('.') : '';
            const msg = typeof msgRaw === 'string' ? msgRaw.trim() : '';
            const line = [loc, msg].filter(Boolean).join(': ');
            if (line) parts.push(line);
          }
          detail = parts.join(' • ') || null;
        } else if (d && typeof d === 'object') {
          const msg = 'msg' in d && typeof (d as { msg?: unknown }).msg === 'string' ? String((d as { msg: string }).msg).trim() : '';
          detail = msg || null;
        }
      }
    } catch {
      detail = null;
    }
    throw { message: `HTTP_${res.status}`, detail } as { message: string; detail: string | null };
  }
  return res.json();
}

async function apiCoachUpdateEvent(
  token: string,
  eventId: string,
  body: {
    title?: string;
    start_at?: string;
    end_at?: string | null;
    location?: string;
    description?: string | null;
    is_tbd?: boolean | null;
    match_deadline_at?: string | null;
    match_meet_at?: string | null;
    opponent_team_id?: string | null;
    opponent_name?: string | null;
    match_kind?: MatchKind | null;
    coach_ids?: string[] | null;
    apply_to_series?: boolean;
  }
): Promise<EventDetails> {
  if (IS_DEV && devMeFromToken(token)) {
    const d = DEV_EVENTS[eventId];
    if (!d) throw new Error('HTTP_404');
    if (d.status === 'CANCELLED') throw new Error('HTTP_403');
    const next: EventDetails = {
      ...d,
      title: body.title !== undefined ? body.title : d.title,
      opponent_name: body.opponent_name !== undefined ? body.opponent_name : d.opponent_name ?? null,
      is_tbd: body.is_tbd !== undefined ? !!body.is_tbd : d.is_tbd ?? false,
      match_kind: body.match_kind !== undefined ? body.match_kind : d.match_kind ?? null,
      start_at: body.start_at !== undefined ? body.start_at : d.start_at,
      end_at: body.end_at !== undefined ? body.end_at : d.end_at ?? null,
      location: body.location !== undefined ? body.location : d.location,
      description: body.description !== undefined ? body.description : d.description ?? null,
      match_deadline_at: body.match_deadline_at !== undefined ? body.match_deadline_at : d.match_deadline_at ?? null,
      match_meet_at: body.match_meet_at !== undefined ? body.match_meet_at : d.match_meet_at ?? null,
    };
    DEV_EVENTS = { ...DEV_EVENTS, [eventId]: next };
    return { ...next };
  }
  const res = await fetch(`/v1/coach/events/${encodeURIComponent(eventId)}`, {
    method: 'PATCH',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiCoachCancelEvent(token: string, eventId: string): Promise<EventDetails> {
  if (IS_DEV && devMeFromToken(token)) {
    const d = DEV_EVENTS[eventId];
    if (!d) throw new Error('HTTP_404');
    const next: EventDetails = { ...d, status: 'CANCELLED', can_take_action: false };
    DEV_EVENTS = { ...DEV_EVENTS, [eventId]: next };
    return { ...next };
  }
  const res = await fetch(`/v1/coach/events/${encodeURIComponent(eventId)}/cancel`, {
    method: 'POST',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiCoachDeleteEvent(token: string, eventId: string): Promise<{ deleted: true }> {
  if (IS_DEV && devMeFromToken(token)) {
    const d = DEV_EVENTS[eventId];
    if (!d) throw new Error('HTTP_404');
    const { [eventId]: _omit, ...rest } = DEV_EVENTS;
    DEV_EVENTS = rest;
    return { deleted: true };
  }
  const res = await fetch(`/v1/coach/events/${encodeURIComponent(eventId)}`, {
    method: 'DELETE',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiCoachRepublishEventToFeed(token: string, eventId: string): Promise<{ ok: true; post_id: string }> {
  if (IS_DEV && devMeFromToken(token)) {
    return { ok: true, post_id: `dev-feed-republish-${eventId}` };
  }
  const res = await fetch(`/v1/coach/events/${encodeURIComponent(eventId)}/republish`, {
    method: 'POST',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiCoachSetMyEventAbsence(token: string, eventId: string, isAbsent: boolean): Promise<EventDetails> {
  if (IS_DEV && devMeFromToken(token)) {
    const d = DEV_EVENTS[eventId];
    if (!d) throw new Error('HTTP_404');
    const me = devMeFromToken(token);
    if (!me) throw new Error('HTTP_403');
    const list = d.training_coaches ?? [];
    const nextList = list.map((c) => (c.id === me.id ? { ...c, is_absent: isAbsent } : c));
    const hasMe = nextList.some((c) => c.id === me.id);
    if (!hasMe) throw new Error('HTTP_404');
    const next: EventDetails = {
      ...d,
      training_coaches: nextList,
      can_mark_coach_absence: true,
      my_coach_absence: isAbsent,
    };
    DEV_EVENTS = { ...DEV_EVENTS, [eventId]: next };
    return { ...next };
  }
  const res = await fetch(`/v1/coach/events/${encodeURIComponent(eventId)}/my-absence`, {
    method: 'POST',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ is_absent: isAbsent }),
  });
  if (res.status === 404) {
    const res2 = await fetch(`/v1/coach/trainings/${encodeURIComponent(eventId)}/my-absence`, {
      method: 'POST',
      headers: { Accept: 'application/json', Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ is_absent: isAbsent }),
    });
    if (!res2.ok) throw new Error(`HTTP_${res2.status}`);
    return res2.json();
  }
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiCoachSetMatchCallups(token: string, eventId: string, childIds: string[]): Promise<EventDetails> {
  if (IS_DEV && devMeFromToken(token)) {
    const d = DEV_EVENTS[eventId];
    if (!d) throw new Error('HTTP_404');
    const next: EventDetails = { ...d, called_up_child_ids: [...childIds] };
    DEV_EVENTS = { ...DEV_EVENTS, [eventId]: next };
    return { ...next };
  }
  const res = await fetch(`/v1/coach/matches/${encodeURIComponent(eventId)}/callups`, {
    method: 'POST',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ child_ids: childIds }),
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiAdminApproveUser(
  token: string,
  userId: string,
  body?: { role?: Role; can_parent?: boolean; can_coach?: boolean; can_admin?: boolean; can_coordinator?: boolean; can_player?: boolean }
): Promise<AdminUserItem> {
  const res = await fetch(`/v1/admin/users/${encodeURIComponent(userId)}/approve`, {
    method: 'POST',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiAdminUpdateUser(
  token: string,
  userId: string,
  body: {
    role?: Role;
    can_parent?: boolean;
    can_coach?: boolean;
    can_admin?: boolean;
    can_coordinator?: boolean;
    can_player?: boolean;
    status?: 'ACTIVE' | 'PENDING' | 'REJECTED';
  }
): Promise<AdminUserItem> {
  const res = await fetch(`/v1/admin/users/${encodeURIComponent(userId)}/update`, {
    method: 'POST',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiAdminSetUserEmail(token: string, userId: string, email: string): Promise<AdminUserItem> {
  const res = await fetch(`/v1/admin/users/${encodeURIComponent(userId)}/set-email`, {
    method: 'POST',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiAdminDeleteUser(token: string, userId: string): Promise<{ deleted: true }> {
  const res = await fetch(`/v1/admin/users/${encodeURIComponent(userId)}`, {
    method: 'DELETE',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiAdminResetPassword(
  token: string,
  userId: string,
  newPassword?: string
): Promise<{ ok: boolean; password: string; tokens_cleared: boolean; user_id: string }> {
  const res = await fetch(`/v1/admin/users/${encodeURIComponent(userId)}/reset-password`, {
    method: 'POST',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ new_password: newPassword || null }),
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiMyChildren(token: string): Promise<ChildItem[]> {
  if (IS_DEV && (token === 'dev-parent' || token === 'dev-player')) {
    return DEV_CHILDREN;
  }
  try {
    const res = await fetch('/v1/children', {
      headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(`HTTP_${res.status}`);
    return res.json();
  } catch (e) {
    if (IS_DEV) return DEV_CHILDREN.slice();
    throw e;
  }
}

async function apiSetChildUnavailability(
  token: string,
  childId: string,
  body: { reason: 'CHOROBA' | 'KONTUZJA' | 'INNE'; from_date: string; until_date: string; note?: string }
): Promise<ChildItem> {
  if (IS_DEV && token === 'dev-parent') {
    const idx = DEV_CHILDREN.findIndex((c) => c.id === childId);
    if (idx < 0) throw new Error('HTTP_404');
    const kind: ChildItem['unavailability_kind'] = body.reason === 'KONTUZJA' ? 'KONTUZJA' : 'NB';
    DEV_CHILDREN[idx] = {
      ...DEV_CHILDREN[idx],
      unavailability_kind: kind,
      unavailability_reason: body.reason,
      unavailability_from: body.from_date,
      unavailability_until: body.until_date,
      unavailability_note: (body.note ?? '').trim() || null,
    };
    return DEV_CHILDREN[idx];
  }
  const res = await fetch(`/v1/children/${encodeURIComponent(childId)}/unavailability`, {
    method: 'POST',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiClearChildUnavailability(token: string, childId: string): Promise<ChildItem> {
  if (IS_DEV && token === 'dev-parent') {
    const idx = DEV_CHILDREN.findIndex((c) => c.id === childId);
    if (idx < 0) throw new Error('HTTP_404');
    DEV_CHILDREN[idx] = {
      ...DEV_CHILDREN[idx],
      unavailability_kind: null,
      unavailability_reason: null,
      unavailability_from: null,
      unavailability_until: null,
      unavailability_note: null,
    };
    return DEV_CHILDREN[idx];
  }
  const res = await fetch(`/v1/children/${encodeURIComponent(childId)}/unavailability/clear`, {
    method: 'POST',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiGetChildCoachNote(token: string, childId: string): Promise<ChildCoachNoteResponse> {
  if (IS_DEV && devMeFromToken(token)) {
    const k = `ac:childcoachnote:${childId}`;
    const raw = localStorage.getItem(k);
    if (!raw) return { child_id: childId, text: null, visibility: null, updated_at: null };
    try {
      const parsed = JSON.parse(raw) as { text?: unknown; visibility?: unknown; updated_at?: unknown };
      const text = typeof parsed.text === 'string' ? parsed.text : null;
      const visibility: CoachNoteVisibility | null = parsed.visibility === 'PARENT' || parsed.visibility === 'PRIVATE' ? parsed.visibility : null;
      const updated_at = typeof parsed.updated_at === 'string' ? parsed.updated_at : null;
      return { child_id: childId, text, visibility, updated_at };
    } catch {
      return { child_id: childId, text: null, visibility: null, updated_at: null };
    }
  }
  const res = await fetch(`/v1/children/${encodeURIComponent(childId)}/coach-note`, {
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiUpsertChildCoachNote(
  token: string,
  childId: string,
  body: { text?: string | null; visibility?: CoachNoteVisibility | null }
): Promise<ChildCoachNoteResponse> {
  if (IS_DEV && devMeFromToken(token)) {
    const nextText = (body.text ?? '').trim();
    if (!nextText) {
      localStorage.removeItem(`ac:childcoachnote:${childId}`);
      return { child_id: childId, text: null, visibility: null, updated_at: null };
    }
    const nextVis: CoachNoteVisibility = body.visibility === 'PARENT' ? 'PARENT' : 'PRIVATE';
    const updatedAt = new Date().toISOString();
    localStorage.setItem(
      `ac:childcoachnote:${childId}`,
      JSON.stringify({ text: nextText, visibility: nextVis, updated_at: updatedAt })
    );
    return { child_id: childId, text: nextText, visibility: nextVis, updated_at: updatedAt };
  }
  const res = await fetch(`/v1/children/${encodeURIComponent(childId)}/coach-note`, {
    method: 'PATCH',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiGetQuickReactions(token: string, childId: string): Promise<QuickReactionsResponse> {
  if (IS_DEV && devMeFromToken(token)) {
    const raw = localStorage.getItem(`ac:quickreactions:${childId}`);
    if (!raw) return { ttl_days: 7, items: [] };
    try {
      const parsed = JSON.parse(raw) as { ttl_days?: unknown; items?: unknown };
      const ttl_days = typeof parsed.ttl_days === 'number' ? parsed.ttl_days : 7;
      const items = Array.isArray(parsed.items) ? (parsed.items as QuickReactionItem[]) : [];
      return { ttl_days, items };
    } catch {
      return { ttl_days: 7, items: [] };
    }
  }
  const res = await fetch(`/v1/children/${encodeURIComponent(childId)}/quick-reactions`, {
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiHealth(token?: string): Promise<{ status: string; build?: string | null }> {
  const res = await fetch('/health', {
    headers: { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiCreateQuickReaction(
  token: string,
  childId: string,
  text: string,
  visibility?: QuickReactionVisibility
): Promise<QuickReactionsResponse> {
  if (IS_DEV && devMeFromToken(token)) {
    const now = new Date();
    const ttl = 7;
    const expires = new Date(now.getTime() + ttl * 24 * 60 * 60 * 1000).toISOString();
    const current = await apiGetQuickReactions(token, childId);
    const next: QuickReactionsResponse = {
      ttl_days: current.ttl_days,
      items: [{ id: `dev:qr:${Date.now()}`, text, created_at: now.toISOString(), expires_at: expires }, ...current.items].slice(0, 30),
    };
    localStorage.setItem(`ac:quickreactions:${childId}`, JSON.stringify(next));
    return next;
  }
  const res = await fetch(`/v1/children/${encodeURIComponent(childId)}/quick-reactions`, {
    method: 'POST',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ text, ...(visibility ? { visibility } : {}) }),
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiDeleteQuickReaction(token: string, childId: string, reactionId: string): Promise<QuickReactionsResponse> {
  if (IS_DEV && devMeFromToken(token)) {
    const current = await apiGetQuickReactions(token, childId);
    const next: QuickReactionsResponse = {
      ttl_days: current.ttl_days,
      items: current.items.filter((x) => x.id !== reactionId),
    };
    localStorage.setItem(`ac:quickreactions:${childId}`, JSON.stringify(next));
    return next;
  }
  const res = await fetch(`/v1/children/${encodeURIComponent(childId)}/quick-reactions/${encodeURIComponent(reactionId)}`, {
    method: 'DELETE',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiAdminGetQuickReactionsConfig(token: string): Promise<QuickReactionsConfigResponse> {
  if (IS_DEV && devMeFromToken(token)) {
    const raw = localStorage.getItem('ac:quickreactions:config');
    if (!raw) return { ttl_days: 7 };
    try {
      const parsed = JSON.parse(raw) as { ttl_days?: unknown };
      const ttl_days = typeof parsed.ttl_days === 'number' ? parsed.ttl_days : 7;
      return { ttl_days };
    } catch {
      return { ttl_days: 7 };
    }
  }
  const res = await fetch('/v1/admin/quick-reactions/config', {
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiAdminSetQuickReactionsConfig(token: string, ttlDays: number): Promise<QuickReactionsConfigResponse> {
  if (IS_DEV && devMeFromToken(token)) {
    const next = { ttl_days: ttlDays };
    localStorage.setItem('ac:quickreactions:config', JSON.stringify(next));
    return next;
  }
  const res = await fetch('/v1/admin/quick-reactions/config', {
    method: 'PATCH',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ ttl_days: ttlDays }),
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiGroupChildren(token: string, groupId: string, childId?: string | null): Promise<ChildItem[]> {
  const demo = [
    {
      id: 'dev:child:1',
      first_name: 'Jan',
      last_initial: 'K.',
      birth_year: 2014,
      group_id: 'demo-group',
      group_name: 'Demo U12',
      group_year: 2014,
    },
    {
      id: 'dev-child-2',
      first_name: 'Kacper',
      last_initial: 'N.',
      birth_year: 2014,
      group_id: 'demo-group',
      group_name: 'Demo U12',
      group_year: 2014,
    },
    {
      id: 'dev-child-3',
      first_name: 'Marek',
      last_initial: 'K.',
      birth_year: 2014,
      group_id: 'demo-group',
      group_name: 'Demo U12',
      group_year: 2014,
    },
  ];
  if (IS_DEV && devMeFromToken(token) && groupId === 'demo-group') {
    return demo;
  }
  try {
    const qs = childId ? `?child_id=${encodeURIComponent(childId)}` : '';
    const res = await fetch(`/v1/groups/${encodeURIComponent(groupId)}/children${qs}`, {
      headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(`HTTP_${res.status}`);
    return res.json();
  } catch (e) {
    if (IS_DEV) return demo;
    throw e;
  }
}

async function apiGetChildPhoto(token: string, childId: string): Promise<ChildPhotoResponse> {
  if (IS_DEV && devMeFromToken(token)) {
    const k = `ac:childphoto:${childId}`;
    const dataUrl = localStorage.getItem(k);
    return { child_id: childId, data_url: dataUrl ? dataUrl : null, updated_at: null };
  }
  const res = await fetch(`/v1/children/${encodeURIComponent(childId)}/photo`, {
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiSetChildPhoto(token: string, childId: string, file: File): Promise<ChildPhotoResponse> {
  if (IS_DEV && devMeFromToken(token)) {
    const dataUrl: string = await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result || ''));
      r.onerror = () => reject(new Error('FILE_READ'));
      r.readAsDataURL(file);
    });
    localStorage.setItem(`ac:childphoto:${childId}`, dataUrl);
    return { child_id: childId, data_url: dataUrl, updated_at: new Date().toISOString() };
  }
  const fd = new FormData();
  fd.set('file', file);
  const res = await fetch(`/v1/children/${encodeURIComponent(childId)}/photo`, {
    method: 'POST',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
    body: fd,
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiClearChildPhoto(token: string, childId: string): Promise<{ ok: true }> {
  if (IS_DEV && devMeFromToken(token)) {
    localStorage.removeItem(`ac:childphoto:${childId}`);
    return { ok: true };
  }
  const res = await fetch(`/v1/children/${encodeURIComponent(childId)}/photo`, {
    method: 'DELETE',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result || ''));
    r.onerror = () => reject(new Error('FILE_READ'));
    r.readAsDataURL(file);
  });
}

async function fileToImageEl(file: File): Promise<{ img: HTMLImageElement; objectUrl: string }> {
  const objectUrl = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.src = objectUrl;
    try {
      await img.decode();
    } catch {
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error('IMAGE_DECODE'));
      });
    }
    return { img, objectUrl };
  } catch (e) {
    URL.revokeObjectURL(objectUrl);
    throw e;
  }
}

async function canvasToJpegFile(
  canvas: HTMLCanvasElement,
  params: { quality: number; name: string }
): Promise<File> {
  const blob: Blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => {
        if (!b) reject(new Error('TO_BLOB'));
        else resolve(b);
      },
      'image/jpeg',
      params.quality
    );
  });
  return new File([blob], params.name, { type: 'image/jpeg' });
}

async function cropAndCompressProfilePhoto(params: {
  file: File;
  crop: { offsetX: number; offsetY: number; zoom: number; viewportPx: number };
  targetMaxBytes: number;
}): Promise<File> {
  const { img, objectUrl } = await fileToImageEl(params.file);
  try {
    const iw = img.naturalWidth || img.width;
    const ih = img.naturalHeight || img.height;
    if (!iw || !ih) throw new Error('IMAGE_DIM');

    const viewport = params.crop.viewportPx;
    const baseScale = Math.max(viewport / iw, viewport / ih);
    const displayedW = iw * baseScale * params.crop.zoom;
    const displayedH = ih * baseScale * params.crop.zoom;

    const x0 = viewport / 2 - displayedW / 2 + params.crop.offsetX;
    const y0 = viewport / 2 - displayedH / 2 + params.crop.offsetY;

    const sx = ((0 - x0) * iw) / displayedW;
    const sy = ((0 - y0) * ih) / displayedH;
    const sw = (viewport * iw) / displayedW;
    const sh = (viewport * ih) / displayedH;

    const sizes = [512, 448, 384, 320];
    const qualities = [0.9, 0.84, 0.78, 0.72, 0.66, 0.6, 0.54, 0.48, 0.42];

    for (const size of sizes) {
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('NO_CTX');
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, size, size);

      for (const q of qualities) {
        const out = await canvasToJpegFile(canvas, { quality: q, name: 'photo.jpg' });
        if (out.size <= params.targetMaxBytes) return out;
      }
    }

    const fallbackCanvas = document.createElement('canvas');
    fallbackCanvas.width = 256;
    fallbackCanvas.height = 256;
    const fctx = fallbackCanvas.getContext('2d');
    if (!fctx) throw new Error('NO_CTX');
    fctx.imageSmoothingEnabled = true;
    fctx.imageSmoothingQuality = 'high';
    fctx.drawImage(img, sx, sy, sw, sh, 0, 0, 256, 256);
    return canvasToJpegFile(fallbackCanvas, { quality: 0.5, name: 'photo.jpg' });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function safeJpegNameFromFileName(name: string): string {
  const raw = (name || '').trim();
  const cleaned = raw.replace(/[/\\]+/g, '_').replace(/["<>:|?*\u0000-\u001F]+/g, '_').trim();
  const dotIdx = cleaned.lastIndexOf('.');
  const base = (dotIdx > 0 ? cleaned.slice(0, dotIdx) : cleaned).trim() || 'zdjecie';
  const trimmed = base.length > 140 ? base.slice(0, 140).trim() : base;
  return `${trimmed || 'zdjecie'}.jpg`;
}

function safeFileNameFromFileName(name: string, maxLen: number): string {
  const raw = (name || '').trim();
  const cleaned = raw.replace(/[/\\]+/g, '_').replace(/["<>:|?*\u0000-\u001F]+/g, '_').trim() || 'plik';
  if (cleaned.length <= maxLen) return cleaned;
  const dotIdx = cleaned.lastIndexOf('.');
  const ext = dotIdx > 0 ? cleaned.slice(dotIdx).trim() : '';
  const base = (dotIdx > 0 ? cleaned.slice(0, dotIdx) : cleaned).trim();
  const keep = Math.max(1, maxLen - ext.length);
  return `${base.slice(0, keep).trim() || 'plik'}${ext}`;
}

function isLikelyImageFile(file: File): boolean {
  const t = String(file.type || '').toLowerCase();
  if (t.startsWith('image/')) return true;
  const n = String(file.name || '').toLowerCase();
  return /\.(jpe?g|png|webp|gif|heic|heif)$/i.test(n);
}

async function compressImageToJpegUnderBytes(params: { file: File; targetMaxBytes: number; outName: string }): Promise<File> {
  const { img, objectUrl } = await fileToImageEl(params.file);
  try {
    const iw = img.naturalWidth || img.width;
    const ih = img.naturalHeight || img.height;
    if (!iw || !ih) throw new Error('IMAGE_DIM');

    const maxSides = [1920, 1600, 1280, 1024, 800, 640, 512, 448, 384];
    const qualities = [0.9, 0.84, 0.78, 0.72, 0.66, 0.6, 0.54, 0.48, 0.42, 0.36];

    for (const maxSide of maxSides) {
      const scale = Math.min(1, maxSide / Math.max(iw, ih));
      const w = Math.max(1, Math.round(iw * scale));
      const h = Math.max(1, Math.round(ih * scale));

      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('NO_CTX');
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, w, h);

      for (const q of qualities) {
        const out = await canvasToJpegFile(canvas, { quality: q, name: params.outName });
        if (out.size <= params.targetMaxBytes) return out;
      }
    }

    const fallbackCanvas = document.createElement('canvas');
    const fctx = fallbackCanvas.getContext('2d');
    if (!fctx) throw new Error('NO_CTX');
    fctx.imageSmoothingEnabled = true;
    fctx.imageSmoothingQuality = 'high';
    const scale = Math.min(1, 320 / Math.max(iw, ih));
    const w = Math.max(1, Math.round(iw * scale));
    const h = Math.max(1, Math.round(ih * scale));
    fallbackCanvas.width = w;
    fallbackCanvas.height = h;
    fctx.drawImage(img, 0, 0, w, h);
    return canvasToJpegFile(fallbackCanvas, { quality: 0.5, name: params.outName });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

async function normalizeFeedAttachmentFile(file: File): Promise<File> {
  const maxBytes = 2_000_000;
  const type = String(file.type || '').toLowerCase();
  const allowed = new Set(['image/jpeg', 'image/png', 'image/webp', 'application/pdf', 'text/plain']);

  const nameOk = String(file.name || '').length <= 160;
  if (!isLikelyImageFile(file)) {
    if (file.size > maxBytes) throw new Error('FEED_FILE_TOO_LARGE');
    if (!allowed.has(type)) throw new Error('FEED_FILE_UNSUPPORTED');
    if (nameOk) return file;
    const safe = safeFileNameFromFileName(file.name || 'plik', 160);
    return new File([file], safe, { type: file.type || 'application/octet-stream' });
  }

  if (file.size > 0 && file.size <= maxBytes && nameOk && allowed.has(type) && type === 'image/jpeg') {
    return file;
  }

  try {
    const outName = safeJpegNameFromFileName(file.name || 'zdjecie.jpg');
    const jpg = await compressImageToJpegUnderBytes({ file, targetMaxBytes: 1_900_000, outName });
    if (jpg.size > maxBytes) throw new Error('FEED_FILE_TOO_LARGE');
    return jpg;
  } catch (e) {
    if (e instanceof Error && e.message === 'FEED_FILE_TOO_LARGE') throw e;
    throw new Error('FEED_IMAGE_UNSUPPORTED');
  }
}

async function apiGroupCoaches(token: string, groupId: string, childId?: string | null): Promise<CoachItem[]> {
  if (IS_DEV && devMeFromToken(token) && groupId === 'demo-group') {
    return [{ id: 'dev:dev-coach', email: 'trener.robert@demo.pl' }];
  }
  const qs = childId ? `?child_id=${encodeURIComponent(childId)}` : '';
  const res = await fetch(`/v1/groups/${encodeURIComponent(groupId)}/coaches${qs}`, {
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiFeedList(token: string, params: { groupId?: string | null; childId?: string | null }): Promise<FeedPostItem[]> {
  if (IS_DEV && devMeFromToken(token)) {
    return DEV_FEED.slice();
  }
  try {
    const sp = new URLSearchParams();
    if (params.groupId) sp.set('group_id', params.groupId);
    if (params.childId) sp.set('child_id', params.childId);
    const qs = sp.toString() ? `?${sp.toString()}` : '';
    const res = await fetch(`/v1/feed${qs}`, {
      headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(`HTTP_${res.status}`);
    return res.json();
  } catch (e) {
    if (IS_DEV) return DEV_FEED.slice();
    throw e;
  }
}

async function apiFeedCreate(
  token: string,
  body: { group_id: string; category: string; title: string; body: string; is_important?: boolean | null }
): Promise<FeedPostItem> {
  const res = await fetch('/v1/feed', {
    method: 'POST',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiFeedUpdate(
  token: string,
  postId: string,
  body: { category: string; title: string; body: string; is_important?: boolean | null }
): Promise<FeedPostItem> {
  const res = await fetch(`/v1/feed/${encodeURIComponent(postId)}/update`, {
    method: 'POST',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiFeedAddLink(
  token: string,
  postId: string,
  body: { url: string; title?: string | null }
): Promise<FeedAttachmentItem[]> {
  const res = await fetch(`/v1/feed/${encodeURIComponent(postId)}/links`, {
    method: 'POST',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiFeedAddAttachment(token: string, postId: string, file: File): Promise<FeedAttachmentItem[]> {
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch(`/v1/feed/${encodeURIComponent(postId)}/attachments`, {
    method: 'POST',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
    body: fd,
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiFeedDownloadAttachment(
  token: string,
  attachmentId: string,
  childId?: string | null
): Promise<{ blob: Blob; fileName: string | null }> {
  const qs = childId ? `?child_id=${encodeURIComponent(childId)}` : '';
  const res = await fetch(`/v1/feed/attachments/${encodeURIComponent(attachmentId)}/download${qs}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  const cd = res.headers.get('content-disposition') || '';
  const m = /filename="([^"]+)"/i.exec(cd);
  const name = m && m[1] ? m[1] : null;
  const blob = await res.blob();
  return { blob, fileName: name };
}

async function apiFeedDeleteAttachment(token: string, attachmentId: string): Promise<{ ok: true }> {
  const res = await fetch(`/v1/feed/attachments/${encodeURIComponent(attachmentId)}`, {
    method: 'DELETE',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiFeedDelete(token: string, postId: string): Promise<{ ok: true }> {
  const res = await fetch(`/v1/feed/${encodeURIComponent(postId)}`, {
    method: 'DELETE',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiFeedConfirm(token: string, postId: string, childId?: string | null): Promise<{ ok: true }> {
  if (IS_DEV && devMeFromToken(token)) {
    DEV_FEED = DEV_FEED.map((p) => {
      if (p.id !== postId) return p;
      const next: FeedPostItem = {
        ...p,
        my_confirmed: true,
        confirms_count: p.confirms_count + 1,
      };
      return next;
    });
    return { ok: true };
  }
  const qs = childId ? `?child_id=${encodeURIComponent(childId)}` : '';
  const res = await fetch(`/v1/feed/${encodeURIComponent(postId)}/confirm${qs}`, {
    method: 'POST',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiFeedReact(token: string, postId: string, emoji: string, childId?: string | null): Promise<{ ok: true }> {
  if (IS_DEV && devMeFromToken(token)) {
    DEV_FEED = DEV_FEED.map((p) => {
      if (p.id !== postId) return p;
      const current: FeedReactionSummary[] = p.reactions ? p.reactions.slice() : [];
      const idx = current.findIndex((r) => r.emoji === emoji);
      let next: FeedReactionSummary[];
      if (idx >= 0) {
        const existing = current[idx];
        if (existing.is_me) {
          const c = existing.count - 1;
          if (c <= 0) {
            next = current.filter((_, i) => i !== idx);
          } else {
            next = current.slice();
            next[idx] = { ...existing, count: c, is_me: false };
          }
        } else {
          next = current.slice();
          next[idx] = { ...existing, count: existing.count + 1, is_me: true };
        }
      } else {
        next = current.concat([{ emoji, count: 1, is_me: true }]);
      }
      return { ...p, reactions: next };
    });
    return { ok: true };
  }
  const qs = childId ? `?child_id=${encodeURIComponent(childId)}` : '';
  const res = await fetch(`/v1/feed/${encodeURIComponent(postId)}/react${qs}`, {
    method: 'POST',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ emoji }),
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiLoungeListMessages(token: string, params: { groupId?: string | null; childId?: string | null }): Promise<LoungeMessageItem[]> {
  const sp = new URLSearchParams();
  if (params.groupId) sp.set('group_id', params.groupId);
  if (params.childId) sp.set('child_id', params.childId);
  const qs = sp.toString() ? `?${sp.toString()}` : '';
  const res = await fetch(`/v1/lounge/messages${qs}`, {
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiLoungeSendMessage(
  token: string,
  body: { group_id: string; body: string },
  childId?: string | null
): Promise<LoungeMessageItem> {
  const qs = childId ? `?child_id=${encodeURIComponent(childId)}` : '';
  const res = await fetch(`/v1/lounge/messages${qs}`, {
    method: 'POST',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

function devChatEmail(userId: string): string {
  if (userId === 'dev:dev-coach') return 'trener.robert@demo.pl';
  if (userId === 'dev:dev-parent') return 'rodzic@demo.pl';
  if (userId === 'dev:dev-admin') return 'admin@demo.pl';
  if (userId === 'dev:dev-coordinator') return 'koordynator@demo.pl';
  return userId;
}

function devChatThreadToItem(t: DevChatThread, meId: string, role: Role): ChatThreadItem {
  const participantsEmails = t.participant_ids.map(devChatEmail).sort((a, b) => a.localeCompare(b, 'pl'));
  const title =
    role === 'ADMIN' || role === 'COORDINATOR'
      ? participantsEmails.join(' ↔ ')
      : devChatEmail(t.participant_ids.find((id) => id !== meId) ?? t.participant_ids[0] ?? 'Wątek');
  const last = t.messages[t.messages.length - 1];
  const lastReadAt = t.reads[meId] ?? '';
  const unread =
    role === 'ADMIN' || role === 'COORDINATOR'
      ? 0
      : t.messages.filter((m) => m.sender_user_id !== meId && (!lastReadAt || m.created_at > lastReadAt)).length;
  return {
    id: t.id,
    title,
    child_id: t.child_id,
    child_label: devChildLabel(t.child_id),
    last_message_at: last?.created_at ?? null,
    last_message_preview: last?.body ? last.body.slice(0, 80) : null,
    unread_count: unread,
  };
}

async function apiChatTargets(token: string, childId?: string | null): Promise<ChatTargetItem[]> {
  const dev = devMeFromToken(token);
  if (IS_DEV && dev) {
    if (dev.role === 'PARENT') {
      if (!childId) return [];
      return [
        {
          recipient_user_id: 'dev:dev-coach',
          recipient_email: devChatEmail('dev:dev-coach'),
          child_id: childId,
          child_label: devChildLabel(childId),
        },
      ];
    }
    if (dev.role === 'COACH') {
      return [
        {
          recipient_user_id: 'dev:dev-parent',
          recipient_email: devChatEmail('dev:dev-parent'),
          child_id: 'dev:child:1',
          child_label: devChildLabel('dev:child:1'),
        },
      ];
    }
    if (dev.role === 'ADMIN') {
      return [
        { recipient_user_id: 'dev:dev-coach', recipient_email: devChatEmail('dev:dev-coach') },
        { recipient_user_id: 'dev:dev-parent', recipient_email: devChatEmail('dev:dev-parent') },
      ];
    }
  }
  const qs = childId ? `?child_id=${encodeURIComponent(childId)}` : '';
  const res = await fetch(`/v1/chat/targets${qs}`, {
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiChatListThreads(token: string): Promise<ChatThreadItem[]> {
  const dev = devMeFromToken(token);
  if (IS_DEV && dev) {
    const meId = dev.id;
    const threads = dev.role === 'ADMIN' ? DEV_CHAT_THREADS : DEV_CHAT_THREADS.filter((t) => t.participant_ids.includes(meId));
    return threads.map((t) => devChatThreadToItem(t, meId, dev.role));
  }
  const res = await fetch('/v1/chat/threads', {
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiChatOpenThread(
  token: string,
  body: { recipient_user_id: string; child_id: string }
): Promise<ChatThreadItem> {
  const dev = devMeFromToken(token);
  if (IS_DEV && dev) {
    const meId = dev.id;
    const existing = DEV_CHAT_THREADS.find(
      (t) => t.child_id === body.child_id && t.participant_ids.includes(meId) && t.participant_ids.includes(body.recipient_user_id)
    );
    if (existing) return devChatThreadToItem(existing, meId, dev.role);
    const id = `dev:thread:${Date.now()}`;
    const createdAt = new Date().toISOString();
    const t: DevChatThread = {
      id,
      child_id: body.child_id,
      participant_ids: [meId, body.recipient_user_id],
      messages: [],
      reads: { [meId]: createdAt, [body.recipient_user_id]: createdAt },
    };
    DEV_CHAT_THREADS = [t, ...DEV_CHAT_THREADS];
    return devChatThreadToItem(t, meId, dev.role);
  }
  const res = await fetch('/v1/chat/threads/open', {
    method: 'POST',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiChatListMessages(token: string, threadId: string): Promise<ChatMessageItem[]> {
  const dev = devMeFromToken(token);
  if (IS_DEV && dev) {
    const t = DEV_CHAT_THREADS.find((x) => x.id === threadId);
    if (!t) throw new Error('HTTP_404');
    const meId = dev.id;
    t.reads[meId] = new Date().toISOString();
    return t.messages.map((m) => ({ ...m, is_me: m.sender_user_id === meId }));
  }
  const res = await fetch(`/v1/chat/threads/${encodeURIComponent(threadId)}/messages`, {
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiChatSendMessage(
  token: string,
  threadId: string,
  body: { body: string }
): Promise<ChatMessageItem> {
  const dev = devMeFromToken(token);
  if (IS_DEV && dev) {
    const t = DEV_CHAT_THREADS.find((x) => x.id === threadId);
    if (!t) throw new Error('HTTP_404');
    const msg: ChatMessageItem = {
      id: `dev:msg:${Date.now()}`,
      sender_user_id: dev.id,
      body: body.body,
      created_at: new Date().toISOString(),
      is_me: true,
    };
    t.messages.push({ id: msg.id, sender_user_id: msg.sender_user_id, body: msg.body, created_at: msg.created_at });
    t.reads[dev.id] = msg.created_at;
    return msg;
  }
  const res = await fetch(`/v1/chat/threads/${encodeURIComponent(threadId)}/messages`, {
    method: 'POST',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiChatMarkRead(token: string, threadId: string): Promise<{ ok: true }> {
  const dev = devMeFromToken(token);
  if (IS_DEV && dev) {
    const t = DEV_CHAT_THREADS.find((x) => x.id === threadId);
    if (t) t.reads[dev.id] = new Date().toISOString();
    return { ok: true };
  }
  const res = await fetch(`/v1/chat/threads/${encodeURIComponent(threadId)}/read`, {
    method: 'POST',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiAdminListChildren(
  token: string,
  args?: {
    group_id?: string;
    group_year?: number;
    birth_year?: number;
    q?: string;
    limit?: number;
    offset?: number;
    sort_by?: 'created_at' | 'child_name' | 'birth_year' | 'group_name';
    sort_dir?: 'asc' | 'desc';
  } | null
): Promise<{ items: ChildItem[]; total: number | null }> {
  const qs = (() => {
    const sp = new URLSearchParams();
    if (args?.group_id) sp.set('group_id', args.group_id);
    if (Number.isFinite(args?.group_year as number)) sp.set('group_year', String(args?.group_year));
    if (Number.isFinite(args?.birth_year as number)) sp.set('birth_year', String(args?.birth_year));
    if (args?.q) sp.set('q', args.q);
    if (Number.isFinite(args?.limit as number)) sp.set('limit', String(args?.limit));
    if (Number.isFinite(args?.offset as number)) sp.set('offset', String(args?.offset));
    if (args?.sort_by) sp.set('sort_by', args.sort_by);
    if (args?.sort_dir) sp.set('sort_dir', args.sort_dir);
    const s = sp.toString();
    return s ? `?${s}` : '';
  })();
  const res = await fetch(`/v1/admin/children${qs}`, {
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  const totalRaw = (res.headers.get('X-Total-Count') || '').trim();
  const totalNum = totalRaw ? Number.parseInt(totalRaw, 10) : NaN;
  const total = Number.isFinite(totalNum) && totalNum >= 0 ? totalNum : null;
  const items = (await res.json()) as ChildItem[];
  return { items, total };
}

async function apiAdminCreateChild(
  token: string,
  body: { first_name: string; last_initial: string; birth_year?: number | null; shirt_number?: number | null }
): Promise<ChildItem> {
  const res = await fetch('/v1/admin/children', {
    method: 'POST',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiAdminUpdateChild(
  token: string,
  childId: string,
  body: { first_name?: string; last_initial?: string; birth_year?: number | null; shirt_number?: number | null }
): Promise<ChildItem> {
  const res = await fetch(`/v1/admin/children/${encodeURIComponent(childId)}/update`, {
    method: 'POST',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiUpdateChild(
  token: string,
  childId: string,
  body: { first_name?: string; last_initial?: string; birth_year?: number | null; shirt_number?: number | null }
): Promise<ChildItem> {
  const res = await fetch(`/v1/children/${encodeURIComponent(childId)}/update`, {
    method: 'POST',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiAdminDeleteChild(token: string, childId: string): Promise<{ deleted: true }> {
  const res = await fetch(`/v1/admin/children/${encodeURIComponent(childId)}`, {
    method: 'DELETE',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

function parseHttpStatus(e: unknown): number | null {
  if (!e) return null;
  if (typeof e === 'object' && 'message' in e && typeof (e as { message: unknown }).message === 'string') {
    const m = (e as { message: string }).message;
    if (m.startsWith('HTTP_')) {
      const n = Number(m.slice(5));
      return Number.isFinite(n) ? n : null;
    }
  }
  return null;
}

function humanizeHttpError(e: unknown, fallback: string): string {
  const code = parseHttpStatus(e);
  if (!code) return fallback;
  if (code === 422) return 'Wybierz dziecko, aby wykonać tę akcję.';
  if (code === 403) return 'Brak uprawnień do wykonania tej akcji (403). Jeśli powinno działać: zrestartuj backend i odśwież aplikację.';
  if (code === 404) return 'Nie znaleziono zasobu lub brak dostępu (404). Jeśli to „nowa” funkcja: zrestartuj backend.';
  if (code === 400) return 'Nie można wykonać tej akcji (400).';
  return fallback;
}

function adminActionError(base: string, e: unknown): string {
  const code = parseHttpStatus(e);
  if (!code) return base;
  if (code === 404) return `${base} (404). Backend nie ma tej funkcji lub nie został zrestartowany.`;
  if (code === 401) return `${base} (401). Sesja wygasła — zaloguj się ponownie.`;
  if (code === 403) return `${base} (403). Brak uprawnień lub konto nie jest ACTIVE.`;
  if (code === 422) return `${base} (422). Sprawdź dane (np. inicjał nazwiska lub rocznik).`;
  return `${base} (${code}).`;
}

function coachTrainingActionError(base: string, e: unknown): string {
  const code = parseHttpStatus(e);
  if (!code) return base;
  if (code === 404) return `${base} (404). Backend nie ma tej funkcji lub nie został zrestartowany.`;
  if (code === 401) return `${base} (401). Sesja wygasła — zaloguj się ponownie.`;
  if (code === 403) return `${base} (403). Brak uprawnień lub trening jest już ODWOŁANY.`;
  if (code === 422) {
    const detail = typeof e === 'object' && e && 'detail' in e && typeof (e as { detail?: unknown }).detail === 'string' ? (e as { detail: string }).detail.trim() : '';
    return detail ? `${base} (422). ${detail}` : `${base} (422). Sprawdź dane formularza.`;
  }
  return `${base} (${code}).`;
}

function coachActionError(base: string, e: unknown): string {
  const code = parseHttpStatus(e);
  if (!code) return base;
  if (code === 404) return `${base} (404). Backend nie ma tej funkcji lub nie został zrestartowany.`;
  if (code === 401) return `${base} (401). Sesja wygasła — zaloguj się ponownie.`;
  if (code === 403) return `${base} (403). Brak uprawnień lub konto nie jest ACTIVE.`;
  if (code === 422) return `${base} (422). Sprawdź dane (np. miejsce i datę).`;
  return `${base} (${code}).`;
}

function googleMapsUrlFromLocation(location: string): string {
  const v = location.trim();
  if (!v) return 'https://www.google.com/maps';
  if (/^https?:\/\//i.test(v)) return v;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(v)}`;
}

function openExternalUrl(url: string): void {
  const u = url.trim();
  if (!u) return;
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
  const isIos = /iPad|iPhone|iPod/i.test(ua);
  const isAndroid = /Android/i.test(ua);
  if (isIos || isAndroid) {
    window.location.assign(u);
    return;
  }
  window.open(u, '_blank', 'noopener,noreferrer');
}

function feedCategoryVisual(
  category: string,
  kind: 'SYSTEM' | 'POST',
  opts?: { isImportant?: boolean; isScheduleChange?: boolean }
): { emoji: string; variant: string; label: string } {
  const isImportant = !!(opts?.isImportant);
  const isScheduleChange = !!(opts?.isScheduleChange);
  if (kind === 'SYSTEM') {
    if (isImportant && isScheduleChange) {
      return { emoji: '⚠️', variant: 'warn-change', label: 'UWAGA' };
    }
    if (isImportant) {
      return { emoji: '🔔', variant: 'system-important', label: 'Ważne' };
    }
    return { emoji: '🤖', variant: 'system', label: 'System' };
  }
  const c = category.trim().toUpperCase();
  if (c === 'OGŁOSZENIE') return { emoji: '📣', variant: 'announce', label: 'Ogłoszenie' };
  if (c === 'ORGANIZACJA') return { emoji: '📅', variant: 'org', label: 'Organizacja' };
  if (c === 'INFO') return { emoji: 'ℹ️', variant: 'info', label: 'Info' };
  return { emoji: '📝', variant: 'note', label: category.trim() ? category.trim() : 'Post' };
}

async function apiAdminAssignParent(token: string, childId: string, parent_email: string): Promise<ChildItem[]> {
  const res = await fetch(`/v1/admin/children/${encodeURIComponent(childId)}/assign-parent`, {
    method: 'POST',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ parent_email }),
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiAdminListIndividualCoaches(token: string, childId: string): Promise<CoachItem[]> {
  const res = await fetch(`/v1/admin/children/${encodeURIComponent(childId)}/individual-coaches`, {
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiAdminAssignIndividualCoach(token: string, childId: string, coach_email: string): Promise<CoachItem[]> {
  const res = await fetch(`/v1/admin/children/${encodeURIComponent(childId)}/assign-individual-coach`, {
    method: 'POST',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ coach_email }),
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiAdminUnassignIndividualCoach(token: string, childId: string, coachUserId: string): Promise<CoachItem[]> {
  const res = await fetch(
    `/v1/admin/children/${encodeURIComponent(childId)}/individual-coaches/${encodeURIComponent(coachUserId)}`,
    {
      method: 'DELETE',
      headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
    }
  );
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiAdminListChildPlayers(token: string, childId: string): Promise<PlayerAccountItem[]> {
  const res = await fetch(`/v1/admin/children/${encodeURIComponent(childId)}/players`, {
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiAdminAssignPlayer(token: string, childId: string, player_email: string): Promise<PlayerAccountItem[]> {
  const res = await fetch(`/v1/admin/children/${encodeURIComponent(childId)}/assign-player`, {
    method: 'POST',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ player_email }),
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiAdminUnassignPlayer(token: string, childId: string, playerUserId: string): Promise<PlayerAccountItem[]> {
  const res = await fetch(`/v1/admin/children/${encodeURIComponent(childId)}/players/${encodeURIComponent(playerUserId)}`, {
    method: 'DELETE',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiAdminListGroups(token: string): Promise<GroupItem[]> {
  if (IS_DEV && token === 'dev-admin') {
    return [{ id: 'demo-group', name: 'Demo U12', year: 2014, created_at: 'dev' }];
  }
  const res = await fetch('/v1/admin/groups', {
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiAdminCreateGroup(token: string, body: { name: string; year?: number | null }): Promise<GroupItem> {
  const res = await fetch('/v1/admin/groups', {
    method: 'POST',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiAdminSetChildGroup(token: string, childId: string, groupId: string | null): Promise<ChildItem> {
  const res = await fetch(`/v1/admin/children/${encodeURIComponent(childId)}/set-group`, {
    method: 'POST',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ group_id: groupId }),
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

async function apiAdminAssignCoachToGroup(token: string, groupId: string, coachEmail: string): Promise<GroupItem[]> {
  const res = await fetch(`/v1/admin/groups/${encodeURIComponent(groupId)}/assign-coach`, {
    method: 'POST',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ coach_email: coachEmail }),
  });
  if (!res.ok) throw new Error(`HTTP_${res.status}`);
  return res.json();
}

function useTab(): Tab {
  const [tab, setTab] = React.useState<Tab>(() => getTabFromHash());

  React.useEffect(() => {
    const onHashChange = () => setTab(getTabFromHash());
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  return tab;
}

function useToken(): [string | null, (next: string | null) => void] {
  const [token, setTokenState] = React.useState<string | null>(() => {
    const v = window.localStorage.getItem(TOKEN_KEY);
    return v && v.trim() ? v : null;
  });

  const setToken = React.useCallback((next: string | null) => {
    if (!next) {
      window.localStorage.removeItem(TOKEN_KEY);
      setTokenState(null);
      return;
    }
    window.localStorage.setItem(TOKEN_KEY, next);
    setTokenState(next);
  }, []);

  return [token, setToken];
}

function useMe(token: string | null): {
  me: Me | null;
  isLoading: boolean;
  isUnauthorized: boolean;
  setMeExternal: (next: Me | null) => void;
  refreshMe: () => void;
} {
  const [me, setMe] = React.useState<Me | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isUnauthorized, setIsUnauthorized] = React.useState(false);
  const [refreshKey, setRefreshKey] = React.useState(0);

  React.useEffect(() => {
    let cancelled = false;

    async function run() {
      if (!token) {
        setMe(null);
        setIsUnauthorized(false);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setIsUnauthorized(false);
      try {
        const nextMe = await apiGetMe(token);
        if (!cancelled) setMe(nextMe);
      } catch (e) {
        if (!cancelled) {
          setMe(null);
          setIsUnauthorized(true);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [refreshKey, token]);

  const setMeExternal = React.useCallback((next: Me | null) => setMe(next), []);
  const refreshMe = React.useCallback(() => setRefreshKey((v) => v + 1), []);

  return { me, isLoading, isUnauthorized, refreshMe, setMeExternal };
}

function Card(props: { title: string; children?: React.ReactNode }) {
  return (
    <section className="Card">
      <div className="CardTitle">{props.title}</div>
      <div className="CardBody">{props.children}</div>
    </section>
  );
}

function PrimaryButton(props: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const { className, ...rest } = props;
  return <button {...rest} className={['PrimaryButton', className].filter(Boolean).join(' ')} />;
}

function SecondaryButton(props: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const { className, ...rest } = props;
  return <button {...rest} className={['SecondaryButton', className].filter(Boolean).join(' ')} />;
}

function getHttpStatusFromError(e: unknown): number | null {
  if (!(e instanceof Error)) return null;
  const m = e.message.match(/^HTTP_(\d{3})$/);
  if (!m) return null;
  return Number(m[1]);
}

function childLabel(c: ChildItem): string {
  const base = `${c.first_name} ${c.last_initial}`;
  const group = c.group_name ? ` • ${c.group_name}` : '';
  const by = c.birth_year ? ` (${c.birth_year})` : '';
  return `${base}${group}${by}`;
}

function roleLabelPl(role: Role): string {
  if (role === 'PARENT') return 'Rodzic';
  if (role === 'PLAYER') return 'Zawodnik';
  if (role === 'COACH') return 'Trener';
  if (role === 'COORDINATOR') return 'Koordynator';
  return 'Admin';
}

function roleContextHint(role: Role): string {
  if (role === 'PARENT') return 'Wydarzenia, potwierdzenia i profil dzieci';
  if (role === 'PLAYER') return 'Twój kalendarz, powołania i własny profil zawodnika';
  if (role === 'COACH') return 'Kalendarz, drużyna, powołania i feed';
  if (role === 'COORDINATOR') return 'Akceptacje, organizacja i panel akademii';
  return 'Pełny panel administracyjny i zarządzanie systemem';
}

function isChildScopedRole(role: Role): boolean {
  return role === 'PARENT' || role === 'PLAYER';
}

const CHILD_ID_KEY = 'ac_child_id';

function useSelectedChildId(): [string | null, (next: string | null) => void] {
  const [childId, setChildIdState] = React.useState<string | null>(() => {
    const v = window.localStorage.getItem(CHILD_ID_KEY);
    return v && v.trim() ? v : null;
  });

  const setChildId = React.useCallback((next: string | null) => {
    if (!next) {
      window.localStorage.removeItem(CHILD_ID_KEY);
      setChildIdState(null);
      return;
    }
    window.localStorage.setItem(CHILD_ID_KEY, next);
    setChildIdState(next);
  }, []);

  return [childId, setChildId];
}

function RegisterSheet(props: { open: boolean; onClose: () => void; onRegistered: (token: string) => void }) {
  const [role, setRole] = React.useState<'PARENT' | 'COACH' | 'PLAYER'>('PARENT');
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [password2, setPassword2] = React.useState('');
  const [childFirstName, setChildFirstName] = React.useState('');
  const [childLastInitial, setChildLastInitial] = React.useState('');
  const [childBirthYear, setChildBirthYear] = React.useState('');
  const [groups, setGroups] = React.useState<GroupItem[]>([]);
  const [groupId, setGroupId] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);
  const [errorText, setErrorText] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!props.open) return;
    let cancelled = false;
    setIsLoading(true);
    setErrorText(null);
    apiPublicGroups()
      .then((list) => {
        if (cancelled) return;
        const filtered = list.filter((g) => String(g.id || '').trim() !== 'individual');
        setGroups(filtered);
        if (!groupId && filtered.length > 0) setGroupId(filtered[0].id);
      })
      .catch(() => {
        if (!cancelled) setGroups([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [groupId, props.open]);

  if (!props.open) return null;

  const canSubmit = (() => {
    if (isLoading) return false;
    if (email.trim() === '') return false;
    if (password.trim().length < 8) return false;
    if (password.trim() !== password2.trim()) return false;
    if (role === 'PARENT' || role === 'PLAYER') {
      if (childFirstName.trim() === '') return false;
      if (childLastInitial.trim() === '') return false;
      const by = Number(childBirthYear.trim());
      if (!Number.isFinite(by) || by < 2000 || by > 2100) return false;
      if (groups.length > 0 && groupId.trim() === '') return false;
    }
    return true;
  })();

  const submit = async () => {
    setIsLoading(true);
    setErrorText(null);
    try {
      const base = { email: email.trim(), password: password.trim(), role };
      const body =
        role === 'PARENT' || role === 'PLAYER'
          ? {
              ...base,
              child_first_name: childFirstName.trim(),
              child_last_initial: childLastInitial.trim(),
              child_birth_year: Number(childBirthYear.trim()),
              ...(groups.length > 0 ? { group_id: groupId.trim() } : {}),
            }
          : base;
      const r = await apiRegister(body);
      props.onRegistered(r.token);
      props.onClose();
    } catch (e) {
      const code = getHttpStatusFromError(e);
      if (code === 409) setErrorText('Ten email jest już zarejestrowany.');
      else if (code === 422) setErrorText('Sprawdź pola formularza.');
      else setErrorText('Nie udało się zarejestrować.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="SheetOverlay"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (isLoading) return;
        if (e.target === e.currentTarget) props.onClose();
      }}
    >
      <div className="Sheet">
        <div className="SheetHeaderRow SheetHeaderRow--sticky">
          <div className="SheetTitle">REJESTRACJA</div>
          <button className="SheetCloseButton" type="button" aria-label="Zamknij" onClick={props.onClose} disabled={isLoading}>
            ×
          </button>
        </div>

        {errorText && <div className="ErrorBanner">{errorText}</div>}

        <div className="Muted">Konto po rejestracji ma status PENDING i wymaga akceptacji przez Admina/Koordynatora.</div>

        <div className="Muted">Wybierz typ konta</div>
        <div className="NotificationActions" aria-label="Typ konta">
          <button type="button" className={`Pill ${role === 'PARENT' ? 'is-active' : ''}`} onClick={() => setRole('PARENT')} disabled={isLoading}>
            Rodzic
          </button>
          <button type="button" className={`Pill ${role === 'PLAYER' ? 'is-active' : ''}`} onClick={() => setRole('PLAYER')} disabled={isLoading}>
            Zawodnik
          </button>
          <button type="button" className={`Pill ${role === 'COACH' ? 'is-active' : ''}`} onClick={() => setRole('COACH')} disabled={isLoading}>
            Trener
          </button>
        </div>

        <input
          className="TextInput"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          inputMode="email"
          autoCapitalize="none"
          autoCorrect="off"
          placeholder="Email"
          disabled={isLoading}
        />
        <input
          className="TextInput"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          type="password"
          placeholder="Hasło (min. 8 znaków)"
          disabled={isLoading}
        />
        <input
          className="TextInput"
          value={password2}
          onChange={(e) => setPassword2(e.target.value)}
          type="password"
          placeholder="Powtórz hasło"
          disabled={isLoading}
        />

        {(role === 'PARENT' || role === 'PLAYER') && (
          <>
            <div className="Muted">{role === 'PLAYER' ? 'Dane zawodnika (do weryfikacji)' : 'Dane dziecka (do weryfikacji)'}</div>
            <input
              className="TextInput"
              value={childFirstName}
              onChange={(e) => setChildFirstName(e.target.value)}
              placeholder={role === 'PLAYER' ? 'Imię zawodnika' : 'Imię dziecka'}
              disabled={isLoading}
            />
            <input
              className="TextInput"
              value={childLastInitial}
              onChange={(e) => setChildLastInitial(e.target.value)}
              placeholder="Inicjał nazwiska (np. K.)"
              disabled={isLoading}
            />
            <input
              className="TextInput"
              value={childBirthYear}
              onChange={(e) => setChildBirthYear(e.target.value)}
              inputMode="numeric"
              placeholder="Rocznik (np. 2014)"
              disabled={isLoading}
            />

            <div className="Muted">Grupa</div>
            {groups.length === 0 ? (
              <div className="Muted">Brak grup do wyboru. Poproś Admina o dodanie grup lub spróbuj później.</div>
            ) : (
              <select className="TextInput" value={groupId} onChange={(e) => setGroupId(e.target.value)} disabled={isLoading}>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                    {g.year ? ` (${g.year})` : ''}
                  </option>
                ))}
              </select>
            )}
          </>
        )}

        <PrimaryButton onClick={submit} disabled={!canSubmit} type="button">
          Zarejestruj
        </PrimaryButton>
        <SecondaryButton onClick={props.onClose} disabled={isLoading} type="button">
          Anuluj
        </SecondaryButton>
      </div>
    </div>
  );
}

function LoginScreen(props: { onLogin: (token: string) => void }) {
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [errorText, setErrorText] = React.useState<string | null>(null);
  const [isRegisterOpen, setIsRegisterOpen] = React.useState(false);

  const canSubmit = email.trim() !== '' && password.trim() !== '' && !isSubmitting;

  const loginWithCreds = async (e: string, p: string) => {
    setIsSubmitting(true);
    setErrorText(null);
    try {
      const r = await apiLogin(e.trim(), p);
      props.onLogin(r.token);
    } catch (err) {
      const code = getHttpStatusFromError(err);
      if (code === 401) {
        setErrorText('Nieprawidłowy email lub hasło.');
      } else if (code === 500) {
        setErrorText('Błąd serwera. Uruchom backend ponownie i spróbuj jeszcze raz.');
      } else {
        setErrorText('Nie udało się zalogować.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const onLoginEmail = async () => {
    await loginWithCreds(email, password);
  };

  return (
    <div className="Page PageCentered">
      <img className="LoginBrandLogo" src={footDeskLogo} alt="FootDesk" />
      <div className="PageTitle">FootDesk</div>
      {errorText && <div className="ErrorBanner">{errorText}</div>}
      <input
        className="TextInput"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        inputMode="email"
        autoCapitalize="none"
        autoCorrect="off"
        placeholder="Email"
        disabled={isSubmitting}
      />
      <input
        className="TextInput"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        type="password"
        placeholder="Hasło"
        disabled={isSubmitting}
      />
      <PrimaryButton onClick={onLoginEmail} disabled={!canSubmit} type="button">
        Zaloguj
      </PrimaryButton>
      <SecondaryButton onClick={() => setIsRegisterOpen(true)} disabled={isSubmitting} type="button">
        Rejestracja
      </SecondaryButton>
      {IS_DEV && (
        <>
          <div className="Hint">Skróty (dev):</div>
          <div className="ButtonGrid">
            <SecondaryButton
              type="button"
              disabled={isSubmitting}
              onClick={() => {
                setEmail('admin@demo.pl');
                setPassword('123456789');
                return loginWithCreds('admin@demo.pl', '123456789');
              }}
            >
              Admin
            </SecondaryButton>
            <SecondaryButton
              type="button"
              disabled={isSubmitting}
              onClick={() => {
                setEmail('rodzic@demo.pl');
                setPassword('123456789');
                return loginWithCreds('rodzic@demo.pl', '123456789');
              }}
            >
              Testowy rodzic
            </SecondaryButton>
          </div>
        </>
      )}
      <RegisterSheet open={isRegisterOpen} onClose={() => setIsRegisterOpen(false)} onRegistered={(t) => props.onLogin(t)} />
    </div>
  );
}

function PendingScreen(props: { onLogout: () => void }) {
  return (
    <div className="Page PageCentered">
      <div className="PageTitle">Konto oczekuje na akceptację</div>
      <div className="PageSubtitle">
        Gdy Admin aktywuje konto, aplikacja odblokuje pełen dostęp.
      </div>
      <SecondaryButton onClick={props.onLogout}>Wyloguj</SecondaryButton>
    </div>
  );
}

function SelectChildScreen(props: {
  token: string;
  children: ChildItem[];
  onSelect: (childId: string) => void;
  onReload: () => void;
}) {
  return (
    <div className="Page">
      <Card title="Wybierz dziecko">
        <div className="Muted">Aby spełnić RODO, pracujemy zawsze w kontekście konkretnego dziecka.</div>
      </Card>
      <div className="List">
        {props.children.map((c) => (
          <button key={c.id} className="ListItem" onClick={() => props.onSelect(c.id)} type="button">
            <div className="ListItemTop">
              <div className="ListItemTitle">{childLabel(c)}</div>
            </div>
            <div className="ListItemMeta">Kliknij, aby przejść dalej</div>
          </button>
        ))}
      </div>
      <SecondaryButton onClick={props.onReload} type="button">
        Odśwież
      </SecondaryButton>
    </div>
  );
}

function FeedPage(props: {
  me: Me;
  token: string;
  selectedChild: ChildItem | null;
  workGroups: GroupItem[];
  activeGroupId: string;
  onChangeActiveGroupId: (groupId: string) => void;
}) {
  const [posts, setPosts] = React.useState<FeedPostItem[]>([]);
  const [eventById, setEventById] = React.useState<Record<string, EventDetails | null>>({});
  const [events, setEvents] = React.useState<EventListItem[]>([]);
  const [expandedByPostId, setExpandedByPostId] = React.useState<Record<string, boolean>>({});
  const [carpoolEventId, setCarpoolEventId] = React.useState<string | null>(null);
  const [isCancelledOpen, setIsCancelledOpen] = React.useState(false);
  const [deletePostConfirm, setDeletePostConfirm] = React.useState<FeedPostItem | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [errorText, setErrorText] = React.useState<string | null>(null);
  const [requiredActionIdx, setRequiredActionIdx] = React.useState(0);
  const [requiredActionCollapsed, setRequiredActionCollapsed] = React.useState(false);
  const requiredActionCollapsedPrefRef = React.useRef(false);
  const [composerOpen, setComposerOpen] = React.useState(false);
  const [editPostId, setEditPostId] = React.useState<string | null>(null);
  const [editExistingAttachments, setEditExistingAttachments] = React.useState<FeedAttachmentItem[]>([]);
  const [deletingAttachmentId, setDeletingAttachmentId] = React.useState<string | null>(null);
  const [newCategory, setNewCategory] = React.useState('OGŁOSZENIE');
  const [newTitle, setNewTitle] = React.useState('');
  const [newBody, setNewBody] = React.useState('');
  const [newImportant, setNewImportant] = React.useState(false);
  const [newLinkUrl, setNewLinkUrl] = React.useState('');
  const [newLinkTitle, setNewLinkTitle] = React.useState('');
  const [newFiles, setNewFiles] = React.useState<File[]>([]);
  const [imageUrlByAttachmentId, setImageUrlByAttachmentId] = React.useState<Record<string, string>>({});
  const imageUrlByAttachmentIdRef = React.useRef<Record<string, string>>({});
  const [imagePreview, setImagePreview] = React.useState<{ attachmentId: string } | null>(null);
  const [imagePreviewZoom, setImagePreviewZoom] = React.useState(1);
  const [imagePreviewPan, setImagePreviewPan] = React.useState({ x: 0, y: 0 });
  const [imagePreviewImgDim, setImagePreviewImgDim] = React.useState<{ w: number; h: number } | null>(null);
  const [imagePreviewVpDim, setImagePreviewVpDim] = React.useState<{ w: number; h: number } | null>(null);
  const imagePreviewVpRef = React.useRef<HTMLDivElement | null>(null);
  const imagePreviewPointersRef = React.useRef<Map<number, { x: number; y: number }>>(new Map());
  const imagePreviewGestureRef = React.useRef<{
    startZoom: number;
    startPan: { x: number; y: number };
    startDist: number;
    startCenter: { x: number; y: number };
  } | null>(null);
  const [hashPostId, setHashPostId] = React.useState<string | null>(() => {
    const p = getHashParts().params.get('p');
    return p && p.trim() ? p : null;
  });

  const groupLabel = React.useMemo(() => {
    if (props.me.role === 'PARENT') {
      const g = props.selectedChild?.group_name ?? '';
      const y = props.selectedChild?.group_year ?? null;
      const base = g ? g : 'GRUPA';
      return y ? `${base} (${y})` : base;
    }
    const g = props.workGroups.find((x) => x.id === props.activeGroupId) ?? null;
    if (!g) return 'GRUPA';
    return g.year ? `${g.name} (${g.year})` : g.name;
  }, [props.activeGroupId, props.me.role, props.selectedChild?.group_name, props.selectedChild?.group_year, props.workGroups]);

  const canPost = props.me.role === 'ADMIN' || props.me.role === 'COACH';
  const canDeletePost = props.me.role === 'ADMIN' || props.me.role === 'COACH';
  const isIndividualCoachFeed = props.me.role === 'COACH' && (props.workGroups ?? []).length === 0;
  const [childPhotoById, setChildPhotoById] = React.useState<Record<string, string | null>>({});

  const eventListById = React.useMemo(() => {
    const out: Record<string, EventListItem> = {};
    (events ?? []).forEach((e) => {
      out[e.id] = e;
    });
    return out;
  }, [events]);

  const load = React.useCallback(async () => {
    setIsLoading(true);
    setErrorText(null);
    try {
      if (isChildScopedRole(props.me.role)) {
        const childId = props.selectedChild?.id ?? null;
        if (!childId) {
          setPosts([]);
          setEvents([]);
          return;
        }
        const [list, ev] = await Promise.all([apiFeedList(props.token, { childId }), apiListEvents(props.token, childId)]);
        setPosts(list);
        setEvents(ev);
        return;
      }
      const gid = props.activeGroupId || null;
      if (!gid) {
        setPosts([]);
        if (isIndividualCoachFeed) {
          const ev = await apiCoachListMyEvents(props.token);
          setEvents(ev);
        } else {
          setEvents([]);
        }
        return;
      }
      const [list, ev] = await Promise.all([apiFeedList(props.token, { groupId: gid }), apiListEvents(props.token, null)]);
      setPosts(list);
      setEvents(ev);
    } catch (e) {
      setErrorText(adminActionError('Nie udało się pobrać feedu.', e));
    } finally {
      setIsLoading(false);
    }
  }, [isIndividualCoachFeed, props.activeGroupId, props.me.role, props.selectedChild?.id, props.token]);

  React.useEffect(() => {
    load();
  }, [load]);

  React.useEffect(() => {
    const onHashChange = () => {
      const p = getHashParts().params.get('p');
      setHashPostId(p && p.trim() ? p : null);
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  React.useEffect(() => {
    if (!composerOpen && !carpoolEventId) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (isLoading) return;
      e.preventDefault();
      if (composerOpen) {
        setComposerOpen(false);
        setEditPostId(null);
        setEditExistingAttachments([]);
        setDeletingAttachmentId(null);
        setNewTitle('');
        setNewBody('');
        setNewImportant(false);
        setNewLinkUrl('');
        setNewLinkTitle('');
        setNewFiles([]);
        return;
      }
      if (carpoolEventId) setCarpoolEventId(null);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [carpoolEventId, composerOpen, isLoading]);

  React.useEffect(() => {
    if (!hashPostId) return;
    if (posts.length === 0) return;
    const found = posts.some((p) => p.id === hashPostId);
    if (!found) return;

    window.setTimeout(() => {
      const el = document.getElementById(`feed-post-${hashPostId}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        (el as HTMLElement).focus?.();
      }
    }, 0);
  }, [hashPostId, posts]);

  React.useEffect(() => {
    const ids = Array.from(new Set(posts.map((p) => p.ref_event_id).filter((x): x is string => !!x)));
    const missing = ids.filter((id) => !(id in eventById));
    if (missing.length === 0) return;
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(
        missing.map(async (id) => {
          try {
            const d = await apiGetEvent(props.token, id, props.me.role === 'PARENT' ? (props.selectedChild?.id ?? null) : null);
            return [id, d] as const;
          } catch (e) {
            return [id, null] as const;
          }
        })
      );
      if (cancelled) return;
      setEventById((prev) => {
        const next: Record<string, EventDetails | null> = { ...prev };
        entries.forEach(([id, d]) => {
          if (!(id in next)) next[id] = d;
        });
        return next;
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [eventById, posts, props.me.role, props.selectedChild?.id, props.token]);

  const confirmRead = React.useCallback(
    async (p: FeedPostItem) => {
      try {
        const childId = props.me.role === 'PARENT' ? props.selectedChild?.id ?? null : null;
        await apiFeedConfirm(props.token, p.id, childId);
        await load();
      } catch (e) {
        setErrorText(adminActionError('Nie udało się zapisać potwierdzenia.', e));
      }
    },
    [load, props.me.role, props.selectedChild?.id, props.token]
  );

  const react = React.useCallback(
    async (p: FeedPostItem, emoji: string) => {
      try {
        const childId = props.me.role === 'PARENT' ? props.selectedChild?.id ?? null : null;
        await apiFeedReact(props.token, p.id, emoji, childId);
        await load();
      } catch (e) {
        setErrorText(adminActionError('Nie udało się zapisać reakcji.', e));
      }
    },
    [load, props.me.role, props.selectedChild?.id, props.token]
  );

  const openEventFromPost = React.useCallback((p: FeedPostItem) => {
    if (!p.ref_event_id) return;
    navigate('kalendarz', { e: p.ref_event_id });
  }, []);

  const openMapsFromPost = React.useCallback(
    (p: FeedPostItem) => {
      if (!p.ref_event_id) return;
      const d = eventById[p.ref_event_id];
      if (!d) return;
      const url = googleMapsUrlFromLocation(d.location);
      openExternalUrl(url);
    },
    [eventById]
  );

  const openCarpoolFromPost = React.useCallback((p: FeedPostItem) => {
    if (!p.ref_event_id) return;
    setCarpoolEventId(p.ref_event_id);
  }, []);

  const downloadAttachment = React.useCallback(
    async (a: FeedAttachmentItem) => {
      if (a.kind !== 'FILE') return;
      try {
        const childId = props.me.role === 'PARENT' ? (props.selectedChild?.id ?? null) : null;
        const r = await apiFeedDownloadAttachment(props.token, a.id, childId);
        const url = URL.createObjectURL(r.blob);
        const el = document.createElement('a');
        el.href = url;
        el.download = r.fileName ?? a.title ?? 'plik';
        document.body.appendChild(el);
        el.click();
        el.remove();
        URL.revokeObjectURL(url);
      } catch (e) {
        setErrorText(adminActionError('Nie udało się pobrać pliku.', e));
      }
    },
    [props.me.role, props.selectedChild?.id, props.token]
  );

  const attachmentById = React.useMemo(() => {
    const out: Record<string, FeedAttachmentItem> = {};
    posts.forEach((p) => (p.attachments ?? []).forEach((a) => (out[a.id] = a)));
    return out;
  }, [posts]);

  const isImageAttachment = React.useCallback((a: FeedAttachmentItem): boolean => {
    if (a.kind !== 'FILE') return false;
    const mime = String(a.mime || '').trim().toLowerCase();
    if (mime.startsWith('image/')) return true;
    const name = String(a.title || '').toLowerCase();
    return /\.(jpe?g|png|webp|gif)$/i.test(name);
  }, []);

  const openImagePreview = React.useCallback(
    (a: FeedAttachmentItem) => {
      if (!isImageAttachment(a)) return;
      setImagePreview({ attachmentId: a.id });
    },
    [isImageAttachment]
  );

  const clampImagePreviewZoom = React.useCallback((z: number): number => {
    if (!Number.isFinite(z)) return 1;
    return Math.max(1, Math.min(4, z));
  }, []);

  const clampImagePreviewPan = React.useCallback(
    (next: { x: number; y: number; zoom: number }): { x: number; y: number } => {
      if (!imagePreviewImgDim || !imagePreviewVpDim) return { x: next.x, y: next.y };
      const iw = imagePreviewImgDim.w;
      const ih = imagePreviewImgDim.h;
      const vw = imagePreviewVpDim.w;
      const vh = imagePreviewVpDim.h;
      if (!iw || !ih || !vw || !vh) return { x: next.x, y: next.y };
      const baseScale = Math.min(vw / iw, vh / ih);
      const w0 = iw * baseScale;
      const h0 = ih * baseScale;
      const w = w0 * next.zoom;
      const h = h0 * next.zoom;
      const maxX = Math.max(0, (w - vw) / 2);
      const maxY = Math.max(0, (h - vh) / 2);
      const x = Math.max(-maxX, Math.min(maxX, next.x));
      const y = Math.max(-maxY, Math.min(maxY, next.y));
      return { x, y };
    },
    [imagePreviewImgDim, imagePreviewVpDim]
  );

  React.useEffect(() => {
    imageUrlByAttachmentIdRef.current = imageUrlByAttachmentId;
  }, [imageUrlByAttachmentId]);

  React.useEffect(() => {
    return () => {
      Object.values(imageUrlByAttachmentIdRef.current).forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  React.useEffect(() => {
    if (!imagePreview) return;
    setImagePreviewZoom(1);
    setImagePreviewPan({ x: 0, y: 0 });
    setImagePreviewImgDim(null);
    setImagePreviewVpDim(null);
    imagePreviewPointersRef.current.clear();
    imagePreviewGestureRef.current = null;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (isLoading) return;
      e.preventDefault();
      setImagePreview(null);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [imagePreview, isLoading]);

  React.useEffect(() => {
    if (!imagePreview) return;
    const measure = () => {
      const el = imagePreviewVpRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      if (!r.width || !r.height) return;
      setImagePreviewVpDim({ w: r.width, h: r.height });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [imagePreview]);

  React.useEffect(() => {
    if (!imagePreview) return;
    const url = imageUrlByAttachmentId[imagePreview.attachmentId] ?? null;
    if (!url) return;
    let alive = true;
    const img = new Image();
    img.src = url;
    const done = () => {
      if (!alive) return;
      const w = img.naturalWidth || img.width;
      const h = img.naturalHeight || img.height;
      if (!w || !h) return;
      setImagePreviewImgDim({ w, h });
    };
    img.onload = done;
    img.onerror = () => {};
    return () => {
      alive = false;
    };
  }, [imagePreview?.attachmentId, imageUrlByAttachmentId]);

  React.useEffect(() => {
    if (!imagePreview) return;
    setImagePreviewPan((p) => {
      const c = clampImagePreviewPan({ x: p.x, y: p.y, zoom: imagePreviewZoom });
      return c.x === p.x && c.y === p.y ? p : c;
    });
  }, [clampImagePreviewPan, imagePreview, imagePreviewZoom]);

  React.useEffect(() => {
    const wanted = new Set<string>();
    posts.forEach((p) => (p.attachments ?? []).forEach((a) => (isImageAttachment(a) ? wanted.add(a.id) : null)));
    setImageUrlByAttachmentId((prev) => {
      let changed = false;
      const next: Record<string, string> = {};
      Object.entries(prev).forEach(([aid, url]) => {
        if (wanted.has(aid)) {
          next[aid] = url;
        } else {
          URL.revokeObjectURL(url);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [isImageAttachment, posts]);

  React.useEffect(() => {
    const imgs: FeedAttachmentItem[] = [];
    posts.forEach((p) => (p.attachments ?? []).forEach((a) => (isImageAttachment(a) ? imgs.push(a) : null)));
    const missing = imgs.filter((a) => !(a.id in imageUrlByAttachmentId));
    if (missing.length === 0) return;
    let cancelled = false;
    (async () => {
      for (const a of missing.slice(0, 18)) {
        try {
          const childId = props.me.role === 'PARENT' ? (props.selectedChild?.id ?? null) : null;
          const r = await apiFeedDownloadAttachment(props.token, a.id, childId);
          if (cancelled) return;
          const url = URL.createObjectURL(r.blob);
          setImageUrlByAttachmentId((prev) => {
            if (prev[a.id]) {
              URL.revokeObjectURL(url);
              return prev;
            }
            return { ...prev, [a.id]: url };
          });
        } catch {
          if (cancelled) return;
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [imageUrlByAttachmentId, isImageAttachment, posts, props.me.role, props.selectedChild?.id, props.token]);

  const renderAttachments = (attachments: FeedAttachmentItem[], excludedAttachmentIds?: Set<string>) => {
    const atts = attachments ?? [];
    if (atts.length === 0) return null;
    const excluded = excludedAttachmentIds ?? null;
    const visible = excluded ? atts.filter((a) => !excluded.has(a.id)) : atts;
    if (visible.length === 0) return null;
    const imageAtts = visible.filter((a) => isImageAttachment(a));
    const otherAtts = visible.filter((a) => !isImageAttachment(a));
    return (
      <div className="PostContent PostContent--stack">
        <div className="PostBody">
          {imageAtts.length > 0 && (
            <div className="FeedThumbGrid" aria-label="Załączone zdjęcia">
              {imageAtts.map((a) => {
                const url = imageUrlByAttachmentId[a.id] ?? null;
                return (
                  <button
                    key={a.id}
                    type="button"
                    className="FeedThumbBtn"
                    onClick={() => (url ? openImagePreview(a) : downloadAttachment(a))}
                    disabled={isLoading}
                    aria-label={a.title || 'Zdjęcie'}
                  >
                    {url ? <img className="FeedThumbImg" src={url} alt={a.title || ''} loading="lazy" /> : <div className="FeedThumbPh">…</div>}
                  </button>
                );
              })}
            </div>
          )}
          {otherAtts.map((a) => (
            <button
              key={a.id}
              type="button"
              className="FeedMoreLink"
              onClick={() => {
                if (a.kind === 'LINK' && a.url) openExternalUrl(a.url);
                else downloadAttachment(a);
              }}
              disabled={isLoading}
            >
              {a.kind === 'LINK' ? `↗ ${a.title}` : `⬇ ${a.title}`}
            </button>
          ))}
        </div>
      </div>
    );
  };

  const toggleExpanded = React.useCallback((postId: string) => {
    setExpandedByPostId((prev) => ({ ...prev, [postId]: !prev[postId] }));
  }, []);

  const shouldClampPostBody = React.useCallback((text: string): boolean => {
    const trimmed = text.trim();
    if (!trimmed) return false;
    const lines = trimmed.split(/\r?\n/).filter((line) => line.trim().length > 0);
    return trimmed.length > 190 || lines.length > 4;
  }, []);

  const MATCH_FEED_PREVIEW_LINES = 3;
  const buildMatchFeedPreviewMeta = React.useCallback((d: EventDetails | EventListItem) => {
    const lines: string[] = [];
    if (d.type !== 'MATCH') return lines;
    const start = d.start_at ? formatDateTime(d.start_at) : null;
    if (start) lines.push(start);
    const deadline = (d as EventDetails).match_deadline_at ?? null;
    if (deadline) lines.push(`Deadline: ${formatDateTime(deadline)}`);
    const loc = (d.location || '').trim();
    if (loc) lines.push(loc);
    return lines;
  }, []);

  const openCarpoolFromEventId = React.useCallback((eventId: string) => {
    const id = eventId.trim();
    if (!id) return;
    setCarpoolEventId(id);
  }, []);

  const submitPost = React.useCallback(async () => {
    if (!canPost) return;
    setIsLoading(true);
    setErrorText(null);
    let createdId: string | null = null;
    try {
      const preparedFiles: File[] = [];
      for (const f of newFiles) {
        preparedFiles.push(await normalizeFeedAttachmentFile(f));
      }

      const isEdit = !!editPostId;
      let postId = editPostId;
      if (isEdit && postId) {
        await apiFeedUpdate(props.token, postId, {
          category: newCategory,
          title: newTitle,
          body: newBody,
          is_important: newImportant,
        });
      } else {
        const gid = props.activeGroupId || '';
        if (!gid) return;
        const created = await apiFeedCreate(props.token, {
          group_id: gid,
          category: newCategory,
          title: newTitle,
          body: newBody,
          is_important: newImportant,
        });
        createdId = created.id;
        postId = created.id;
      }

      if (!postId) return;
      const linkUrl = newLinkUrl.trim();
      const linkTitle = newLinkTitle.trim();
      if (linkUrl) await apiFeedAddLink(props.token, postId, { url: linkUrl, title: linkTitle ? linkTitle : null });
      for (const f of preparedFiles) {
        await apiFeedAddAttachment(props.token, postId, f);
      }
      setComposerOpen(false);
      setEditPostId(null);
      setNewTitle('');
      setNewBody('');
      setNewImportant(false);
      setNewLinkUrl('');
      setNewLinkTitle('');
      setNewFiles([]);
      await load();
    } catch (e) {
      if (createdId) {
        try {
          await apiFeedDelete(props.token, createdId);
        } catch {}
      }
      if (e instanceof Error && e.message === 'FEED_FILE_TOO_LARGE') {
        setErrorText('Załącznik jest za duży. Maksymalny rozmiar pliku to 2 MB.');
        return;
      }
      if (e instanceof Error && e.message === 'FEED_IMAGE_UNSUPPORTED') {
        setErrorText('Nie udało się przetworzyć zdjęcia. Spróbuj wysłać JPG/PNG lub inną fotografię.');
        return;
      }
      if (e instanceof Error && e.message === 'FEED_FILE_UNSUPPORTED') {
        setErrorText('Ten typ pliku nie jest obsługiwany. Dozwolone: JPG/PNG/WEBP/PDF/TXT.');
        return;
      }
      const code = parseHttpStatus(e);
      if (code === 422 && newFiles.length > 0) {
        setErrorText('Nie udało się dodać posta (422). Sprawdź załączniki: JPG/PNG/WEBP/PDF/TXT, max 2 MB.');
        return;
      }
      setErrorText(adminActionError(editPostId ? 'Nie udało się zapisać posta.' : 'Nie udało się dodać posta.', e));
    } finally {
      setIsLoading(false);
    }
  }, [
    canPost,
    editPostId,
    load,
    newBody,
    newCategory,
    newFiles,
    newImportant,
    newLinkTitle,
    newLinkUrl,
    newTitle,
    props.activeGroupId,
    props.token,
  ]);

  const openDeletePostConfirm = React.useCallback(
    (p: FeedPostItem) => {
      if (!canDeletePost) return;
      if (p.kind !== 'POST' && p.kind !== 'SYSTEM') return;
      setDeletePostConfirm(p);
    },
    [canDeletePost]
  );

  const openEditPost = React.useCallback(
    (p: FeedPostItem) => {
      if (!canPost) return;
      if (p.kind !== 'POST') return;
      setEditPostId(p.id);
      setEditExistingAttachments((p.attachments ?? []).slice());
      setDeletingAttachmentId(null);
      setNewCategory((p.category || '').trim() || 'OGŁOSZENIE');
      setNewTitle((p.title || '').trim());
      setNewBody(p.body || '');
      setNewImportant(!!p.is_important);
      setNewLinkUrl('');
      setNewLinkTitle('');
      setNewFiles([]);
      setComposerOpen(true);
    },
    [canPost]
  );

  const deleteExistingAttachment = React.useCallback(
    async (a: FeedAttachmentItem) => {
      if (!editPostId) return;
      if (deletingAttachmentId) return;
      setDeletingAttachmentId(a.id);
      setErrorText(null);
      try {
        await apiFeedDeleteAttachment(props.token, a.id);
        setEditExistingAttachments((prev) => prev.filter((x) => x.id !== a.id));
        setPosts((prev) =>
          prev.map((p) => (p.id === editPostId ? { ...p, attachments: (p.attachments ?? []).filter((x) => x.id !== a.id) } : p))
        );
      } catch (e) {
        setErrorText(adminActionError('Nie udało się usunąć załącznika.', e));
      } finally {
        setDeletingAttachmentId(null);
      }
    },
    [deletingAttachmentId, editPostId, props.token]
  );

  const onDeletePostConfirm = React.useCallback(async () => {
    if (!canDeletePost) return;
    const p = deletePostConfirm;
    if (!p) return;
    if (p.kind !== 'POST' && p.kind !== 'SYSTEM') return;
    setIsLoading(true);
    setErrorText(null);
    try {
      await apiFeedDelete(props.token, p.id);
      setDeletePostConfirm(null);
      await load();
    } catch (e) {
      setErrorText(adminActionError('Nie udało się usunąć posta.', e));
    } finally {
      setIsLoading(false);
    }
  }, [canDeletePost, deletePostConfirm, load, props.token]);

  const todayKey = dateKeyFromDate(new Date());
  const importantTodayEvents = React.useMemo(() => {
    const list = events
      .filter((e) => dateKeyFromIso(e.start_at) === todayKey)
      .filter((e) => {
        if (props.me.role === 'PARENT') return true;
        if (!props.activeGroupId) return true;
        return e.group_id === props.activeGroupId;
      })
      .slice()
      .sort((a, b) => a.start_at.localeCompare(b.start_at));
    return list;
  }, [events, props.activeGroupId, props.me.role, todayKey]);

  const upcomingCoachIndividualTrainings = React.useMemo(() => {
    if (!isIndividualCoachFeed) return [] as EventListItem[];
    const nowIso = new Date().toISOString();
    return events
      .filter((e) => e.type === 'TRAINING' && e.status !== 'CANCELLED')
      .filter((e) => e.is_individual && !!(e.target_child_id ?? '').trim())
      .filter((e) => e.start_at >= nowIso)
      .slice()
      .sort((a, b) => a.start_at.localeCompare(b.start_at))
      .slice(0, 4);
  }, [events, isIndividualCoachFeed]);

  const upcomingIndividualChildIds = React.useMemo(() => {
    return Array.from(
      new Set(
        upcomingCoachIndividualTrainings
          .map((e) => String(e.target_child_id ?? '').trim())
          .filter(Boolean)
      )
    );
  }, [upcomingCoachIndividualTrainings]);

  React.useEffect(() => {
    if (!isIndividualCoachFeed) return;
    if (props.me.role !== 'COACH') return;
    if (props.me.status !== 'ACTIVE') return;
    if (upcomingIndividualChildIds.length === 0) return;
    let cancelled = false;
    const missing = upcomingIndividualChildIds.filter((id) => !(id in childPhotoById));
    if (missing.length === 0) return;
    (async () => {
      const limit = 4;
      const results: Record<string, string | null> = {};
      for (let i = 0; i < missing.length; i += limit) {
        const batch = missing.slice(i, i + limit);
        const settled = await Promise.allSettled(batch.map((id) => apiGetChildPhoto(props.token, id)));
        for (let j = 0; j < batch.length; j += 1) {
          const id = batch[j];
          const r = settled[j];
          if (r.status === 'fulfilled') results[id] = r.value.data_url ?? null;
          else results[id] = null;
        }
        if (cancelled) return;
        setChildPhotoById((prev) => ({ ...prev, ...results }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [childPhotoById, isIndividualCoachFeed, props.me.role, props.me.status, props.token, upcomingIndividualChildIds]);

  const resolveChildPhotoUrl = React.useCallback(
    (childId: string): string | null => {
      const id = (childId ?? '').trim();
      if (!id) return null;
      return childPhotoById[id] ?? null;
    },
    [childPhotoById]
  );

  const pendingMatchesNeedingRsvp = React.useMemo(() => {
    if (props.me.role !== 'PARENT') return [] as EventListItem[];
    return events
      .filter(
        (e) =>
          e.type === 'MATCH' &&
          (e.called_up ?? false) &&
          (e.match_rsvp ?? 'PENDING') === 'PENDING' &&
          e.can_match_rsvp !== false
      )
      .slice()
      .sort((a, b) => a.start_at.localeCompare(b.start_at));
  }, [events, props.me.role]);

  const isScheduleChangePost = React.useCallback((p: FeedPostItem) => {
    const t = `${p.title || ''}\n${p.body || ''}`;
    const tl = t.toLowerCase();
    if (!t.trim()) return false;
    if (p.kind !== 'SYSTEM') return false;
    if (/⚠️|⚠|zmieniono|zmiana|przesuni|przenies|godzin|termin|start|data|miejsce|odwoł|odwol|cancel|potwierdzeń|deadline|zbiór|zbior|przeciwnik/.test(t)) return true;
    if (/(zmian|godzin|termin|odwoł)/i.test(tl)) return true;
    if (p.category && /ORGANIZACJA|SYSTEM/i.test(p.category) && p.ref_event_id && /^(⏰|📅|📍|🕒|🏆|📌|🎖️|❓|⚠️)/.test((p.body || '').trim())) return true;
    return false;
  }, []);

  const pinnedScheduleChangePosts = React.useMemo(() => {
    return posts
      .filter((p) => {
        if (!isScheduleChangePost(p)) return false;
        if (p.ref_event_id) {
          const d = eventById[p.ref_event_id] ?? null;
          if (d && (d.start_at ?? '').trim()) {
            if (dateKeyFromIso(d.start_at) < todayKey) return false;
          }
        } else {
          if (dateKeyFromIso(p.created_at) < todayKey) return false;
        }
        return true;
      })
      .slice()
      .sort((a, b) => b.created_at.localeCompare(a.created_at));
  }, [eventById, isScheduleChangePost, posts, todayKey]);

  const pinnedCancelledPosts = React.useMemo(() => {
    return posts
      .filter((p) => {
        if (!p.ref_event_id) return false;
        const t = `${p.title || ''}\n${p.body || ''}`.toLowerCase();
        const isCancelled = /odwołan|odwol|cancel/.test(t) || (!!eventById[p.ref_event_id] && eventById[p.ref_event_id]?.status === 'CANCELLED');
        if (!isCancelled) return false;
        const d = eventById[p.ref_event_id] ?? null;
        if (d && (d.start_at ?? '').trim()) {
          return dateKeyFromIso(d.start_at) >= todayKey;
        }
        return dateKeyFromIso(p.created_at) >= todayKey;
      })
      .slice()
      .sort((a, b) => b.created_at.localeCompare(a.created_at));
  }, [eventById, posts, todayKey]);

  const cancelledPinnedSummary = React.useMemo(() => {
    if (pinnedCancelledPosts.length === 0) return null;
    const p0 = pinnedCancelledPosts[0]!;
    const d0 = p0.ref_event_id ? eventById[p0.ref_event_id] ?? null : null;
    const meta = d0 ? `${formatDateTime(d0.start_at)} • ${d0.location}` : null;
    const title = d0 ? (d0.title || '').trim() || (d0.type === 'MATCH' ? 'Mecz' : 'Trening') : (p0.title || '').trim();
    const extra = pinnedCancelledPosts.length > 1 ? ` (+${pinnedCancelledPosts.length - 1})` : '';
    return `${title}${meta ? ` • ${meta}` : ''}${extra}`;
  }, [eventById, pinnedCancelledPosts]);

  const parentMatchCtaLabel = React.useCallback((d: EventDetails) => {
    if (d.match_rsvp === 'ACCEPTED') return 'OK';
    if (d.match_rsvp === 'DECLINED') return 'NB';
    if (d.can_match_rsvp === false) return 'OTWÓRZ';
    return 'JESTEM';
  }, []);

  const shouldRequireReadConfirm = props.me.role === 'PARENT';

  const importantPostsNeedingConfirm = React.useMemo(() => {
    if (!shouldRequireReadConfirm) return [] as FeedPostItem[];
    return posts
      .filter((p) => p.is_important && !p.my_confirmed)
      .slice()
      .sort((a, b) => b.created_at.localeCompare(a.created_at));
  }, [posts, shouldRequireReadConfirm]);

  const requiredActionItems = React.useMemo(() => {
    const out: Array<{ key: string; kind: 'MATCH_RSVP'; event: EventListItem } | { key: string; kind: 'IMPORTANT_POST'; post: FeedPostItem }> = [];
    if (props.me.role === 'PARENT') {
      pendingMatchesNeedingRsvp.forEach((e) => out.push({ key: `m:${e.id}`, kind: 'MATCH_RSVP', event: e }));
    }
    if (shouldRequireReadConfirm) {
      importantPostsNeedingConfirm.forEach((p) => out.push({ key: `p:${p.id}`, kind: 'IMPORTANT_POST', post: p }));
    }
    return out.slice(0, 8);
  }, [importantPostsNeedingConfirm, pendingMatchesNeedingRsvp, props.me.role, shouldRequireReadConfirm]);

  const requiredActionCurrent =
    requiredActionItems.length > 0 ? requiredActionItems[requiredActionIdx % requiredActionItems.length] ?? null : null;

  React.useEffect(() => {
    if (requiredActionCollapsedPrefRef.current) return;
    if (requiredActionCurrent && importantTodayEvents.length > 0) setRequiredActionCollapsed(true);
  }, [importantTodayEvents.length, requiredActionCurrent?.key]);

  React.useEffect(() => {
    if (requiredActionItems.length <= 1) return;
    setRequiredActionIdx((i) => (requiredActionItems.length > 0 ? i % requiredActionItems.length : 0));
    const loop = window.setInterval(() => {
      setRequiredActionIdx((i) => (requiredActionItems.length > 0 ? (i + 1) % requiredActionItems.length : 0));
    }, 8200);
    return () => {
      window.clearInterval(loop);
    };
  }, [requiredActionItems.length]);

  const restPosts = React.useMemo(() => {
    const pinnedEventIds = new Set(importantTodayEvents.map((e) => e.id));
    const pinnedChangeIds = new Set(pinnedScheduleChangePosts.map((p) => p.id));
    return posts.filter((p) => {
      if (pinnedChangeIds.has(p.id)) return false;
      if (p.kind === 'SYSTEM' && p.ref_event_id && pinnedEventIds.has(p.ref_event_id) && !((p.target_child_id ?? '').trim())) return false;
      return true;
    });
  }, [importantTodayEvents, pinnedScheduleChangePosts, posts]);

  const openEventFromListItem = React.useCallback((e: EventListItem) => {
    navigate('kalendarz', { e: e.id });
  }, []);

  const hasMultipleTodayEvents = importantTodayEvents.length > 1;
  const primaryTodayEvent = importantTodayEvents[0] ?? null;
  const secondaryTodayEvent = hasMultipleTodayEvents ? importantTodayEvents[1] ?? null : null;
  const otherTodayEvents = importantTodayEvents.slice(2);
  const [importantTodayExpanded, setImportantTodayExpanded] = React.useState(() => !hasMultipleTodayEvents);
  const importantTodayExpandedPrefRef = React.useRef(false);
  const collapsePref = React.useMemo(() => loadImportantTodayCollapsedPref(), []);
  const collapsePrefRef = React.useRef<boolean>(collapsePref !== null);
  const [importantTodayCollapsed, setImportantTodayCollapsed] = React.useState<boolean>(() => (hasMultipleTodayEvents ? collapsePref ?? false : false));

  React.useEffect(() => {
    if (importantTodayExpandedPrefRef.current) return;
    if (primaryTodayEvent && requiredActionCurrent && !hasMultipleTodayEvents) setImportantTodayExpanded(false);
  }, [primaryTodayEvent?.id, requiredActionCurrent?.key, hasMultipleTodayEvents]);

  React.useEffect(() => {
    if (collapsePrefRef.current) return;
    if (otherTodayEvents.length > 0) setImportantTodayCollapsed(true);
  }, [otherTodayEvents.length]);

  const toggleImportantTodayExpanded = React.useCallback(() => {
    setImportantTodayExpanded((prev) => {
      importantTodayExpandedPrefRef.current = true;
      return !prev;
    });
  }, []);

  const toggleImportantTodayCollapsed = React.useCallback(() => {
    setImportantTodayCollapsed((prev) => {
      const next = !prev;
      collapsePrefRef.current = true;
      saveImportantTodayCollapsedPref(next);
      return next;
    });
  }, []);

  const scheduleChangesCollapsePref = React.useMemo(() => loadScheduleChangesCollapsedPref(), []);
  const scheduleChangesCollapsePrefRef = React.useRef<boolean>(scheduleChangesCollapsePref !== null);
  const [scheduleChangesCollapsed, setScheduleChangesCollapsed] = React.useState<boolean>(() => {
    if (scheduleChangesCollapsePref !== null) return scheduleChangesCollapsePref;
    return pinnedScheduleChangePosts.length >= 2;
  });
  const toggleScheduleChangesCollapsed = React.useCallback(() => {
    setScheduleChangesCollapsed((prev) => {
      const next = !prev;
      scheduleChangesCollapsePrefRef.current = true;
      saveScheduleChangesCollapsedPref(next);
      return next;
    });
  }, []);

  const importantTodayCompact = true;

  const requiredActionSummary = React.useMemo(() => {
    if (!requiredActionCurrent) return '';
    if (requiredActionCurrent.kind === 'MATCH_RSVP') return 'Wysłano powołania';
    const t = (requiredActionCurrent.post.title || '').trim();
    return t ? `Przeczytaj: ${t}` : 'Przeczytaj komunikat';
  }, [requiredActionCurrent]);

  const liveGroupId = (() => {
    if (props.me.role === 'PARENT') {
      const gid = (props.selectedChild?.group_id ?? '').trim();
      return gid ? gid : null;
    }
    const gid = (props.activeGroupId ?? '').trim();
    return gid ? gid : null;
  })();
  const liveChildId = props.me.role === 'PARENT' ? (props.selectedChild?.id ?? null) : null;

  const postComposer = (
    <>
      <div className="SheetHeaderRow SheetHeaderRow--sticky">
        <div className="SheetTitle">{editPostId ? 'EDYTUJ POST' : 'NOWY POST'}</div>
        <button
          className="SheetCloseButton"
          type="button"
          aria-label="Zamknij"
          onClick={() => {
            setComposerOpen(false);
            setEditPostId(null);
            setEditExistingAttachments([]);
            setDeletingAttachmentId(null);
            setNewTitle('');
            setNewBody('');
            setNewImportant(false);
            setNewLinkUrl('');
            setNewLinkTitle('');
            setNewFiles([]);
          }}
          disabled={isLoading}
        >
          ×
        </button>
      </div>
      {editPostId ? (
        <div className="Muted">Edycja posta</div>
      ) : props.workGroups.length === 0 ? (
        <div className="Muted">Brak grup.</div>
      ) : props.activeGroupId ? (
        <div className="Muted">{`Grupa: ${groupLabel}`}</div>
      ) : (
        <div className="ErrorBanner">Wybierz grupę w menu (hamburger).</div>
      )}
      <select className="TextInput" value={newCategory} onChange={(e) => setNewCategory(e.target.value)} disabled={isLoading}>
        <option value="OGŁOSZENIE">OGŁOSZENIE</option>
        <option value="ORGANIZACJA">ORGANIZACJA</option>
        <option value="INFO">INFO</option>
      </select>
      <input className="TextInput" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="Tytuł" disabled={isLoading} />
      <textarea className="TextArea" value={newBody} onChange={(e) => setNewBody(e.target.value)} placeholder="Treść" disabled={isLoading} />

      {editPostId && editExistingAttachments.length > 0 && (
        <div className="EditAttachments" aria-label="Aktualne załączniki">
          <div className="Muted">Załączniki w poście</div>
          <div className="EditAttachmentList">
            {editExistingAttachments.map((a) => (
              <div key={a.id} className="EditAttachmentRow">
                <button
                  className="EditAttachmentMain"
                  type="button"
                  onClick={() => {
                    if (a.kind === 'LINK' && a.url) openExternalUrl(a.url);
                    else downloadAttachment(a);
                  }}
                  disabled={isLoading || deletingAttachmentId === a.id}
                >
                  <span className="EditAttachmentTitle">{a.title}</span>
                  <span className="EditAttachmentMeta">{a.kind === 'LINK' ? 'LINK' : a.mime ? a.mime.toUpperCase() : 'PLIK'}</span>
                </button>
                <button
                  className="EditAttachmentDelete"
                  type="button"
                  aria-label="Usuń załącznik"
                  onClick={() => deleteExistingAttachment(a)}
                  disabled={isLoading || deletingAttachmentId === a.id}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <input
        className="TextInput"
        value={newLinkUrl}
        onChange={(e) => setNewLinkUrl(e.target.value)}
        placeholder="Link (https://...)"
        disabled={isLoading}
      />
      <input
        className="TextInput"
        value={newLinkTitle}
        onChange={(e) => setNewLinkTitle(e.target.value)}
        placeholder="Tytuł linku (opcjonalnie)"
        disabled={isLoading || newLinkUrl.trim() === ''}
      />
      <input
        className="TextInput"
        type="file"
        multiple
        accept="image/jpeg,image/png,image/webp,application/pdf,text/plain"
        disabled={isLoading}
        onChange={(e) => {
          const files = Array.from(e.currentTarget.files ?? []);
          setNewFiles(files.slice(0, 3));
        }}
      />
      {(newFiles.length > 0 || newLinkUrl.trim()) && (
        <div className="Muted">
          {newLinkUrl.trim() ? `Link: ${newLinkTitle.trim() ? newLinkTitle.trim() : newLinkUrl.trim()}` : ''}
          {newFiles.length > 0 ? `${newLinkUrl.trim() ? ' • ' : ''}Pliki: ${newFiles.map((f) => f.name).join(', ')}` : ''}
        </div>
      )}
      <label className="Row">
        <input type="checkbox" checked={newImportant} onChange={(e) => setNewImportant(e.target.checked)} disabled={isLoading} />
        <span>Ważne (wymaga „Przeczytałem”)</span>
      </label>
      <PrimaryButton
        onClick={submitPost}
        disabled={isLoading || newTitle.trim() === '' || newBody.trim() === '' || (!editPostId && props.activeGroupId === '')}
        type="button"
      >
        {editPostId ? 'Zapisz' : 'Opublikuj'}
      </PrimaryButton>
      <SecondaryButton
        onClick={() => {
          setComposerOpen(false);
          setEditPostId(null);
          setEditExistingAttachments([]);
          setDeletingAttachmentId(null);
          setNewTitle('');
          setNewBody('');
          setNewImportant(false);
          setNewLinkUrl('');
          setNewLinkTitle('');
          setNewFiles([]);
        }}
        disabled={isLoading}
        type="button"
      >
        Anuluj
      </SecondaryButton>
    </>
  );

  return (
    <div className="Page">
      <div className="PageHeroTitle">NAJNOWSZE WPISY</div>

      {errorText && <div className="ErrorBanner">{errorText}</div>}

      {props.me.role === 'PARENT' && (!props.selectedChild || !props.selectedChild.group_id) && (
        <Card title="Brak grupy">
          <div className="Muted">Wybrane dziecko nie ma przypisanej grupy. Poproś Admina o przypisanie.</div>
        </Card>
      )}

      {(primaryTodayEvent ||
        pinnedCancelledPosts.length > 0 ||
        pinnedScheduleChangePosts.length > 0) && (
        <div className="FeedPinned" aria-label="Sekcja przypięta">

          {primaryTodayEvent && (
            <div
              className={[
                'ImportantTodayBanner',
                primaryTodayEvent.type === 'TRAINING' ? 'ImportantTodayBanner--training' : '',
                !importantTodayExpanded ? 'ImportantTodayBanner--collapsed' : '',
                hasMultipleTodayEvents ? 'ImportantTodayBanner--multi' : '',
              ].join(' ')}
              role="region"
              tabIndex={-1}
              aria-label="Ważne na dziś"
              aria-expanded={importantTodayExpanded}
            >
              {!importantTodayExpanded ? (
                <div className="ImportantTodayCompactRow">
                  <div className="ImportantTodayCompactLeft">
                    <div className="ImportantTodayCompactTitle">
                      WAŻNE NA DZIŚ
                      <span className="ImportantTodayCompactBell" aria-hidden="true">
                        <BellIcon />
                      </span>
                    </div>
                    <div className="ImportantTodayCompactSubtitle">
                      {hasMultipleTodayEvents
                        ? `${importantTodayEvents.length} wydarzeń: ${formatTime(primaryTodayEvent.start_at)} — ${formatTime(secondaryTodayEvent?.start_at ?? primaryTodayEvent.start_at)}`
                        : `${formatTime(primaryTodayEvent.start_at)} • ${(
                            primaryTodayEvent.title || (primaryTodayEvent.type === 'MATCH' ? 'MECZ' : 'TRENING')
                          ).toUpperCase()}`}
                      {primaryTodayEvent.is_individual && (primaryTodayEvent.target_child_label ?? '').trim()
                        ? ` • ${(primaryTodayEvent.target_child_label ?? '').trim()}`
                        : ''}
                      {otherTodayEvents.length > 0 ? ` • +${otherTodayEvents.length}` : ''}
                    </div>
                  </div>
                  <button
                    className="ImportantTodayCompactMore"
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleImportantTodayExpanded();
                    }}
                    aria-label="Pokaż szczegóły „Ważne na dziś”"
                    disabled={isLoading}
                  >
                    Więcej
                  </button>
                </div>
              ) : (
                <>
                  <div className="ImportantTodayBannerTop">
                    <div className="ImportantTodayBannerTitle">
                      WAŻNE NA DZIŚ
                      <span className="ImportantTodayBannerBell" aria-hidden="true">
                        <BellIcon />
                      </span>
                    </div>
                    <div className="ImportantTodayBannerTopRight">
                      <div className="ImportantTodayBannerMeta">
                        {new Intl.DateTimeFormat('pl-PL', { day: '2-digit', month: 'long' }).format(new Date())}
                      </div>
                      <button
                        className="ImportantTodayBannerExpandToggle"
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleImportantTodayExpanded();
                        }}
                        aria-label="Ukryj szczegóły „Ważne na dziś”"
                        disabled={isLoading}
                      >
                        Ukryj
                      </button>
                      {(secondaryTodayEvent || otherTodayEvents.length > 0) && (
                        <button
                          className="ImportantTodayBannerToggle"
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleImportantTodayCollapsed();
                          }}
                          aria-label={importantTodayCollapsed ? 'Rozwiń listę wydarzeń na dziś' : 'Zwiń listę wydarzeń na dziś'}
                          disabled={isLoading}
                        >
                          {importantTodayCollapsed ? 'Rozwiń' : 'Zwiń'}
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="ImportantTodayHeroes" aria-label="Wydarzenia dzisiaj">
                    <div
                      className={['ImportantTodayHero', importantTodayCompact ? 'ImportantTodayHero--compact' : '', hasMultipleTodayEvents ? 'ImportantTodayHero--multi' : ''].join(' ')}
                    >
                      <button
                        type="button"
                        className="ImportantTodayHeroMain"
                        onClick={(e) => {
                          e.stopPropagation();
                          openEventFromListItem(primaryTodayEvent);
                        }}
                        aria-label={`${primaryTodayEvent.type === 'MATCH' ? 'Mecz' : 'Trening'}: ${primaryTodayEvent.title}`}
                      >
                        <div className="ImportantTodayHeroTitle">
                          {(primaryTodayEvent.title || (primaryTodayEvent.type === 'MATCH' ? 'MECZ' : 'TRENING')).toUpperCase()}
                        </div>
                        <div className="ImportantTodayHeroLine">
                          <span className="ImportantTodayHeroLineIcon" aria-hidden="true">
                            <ClockIcon />
                          </span>
                          <span className="ImportantTodayHeroLineText">{`Dzisiaj, ${formatTime(primaryTodayEvent.start_at)}`}</span>
                          <span className="ImportantTodayHeroLineSep" aria-hidden="true">•</span>
                          <span className="ImportantTodayHeroLineIcon" aria-hidden="true">
                            <MapPinIcon />
                          </span>
                          <span className="ImportantTodayHeroLineText ImportantTodayHeroLineText--truncate">{primaryTodayEvent.location}</span>
                          {primaryTodayEvent.status === 'CANCELLED' && <span className="ImportantTodayHeroCancelled">ODWOŁANE</span>}
                        </div>
                        {primaryTodayEvent.is_individual && (primaryTodayEvent.target_child_label ?? '').trim() && (
                          <div className="ImportantTodayHeroLine">
                            <span className="ImportantTodayHeroLineIcon" aria-hidden="true">
                              👤
                            </span>
                            <span className="ImportantTodayHeroLineText">{`Podopieczny: ${(primaryTodayEvent.target_child_label ?? '').trim()}`}</span>
                          </div>
                        )}
                      </button>

                      <div className="ImportantTodayHeroSide" aria-hidden="true">
                        <div className="ImportantTodayHeroActions">
                          <button
                            className="ImportantTodayIconBtn"
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              openExternalUrl(googleMapsUrlFromLocation(primaryTodayEvent.location));
                            }}
                            aria-label="Google Maps"
                            disabled={isLoading}
                          >
                            <MapIcon />
                          </button>
                          <button
                            className={['ImportantTodayIconBtn', transportIconClass(primaryTodayEvent)].filter(Boolean).join(' ')}
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              openCarpoolFromEventId(primaryTodayEvent.id);
                            }}
                            aria-label="Transport (carpooling)"
                            disabled={isLoading}
                          >
                            <CarIcon />
                          </button>
                        </div>
                        {!importantTodayCompact && (
                          <div className="ImportantTodayHeroArt">
                            {primaryTodayEvent.type === 'MATCH' ? <SoccerIcon /> : <TrainingIcon />}
                          </div>
                        )}
                      </div>
                    </div>

                    {secondaryTodayEvent && (
                      <div
                        className={['ImportantTodayHero', 'ImportantTodayHero--secondary', importantTodayCompact ? 'ImportantTodayHero--compact' : ''].join(' ')}
                      >
                        <button
                          type="button"
                          className="ImportantTodayHeroMain"
                          onClick={(e) => {
                            e.stopPropagation();
                            openEventFromListItem(secondaryTodayEvent);
                          }}
                          aria-label={`${secondaryTodayEvent.type === 'MATCH' ? 'Mecz' : 'Trening'}: ${secondaryTodayEvent.title}`}
                        >
                          <div className="ImportantTodayHeroTitle">
                            {(secondaryTodayEvent.title || (secondaryTodayEvent.type === 'MATCH' ? 'MECZ' : 'TRENING')).toUpperCase()}
                          </div>
                          <div className="ImportantTodayHeroLine">
                            <span className="ImportantTodayHeroLineIcon" aria-hidden="true">
                              <ClockIcon />
                            </span>
                            <span className="ImportantTodayHeroLineText">{`Dzisiaj, ${formatTime(secondaryTodayEvent.start_at)}`}</span>
                            <span className="ImportantTodayHeroLineSep" aria-hidden="true">•</span>
                            <span className="ImportantTodayHeroLineIcon" aria-hidden="true">
                              <MapPinIcon />
                            </span>
                            <span className="ImportantTodayHeroLineText ImportantTodayHeroLineText--truncate">{secondaryTodayEvent.location}</span>
                            {secondaryTodayEvent.status === 'CANCELLED' && <span className="ImportantTodayHeroCancelled">ODWOŁANE</span>}
                          </div>
                          {secondaryTodayEvent.is_individual && (secondaryTodayEvent.target_child_label ?? '').trim() && (
                            <div className="ImportantTodayHeroLine">
                              <span className="ImportantTodayHeroLineIcon" aria-hidden="true">
                                👤
                              </span>
                              <span className="ImportantTodayHeroLineText">{`Podopieczny: ${(secondaryTodayEvent.target_child_label ?? '').trim()}`}</span>
                            </div>
                          )}
                        </button>

                        <div className="ImportantTodayHeroSide" aria-hidden="true">
                          <div className="ImportantTodayHeroActions">
                            <button
                              className="ImportantTodayIconBtn"
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                openExternalUrl(googleMapsUrlFromLocation(secondaryTodayEvent.location));
                              }}
                              aria-label="Google Maps"
                              disabled={isLoading}
                            >
                              <MapIcon />
                            </button>
                            <button
                              className={['ImportantTodayIconBtn', transportIconClass(secondaryTodayEvent)].filter(Boolean).join(' ')}
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                openCarpoolFromEventId(secondaryTodayEvent.id);
                              }}
                              aria-label="Transport (carpooling)"
                              disabled={isLoading}
                            >
                              <CarIcon />
                            </button>
                          </div>
                          {!importantTodayCompact && (
                            <div className="ImportantTodayHeroArt">
                              {secondaryTodayEvent.type === 'MATCH' ? <SoccerIcon /> : <TrainingIcon />}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {(secondaryTodayEvent || otherTodayEvents.length > 0) && !importantTodayCollapsed && otherTodayEvents.length > 0 && (
                    <div className="ImportantTodayList" aria-label="Pozostałe wydarzenia na dziś">
                      {otherTodayEvents.map((e) => (
                        <div key={e.id} className="ImportantTodayRow">
                          <button
                            type="button"
                            className="ImportantTodayRowMain"
                            onClick={(ev) => {
                              ev.stopPropagation();
                              openEventFromListItem(e);
                            }}
                            onKeyDown={(ev) => {
                              if (ev.key !== 'Enter' && ev.key !== ' ') return;
                              ev.stopPropagation();
                            }}
                            aria-label={`${e.type === 'MATCH' ? 'Mecz' : 'Trening'}: ${e.title}`}
                          >
                            <div className="ImportantTodayRowTitle">{(e.title || (e.type === 'MATCH' ? 'MECZ' : 'TRENING')).toUpperCase()}</div>
                            <div className="ImportantTodayRowMeta">
                              <span className="ImportantTodayRowMetaItem">{formatTime(e.start_at)}</span>
                              <span className="ImportantTodayRowMetaSep" aria-hidden="true">
                                •
                              </span>
                              <span className="ImportantTodayRowMetaItem">{e.location}</span>
                              {e.is_individual && (e.target_child_label ?? '').trim() && (
                                <>
                                  <span className="ImportantTodayRowMetaSep" aria-hidden="true">
                                    •
                                  </span>
                                  <span className="ImportantTodayRowMetaItem">{`Podopieczny: ${(e.target_child_label ?? '').trim()}`}</span>
                                </>
                              )}
                              {e.status === 'CANCELLED' && (
                                <>
                                  <span className="ImportantTodayRowMetaSep" aria-hidden="true">
                                    •
                                  </span>
                                  <span className="ImportantTodayRowMetaItem is-cancelled">ODWOŁANE</span>
                                </>
                              )}
                            </div>
                          </button>
                          <div className="ImportantTodayRowActions">
                            {!importantTodayCompact && (
                              <button
                                className="ImportantTodayIconBtn"
                                type="button"
                                onClick={(ev) => {
                                  ev.stopPropagation();
                                  openExternalUrl(googleMapsUrlFromLocation(e.location));
                                }}
                                aria-label="Google Maps"
                                disabled={isLoading}
                              >
                                <MapIcon />
                              </button>
                            )}
                            <button
                              className={['ImportantTodayIconBtn', transportIconClass(e)].filter(Boolean).join(' ')}
                              type="button"
                              onClick={(ev) => {
                                ev.stopPropagation();
                                openCarpoolFromEventId(e.id);
                              }}
                              aria-label="Transport (carpooling)"
                              disabled={isLoading}
                            >
                              <CarIcon />
                            </button>
                            <span className="ImportantTodayTypeBadge" aria-hidden="true">
                              {e.type === 'MATCH' ? <SoccerIcon /> : <TrainingIcon />}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {isIndividualCoachFeed && upcomingCoachIndividualTrainings.length > 0 && (
            <div aria-label="Nadchodzące treningi indywidualne">
              <div className="CoachSectionTitle">NADCHODZĄCE TRENINGI</div>
              <div className="CoachMatchList">
                {upcomingCoachIndividualTrainings.map((e) => (
                  <CoachTrainingBanner
                    key={e.id}
                    e={e}
                    onOpen={() => openEventFromListItem(e)}
                    disabled={isLoading}
                    resolveChildPhotoUrl={resolveChildPhotoUrl}
                  />
                ))}
              </div>
            </div>
          )}

          {pinnedCancelledPosts.length > 0 && (
            <div
              className="CancelledPinnedBanner"
              role="button"
              tabIndex={0}
              aria-label="Odwołane wydarzenia"
              onClick={() => setIsCancelledOpen(true)}
              onKeyDown={(e) => {
                if (e.key !== 'Enter' && e.key !== ' ') return;
                e.preventDefault();
                setIsCancelledOpen(true);
              }}
            >
              <div className="CancelledPinnedLeft">
                <div className="CancelledPinnedTitle">
                  <span className="CancelledPinnedIcon" aria-hidden="true">
                    ⛔
                  </span>
                  ODWOŁANE
                </div>
                <div className="CancelledPinnedSubtitle">{cancelledPinnedSummary ?? 'Otwórz listę odwołanych wydarzeń'}</div>
              </div>
              <button
                className="CancelledPinnedMore"
                type="button"
                onClick={(ev) => {
                  ev.stopPropagation();
                  setIsCancelledOpen(true);
                }}
                disabled={isLoading}
              >
                Pokaż
              </button>
            </div>
          )}

          {pinnedScheduleChangePosts.length > 0 && (
            <div
              className={[
                'UpcomingMatchCard',
                'ScheduleChangeBanner',
                scheduleChangesCollapsed ? 'ScheduleChangeBanner--collapsed' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              role="region"
              aria-label="Zmiany terminów i godzin"
            >
              <div
                className="ScheduleChangeBannerHeader"
                role="button"
                tabIndex={0}
                aria-expanded={!scheduleChangesCollapsed}
                onClick={() => toggleScheduleChangesCollapsed()}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter' && e.key !== ' ') return;
                  e.preventDefault();
                  toggleScheduleChangesCollapsed();
                }}
              >
                <div className="ScheduleChangeBannerHeaderLeft">
                  <div className="ScheduleChangeBannerIcon" aria-hidden="true">⚠️</div>
                  <div className="ScheduleChangeBannerHeaderText">
                    <div className="ScheduleChangeBannerTitle">
                      {scheduleChangesCollapsed
                        ? `⚠️ ZMIANA${pinnedScheduleChangePosts.length > 1 ? `${pinnedScheduleChangePosts.length} ZMIAN` : ' ZMIANA'} — ${
                            pinnedScheduleChangePosts[0]!.ref_event_id
                              ? (eventById[pinnedScheduleChangePosts[0]!.ref_event_id]?.type ?? '').toUpperCase() === 'MATCH'
                                ? 'MECZ / TRENING'
                                : 'TRENING / MECZ'
                              : 'TERMINU'
                          }`
                        : `⚠️ ZMIANY TERMINÓW (${pinnedScheduleChangePosts.length})`}
                    </div>
                    {!scheduleChangesCollapsed && (
                      <div className="ScheduleChangeBannerSubtitle">
                        Kliknij ⚠️ aby zobaczyć co się zmieniło. Po przeczytaniu potwierdź „Przeczytałem”.
                      </div>
                    )}
                    {scheduleChangesCollapsed && (
                      <div className="ScheduleChangeBannerSubtitle">
                        {(() => {
                          const first = pinnedScheduleChangePosts[0]!;
                          const d = first.ref_event_id ? eventById[first.ref_event_id] ?? null : null;
                          const when = d ? formatDateTime(d.start_at) : formatDateTime(first.created_at);
                          const where = d?.location ? ` • ${d.location}` : '';
                          return `${when}${where}`;
                        })()}
                      </div>
                    )}
                  </div>
                </div>
                <div className="ScheduleChangeBannerHeaderRight">
                  <button
                    className="ScheduleChangeBannerToggle"
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleScheduleChangesCollapsed();
                    }}
                    disabled={isLoading}
                    aria-label={scheduleChangesCollapsed ? 'Rozwiń zmiany terminów' : 'Zwiń zmiany terminów'}
                  >
                    {scheduleChangesCollapsed ? 'Rozwiń' : 'Zwiń'}
                  </button>
                  <button
                    className="ScheduleChangeBannerClose"
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleScheduleChangesCollapsed();
                    }}
                    disabled={isLoading}
                    aria-label="Ukryj baner zmian"
                  >
                    ×
                  </button>
                </div>
              </div>
              {!scheduleChangesCollapsed && (
                <div className="ScheduleChangeBannerList" aria-label="Lista zmian">
                  {pinnedScheduleChangePosts.slice(0, 6).map((p) => {
                    const d = p.ref_event_id ? eventById[p.ref_event_id] ?? null : null;
                    const typeLabel = d
                      ? d.type === 'MATCH'
                        ? 'MECZ'
                        : d.type === 'TRAINING'
                          ? 'TRENING'
                          : 'WYDARZENIE'
                      : isScheduleChangePost(p)
                        ? 'TERMIN'
                        : 'WYDARZENIE';
                    const when = d ? formatDateTime(d.start_at) : formatDateTime(p.created_at);
                    const where = d?.location ? ` • ${d.location}` : '';
                    const firstChange = (() => {
                      const line = (p.body || '')
                        .split('\n')
                        .map((l) => l.trim())
                        .find((l) => /^(•|[⏰📅📍🕒🏆📌🎖️❓⚠️])/.test(l) || l.includes('→'));
                      if (!line) return '';
                      const clean = line.replace(/^[•\s-]+/, '').trim();
                      return clean.length > 80 ? `${clean.slice(0, 80).trim()}…` : clean;
                    })();
                    return (
                      <div key={p.id} className="ScheduleChangeRow" role="button" tabIndex={0}>
                        <div className="ScheduleChangeRowLeft">
                          <div className="ScheduleChangeRowTitle">
                            ⚠️ ZMIANA {typeLabel}
                          </div>
                          <div className="ScheduleChangeRowMeta">
                            {when}{where}
                          </div>
                          {firstChange && (
                            <div className="ScheduleChangeRowChange">
                              {firstChange}
                            </div>
                          )}
                        </div>
                        <div className="ScheduleChangeRowRight">
                          <button
                            className="ScheduleChangeRowBtn"
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (p.ref_event_id) navigate('kalendarz', { e: p.ref_event_id });
                              else navigate('feed', { p: p.id });
                            }}
                            disabled={isLoading}
                          >
                            Szczegóły
                          </button>
                          {p.is_important && props.me.role === 'PARENT' ? (
                            <button
                              className={[
                                'ScheduleChangeRowBtn',
                                'ScheduleChangeRowBtn--primary',
                                p.my_confirmed ? 'is-done' : '',
                              ]
                                .filter(Boolean)
                                .join(' ')}
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                confirmRead(p);
                              }}
                              disabled={isLoading || p.my_confirmed}
                            >
                              {p.my_confirmed ? `Przeczytano (${p.confirms_count})` : 'Przeczytałem'}
                            </button>
                          ) : p.is_important ? (
                            <span className="ScheduleChangeRowBadge" aria-label="Post ważny">
                              Ważne ⭐
                            </span>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                  {pinnedScheduleChangePosts.length > 6 && (
                    <div className="ScheduleChangeBannerMore">
                      +{pinnedScheduleChangePosts.length - 6} starszych zmian — zobacz w Aktualnościach poniżej.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="ListMeta">
        <div className="ListMetaLeft">{groupLabel.toUpperCase()}</div>
        <div className="ListMetaRight">{posts.length} POSTÓW</div>
      </div>

      <div className="PostList" aria-label="Posty">
        {isLoading && posts.length === 0 && <div className="Muted">Ładowanie…</div>}
        {!isLoading && posts.length === 0 && <div className="Muted">Brak postów.</div>}

        {restPosts.map((p) => {
          const d = p.ref_event_id ? eventById[p.ref_event_id] ?? null : null;
          const isEventCard = !!p.ref_event_id && !!d;
          const listEvent = isEventCard && p.ref_event_id ? eventListById[p.ref_event_id] ?? null : null;
          const isIndividualEvent = !!listEvent?.is_individual;
          const isExpanded = !!expandedByPostId[p.id];
          const isChangePost = isScheduleChangePost(p);
          let v = isEventCard
            ? d!.type === 'MATCH'
              ? isIndividualEvent
                ? { emoji: '🎯', variant: 'match', label: 'Dodatkowy mecz' }
                : { emoji: matchKindEmoji(listEvent?.match_kind), variant: 'match', label: matchKindLabel(listEvent?.match_kind) }
              : d!.type === 'TRAINING'
                ? isIndividualEvent
                  ? { emoji: '🎯', variant: 'training', label: 'Trening indywidualny' }
                  : { emoji: '🏋️', variant: 'training', label: 'Trening' }
                : feedCategoryVisual(p.category, p.kind, {
                    isImportant: p.is_important,
                    isScheduleChange: isChangePost,
                  })
            : feedCategoryVisual(p.category, p.kind, {
                isImportant: p.is_important,
                isScheduleChange: isChangePost,
              });
          if (p.is_important && isChangePost) {
            v = { emoji: '⚠️', variant: 'warn-change', label: 'UWAGA' };
          }
          const cardTitle = isEventCard ? d!.title : p.title;
          const ctaLabel =
            isEventCard && d!.type === 'MATCH' && props.me.role === 'PARENT' ? parentMatchCtaLabel(d!) : 'OTWÓRZ';
          const eventVariant = isEventCard ? (d!.type === 'MATCH' ? 'EventCard--match' : 'EventCard--training') : null;
          const isRoleCoaches = props.me.role === 'COACH' || props.me.role === 'ADMIN';
          const visuallyImportantForUser = p.is_important && (isRoleCoaches || isChangePost);
          return (
            <section
              key={p.id}
              id={`feed-post-${p.id}`}
              tabIndex={-1}
              className={[
                'PostCard',
                `PostCard--${v.variant}`,
                isEventCard && isIndividualEvent ? 'PostCard--individual' : '',
                isEventCard && d!.status === 'CANCELLED' ? 'PostCard--cancelled' : '',
                isEventCard ? 'PostCard--event' : '',
                visuallyImportantForUser ? 'PostCard--important' : '',
                p.is_important && isChangePost ? 'PostCard--warn-change' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <div className="FeedCardTop">
                <div className="FeedCardTopLeft">
                  <span className="FeedCardTopEmoji" aria-hidden="true">
                    {v.emoji}
                  </span>
                  <span className="FeedCardTopText">{v.label.toUpperCase()}</span>
                </div>
                {isEventCard && (
                  <div className="FeedCardTopIcons">
                    {d!.type === 'MATCH' && isRoleCoaches && (
                      <button
                        className="FeedCardTopIcon"
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          const w = window as any;
                          if (typeof w.__acOpenResultEdit === 'function') {
                            try { w.__acOpenResultEdit(d!); return; } catch(_){}
                          }
                          w.__acPendingResultEditEvent = d!;
                          const curr = (window.location.hash || '').replace(/^#\/?/, '');
                          if (curr !== 'druzyna' && curr !== 'druzyna/rozgrywki') {
                            window.location.hash = '#druzyna';
                          }
                        }}
                        aria-label="Edytuj wynik meczu"
                      >
                        <CheckIcon />
                      </button>
                    )}
                    <button className="FeedCardTopIcon" type="button" onClick={() => openMapsFromPost(p)} aria-label="Google Maps">
                      <MapIcon />
                    </button>
                    <button
                      className={[
                        'FeedCardTopIcon',
                        transportIconClass(p.ref_event_id ? eventListById[p.ref_event_id] : null),
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      type="button"
                      onClick={() => openCarpoolFromPost(p)}
                      aria-label="Transport (carpooling)"
                    >
                      <CarIcon />
                    </button>
                  </div>
                )}
              </div>

              {isEventCard ? (
                <div
                  className={[
                    'EventCard',
                    eventVariant || '',
                    'EventCard--feed',
                    'EventCard--ultraFeed',
                    isExpanded ? 'is-expanded' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  role="button"
                  tabIndex={0}
                  onClick={() => openEventFromPost(p)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') openEventFromPost(p);
                  }}
                >
                  <div className="EventCardMain">
                    {(() => {
                      const isMatch = d!.type === 'MATCH';
                      const opponentLabel = isMatch ? d!.opponent_name || opponentLabelFromMatchTitle(d!.title || '') || 'RYWAL' : '';
                      const title = isMatch ? opponentLabel : cardTitle;
                      const compactChildLabel = (d!.target_child_label ?? '').trim();
                      const typeLabel = isMatch ? 'MECZ' : 'TRENING';
                      const groupId = (d!.group_id ?? '').trim();
                      const groupLabel = groupId ? (props.workGroups.find((g) => g.id === groupId)?.name ?? '').trim() : '';
                      const locationLabel = (d!.location ?? '').trim();
                      const singleLineLead = [
                        typeLabel,
                        formatDateTime(d!.start_at),
                        compactChildLabel ? `Podopieczny: ${compactChildLabel}` : null,
                        groupLabel ? groupLabel : null,
                        locationLabel ? locationLabel : null,
                      ]
                        .filter(Boolean)
                        .join(' · ');
                      return (
                        <>
                          <div className={['EventCardTitle', isMatch ? 'EventCardTitle--opponent' : '', 'EventCardTitle--feedUltra'].filter(Boolean).join(' ')}>
                            {title.toUpperCase()}
                          </div>
                          {!isExpanded && (
                            <div className="EventCardFeedSingleMeta">
                              <span className="EventCardFeedSingleMetaText" title={singleLineLead}>
                                {singleLineLead}
                              </span>
                            </div>
                          )}
                          {isExpanded && compactChildLabel && <div className="EventCardCompactSub">{`Podopieczny: ${compactChildLabel}`}</div>}
                        </>
                      );
                    })()}
                    <div
                      className={[
                        'EventCardMeta',
                        d!.type === 'MATCH' ? 'EventCardMeta--match' : '',
                        !isExpanded ? 'EventCardMeta--compact' : '',
                        !isExpanded ? 'EventCardMeta--ultraFeedCollapsed' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      {d!.type === 'MATCH' && (
                        <div className={['EventCardMetaLine', !isExpanded ? 'is-hidden' : ''].filter(Boolean).join(' ')}>
                          <span className="EventMetaIcon" aria-hidden="true">
                            <SoccerIcon />
                          </span>
                          <span className="EventMetaText">{(d!.title || '').trim() ? d!.title : 'Mecz'}</span>
                        </div>
                      )}
                      <div className={['EventCardMetaLine', d!.type === 'MATCH' && !isExpanded ? 'EventCardMetaLine--primary' : '', !isExpanded ? 'is-hidden' : ''].filter(Boolean).join(' ')}>
                        <span className="EventMetaStrong">{formatDateTime(d!.start_at)}</span>
                      </div>
                      <div className={['EventCardMetaLine', !isExpanded ? 'is-hidden' : ''].filter(Boolean).join(' ')}>
                        <span className="EventMetaIcon" aria-hidden="true">
                          <MapPinIcon />
                        </span>
                        <span className="EventMetaText">{d!.location}</span>
                      </div>
                      {d!.type === 'MATCH' && !!(d! as EventDetails).match_deadline_at && (
                        <div className={['EventCardMetaLine', !isExpanded ? 'is-hidden' : ''].filter(Boolean).join(' ')}>
                          <span className="EventMetaIcon" aria-hidden="true">
                            <ClockIcon />
                          </span>
                          <span className="EventMetaText">{`Deadline: ${formatDateTime((d! as EventDetails).match_deadline_at!)}`}</span>
                        </div>
                      )}
                      {d!.type === 'MATCH' && !!(d! as EventDetails).match_meet_at && (
                        <div className={['EventCardMetaLine', !isExpanded ? 'is-hidden' : ''].filter(Boolean).join(' ')}>
                          <span className="EventMetaIcon" aria-hidden="true">⏰</span>
                          <span className="EventMetaText">{`Zbiórka: ${formatDateTime((d! as EventDetails).match_meet_at!)}`}</span>
                        </div>
                      )}
                      {d!.description && (
                        <div className={['EventCardMetaLine is-sub', !isExpanded ? 'is-hidden' : ''].filter(Boolean).join(' ')}>{d!.description}</div>
                      )}
                    </div>
                    <div className="FeedEventBottom FeedEventBottom--ultraFeed">
                      <button
                        type="button"
                        className={['FeedMoreLink', !isExpanded ? 'FeedMoreLink--ultraFeed' : ''].filter(Boolean).join(' ')}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleExpanded(p.id);
                        }}
                      >
                        {isExpanded ? 'Mniej' : 'Więcej'}
                      </button>
                      <button
                        type="button"
                        className={['EventPill', 'EventPill--feedUltra'].filter(Boolean).join(' ')}
                        onClick={(e) => {
                          e.stopPropagation();
                          openEventFromPost(p);
                        }}
                        disabled={isLoading}
                      >
                        {ctaLabel}
                      </button>
                    </div>
                  </div>
                  {d!.type === 'MATCH' && (
                    <div className={['EventCardSide', 'EventCardSide--ultraFeed'].filter(Boolean).join(' ')} aria-hidden="true">
                      {(() => {
                        const opponentLabel = d!.opponent_name || opponentLabelFromMatchTitle(d!.title || '') || 'RYWAL';
                        const logoUrl = (d!.opponent_logo_url ?? '').trim();
                        const opponent = layoutOpponentLabel(opponentLabel);
                        return (
                          <div className={['EventOpponentCircle UpcomingMatchLogo is-opponent', 'EventOpponentCircle--feedUltra'].filter(Boolean).join(' ')}>
                            {logoUrl ? (
                              <img className="OpponentLogoImg" src={logoUrl} alt={opponentLabel} />
                            ) : (
                              <div className="OpponentLogoInner">
                                <div
                                  className={['OpponentText', opponent.lines.length === 1 ? 'is-single' : 'is-multi'].filter(Boolean).join(' ')}
                                  style={{ transform: `scale(${opponent.scale})` }}
                                >
                                  {opponent.lines.map((line, idx) => (
                                    <div key={`${idx}-${line}`} className="OpponentTextLine">
                                      {line}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              ) : (
                (() => {
                  const atts = p.attachments ?? [];
                  const imgs = atts.filter((a) => isImageAttachment(a));
                  const primary = imgs[0] ?? null;
                  const primaryUrl = primary ? (imageUrlByAttachmentId[primary.id] ?? null) : null;
                  const bodyText = p.body || '';
                  const shouldClampBody = shouldClampPostBody(bodyText);
                  if (primary) {
                    return (
                      <div className={['PostContent', 'PostContent--mediaRow', p.is_important ? 'PostContent--important' : ''].filter(Boolean).join(' ')}>
                        <button
                          type="button"
                          className="PostMediaThumbBtn"
                          onClick={() => (primaryUrl ? openImagePreview(primary) : downloadAttachment(primary))}
                          disabled={isLoading}
                          aria-label="Otwórz zdjęcie"
                        >
                          {primaryUrl ? (
                            <img className="PostMediaThumbImg" src={primaryUrl} alt="" loading="lazy" />
                          ) : (
                            <div className="PostMediaThumbPh">…</div>
                          )}
                        </button>
                        <div className="PostBody">
                          <div className="PostTitle">{cardTitle}</div>
                          <div
                            className={['PostBodyText', shouldClampBody && !isExpanded ? 'PostBodyText--clamp' : ''].filter(Boolean).join(' ')}
                            style={{ whiteSpace: 'pre-wrap' }}
                          >
                            {bodyText}
                          </div>
                          {shouldClampBody && (
                            <div className="PostBodyToggleRow">
                              <button type="button" className="FeedMoreLink" onClick={() => toggleExpanded(p.id)}>
                                {isExpanded ? 'Mniej' : 'Więcej'}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div className={['PostContent', 'PostContent--stack', p.is_important ? 'PostContent--important' : ''].filter(Boolean).join(' ')}>
                      <div className="PostBody">
                        <div className="PostTitle">{cardTitle}</div>
                        <div
                          className={['PostBodyText', shouldClampBody && !isExpanded ? 'PostBodyText--clamp' : ''].filter(Boolean).join(' ')}
                          style={{ whiteSpace: 'pre-wrap' }}
                        >
                          {bodyText}
                        </div>
                        {shouldClampBody && (
                          <div className="PostBodyToggleRow">
                            <button type="button" className="FeedMoreLink" onClick={() => toggleExpanded(p.id)}>
                              {isExpanded ? 'Mniej' : 'Więcej'}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()
              )}

              {(() => {
                const atts = p.attachments ?? [];
                if (isEventCard) return renderAttachments(atts);
                const imgs = atts.filter((a) => isImageAttachment(a));
                const primary = imgs[0] ?? null;
                return primary ? renderAttachments(atts, new Set([primary.id])) : renderAttachments(atts);
              })()}

              <div className="FeedCardActions">
                <div className="FeedCardActionsLeft">
                  {canPost && p.kind === 'POST' && (
                    <button
                      className="FeedEditIcon"
                      type="button"
                      onClick={() => openEditPost(p)}
                      disabled={isLoading}
                      aria-label="Edytuj post"
                    >
                      ✎
                    </button>
                  )}
                  {canDeletePost && (p.kind === 'POST' || p.kind === 'SYSTEM') && (
                    <button
                      className="FeedDeleteIcon"
                      type="button"
                      onClick={() => openDeletePostConfirm(p)}
                      disabled={isLoading}
                      aria-label="Usuń post"
                    >
                      <TrashIcon className="FeedDeleteIconSvg" />
                    </button>
                  )}
                  <div className="FeedCardAuthor">
                    {p.kind === 'SYSTEM' ? (
                      <span className="Muted">{formatDateTime(p.created_at)}</span>
                    ) : (
                      <span className="Muted">{`${p.author_label} • ${formatDateTime(p.created_at)}`}</span>
                    )}
                  </div>
                </div>
                <div className="FeedCardActionsRight">
                  {p.is_important && props.me.role === 'PARENT' && isChangePost ? (
                    <button
                      className={['FeedChip', p.my_confirmed ? 'is-done' : ''].filter(Boolean).join(' ')}
                      type="button"
                      onClick={() => confirmRead(p)}
                      disabled={isLoading || p.my_confirmed}
                    >
                      {p.my_confirmed ? `Przeczytano (${p.confirms_count})` : `Przeczytałem (${p.confirms_count})`}
                      <span className="FeedChipStar" aria-hidden="true">
                        ⭐
                      </span>
                    </button>
                  ) : p.is_important && isRoleCoaches ? (
                    <span className="FeedChip is-done" aria-label="Post ważny">
                      Ważne <span className="FeedChipStar" aria-hidden="true">⭐</span>
                    </span>
                  ) : null}
                  <button className="FeedChip" type="button" onClick={() => react(p, '👍')} disabled={isLoading}>
                    👍
                  </button>
                  <button className="FeedChip" type="button" onClick={() => react(p, '❤️')} disabled={isLoading}>
                    ❤️
                  </button>
                </div>
              </div>

              {p.reactions.length > 0 && (
                <div className="FeedReactions">
                  {p.reactions.map((r) => (
                    <span key={r.emoji} className={['FeedReaction', r.is_me ? 'is-me' : ''].filter(Boolean).join(' ')}>
                      {r.emoji} {r.count}
                    </span>
                  ))}
                </div>
              )}
            </section>
          );
        })}
      </div>

      {isCancelledOpen && (
        <div
          className="SheetOverlay"
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => {
            if (isLoading) return;
            if (e.target === e.currentTarget) setIsCancelledOpen(false);
          }}
        >
          <div className="Sheet">
            <div className="SheetHeaderRow SheetHeaderRow--sticky">
              <div className="SheetTitle">⛔ ODWOŁANE</div>
              <button className="SheetCloseButton" type="button" aria-label="Zamknij" onClick={() => setIsCancelledOpen(false)} disabled={isLoading}>
                ×
              </button>
            </div>
            <div className="Muted">Kliknij pozycję, aby otworzyć wydarzenie.</div>
            <div className="List">
              {pinnedCancelledPosts.map((p) => {
                const d = p.ref_event_id ? eventById[p.ref_event_id] ?? null : null;
                const title = d ? (d.title || '').trim() || (d.type === 'MATCH' ? 'Mecz' : 'Trening') : (p.title || '').trim() || 'Post';
                const meta = d ? `${formatDateTime(d.start_at)} • ${d.location}` : (p.body || '').trim().replace(/\s+/g, ' ');
                return (
                  <button
                    key={p.id}
                    className="ListItem"
                    type="button"
                    onClick={() => {
                      setIsCancelledOpen(false);
                      if (p.ref_event_id) navigate('kalendarz', { e: p.ref_event_id });
                      else navigate('feed', { p: p.id });
                    }}
                    disabled={isLoading}
                  >
                    <div className="ListItemTop">
                      <div className="ListItemTitle">{title.toUpperCase()}</div>
                      <div className="ListItemBadge">⛔</div>
                    </div>
                    {meta ? <div className="Muted">{meta}</div> : null}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {composerOpen && (
        <div
          className="SheetOverlay"
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => {
            if (isLoading) return;
            if (e.target === e.currentTarget) {
              setComposerOpen(false);
              setEditPostId(null);
                      setEditExistingAttachments([]);
                      setDeletingAttachmentId(null);
              setNewTitle('');
              setNewBody('');
              setNewImportant(false);
              setNewLinkUrl('');
              setNewLinkTitle('');
              setNewFiles([]);
            }
          }}
        >
          <div className="Sheet">
            {postComposer}
          </div>
        </div>
      )}

      {carpoolEventId && (
        <div
          className="SheetOverlay"
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => {
            if (isLoading) return;
            if (e.target === e.currentTarget) setCarpoolEventId(null);
          }}
        >
          <div className="Sheet">
            {(() => {
              const fromList = events.find((e) => e.id === carpoolEventId) ?? null;
              const fromDetails = carpoolEventId ? eventById[carpoolEventId] ?? null : null;
              const title = (fromDetails?.title ?? fromList?.title ?? '').trim() || null;
              const loc = (fromDetails?.location ?? fromList?.location ?? '').trim() || null;
              const when = (fromDetails?.start_at ?? fromList?.start_at ?? null) as string | null;
              const meta = [when ? formatDateTime(when) : null, loc].filter(Boolean).join(' • ') || null;
              return (
                <CarpoolSheet
                  token={props.token}
                  me={props.me}
                  eventId={carpoolEventId}
                  childId={props.selectedChild?.id ?? null}
                  eventTitle={title}
                  eventMeta={meta}
                  onChanged={load}
                  onOpenEvent={() => {
                    const id = carpoolEventId;
                    setCarpoolEventId(null);
                    if (id) navigate('kalendarz', { e: id });
                  }}
                  onClose={() => setCarpoolEventId(null)}
                />
              );
            })()}
          </div>
        </div>
      )}

      {imagePreview && (
        <div
          className="SheetOverlay"
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => {
            if (isLoading) return;
            if (e.target === e.currentTarget) setImagePreview(null);
          }}
        >
          <div className="Sheet Sheet--imagePreview">
            <div className="SheetHeaderRow SheetHeaderRow--sticky">
              <div className="SheetTitle">ZDJĘCIE</div>
              <button className="SheetCloseButton" type="button" aria-label="Zamknij" onClick={() => setImagePreview(null)} disabled={isLoading}>
                ×
              </button>
            </div>
            <div className="FeedImagePreviewBody">
              {(() => {
                const url = imageUrlByAttachmentId[imagePreview.attachmentId] ?? null;
                const iw = imagePreviewImgDim?.w ?? 0;
                const ih = imagePreviewImgDim?.h ?? 0;
                const vw = imagePreviewVpDim?.w ?? 0;
                const vh = imagePreviewVpDim?.h ?? 0;
                const baseScale = iw && ih && vw && vh ? Math.min(vw / iw, vh / ih) : 0;
                const baseW = baseScale ? iw * baseScale : 0;
                const baseH = baseScale ? ih * baseScale : 0;
                const z = clampImagePreviewZoom(imagePreviewZoom);
                const pan = clampImagePreviewPan({ x: imagePreviewPan.x, y: imagePreviewPan.y, zoom: z });

                if (!url) return <div className="Muted">Ładowanie…</div>;
                return (
                  <>
                    <div className="FeedZoomControls" aria-label="Powiększenie zdjęcia">
                      <button
                        className="FeedZoomBtn"
                        type="button"
                        onClick={() => setImagePreviewZoom((p) => clampImagePreviewZoom(p / 1.15))}
                        disabled={isLoading || z <= 1.01}
                        aria-label="Pomniejsz"
                      >
                        −
                      </button>
                      <button
                        className="FeedZoomBtn"
                        type="button"
                        onClick={() => {
                          setImagePreviewZoom(1);
                          setImagePreviewPan({ x: 0, y: 0 });
                        }}
                        disabled={isLoading || (z <= 1.01 && Math.abs(pan.x) < 0.5 && Math.abs(pan.y) < 0.5)}
                        aria-label="Reset"
                      >
                        1×
                      </button>
                      <button
                        className="FeedZoomBtn"
                        type="button"
                        onClick={() => setImagePreviewZoom((p) => clampImagePreviewZoom(p * 1.15))}
                        disabled={isLoading || z >= 3.99}
                        aria-label="Powiększ"
                      >
                        +
                      </button>
                    </div>

                    <div
                      className="FeedImagePreviewViewport"
                      ref={imagePreviewVpRef}
                      onWheel={(e) => {
                        if (isLoading) return;
                        if (!e.ctrlKey && z <= 1.01) return;
                        e.preventDefault();
                        const k = e.deltaY > 0 ? 0.92 : 1.08;
                        setImagePreviewZoom((p) => clampImagePreviewZoom(p * k));
                      }}
                      onDoubleClick={() => {
                        if (isLoading) return;
                        setImagePreviewZoom((p) => (p <= 1.01 ? 2 : 1));
                        setImagePreviewPan({ x: 0, y: 0 });
                      }}
                      onPointerDown={(e) => {
                        if (isLoading) return;
                        (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
                        imagePreviewPointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
                        const pts = Array.from(imagePreviewPointersRef.current.values());
                        if (pts.length === 1) {
                          imagePreviewGestureRef.current = {
                            startZoom: z,
                            startPan: { x: pan.x, y: pan.y },
                            startDist: 0,
                            startCenter: { x: pts[0]!.x, y: pts[0]!.y },
                          };
                        } else if (pts.length >= 2) {
                          const a = pts[0]!;
                          const b = pts[1]!;
                          const dx = b.x - a.x;
                          const dy = b.y - a.y;
                          imagePreviewGestureRef.current = {
                            startZoom: z,
                            startPan: { x: pan.x, y: pan.y },
                            startDist: Math.hypot(dx, dy) || 1,
                            startCenter: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
                          };
                        }
                      }}
                      onPointerMove={(e) => {
                        const g = imagePreviewGestureRef.current;
                        if (!g) return;
                        if (!imagePreviewPointersRef.current.has(e.pointerId)) return;
                        imagePreviewPointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
                        const pts = Array.from(imagePreviewPointersRef.current.values());
                        if (pts.length === 1) {
                          if (z <= 1.01) return;
                          const p0 = pts[0]!;
                          const dx = p0.x - g.startCenter.x;
                          const dy = p0.y - g.startCenter.y;
                          const next = clampImagePreviewPan({ x: g.startPan.x + dx, y: g.startPan.y + dy, zoom: g.startZoom });
                          setImagePreviewPan(next);
                          return;
                        }
                        if (pts.length >= 2) {
                          const a = pts[0]!;
                          const b = pts[1]!;
                          const dx = b.x - a.x;
                          const dy = b.y - a.y;
                          const dist = Math.hypot(dx, dy) || 1;
                          const center = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
                          const scale = dist / (g.startDist || 1);
                          const nextZoom = clampImagePreviewZoom(g.startZoom * scale);
                          const cdx = center.x - g.startCenter.x;
                          const cdy = center.y - g.startCenter.y;
                          setImagePreviewZoom(nextZoom);
                          const nextPan = clampImagePreviewPan({ x: g.startPan.x + cdx, y: g.startPan.y + cdy, zoom: nextZoom });
                          setImagePreviewPan(nextPan);
                        }
                      }}
                      onPointerUp={(e) => {
                        imagePreviewPointersRef.current.delete(e.pointerId);
                        const pts = Array.from(imagePreviewPointersRef.current.values());
                        if (pts.length === 1) {
                          imagePreviewGestureRef.current = {
                            startZoom: z,
                            startPan: { x: pan.x, y: pan.y },
                            startDist: 0,
                            startCenter: { x: pts[0]!.x, y: pts[0]!.y },
                          };
                        } else if (pts.length === 0) {
                          imagePreviewGestureRef.current = null;
                        }
                      }}
                      onPointerCancel={(e) => {
                        imagePreviewPointersRef.current.delete(e.pointerId);
                        if (imagePreviewPointersRef.current.size === 0) imagePreviewGestureRef.current = null;
                      }}
                    >
                      {baseW > 0 && baseH > 0 ? (
                        <img
                          className="FeedImagePreviewImg"
                          src={url}
                          alt="Zdjęcie"
                          style={{
                            width: baseW,
                            height: baseH,
                            transform: `translate(-50%, -50%) translate(${pan.x}px, ${pan.y}px) scale(${z})`,
                          }}
                          draggable={false}
                        />
                      ) : (
                        <div className="Muted">Ładowanie…</div>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>
            <div className="SheetActionsRow">
              <SecondaryButton
                type="button"
                disabled={isLoading}
                onClick={() => {
                  const a = attachmentById[imagePreview.attachmentId] ?? null;
                  if (a) downloadAttachment(a);
                }}
              >
                Pobierz
              </SecondaryButton>
              <PrimaryButton type="button" disabled={isLoading} onClick={() => setImagePreview(null)}>
                Zamknij
              </PrimaryButton>
            </div>
          </div>
        </div>
      )}

      {deletePostConfirm && (
        <div
          className="SheetOverlay SheetOverlay--top"
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => {
            if (isLoading) return;
            if (e.target === e.currentTarget) setDeletePostConfirm(null);
          }}
        >
          <div className="Sheet">
            <div className="SheetHeaderRow SheetHeaderRow--sticky">
              <div className="SheetTitle">USUNĄĆ POST?</div>
              <button className="SheetCloseButton" type="button" aria-label="Zamknij" onClick={() => setDeletePostConfirm(null)} disabled={isLoading}>
                ×
              </button>
            </div>
            {deletePostConfirm.title ? <div className="Muted">{deletePostConfirm.title}</div> : null}
            <PrimaryButton onClick={onDeletePostConfirm} disabled={isLoading} type="button">
              Usuń
            </PrimaryButton>
            <SecondaryButton onClick={() => setDeletePostConfirm(null)} disabled={isLoading} type="button">
              Anuluj
            </SecondaryButton>
          </div>
        </div>
      )}

      {canPost && (props.me.role === 'COACH' || props.me.role === 'ADMIN') && (
        <button
          type="button"
          className="FeedFab"
          aria-label="Dodaj post"
          onClick={() => setComposerOpen(true)}
          disabled={isLoading || props.workGroups.length === 0 || !props.activeGroupId}
        >
          <PlusIcon className="FeedFabIcon" />
        </button>
      )}
    </div>
  );
}

function CarpoolSheet(props: {
  token: string;
  me: Me;
  eventId: string;
  childId?: string | null;
  eventTitle?: string | null;
  eventMeta?: string | null;
  onChanged?: () => void;
  onOpenEvent?: () => void;
  openEventLabel?: string | null;
  onClose: () => void;
}) {
  const [data, setData] = React.useState<TransportResponse | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [errorText, setErrorText] = React.useState<string | null>(null);
  const [offerSeats, setOfferSeats] = React.useState('3');
  const [offerNote, setOfferNote] = React.useState('');
  const [requestNote, setRequestNote] = React.useState('');
  const [showOfferEditor, setShowOfferEditor] = React.useState(false);
  const [showRequestEditor, setShowRequestEditor] = React.useState(false);

  const load = React.useCallback(async () => {
    setIsLoading(true);
    setErrorText(null);
    try {
      const res = await apiTransportGet(props.token, props.eventId, props.me.role === 'PARENT' ? props.childId ?? null : null);
      setData(res);
    } catch (e) {
      setErrorText(humanizeHttpError(e, 'Nie udało się pobrać transportu.'));
    } finally {
      setIsLoading(false);
    }
  }, [props.childId, props.eventId, props.me.role, props.token]);

  React.useEffect(() => {
    load();
  }, [load]);

  React.useEffect(() => {
    const myOfferId = data?.my_offer_id ?? null;
    if (!myOfferId) return;
    const my = (data?.offers ?? []).find((o) => o.id === myOfferId) ?? null;
    if (!my) return;
    setOfferSeats(String(my.seats_total ?? 0));
    setOfferNote(my.note ?? '');
  }, [data?.my_offer_id]);

  React.useEffect(() => {
    const myReq = data?.my_request ?? null;
    if (!myReq) return;
    setRequestNote(myReq.note ?? '');
  }, [data?.my_request?.id]);

  const saveRequest = React.useCallback(async () => {
    if (props.me.role !== 'PARENT') return;
    const cid = (props.childId ?? '').trim();
    if (!cid) return;
    setIsLoading(true);
    setErrorText(null);
    try {
      const res = await apiTransportUpsertRequest(
        props.token,
        props.eventId,
        { child_id: cid, note: requestNote.trim() ? requestNote.trim() : null },
        cid
      );
      setData(res);
      props.onChanged?.();
    } catch (e) {
      setErrorText(humanizeHttpError(e, 'Nie udało się zapisać zgłoszenia.'));
    } finally {
      setIsLoading(false);
    }
  }, [props.childId, props.eventId, props.me.role, props.onChanged, props.token, requestNote]);

  const deleteRequest = React.useCallback(async () => {
    if (props.me.role !== 'PARENT') return;
    const cid = (props.childId ?? '').trim();
    const rid = data?.my_request?.id ?? null;
    if (!cid || !rid) return;
    setIsLoading(true);
    setErrorText(null);
    try {
      const res = await apiTransportDeleteRequest(props.token, props.eventId, rid, cid);
      setData(res);
      setRequestNote('');
      setShowRequestEditor(false);
      props.onChanged?.();
    } catch (e) {
      setErrorText(humanizeHttpError(e, 'Nie udało się cofnąć zgłoszenia.'));
    } finally {
      setIsLoading(false);
    }
  }, [data?.my_request?.id, props.childId, props.eventId, props.me.role, props.onChanged, props.token]);

  const saveOffer = React.useCallback(async () => {
    if (props.me.role !== 'PARENT') return;
    const n = Number.parseInt(offerSeats, 10);
    if (!Number.isFinite(n) || n < 0 || n > 8) return;
    setIsLoading(true);
    setErrorText(null);
    try {
      const res = await apiTransportUpsertOffer(
        props.token,
        props.eventId,
        { seats_total: n, note: offerNote.trim() ? offerNote.trim() : null },
        props.childId ?? null
      );
      setData(res);
      props.onChanged?.();
    } catch (e) {
      setErrorText(humanizeHttpError(e, 'Nie udało się zapisać oferty.'));
    } finally {
      setIsLoading(false);
    }
  }, [offerNote, offerSeats, props.childId, props.eventId, props.me.role, props.onChanged, props.token]);

  const deleteOffer = React.useCallback(async () => {
    if (props.me.role !== 'PARENT') return;
    const offerId = data?.my_offer_id ?? null;
    if (!offerId) return;
    setIsLoading(true);
    setErrorText(null);
    try {
      const res = await apiTransportDeleteOffer(props.token, props.eventId, offerId, props.childId ?? null);
      setData(res);
      setOfferSeats('3');
      setOfferNote('');
      setShowOfferEditor(false);
      props.onChanged?.();
    } catch (e) {
      setErrorText(humanizeHttpError(e, 'Nie udało się usunąć oferty.'));
    } finally {
      setIsLoading(false);
    }
  }, [data?.my_offer_id, props.childId, props.eventId, props.me.role, props.onChanged, props.token]);

  const reserve = React.useCallback(
    async (offerId: string) => {
      if (props.me.role !== 'PARENT') return;
      const cid = (props.childId ?? '').trim();
      if (!cid) return;
      setIsLoading(true);
      setErrorText(null);
      try {
        const res = await apiTransportReserve(props.token, props.eventId, { offer_id: offerId, child_id: cid }, cid);
        setData(res);
      } catch (e) {
        setErrorText(humanizeHttpError(e, 'Nie udało się zarezerwować miejsca.'));
      } finally {
        setIsLoading(false);
      }
    },
    [props.childId, props.eventId, props.me.role, props.token]
  );

  const cancelReservation = React.useCallback(async () => {
    if (props.me.role !== 'PARENT') return;
    const rid = data?.my_reservation_id ?? null;
    const cid = (props.childId ?? '').trim();
    if (!rid || !cid) return;
    setIsLoading(true);
    setErrorText(null);
    try {
      const res = await apiTransportCancelReservation(props.token, props.eventId, rid, cid);
      setData(res);
    } catch (e) {
      setErrorText(humanizeHttpError(e, 'Nie udało się anulować rezerwacji.'));
    } finally {
      setIsLoading(false);
    }
  }, [data?.my_reservation_id, props.childId, props.eventId, props.me.role, props.token]);

  const takeRequest = React.useCallback(
    async (requestId: string) => {
      if (props.me.role !== 'PARENT') return;
      const cid = (props.childId ?? '').trim();
      if (!cid) return;
      const rid = requestId.trim();
      if (!rid) return;
      setIsLoading(true);
      setErrorText(null);
      try {
        const res = await apiTransportTakeRequest(props.token, props.eventId, rid, cid);
        setData(res);
        props.onChanged?.();
      } catch (e) {
        setErrorText(humanizeHttpError(e, 'Nie udało się potwierdzić „Zabiorę”.'));
      } finally {
        setIsLoading(false);
      }
    },
    [props.childId, props.eventId, props.me.role, props.onChanged, props.token]
  );

  const myOfferId = data?.my_offer_id ?? null;
  const myResOfferId = data?.my_reservation_offer_id ?? null;
  const canEditOffer = props.me.role === 'PARENT' && !!props.childId;
  const canEditRequest = props.me.role === 'PARENT' && !!props.childId;
  const myReq = data?.my_request ?? null;

  return (
    <>
      <div className="SheetHeaderRow SheetHeaderRow--sticky">
        <div className="SheetTitle">TRANSPORT (CARPOOLING)</div>
        <button className="SheetCloseButton" type="button" aria-label="Zamknij" onClick={props.onClose} disabled={isLoading}>
          ×
        </button>
      </div>
      <div className="Muted">
        {props.eventTitle ? props.eventTitle : 'Wydarzenie'}
        {props.eventMeta ? ` • ${props.eventMeta}` : ''}
      </div>

      {errorText && <div className="ErrorBanner">{errorText}</div>}
      {isLoading && <div className="Muted">Ładowanie…</div>}

      {props.me.role === 'PARENT' && (
        <Card title="Szukam / Zabiorę">
          {!props.childId && <div className="Muted">Najpierw wybierz dziecko w Profilu.</div>}

          <div className="TransportQuickRow" aria-label="Szybkie akcje transportu">
            <button
              type="button"
              className={['TransportQuickBtn', 'is-need', myReq ? 'is-active' : ''].filter(Boolean).join(' ')}
              disabled={isLoading || !canEditRequest}
              onClick={() => {
                if (myReq) deleteRequest();
                else saveRequest();
              }}
              aria-label={myReq ? 'Cofnij: szukam transportu' : 'Zgłoś: szukam transportu'}
            >
              <div className="TransportQuickLabel">Szukam</div>
              <div className="TransportQuickMeta">{myReq ? 'Włączone' : 'Kliknij, aby zgłosić'}</div>
            </button>

            <button
              type="button"
              className={['TransportQuickBtn', 'is-offer', myOfferId ? 'is-active' : ''].filter(Boolean).join(' ')}
              disabled={isLoading || !canEditOffer}
              onClick={() => {
                if (myOfferId) deleteOffer();
                else saveOffer();
              }}
              aria-label={myOfferId ? 'Usuń: zabiorę' : 'Zgłoś: zabiorę'}
            >
              <div className="TransportQuickLabel">Zabiorę</div>
              <div className="TransportQuickMeta">{myOfferId ? 'Włączone' : 'Kliknij, aby zgłosić'}</div>
            </button>
          </div>

          {myReq && (
            <div className="TransportQuickDetails">
              <div className="TransportQuickDetailsTop">
                <div className="Muted">{myReq.note ? `Notatka: ${myReq.note}` : 'Bez notatki.'}</div>
                <button
                  type="button"
                  className="TransportQuickLink"
                  disabled={isLoading || !canEditRequest}
                  onClick={() => setShowRequestEditor((v) => !v)}
                >
                  {showRequestEditor ? 'Ukryj' : 'Edytuj'}
                </button>
              </div>
              {showRequestEditor && (
                <>
                  <input
                    className="TextInput"
                    value={requestNote}
                    onChange={(e) => setRequestNote(e.target.value)}
                    placeholder="Notatka (opcjonalnie)"
                    disabled={isLoading || !canEditRequest}
                  />
                  <div className="SheetActionsRow">
                    <PrimaryButton onClick={saveRequest} disabled={isLoading || !canEditRequest} type="button">
                      Zapisz
                    </PrimaryButton>
                    <SecondaryButton onClick={deleteRequest} disabled={isLoading || !canEditRequest} type="button">
                      Cofnij
                    </SecondaryButton>
                  </div>
                </>
              )}
            </div>
          )}

          {myOfferId && (
            <div className="TransportQuickDetails">
              <div className="TransportQuickDetailsTop">
                <div className="Muted">{`Wolne miejsca: ${data?.offers.find((o) => o.id === myOfferId)?.seats_free ?? 0}`}</div>
                <button
                  type="button"
                  className="TransportQuickLink"
                  disabled={isLoading || !canEditOffer}
                  onClick={() => setShowOfferEditor((v) => !v)}
                >
                  {showOfferEditor ? 'Ukryj' : 'Edytuj'}
                </button>
              </div>
              {showOfferEditor && (
                <>
                  <input
                    className="TextInput"
                    inputMode="numeric"
                    value={offerSeats}
                    onChange={(e) => setOfferSeats(e.target.value)}
                    placeholder="Ile miejsc oferujesz (0-8)"
                    disabled={isLoading || !canEditOffer}
                  />
                  <input
                    className="TextInput"
                    value={offerNote}
                    onChange={(e) => setOfferNote(e.target.value)}
                    placeholder="Notatka (opcjonalnie)"
                    disabled={isLoading || !canEditOffer}
                  />
                  <div className="SheetActionsRow">
                    <PrimaryButton onClick={saveOffer} disabled={isLoading || !canEditOffer} type="button">
                      Zapisz
                    </PrimaryButton>
                    <SecondaryButton onClick={deleteOffer} disabled={isLoading || !myOfferId || !canEditOffer} type="button">
                      Usuń
                    </SecondaryButton>
                  </div>
                </>
              )}
            </div>
          )}
        </Card>
      )}

      {props.me.role === 'PARENT' && (
        <Card title="Twoje miejsce">
          {data?.my_reservation_id ? (
            <>
              <div className="Muted">Masz zarezerwowane miejsce w aucie.</div>
              <SecondaryButton onClick={cancelReservation} disabled={isLoading || !props.childId} type="button">
                Anuluj rezerwację
              </SecondaryButton>
            </>
          ) : (
            <div className="Muted">Brak rezerwacji.</div>
          )}
        </Card>
      )}

      <Card title="Szukający transportu">
        {(data?.requests ?? []).length === 0 ? (
          Number(data?.requests_count ?? 0) <= 0 ? (
            <div className="Muted">Brak zgłoszeń.</div>
          ) : (
            <div className="Muted">{`Zgłoszeń: ${Number(data?.requests_count ?? 0)}`}</div>
          )
        ) : (
          <div className="List" aria-label="Szukający transportu">
            {(data?.requests ?? []).map((r) => {
              const statusVal = String(r.status ?? 'OPEN').toUpperCase();
              const isDone = statusVal === 'DONE';
              const canTake =
                props.me.role === 'PARENT' &&
                !!props.childId &&
                !isLoading &&
                !isDone &&
                !(r.is_mine ?? false);
              const requesterLine = r.requester_label ? `Zgłosił: ${r.requester_label}` : null;
              const noteLine = r.note ? `Notatka: ${r.note}` : null;
              const doneLine = isDone ? (r.handled_by_label ? `Załatwione: ${r.handled_by_label}` : 'Załatwione') : null;
              return (
                <div key={r.id} className="ListItem">
                  <div className="ListItemTop">
                    <div className="ListItemTitle">{r.child_label}</div>
                    <div className="ListItemBadge">{isDone ? '✓' : 'S'}</div>
                  </div>
                  {requesterLine ? <div className="ListItemMeta">{requesterLine}</div> : null}
                  {noteLine ? <div className="ListItemMeta">{noteLine}</div> : null}
                  {doneLine ? <div className="ListItemMeta">{doneLine}</div> : null}
                  {props.me.role === 'PARENT' && !isDone && (
                    <div className="CoachManageActions">
                      <SecondaryButton onClick={() => takeRequest(r.id)} disabled={!canTake} type="button">
                        Zabiorę
                      </SecondaryButton>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <div className="List" aria-label="Oferty transportu">
        {(data?.offers ?? []).length === 0 ? (
          <div className="Muted">Brak ofert.</div>
        ) : (
          (data?.offers ?? []).map((o) => {
            const isMine = myOfferId === o.id;
            const isReservedHere = myResOfferId === o.id;
            const canReserve =
              props.me.role === 'PARENT' &&
              !!props.childId &&
              !isMine &&
              !data?.my_reservation_id &&
              o.seats_free > 0;
            return (
              <div key={o.id} className="ListItem">
                <div className="ListItemTop">
                  <div className="ListItemTitle">{o.driver_label || 'Kierowca'}</div>
                  <div className="ListItemBadge">{`${o.seats_free}/${o.seats_total}`}</div>
                </div>
                {o.note ? <div className="ListItemMeta">{o.note}</div> : null}
                {props.me.role === 'COACH' || props.me.role === 'ADMIN' ? (
                  o.reservations && o.reservations.length > 0 ? (
                    <div className="ListItemMeta">{`Rezerwacje: ${o.reservations.map((r) => r.child_label).join(', ')}`}</div>
                  ) : (
                    <div className="ListItemMeta">Rezerwacje: —</div>
                  )
                ) : null}
                {props.me.role === 'PARENT' && (
                  <div className="CoachManageActions">
                    {isReservedHere && <div className="Muted">Zarezerwowane</div>}
                    <SecondaryButton onClick={() => reserve(o.id)} disabled={isLoading || !canReserve} type="button">
                      Rezerwuj
                    </SecondaryButton>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {props.onOpenEvent && (
        <PrimaryButton onClick={props.onOpenEvent} disabled={isLoading} type="button">
          {props.openEventLabel ? props.openEventLabel : 'Otwórz wydarzenie'}
        </PrimaryButton>
      )}
      <SecondaryButton onClick={props.onClose} disabled={isLoading} type="button">
        Zamknij
      </SecondaryButton>
    </>
  );
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat('pl-PL', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat('pl-PL', { hour: '2-digit', minute: '2-digit' }).format(d);
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function dateKeyFromDate(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function dateKeyFromIso(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  return dateKeyFromDate(d);
}

function parseDateKey(key: string): Date {
  const m = key.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return new Date();
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

function formatDayLabel(key: string): string {
  const d = parseDateKey(key);
  return new Intl.DateTimeFormat('pl-PL', { weekday: 'long', day: '2-digit', month: 'long' }).format(d);
}

function monthTitle(d: Date): string {
  return new Intl.DateTimeFormat('pl-PL', { month: 'long' }).format(d).toUpperCase();
}

function monthTitleWithYear(d: Date): string {
  return `${monthTitle(d)} ${d.getFullYear()}`;
}

function opponentLabelFromMatchTitle(title: string): string {
  const t = (title || '').trim();
  if (!t) return 'VS';
  const lowered = t.toLowerCase();
  const separators = [' vs ', ' v ', ' - ', ' — ', ' – '];
  let parts: string[] | null = null;
  for (const sep of separators) {
    if (lowered.includes(sep.trim())) {
      const re = new RegExp(sep.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i');
      const raw = t.split(re).map((p) => p.trim()).filter(Boolean);
      if (raw.length >= 2) {
        parts = raw;
        break;
      }
    }
  }
  if (!parts) return t.length <= 32 ? t.toUpperCase() : t.slice(0, 32).toUpperCase();

  const ourAliases = ['błonianka', 'blonianka', 'footdesk'];
  const candidate = parts.find((p) => !ourAliases.some((a) => p.toLowerCase().includes(a))) ?? parts[1];
  const compact = candidate.replace(/\s+/g, ' ').trim();
  if (!compact) return 'VS';
  return compact.length <= 32 ? compact.toUpperCase() : compact.slice(0, 32).toUpperCase();
}

function layoutOpponentLabel(label: string): { lines: string[]; scale: number } {
  const raw = (label || '').trim().replace(/\s+/g, ' ');
  const text = raw ? raw.toUpperCase() : 'RYWAL';
  const words = text.split(' ').filter(Boolean);
  if (words.length <= 1) {
    const w = words[0] ?? 'RYWAL';
    const scale = Math.max(0.68, 1 - Math.max(0, w.length - 10) * 0.04);
    return { lines: [w], scale };
  }
  if (words.length === 2) {
    const joined = `${words[0]} ${words[1]}`;
    const lines = [words[0], words[1]];
    const longest = Math.max(...lines.map((l) => l.length));
    const scale = Math.max(0.62, 1 - Math.max(0, Math.max(joined.length - 12, longest - 10)) * 0.05);
    return { lines, scale };
  }

  const joined = words.join(' ');
  if (joined.length <= 22) {
    let bestIdx = 1;
    let bestScore = Number.POSITIVE_INFINITY;
    for (let i = 1; i < words.length; i++) {
      const a = words.slice(0, i).join(' ');
      const b = words.slice(i).join(' ');
      const score = Math.abs(a.length - b.length) + Math.max(0, a.length - 16) * 2 + Math.max(0, b.length - 16) * 2;
      if (score < bestScore) {
        bestScore = score;
        bestIdx = i;
      }
    }
    const lines = [words.slice(0, bestIdx).join(' '), words.slice(bestIdx).join(' ')].filter(Boolean);
    const longest = Math.max(...lines.map((l) => l.length));
    const scale = Math.max(0.62, 1 - Math.max(0, longest - 10) * 0.055);
    return { lines, scale };
  }

  const total = joined.length;
  const target = total / 3;
  const lines: string[] = [];
  let cur: string[] = [];
  for (const w of words) {
    const candidate = [...cur, w].join(' ');
    const curLen = cur.join(' ').length;
    if (lines.length < 2 && cur.length > 0 && curLen >= target) {
      lines.push(cur.join(' '));
      cur = [w];
      continue;
    }
    if (lines.length < 2 && candidate.length > target + 4 && cur.length > 0) {
      lines.push(cur.join(' '));
      cur = [w];
      continue;
    }
    cur.push(w);
  }
  if (cur.length) lines.push(cur.join(' '));
  const finalLines = lines.slice(0, 3);
  const longest = Math.max(...finalLines.map((l) => l.length));
  const scale = Math.max(0.58, 1 - Math.max(0, longest - 10) * 0.06);
  return { lines: finalLines, scale };
}

function pickNextUpcomingMatch(events: EventListItem[], groupId: string | null): EventListItem | null {
  const nowIso = new Date().toISOString();
  return (
    events
      .filter((e) => e.type === 'MATCH' && e.status === 'ACTIVE' && e.start_at >= nowIso && (!groupId || e.group_id === groupId))
      .slice()
      .sort((a, b) => a.start_at.localeCompare(b.start_at))[0] ?? null
  );
}

function mondayIndex(d: Date): number {
  const js = d.getDay();
  return (js + 6) % 7;
}

type DayMarkers = { hasTraining: boolean; hasMatch: boolean; hasIndividualTraining: boolean; hasIndividualMatch: boolean };

function MonthCard(props: {
  monthDate: Date;
  selectedKey: string;
  markers: Record<string, DayMarkers>;
  onSelect: (key: string) => void;
  onPrevMonth: () => void;
  onNextMonth: () => void;
}) {
  const [anim, setAnim] = React.useState<{ phase: 'idle' | 'leaving' | 'entering'; dir: 'prev' | 'next' | null }>({
    phase: 'idle',
    dir: null,
  });
  const swipeRef = React.useRef<{ isDown: boolean; x0: number; y0: number; x1: number; y1: number }>({
    isDown: false,
    x0: 0,
    y0: 0,
    x1: 0,
    y1: 0,
  });
  const swipeLockRef = React.useRef(false);
  const timersRef = React.useRef<{ leave?: number; enter?: number; unlock?: number }>({});

  React.useEffect(() => {
    return () => {
      if (timersRef.current.leave) window.clearTimeout(timersRef.current.leave);
      if (timersRef.current.enter) window.clearTimeout(timersRef.current.enter);
      if (timersRef.current.unlock) window.clearTimeout(timersRef.current.unlock);
    };
  }, []);

  const triggerShift = React.useCallback(
    (dir: 'prev' | 'next') => {
      if (anim.phase !== 'idle') return;
      setAnim({ phase: 'leaving', dir });
      swipeLockRef.current = true;
      if (timersRef.current.leave) window.clearTimeout(timersRef.current.leave);
      if (timersRef.current.enter) window.clearTimeout(timersRef.current.enter);
      if (timersRef.current.unlock) window.clearTimeout(timersRef.current.unlock);
      timersRef.current.leave = window.setTimeout(() => {
        if (dir === 'prev') props.onPrevMonth();
        else props.onNextMonth();
        setAnim({ phase: 'entering', dir });
        timersRef.current.enter = window.setTimeout(() => setAnim({ phase: 'idle', dir: null }), 260);
      }, 170);
      timersRef.current.unlock = window.setTimeout(() => {
        swipeLockRef.current = false;
      }, 340);
    },
    [anim.phase, props]
  );

  const triggerShiftToDate = React.useCallback(
    (dir: 'prev' | 'next', key: string) => {
      if (anim.phase !== 'idle') return;
      setAnim({ phase: 'leaving', dir });
      swipeLockRef.current = true;
      if (timersRef.current.leave) window.clearTimeout(timersRef.current.leave);
      if (timersRef.current.enter) window.clearTimeout(timersRef.current.enter);
      if (timersRef.current.unlock) window.clearTimeout(timersRef.current.unlock);
      timersRef.current.leave = window.setTimeout(() => {
        props.onSelect(key);
        setAnim({ phase: 'entering', dir });
        timersRef.current.enter = window.setTimeout(() => setAnim({ phase: 'idle', dir: null }), 260);
      }, 170);
      timersRef.current.unlock = window.setTimeout(() => {
        swipeLockRef.current = false;
      }, 340);
    },
    [anim.phase, props]
  );

  const first = new Date(props.monthDate.getFullYear(), props.monthDate.getMonth(), 1);
  const month = first.getMonth();
  const year = first.getFullYear();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startOffset = mondayIndex(first);

  const cells: Array<{ key: string; day: number; inMonth: boolean }> = [];
  if (startOffset > 0) {
    const prevLast = new Date(year, month, 0);
    const prevYear = prevLast.getFullYear();
    const prevMonth = prevLast.getMonth();
    const prevDaysInMonth = prevLast.getDate();
    for (let i = startOffset - 1; i >= 0; i -= 1) {
      const day = prevDaysInMonth - i;
      const k = `${prevYear}-${pad2(prevMonth + 1)}-${pad2(day)}`;
      cells.push({ key: k, day, inMonth: false });
    }
  }
  for (let day = 1; day <= daysInMonth; day += 1) {
    const k = `${year}-${pad2(month + 1)}-${pad2(day)}`;
    cells.push({ key: k, day, inMonth: true });
  }
  if (cells.length % 7 !== 0) {
    const padCount = 7 - (cells.length % 7);
    const nextFirst = new Date(year, month + 1, 1);
    const nextYear = nextFirst.getFullYear();
    const nextMonth = nextFirst.getMonth();
    for (let i = 1; i <= padCount; i += 1) {
      const k = `${nextYear}-${pad2(nextMonth + 1)}-${pad2(i)}`;
      cells.push({ key: k, day: i, inMonth: false });
    }
  }

  const weekdays = ['LN', 'WT', 'ŚR', 'CZ', 'PT', 'SO', 'ND'];

  return (
    <section
      className="MonthCard"
      aria-label="Kalendarz miesiąca"
      onPointerDown={(e) => {
        if (anim.phase !== 'idle') return;
        if (e.pointerType !== 'touch' && e.pointerType !== 'pen') return;
        swipeRef.current.isDown = true;
        swipeRef.current.x0 = e.clientX;
        swipeRef.current.y0 = e.clientY;
        swipeRef.current.x1 = e.clientX;
        swipeRef.current.y1 = e.clientY;
      }}
      onPointerMove={(e) => {
        if (!swipeRef.current.isDown) return;
        swipeRef.current.x1 = e.clientX;
        swipeRef.current.y1 = e.clientY;
      }}
      onPointerUp={() => {
        if (!swipeRef.current.isDown) return;
        swipeRef.current.isDown = false;
        const dx = swipeRef.current.x1 - swipeRef.current.x0;
        const dy = swipeRef.current.y1 - swipeRef.current.y0;
        if (Math.abs(dx) < 50) return;
        if (Math.abs(dx) < Math.abs(dy) * 1.2) return;
        if (dx < 0) triggerShift('next');
        else triggerShift('prev');
      }}
      onPointerCancel={() => {
        swipeRef.current.isDown = false;
      }}
    >
      <div className="MonthCardHeader">
        <button className="MonthNavButton" type="button" onClick={() => triggerShift('prev')} aria-label="Poprzedni miesiąc">
          ‹
        </button>
        <div className="MonthCardTitle">{monthTitleWithYear(first)}</div>
        <button className="MonthNavButton" type="button" onClick={() => triggerShift('next')} aria-label="Następny miesiąc">
          ›
        </button>
      </div>
      <div
        className={[
          'MonthCardBody',
          anim.phase === 'leaving' && anim.dir ? `is-leaving is-${anim.dir}` : '',
          anim.phase === 'entering' && anim.dir ? `is-entering is-${anim.dir}` : '',
        ]
          .filter(Boolean)
          .join(' ')}
        aria-label="Siatka kalendarza"
      >
        <div className="MonthWeekdays">
          {weekdays.map((w) => (
            <div key={w} className="MonthWeekday">
              {w}
            </div>
          ))}
        </div>
        <div className="MonthGrid">
          {cells.map((c) => {
            const isSelected = c.key === props.selectedKey;
            const m = props.markers[c.key];
            const showMarkers = !!(m && (m.hasTraining || m.hasMatch || m.hasIndividualTraining || m.hasIndividualMatch));
            return (
              <button
                key={c.key}
                type="button"
                className={['MonthCell', !c.inMonth ? 'is-outside' : '', isSelected ? 'is-selected' : ''].filter(Boolean).join(' ')}
                onClick={() => {
                  if (swipeLockRef.current) return;
                  if (c.inMonth) {
                    props.onSelect(c.key);
                    return;
                  }
                  const a = `${props.monthDate.getFullYear()}-${pad2(props.monthDate.getMonth() + 1)}`;
                  const b = c.key.slice(0, 7);
                  const dir: 'prev' | 'next' = b < a ? 'prev' : 'next';
                  triggerShiftToDate(dir, c.key);
                }}
              >
                <div className="MonthCellNum">{c.day}</div>
                {showMarkers && (
                  <div className="MonthMarkers" aria-hidden="true">
                    {m?.hasTraining && <span className="MonthDot is-blue" />}
                    {m?.hasIndividualTraining && <span className="MonthDot is-purple" />}
                    {(m?.hasMatch || m?.hasIndividualMatch) && (
                      <span
                        className={['MonthDot', 'is-green', m?.hasIndividualMatch ? 'is-individual' : ''].filter(Boolean).join(' ')}
                      >
                        <SoccerIcon className="MonthBall" />
                      </span>
                    )}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function UpcomingMatchCard(props: {
  token: string;
  childId?: string | null;
  groupId?: string | null;
  events?: EventListItem[];
  disabled?: boolean;
  sticky?: boolean;
  onManageCallups?: (eventId: string) => void;
}) {
  const [loaded, setLoaded] = React.useState<EventListItem[] | null>(null);
  const [phase, setPhase] = React.useState<'A' | 'B'>('A');
  const [carouselIdx, setCarouselIdx] = React.useState(0);

  React.useEffect(() => {
    if (props.events) return;
    let cancelled = false;
    apiListEvents(props.token, props.childId ?? null)
      .then((list) => {
        if (cancelled) return;
        setLoaded(list);
      })
      .catch(() => {
        if (cancelled) return;
        setLoaded([]);
      });
    return () => {
      cancelled = true;
    };
  }, [props.childId, props.events, props.token]);

  const source = props.events ?? loaded ?? [];
  const weekUpcoming = React.useMemo(() => {
    const now = new Date();
    const startDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
    const dow = startDay.getDay();
    const diff = (dow + 6) % 7;
    const start = new Date(startDay.getFullYear(), startDay.getMonth(), startDay.getDate() - diff, 0, 0, 0);
    const windowStart = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7, 0, 0, 0);
    const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 7, 0, 0, 0);
    const nowIso = now.toISOString();
    const startIso = windowStart.toISOString();
    const endIso = end.toISOString();
    const isSpecialTraining = (e: EventListItem) => {
      const t = `${e.title || ''}`.toLowerCase();
      return /specjal|specjalny|turniej|mecz\s*treningowy/.test(t);
    };
    const gid = (props.groupId ?? '').trim();
    return source
      .filter((e) => (gid ? e.group_id === gid : true))
      .filter((e) => {
        if (e.start_at < startIso || e.start_at >= endIso) return false;
        if (e.start_at >= nowIso) return e.type === 'MATCH' || (e.type === 'TRAINING' && isSpecialTraining(e));
        if (e.type === 'MATCH' && e.match_finalized) return true;
        return false;
      })
      .slice()
      .sort((a, b) => a.start_at.localeCompare(b.start_at));
  }, [props.groupId, source]);

  const next = React.useMemo(() => pickNextUpcomingMatch(source, props.groupId ?? null), [props.groupId, source]);

  React.useEffect(() => {
    if (weekUpcoming.length <= 1) return;
    setCarouselIdx((i) => (weekUpcoming.length > 0 ? i % weekUpcoming.length : 0));
    const loop = window.setInterval(() => {
      setCarouselIdx((i) => (weekUpcoming.length > 0 ? (i + 1) % weekUpcoming.length : 0));
    }, 8200);
    return () => {
      window.clearInterval(loop);
    };
  }, [weekUpcoming.length]);

  const current = (weekUpcoming.length > 0 ? weekUpcoming[carouselIdx % weekUpcoming.length] : next) ?? null;

  React.useEffect(() => {
    if (!current) return;
    setPhase('A');
    const first = window.setTimeout(() => setPhase('B'), 5200);
    if (weekUpcoming.length > 1) {
      return () => {
        window.clearTimeout(first);
      };
    }
    const loop = window.setInterval(() => setPhase((p) => (p === 'A' ? 'B' : 'A')), 8200);
    return () => {
      window.clearTimeout(first);
      window.clearInterval(loop);
    };
  }, [current?.id, weekUpcoming.length]);

  if (!current) return null;
  const opponentLabel = current.opponent_name || opponentLabelFromMatchTitle(current.title || '');
  const titleActual = (current.title || (current.type === 'TRAINING' ? 'TRENING' : 'MECZ')).toUpperCase();
  const opponent = layoutOpponentLabel(opponentLabel || 'RYWAL');
  const countLabel = weekUpcoming.length > 1 ? ` (${(carouselIdx % weekUpcoming.length) + 1}/${weekUpcoming.length})` : '';
  const canManageCallups = !!props.onManageCallups && current.type === 'MATCH';
  const isFinalizedMatch = current.type === 'MATCH' && !!current.match_finalized;
  const scoreText = isFinalizedMatch ? `${Number(current.home_score ?? 0)}:${Number(current.away_score ?? 0)}` : null;

  return (
    <button
      type="button"
      className={[
        'UpcomingMatchCard',
        'UpcomingMatchCard--button',
        'UpcomingMatchCard--live',
        props.sticky ? 'UpcomingMatchCard--sticky' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={() => navigate('kalendarz', { e: current.id })}
      disabled={props.disabled}
      aria-label={isFinalizedMatch ? `Zakończony mecz: ${scoreText ?? ''}` : `Nadchodzący mecz: ${titleActual}`}
    >
      <div className={['UpcomingMatchInner', canManageCallups ? 'UpcomingMatchInner--coach' : ''].filter(Boolean).join(' ')}>
        <div className="UpcomingMatchLogo">
          <img src={clubLogo} alt="Błonianka Błonie" />
        </div>
        <div className="UpcomingMatchText">
          <div className="UpcomingMatchSwap" aria-hidden="true">
            <div className={['UpcomingMatchSlide', phase === 'A' ? 'is-active' : ''].filter(Boolean).join(' ')}>
              {isFinalizedMatch ? (
                <>
                  <div className="UpcomingMatchTitle">{`🏁 WYNIK MECZU${countLabel}`}</div>
                  <div
                    className="UpcomingMatchSubtitle"
                    style={{ fontWeight: 700, fontSize: 18, color: 'var(--color-primary-700, #1a237e)', display: 'flex', gap: 6, alignItems: 'center', justifyContent: 'center' }}
                  >
                    {scoreText}
                    {(current.external_source ?? '').trim() === 'LNP' && (
                      <span className="LnpBadge"><span className="LnpBadgeDot"></span>LNP</span>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div className="UpcomingMatchTitle">{`NADCHODZĄCY MECZ${countLabel}`}</div>
                  <div className="UpcomingMatchSubtitle">{current.location}</div>
                </>
              )}
            </div>
            <div className={['UpcomingMatchSlide', phase === 'B' ? 'is-active' : ''].filter(Boolean).join(' ')}>
              <div className="UpcomingMatchTitle">{titleActual}</div>
              <div className="UpcomingMatchSubtitle">{formatDateTime(current.start_at)}</div>
            </div>
          </div>
        </div>
        {canManageCallups && (
          <button
            type="button"
            className="UpcomingMatchAction"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              props.onManageCallups?.(current.id);
            }}
            disabled={props.disabled}
            aria-label="Zarządzaj powołaniami"
          >
            <SoccerIcon className="UpcomingMatchActionIcon" />
          </button>
        )}
        <div className="UpcomingMatchLogo is-opponent" aria-hidden="true">
          {(current.opponent_logo_url || '').trim() ? (
            <img className="OpponentLogoImg" src={(current.opponent_logo_url || '').trim()} alt={opponentLabel || 'Przeciwnik'} />
          ) : (
            <div className="OpponentLogoInner">
              <div
                className={['OpponentText', opponent.lines.length === 1 ? 'is-single' : 'is-multi'].filter(Boolean).join(' ')}
                style={{ transform: `scale(${opponent.scale})` }}
              >
                {opponent.lines.map((line, idx) => (
                  <div key={`${idx}-${line}`} className="OpponentTextLine">
                    {line}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </button>
  );
}

function CoachMatchBanner(props: {
  e: EventListItem;
  need?: { formula: string; minPlayers: number; confirmed: number; isEnough: boolean } | null;
  onOpen: () => void;
  onManageCallups: () => void;
  disabled?: boolean;
}) {
  const isIndividual = !!props.e.is_individual;
  const isIndividualCoachEvent = !!props.e.is_individual_coach_event;
  const title = (props.e.title || '').trim() ? props.e.title.trim().toUpperCase() : 'MECZ';
  const subtitle = `${formatDateTime(props.e.start_at)} • ${props.e.location}${props.e.status === 'CANCELLED' ? ' • ODWOŁANE' : ''}`;
  const childLine = isIndividual && (props.e.target_child_label ?? '').trim() ? `Podopieczny: ${(props.e.target_child_label ?? '').trim()}` : null;
  const opponentLabel = props.e.opponent_name || opponentLabelFromMatchTitle(props.e.title || '');
  const opponent = layoutOpponentLabel(opponentLabel || 'RYWAL');
  const need = props.need;
  const needText = need && need.minPlayers > 0 ? `Potrzeba min ${need.confirmed}/${need.minPlayers}` : null;
  const callupsCount = Math.max(0, Number(props.e.called_up_count ?? 0) || 0);
  const callupsReminderLevel = (() => {
    if (isIndividual) return null;
    if (props.e.status === 'CANCELLED') return null;
    if (callupsCount > 0) return null;
    const start = new Date(props.e.start_at);
    if (Number.isNaN(start.getTime())) return null;
    const now = Date.now();
    if (start.getTime() <= now) return null;
    const msDay = 24 * 60 * 60 * 1000;
    const daysLeft = Math.ceil((start.getTime() - now) / msDay);
    if (daysLeft <= 1) return 'CRITICAL' as const;
    if (daysLeft <= 2) return 'INFO' as const;
    return null;
  })();
  return (
    <button
      type="button"
      className={[
        'UpcomingMatchCard',
        'UpcomingMatchCard--button',
        isIndividual ? 'UpcomingMatchCard--individual' : '',
        isIndividualCoachEvent ? 'UpcomingMatchCard--individualCoach' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={props.onOpen}
      disabled={props.disabled}
      aria-label={`${isIndividual ? 'Dodatkowy mecz' : 'Mecz'}: ${title}`}
    >
      <div className="UpcomingMatchInner UpcomingMatchInner--coach">
        <div className="UpcomingMatchLogo">
          <img src={clubLogo} alt="Błonianka Błonie" />
        </div>
        <div className="UpcomingMatchText UpcomingMatchText--left">
          <div className="UpcomingMatchTitle">{title}</div>
          <div className="UpcomingMatchSubtitle">{subtitle}</div>
          {childLine ? <div className="UpcomingMatchSubtitle">{childLine}</div> : null}
          {!isIndividual && (
            <div className={['MatchKindPill', matchKindClass(props.e.match_kind)].filter(Boolean).join(' ')}>
              {matchKindShort(props.e.match_kind)}
            </div>
          )}
          {isIndividual && <div className="UpcomingMatchIndPill">🎯 DODATKOWY MECZ</div>}
          {!isIndividual && callupsCount > 0 && <div className="CoachMatchCallupsPill">{`Wysłano: ${callupsCount}`}</div>}
          {callupsReminderLevel && (
            <div
              className={[
                'CoachMatchCallupsReminderPill',
                callupsReminderLevel === 'CRITICAL' ? 'is-critical' : 'is-info',
              ].join(' ')}
            >
              {callupsReminderLevel === 'CRITICAL' ? 'PILNE: BRAK POWOŁAŃ' : 'BRAK POWOŁAŃ (2 DNI)'}
            </div>
          )}
          {!isIndividual && needText && (
            <div className={['CoachMatchNeedPill', need?.isEnough ? 'is-ok' : ''].filter(Boolean).join(' ')}>{needText}</div>
          )}
        </div>
        {!isIndividual && (
          <button
            type="button"
            className="UpcomingMatchAction"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              props.onManageCallups();
            }}
            disabled={props.disabled}
            aria-label="Zarządzaj powołaniami"
          >
            <SoccerIcon className="UpcomingMatchActionIcon" />
          </button>
        )}
        <div className="UpcomingMatchLogo is-opponent" aria-hidden="true">
          <div className="OpponentLogoInner">
            <div
              className={['OpponentText', opponent.lines.length === 1 ? 'is-single' : 'is-multi'].filter(Boolean).join(' ')}
              style={{ transform: `scale(${opponent.scale})` }}
            >
              {opponent.lines.map((line, idx) => (
                <div key={`${idx}-${line}`} className="OpponentTextLine">
                  {line}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </button>
  );
}

function CoachTrainingBanner(props: {
  e: EventListItem;
  onOpen: () => void;
  disabled?: boolean;
  resolveChildLabel?: (childId: string) => string | null;
  resolveChildPhotoUrl?: (childId: string) => string | null;
}) {
  const isIndividual = !!props.e.is_individual;
  const isIndividualCoachEvent = !!props.e.is_individual_coach_event;
  const title = (props.e.title || '').trim() ? props.e.title.trim().toUpperCase() : 'TRENING';
  const subtitle = `${formatDateTime(props.e.start_at)} • ${props.e.location}${props.e.status === 'CANCELLED' ? ' • ODWOŁANE' : ''}`;
  const directChildLabel = (props.e.target_child_label ?? '').trim();
  const targetChildId = (props.e.target_child_id ?? '').trim();
  const fallbackChildLabel =
    !directChildLabel && targetChildId && props.resolveChildLabel ? (props.resolveChildLabel(targetChildId) ?? '').trim() : '';
  const childLabelText = directChildLabel || fallbackChildLabel;
  const childLine = isIndividual && childLabelText ? `Podopieczny: ${childLabelText}` : null;
  const childPhotoUrl = isIndividual && targetChildId && props.resolveChildPhotoUrl ? props.resolveChildPhotoUrl(targetChildId) : null;
  const childInitials = (() => {
    const raw = (childLabelText || '').replace(/\([^)]+\)/g, '').trim();
    if (!raw) return '🎯';
    const parts = raw.split(/\s+/).filter(Boolean);
    const a = (parts[0] || '').slice(0, 1);
    const b = (parts[1] || '').slice(0, 1);
    const out = `${a}${b}`.trim().toUpperCase();
    return out || '🎯';
  })();
  return (
    <button
      type="button"
      className={[
        'UpcomingMatchCard',
        'UpcomingMatchCard--button',
        'UpcomingMatchCard--training',
        isIndividual ? 'UpcomingMatchCard--individual' : '',
        isIndividualCoachEvent ? 'UpcomingMatchCard--individualCoach' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={props.onOpen}
      disabled={props.disabled}
      aria-label={`${isIndividual ? 'Trening indywidualny' : 'Trening'}: ${title}`}
    >
      <div className="UpcomingMatchInner">
        <div className="UpcomingMatchLogo">
          <img src={clubLogo} alt="Błonianka Błonie" />
        </div>
        <div className="UpcomingMatchText UpcomingMatchText--left">
          <div className="UpcomingMatchTitle">{title}</div>
          <div className="UpcomingMatchSubtitle">{subtitle}</div>
          {childLine ? <div className="UpcomingMatchSubtitle">{childLine}</div> : null}
          {isIndividual && <div className="UpcomingMatchIndPill">🎯 TRENING INDYWIDUALNY</div>}
        </div>
        {isIndividual ? (
          <div className="UpcomingMatchLogo is-opponent is-training is-child" aria-hidden="true">
            {childPhotoUrl ? <img className="UpcomingChildAvatarImg" src={childPhotoUrl} alt="" /> : <div className="UpcomingChildInitials">{childInitials}</div>}
          </div>
        ) : (
          <div className="UpcomingMatchLogo is-opponent is-training" aria-hidden="true">
            <div className="OpponentLogoInner">
              <TrainingIcon className="OpponentBall" />
            </div>
          </div>
        )}
      </div>
    </button>
  );
}

function ParentMatchBanner(props: { e: EventListItem; onOpen: () => void; disabled?: boolean }) {
  const isIndividual = !!props.e.is_individual;
  const isIndividualCoachEvent = !!props.e.is_individual_coach_event;
  const title = (props.e.title || '').trim() ? props.e.title.trim().toUpperCase() : 'MECZ';
  const subtitle = `${formatDateTime(props.e.start_at)} • ${props.e.location}${props.e.status === 'CANCELLED' ? ' • ODWOŁANE' : ''}`;
  const opponentLabel = props.e.opponent_name || opponentLabelFromMatchTitle(props.e.title || '');
  const opponent = layoutOpponentLabel(opponentLabel || 'RYWAL');
  return (
    <button
      type="button"
      className={[
        'UpcomingMatchCard',
        'UpcomingMatchCard--button',
        isIndividual ? 'UpcomingMatchCard--individual' : '',
        isIndividualCoachEvent ? 'UpcomingMatchCard--individualCoach' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={props.onOpen}
      disabled={props.disabled}
      aria-label={`${isIndividual ? 'Dodatkowy mecz' : 'Mecz'}: ${title}`}
    >
      <div className="UpcomingMatchInner UpcomingMatchInner--coach">
        <div className="UpcomingMatchLogo">
          <img src={clubLogo} alt="Błonianka Błonie" />
        </div>
        <div className="UpcomingMatchText UpcomingMatchText--left">
          <div className="UpcomingMatchTitle">{title}</div>
          <div className="UpcomingMatchSubtitle">{subtitle}</div>
          {!isIndividual && (
            <div className={['MatchKindPill', matchKindClass(props.e.match_kind)].filter(Boolean).join(' ')}>
              {matchKindShort(props.e.match_kind)}
            </div>
          )}
          {isIndividual && <div className="UpcomingMatchIndPill">🎯 DODATKOWY MECZ</div>}
        </div>
        <div className="UpcomingMatchLogo is-opponent" aria-hidden="true">
          <div className="OpponentLogoInner">
            <div
              className={['OpponentText', opponent.lines.length === 1 ? 'is-single' : 'is-multi'].filter(Boolean).join(' ')}
              style={{ transform: `scale(${opponent.scale})` }}
            >
              {opponent.lines.map((line, idx) => (
                <div key={`${idx}-${line}`} className="OpponentTextLine">
                  {line}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </button>
  );
}

function EventCard(props: { e: EventListItem; onOpen: () => void; disabled?: boolean }) {
  const time = formatTime(props.e.start_at);
  const variant = props.e.type === 'MATCH' ? 'EventCard--match' : 'EventCard--training';
  const title = props.e.title.toUpperCase();
  const pillText =
    props.e.status === 'CANCELLED'
      ? 'ODWOŁANE'
      : props.e.is_individual
        ? props.e.type === 'MATCH'
          ? 'DODATKOWY'
          : 'INDYW.'
        : props.e.type === 'MATCH'
          ? matchKindShort(props.e.match_kind)
          : 'TRENING';
  const tileIcon = props.e.type === 'MATCH' ? <SoccerIcon /> : <TrainingIcon />;
  const isTbd = props.e.type === 'MATCH' && props.e.is_tbd === true;
  const childLine =
    props.e.type === 'TRAINING' && props.e.is_individual && (props.e.target_child_label ?? '').trim()
      ? `Podopieczny: ${(props.e.target_child_label ?? '').trim()}`
      : null;

  return (
    <button
      type="button"
      className={['EventCard', variant, props.e.is_individual_coach_event ? 'EventCard--individualCoach' : ''].filter(Boolean).join(' ')}
      onClick={props.onOpen}
      disabled={props.disabled}
    >
      <div className="EventCardMain">
        <div className="EventCardTitle">{title}</div>
        <div className="EventCardMeta">
          <div className="EventCardMetaLine">
            <span className="EventMetaStrong">{time}</span>, {props.e.location}
            {isTbd ? ' • DO USTALENIA' : ''}
          </div>
          {childLine ? <div className="EventCardMetaLine is-sub">{childLine}</div> : null}
        </div>
      </div>
      <div className="EventCardSide">
        <div className="EventIconTile" aria-hidden="true">
          {tileIcon}
        </div>
        <div className="EventPill" aria-hidden="true">
          {pillText}
        </div>
      </div>
    </button>
  );
}

function KalendarzPage(props: {
  me: Me;
  role: Role;
  token: string;
  childId?: string | null;
  parentChildren?: ChildItem[];
  workGroups: GroupItem[];
  activeGroupId: string;
  coachViewMode?: CoachViewMode;
  coachChildren?: ChildItem[];
}) {
  const PLANNER_START_MIN = 7 * 60;
  const PLANNER_END_MIN = 22 * 60;
  const PLANNER_STEP_MIN = 30;
  const PLANNER_STEP_PX = 44;
  const PLANNER_PX_PER_MIN = PLANNER_STEP_PX / PLANNER_STEP_MIN;
  const normalizeMatchFormula = React.useCallback((v: string) => v.trim().replace(/\s+/g, ' ').toUpperCase(), []);
  const parseMatchMeta = React.useCallback(
    (desc: string | null | undefined): { visible: string; formula: string | null; minPlayers: number | null } => {
      const raw = (desc ?? '').trim();
      if (!raw) return { visible: '', formula: null, minPlayers: null };
      const m = raw.match(/\[\[\s*FORMAT\s*=\s*([^\]]+?)\s*\]\]/i);
      const formula = m ? normalizeMatchFormula(m[1]) : null;
      const mp = raw.match(/\[\[\s*MINPLAYERS\s*=\s*([0-9]{1,3})\s*\]\]/i);
      const fromTag = mp ? Number.parseInt(mp[1], 10) : NaN;
      const minPlayers =
        Number.isFinite(fromTag) && fromTag > 0
          ? fromTag
          : formula === '5X5'
            ? 5
            : formula === '7X7'
              ? 7
              : formula === '9X9'
                ? 9
                : formula === '11X11'
                  ? 11
                  : null;
      const visible = raw
        .replace(/\[\[\s*FORMAT\s*=\s*[^\]]+?\s*\]\]\s*/gi, '')
        .replace(/\[\[\s*MINPLAYERS\s*=\s*[0-9]{1,3}\s*\]\]\s*/gi, '')
        .trim();
      const formulaLabel = formula === 'INNA' && minPlayers ? `INNA (${minPlayers})` : formula;
      return { visible, formula: formulaLabel, minPlayers };
    },
    [normalizeMatchFormula]
  );
  const [events, setEvents] = React.useState<EventListItem[]>([]);
  const [childPhotoById, setChildPhotoById] = React.useState<Record<string, string | null>>({});
  const manageSectionRef = React.useRef<HTMLElement | null>(null);
  const isChildScoped = isChildScopedRole(props.role);
  const [isLiveSticky, setIsLiveSticky] = React.useState(() => isChildScoped);
  const [selectedId, setSelectedId] = React.useState<string | null>(() => {
    const e = getHashParts().params.get('e');
    const t = e?.trim() ?? '';
    return t ? t : null;
  });
  const [coachFocusChildId, setCoachFocusChildId] = React.useState<string | null>(() => {
    const cc = getHashParts().params.get('cc');
    const t = cc?.trim() ?? '';
    return t ? t : null;
  });
  const [selectedDayKey, setSelectedDayKey] = React.useState<string>(() => dateKeyFromDate(new Date()));
  const [monthCursor, setMonthCursor] = React.useState<Date>(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [plannerOpen, setPlannerOpen] = React.useState(false);
  const [plannerAddReq, setPlannerAddReq] = React.useState<{ startMinutes: number } | null>(null);
  const [plannerPickChildReq, setPlannerPickChildReq] = React.useState<{ type: 'TRAINING' | 'MATCH'; startMinutes: number } | null>(null);
  const [plannerPickChildQuery, setPlannerPickChildQuery] = React.useState('');
  const plannerTrackInnerRef = React.useRef<HTMLDivElement | null>(null);
  const plannerDragMovedRef = React.useRef(false);
  const [plannerDrag, setPlannerDrag] = React.useState<{
    eventId: string;
    pointerId: number;
    grabOffsetPx: number;
    startClientY: number;
    startClientX: number;
    durationMin: number;
    previewStartMin: number;
  } | null>(null);
  const [plannerLayout, setPlannerLayout] = React.useState<{ dayKey: string; byId: Record<string, number> } | null>(null);
  const plannerUndoTimeoutRef = React.useRef<number | null>(null);
  const [plannerUndo, setPlannerUndo] = React.useState<{
    dayKey: string;
    items: Array<{ id: string; start_at: string; end_at: string | null; hasEnd: boolean }>;
  } | null>(null);
  const [calendarPickChildReq, setCalendarPickChildReq] = React.useState<{ type: 'TRAINING' | 'MATCH' } | null>(null);
  const [calendarPickChildQuery, setCalendarPickChildQuery] = React.useState('');
  const [details, setDetails] = React.useState<EventDetails | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [errorText, setErrorText] = React.useState<string | null>(null);
  const [isDeclineOpen, setIsDeclineOpen] = React.useState(false);
  const [declineReason, setDeclineReason] = React.useState('');
  const [trainingAbsenceSheet, setTrainingAbsenceSheet] = React.useState<'NONE' | 'PICK' | 'LONG'>('NONE');
  const [longAbsReason, setLongAbsReason] = React.useState<'CHOROBA' | 'KONTUZJA' | 'INNE'>('CHOROBA');
  const [longAbsFrom, setLongAbsFrom] = React.useState('');
  const [longAbsUntil, setLongAbsUntil] = React.useState('');
  const [longAbsNote, setLongAbsNote] = React.useState('');
  const [longAbsChild, setLongAbsChild] = React.useState<ChildItem | null>(null);
  const [createTargetChildId, setCreateTargetChildId] = React.useState<string | null>(null);
  const [siblingRsvpPrompt, setSiblingRsvpPrompt] = React.useState<{
    eventId: string;
    actionLabel: string;
    primaryChildLabel: string;
    rows: Array<{
      child: ChildItem;
      match_rsvp: MatchRsvp;
      declineReason: string;
      isDeclineOpen: boolean;
      isSaving: boolean;
      error: string | null;
    }>;
  } | null>(null);

  const parentChildById = React.useMemo(() => {
    const out = new Map<string, ChildItem>();
    for (const c of props.parentChildren ?? []) out.set(c.id, c);
    return out;
  }, [props.parentChildren]);

  const individualEventChildIds = React.useMemo(() => {
    const ids = new Set<string>();
    events.forEach((e) => {
      if (e.type !== 'TRAINING') return;
      if (!e.is_individual) return;
      const id = (e.target_child_id ?? '').trim();
      if (id) ids.add(id);
    });
    return Array.from(ids);
  }, [events]);

  React.useEffect(() => {
    if (props.role !== 'COACH' && props.role !== 'ADMIN') return;
    if (individualEventChildIds.length === 0) return;
    let cancelled = false;
    const missing = individualEventChildIds.filter((id) => !(id in childPhotoById));
    if (missing.length === 0) return;
    (async () => {
      const limit = 4;
      const results: Record<string, string | null> = {};
      for (let i = 0; i < missing.length; i += limit) {
        const batch = missing.slice(i, i + limit);
        const settled = await Promise.allSettled(batch.map((id) => apiGetChildPhoto(props.token, id)));
        for (let j = 0; j < batch.length; j += 1) {
          const id = batch[j];
          const r = settled[j];
          if (r.status === 'fulfilled') results[id] = r.value.data_url ?? null;
          else results[id] = null;
        }
        if (cancelled) return;
        setChildPhotoById((prev) => ({ ...prev, ...results }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [childPhotoById, individualEventChildIds, props.role, props.token]);

  const resolveChildPhotoUrl = React.useCallback(
    (childId: string): string | null => {
      const id = (childId ?? '').trim();
      if (!id) return null;
      return childPhotoById[id] ?? null;
    },
    [childPhotoById]
  );

  React.useEffect(() => {
    if (props.role !== 'PARENT') return;
    const abs = (getHashParts().params.get('abs') ?? '').trim().toUpperCase();
    if (abs !== 'LONG') return;
    const childId = ((getHashParts().params.get('c') ?? '') || (props.childId ?? '')).trim();
    if (!childId) return;
    const child = (props.parentChildren ?? []).find((c) => c.id === childId) ?? null;
    if (!child) return;
    setLongAbsChild(child);
    setTrainingAbsenceSheet('LONG');
  }, [props.childId, props.parentChildren, props.role]);
  const [pendingIndividualCreate, setPendingIndividualCreate] = React.useState<{
    childId: string;
    type: 'MATCH' | 'TRAINING';
    groupId: string;
  } | null>(null);
  const [pendingParentPrivateTrainingChildId, setPendingParentPrivateTrainingChildId] = React.useState<string | null>(null);
  const [isParentPrivateTrainingOpen, setIsParentPrivateTrainingOpen] = React.useState(false);
  const [pptTitle, setPptTitle] = React.useState('');
  const [pptStartLocal, setPptStartLocal] = React.useState('');
  const [pptEndLocal, setPptEndLocal] = React.useState('');
  const [pptLocation, setPptLocation] = React.useState('');
  const [pptDescription, setPptDescription] = React.useState('');
  const [isCreateMatchOpen, setIsCreateMatchOpen] = React.useState(false);
  const [cmGroupId, setCmGroupId] = React.useState('');
  const [cmTitle, setCmTitle] = React.useState('');
  const [cmOpponent, setCmOpponent] = React.useState('');
  const [cmOpponentTeamId, setCmOpponentTeamId] = React.useState<string | null>(null);
  const [cmIsTbd, setCmIsTbd] = React.useState(false);
  const [cmMatchKind, setCmMatchKind] = React.useState<MatchKind>('LEAGUE');
  const [cmStartLocal, setCmStartLocal] = React.useState('');
  const [cmEndLocal, setCmEndLocal] = React.useState('');
  const [cmLocation, setCmLocation] = React.useState('');
  const [cmDeadlineLocal, setCmDeadlineLocal] = React.useState('');
  const [cmMeetTime, setCmMeetTime] = React.useState('');
  const [cmNeedsConfirmation, setCmNeedsConfirmation] = React.useState(true);
  const cmDeadlineBackupRef = React.useRef('');
  const [cmDescription, setCmDescription] = React.useState('');
  const [cmFormula, setCmFormula] = React.useState<'5X5' | '7X7' | '9X9' | '11X11' | 'INNA' | ''>('');
  const [cmFormulaOther, setCmFormulaOther] = React.useState('');
  const [cmMinPlayersOther, setCmMinPlayersOther] = React.useState('');
  const [cmShowMissing, setCmShowMissing] = React.useState(false);
  const [cmConflictEvents, setCmConflictEvents] = React.useState<EventDetails[]>([]);
  const cmGroupRef = React.useRef<HTMLSelectElement | null>(null);
  const cmTitleRef = React.useRef<HTMLInputElement | null>(null);
  const cmDateRef = React.useRef<HTMLInputElement | null>(null);
  const cmTimeRef = React.useRef<HTMLInputElement | null>(null);
  const cmLocationRef = React.useRef<HTMLInputElement | null>(null);
  const cmMinPlayersRef = React.useRef<HTMLInputElement | null>(null);
  const [opponentTeams, setOpponentTeams] = React.useState<OpponentTeamItem[]>([]);
  const [editMatchEventId, setEditMatchEventId] = React.useState<string | null>(null);
  const [isAddOpponentConfirmOpen, setIsAddOpponentConfirmOpen] = React.useState(false);
  const [pendingOpponentName, setPendingOpponentName] = React.useState('');
  const [pendingMatchBody, setPendingMatchBody] = React.useState<{
    group_id: string;
    target_child_id?: string | null;
    title: string;
    opponent_team_id?: string | null;
    opponent_name?: string | null;
    is_tbd?: boolean | null;
    start_at: string;
    end_at?: string | null;
    match_meet_at?: string | null;
    location: string;
    description?: string | null;
    match_deadline_at?: string | null;
  } | null>(null);
  const [pendingMatchUpdateEventId, setPendingMatchUpdateEventId] = React.useState<string | null>(null);
  const [pendingMatchUpdateBody, setPendingMatchUpdateBody] = React.useState<{
    title: string;
    is_tbd: boolean;
    start_at: string;
    end_at: string | null;
    match_meet_at: string | null;
    location: string;
    description: string | null;
    match_deadline_at: string | null;
    opponent_team_id: string | null;
    opponent_name: string | null;
    match_kind?: MatchKind | null;
  } | null>(null);
  const [isCreateTrainingOpen, setIsCreateTrainingOpen] = React.useState(false);
  const [ctGroupId, setCtGroupId] = React.useState('');
  const [ctTitle, setCtTitle] = React.useState('');
  const [ctStartLocal, setCtStartLocal] = React.useState('');
  const [ctEndLocal, setCtEndLocal] = React.useState('');
  const [ctLocation, setCtLocation] = React.useState('');
  const [ctDescription, setCtDescription] = React.useState('');
  const [ctRecurring, setCtRecurring] = React.useState(false);
  const [ctIntervalWeeks, setCtIntervalWeeks] = React.useState('1');
  const [ctDurationWeeks, setCtDurationWeeks] = React.useState('8');
  const [ctConflictEvents, setCtConflictEvents] = React.useState<EventDetails[]>([]);
  const [isEditTrainingOpen, setIsEditTrainingOpen] = React.useState(false);
  const [etTitle, setEtTitle] = React.useState('');
  const [etStartLocal, setEtStartLocal] = React.useState('');
  const [etEndLocal, setEtEndLocal] = React.useState('');
  const [etLocation, setEtLocation] = React.useState('');
  const [etDescription, setEtDescription] = React.useState('');
  const [etConflictEvents, setEtConflictEvents] = React.useState<EventDetails[]>([]);
  const [etApplyToSeries, setEtApplyToSeries] = React.useState(false);
  const [isManageMatchOpen, setIsManageMatchOpen] = React.useState(false);
  const [isCancelMatchConfirmOpen, setIsCancelMatchConfirmOpen] = React.useState(false);
  const [isCancelTrainingConfirmOpen, setIsCancelTrainingConfirmOpen] = React.useState(false);
  const [isDeleteEventConfirmOpen, setIsDeleteEventConfirmOpen] = React.useState(false);
  const [callupRoster, setCallupRoster] = React.useState<ChildItem[]>([]);
  const [callupRosterGroupId, setCallupRosterGroupId] = React.useState<string | null>(null);
  const [callupPhotoById, setCallupPhotoById] = React.useState<Record<string, string | null>>({});
  const [callupSelected, setCallupSelected] = React.useState<Record<string, boolean>>({});
  const [callupEventId, setCallupEventId] = React.useState<string | null>(null);
  const [isCallupListOpen, setIsCallupListOpen] = React.useState(false);
  const [callupListFilter, setCallupListFilter] = React.useState<'ALL' | MatchRsvp>('ALL');
  const [callupFilter, setCallupFilter] = React.useState<'ALL' | 'CALLED_UP' | MatchRsvp>('CALLED_UP');
  const [callupInfoText, setCallupInfoText] = React.useState<string | null>(null);
  const callupInfoTimeoutRef = React.useRef<number | null>(null);
  const [parentActionInfoText, setParentActionInfoText] = React.useState<string | null>(null);
  const parentActionInfoTimeoutRef = React.useRef<number | null>(null);
  const [matchNeedById, setMatchNeedById] = React.useState<
    Record<
      string,
      | {
          formula: string;
          minPlayers: number;
          confirmed: number;
          isEnough: boolean;
        }
      | null
      | undefined
    >
  >({});
  const safeChildId = isChildScoped ? (props.childId ?? '').trim() || null : null;
  const isCoachIndividualMode = props.role === 'COACH' && props.coachViewMode === 'INDIVIDUAL';
  const isCoachChildCalendar = isCoachIndividualMode && !!coachFocusChildId;
  const coachChildLabelById = React.useMemo(() => {
    const next: Record<string, string> = {};
    (props.coachChildren ?? []).forEach((c) => {
      const id = (c.id ?? '').trim();
      if (!id) return;
      next[id] = childLabel(c);
    });
    return next;
  }, [props.coachChildren]);
  const coachChildById = React.useMemo(() => {
    const next: Record<string, ChildItem> = {};
    (props.coachChildren ?? []).forEach((c) => {
      const id = (c.id ?? '').trim();
      if (!id) return;
      next[id] = c;
    });
    return next;
  }, [props.coachChildren]);
  const resolveCoachChildLabel = React.useCallback(
    (childId: string): string | null => {
      const id = (childId ?? '').trim();
      if (!id) return null;
      const label = (coachChildLabelById[id] ?? '').trim();
      return label || null;
    },
    [coachChildLabelById]
  );
  const createTargetChild = React.useMemo(() => {
    const id = (createTargetChildId ?? '').trim();
    if (!id) return null;
    return coachChildById[id] ?? null;
  }, [coachChildById, createTargetChildId]);
  const coachFocusChild = React.useMemo(() => {
    if (!isCoachChildCalendar) return null;
    const id = (coachFocusChildId ?? '').trim();
    if (!id) return null;
    return (props.coachChildren ?? []).find((c) => c.id === id) ?? null;
  }, [coachFocusChildId, isCoachChildCalendar, props.coachChildren]);
  const [carpoolEventId, setCarpoolEventId] = React.useState<string | null>(null);

  React.useEffect(() => {
    const id = (createTargetChildId ?? '').trim();
    if (!id) return;
    const gid = (createTargetChild?.group_id ?? '').trim();
    if (!gid) return;
    if (cmGroupId.trim() !== gid) setCmGroupId(gid);
    if (ctGroupId.trim() === gid) return;
    setCtGroupId(gid);
  }, [cmGroupId, createTargetChild?.group_id, createTargetChildId, ctGroupId]);

  const loadList = React.useCallback(async () => {
    setIsLoading(true);
    setErrorText(null);
    try {
      if (isCoachIndividualMode) {
        const cc = (coachFocusChildId ?? '').trim();
        const list0 = cc ? await apiCoachListChildEvents(props.token, cc) : await apiCoachListMyEvents(props.token);
        const onlyChild =
          Array.isArray(props.coachChildren) && props.coachChildren.length === 1 ? (props.coachChildren[0] ?? null) : null;
        const onlyChildId = (onlyChild?.id ?? '').trim();
        const onlyChildLabel = onlyChild ? childLabel(onlyChild) : '';

        const list = list0.map((e) => {
          if ((e.target_child_label ?? '').trim()) return e;
          const cid = (e.target_child_id ?? '').trim();
          if (!cid) {
            if (e.is_individual && onlyChildId && onlyChildLabel) {
              return { ...e, target_child_id: onlyChildId, target_child_label: onlyChildLabel };
            }
            return e;
          }
          const label = (coachChildLabelById[cid] ?? '').trim();
          if (!label) return e;
          return { ...e, target_child_label: label };
        });
        setEvents(list);
      } else {
        const list = await apiListEvents(props.token, isChildScoped ? safeChildId : null);
        setEvents(list);
      }
    } catch (e) {
      setErrorText('Nie udało się pobrać wydarzeń.');
    } finally {
      setIsLoading(false);
    }
  }, [coachChildLabelById, coachFocusChildId, isChildScoped, isCoachIndividualMode, props.coachChildren, props.role, props.token, safeChildId]);

  React.useEffect(() => {
    if (props.role !== 'COACH' && props.role !== 'ADMIN') return;
    if (!props.activeGroupId) return;
    if (!isCreateMatchOpen && cmGroupId !== props.activeGroupId) setCmGroupId(props.activeGroupId);
    if (!isCreateTrainingOpen && ctGroupId !== props.activeGroupId) setCtGroupId(props.activeGroupId);
  }, [cmGroupId, ctGroupId, isCreateMatchOpen, isCreateTrainingOpen, props.activeGroupId, props.role]);

  React.useEffect(() => {
    if (isChildScoped) {
      setIsLiveSticky(true);
      return;
    }
    if (props.role !== 'COACH' && props.role !== 'ADMIN') return;
    const el = manageSectionRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        setIsLiveSticky(!entry.isIntersecting);
      },
      { threshold: 0.01 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [isChildScoped, props.role]);

  const coachGroups = props.workGroups;

  const callupDmFmt = React.useMemo(() => new Intl.DateTimeFormat('pl-PL', { day: '2-digit', month: '2-digit' }), []);

  const callupAbsenceBadgeLabel = React.useCallback(
    (c: ChildItem): string | null => {
      const kind = (c.unavailability_kind ?? '').trim();
      if (!kind) return null;
      const rawReason = String(c.unavailability_reason ?? '')
        .trim()
        .toUpperCase();
      const reason =
        kind === 'KONTUZJA'
          ? 'KONTUZJA'
          : rawReason.includes('CHOROBA')
            ? 'CHOROBA'
            : rawReason.includes('INNE')
              ? 'INNE'
              : 'NIEOBECNY';
      const fromKey = c.unavailability_from ? c.unavailability_from.slice(0, 10) : '';
      const untilKey = c.unavailability_until ? c.unavailability_until.slice(0, 10) : '';
      const fromLabel = fromKey ? callupDmFmt.format(parseDateKey(fromKey)) : '';
      const untilLabel = untilKey ? callupDmFmt.format(parseDateKey(untilKey)) : '';
      const range =
        fromLabel && untilLabel ? `${fromLabel}–${untilLabel}` : fromLabel ? `OD ${fromLabel}` : untilLabel ? `DO ${untilLabel}` : '';
      return range ? `${reason} ${range}` : reason;
    },
    [callupDmFmt]
  );

  const cmOpponentSuggestions = React.useMemo(() => {
    const q = cmOpponent.trim().toLowerCase().replace(/\s+/g, ' ');
    if (!q) return [] as OpponentTeamItem[];
    return opponentTeams.filter((t) => t.name.toLowerCase().includes(q)).slice(0, 6);
  }, [cmOpponent, opponentTeams]);

  const loadDetails = React.useCallback(
    async (eventId: string) => {
      setIsLoading(true);
      setErrorText(null);
      try {
        const childId = isChildScoped ? safeChildId : isCoachIndividualMode ? (coachFocusChildId ?? null) : null;
        const d = await apiGetEvent(props.token, eventId, childId);
        setDetails(d);
      } catch (e) {
        setErrorText('Nie udało się pobrać szczegółów.');
        setDetails(null);
      } finally {
        setIsLoading(false);
      }
    },
    [coachFocusChildId, isCoachIndividualMode, props.role, props.token, safeChildId]
  );

  React.useEffect(() => {
    loadList();
  }, [loadList]);

  React.useEffect(() => {
    if (props.role !== 'COACH' && props.role !== 'ADMIN') {
      setOpponentTeams([]);
      return;
    }
    let cancelled = false;
    apiListOpponentTeams(props.token)
      .then((list) => {
        if (cancelled) return;
        setOpponentTeams(list);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [props.role, props.token]);

  React.useEffect(() => {
    if (!selectedId) {
      setDetails(null);
      return;
    }
    loadDetails(selectedId);
  }, [loadDetails, selectedId]);

  React.useEffect(() => {
    if (!details || details.type !== 'MATCH') return;
    const gid = (details.group_id || '').trim();
    if (!gid) return;
    const ids = details.called_up_child_ids ?? [];
    if (!Array.isArray(ids) || ids.length === 0) return;
    if (isChildScoped && !safeChildId) return;
    if (callupRosterGroupId === gid && callupRoster.length > 0) return;
    let cancelled = false;
    apiGroupChildren(props.token, gid, isChildScoped ? safeChildId : null)
      .then((roster) => {
        if (cancelled) return;
        setCallupRoster(roster);
        setCallupRosterGroupId(gid);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [callupRoster.length, callupRosterGroupId, details, isChildScoped, props.token, safeChildId]);

  React.useEffect(() => {
    const ids = new Set<string>();
    if (callupEventId) callupRoster.forEach((c) => ids.add(c.id));
    if (isCallupListOpen && details?.type === 'MATCH') (details.called_up_child_ids ?? []).forEach((id) => ids.add(id));
    const missing = Array.from(ids).filter((id) => !(id in callupPhotoById));
    if (missing.length === 0) return;
    let cancelled = false;
    Promise.all(
      missing.map(async (id) => {
        try {
          const r = await apiGetChildPhoto(props.token, id);
          return [id, r.data_url ?? null] as const;
        } catch {
          return [id, null] as const;
        }
      })
    ).then((pairs) => {
      if (cancelled) return;
      setCallupPhotoById((prev) => {
        const next = { ...prev };
        pairs.forEach(([id, url]) => {
          if (!(id in next)) next[id] = url;
        });
        return next;
      });
    });
    return () => {
      cancelled = true;
    };
  }, [callupEventId, callupPhotoById, callupRoster, details, isCallupListOpen, props.token]);

  React.useEffect(() => {
    const onHashChange = () => {
      const { params } = getHashParts();
      const e = params.get('e');
      const t = e?.trim() ?? '';
      setSelectedId(t ? t : null);

      const cc = (params.get('cc') ?? '').trim();
      setCoachFocusChildId(cc ? cc : null);

      const ic = (params.get('ic') ?? '').trim();
      const it = (params.get('it') ?? '').trim().toUpperCase();
      const ig = (params.get('ig') ?? '').trim();
      if ((props.role === 'COACH' || props.role === 'ADMIN') && ic && (it === 'MATCH' || it === 'TRAINING')) {
        setPendingIndividualCreate({ childId: ic, type: it as 'MATCH' | 'TRAINING', groupId: ig });
        navigate('kalendarz');
      }

      const pt = (params.get('pt') ?? '').trim();
      const ptc = (params.get('ptc') ?? '').trim();
      if (props.role === 'PARENT' && pt === '1' && ptc) {
        setPendingParentPrivateTrainingChildId(ptc);
        navigate('kalendarz');
      }
    };
    window.addEventListener('hashchange', onHashChange);
    onHashChange();
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const openEvent = (eventId: string) => {
    const cc = isCoachIndividualMode ? (coachFocusChildId ?? '').trim() : '';
    navigate('kalendarz', cc ? { e: eventId, cc } : { e: eventId });
  };
  const closeEvent = () => {
    const cc = isCoachIndividualMode ? (coachFocusChildId ?? '').trim() : '';
    navigate('kalendarz', cc ? { cc } : undefined);
  };

  const onRepublishEventToFeed = React.useCallback(async () => {
    if (props.role !== 'COACH' && props.role !== 'ADMIN') return;
    if (!selectedId) return;
    setIsLoading(true);
    setErrorText(null);
    try {
      const r = await apiCoachRepublishEventToFeed(props.token, selectedId);
      navigate('feed', { p: r.post_id });
    } catch (e) {
      setErrorText(adminActionError('Nie udało się ponownie opublikować na feed.', e));
    } finally {
      setIsLoading(false);
    }
  }, [props.role, props.token, selectedId]);

  const toLocalInputValue = (d: Date) => {
    const pad2 = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  };

  const openParentPrivateTraining = React.useCallback(() => {
    if (props.role !== 'PARENT') return;
    const base = parseDateKey(selectedDayKey);
    const start = new Date(base.getFullYear(), base.getMonth(), base.getDate(), 18, 0, 0);
    const end = new Date(base.getFullYear(), base.getMonth(), base.getDate(), 19, 0, 0);
    setPptTitle('Trening prywatny');
    setPptStartLocal(toLocalInputValue(start));
    setPptEndLocal(toLocalInputValue(end));
    setPptLocation(loadLastLocation() ?? 'Prywatnie');
    setPptDescription('');
    setIsParentPrivateTrainingOpen(true);
  }, [props.role, selectedDayKey]);

  const openCreateMatch = (opts?: { groupId?: string | null; targetChildId?: string | null }) => {
    const targetId = (opts?.targetChildId ?? '').trim() || null;
    const preferredGroupId = (opts?.groupId ?? '').trim();
    setCreateTargetChildId(targetId);
    setEditMatchEventId(null);
    const base = parseDateKey(selectedDayKey);
    const start = new Date(base.getFullYear(), base.getMonth(), base.getDate(), 11, 0, 0);
    const end = new Date(base.getFullYear(), base.getMonth(), base.getDate(), 13, 0, 0);
    const meet = new Date(start.getTime() - 30 * 60 * 1000);
    const deadline = new Date(base.getFullYear(), base.getMonth(), base.getDate() - 1, 20, 0, 0);
    const defaultGroupId = (preferredGroupId || props.activeGroupId || coachGroups[0]?.id || '').trim();
    if (targetId) {
      setCmGroupId(defaultGroupId);
      setCmMatchKind('LEAGUE');
      setCmTitle('Mecz');
      setCmOpponent('');
      setCmOpponentTeamId(null);
      setCmIsTbd(false);
      setCmStartLocal(toLocalInputValue(start));
      setCmEndLocal(toLocalInputValue(end));
      setCmNeedsConfirmation(true);
      setCmDeadlineLocal(toLocalInputValue(deadline));
      setCmMeetTime(`${String(meet.getHours()).padStart(2, '0')}:${String(meet.getMinutes()).padStart(2, '0')}`);
      setCmDescription('');
      setCmFormula('');
      setCmFormulaOther('');
      setCmMinPlayersOther('');
      setCmShowMissing(false);
      setCmLocation(loadLastLocation() ?? '');
      setIsCreateMatchOpen(true);
      return;
    }
    if (!cmGroupId.trim() && defaultGroupId) setCmGroupId(defaultGroupId);
    if (!cmTitle.trim()) setCmTitle('Mecz');
    if (!cmOpponent.trim()) {
      setCmOpponent('');
      setCmOpponentTeamId(null);
    }
    if (!cmStartLocal) setCmStartLocal(toLocalInputValue(start));
    if (!cmEndLocal) setCmEndLocal(toLocalInputValue(end));
    if (!cmMeetTime.trim())
      setCmMeetTime(`${String(meet.getHours()).padStart(2, '0')}:${String(meet.getMinutes()).padStart(2, '0')}`);
    if (cmNeedsConfirmation) {
      if (!cmDeadlineLocal) setCmDeadlineLocal(toLocalInputValue(deadline));
    } else {
      if (cmDeadlineLocal) {
        cmDeadlineBackupRef.current = cmDeadlineLocal;
        setCmDeadlineLocal('');
      }
    }
    if (!cmLocation.trim()) setCmLocation(loadLastLocation() ?? '');
    if (!cmFormula) {
      setCmFormula('');
      setCmFormulaOther('');
      setCmMinPlayersOther('');
    }
    setIsCreateMatchOpen(true);
  };

  const isEditingMatch = !!editMatchEventId;

  React.useEffect(() => {
    if (!isCreateMatchOpen) return;
    if (isEditingMatch) return;
    const defaultGroupId = (props.activeGroupId || coachGroups[0]?.id || '').trim();
    if (!cmGroupId.trim() && defaultGroupId) setCmGroupId(defaultGroupId);
    const base = parseDateKey(selectedDayKey);
    const start = new Date(base.getFullYear(), base.getMonth(), base.getDate(), 11, 0, 0);
    const end = new Date(base.getFullYear(), base.getMonth(), base.getDate(), 13, 0, 0);
    const meet = new Date(start.getTime() - 30 * 60 * 1000);
    const deadline = new Date(base.getFullYear(), base.getMonth(), base.getDate() - 1, 20, 0, 0);
    if (!cmTitle.trim()) setCmTitle('Mecz');
    if (!cmStartLocal.trim()) setCmStartLocal(toLocalInputValue(start));
    if (!cmEndLocal.trim()) setCmEndLocal(toLocalInputValue(end));
    if (!cmMeetTime.trim())
      setCmMeetTime(`${String(meet.getHours()).padStart(2, '0')}:${String(meet.getMinutes()).padStart(2, '0')}`);
    if (cmNeedsConfirmation && !cmDeadlineLocal.trim()) setCmDeadlineLocal(toLocalInputValue(deadline));
  }, [
    cmDeadlineLocal,
    cmEndLocal,
    cmGroupId,
    cmMeetTime,
    cmNeedsConfirmation,
    cmStartLocal,
    cmTitle,
    coachGroups,
    isCreateMatchOpen,
    isEditingMatch,
    props.activeGroupId,
    selectedDayKey,
  ]);

  const openCreateTraining = (opts?: { groupId?: string | null; targetChildId?: string | null }) => {
    const targetId = (opts?.targetChildId ?? '').trim() || null;
    const preferredGroupId = (opts?.groupId ?? '').trim();
    setCreateTargetChildId(targetId);
    const base = parseDateKey(selectedDayKey);
    const start = new Date(base.getFullYear(), base.getMonth(), base.getDate(), 18, 0, 0);
    const end = new Date(base.getFullYear(), base.getMonth(), base.getDate(), 19, 30, 0);
    const defaultGroupId = (preferredGroupId || props.activeGroupId || coachGroups[0]?.id || '').trim();
    if (targetId) {
      setCtGroupId(defaultGroupId);
      setCtTitle('Trening');
      setCtStartLocal(toLocalInputValue(start));
      setCtEndLocal(toLocalInputValue(end));
      setCtLocation(loadLastLocation() ?? cmLocation.trim() ?? '');
      setCtDescription('');
      setCtRecurring(false);
      setCtIntervalWeeks('1');
      setCtDurationWeeks('8');
      setIsCreateTrainingOpen(true);
      return;
    }
    if (!ctGroupId.trim() && defaultGroupId) setCtGroupId(defaultGroupId);
    if (!ctTitle.trim()) setCtTitle('Trening');
    if (!ctStartLocal) setCtStartLocal(toLocalInputValue(start));
    if (!ctEndLocal) setCtEndLocal(toLocalInputValue(end));
    if (!ctLocation.trim()) setCtLocation(loadLastLocation() ?? cmLocation.trim() ?? '');
    if (!ctIntervalWeeks.trim()) setCtIntervalWeeks('1');
    if (!ctDurationWeeks.trim()) setCtDurationWeeks('8');
    setIsCreateTrainingOpen(true);
  };

  const openCreateTrainingAt = (startMinutes: number, opts?: { groupId?: string | null; targetChildId?: string | null }) => {
    const targetId = (opts?.targetChildId ?? '').trim() || null;
    const preferredGroupId = (opts?.groupId ?? '').trim();
    setCreateTargetChildId(targetId);
    const base = parseDateKey(selectedDayKey);
    const start = new Date(base.getFullYear(), base.getMonth(), base.getDate(), 0, 0, 0);
    start.setMinutes(startMinutes);
    const end = new Date(start.getTime() + 90 * 60 * 1000);
    const defaultGroupId = (preferredGroupId || props.activeGroupId || coachGroups[0]?.id || '').trim();
    setCtGroupId(defaultGroupId);
    setCtTitle('Trening');
    setCtStartLocal(toLocalInputValue(start));
    setCtEndLocal(toLocalInputValue(end));
    setCtLocation(loadLastLocation() ?? cmLocation.trim() ?? '');
    setCtDescription('');
    setCtRecurring(false);
    setCtIntervalWeeks('1');
    setCtDurationWeeks('8');
    setIsCreateTrainingOpen(true);
  };

  const openCreateMatchAt = (startMinutes: number, opts?: { groupId?: string | null; targetChildId?: string | null }) => {
    const targetId = (opts?.targetChildId ?? '').trim() || null;
    const preferredGroupId = (opts?.groupId ?? '').trim();
    setCreateTargetChildId(targetId);
    setEditMatchEventId(null);
    const base = parseDateKey(selectedDayKey);
    const start = new Date(base.getFullYear(), base.getMonth(), base.getDate(), 0, 0, 0);
    start.setMinutes(startMinutes);
    const end = new Date(start.getTime() + 120 * 60 * 1000);
    const meet = new Date(start.getTime() - 30 * 60 * 1000);
    const deadline = new Date(start.getFullYear(), start.getMonth(), start.getDate() - 1, 20, 0, 0);
    const defaultGroupId = (preferredGroupId || props.activeGroupId || coachGroups[0]?.id || '').trim();
    if (defaultGroupId) setCmGroupId(defaultGroupId);
    setCmTitle('Mecz');
    setCmOpponent('');
    setCmOpponentTeamId(null);
    setCmIsTbd(false);
    setCmMatchKind('LEAGUE');
    setCmStartLocal(toLocalInputValue(start));
    setCmEndLocal(toLocalInputValue(end));
    setCmLocation(loadLastLocation() ?? cmLocation.trim() ?? '');
    setCmNeedsConfirmation(true);
    setCmDeadlineLocal(toLocalInputValue(deadline));
    setCmMeetTime(`${String(meet.getHours()).padStart(2, '0')}:${String(meet.getMinutes()).padStart(2, '0')}`);
    setCmDescription('');
    setCmShowMissing(false);
    setIsCreateMatchOpen(true);
  };

  React.useEffect(() => {
    if (!isCreateTrainingOpen) return;
    const defaultGroupId = (props.activeGroupId || coachGroups[0]?.id || '').trim();
    if (!ctGroupId.trim() && defaultGroupId) setCtGroupId(defaultGroupId);
    const base = parseDateKey(selectedDayKey);
    const start = new Date(base.getFullYear(), base.getMonth(), base.getDate(), 18, 0, 0);
    const end = new Date(base.getFullYear(), base.getMonth(), base.getDate(), 19, 30, 0);
    if (!ctTitle.trim()) setCtTitle('Trening');
    if (!ctStartLocal.trim()) setCtStartLocal(toLocalInputValue(start));
    if (!ctEndLocal.trim()) setCtEndLocal(toLocalInputValue(end));
  }, [coachGroups, ctEndLocal, ctGroupId, ctStartLocal, ctTitle, isCreateTrainingOpen, props.activeGroupId, selectedDayKey]);

  React.useEffect(() => {
    const req = pendingIndividualCreate;
    if (!req) return;
    if (props.role !== 'COACH' && props.role !== 'ADMIN') return;
    const fallbackGroupId = (props.activeGroupId || coachGroups[0]?.id || '').trim();
    const gid = (req.groupId || fallbackGroupId).trim();
    if (req.type === 'MATCH') openCreateMatch({ groupId: gid, targetChildId: req.childId });
    else openCreateTraining({ groupId: gid, targetChildId: req.childId });
    setPendingIndividualCreate(null);
  }, [coachGroups, openCreateMatch, openCreateTraining, pendingIndividualCreate, props.activeGroupId, props.role]);

  React.useEffect(() => {
    const cid = (pendingParentPrivateTrainingChildId ?? '').trim();
    if (!cid) return;
    if (props.role !== 'PARENT') {
      setPendingParentPrivateTrainingChildId(null);
      return;
    }
    if (!safeChildId || safeChildId !== cid) {
      setPendingParentPrivateTrainingChildId(null);
      return;
    }
    openParentPrivateTraining();
    setPendingParentPrivateTrainingChildId(null);
  }, [openParentPrivateTraining, pendingParentPrivateTrainingChildId, props.role, safeChildId]);

  React.useEffect(() => {
    if (isCreateMatchOpen) return;
    if (isCreateTrainingOpen) return;
    setCreateTargetChildId(null);
  }, [isCreateMatchOpen, isCreateTrainingOpen]);

  const localToIso = (value: string) => {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return d.toISOString();
  };

  const splitLocalDateTime = (value: string): { date: string; time: string } => {
    const v = value.trim();
    if (!v) return { date: '', time: '' };
    const [date, time] = v.split('T');
    return { date: date || '', time: (time || '').slice(0, 5) };
  };

  const joinLocalDateTime = (date: string, time: string) => {
    const d = date.trim();
    const t = time.trim();
    if (!d || !t) return '';
    return `${d}T${t}`;
  };

  const cmStartParts = React.useMemo(() => splitLocalDateTime(cmStartLocal), [cmStartLocal]);
  const cmEndParts = React.useMemo(() => splitLocalDateTime(cmEndLocal), [cmEndLocal]);
  const cmDeadlineParts = React.useMemo(() => splitLocalDateTime(cmDeadlineLocal), [cmDeadlineLocal]);

  const minutesFromIsoLocal = React.useCallback((iso: string): number | null => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.getHours() * 60 + d.getMinutes();
  }, []);

  const eventDurationMinutes = React.useCallback((e: { type: EventType; start_at: string; end_at?: string | null }): number => {
    const s = Date.parse(e.start_at);
    const endRaw = (e.end_at ?? '').trim();
    if (!Number.isNaN(s) && endRaw) {
      const ee = Date.parse(endRaw);
      if (!Number.isNaN(ee) && ee > s) return Math.max(30, Math.round((ee - s) / 60000));
    }
    return e.type === 'MATCH' ? 120 : 90;
  }, []);

  const clampNumber = React.useCallback((v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v)), []);

  const roundToPlannerStep = React.useCallback(
    (minutes: number) => Math.round(minutes / PLANNER_STEP_MIN) * PLANNER_STEP_MIN,
    [PLANNER_STEP_MIN]
  );

  const roundUpToPlannerStep = React.useCallback(
    (minutes: number) => Math.ceil(minutes / PLANNER_STEP_MIN) * PLANNER_STEP_MIN,
    [PLANNER_STEP_MIN]
  );

  React.useEffect(() => {
    if (plannerUndoTimeoutRef.current) {
      window.clearTimeout(plannerUndoTimeoutRef.current);
      plannerUndoTimeoutRef.current = null;
    }
    if (!plannerUndo) return;
    plannerUndoTimeoutRef.current = window.setTimeout(() => {
      setPlannerUndo(null);
      plannerUndoTimeoutRef.current = null;
    }, 9000);
    return () => {
      if (plannerUndoTimeoutRef.current) {
        window.clearTimeout(plannerUndoTimeoutRef.current);
        plannerUndoTimeoutRef.current = null;
      }
    };
  }, [plannerUndo]);

  React.useEffect(() => {
    if (!plannerOpen) {
      setPlannerUndo(null);
      setPlannerLayout(null);
      return;
    }
    if (plannerUndo && plannerUndo.dayKey !== selectedDayKey) setPlannerUndo(null);
    if (plannerLayout && plannerLayout.dayKey !== selectedDayKey) setPlannerLayout(null);
  }, [plannerOpen, plannerUndo, selectedDayKey]);

  const applyPlannerReorder = React.useCallback(
    async (movedEventId: string, targetStartMin: number) => {
      if (props.role !== 'COACH' && props.role !== 'ADMIN') return;
      const id = movedEventId.trim();
      if (!id) return;
      const moved = events.find((e) => e.id === id) ?? null;
      if (!moved) return;
      if (moved.status !== 'ACTIVE') return;
      if (props.role === 'COACH' && isCoachIndividualMode) {
        if (!moved.is_individual || !moved.can_take_action) {
          setErrorText('Nie możesz przesuwać tych wydarzeń w trybie trenera indywidualnego.');
          return;
        }
      }
      if (dateKeyFromIso(moved.start_at) !== selectedDayKey) return;

      const dayEvents = events
        .filter((e) => e.status === 'ACTIVE')
        .filter((e) => dateKeyFromIso(e.start_at) === selectedDayKey)
        .filter((e) => minutesFromIsoLocal(e.start_at) !== null);

      const movedDuration = eventDurationMinutes(moved);
      const movedStart = clampNumber(roundToPlannerStep(targetStartMin), PLANNER_START_MIN, PLANNER_END_MIN - movedDuration);

      const desired = dayEvents
        .map((e) => {
          const m0 = minutesFromIsoLocal(e.start_at);
          if (m0 === null) return null;
          const duration = eventDurationMinutes(e);
          const start =
            e.id === id ? movedStart : clampNumber(m0, PLANNER_START_MIN, Math.max(PLANNER_START_MIN, PLANNER_END_MIN - duration));
          return { id: e.id, desired: start, duration, hasEnd: !!(e.end_at ?? '').trim() };
        })
        .filter((x): x is { id: string; desired: number; duration: number; hasEnd: boolean } => !!x);

      const movedEntry = desired.find((x) => x.id === id) ?? null;
      if (!movedEntry) return;
      const others = desired
        .filter((x) => x.id !== id)
        .sort((a, b) => a.desired - b.desired || a.id.localeCompare(b.id));

      const scheduled: Array<{ id: string; start: number; end: number; duration: number; hasEnd: boolean }> = [];
      const place = (x: { id: string; desired: number; duration: number; hasEnd: boolean }) => {
        let s = x.desired;
        while (true) {
          const e = s + x.duration;
          if (e > PLANNER_END_MIN) return null as { start: number; end: number } | null;
          const overlapping = scheduled.filter((p) => s < p.end && p.start < e);
          if (overlapping.length === 0) return { start: s, end: e };
          const next = Math.max(...overlapping.map((p) => p.end));
          s = roundUpToPlannerStep(next);
        }
      };

      const firstPlaced = place(movedEntry);
      if (!firstPlaced) {
        setPlannerLayout(null);
        setErrorText('Brak miejsca w planie dnia, aby przestawić wydarzenie.');
        return;
      }
      scheduled.push({ id: movedEntry.id, start: firstPlaced.start, end: firstPlaced.end, duration: movedEntry.duration, hasEnd: movedEntry.hasEnd });

      for (const x of others) {
        const placed = place(x);
        if (!placed) {
          setPlannerLayout(null);
          setErrorText('Brak miejsca w planie dnia — wydarzenie spod spodu nie ma już zapasu godzinowego.');
          return;
        }
        scheduled.push({ id: x.id, start: placed.start, end: placed.end, duration: x.duration, hasEnd: x.hasEnd });
      }

      const changed = scheduled
        .map((p) => {
          const src = dayEvents.find((e) => e.id === p.id) ?? null;
          if (!src) return null;
          const curMin = minutesFromIsoLocal(src.start_at);
          if (curMin === null) return null;
          if (curMin === p.start) return null;
          return { id: p.id, startMin: p.start, duration: p.duration, hasEnd: p.hasEnd };
        })
        .filter((x): x is { id: string; startMin: number; duration: number; hasEnd: boolean } => !!x);

      if (changed.length === 0) return;

      const undoItems = changed
        .map((ch) => {
          const src = dayEvents.find((e) => e.id === ch.id) ?? null;
          if (!src) return null;
          return { id: ch.id, start_at: src.start_at, end_at: (src.end_at ?? null) as string | null, hasEnd: ch.hasEnd };
        })
        .filter((x): x is { id: string; start_at: string; end_at: string | null; hasEnd: boolean } => !!x);

      const layoutById: Record<string, number> = {};
      for (const p of scheduled) layoutById[p.id] = p.start;
      setPlannerLayout({ dayKey: selectedDayKey, byId: layoutById });

      setIsLoading(true);
      setErrorText(null);
      try {
        const base = parseDateKey(selectedDayKey);
        for (const ch of changed) {
          const startLocal = new Date(base.getFullYear(), base.getMonth(), base.getDate(), 0, 0, 0);
          startLocal.setMinutes(ch.startMin);
          const body: any = { start_at: startLocal.toISOString() };
          if (ch.hasEnd) body.end_at = new Date(startLocal.getTime() + ch.duration * 60 * 1000).toISOString();
          await apiCoachUpdateEvent(props.token, ch.id, body);
        }
        await loadList();
        if (selectedId && changed.some((c) => c.id === selectedId)) {
          await loadDetails(selectedId);
        }
        if (undoItems.length > 0) setPlannerUndo({ dayKey: selectedDayKey, items: undoItems });
        setPlannerLayout(null);
      } catch (e) {
        setPlannerLayout(null);
        setErrorText(humanizeHttpError(e, 'Nie udało się przestawić wydarzeń.'));
        await loadList();
      } finally {
        setIsLoading(false);
      }
    },
    [
      PLANNER_END_MIN,
      PLANNER_START_MIN,
      PLANNER_STEP_MIN,
      clampNumber,
      eventDurationMinutes,
      events,
      isCoachIndividualMode,
      loadDetails,
      loadList,
      minutesFromIsoLocal,
      parseDateKey,
      props.role,
      props.token,
      roundToPlannerStep,
      roundUpToPlannerStep,
      selectedDayKey,
      selectedId,
    ]
  );

  const onPlannerUndo = React.useCallback(async () => {
    if (!plannerUndo) return;
    if (plannerUndo.dayKey !== selectedDayKey) {
      setPlannerUndo(null);
      return;
    }
    if (props.role !== 'COACH' && props.role !== 'ADMIN') return;
    if (isLoading) return;
    setIsLoading(true);
    setErrorText(null);
    try {
      for (const it of plannerUndo.items) {
        const body: any = { start_at: it.start_at };
        if (it.hasEnd) body.end_at = it.end_at;
        await apiCoachUpdateEvent(props.token, it.id, body);
      }
      setPlannerUndo(null);
      await loadList();
      if (selectedId) await loadDetails(selectedId);
    } catch (e) {
      setErrorText(humanizeHttpError(e, 'Nie udało się cofnąć zmian.'));
      await loadList();
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, loadDetails, loadList, plannerUndo, props.role, props.token, selectedDayKey, selectedId]);

  React.useEffect(() => {
    if (!plannerDrag) return;
    const onMove = (ev: PointerEvent) => {
      if (ev.pointerId !== plannerDrag.pointerId) return;
      const dx = Math.abs(ev.clientX - plannerDrag.startClientX);
      const dy = Math.abs(ev.clientY - plannerDrag.startClientY);
      if (dx + dy > 6) plannerDragMovedRef.current = true;
      const el = plannerTrackInnerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const yRel = ev.clientY - rect.top - plannerDrag.grabOffsetPx;
      const startCandidate = PLANNER_START_MIN + yRel / PLANNER_PX_PER_MIN;
      const rounded = clampNumber(
        roundToPlannerStep(startCandidate),
        PLANNER_START_MIN,
        Math.max(PLANNER_START_MIN, PLANNER_END_MIN - plannerDrag.durationMin)
      );
      setPlannerDrag((prev) => (prev ? { ...prev, previewStartMin: rounded } : prev));
    };
    const onUp = (ev: PointerEvent) => {
      if (ev.pointerId !== plannerDrag.pointerId) return;
      const endMin = plannerDrag.previewStartMin;
      const id = plannerDrag.eventId;
      setPlannerDrag(null);
      if (!plannerDragMovedRef.current) return;
      plannerDragMovedRef.current = false;
      void applyPlannerReorder(id, endMin);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [
    PLANNER_END_MIN,
    PLANNER_PX_PER_MIN,
    PLANNER_START_MIN,
    applyPlannerReorder,
    clampNumber,
    plannerDrag,
    roundToPlannerStep,
  ]);

  const cmMinPlayersOtherInt = React.useMemo(() => Number.parseInt(cmMinPlayersOther.trim(), 10), [cmMinPlayersOther]);
  const cmHasValidMinPlayersOther = React.useMemo(
    () => Number.isFinite(cmMinPlayersOtherInt) && cmMinPlayersOtherInt > 0 && cmMinPlayersOtherInt <= 30,
    [cmMinPlayersOtherInt]
  );
  const cmCanSubmit = React.useMemo(() => {
    if (isLoading) return false;
    if (!cmGroupId.trim()) return false;
    if (!cmTitle.trim()) return false;
    if (!cmStartLocal.trim()) return false;
    if (!cmLocation.trim()) return false;
    if (cmFormula === 'INNA' && !cmHasValidMinPlayersOther) return false;
    return true;
  }, [cmFormula, cmGroupId, cmHasValidMinPlayersOther, cmLocation, cmStartLocal, cmTitle, isLoading]);

  const pptCanSubmit = React.useMemo(() => {
    if (isLoading) return false;
    if (props.role !== 'PARENT') return false;
    if (!safeChildId) return false;
    if (!pptTitle.trim()) return false;
    if (!pptStartLocal.trim()) return false;
    if (!pptLocation.trim()) return false;
    return true;
  }, [isLoading, pptLocation, pptStartLocal, pptTitle, props.role, safeChildId]);

  const ctIntervalWeeksInt = React.useMemo(() => {
    const n = Number.parseInt(ctIntervalWeeks, 10);
    if (!Number.isFinite(n) || n < 1) return 1;
    if (n > 8) return 8;
    return n;
  }, [ctIntervalWeeks]);

  const ctMaxDurationWeeks = ctIntervalWeeksInt * 52;

  const ctDurationWeeksInt = React.useMemo(() => {
    const n = Number.parseInt(ctDurationWeeks, 10);
    if (!Number.isFinite(n) || n < 1) return 1;
    return n;
  }, [ctDurationWeeks]);

  const ctOccurrences = React.useMemo(() => {
    if (!ctRecurring) return 1;
    const duration = ctDurationWeeksInt;
    const interval = ctIntervalWeeksInt;
    const occ = Math.floor(Math.max(0, duration - 1) / interval) + 1;
    return Math.max(1, occ);
  }, [ctDurationWeeksInt, ctIntervalWeeksInt, ctRecurring]);

  const ctRecurringInvalid = React.useMemo(() => {
    if (!ctRecurring) return null;
    if (ctDurationWeeksInt < 1) return 'Uzupełnij pole: Przez ile tygodni';
    if (ctDurationWeeksInt > ctMaxDurationWeeks) return `Maksymalnie: ${ctMaxDurationWeeks} tyg. (limit 52 treningów)`;
    if (ctOccurrences < 1 || ctOccurrences > 52) return 'Limit: maks. 52 treningi w cyklu';
    return null;
  }, [ctDurationWeeksInt, ctMaxDurationWeeks, ctOccurrences, ctRecurring]);

  React.useEffect(() => {
    if (!isCreateMatchOpen) return;
    const targetId = (createTargetChildId ?? '').trim();
    if (!targetId) {
      setCmConflictEvents([]);
      return;
    }
    const plannedStartIso = cmStartLocal.trim() ? localToIso(cmStartLocal.trim()) : '';
    if (!plannedStartIso) {
      setCmConflictEvents([]);
      return;
    }
    const plannedEndIso = cmEndLocal.trim() ? localToIso(cmEndLocal.trim()) : '';
    const plannedStartMs = Date.parse(plannedStartIso);
    if (Number.isNaN(plannedStartMs)) {
      setCmConflictEvents([]);
      return;
    }
    const plannedEndMs = (() => {
      if (plannedEndIso) {
        const ms = Date.parse(plannedEndIso);
        if (!Number.isNaN(ms)) return ms;
      }
      return plannedStartMs + 120 * 60 * 1000;
    })();
    const aStart = plannedStartMs;
    const aEnd = Math.max(plannedEndMs, plannedStartMs);
    const overlaps = (bStart: number, bEnd: number) => aStart < bEnd && bStart < aEnd;

    const keyA = dateKeyFromIso(plannedStartIso);
    const keyB = plannedEndIso ? dateKeyFromIso(plannedEndIso) : keyA;
    const candidates = events
      .filter((e) => e.status === 'ACTIVE')
      .filter((e) => {
        const k = dateKeyFromIso(e.start_at);
        return k === keyA || k === keyB;
      })
      .slice(0, 14);

    let alive = true;
    Promise.all(
      candidates.map(async (e) => {
        try {
          return await apiGetEvent(props.token, e.id, null);
        } catch {
          return null;
        }
      })
    ).then((rows) => {
      if (!alive) return;
      const found = rows
        .filter((d): d is EventDetails => !!d)
        .filter((d) => d.status === 'ACTIVE')
        .filter((d) => {
          const bStart = Date.parse(d.start_at);
          if (Number.isNaN(bStart)) return false;
          const bEnd = (() => {
            const raw = (d.end_at ?? '').trim();
            if (raw) {
              const ms = Date.parse(raw);
              if (!Number.isNaN(ms)) return ms;
            }
            if (d.type === 'MATCH') return bStart + 120 * 60 * 1000;
            if (d.type === 'TRAINING') return bStart + 90 * 60 * 1000;
            return bStart + 60 * 60 * 1000;
          })();
          return overlaps(bStart, Math.max(bEnd, bStart));
        })
        .slice()
        .sort((a, b) => a.start_at.localeCompare(b.start_at));
      setCmConflictEvents(found);
    });
    return () => {
      alive = false;
    };
  }, [cmEndLocal, cmStartLocal, createTargetChildId, events, isCreateMatchOpen, props.token]);

  React.useEffect(() => {
    if (!isCreateTrainingOpen) return;
    const targetId = (createTargetChildId ?? '').trim();
    if (!targetId) {
      setCtConflictEvents([]);
      return;
    }
    const plannedStartIso = ctStartLocal.trim() ? localToIso(ctStartLocal.trim()) : '';
    if (!plannedStartIso) {
      setCtConflictEvents([]);
      return;
    }
    const plannedEndIso = ctEndLocal.trim() ? localToIso(ctEndLocal.trim()) : '';
    const plannedStartMs = Date.parse(plannedStartIso);
    if (Number.isNaN(plannedStartMs)) {
      setCtConflictEvents([]);
      return;
    }
    const plannedEndMs = (() => {
      if (plannedEndIso) {
        const ms = Date.parse(plannedEndIso);
        if (!Number.isNaN(ms)) return ms;
      }
      return plannedStartMs + 90 * 60 * 1000;
    })();
    const aStart = plannedStartMs;
    const aEnd = Math.max(plannedEndMs, plannedStartMs);
    const overlaps = (bStart: number, bEnd: number) => aStart < bEnd && bStart < aEnd;

    const keyA = dateKeyFromIso(plannedStartIso);
    const keyB = plannedEndIso ? dateKeyFromIso(plannedEndIso) : keyA;
    const candidates = events
      .filter((e) => e.status === 'ACTIVE')
      .filter((e) => {
        const k = dateKeyFromIso(e.start_at);
        return k === keyA || k === keyB;
      })
      .slice(0, 14);

    let alive = true;
    Promise.all(
      candidates.map(async (e) => {
        try {
          return await apiGetEvent(props.token, e.id, null);
        } catch {
          return null;
        }
      })
    ).then((rows) => {
      if (!alive) return;
      const found = rows
        .filter((d): d is EventDetails => !!d)
        .filter((d) => d.status === 'ACTIVE')
        .filter((d) => {
          const bStart = Date.parse(d.start_at);
          if (Number.isNaN(bStart)) return false;
          const bEnd = (() => {
            const raw = (d.end_at ?? '').trim();
            if (raw) {
              const ms = Date.parse(raw);
              if (!Number.isNaN(ms)) return ms;
            }
            if (d.type === 'MATCH') return bStart + 120 * 60 * 1000;
            if (d.type === 'TRAINING') return bStart + 90 * 60 * 1000;
            return bStart + 60 * 60 * 1000;
          })();
          return overlaps(bStart, Math.max(bEnd, bStart));
        })
        .slice()
        .sort((a, b) => a.start_at.localeCompare(b.start_at));
      setCtConflictEvents(found);
    });
    return () => {
      alive = false;
    };
  }, [createTargetChildId, ctEndLocal, ctStartLocal, events, isCreateTrainingOpen, props.token]);

  React.useEffect(() => {
    if (!isEditTrainingOpen) return;
    if (!details || details.type !== 'TRAINING') {
      setEtConflictEvents([]);
      return;
    }
    const plannedStartIso = etStartLocal.trim() ? localToIso(etStartLocal.trim()) : '';
    if (!plannedStartIso) {
      setEtConflictEvents([]);
      return;
    }
    const plannedEndIso = etEndLocal.trim() ? localToIso(etEndLocal.trim()) : '';
    const plannedStartMs = Date.parse(plannedStartIso);
    if (Number.isNaN(plannedStartMs)) {
      setEtConflictEvents([]);
      return;
    }
    const plannedEndMs = (() => {
      if (plannedEndIso) {
        const ms = Date.parse(plannedEndIso);
        if (!Number.isNaN(ms)) return ms;
      }
      return plannedStartMs + 90 * 60 * 1000;
    })();
    const aStart = plannedStartMs;
    const aEnd = Math.max(plannedEndMs, plannedStartMs);
    const overlaps = (bStart: number, bEnd: number) => aStart < bEnd && bStart < aEnd;

    const keyA = dateKeyFromIso(plannedStartIso);
    const keyB = plannedEndIso ? dateKeyFromIso(plannedEndIso) : keyA;
    const candidates = events
      .filter((e) => e.status === 'ACTIVE')
      .filter((e) => e.id !== details.id)
      .filter((e) => {
        const k = dateKeyFromIso(e.start_at);
        return k === keyA || k === keyB;
      })
      .slice(0, 14);

    let alive = true;
    Promise.all(
      candidates.map(async (e) => {
        try {
          return await apiGetEvent(props.token, e.id, null);
        } catch {
          return null;
        }
      })
    ).then((rows) => {
      if (!alive) return;
      const found = rows
        .filter((d): d is EventDetails => !!d)
        .filter((d) => d.status === 'ACTIVE')
        .filter((d) => {
          const bStart = Date.parse(d.start_at);
          if (Number.isNaN(bStart)) return false;
          const bEnd = (() => {
            const raw = (d.end_at ?? '').trim();
            if (raw) {
              const ms = Date.parse(raw);
              if (!Number.isNaN(ms)) return ms;
            }
            if (d.type === 'MATCH') return bStart + 120 * 60 * 1000;
            if (d.type === 'TRAINING') return bStart + 90 * 60 * 1000;
            return bStart + 60 * 60 * 1000;
          })();
          return overlaps(bStart, Math.max(bEnd, bStart));
        })
        .slice()
        .sort((a, b) => a.start_at.localeCompare(b.start_at));
      setEtConflictEvents(found);
    });
    return () => {
      alive = false;
    };
  }, [details, etEndLocal, etStartLocal, events, isEditTrainingOpen, props.token]);

  const openEditTraining = () => {
    if (props.role !== 'COACH' && props.role !== 'ADMIN') return;
    if (!details || details.type !== 'TRAINING') return;
    setCreateTargetChildId(null);
    const start = new Date(details.start_at);
    const end = details.end_at ? new Date(details.end_at) : null;
    setEtTitle(details.title || 'Trening');
    setEtStartLocal(toLocalInputValue(start));
    setEtEndLocal(end ? toLocalInputValue(end) : '');
    setEtLocation(details.location || '');
    setEtDescription(details.description || '');
    setEtApplyToSeries(false);
    setIsEditTrainingOpen(true);
  };

  const onCreateMatchSubmit = async () => {
    if (props.role !== 'COACH' && props.role !== 'ADMIN') return;
    const gid = cmGroupId.trim();
    const title = cmTitle.trim();
    const opponentRaw = cmOpponent.trim();
    const startIso = localToIso(cmStartLocal.trim());
    const endIso = cmEndLocal.trim() ? localToIso(cmEndLocal.trim()) : '';
    const deadlineIso = cmDeadlineLocal.trim() ? localToIso(cmDeadlineLocal.trim()) : '';
    const meetTime = cmMeetTime.trim();
    const meetLocal = meetTime ? joinLocalDateTime(cmStartParts.date || selectedDayKey, meetTime) : '';
    const meetIso = meetLocal ? localToIso(meetLocal) : '';
    const loc = cmLocation.trim();
    const desc = cmDescription.trim();
    const formula = cmFormula ? cmFormula : '';
    const minPlayersOther = cmMinPlayersOtherInt;
    const hasValidMinPlayersOther = cmHasValidMinPlayersOther;
    if (!gid || !title || !startIso || !loc) return;
    if (formula === 'INNA' && !hasValidMinPlayersOther) {
      setErrorText('Podaj ilu zawodników potrzebujesz (1–30).');
      return;
    }

    const normalize = (v: string) => v.trim().toLowerCase().replace(/\s+/g, ' ');
    const exactTeam = opponentRaw
      ? opponentTeams.find((t) => normalize(t.name) === normalize(opponentRaw)) ?? null
      : null;
    const effectiveOpponentTeamId = exactTeam?.id ?? cmOpponentTeamId;
    const effectiveOpponentName = opponentRaw ? opponentRaw.replace(/\s+/g, ' ').trim() : '';

    const metaLines: string[] = [];
    if (formula) metaLines.push(`[[FORMAT=${formula}]]`);
    if (formula === 'INNA' && hasValidMinPlayersOther) metaLines.push(`[[MINPLAYERS=${minPlayersOther}]]`);
    const metaPrefix = metaLines.join('\n').trim();
    const combinedDesc = `${metaPrefix}${metaPrefix && desc ? '\n' : ''}${desc}`.trim();

    const matchBody = {
      group_id: gid,
      target_child_id: createTargetChildId ? createTargetChildId : null,
      match_kind: cmMatchKind,
      title,
      opponent_team_id: effectiveOpponentTeamId ? effectiveOpponentTeamId : null,
      opponent_name: effectiveOpponentName ? effectiveOpponentName : null,
      is_tbd: cmIsTbd,
      start_at: startIso,
      end_at: endIso ? endIso : null,
      match_meet_at: meetIso ? meetIso : null,
      location: loc,
      description: combinedDesc ? combinedDesc : null,
      match_deadline_at: deadlineIso ? deadlineIso : null,
    };

    const matchUpdateBody = {
      title,
      is_tbd: cmIsTbd,
      start_at: startIso,
      end_at: endIso ? endIso : null,
      match_meet_at: meetIso ? meetIso : null,
      location: loc,
      description: combinedDesc ? combinedDesc : null,
      match_deadline_at: cmNeedsConfirmation ? (deadlineIso ? deadlineIso : null) : null,
      opponent_team_id: effectiveOpponentName ? (effectiveOpponentTeamId ? effectiveOpponentTeamId : null) : null,
      opponent_name: effectiveOpponentName ? effectiveOpponentName : null,
      match_kind: cmMatchKind,
    };

    if (effectiveOpponentName && !effectiveOpponentTeamId && !exactTeam) {
      setPendingOpponentName(effectiveOpponentName);
      if (isEditingMatch && editMatchEventId) {
        setPendingMatchUpdateEventId(editMatchEventId);
        setPendingMatchUpdateBody(matchUpdateBody);
      } else {
        setPendingMatchBody(matchBody);
      }
      setIsAddOpponentConfirmOpen(true);
      return;
    }

    setIsLoading(true);
    setErrorText(null);
    try {
      if (isEditingMatch && editMatchEventId) {
        const next = await apiCoachUpdateEvent(props.token, editMatchEventId, matchUpdateBody);
        saveLastLocation(loc);
        setDetails(next);
        setIsCreateMatchOpen(false);
        setEditMatchEventId(null);
        setSelectedDayKey(dateKeyFromIso(next.start_at));
        await loadList();
      } else {
        const created = await apiCoachCreateMatch(props.token, matchBody);
        saveLastLocation(loc);
        setIsCreateMatchOpen(false);
        setSelectedDayKey(dateKeyFromIso(created.start_at));
        await loadList();
        openEvent(created.id);
      }
    } catch (e) {
      setErrorText(coachActionError(isEditingMatch ? 'Nie udało się zapisać meczu.' : 'Nie udało się utworzyć meczu.', e));
    } finally {
      setIsLoading(false);
    }
  };

  const showMatchMissing = React.useCallback(() => {
    setCmShowMissing(true);
    const missing: string[] = [];
    if (!cmGroupId.trim()) missing.push('Grupa');
    if (!cmTitle.trim()) missing.push('Tytuł');
    if (!cmStartLocal.trim()) missing.push('Data i godzina');
    if (!cmLocation.trim()) missing.push('Miejsce');
    if (cmFormula === 'INNA' && !cmHasValidMinPlayersOther) missing.push('Liczba zawodników (1–30)');
    setErrorText(missing.length ? `Uzupełnij: ${missing.join(' • ')}` : 'Uzupełnij wymagane pola.');
    window.setTimeout(() => {
      if (!cmGroupId.trim()) {
        cmGroupRef.current?.focus();
        return;
      }
      if (!cmTitle.trim()) {
        cmTitleRef.current?.focus();
        return;
      }
      if (!cmStartLocal.trim()) {
        (cmDateRef.current ?? cmTimeRef.current)?.focus();
        return;
      }
      if (!cmLocation.trim()) {
        cmLocationRef.current?.focus();
        return;
      }
      if (cmFormula === 'INNA' && !cmHasValidMinPlayersOther) {
        cmMinPlayersRef.current?.focus();
      }
    }, 0);
  }, [cmFormula, cmGroupId, cmHasValidMinPlayersOther, cmLocation, cmStartLocal, cmTitle]);

  const onConfirmAddOpponentYes = async () => {
    if (props.role !== 'COACH' && props.role !== 'ADMIN') return;
    const name = pendingOpponentName.trim();
    if (!name) return;
    setIsLoading(true);
    setErrorText(null);
    try {
      const createdTeam = await apiCoachCreateOpponentTeam(props.token, { name });
      if (pendingMatchBody) {
        const nextBody = { ...pendingMatchBody, opponent_team_id: createdTeam.id, opponent_name: createdTeam.name };
        const created = await apiCoachCreateMatch(props.token, nextBody);
        saveLastLocation(nextBody.location);
        setIsAddOpponentConfirmOpen(false);
        setPendingMatchBody(null);
        setPendingOpponentName('');
        setIsCreateMatchOpen(false);
        setSelectedDayKey(dateKeyFromIso(created.start_at));
        await loadList();
        openEvent(created.id);
      } else if (pendingMatchUpdateEventId && pendingMatchUpdateBody) {
        const next = await apiCoachUpdateEvent(props.token, pendingMatchUpdateEventId, {
          ...pendingMatchUpdateBody,
          opponent_team_id: createdTeam.id,
          opponent_name: createdTeam.name,
        });
        saveLastLocation(pendingMatchUpdateBody.location);
        setDetails(next);
        setIsAddOpponentConfirmOpen(false);
        setPendingMatchUpdateEventId(null);
        setPendingMatchUpdateBody(null);
        setPendingOpponentName('');
        setIsCreateMatchOpen(false);
        setEditMatchEventId(null);
        setSelectedDayKey(dateKeyFromIso(next.start_at));
        await loadList();
      } else {
        return;
      }
      apiListOpponentTeams(props.token)
        .then((list) => setOpponentTeams(list))
        .catch(() => {});
    } catch (e) {
      setErrorText(coachActionError(pendingMatchBody ? 'Nie udało się dodać drużyny i utworzyć meczu.' : 'Nie udało się dodać drużyny i zapisać meczu.', e));
    } finally {
      setIsLoading(false);
    }
  };

  const onConfirmAddOpponentNo = async () => {
    if (props.role !== 'COACH' && props.role !== 'ADMIN') return;
    setIsLoading(true);
    setErrorText(null);
    try {
      if (pendingMatchBody) {
        const created = await apiCoachCreateMatch(props.token, pendingMatchBody);
        saveLastLocation(pendingMatchBody.location);
        setIsAddOpponentConfirmOpen(false);
        setPendingMatchBody(null);
        setPendingOpponentName('');
        setIsCreateMatchOpen(false);
        setSelectedDayKey(dateKeyFromIso(created.start_at));
        await loadList();
        openEvent(created.id);
      } else if (pendingMatchUpdateEventId && pendingMatchUpdateBody) {
        const next = await apiCoachUpdateEvent(props.token, pendingMatchUpdateEventId, pendingMatchUpdateBody);
        saveLastLocation(pendingMatchUpdateBody.location);
        setDetails(next);
        setIsAddOpponentConfirmOpen(false);
        setPendingMatchUpdateEventId(null);
        setPendingMatchUpdateBody(null);
        setPendingOpponentName('');
        setIsCreateMatchOpen(false);
        setEditMatchEventId(null);
        setSelectedDayKey(dateKeyFromIso(next.start_at));
        await loadList();
      } else {
        return;
      }
    } catch (e) {
      setErrorText(coachActionError(pendingMatchBody ? 'Nie udało się utworzyć meczu.' : 'Nie udało się zapisać meczu.', e));
    } finally {
      setIsLoading(false);
    }
  };

  const onEditTrainingSubmit = async () => {
    if (props.role !== 'COACH' && props.role !== 'ADMIN') return;
    if (!details || details.type !== 'TRAINING') return;
    const title = etTitle.trim();
    const startIso = localToIso(etStartLocal.trim());
    const endIso = etEndLocal.trim() ? localToIso(etEndLocal.trim()) : '';
    const loc = etLocation.trim();
    const desc = etDescription.trim();
    if (!title || !startIso || !loc) return;
    setIsLoading(true);
    setErrorText(null);
    try {
      const next = await apiCoachUpdateEvent(props.token, details.id, {
        title,
        start_at: startIso,
        end_at: endIso ? endIso : null,
        location: loc,
        description: desc ? desc : null,
        apply_to_series: details.series_id ? etApplyToSeries : false,
      });
      saveLastLocation(loc);
      setDetails(next);
      setIsEditTrainingOpen(false);
      setSelectedDayKey(dateKeyFromIso(next.start_at));
      await loadList();
    } catch (e) {
      setErrorText('Nie udało się zapisać zmian treningu.');
    } finally {
      setIsLoading(false);
    }
  };

  const onCancelTraining = async () => {
    if (props.role !== 'COACH' && props.role !== 'ADMIN') return;
    if (!details || details.type !== 'TRAINING') return;
    setIsLoading(true);
    setErrorText(null);
    try {
      const next = await apiCoachCancelEvent(props.token, details.id);
      setDetails(next);
      setIsCancelTrainingConfirmOpen(false);
      await loadList();
    } catch (e) {
      setErrorText('Nie udało się odwołać treningu.');
    } finally {
      setIsLoading(false);
    }
  };

  const onCancelMatch = async () => {
    if (props.role !== 'COACH' && props.role !== 'ADMIN') return;
    if (!details || details.type !== 'MATCH') return;
    setIsLoading(true);
    setErrorText(null);
    try {
      const next = await apiCoachCancelEvent(props.token, details.id);
      setDetails(next);
      setIsCancelMatchConfirmOpen(false);
      await loadList();
    } catch (e) {
      setErrorText('Nie udało się odwołać meczu.');
    } finally {
      setIsLoading(false);
    }
  };

  const onDeleteEvent = async () => {
    if (props.role !== 'COACH' && props.role !== 'ADMIN') return;
    if (!details || (details.type !== 'TRAINING' && details.type !== 'MATCH')) return;
    setIsLoading(true);
    setErrorText(null);
    try {
      await apiCoachDeleteEvent(props.token, details.id);
      setIsDeleteEventConfirmOpen(false);
      setCallupEventId(null);
      setDetails(null);
      setSelectedId(null);
      navigate('kalendarz', { e: null });
      await loadList();
    } catch (e) {
      setErrorText(details.type === 'MATCH' ? 'Nie udało się usunąć meczu.' : 'Nie udało się usunąć treningu.');
    } finally {
      setIsLoading(false);
    }
  };

  const onSetMyCoachAbsence = async (isAbsent: boolean) => {
    if (props.role !== 'COACH') return;
    if (!details || (details.type !== 'TRAINING' && details.type !== 'MATCH')) return;
    if (!details.can_take_action) return;
    if (!details.can_mark_coach_absence) return;
    setIsLoading(true);
    setErrorText(null);
    try {
      const next = await apiCoachSetMyEventAbsence(props.token, details.id, isAbsent);
      setDetails(next);
    } catch (e) {
      setErrorText('Nie udało się zapisać nieobecności trenera.');
    } finally {
      setIsLoading(false);
    }
  };

  const onParentPrivateTrainingSubmit = async () => {
    if (props.role !== 'PARENT') return;
    if (!safeChildId) return;
    const title = pptTitle.trim();
    const startIso = localToIso(pptStartLocal.trim());
    const endIso = pptEndLocal.trim() ? localToIso(pptEndLocal.trim()) : '';
    const loc = pptLocation.trim();
    const desc = pptDescription.trim();
    if (!title || !startIso || !loc) return;
    setIsLoading(true);
    setErrorText(null);
    try {
      const created = await apiParentCreatePrivateTraining(props.token, {
        child_id: safeChildId,
        title,
        start_at: startIso,
        end_at: endIso ? endIso : null,
        location: loc,
        description: desc ? desc : null,
      });
      saveLastLocation(loc);
      setIsParentPrivateTrainingOpen(false);
      setSelectedDayKey(dateKeyFromIso(created.start_at));
      await loadList();
      openEvent(created.id);
    } catch (e) {
      setErrorText('Nie udało się dodać treningu prywatnego.');
    } finally {
      setIsLoading(false);
    }
  };

  const onCreateTrainingSubmit = async () => {
    if (props.role !== 'COACH' && props.role !== 'ADMIN') return;
    const gid = ctGroupId.trim();
    const title = ctTitle.trim();
    const startIso = localToIso(ctStartLocal.trim());
    const endIso = ctEndLocal.trim() ? localToIso(ctEndLocal.trim()) : '';
    const loc = ctLocation.trim();
    const desc = ctDescription.trim();
    if (!gid || !title || !startIso || !loc) return;
    if (ctRecurringInvalid) return;
    setIsLoading(true);
    setErrorText(null);
    try {
      const isIndividual = !!(createTargetChildId ?? '').trim();
      const shouldCreateSeries = !isIndividual && ctRecurring && ctOccurrences > 1;
      const created = await apiCoachCreateTraining(props.token, {
        group_id: gid,
        target_child_id: isIndividual ? createTargetChildId : null,
        title,
        start_at: startIso,
        end_at: endIso ? endIso : null,
        location: loc,
        description: desc ? desc : null,
        is_recurring: shouldCreateSeries,
        interval_weeks: shouldCreateSeries ? ctIntervalWeeksInt : undefined,
        occurrences: shouldCreateSeries ? ctOccurrences : undefined,
      });
      saveLastLocation(loc);
      setIsCreateTrainingOpen(false);
      setSelectedDayKey(dateKeyFromIso(created.start_at));
      await loadList();
      openEvent(created.id);
    } catch (e) {
      let extra = '';
      try {
        const h = await apiHealth(props.token);
        extra = ` (Backend: ${h.build ? h.build : 'legacy-no-build'})`;
      } catch {
        extra = ' (Backend: unreachable)';
      }
      setErrorText(`${coachTrainingActionError('Nie udało się utworzyć treningu.', e)}${extra}`);
    } finally {
      setIsLoading(false);
    }
  };

  const openCallups = async () => {
    if (props.role !== 'COACH' && props.role !== 'ADMIN') return;
    if (!details || details.type !== 'MATCH') return;
    if (details.is_individual) {
      setErrorText('To jest wydarzenie indywidualne — powołania są wyłączone.');
      return;
    }
    openCallupsForEvent(details.id);
  };

  const openCallupsForEvent = React.useCallback(
    async (eventId: string) => {
      if (props.role !== 'COACH' && props.role !== 'ADMIN') return;
      const id = eventId.trim();
      if (!id) return;
      setIsLoading(true);
      setErrorText(null);
      try {
        const d = await apiGetEvent(props.token, id, null);
        if (d.type !== 'MATCH') {
          setErrorText('To wydarzenie nie jest meczem.');
          return;
        }
        setDetails(d);
        const roster = await apiGroupChildren(props.token, d.group_id);
        setCallupRoster(roster);
        setCallupRosterGroupId(d.group_id);
        const eventTs = (() => {
          const dt = new Date(d.start_at);
          return Number.isNaN(dt.getTime()) ? Date.now() : dt.getTime();
        })();
        const isChildUnavailable = (c: ChildItem) => {
          const kind = (c.unavailability_kind ?? '').trim();
          if (!kind) return false;
          const fromRaw = (c.unavailability_from ?? '').trim();
          const untilRaw = (c.unavailability_until ?? '').trim();
          const fromKey = fromRaw ? fromRaw.slice(0, 10) : '';
          const untilKey = untilRaw ? untilRaw.slice(0, 10) : '';
          const fromMs = fromKey ? Date.parse(`${fromKey}T00:00:00Z`) : Number.NEGATIVE_INFINITY;
          const untilMs = untilKey ? Date.parse(`${untilKey}T23:59:59.999Z`) : Number.POSITIVE_INFINITY;
          if (Number.isNaN(fromMs) || Number.isNaN(untilMs)) return true;
          return eventTs >= fromMs && eventTs <= untilMs;
        };
        const ids = new Set(d.called_up_child_ids ?? []);
        const next: Record<string, boolean> = {};
        roster.forEach((c) => {
          next[c.id] = ids.has(c.id) && !isChildUnavailable(c);
        });
        setCallupSelected(next);
        setCallupEventId(d.id);
        setCallupFilter('ALL');
      } catch (e) {
        setErrorText('Nie udało się pobrać listy zawodników.');
      } finally {
        setIsLoading(false);
      }
    },
    [props.role, props.token]
  );

  const weekCoachMatches = React.useMemo(() => {
    if (props.role !== 'COACH' && props.role !== 'ADMIN') return [] as EventListItem[];
    const base = parseDateKey(selectedDayKey);
    const dow = base.getDay();
    const diff = (dow + 6) % 7;
    const start = new Date(base.getFullYear(), base.getMonth(), base.getDate() - diff, 0, 0, 0);
    const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 7, 0, 0, 0);
    const startIso = start.toISOString();
    const endIso = end.toISOString();
    const groupIds = new Set((props.activeGroupId ? [props.activeGroupId] : coachGroups.map((g) => g.id)).filter(Boolean));
    return events
      .filter((e) => e.type === 'MATCH' && e.start_at >= startIso && e.start_at < endIso && (groupIds.size === 0 || groupIds.has(e.group_id)))
      .slice()
      .sort((a, b) => a.start_at.localeCompare(b.start_at));
  }, [coachGroups, events, props.activeGroupId, props.role, selectedDayKey]);

  React.useEffect(() => {
    if (props.role !== 'COACH' && props.role !== 'ADMIN') return;
    const ids = weekCoachMatches.map((e) => e.id);
    const missing = ids.filter((id) => !Object.prototype.hasOwnProperty.call(matchNeedById, id));
    if (missing.length === 0) return;
    let alive = true;
    Promise.all(
      missing.map(async (id) => {
        try {
          const d = await apiGetEvent(props.token, id, null);
          if (d.type !== 'MATCH') return { id, need: null as null };
          const meta = parseMatchMeta(d.description);
          if (!meta.minPlayers) return { id, need: null as null };
          const confirmed = Object.values(d.callup_rsvps ?? {}).filter((s) => s === 'ACCEPTED').length;
          return {
            id,
            need: { formula: meta.formula ?? 'MECZ', minPlayers: meta.minPlayers, confirmed, isEnough: confirmed >= meta.minPlayers },
          };
        } catch {
          return { id, need: null as null };
        }
      })
    ).then((rows) => {
      if (!alive) return;
      setMatchNeedById((prev) => {
        const next = { ...prev };
        rows.forEach((r) => {
          next[r.id] = r.need ? r.need : null;
        });
        return next;
      });
    });
    return () => {
      alive = false;
    };
  }, [matchNeedById, parseMatchMeta, props.role, props.token, weekCoachMatches]);

  const weekCoachTrainings = React.useMemo(() => {
    if (props.role !== 'COACH' && props.role !== 'ADMIN') return [] as EventListItem[];
    const base = parseDateKey(selectedDayKey);
    const dow = base.getDay();
    const diff = (dow + 6) % 7;
    const start = new Date(base.getFullYear(), base.getMonth(), base.getDate() - diff, 0, 0, 0);
    const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 7, 0, 0, 0);
    const startIso = start.toISOString();
    const endIso = end.toISOString();
    const groupIds = new Set((props.activeGroupId ? [props.activeGroupId] : coachGroups.map((g) => g.id)).filter(Boolean));
    return events
      .filter((e) => e.type === 'TRAINING' && e.start_at >= startIso && e.start_at < endIso && (groupIds.size === 0 || groupIds.has(e.group_id)))
      .slice()
      .sort((a, b) => a.start_at.localeCompare(b.start_at));
  }, [coachGroups, events, props.activeGroupId, props.role, selectedDayKey]);

  const onSaveCallups = async () => {
    if (props.role !== 'COACH' && props.role !== 'ADMIN') return;
    if (!callupEventId) return;
    const eventTs = (() => {
      const dt = details?.start_at ? new Date(details.start_at) : null;
      return dt && !Number.isNaN(dt.getTime()) ? dt.getTime() : Date.now();
    })();
    const isChildUnavailable = (c: ChildItem) => {
      const kind = (c.unavailability_kind ?? '').trim();
      if (!kind) return false;
      const fromRaw = (c.unavailability_from ?? '').trim();
      const untilRaw = (c.unavailability_until ?? '').trim();
      const fromKey = fromRaw ? fromRaw.slice(0, 10) : '';
      const untilKey = untilRaw ? untilRaw.slice(0, 10) : '';
      const fromMs = fromKey ? Date.parse(`${fromKey}T00:00:00Z`) : Number.NEGATIVE_INFINITY;
      const untilMs = untilKey ? Date.parse(`${untilKey}T23:59:59.999Z`) : Number.POSITIVE_INFINITY;
      if (Number.isNaN(fromMs) || Number.isNaN(untilMs)) return true;
      return eventTs >= fromMs && eventTs <= untilMs;
    };
    const unavailable = new Set(callupRoster.filter(isChildUnavailable).map((c) => c.id));
    const childIds = Object.keys(callupSelected).filter((k) => callupSelected[k] && !unavailable.has(k));
    setIsLoading(true);
    setErrorText(null);
    try {
      const next = await apiCoachSetMatchCallups(props.token, callupEventId, childIds);
      setDetails(next);
      setCallupEventId(null);
      await loadList();
    } catch (e) {
      setErrorText('Nie udało się zapisać powołań.');
    } finally {
      setIsLoading(false);
    }
  };

  const onCallupSelectAll = () => {
    const eventTs = (() => {
      const dt = details?.start_at ? new Date(details.start_at) : null;
      return dt && !Number.isNaN(dt.getTime()) ? dt.getTime() : Date.now();
    })();
    let selectedCount = 0;
    let skippedUnavailable = 0;
    let skippedDeclined = 0;
    callupRoster.forEach((c) => {
      const kind = (c.unavailability_kind ?? '').trim();
      const isUnavailable = (() => {
        if (!kind) return false;
        const fromRaw = (c.unavailability_from ?? '').trim();
        const untilRaw = (c.unavailability_until ?? '').trim();
        const fromKey = fromRaw ? fromRaw.slice(0, 10) : '';
        const untilKey = untilRaw ? untilRaw.slice(0, 10) : '';
        const fromMs = fromKey ? Date.parse(`${fromKey}T00:00:00Z`) : Number.NEGATIVE_INFINITY;
        const untilMs = untilKey ? Date.parse(`${untilKey}T23:59:59.999Z`) : Number.POSITIVE_INFINITY;
        if (Number.isNaN(fromMs) || Number.isNaN(untilMs)) return true;
        return eventTs >= fromMs && eventTs <= untilMs;
      })();
      if (isUnavailable) {
        skippedUnavailable += 1;
        return;
      }
      const s = details?.callup_rsvps?.[c.id] ?? 'PENDING';
      if (s === 'DECLINED') {
        skippedDeclined += 1;
        return;
      }
      selectedCount += 1;
    });
    setCallupSelected((prev) => {
      const next = { ...prev };
      callupRoster.forEach((c) => {
        const unavailableKind = (c.unavailability_kind ?? '').trim();
        if (unavailableKind) {
          const fromRaw = (c.unavailability_from ?? '').trim();
          const untilRaw = (c.unavailability_until ?? '').trim();
          const fromKey = fromRaw ? fromRaw.slice(0, 10) : '';
          const untilKey = untilRaw ? untilRaw.slice(0, 10) : '';
          const fromMs = fromKey ? Date.parse(`${fromKey}T00:00:00Z`) : Number.NEGATIVE_INFINITY;
          const untilMs = untilKey ? Date.parse(`${untilKey}T23:59:59.999Z`) : Number.POSITIVE_INFINITY;
          if (Number.isNaN(fromMs) || Number.isNaN(untilMs)) return;
          if (eventTs >= fromMs && eventTs <= untilMs) return;
        }
        const s = details?.callup_rsvps?.[c.id] ?? 'PENDING';
        if (s === 'DECLINED') return;
        next[c.id] = true;
      });
      return next;
    });
    setCallupFilter('ALL');
    if (callupInfoTimeoutRef.current) window.clearTimeout(callupInfoTimeoutRef.current);
    const parts = [`Zaznaczono ${selectedCount} zawodników.`];
    if (skippedUnavailable > 0) parts.push(`Pominięto ${skippedUnavailable} nieobecnych.`);
    if (skippedDeclined > 0) parts.push(`Pominięto ${skippedDeclined} odmów.`);
    setCallupInfoText(parts.join(' '));
    callupInfoTimeoutRef.current = window.setTimeout(() => setCallupInfoText(null), 2800);
  };

  const callupStats = React.useMemo(() => {
    const calledUp = callupRoster.filter((c) => !!callupSelected[c.id]);
    const accepted = calledUp.filter((c) => (details?.callup_rsvps?.[c.id] ?? 'PENDING') === 'ACCEPTED');
    const declined = calledUp.filter((c) => (details?.callup_rsvps?.[c.id] ?? 'PENDING') === 'DECLINED');
    const pending = calledUp.filter((c) => (details?.callup_rsvps?.[c.id] ?? 'PENDING') === 'PENDING');
    return {
      all: callupRoster.length,
      called: calledUp.length,
      accepted: accepted.length,
      declined: declined.length,
      pending: pending.length,
    };
  }, [callupRoster, callupSelected, details?.callup_rsvps]);

  const filteredCallupRoster = React.useMemo(() => {
    return callupRoster
      .filter((c) => {
        const isCalledUp = !!callupSelected[c.id];
        const s = details?.callup_rsvps?.[c.id] ?? 'PENDING';
        if (callupFilter === 'ALL') return true;
        if (callupFilter === 'CALLED_UP') return isCalledUp;
        return isCalledUp && s === callupFilter;
      })
      .slice()
      .sort((a, b) => {
        const al = String(a.last_initial ?? '')
          .replace('.', '')
          .trim()
          .toLowerCase();
        const bl = String(b.last_initial ?? '')
          .replace('.', '')
          .trim()
          .toLowerCase();
        const af = String(a.first_name ?? '').trim().toLowerCase();
        const bf = String(b.first_name ?? '').trim().toLowerCase();
        const byLast = al.localeCompare(bl, 'pl', { sensitivity: 'base' });
        if (byLast !== 0) return byLast;
        const byFirst = af.localeCompare(bf, 'pl', { sensitivity: 'base' });
        if (byFirst !== 0) return byFirst;
        return String(a.id ?? '').localeCompare(String(b.id ?? ''));
      });
  }, [callupFilter, callupRoster, callupSelected, details?.callup_rsvps]);

  const markers = React.useMemo<Record<string, DayMarkers>>(() => {
    const next: Record<string, DayMarkers> = {};
    events.forEach((e) => {
      const key = dateKeyFromIso(e.start_at);
      const prev = next[key] ?? { hasTraining: false, hasMatch: false, hasIndividualTraining: false, hasIndividualMatch: false };
      const isIndividual = !!e.is_individual;
      next[key] = {
        hasTraining: prev.hasTraining || (e.type === 'TRAINING' && !isIndividual),
        hasMatch: prev.hasMatch || (e.type === 'MATCH' && !isIndividual),
        hasIndividualTraining: prev.hasIndividualTraining || (e.type === 'TRAINING' && isIndividual),
        hasIndividualMatch: prev.hasIndividualMatch || (e.type === 'MATCH' && isIndividual),
      };
    });
    return next;
  }, [events]);

  React.useEffect(() => {
    const d = parseDateKey(selectedDayKey);
    const next = new Date(d.getFullYear(), d.getMonth(), 1);
    if (next.getFullYear() !== monthCursor.getFullYear() || next.getMonth() !== monthCursor.getMonth()) {
      setMonthCursor(next);
    }
  }, [monthCursor, selectedDayKey]);

  const monthDate = monthCursor;

  const shiftMonth = (delta: number) => {
    const next = new Date(monthCursor.getFullYear(), monthCursor.getMonth() + delta, 1);
    setMonthCursor(next);
    const nextDayKey = `${next.getFullYear()}-${pad2(next.getMonth() + 1)}-01`;
    setSelectedDayKey(nextDayKey);
  };

  const shiftDay = (delta: number) => {
    const d = parseDateKey(selectedDayKey);
    const next = new Date(d.getFullYear(), d.getMonth(), d.getDate() + delta, 0, 0, 0);
    const nextDayKey = `${next.getFullYear()}-${pad2(next.getMonth() + 1)}-${pad2(next.getDate())}`;
    setSelectedDayKey(nextDayKey);
    setMonthCursor(new Date(next.getFullYear(), next.getMonth(), 1));
  };

  const eventsForSelectedDay = React.useMemo(() => {
    return events
      .filter((e) => dateKeyFromIso(e.start_at) === selectedDayKey)
      .slice()
      .sort((a, b) => a.start_at.localeCompare(b.start_at));
  }, [events, selectedDayKey]);

  const weekParentEvents = React.useMemo(() => {
    if (props.role !== 'PARENT') return [] as EventListItem[];
    const base = parseDateKey(selectedDayKey);
    const dow = base.getDay();
    const diff = (dow + 6) % 7;
    const start = new Date(base.getFullYear(), base.getMonth(), base.getDate() - diff, 0, 0, 0);
    const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 7, 0, 0, 0);
    const startIso = start.toISOString();
    const endIso = end.toISOString();
    return events
      .filter((e) => e.start_at >= startIso && e.start_at < endIso)
      .slice()
      .sort((a, b) => a.start_at.localeCompare(b.start_at));
  }, [events, props.role, selectedDayKey]);

  const otherWeekParentMatches = React.useMemo(() => {
    if (props.role !== 'PARENT') return [] as EventListItem[];
    const ids = new Set(eventsForSelectedDay.map((e) => e.id));
    return weekParentEvents.filter((e) => e.type === 'MATCH' && !ids.has(e.id));
  }, [eventsForSelectedDay, props.role, weekParentEvents]);

  const otherWeekParentTrainings = React.useMemo(() => {
    if (props.role !== 'PARENT') return [] as EventListItem[];
    const ids = new Set(eventsForSelectedDay.map((e) => e.id));
    return weekParentEvents.filter((e) => e.type === 'TRAINING' && !ids.has(e.id));
  }, [eventsForSelectedDay, props.role, weekParentEvents]);

  const selectedDayCoachEvents = React.useMemo(() => {
    if (props.role !== 'COACH' && props.role !== 'ADMIN') return [] as EventListItem[];
    const groupIds = new Set((props.activeGroupId ? [props.activeGroupId] : coachGroups.map((g) => g.id)).filter(Boolean));
    return events
      .filter((e) => dateKeyFromIso(e.start_at) === selectedDayKey)
      .filter((e) => (groupIds.size === 0 ? true : groupIds.has(e.group_id)))
      .slice()
      .sort((a, b) => a.start_at.localeCompare(b.start_at));
  }, [coachGroups, events, props.activeGroupId, props.role, selectedDayKey]);

  const selectedDayEventsByRole = props.role === 'COACH' || props.role === 'ADMIN' ? selectedDayCoachEvents : eventsForSelectedDay;

  const otherWeekCoachMatches = React.useMemo(() => {
    if (props.role !== 'COACH' && props.role !== 'ADMIN') return [] as EventListItem[];
    const ids = new Set(selectedDayCoachEvents.map((e) => e.id));
    return weekCoachMatches.filter((e) => !ids.has(e.id));
  }, [props.role, selectedDayCoachEvents, weekCoachMatches]);

  const otherWeekCoachTrainings = React.useMemo(() => {
    if (props.role !== 'COACH' && props.role !== 'ADMIN') return [] as EventListItem[];
    const ids = new Set(selectedDayCoachEvents.map((e) => e.id));
    return weekCoachTrainings.filter((e) => !ids.has(e.id));
  }, [props.role, selectedDayCoachEvents, weekCoachTrainings]);

  const onOptOut = async () => {
    if (!details) return;
    if (isChildScoped && !safeChildId) {
      setErrorText('Wybierz dziecko, aby wykonać tę akcję.');
      return;
    }
    setIsLoading(true);
    setErrorText(null);
    try {
      const next = await apiOptOut(props.token, details.id, safeChildId);
      setDetails(next);
      await loadList();
    } catch (e) {
      setErrorText(humanizeHttpError(e, 'Nie udało się zgłosić nieobecności.'));
    } finally {
      setIsLoading(false);
    }
  };

  const openLongAbsenceForm = async () => {
    if (!details || details.type !== 'TRAINING') return;
    if (props.role !== 'PARENT') return;
    if (!safeChildId) {
      setErrorText('Wybierz dziecko, aby wykonać tę akcję.');
      return;
    }
    const eventDay = dateKeyFromIso(details.start_at);
    setLongAbsReason('CHOROBA');
    setLongAbsFrom(eventDay);
    setLongAbsUntil(eventDay);
    setLongAbsNote('');
    setLongAbsChild(null);
    setIsLoading(true);
    try {
      const list = await apiMyChildren(props.token);
      const child = list.find((c) => c.id === safeChildId) ?? null;
      setLongAbsChild(child);
      if (child) {
        const kind = (child.unavailability_kind ?? '').trim();
        const existingReason = (child.unavailability_reason ?? '').trim();
        const inferred =
          existingReason === 'CHOROBA' || existingReason === 'KONTUZJA' || existingReason === 'INNE'
            ? existingReason
            : kind === 'KONTUZJA'
              ? 'KONTUZJA'
              : 'INNE';
        setLongAbsReason(inferred);
        const f = (child.unavailability_from ?? '').trim();
        const u = (child.unavailability_until ?? '').trim();
        setLongAbsFrom((f ? f.slice(0, 10) : eventDay) || eventDay);
        setLongAbsUntil((u ? u.slice(0, 10) : eventDay) || eventDay);
        setLongAbsNote((child.unavailability_note ?? '').trim());
      }
      setTrainingAbsenceSheet('LONG');
    } catch {
      setLongAbsChild(null);
      setTrainingAbsenceSheet('LONG');
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmitLongAbsence = async () => {
    if (!details || details.type !== 'TRAINING') return;
    if (props.role !== 'PARENT') return;
    if (!safeChildId) {
      setErrorText('Wybierz dziecko, aby wykonać tę akcję.');
      return;
    }
    const from = longAbsFrom.trim();
    const until = longAbsUntil.trim();
    if (!from || !until) return;
    if (until < from) return;
    const note = longAbsNote.trim();
    if (note.length > 240) return;
    setIsLoading(true);
    setErrorText(null);
    try {
      const updated = await apiSetChildUnavailability(props.token, safeChildId, {
        reason: longAbsReason,
        from_date: from,
        until_date: until,
        note: note ? note : undefined,
      });
      setLongAbsChild(updated);
      try {
        if (details.attendance_status !== 'OUT') {
          const next = await apiOptOut(props.token, details.id, safeChildId);
          setDetails(next);
        }
      } catch {
      }
      setTrainingAbsenceSheet('NONE');
      setParentActionInfoText('Ustawiono długą nieobecność.');
      if (parentActionInfoTimeoutRef.current) window.clearTimeout(parentActionInfoTimeoutRef.current);
      parentActionInfoTimeoutRef.current = window.setTimeout(() => setParentActionInfoText(null), 4200);
      await loadList();
    } catch (e) {
      setErrorText(humanizeHttpError(e, 'Nie udało się zapisać długiej nieobecności.'));
    } finally {
      setIsLoading(false);
    }
  };

  const onClearLongAbsence = async () => {
    if (props.role !== 'PARENT') return;
    if (!safeChildId) {
      setErrorText('Wybierz dziecko, aby wykonać tę akcję.');
      return;
    }
    setIsLoading(true);
    setErrorText(null);
    try {
      const updated = await apiClearChildUnavailability(props.token, safeChildId);
      setLongAbsChild(updated);
      setTrainingAbsenceSheet('NONE');
      setParentActionInfoText('Usunięto długą nieobecność.');
      if (parentActionInfoTimeoutRef.current) window.clearTimeout(parentActionInfoTimeoutRef.current);
      parentActionInfoTimeoutRef.current = window.setTimeout(() => setParentActionInfoText(null), 4200);
    } catch (e) {
      setErrorText(humanizeHttpError(e, 'Nie udało się usunąć długiej nieobecności.'));
    } finally {
      setIsLoading(false);
    }
  };

  const onTrainingAbsenceClick = () => {
    if (!details || details.type !== 'TRAINING') return;
    if (props.role !== 'PARENT') return;
    if (!safeChildId) {
      setErrorText('Wybierz dziecko, aby wykonać tę akcję.');
      return;
    }
    if (details.attendance_status === 'OUT' || details.attendance_status === 'LATE') {
      onOptIn();
      return;
    }
    onOptOut();
  };

  const onMarkTrainingLate = async () => {
    if (!details || details.type !== 'TRAINING') return;
    if (props.role !== 'PARENT') return;
    if (!safeChildId) {
      setErrorText('Wybierz dziecko, aby wykonać tę akcję.');
      return;
    }
    setIsLoading(true);
    setErrorText(null);
    try {
      const next = await apiMarkTrainingLate(props.token, details.id, safeChildId);
      setDetails(next);
      setTrainingAbsenceSheet('NONE');
      setParentActionInfoText('Zapisano: spóźnię się.');
      if (parentActionInfoTimeoutRef.current) window.clearTimeout(parentActionInfoTimeoutRef.current);
      parentActionInfoTimeoutRef.current = window.setTimeout(() => setParentActionInfoText(null), 4200);
      await loadList();
    } catch (e) {
      setErrorText(humanizeHttpError(e, 'Nie udało się zapisać spóźnienia.'));
    } finally {
      setIsLoading(false);
    }
  };

  const onOptIn = async () => {
    if (!details) return;
    if (isChildScoped && !safeChildId) {
      setErrorText('Wybierz dziecko, aby wykonać tę akcję.');
      return;
    }
    setIsLoading(true);
    setErrorText(null);
    try {
      const next = await apiOptIn(props.token, details.id, safeChildId);
      setDetails(next);
      await loadList();
    } catch (e) {
      setErrorText(humanizeHttpError(e, 'Nie udało się cofnąć nieobecności.'));
    } finally {
      setIsLoading(false);
    }
  };

  const showParentActionInfo = React.useCallback((text: string) => {
    setParentActionInfoText(text);
    if (parentActionInfoTimeoutRef.current) window.clearTimeout(parentActionInfoTimeoutRef.current);
    parentActionInfoTimeoutRef.current = window.setTimeout(() => setParentActionInfoText(null), 4200);
  }, []);

  const maybeOpenSiblingRsvpPrompt = React.useCallback(
    async (eventId: string, primaryChildId: string, actionLabel: string) => {
      if (props.role !== 'PARENT') return;
      const siblings = (props.parentChildren ?? []).filter((c) => c.id !== primaryChildId);
      if (siblings.length === 0) return;
      try {
        const settled = await Promise.allSettled(
          siblings.map(async (child) => ({
            child,
            event: await apiGetEvent(props.token, eventId, child.id),
          }))
        );
        const rows = settled
          .filter((r): r is PromiseFulfilledResult<{ child: ChildItem; event: EventDetails }> => r.status === 'fulfilled')
          .map((r) => r.value)
          .filter(({ event }) => event.type === 'MATCH' && event.called_up === true && event.can_match_rsvp !== false && (event.match_rsvp ?? 'PENDING') === 'PENDING')
          .map(({ child, event }) => ({
            child,
            match_rsvp: (event.match_rsvp ?? 'PENDING') as MatchRsvp,
            declineReason: '',
            isDeclineOpen: false,
            isSaving: false,
            error: null,
          }));
        if (rows.length === 0) return;
        const primaryChild = parentChildById.get(primaryChildId) ?? null;
        setSiblingRsvpPrompt({
          eventId,
          actionLabel,
          primaryChildLabel: primaryChild ? childLabel(primaryChild) : 'wybranego dziecka',
          rows,
        });
      } catch {}
    },
    [parentChildById, props.parentChildren, props.role, props.token]
  );

  const applySiblingPromptRsvp = React.useCallback(
    async (childId: string, response: 'ACCEPT' | 'DECLINE') => {
      if (!siblingRsvpPrompt) return;
      const row = siblingRsvpPrompt.rows.find((x) => x.child.id === childId);
      if (!row) return;
      const reason = row.declineReason.trim();
      if (response === 'DECLINE' && !reason) {
        setSiblingRsvpPrompt((prev) =>
          !prev
            ? prev
            : {
                ...prev,
                rows: prev.rows.map((x) => (x.child.id === childId ? { ...x, error: 'Podaj krótki powód nieobecności.' } : x)),
              }
        );
        return;
      }
      setSiblingRsvpPrompt((prev) =>
        !prev
          ? prev
          : {
              ...prev,
              rows: prev.rows.map((x) => (x.child.id === childId ? { ...x, isSaving: true, error: null } : x)),
            }
      );
      try {
        await apiMatchRsvpRequest(props.token, siblingRsvpPrompt.eventId, response === 'ACCEPT' ? { response } : { response, reason }, childId);
        setSiblingRsvpPrompt((prev) => {
          if (!prev) return prev;
          const rows = prev.rows.filter((x) => x.child.id !== childId);
          return rows.length > 0 ? { ...prev, rows } : null;
        });
        showParentActionInfo(
          response === 'ACCEPT' ? `Potwierdzono też ${childLabel(row.child)}.` : `Zgłoszono też nieobecność: ${childLabel(row.child)}.`
        );
        await loadList();
        if (selectedId) await loadDetails(selectedId);
      } catch (e) {
        setSiblingRsvpPrompt((prev) =>
          !prev
            ? prev
            : {
                ...prev,
                rows: prev.rows.map((x) =>
                  x.child.id === childId ? { ...x, isSaving: false, error: humanizeHttpError(e, 'Nie udało się zapisać odpowiedzi.') } : x
                ),
              }
        );
      }
    },
    [loadDetails, loadList, props.token, selectedId, showParentActionInfo, siblingRsvpPrompt]
  );

  const onMatchAccept = async () => {
    if (!details) return;
    if (isChildScoped && !safeChildId) {
      setErrorText('Wybierz dziecko, aby wykonać tę akcję.');
      return;
    }
    setIsLoading(true);
    setErrorText(null);
    try {
      const eventId = details.id;
      const activeChildId = safeChildId;
      const next = await apiMatchRsvpRequest(props.token, eventId, { response: 'ACCEPT' }, activeChildId);
      setDetails(next);
      showParentActionInfo('Potwierdzono obecność.');
      await loadList();
      if (activeChildId) await maybeOpenSiblingRsvpPrompt(eventId, activeChildId, 'Potwierdzono obecność dla');
    } catch (e) {
      setErrorText(humanizeHttpError(e, 'Nie udało się potwierdzić.'));
    } finally {
      setIsLoading(false);
    }
  };

  const onMatchDecline = async () => {
    if (!details) return;
    setDeclineReason('');
    setIsDeclineOpen(true);
  };

  const onMatchDeclineConfirm = async () => {
    if (!details) return;
    const reason = declineReason.trim();
    if (!reason) return;
    if (isChildScoped && !safeChildId) {
      setErrorText('Wybierz dziecko, aby wykonać tę akcję.');
      return;
    }
    setIsLoading(true);
    setErrorText(null);
    try {
      const eventId = details.id;
      const activeChildId = safeChildId;
      const next = await apiMatchRsvpRequest(props.token, eventId, {
        response: 'DECLINE',
        reason,
      }, activeChildId);
      setDetails(next);
      setIsDeclineOpen(false);
      showParentActionInfo('Zgłoszono nieobecność.');
      await loadList();
      if (activeChildId) await maybeOpenSiblingRsvpPrompt(eventId, activeChildId, 'Zgłoszono nieobecność dla');
    } catch (e) {
      setErrorText(humanizeHttpError(e, 'Nie udało się wysłać odmowy.'));
    } finally {
      setIsLoading(false);
    }
  };

  const openMaps = React.useCallback(() => {
    if (!details) return;
    const url = googleMapsUrlFromLocation(details.location);
    openExternalUrl(url);
  }, [details]);

  const openCarpoolFromEventId = React.useCallback((eventId: string) => {
    const id = eventId.trim();
    if (!id) return;
    setCarpoolEventId(id);
  }, []);

  const openEditMatch = () => {
    if (props.role !== 'COACH' && props.role !== 'ADMIN') return;
    if (!details || details.type !== 'MATCH') return;
    setCreateTargetChildId(null);
    const start = new Date(details.start_at);
    const end = details.end_at ? new Date(details.end_at) : null;
    const deadline = details.match_deadline_at ? new Date(details.match_deadline_at) : null;
    const meet = details.match_meet_at ? new Date(details.match_meet_at) : null;
    setEditMatchEventId(details.id);
    setCmGroupId(details.group_id || cmGroupId);
    setCmMatchKind((details.match_kind as MatchKind | null) ?? 'LEAGUE');
    setCmTitle(details.title || 'Mecz');
    setCmIsTbd(!!details.is_tbd);
    setCmStartLocal(toLocalInputValue(start));
    setCmEndLocal(end ? toLocalInputValue(end) : '');
    setCmLocation(details.location || '');
    const meetTime = meet && !Number.isNaN(meet.getTime()) ? toLocalInputValue(meet).split('T')[1]?.slice(0, 5) ?? '' : '';
    setCmMeetTime(meetTime);
    const deadlineLocal = deadline && !Number.isNaN(deadline.getTime()) ? toLocalInputValue(deadline) : '';
    setCmNeedsConfirmation(!!deadlineLocal);
    setCmDeadlineLocal(deadlineLocal);
    setCmOpponent(details.opponent_name || '');
    setCmOpponentTeamId(null);
    const meta = parseMatchMeta(details.description);
    setCmDescription(meta.visible);
    const formula = (meta.formula || '').trim();
    if (formula === '5X5' || formula === '7X7' || formula === '9X9' || formula === '11X11') {
      setCmFormula(formula);
      setCmMinPlayersOther('');
    } else if (formula && formula.startsWith('INNA')) {
      setCmFormula('INNA');
      const m = formula.match(/\(([0-9]{1,2})\)/);
      setCmMinPlayersOther(m ? m[1] : '');
    } else {
      setCmFormula('');
      setCmMinPlayersOther('');
    }
    setIsCreateMatchOpen(true);
  };

  React.useEffect(() => {
    if (
      !isDeclineOpen &&
      trainingAbsenceSheet === 'NONE' &&
      !isAddOpponentConfirmOpen &&
      !isCreateMatchOpen &&
      !isCreateTrainingOpen &&
      !isEditTrainingOpen &&
      !isManageMatchOpen &&
      !isCancelMatchConfirmOpen &&
      !isCancelTrainingConfirmOpen &&
      !isDeleteEventConfirmOpen &&
      !isCallupListOpen &&
      !callupEventId &&
      !carpoolEventId &&
      !siblingRsvpPrompt
    )
      return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (isLoading) return;
      e.preventDefault();
      if (carpoolEventId) {
        setCarpoolEventId(null);
        return;
      }
      if (siblingRsvpPrompt) {
        setSiblingRsvpPrompt(null);
        return;
      }
      if (isCallupListOpen) {
        setIsCallupListOpen(false);
        return;
      }
      if (callupEventId) {
        setCallupEventId(null);
        return;
      }
      if (isDeleteEventConfirmOpen) {
        setIsDeleteEventConfirmOpen(false);
        return;
      }
      if (isCancelTrainingConfirmOpen) {
        setIsCancelTrainingConfirmOpen(false);
        return;
      }
      if (isCancelMatchConfirmOpen) {
        setIsCancelMatchConfirmOpen(false);
        return;
      }
      if (isManageMatchOpen) {
        setIsManageMatchOpen(false);
        return;
      }
      if (isEditTrainingOpen) {
        setIsEditTrainingOpen(false);
        return;
      }
      if (isAddOpponentConfirmOpen) {
        setIsAddOpponentConfirmOpen(false);
        setPendingMatchBody(null);
        setPendingMatchUpdateEventId(null);
        setPendingMatchUpdateBody(null);
        setPendingOpponentName('');
        return;
      }
      if (isCreateTrainingOpen) {
        setIsCreateTrainingOpen(false);
        return;
      }
      if (isCreateMatchOpen) {
        setIsCreateMatchOpen(false);
        setEditMatchEventId(null);
        return;
      }
      if (trainingAbsenceSheet !== 'NONE') {
        setTrainingAbsenceSheet('NONE');
        return;
      }
      if (isDeclineOpen) setIsDeclineOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    carpoolEventId,
    callupEventId,
    isCallupListOpen,
    isCancelMatchConfirmOpen,
    isCancelTrainingConfirmOpen,
    isAddOpponentConfirmOpen,
    isCreateMatchOpen,
    isCreateTrainingOpen,
    isDeclineOpen,
    isDeleteEventConfirmOpen,
    isEditTrainingOpen,
    isManageMatchOpen,
    isLoading,
    siblingRsvpPrompt,
    trainingAbsenceSheet,
  ]);

  return (
    <div className={['Page', selectedId ? 'PageHasActionBar' : ''].filter(Boolean).join(' ')}>
      {selectedId && (
        <div className="EventBackBar" role="region" aria-label="Nawigacja">
          <button className="EventBackButton" type="button" onClick={closeEvent} disabled={isLoading} aria-label="Wróć do listy">
            ‹
          </button>
        </div>
      )}

      {errorText && <div className="ErrorBanner">{errorText}</div>}

      {isDeclineOpen && (
        <div
          className="SheetOverlay"
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => {
            if (isLoading) return;
            if (e.target === e.currentTarget) setIsDeclineOpen(false);
          }}
        >
          <div className="Sheet">
            <div className="SheetHeaderRow SheetHeaderRow--sticky">
              <div className="SheetTitle">POWÓD NIEOBECNOŚCI</div>
              <button className="SheetCloseButton" type="button" aria-label="Zamknij" onClick={() => setIsDeclineOpen(false)} disabled={isLoading}>
                ×
              </button>
            </div>
            <textarea
              className="TextArea"
              rows={3}
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              placeholder="Np. choroba, wyjazd rodzinny…"
              disabled={isLoading}
            />
            <PrimaryButton
              onClick={onMatchDeclineConfirm}
              disabled={isLoading || declineReason.trim() === ''}
              type="button"
            >
              Wyślij
            </PrimaryButton>
            <SecondaryButton onClick={() => setIsDeclineOpen(false)} disabled={isLoading} type="button">
              Anuluj
            </SecondaryButton>
          </div>
        </div>
      )}

      {siblingRsvpPrompt && (
        <div
          className="SheetOverlay"
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setSiblingRsvpPrompt(null);
          }}
        >
          <div className="Sheet">
            <div className="SheetHeaderRow SheetHeaderRow--sticky">
              <div className="SheetTitle">CO Z POZOSTAŁYMI DZIEĆMI?</div>
              <button className="SheetCloseButton" type="button" aria-label="Zamknij" onClick={() => setSiblingRsvpPrompt(null)}>
                ×
              </button>
            </div>
            <div className="Muted">{`${siblingRsvpPrompt.actionLabel} ${siblingRsvpPrompt.primaryChildLabel}. Jeśli inne dziecko też ma powołanie na ten sam mecz, odpowiedz od razu tutaj.`}</div>
            <div className="List">
              {siblingRsvpPrompt.rows.map((row) => (
                <div key={row.child.id} className="SiblingPromptCard">
                  <div className="ListItemTop">
                    <div className="ListItemTitle">{childLabel(row.child)}</div>
                    <div className="ListItemBadge">OCZEKUJE</div>
                  </div>
                  {row.error && <div className="ErrorBanner">{row.error}</div>}
                  {row.isDeclineOpen ? (
                    <>
                      <textarea
                        className="TextArea"
                        rows={3}
                        value={row.declineReason}
                        onChange={(e) =>
                          setSiblingRsvpPrompt((prev) =>
                            !prev
                              ? prev
                              : {
                                  ...prev,
                                  rows: prev.rows.map((x) =>
                                    x.child.id === row.child.id ? { ...x, declineReason: e.target.value, error: null } : x
                                  ),
                                }
                          )
                        }
                        placeholder="Powód nieobecności dla tego dziecka"
                        disabled={row.isSaving}
                      />
                      <div className="SheetActionsRow">
                        <PrimaryButton onClick={() => applySiblingPromptRsvp(row.child.id, 'DECLINE')} disabled={row.isSaving} type="button">
                          Zapisz NB
                        </PrimaryButton>
                        <SecondaryButton
                          onClick={() =>
                            setSiblingRsvpPrompt((prev) =>
                              !prev
                                ? prev
                                : {
                                    ...prev,
                                    rows: prev.rows.map((x) =>
                                      x.child.id === row.child.id ? { ...x, isDeclineOpen: false, declineReason: '', error: null } : x
                                    ),
                                  }
                            )
                          }
                          disabled={row.isSaving}
                          type="button"
                        >
                          Wróć
                        </SecondaryButton>
                      </div>
                    </>
                  ) : (
                    <div className="SheetActionsRow">
                      <PrimaryButton onClick={() => applySiblingPromptRsvp(row.child.id, 'ACCEPT')} disabled={row.isSaving} type="button">
                        Jestem
                      </PrimaryButton>
                      <SecondaryButton
                        onClick={() =>
                          setSiblingRsvpPrompt((prev) =>
                            !prev
                              ? prev
                              : {
                                  ...prev,
                                  rows: prev.rows.map((x) => (x.child.id === row.child.id ? { ...x, isDeclineOpen: true, error: null } : x)),
                                }
                          )
                        }
                        disabled={row.isSaving}
                        type="button"
                      >
                        Brak
                      </SecondaryButton>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <SecondaryButton onClick={() => setSiblingRsvpPrompt(null)} type="button">
              Zajmę się tym później
            </SecondaryButton>
          </div>
        </div>
      )}

      {trainingAbsenceSheet !== 'NONE' && props.role === 'PARENT' && details?.type === 'TRAINING' && (
        <div
          className="SheetOverlay"
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => {
            if (isLoading) return;
            if (e.target === e.currentTarget) setTrainingAbsenceSheet('NONE');
          }}
        >
          <div
            className={['Sheet', trainingAbsenceSheet === 'LONG' ? 'Sheet--longAbsence' : ''].filter(Boolean).join(' ')}
          >
            <div className="SheetHeaderRow SheetHeaderRow--sticky">
              <div className="SheetTitle">{trainingAbsenceSheet === 'LONG' ? 'DŁUGA NIEOBECNOŚĆ' : 'NIEOBECNOŚĆ'}</div>
              <button
                className="SheetCloseButton"
                type="button"
                aria-label="Zamknij"
                onClick={() => setTrainingAbsenceSheet('NONE')}
                disabled={isLoading}
              >
                ×
              </button>
            </div>
            {trainingAbsenceSheet === 'PICK' && (
              <>
                <div className="Muted">Wybierz jednorazową nieobecność, długą nieobecność albo „Spóźnię się”.</div>
                <PrimaryButton
                  onClick={async () => {
                    setTrainingAbsenceSheet('NONE');
                    await onOptOut();
                  }}
                  disabled={isLoading}
                  type="button"
                >
                  Jednorazowa (ten trening)
                </PrimaryButton>
                <SecondaryButton
                  onClick={async () => {
                    await onMarkTrainingLate();
                  }}
                  disabled={isLoading}
                  type="button"
                >
                  Spóźnię się
                </SecondaryButton>
                <SecondaryButton onClick={openLongAbsenceForm} disabled={isLoading} type="button">
                  Długa (zakres dat)
                </SecondaryButton>
                <SecondaryButton onClick={() => setTrainingAbsenceSheet('NONE')} disabled={isLoading} type="button">
                  Anuluj
                </SecondaryButton>
              </>
            )}

            {trainingAbsenceSheet === 'LONG' && (
              <>
                <div className="Muted">Ustaw zakres dat dla dziecka (wpływa na powołania, a na ten trening zapisze NB).</div>

                <div className="Muted">Powód</div>
                <div className="NotificationActions" aria-label="Powód długiej nieobecności">
                  {([
                    { k: 'CHOROBA' as const, label: 'Choroba' },
                    { k: 'KONTUZJA' as const, label: 'Kontuzja' },
                    { k: 'INNE' as const, label: 'Inne' },
                  ] as const).map((x) => (
                    <button
                      key={x.k}
                      type="button"
                      className={`Pill ${longAbsReason === x.k ? 'is-active' : ''}`}
                      onClick={() => setLongAbsReason(x.k)}
                      disabled={isLoading}
                    >
                      {x.label}
                    </button>
                  ))}
                </div>

                <div className="MatchCreateGrid2">
                  <div className="MatchCreateField MatchCreateField--compact">
                    <div className="MatchCreateFieldIcon" aria-hidden="true">
                      <ClockIcon />
                    </div>
                    <div className="MatchCreateFieldBody">
                      <div className="MatchCreateFieldLabel">OD</div>
                      <input
                        className="MatchCreateInput"
                        type="date"
                        value={longAbsFrom}
                        onChange={(e) => setLongAbsFrom(e.target.value)}
                        disabled={isLoading}
                      />
                    </div>
                  </div>

                  <div className="MatchCreateField MatchCreateField--compact">
                    <div className="MatchCreateFieldIcon" aria-hidden="true">
                      <ClockIcon />
                    </div>
                    <div className="MatchCreateFieldBody">
                      <div className="MatchCreateFieldLabel">DO</div>
                      <input
                        className="MatchCreateInput"
                        type="date"
                        value={longAbsUntil}
                        onChange={(e) => setLongAbsUntil(e.target.value)}
                        disabled={isLoading}
                      />
                    </div>
                  </div>
                </div>

                <div className="MatchCreateField">
                  <div className="MatchCreateFieldIcon" aria-hidden="true">
                    <ChatIcon />
                  </div>
                  <div className="MatchCreateFieldBody">
                    <div className="MatchCreateFieldLabel">OPIS (OPCJONALNIE)</div>
                    <textarea
                      className="MatchCreateTextArea"
                      rows={3}
                      value={longAbsNote}
                      onChange={(e) => setLongAbsNote(e.target.value)}
                      placeholder="Np. L4 do piątku, wyjazd rodzinny…"
                      disabled={isLoading}
                    />
                  </div>
                </div>

                <PrimaryButton
                  onClick={onSubmitLongAbsence}
                  disabled={
                    isLoading ||
                    !longAbsFrom.trim() ||
                    !longAbsUntil.trim() ||
                    longAbsUntil.trim() < longAbsFrom.trim() ||
                    longAbsNote.trim().length > 240
                  }
                  type="button"
                >
                  Zapisz
                </PrimaryButton>
                {((longAbsChild?.unavailability_kind ?? '').trim() || '').length > 0 && (
                  <SecondaryButton onClick={onClearLongAbsence} disabled={isLoading} type="button">
                    Usuń długą nieobecność
                  </SecondaryButton>
                )}
                <SecondaryButton onClick={() => setTrainingAbsenceSheet('PICK')} disabled={isLoading} type="button">
                  Wróć
                </SecondaryButton>
              </>
            )}
          </div>
        </div>
      )}

      {carpoolEventId && (
        <div
          className="SheetOverlay"
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => {
            if (isLoading) return;
            if (e.target === e.currentTarget) setCarpoolEventId(null);
          }}
        >
          <div className="Sheet">
            {(() => {
              const fromList = events.find((e) => e.id === carpoolEventId) ?? null;
              const fromDetails = details?.id === carpoolEventId ? details : null;
              const title = (fromDetails?.title ?? fromList?.title ?? '').trim() || null;
              const loc = (fromDetails?.location ?? fromList?.location ?? '').trim() || null;
              const when = (fromDetails?.start_at ?? fromList?.start_at ?? null) as string | null;
              const meta = [when ? formatDateTime(when) : null, loc].filter(Boolean).join(' • ') || null;
              return (
                <CarpoolSheet
                  token={props.token}
                  me={props.me}
                  eventId={carpoolEventId}
                  childId={props.role === 'PARENT' ? props.childId ?? null : null}
                  eventTitle={title}
                  eventMeta={meta}
                  onChanged={loadList}
                  onOpenEvent={() => {
                    const id = carpoolEventId;
                    setCarpoolEventId(null);
                    if (id) navigate('kalendarz', { e: id });
                  }}
                  onClose={() => setCarpoolEventId(null)}
                />
              );
            })()}
          </div>
        </div>
      )}

      {isCallupListOpen && details?.type === 'MATCH' && Array.isArray(details.called_up_child_ids) && details.called_up_child_ids.length > 0 && (
        <div
          className="SheetOverlay"
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => {
            if (isLoading) return;
            if (e.target === e.currentTarget) setIsCallupListOpen(false);
          }}
        >
          <div className="Sheet Sheet--callups">
            <div className="SheetHeaderRow SheetHeaderRow--sticky">
              <div className="CallupTitle">POWOŁANI</div>
              <button
                className="SheetCloseButton"
                type="button"
                onClick={() => setIsCallupListOpen(false)}
                disabled={isLoading}
                aria-label="Zamknij"
              >
                ×
              </button>
            </div>
            <div className="CallupMeta">
              <div className="CallupMetaLine">{details.title}</div>
              <div className="CallupMetaLine is-sub">{`${formatDateTime(details.start_at)} • ${details.location}`}</div>
            </div>
            {(() => {
              const ids = details.called_up_child_ids ?? [];
              const childById = new Map(callupRoster.map((c) => [c.id, c]));
              const rows = ids.map((id) => ({
                id,
                child: childById.get(id) ?? null,
                rsvp: (details.callup_rsvps?.[id] ?? 'PENDING') as MatchRsvp,
                reason: (details.callup_rsvp_reasons?.[id] ?? '') as string,
              }));
              const sortedRows = rows
                .slice()
                .sort((a, b) => {
                  const ac = a.child;
                  const bc = b.child;
                  const al = String(ac?.last_initial ?? '')
                    .replace('.', '')
                    .trim()
                    .toLowerCase();
                  const bl = String(bc?.last_initial ?? '')
                    .replace('.', '')
                    .trim()
                    .toLowerCase();
                  const af = String(ac?.first_name ?? '').trim().toLowerCase();
                  const bf = String(bc?.first_name ?? '').trim().toLowerCase();
                  const byLast = al.localeCompare(bl, 'pl', { sensitivity: 'base' });
                  if (byLast !== 0) return byLast;
                  const byFirst = af.localeCompare(bf, 'pl', { sensitivity: 'base' });
                  if (byFirst !== 0) return byFirst;
                  return String(a.id ?? '').localeCompare(String(b.id ?? ''));
                });
              const stats = {
                all: rows.length,
                accepted: rows.filter((r) => r.rsvp === 'ACCEPTED').length,
                declined: rows.filter((r) => r.rsvp === 'DECLINED').length,
                pending: rows.filter((r) => r.rsvp === 'PENDING').length,
              };
              const filtered =
                callupListFilter === 'ALL' ? sortedRows : sortedRows.filter((r) => r.rsvp === callupListFilter);
              return (
                <>
                  <div className="CallupFilters">
                    <button
                      className={`Pill ${callupListFilter === 'ALL' ? 'is-active' : ''}`}
                      onClick={() => setCallupListFilter('ALL')}
                      disabled={isLoading}
                      type="button"
                    >
                      {`Wszyscy (${stats.all})`}
                    </button>
                    <button
                      className={`Pill ${callupListFilter === 'PENDING' ? 'is-active' : ''}`}
                      onClick={() => setCallupListFilter('PENDING')}
                      disabled={isLoading}
                      type="button"
                    >
                      {`Oczekuje (${stats.pending})`}
                    </button>
                    <button
                      className={`Pill ${callupListFilter === 'ACCEPTED' ? 'is-active' : ''}`}
                      onClick={() => setCallupListFilter('ACCEPTED')}
                      disabled={isLoading}
                      type="button"
                    >
                      {`Potwierdzone (${stats.accepted})`}
                    </button>
                    <button
                      className={`Pill ${callupListFilter === 'DECLINED' ? 'is-active' : ''}`}
                      onClick={() => setCallupListFilter('DECLINED')}
                      disabled={isLoading}
                      type="button"
                    >
                      {`Nieobecny (${stats.declined})`}
                    </button>
                  </div>

                  <div className="CallupList" role="list">
                    {filtered.map((row) => {
                      const c = row.child;
                      const fn = c?.first_name ?? '';
                      const ln = c?.last_initial ?? '';
                      const initials = `${fn.slice(0, 1)}${ln.slice(0, 1)}`.toUpperCase();
                      const photo = callupPhotoById[row.id] ?? null;
                      const reason = (row.reason ?? '').trim();
                      const eventTs = (() => {
                        const dt = details?.start_at ? new Date(details.start_at) : null;
                        return dt && !Number.isNaN(dt.getTime()) ? dt.getTime() : Date.now();
                      })();
                      const unavailableKind = (c?.unavailability_kind ?? '').trim();
                      const isUnavailable = (() => {
                        if (!unavailableKind || !c) return false;
                        const fromRaw = (c.unavailability_from ?? '').trim();
                        const untilRaw = (c.unavailability_until ?? '').trim();
                        const fromKey = fromRaw ? fromRaw.slice(0, 10) : '';
                        const untilKey = untilRaw ? untilRaw.slice(0, 10) : '';
                        const fromMs = fromKey ? Date.parse(`${fromKey}T00:00:00Z`) : Number.NEGATIVE_INFINITY;
                        const untilMs = untilKey ? Date.parse(`${untilKey}T23:59:59.999Z`) : Number.POSITIVE_INFINITY;
                        if (Number.isNaN(fromMs) || Number.isNaN(untilMs)) return true;
                        return eventTs >= fromMs && eventTs <= untilMs;
                      })();
                      const absBadge = c && isUnavailable ? callupAbsenceBadgeLabel(c) : null;
                      return (
                        <div key={row.id} className="CallupRow" role="listitem">
                          <div className={['CallupAvatar', isUnavailable ? 'is-unavailable' : ''].filter(Boolean).join(' ')} aria-hidden="true">
                            {photo ? <img src={photo} alt="" /> : initials || '•'}
                          </div>
                          <div className="CallupMain">
                            <div className="CallupName">{c ? `${c.first_name} ${c.last_initial}` : 'Zawodnik'}</div>
                            {absBadge ? (
                              <div className="CallupBadges">
                                <div className="CallupAbsenceBadge">{absBadge}</div>
                              </div>
                            ) : null}
                            <div className="CallupSub">
                              <span className={`CallupStatus is-${row.rsvp.toLowerCase()}`}>
                                {row.rsvp === 'ACCEPTED' ? 'POTWIERDZONE' : row.rsvp === 'DECLINED' ? 'NIEOBECNY' : 'OCZEKUJE'}
                              </span>
                              {row.rsvp === 'DECLINED' && reason ? <span className="CallupReason">{reason}</span> : null}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {isAddOpponentConfirmOpen && (props.role === 'COACH' || props.role === 'ADMIN') && (
        <div
          className="SheetOverlay SheetOverlay--top"
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => {
            if (isLoading) return;
            if (e.target === e.currentTarget) {
              setIsAddOpponentConfirmOpen(false);
              setPendingMatchBody(null);
              setPendingMatchUpdateEventId(null);
              setPendingMatchUpdateBody(null);
              setPendingOpponentName('');
            }
          }}
        >
          <div className="Sheet">
            <div className="SheetHeaderRow SheetHeaderRow--sticky">
              <div className="SheetTitle">DODAĆ DRUŻYNĘ DO BAZY?</div>
              <button
                className="SheetCloseButton"
                type="button"
                aria-label="Zamknij"
                onClick={() => {
                  setIsAddOpponentConfirmOpen(false);
                  setPendingMatchBody(null);
                  setPendingMatchUpdateEventId(null);
                  setPendingMatchUpdateBody(null);
                  setPendingOpponentName('');
                }}
                disabled={isLoading}
              >
                ×
              </button>
            </div>
            <div className="Muted">
              Nie znaleziono przeciwnika: <strong>{pendingOpponentName}</strong>. Dodać do bazy drużyn?
            </div>
            <PrimaryButton onClick={onConfirmAddOpponentYes} disabled={isLoading} type="button">
              {pendingMatchBody ? 'Dodaj do bazy i utwórz mecz' : 'Dodaj do bazy i zapisz mecz'}
            </PrimaryButton>
            <SecondaryButton onClick={onConfirmAddOpponentNo} disabled={isLoading} type="button">
              {pendingMatchBody ? 'Utwórz bez dodawania' : 'Zapisz bez dodawania'}
            </SecondaryButton>
            <SecondaryButton
              onClick={() => {
                setIsAddOpponentConfirmOpen(false);
                setPendingMatchBody(null);
                setPendingMatchUpdateEventId(null);
                setPendingMatchUpdateBody(null);
                setPendingOpponentName('');
              }}
              disabled={isLoading}
              type="button"
            >
              Anuluj
            </SecondaryButton>
          </div>
        </div>
      )}

      {plannerOpen && (
        <div
          className="SheetOverlay"
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => {
            if (isLoading) return;
            if (e.target === e.currentTarget) {
              setPlannerOpen(false);
              setPlannerAddReq(null);
              setPlannerPickChildReq(null);
              setPlannerPickChildQuery('');
            }
          }}
        >
          <div className="Sheet Sheet--planner" aria-label="Plan dnia">
            <div className="SheetHeaderRow SheetHeaderRow--sticky">
              <div className="SheetTitle">PLAN DNIA</div>
              <button
                className="SheetCloseButton"
                type="button"
                aria-label="Zamknij"
                onClick={() => {
                  setPlannerOpen(false);
                  setPlannerAddReq(null);
                  setPlannerPickChildReq(null);
                  setPlannerPickChildQuery('');
                }}
                disabled={isLoading}
              >
                ×
              </button>
            </div>

            <div className="PlannerTop">
              <div className="PlannerMonthNav" aria-label="Nawigacja miesiąca">
                <button className="PlannerNavBtn" type="button" onClick={() => shiftMonth(-1)} disabled={isLoading} aria-label="Poprzedni miesiąc">
                  ‹
                </button>
                <div className="PlannerMonthLabel">{monthTitleWithYear(monthCursor)}</div>
                <button className="PlannerNavBtn" type="button" onClick={() => shiftMonth(1)} disabled={isLoading} aria-label="Następny miesiąc">
                  ›
                </button>
              </div>
              <div className="PlannerDayNav" aria-label="Nawigacja dnia">
                <button className="PlannerNavBtn" type="button" onClick={() => shiftDay(-1)} disabled={isLoading} aria-label="Poprzedni dzień">
                  ‹
                </button>
                <div className="PlannerDayLabel">{formatDayLabel(selectedDayKey)}</div>
                <button className="PlannerNavBtn" type="button" onClick={() => shiftDay(1)} disabled={isLoading} aria-label="Następny dzień">
                  ›
                </button>
              </div>
            </div>

            {(() => {
              const dayEvents = selectedDayEventsByRole.slice().sort((a, b) => a.start_at.localeCompare(b.start_at));
              const canPlan =
                (props.role === 'COACH' || props.role === 'ADMIN') &&
                (coachGroups.length > 0 || (isCoachIndividualMode && (props.coachChildren ?? []).some((c) => (c.group_id ?? '').trim())));
              const createOpts = isCoachChildCalendar
                ? { groupId: coachFocusChild?.group_id ?? props.activeGroupId, targetChildId: coachFocusChildId }
                : undefined;

              const startMin = PLANNER_START_MIN;
              const endMin = PLANNER_END_MIN;
              const stepMin = PLANNER_STEP_MIN;
              const stepPx = PLANNER_STEP_PX;
              const pxPerMin = PLANNER_PX_PER_MIN;
              const totalHeight = ((endMin - startMin) / stepMin) * stepPx;
              const layoutById = plannerLayout && plannerLayout.dayKey === selectedDayKey ? plannerLayout.byId : null;

              const minuteOfIso = (iso: string): number => {
                const d = new Date(iso);
                if (Number.isNaN(d.getTime())) return startMin;
                return d.getHours() * 60 + d.getMinutes();
              };

              const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
              const roundUpToStep = (m: number) => Math.ceil(m / stepMin) * stepMin;

              const normalized = dayEvents
                .map((e) => {
                  const startOverride = layoutById && layoutById[e.id] !== undefined ? layoutById[e.id] : null;
                  const s0 = startOverride !== null ? startOverride : minuteOfIso(e.start_at);
                  const durationMin = eventDurationMinutes(e);
                  const e0 = s0 + durationMin;
                  const s = clamp(s0, startMin, endMin);
                  const eMin = clamp(Math.max(e0, s0 + 20), startMin, endMin);
                  const e2 = Math.max(eMin, s + 20);
                  return { e, s, e2 };
                })
                .sort((a, b) => a.s - b.s);

              const blocks: Array<
                | { kind: 'FREE'; s: number; e: number }
                | { kind: 'EVENT'; s: number; e: number; item: EventListItem }
              > = [];

              let cursor = startMin;
              normalized.forEach(({ e, s, e2 }) => {
                if (s > cursor + 15) blocks.push({ kind: 'FREE', s: cursor, e: s });
                blocks.push({ kind: 'EVENT', s, e: e2, item: e });
                cursor = Math.max(cursor, e2);
              });
              if (cursor < endMin - 15) blocks.push({ kind: 'FREE', s: cursor, e: endMin });

              const times: number[] = [];
              for (let m = startMin; m <= endMin; m += stepMin) times.push(m);

              const timeLabel = (m: number) => `${pad2(Math.floor(m / 60))}:${pad2(m % 60)}`;

              const canUndo = !!plannerUndo && plannerUndo.dayKey === selectedDayKey;
              const ghostTop = plannerDrag ? (plannerDrag.previewStartMin - startMin) * pxPerMin : 0;
              const ghostHeight = plannerDrag ? Math.max(40, plannerDrag.durationMin * pxPerMin) : 0;

              return (
                <>
                  <div className="PlannerTimeline" aria-label="Oś czasu dnia">
                    <div className="PlannerTimes" aria-hidden="true">
                      {times.map((m) => (
                        <div key={m} className="PlannerTimeRow" style={{ height: stepPx }}>
                          {timeLabel(m)}
                        </div>
                      ))}
                    </div>
                    <div className="PlannerTrack">
                      <div ref={plannerTrackInnerRef} className="PlannerTrackInner" style={{ height: totalHeight }}>
                        {plannerDrag ? <div className="PlannerGhost" style={{ top: ghostTop, height: ghostHeight }} /> : null}
                        {blocks.map((b, idx) => {
                        const top = (b.s - startMin) * pxPerMin;
                        const height = Math.max(40, (b.e - b.s) * pxPerMin);
                        if (b.kind === 'FREE') {
                          const showAdd = canPlan;
                          const isCompactAdd = b.e - b.s < 60;
                          const startForCreate = clamp(roundUpToStep(b.s), startMin, endMin - 60);
                          return (
                            <div key={`f:${idx}:${b.s}`} className="PlannerBlock PlannerBlock--free" style={{ top, height }}>
                              <div className="PlannerFreeLabel">WOLNY SLOT</div>
                              {showAdd && (
                                <button
                                  className={['PlannerAddBtn', isCompactAdd ? 'is-compact' : ''].filter(Boolean).join(' ')}
                                  type="button"
                                  onClick={() => {
                                    if (isLoading) return;
                                    setPlannerAddReq({ startMinutes: startForCreate });
                                  }}
                                  disabled={isLoading || (isCoachChildCalendar && !(coachFocusChild?.group_id ?? props.activeGroupId))}
                                  aria-label="Dodaj wydarzenie"
                                >
                                  {isCompactAdd ? <PlusIcon /> : '+ DODAJ'}
                                </button>
                              )}
                            </div>
                          );
                        }
                        const item = b.item;
                        const isMatch = item.type === 'MATCH';
                        const title = (item.title || (isMatch ? 'Mecz' : 'Trening')).trim().toUpperCase();
                        const opponent = isMatch ? item.opponent_name || opponentLabelFromMatchTitle(item.title || '') || '' : '';
                        const subtitle = `${timeLabel(b.s)}${item.location ? `, ${item.location}` : ''}${item.status === 'CANCELLED' ? ' • ODWOŁANE' : ''}`;
                        const coachLine =
                          isCoachIndividualMode &&
                          !coachFocusChildId &&
                          item.type === 'TRAINING' &&
                          item.is_individual &&
                          (item.target_child_label ?? '').trim()
                            ? `Podopieczny: ${(item.target_child_label ?? '').trim()}`
                            : null;
                        const plannerTargetChildId = (item.target_child_id ?? '').trim();
                        const plannerChildLabelRaw =
                          (item.target_child_label ?? '').trim() ||
                          (plannerTargetChildId ? (resolveCoachChildLabel(plannerTargetChildId) ?? '').trim() : '');
                        const plannerChildPhotoUrl = plannerTargetChildId ? resolveChildPhotoUrl(plannerTargetChildId) : null;
                        const plannerChildInitials = (() => {
                          const raw = (plannerChildLabelRaw || '').replace(/\([^)]+\)/g, '').trim();
                          if (!raw) return '•';
                          const parts = raw
                            .split(/\s+/g)
                            .map((x) => x.trim())
                            .filter(Boolean);
                          const a = parts[0]?.slice(0, 1) ?? '';
                          const b = (parts[1]?.slice(0, 1) ?? parts[0]?.slice(1, 2) ?? '').trim();
                          return `${a}${b}`.toUpperCase() || '•';
                        })();
                        const duration = eventDurationMinutes(item);
                        const isLockedByOwner =
                          props.role === 'COACH' && isCoachIndividualMode && !(item.is_individual && item.can_take_action);
                        const canDrag = (props.role === 'COACH' || props.role === 'ADMIN') && item.status === 'ACTIVE' && !isLoading && !isLockedByOwner;
                        const isDragging = plannerDrag?.eventId === item.id;
                        const dragTop = isDragging ? (plannerDrag.previewStartMin - startMin) * pxPerMin : top;
                        return (
                          <button
                            key={`e:${item.id}`}
                            className={[
                              'PlannerBlock',
                              'PlannerBlock--event',
                              isMatch ? 'is-match' : 'is-training',
                              item.status === 'CANCELLED' ? 'is-cancelled' : '',
                              canDrag ? 'is-draggable' : '',
                              isDragging ? 'is-dragging' : '',
                              isLockedByOwner ? 'is-locked' : '',
                            ]
                              .filter(Boolean)
                              .join(' ')}
                            type="button"
                            style={{ top: dragTop, height }}
                            onPointerDown={(e) => {
                              if (!canDrag) return;
                              if (e.button !== 0) return;
                              if (plannerDrag) return;
                              const curMinRaw = layoutById && layoutById[item.id] !== undefined ? layoutById[item.id] : minuteOfIso(item.start_at);
                              const curMin = clamp(curMinRaw, startMin, endMin - duration);
                              const rect = plannerTrackInnerRef.current?.getBoundingClientRect();
                              if (!rect) return;
                              plannerDragMovedRef.current = false;
                              const curTop = (curMin - startMin) * pxPerMin;
                              const grabOffsetPx = e.clientY - rect.top - curTop;
                              try {
                                (e.currentTarget as any)?.setPointerCapture?.(e.pointerId);
                              } catch {}
                              setPlannerDrag({
                                eventId: item.id,
                                pointerId: e.pointerId,
                                grabOffsetPx,
                                startClientY: e.clientY,
                                startClientX: e.clientX,
                                durationMin: duration,
                                previewStartMin: curMin,
                              });
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                            onClick={(e) => {
                              if (isLoading) return;
                              if (plannerDragMovedRef.current) {
                                e.preventDefault();
                                e.stopPropagation();
                                return;
                              }
                              setPlannerOpen(false);
                              openEvent(item.id);
                            }}
                            disabled={isLoading}
                            aria-label={title}
                          >
                            <div className="PlannerEventMain">
                              <div className="PlannerEventTitle">{title}</div>
                              {isMatch && opponent ? <div className="PlannerEventVs">{`vs. ${opponent}`}</div> : null}
                              <div className="PlannerEventMeta">{subtitle}</div>
                              {coachLine ? <div className="PlannerEventMeta">{coachLine}</div> : null}
                              {item.is_individual && plannerTargetChildId ? (
                                <div
                                  className="PlannerChildAvatar PlannerChildAvatar--inline"
                                  aria-label={plannerChildLabelRaw ? `Podopieczny: ${plannerChildLabelRaw}` : 'Podopieczny'}
                                >
                                  {plannerChildPhotoUrl ? (
                                    <img className="PlannerChildAvatarImg" src={plannerChildPhotoUrl} alt="" />
                                  ) : (
                                    <div className="PlannerChildInitials">{plannerChildInitials}</div>
                                  )}
                                </div>
                              ) : null}
                            </div>
                            <div className="PlannerEventSide" aria-hidden="true">
                              <div className="PlannerEventIcon">{isMatch ? <SoccerIcon /> : <TrainingIcon />}</div>
                              {isLockedByOwner ? <div className="PlannerEventLock">🔒</div> : null}
                              <div className="PlannerEventArrow">›</div>
                            </div>
                          </button>
                        );
                        })}
                      </div>
                    </div>
                  </div>
                  {canUndo && (
                    <div className="PlannerUndoBar" role="status" aria-label="Cofnij zmianę">
                      <div className="PlannerUndoText">Zmieniono plan dnia</div>
                      <button className="PlannerUndoBtn" type="button" onClick={onPlannerUndo} disabled={isLoading}>
                        Cofnij
                      </button>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      )}

      {calendarPickChildReq && (
        <div
          className="SheetOverlay"
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => {
            if (isLoading) return;
            if (e.target === e.currentTarget) {
              setCalendarPickChildReq(null);
              setCalendarPickChildQuery('');
            }
          }}
        >
          <div className="Sheet" aria-label="Wybierz podopiecznego">
            <div className="SheetHeaderRow SheetHeaderRow--sticky">
              <div className="SheetTitle">
                {calendarPickChildReq.type === 'MATCH' ? 'WYBIERZ PODOPIECZNEGO — MECZ' : 'WYBIERZ PODOPIECZNEGO — TRENING'}
              </div>
              <button
                className="SheetCloseButton"
                type="button"
                aria-label="Zamknij"
                onClick={() => {
                  setCalendarPickChildReq(null);
                  setCalendarPickChildQuery('');
                }}
                disabled={isLoading}
              >
                ×
              </button>
            </div>
            <input
              className="TextInput"
              value={calendarPickChildQuery}
              onChange={(e) => setCalendarPickChildQuery(e.target.value)}
              placeholder="Szukaj podopiecznego..."
              disabled={isLoading}
            />
            <div className="List">
              {(props.coachChildren ?? [])
                .slice()
                .sort((a, b) => childLabel(a).localeCompare(childLabel(b), 'pl', { sensitivity: 'base' }))
                .filter((c) => {
                  const q = calendarPickChildQuery.trim().toLowerCase();
                  if (!q) return true;
                  const full = `${c.first_name} ${c.last_initial}`.trim().toLowerCase();
                  const group = (c.group_name ?? '').trim().toLowerCase();
                  return full.includes(q) || group.includes(q);
                })
                .map((c) => {
                  return (
                    <button
                      key={c.id}
                      className="ListItem"
                      type="button"
                      onClick={() => {
                        void (async () => {
                          if (isLoading) return;
                          let gid = (c.group_id ?? '').trim();
                          if (!gid) {
                            try {
                              const next = await apiCoachMyChildren(props.token);
                              const updated = next.find((x) => x.id === c.id) ?? null;
                              gid = (updated?.group_id ?? '').trim();
                            } catch {
                              gid = '';
                            }
                          }
                          const type = calendarPickChildReq.type;
                          setCalendarPickChildReq(null);
                          setCalendarPickChildQuery('');
                          setPendingIndividualCreate({ childId: c.id, type, groupId: (gid || 'individual').trim() });
                        })();
                      }}
                      disabled={isLoading}
                    >
                      <div className="ListItemTitle">{childLabel(c)}</div>
                      <div className="ListItemSubtitle">{(c.group_name ?? '').trim() || 'Grupa'}</div>
                    </button>
                  );
                })}
            </div>
          </div>
        </div>
      )}

      {plannerAddReq && (
        <div
          className="SheetOverlay"
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => {
            if (isLoading) return;
            if (e.target === e.currentTarget) setPlannerAddReq(null);
          }}
        >
          <div className="Sheet" aria-label="Dodaj wydarzenie">
            <div className="SheetHeaderRow SheetHeaderRow--sticky">
              <div className="SheetTitle">DODAJ WYDARZENIE</div>
              <button className="SheetCloseButton" type="button" aria-label="Zamknij" onClick={() => setPlannerAddReq(null)} disabled={isLoading}>
                ×
              </button>
            </div>
            <div className="Muted">Wybierz typ wydarzenia dla wybranego slotu.</div>
            <div className="SheetActionsRow">
              <PrimaryButton
                type="button"
                onClick={() => {
                  if (isLoading) return;
                  setPlannerOpen(false);
                  setPlannerAddReq(null);
                  openCreateTrainingAt(plannerAddReq.startMinutes, isCoachChildCalendar ? { groupId: coachFocusChild?.group_id ?? props.activeGroupId, targetChildId: coachFocusChildId } : undefined);
                }}
                disabled={isLoading || (props.role !== 'COACH' && props.role !== 'ADMIN') || (isCoachChildCalendar && !(coachFocusChild?.group_id ?? props.activeGroupId))}
              >
                TRENING
              </PrimaryButton>
              <SecondaryButton
                type="button"
                onClick={() => {
                  if (isLoading) return;
                  setPlannerOpen(false);
                  setPlannerAddReq(null);
                  openCreateMatchAt(plannerAddReq.startMinutes, isCoachChildCalendar ? { groupId: coachFocusChild?.group_id ?? props.activeGroupId, targetChildId: coachFocusChildId } : undefined);
                }}
                disabled={isLoading || (props.role !== 'COACH' && props.role !== 'ADMIN') || (isCoachChildCalendar && !(coachFocusChild?.group_id ?? props.activeGroupId))}
              >
                MECZ
              </SecondaryButton>
            </div>
            {props.role === 'COACH' && !isCoachChildCalendar && (props.coachChildren ?? []).length > 0 && (
              <>
                <div className="CoachSectionTitle">DLA PODOPIECZNEGO</div>
                <div className="SheetActionsRow">
                  <SecondaryButton
                    type="button"
                    onClick={() => {
                      setPlannerPickChildReq({ type: 'TRAINING', startMinutes: plannerAddReq.startMinutes });
                      setPlannerAddReq(null);
                    }}
                    disabled={isLoading}
                  >
                    TRENING INDYW.
                  </SecondaryButton>
                  <SecondaryButton
                    type="button"
                    onClick={() => {
                      setPlannerPickChildReq({ type: 'MATCH', startMinutes: plannerAddReq.startMinutes });
                      setPlannerAddReq(null);
                    }}
                    disabled={isLoading}
                  >
                    MECZ INDYW.
                  </SecondaryButton>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {plannerPickChildReq && (
        <div
          className="SheetOverlay"
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => {
            if (isLoading) return;
            if (e.target === e.currentTarget) {
              setPlannerPickChildReq(null);
              setPlannerPickChildQuery('');
            }
          }}
        >
          <div className="Sheet" aria-label="Wybierz podopiecznego">
            <div className="SheetHeaderRow SheetHeaderRow--sticky">
              <div className="SheetTitle">{plannerPickChildReq.type === 'MATCH' ? 'WYBIERZ PODOPIECZNEGO — MECZ' : 'WYBIERZ PODOPIECZNEGO — TRENING'}</div>
              <button
                className="SheetCloseButton"
                type="button"
                aria-label="Zamknij"
                onClick={() => {
                  setPlannerPickChildReq(null);
                  setPlannerPickChildQuery('');
                }}
                disabled={isLoading}
              >
                ×
              </button>
            </div>
            <input
              className="TextInput"
              value={plannerPickChildQuery}
              onChange={(e) => setPlannerPickChildQuery(e.target.value)}
              placeholder="Szukaj podopiecznego..."
              disabled={isLoading}
            />
            <div className="List">
              {(props.coachChildren ?? [])
                .slice()
                .sort((a, b) => childLabel(a).localeCompare(childLabel(b), 'pl', { sensitivity: 'base' }))
                .filter((c) => {
                  const q = plannerPickChildQuery.trim().toLowerCase();
                  if (!q) return true;
                  const full = `${c.first_name} ${c.last_initial}`.trim().toLowerCase();
                  const group = (c.group_name ?? '').trim().toLowerCase();
                  return full.includes(q) || group.includes(q);
                })
                .map((c) => {
                  return (
                    <button
                      key={c.id}
                      className="ListItem"
                      type="button"
                      onClick={() => {
                        void (async () => {
                          if (isLoading) return;
                          let gid = (c.group_id ?? '').trim();
                          if (!gid) {
                            try {
                              const next = await apiCoachMyChildren(props.token);
                              const updated = next.find((x) => x.id === c.id) ?? null;
                              gid = (updated?.group_id ?? '').trim();
                            } catch {
                              gid = '';
                            }
                          }
                          const startMin = plannerPickChildReq.startMinutes;
                          const type = plannerPickChildReq.type;
                          setPlannerOpen(false);
                          setPlannerPickChildReq(null);
                          setPlannerPickChildQuery('');
                          const gidFinal = (gid || 'individual').trim();
                          if (type === 'TRAINING') openCreateTrainingAt(startMin, { groupId: gidFinal, targetChildId: c.id });
                          else openCreateMatchAt(startMin, { groupId: gidFinal, targetChildId: c.id });
                        })();
                      }}
                      disabled={isLoading}
                    >
                      <div className="ListItemTop">
                        <div className="ListItemTitle">{childLabel(c).toUpperCase()}</div>
                      </div>
                      <div className="Muted">{(c.group_name ?? '').trim() ? `${c.group_name}${c.group_year ? ` (${c.group_year})` : ''}` : '—'}</div>
                    </button>
                  );
                })}
            </div>
          </div>
        </div>
      )}

      {isCreateMatchOpen && (props.role === 'COACH' || props.role === 'ADMIN') && (
        <div
          className="SheetOverlay"
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => {
            if (isLoading) return;
            if (e.target === e.currentTarget) {
              setIsCreateMatchOpen(false);
              setEditMatchEventId(null);
            }
          }}
        >
          <div className="Sheet Sheet--matchCreate">
            <div className="MatchCreateHeader">
              <div className="MatchCreateHeaderTitle">{isEditingMatch ? 'EDYTUJ WYDARZENIE: MECZ' : 'NOWE WYDARZENIE: MECZ'}</div>
              <button
                className="SheetCloseButton"
                type="button"
                onClick={() => {
                  setIsCreateMatchOpen(false);
                  setEditMatchEventId(null);
                }}
                disabled={isLoading}
                aria-label="Zamknij"
              >
                ×
              </button>
            </div>

            <div className="MatchCreateFields">
              {!!createTargetChildId && cmConflictEvents.length > 0 && (
                <div className="ErrorBanner">
                  {`Uwaga: mecz nakłada się na inne wydarzenie w grafiku (${cmConflictEvents.length}).`}
                  <div className="Muted">
                    {cmConflictEvents
                      .slice(0, 3)
                      .map((d) => `${d.type === 'MATCH' ? 'Mecz' : 'Trening'} • ${formatDateTime(d.start_at)} • ${d.location}`)
                      .join(' | ')}
                    {cmConflictEvents.length > 3 ? ` (+${cmConflictEvents.length - 3})` : ''}
                  </div>
                </div>
              )}
              <div
                className={['MatchCreateField', cmShowMissing && !cmGroupId.trim() ? 'is-missing' : ''].filter(Boolean).join(' ')}
              >
                <div className="MatchCreateFieldIcon" aria-hidden="true">
                  <UserIcon />
                </div>
                <div className="MatchCreateFieldBody">
                  <div className="MatchCreateFieldLabel">{createTargetChildId ? 'GRUPA' : 'WYBIERZ GRUPĘ'}</div>
                  {createTargetChildId ? (
                    <input
                      className="MatchCreateInput"
                      value={`${createTargetChild?.group_name ?? 'INDYWIDUALNE'}${createTargetChild?.group_year ? ` (${createTargetChild.group_year})` : ''}`}
                      disabled
                      readOnly
                    />
                  ) : (
                    <select
                      ref={cmGroupRef}
                      className="MatchCreateSelect"
                      value={cmGroupId}
                      onChange={(e) => setCmGroupId(e.target.value)}
                      disabled={isLoading || isEditingMatch}
                    >
                      {coachGroups.length === 0 ? (
                        <option value="">Brak grup</option>
                      ) : (
                        coachGroups.map((g) => (
                          <option key={g.id} value={g.id}>
                            {`${g.name}${g.year ? ` (${g.year})` : ''}`}
                          </option>
                        ))
                      )}
                    </select>
                  )}
                </div>
                {!createTargetChildId && (
                  <div className="MatchCreateFieldEnd" aria-hidden="true">
                    <span className="MatchCreateChevron">▾</span>
                  </div>
                )}
              </div>

              <div
                className={['MatchCreateField', cmShowMissing && !cmTitle.trim() ? 'is-missing' : ''].filter(Boolean).join(' ')}
              >
                <div className="MatchCreateFieldIcon" aria-hidden="true">
                  <SoccerIcon />
                </div>
                <div className="MatchCreateFieldBody">
                  <div className="MatchCreateFieldLabel">TYP MECZU</div>
                  <select
                    className="MatchCreateSelect"
                    value={cmMatchKind}
                    onChange={(e) => setCmMatchKind(e.target.value as MatchKind)}
                    disabled={isLoading}
                  >
                    <option value="LEAGUE">Mecz ligowy</option>
                    <option value="FRIENDLY">Mecz sparingowy</option>
                    <option value="TOURNAMENT">Turniej</option>
                    <option value="OTHER">Mecz inny</option>
                  </select>
                  <div className="MatchCreateFieldHint">Ta etykieta wyróżnia mecz w kalendarzu i w aktualnościach</div>
                </div>
              </div>

              <div
                className={['MatchCreateField', cmShowMissing && !cmTitle.trim() ? 'is-missing' : ''].filter(Boolean).join(' ')}
              >
                <div className="MatchCreateFieldIcon" aria-hidden="true">
                  <SoccerIcon />
                </div>
                <div className="MatchCreateFieldBody">
                  <div className="MatchCreateFieldLabel">TYTUŁ</div>
                  <input
                    ref={cmTitleRef}
                    className="MatchCreateInput"
                    value={cmTitle}
                    onChange={(e) => setCmTitle(e.target.value)}
                    placeholder={matchKindLabel(cmMatchKind)}
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div className="MatchCreateField MatchCreateField--highlight">
                <div className="MatchCreateFieldIcon" aria-hidden="true">
                  <SoccerIcon />
                </div>
                <div className="MatchCreateFieldBody">
                  <div className="MatchCreateFieldLabel">PRZECIWNIK</div>
                  <input
                    className="MatchCreateInput"
                    value={cmOpponent}
                    onChange={(e) => {
                      setCmOpponent(e.target.value);
                      setCmOpponentTeamId(null);
                    }}
                    placeholder="Wpisz nazwę klubu (np. Legia Warszawa)"
                    disabled={isLoading}
                  />
                  <div className="MatchCreateFieldHint">Krótka nazwa przeciwnika</div>
                </div>
                <div className="MatchCreateFieldEnd" aria-hidden="true">
                  <span className="MatchCreateBallMark">⚽</span>
                </div>
              </div>

              {cmOpponentSuggestions.length > 0 && (
                <div className="MatchCreateSuggestions" aria-label="Podpowiedzi przeciwnika">
                  {cmOpponentSuggestions.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      className="Pill"
                      onClick={() => {
                        setCmOpponent(t.name);
                        setCmOpponentTeamId(t.id);
                      }}
                      disabled={isLoading}
                    >
                      {t.name}
                    </button>
                  ))}
                </div>
              )}

              <div className="MatchCreateGrid2">
                <div
                  className={[
                    'MatchCreateField',
                    'MatchCreateField--compact',
                    cmShowMissing && !cmStartLocal.trim() ? 'is-missing' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <div className="MatchCreateFieldIcon" aria-hidden="true">
                    <ClockIcon />
                  </div>
                  <div className="MatchCreateFieldBody">
                    <div className="MatchCreateFieldLabel">DATA</div>
                    <input
                      ref={cmDateRef}
                      className="MatchCreateInput"
                      type="date"
                      value={cmStartParts.date}
                      onChange={(e) => setCmStartLocal(joinLocalDateTime(e.target.value, cmStartParts.time || '11:00'))}
                      disabled={isLoading}
                    />
                  </div>
                </div>

                <div
                  className={[
                    'MatchCreateField',
                    'MatchCreateField--compact',
                    cmShowMissing && !cmStartLocal.trim() ? 'is-missing' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <div className="MatchCreateFieldIcon" aria-hidden="true">
                    <ClockIcon />
                  </div>
                  <div className="MatchCreateFieldBody">
                    <div className="MatchCreateFieldLabel">GODZINA</div>
                    <input
                      ref={cmTimeRef}
                      className="MatchCreateInput"
                      type="time"
                      value={cmStartParts.time}
                      onChange={(e) => setCmStartLocal(joinLocalDateTime(cmStartParts.date || selectedDayKey, e.target.value))}
                      disabled={isLoading}
                    />
                  </div>
                </div>
              </div>

              <div className="MatchCreateField">
                <div className="MatchCreateFieldIcon" aria-hidden="true">
                  <ClockIcon />
                </div>
                <div className="MatchCreateFieldBody">
                  <div className="MatchCreateFieldLabel">GODZINA ZBIÓRKI (OPCJONALNIE)</div>
                  <input className="MatchCreateInput" type="time" value={cmMeetTime} onChange={(e) => setCmMeetTime(e.target.value)} disabled={isLoading} />
                  <div className="MatchCreateFieldHint">Np. 10:30 (domyślnie 30 min przed startem)</div>
                </div>
              </div>

              <div className="MatchCreateField">
                <div className="MatchCreateFieldIcon" aria-hidden="true">
                  <ClockIcon />
                </div>
                <div className="MatchCreateFieldBody">
                  <div className="MatchCreateFieldLabel">KONIEC (OPCJONALNIE)</div>
                  <div className="MatchCreateGrid2 MatchCreateGrid2--inside">
                    <input
                      className="MatchCreateInput"
                      type="date"
                      value={cmEndParts.date}
                      onChange={(e) => setCmEndLocal(e.target.value ? joinLocalDateTime(e.target.value, cmEndParts.time || '13:00') : '')}
                      disabled={isLoading}
                    />
                    <input
                      className="MatchCreateInput"
                      type="time"
                      value={cmEndParts.time}
                      onChange={(e) =>
                        setCmEndLocal(e.target.value ? joinLocalDateTime(cmEndParts.date || cmStartParts.date || selectedDayKey, e.target.value) : '')
                      }
                      disabled={isLoading}
                    />
                  </div>
                </div>
              </div>

              <label className="MatchCreateSwitchRow" aria-label="Termin do ustalenia">
                <span className="MatchCreateSwitchText">
                  <span className="MatchCreateSwitchTitle">DO USTALENIA</span>
                  <span className="MatchCreateSwitchSub">Termin lub godzina jest do ustalenia</span>
                </span>
                <span className="MatchCreateSwitchControl">
                  <span className="Switch">
                    <input
                      className="SwitchInput"
                      type="checkbox"
                      checked={cmIsTbd}
                      onChange={(e) => setCmIsTbd(e.target.checked)}
                      disabled={isLoading}
                    />
                    <span className="SwitchTrack">
                      <span className="SwitchThumb" />
                    </span>
                  </span>
                </span>
              </label>

              <div
                className={['MatchCreateField', cmShowMissing && !cmLocation.trim() ? 'is-missing' : ''].filter(Boolean).join(' ')}
              >
                <div className="MatchCreateFieldIcon" aria-hidden="true">
                  <MapPinIcon />
                </div>
                <div className="MatchCreateFieldBody">
                  <div className="MatchCreateFieldLabel">NAZWA STADIONU / MIEJSCE</div>
                  <input
                    ref={cmLocationRef}
                    className="MatchCreateInput"
                    value={cmLocation}
                    onChange={(e) => setCmLocation(e.target.value)}
                    placeholder="Stadion, adres lub miejsce zbiórki"
                    disabled={isLoading}
                  />
                  <div className="MatchCreateFieldInline">
                    <div className="MatchCreateFieldHint">Adres lub mapa</div>
                    <button
                      type="button"
                      className="MatchCreateInlineLink"
                      onClick={(e) => {
                        e.preventDefault();
                        const loc = cmLocation.trim();
                        if (!loc) return;
                        openExternalUrl(googleMapsUrlFromLocation(loc));
                      }}
                      disabled={isLoading || !cmLocation.trim()}
                    >
                      ZOBACZ NA MAPIE
                    </button>
                  </div>
                </div>
              </div>

              <div className="MatchCreateGrid2">
                <div
                  className={[
                    'MatchCreateField',
                    'MatchCreateField--compact',
                    cmShowMissing && cmFormula === 'INNA' && !cmHasValidMinPlayersOther ? 'is-missing' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <div className="MatchCreateFieldIcon" aria-hidden="true">
                    <SoccerIcon />
                  </div>
                  <div className="MatchCreateFieldBody">
                    <div className="MatchCreateFieldLabel">FORMA GRY</div>
                    <select
                      className="MatchCreateSelect"
                      value={cmFormula}
                      onChange={(e) => {
                        const v = (e.target.value || '') as typeof cmFormula;
                        setCmFormula(v);
                        if (v !== 'INNA') {
                          setCmFormulaOther('');
                          setCmMinPlayersOther('');
                        }
                      }}
                      disabled={isLoading}
                    >
                      <option value="">—</option>
                      <option value="5X5">5x5</option>
                      <option value="7X7">7x7</option>
                      <option value="9X9">9x9</option>
                      <option value="11X11">11x11</option>
                      <option value="INNA">inna</option>
                    </select>
                    {cmFormula === 'INNA' && (
                      <input
                        ref={cmMinPlayersRef}
                        className="MatchCreateInput"
                        type="number"
                        inputMode="numeric"
                        min={1}
                        max={30}
                        value={cmMinPlayersOther}
                        onChange={(e) => setCmMinPlayersOther(e.target.value)}
                        placeholder="Ilu zawodników potrzebujesz?"
                        disabled={isLoading}
                      />
                    )}
                  </div>
                  <div className="MatchCreateFieldEnd" aria-hidden="true">
                    <span className="MatchCreateChevron">▾</span>
                  </div>
                </div>

                <div className="MatchCreateField MatchCreateField--compact">
                  <div className="MatchCreateFieldIcon" aria-hidden="true">
                    <CheckIcon />
                  </div>
                  <div className="MatchCreateFieldBody">
                    <div className="MatchCreateFieldLabel">POTWIERDZENIE</div>
                    <label className="MatchCreateToggle">
                      <span className="MatchCreateToggleText">{cmNeedsConfirmation ? 'Włączone' : 'Wyłączone'}</span>
                      <span className="Switch">
                        <input
                          className="SwitchInput"
                          type="checkbox"
                          checked={cmNeedsConfirmation}
                          onChange={(e) => {
                            const next = e.target.checked;
                            setCmNeedsConfirmation(next);
                            if (!next) {
                              if (cmDeadlineLocal.trim()) cmDeadlineBackupRef.current = cmDeadlineLocal;
                              setCmDeadlineLocal('');
                              return;
                            }
                            if (!cmDeadlineLocal.trim()) {
                              const base = parseDateKey(selectedDayKey);
                              const deadline = new Date(base.getFullYear(), base.getMonth(), base.getDate() - 1, 20, 0, 0);
                              const restored = cmDeadlineBackupRef.current.trim();
                              setCmDeadlineLocal(restored ? restored : toLocalInputValue(deadline));
                            }
                          }}
                          disabled={isLoading}
                        />
                        <span className="SwitchTrack">
                          <span className="SwitchThumb" />
                        </span>
                      </span>
                    </label>
                    {cmNeedsConfirmation && (
                      <div className="MatchCreateDeadline">
                        <div className="MatchCreateDeadlineLabel">TERMIN POTWIERDZENIA</div>
                        <div className="MatchCreateGrid2 MatchCreateGrid2--inside">
                          <input
                            className="MatchCreateInput"
                            type="date"
                            value={cmDeadlineParts.date}
                            onChange={(e) => setCmDeadlineLocal(joinLocalDateTime(e.target.value, cmDeadlineParts.time || '20:00'))}
                            disabled={isLoading}
                          />
                          <input
                            className="MatchCreateInput"
                            type="time"
                            value={cmDeadlineParts.time}
                            onChange={(e) =>
                              setCmDeadlineLocal(joinLocalDateTime(cmDeadlineParts.date || cmStartParts.date || selectedDayKey, e.target.value))
                            }
                            disabled={isLoading}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="MatchCreateField">
                <div className="MatchCreateFieldIcon" aria-hidden="true">
                  <ChatIcon />
                </div>
                <div className="MatchCreateFieldBody">
                  <div className="MatchCreateFieldLabel">OPIS OPCJONALNY</div>
                  <textarea
                    className="MatchCreateTextArea"
                    rows={3}
                    value={cmDescription}
                    onChange={(e) => setCmDescription(e.target.value)}
                    placeholder="Dodatkowe informacje (np. stroje, transport...)"
                    disabled={isLoading}
                  />
                </div>
              </div>
            </div>

            <div className="MatchCreateActions">
              <div className="SheetActionsRow">
                <PrimaryButton
                  onClick={() => {
                    if (cmCanSubmit) {
                      setCmShowMissing(false);
                      onCreateMatchSubmit();
                      return;
                    }
                    showMatchMissing();
                  }}
                  disabled={isLoading}
                  aria-disabled={isLoading || !cmCanSubmit}
                  className={!cmCanSubmit ? 'is-disabled' : ''}
                  type="button"
                >
                  {isEditingMatch ? 'Zapisz mecz' : 'Dodaj mecz'}
                </PrimaryButton>
                <SecondaryButton
                  onClick={() => {
                    setIsCreateMatchOpen(false);
                    setEditMatchEventId(null);
                  }}
                  disabled={isLoading}
                  type="button"
                >
                  Anuluj
                </SecondaryButton>
              </div>
            </div>
          </div>
        </div>
      )}

      {isParentPrivateTrainingOpen && props.role === 'PARENT' && (
        <div
          className="SheetOverlay"
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => {
            if (isLoading) return;
            if (e.target === e.currentTarget) setIsParentPrivateTrainingOpen(false);
          }}
        >
          <div className="Sheet Sheet--trainingCreate">
            <div className="MatchCreateHeader">
              <div className="MatchCreateHeaderTitle">NOWE WYDARZENIE: TRENING PRYWATNY</div>
              <button
                className="SheetCloseButton"
                type="button"
                onClick={() => setIsParentPrivateTrainingOpen(false)}
                disabled={isLoading}
                aria-label="Zamknij"
              >
                ×
              </button>
            </div>

            <div className="MatchCreateFields">
              <div className="MatchCreateField">
                <div className="MatchCreateFieldIcon" aria-hidden="true">
                  <TrainingIcon />
                </div>
                <div className="MatchCreateFieldBody">
                  <div className="MatchCreateFieldLabel">TYTUŁ</div>
                  <input
                    className="MatchCreateInput"
                    value={pptTitle}
                    onChange={(e) => setPptTitle(e.target.value)}
                    placeholder="Np. Siłownia / Basen / Technika"
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div className="MatchCreateGrid2">
                <div className="MatchCreateField MatchCreateField--compact">
                  <div className="MatchCreateFieldIcon" aria-hidden="true">
                    <ClockIcon />
                  </div>
                  <div className="MatchCreateFieldBody">
                    <div className="MatchCreateFieldLabel">START</div>
                    <input
                      className="MatchCreateInput"
                      type="datetime-local"
                      value={pptStartLocal}
                      onChange={(e) => setPptStartLocal(e.target.value)}
                      disabled={isLoading}
                    />
                  </div>
                </div>

                <div className="MatchCreateField MatchCreateField--compact">
                  <div className="MatchCreateFieldIcon" aria-hidden="true">
                    <ClockIcon />
                  </div>
                  <div className="MatchCreateFieldBody">
                    <div className="MatchCreateFieldLabel">KONIEC (OPCJONALNIE)</div>
                    <input
                      className="MatchCreateInput"
                      type="datetime-local"
                      value={pptEndLocal}
                      onChange={(e) => setPptEndLocal(e.target.value)}
                      disabled={isLoading}
                    />
                  </div>
                </div>
              </div>

              <div className="MatchCreateField">
                <div className="MatchCreateFieldIcon" aria-hidden="true">
                  <MapPinIcon />
                </div>
                <div className="MatchCreateFieldBody">
                  <div className="MatchCreateFieldLabel">MIEJSCE</div>
                  <input
                    className="MatchCreateInput"
                    value={pptLocation}
                    onChange={(e) => setPptLocation(e.target.value)}
                    placeholder="Np. OSiR / Prywatnie"
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div className="MatchCreateField">
                <div className="MatchCreateFieldIcon" aria-hidden="true">
                  <ChatIcon />
                </div>
                <div className="MatchCreateFieldBody">
                  <div className="MatchCreateFieldLabel">OPIS OPCJONALNY</div>
                  <textarea
                    className="MatchCreateTextArea"
                    rows={3}
                    value={pptDescription}
                    onChange={(e) => setPptDescription(e.target.value)}
                    placeholder="Dodatkowe informacje (np. plan, kontakt...)"
                    disabled={isLoading}
                  />
                </div>
              </div>
            </div>

            <div className="MatchCreateActions">
              <div className="SheetActionsRow">
                <PrimaryButton
                  onClick={() => {
                    if (pptCanSubmit) onParentPrivateTrainingSubmit();
                  }}
                  disabled={isLoading}
                  aria-disabled={isLoading || !pptCanSubmit}
                  className={!pptCanSubmit ? 'is-disabled' : ''}
                  type="button"
                >
                  Dodaj trening prywatny
                </PrimaryButton>
                <SecondaryButton onClick={() => setIsParentPrivateTrainingOpen(false)} disabled={isLoading} type="button">
                  Anuluj
                </SecondaryButton>
              </div>
            </div>
          </div>
        </div>
      )}

      {isCreateTrainingOpen && (props.role === 'COACH' || props.role === 'ADMIN') && (
        <div
          className="SheetOverlay"
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => {
            if (isLoading) return;
            if (e.target === e.currentTarget) setIsCreateTrainingOpen(false);
          }}
        >
          <div className="Sheet Sheet--trainingCreate">
            <div className="MatchCreateHeader">
              <div className="MatchCreateHeaderTitle">NOWE WYDARZENIE: TRENING</div>
              <button
                className="SheetCloseButton"
                type="button"
                onClick={() => setIsCreateTrainingOpen(false)}
                disabled={isLoading}
                aria-label="Zamknij"
              >
                ×
              </button>
            </div>

            <div className="MatchCreateFields">
              {!!createTargetChildId && ctConflictEvents.length > 0 && (
                <div className="ErrorBanner">
                  {`Uwaga: trening nakłada się na inne wydarzenie w grafiku (${ctConflictEvents.length}).`}
                  <div className="Muted">
                    {ctConflictEvents
                      .slice(0, 3)
                      .map((d) => `${d.type === 'MATCH' ? 'Mecz' : 'Trening'} • ${formatDateTime(d.start_at)} • ${d.location}`)
                      .join(' | ')}
                    {ctConflictEvents.length > 3 ? ` (+${ctConflictEvents.length - 3})` : ''}
                  </div>
                </div>
              )}
              <div className="MatchCreateField">
                <div className="MatchCreateFieldIcon" aria-hidden="true">
                  <UserIcon />
                </div>
                <div className="MatchCreateFieldBody">
                  <div className="MatchCreateFieldLabel">{createTargetChildId ? 'GRUPA' : 'WYBIERZ GRUPĘ'}</div>
                  {createTargetChildId ? (
                    <input
                      className="MatchCreateInput"
                      value={`${createTargetChild?.group_name ?? 'INDYWIDUALNE'}${createTargetChild?.group_year ? ` (${createTargetChild.group_year})` : ''}`}
                      disabled
                      readOnly
                    />
                  ) : (
                    <select className="MatchCreateSelect" value={ctGroupId} onChange={(e) => setCtGroupId(e.target.value)} disabled={isLoading}>
                      {coachGroups.length === 0 ? (
                        <option value="">Brak grup</option>
                      ) : (
                        coachGroups.map((g) => (
                          <option key={g.id} value={g.id}>
                            {`${g.name}${g.year ? ` (${g.year})` : ''}`}
                          </option>
                        ))
                      )}
                    </select>
                  )}
                </div>
                {!createTargetChildId && (
                  <div className="MatchCreateFieldEnd" aria-hidden="true">
                    <span className="MatchCreateChevron">▾</span>
                  </div>
                )}
              </div>

              <div className="MatchCreateField">
                <div className="MatchCreateFieldIcon" aria-hidden="true">
                  <TrainingIcon />
                </div>
                <div className="MatchCreateFieldBody">
                  <div className="MatchCreateFieldLabel">TYTUŁ</div>
                  <input
                    className="MatchCreateInput"
                    value={ctTitle}
                    onChange={(e) => setCtTitle(e.target.value)}
                    placeholder="Np. Trening"
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div className="MatchCreateGrid2">
                <div className="MatchCreateField MatchCreateField--compact">
                  <div className="MatchCreateFieldIcon" aria-hidden="true">
                    <ClockIcon />
                  </div>
                  <div className="MatchCreateFieldBody">
                    <div className="MatchCreateFieldLabel">START</div>
                    <input
                      className="MatchCreateInput"
                      type="datetime-local"
                      value={ctStartLocal}
                      onChange={(e) => setCtStartLocal(e.target.value)}
                      disabled={isLoading}
                    />
                  </div>
                </div>

                <div className="MatchCreateField MatchCreateField--compact">
                  <div className="MatchCreateFieldIcon" aria-hidden="true">
                    <ClockIcon />
                  </div>
                  <div className="MatchCreateFieldBody">
                    <div className="MatchCreateFieldLabel">KONIEC (OPCJONALNIE)</div>
                    <input
                      className="MatchCreateInput"
                      type="datetime-local"
                      value={ctEndLocal}
                      onChange={(e) => setCtEndLocal(e.target.value)}
                      disabled={isLoading}
                    />
                  </div>
                </div>
              </div>

              <div className="MatchCreateField">
                <div className="MatchCreateFieldIcon" aria-hidden="true">
                  <MapPinIcon />
                </div>
                <div className="MatchCreateFieldBody">
                  <div className="MatchCreateFieldLabel">MIEJSCE</div>
                  <input
                    className="MatchCreateInput"
                    value={ctLocation}
                    onChange={(e) => setCtLocation(e.target.value)}
                    placeholder="Wymagane"
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div className="MatchCreateField">
                <div className="MatchCreateFieldIcon" aria-hidden="true">
                  <UserIcon />
                </div>
                <div className="MatchCreateFieldBody">
                  <div className="MatchCreateFieldLabel">TRENERZY</div>
                  <div className="MatchCreateFieldHint">Automatycznie z grupy</div>
                </div>
              </div>

              <div className="MatchCreateField">
                <div className="MatchCreateFieldIcon" aria-hidden="true">
                  <ChatIcon />
                </div>
                <div className="MatchCreateFieldBody">
                  <div className="MatchCreateFieldLabel">OPIS OPCJONALNY</div>
                  <textarea
                    className="MatchCreateTextArea"
                    rows={3}
                    value={ctDescription}
                    onChange={(e) => setCtDescription(e.target.value)}
                    placeholder="Dodatkowe informacje"
                    disabled={isLoading}
                  />
                </div>
              </div>

              <label className="MatchCreateSwitchRow" aria-label="Cykliczny trening">
                <span className="MatchCreateSwitchText">
                  <span className="MatchCreateSwitchTitle">CYKLICZNY TRENING</span>
                  <span className="MatchCreateSwitchSub">
                    {ctRecurring ? `Utworzy: ${Math.min(52, ctOccurrences)} treningów` : 'Opcjonalnie: utwórz cykl treningów'}
                  </span>
                </span>
                <span className="Switch">
                  <input
                    className="SwitchInput"
                    type="checkbox"
                    checked={ctRecurring}
                    onChange={(e) => setCtRecurring(e.target.checked)}
                    disabled={isLoading || !!createTargetChildId}
                  />
                  <span className="SwitchTrack" aria-hidden="true">
                    <span className="SwitchThumb" aria-hidden="true" />
                  </span>
                </span>
              </label>

              {ctRecurring && (
                <div className="MatchCreateGrid2">
                  <div className="MatchCreateField MatchCreateField--compact">
                    <div className="MatchCreateFieldIcon" aria-hidden="true">
                      <ClockIcon />
                    </div>
                    <div className="MatchCreateFieldBody">
                      <div className="MatchCreateFieldLabel">CO ILE TYGODNI</div>
                      <select
                        className="MatchCreateSelect"
                        value={ctIntervalWeeks}
                        onChange={(e) => setCtIntervalWeeks(e.target.value)}
                        disabled={isLoading}
                      >
                        {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                          <option key={n} value={String(n)}>
                            {n}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="MatchCreateField MatchCreateField--compact">
                    <div className="MatchCreateFieldIcon" aria-hidden="true">
                      <ClockIcon />
                    </div>
                    <div className="MatchCreateFieldBody">
                      <div className="MatchCreateFieldLabel">PRZEZ ILE TYGODNI</div>
                      <input
                        className="MatchCreateInput"
                        type="number"
                        min={1}
                        value={ctDurationWeeks}
                        onChange={(e) => setCtDurationWeeks(e.target.value)}
                        onBlur={() => {
                          const n = Number.parseInt(ctDurationWeeks, 10);
                          if (!Number.isFinite(n) || n < 1) {
                            setCtDurationWeeks('1');
                            return;
                          }
                          if (n > ctMaxDurationWeeks) {
                            setCtDurationWeeks(String(ctMaxDurationWeeks));
                            return;
                          }
                          setCtDurationWeeks(String(n));
                        }}
                        disabled={isLoading}
                      />
                    </div>
                  </div>
                </div>
              )}

              {!ctLocation.trim() && <div className="Muted">Uzupełnij pole: Miejsce</div>}
              {ctRecurringInvalid && <div className="Muted">{ctRecurringInvalid}</div>}
            </div>

            <div className="MatchCreateActions">
              <div className="SheetActionsRow">
                <PrimaryButton
                  onClick={onCreateTrainingSubmit}
                  disabled={
                    isLoading ||
                    !ctGroupId.trim() ||
                    !ctTitle.trim() ||
                    !ctStartLocal.trim() ||
                    !ctLocation.trim() ||
                    !!ctRecurringInvalid
                  }
                  type="button"
                >
                  Dodaj trening
                </PrimaryButton>
                <SecondaryButton onClick={() => setIsCreateTrainingOpen(false)} disabled={isLoading} type="button">
                  Anuluj
                </SecondaryButton>
              </div>
            </div>
          </div>
        </div>
      )}

      {isEditTrainingOpen && (props.role === 'COACH' || props.role === 'ADMIN') && (
        <div
          className="SheetOverlay"
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => {
            if (isLoading) return;
            if (e.target === e.currentTarget) setIsEditTrainingOpen(false);
          }}
        >
          <div className="Sheet Sheet--trainingCreate">
            <div className="MatchCreateHeader">
              <div className="MatchCreateHeaderTitle">EDYTUJ WYDARZENIE: TRENING</div>
              <button
                className="SheetCloseButton"
                type="button"
                onClick={() => setIsEditTrainingOpen(false)}
                disabled={isLoading}
                aria-label="Zamknij"
              >
                ×
              </button>
            </div>

            <div className="MatchCreateFields">
              {etConflictEvents.length > 0 && (
                <div className="ErrorBanner">
                  {`Uwaga: trening nakłada się na inne wydarzenie w grafiku (${etConflictEvents.length}).`}
                  <div className="Muted">
                    {etConflictEvents
                      .slice(0, 3)
                      .map((d) => `${d.type === 'MATCH' ? 'Mecz' : 'Trening'} • ${formatDateTime(d.start_at)} • ${d.location}`)
                      .join(' | ')}
                    {etConflictEvents.length > 3 ? ` (+${etConflictEvents.length - 3})` : ''}
                  </div>
                </div>
              )}
              <div className="MatchCreateField">
                <div className="MatchCreateFieldIcon" aria-hidden="true">
                  <TrainingIcon />
                </div>
                <div className="MatchCreateFieldBody">
                  <div className="MatchCreateFieldLabel">TYTUŁ</div>
                  <input
                    className="MatchCreateInput"
                    value={etTitle}
                    onChange={(e) => setEtTitle(e.target.value)}
                    placeholder="Np. Trening"
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div className="MatchCreateGrid2">
                <div className="MatchCreateField MatchCreateField--compact">
                  <div className="MatchCreateFieldIcon" aria-hidden="true">
                    <ClockIcon />
                  </div>
                  <div className="MatchCreateFieldBody">
                    <div className="MatchCreateFieldLabel">START</div>
                    <input
                      className="MatchCreateInput"
                      type="datetime-local"
                      value={etStartLocal}
                      onChange={(e) => setEtStartLocal(e.target.value)}
                      disabled={isLoading}
                    />
                  </div>
                </div>

                <div className="MatchCreateField MatchCreateField--compact">
                  <div className="MatchCreateFieldIcon" aria-hidden="true">
                    <ClockIcon />
                  </div>
                  <div className="MatchCreateFieldBody">
                    <div className="MatchCreateFieldLabel">KONIEC (OPCJONALNIE)</div>
                    <input
                      className="MatchCreateInput"
                      type="datetime-local"
                      value={etEndLocal}
                      onChange={(e) => setEtEndLocal(e.target.value)}
                      disabled={isLoading}
                    />
                  </div>
                </div>
              </div>

              <div className="MatchCreateField">
                <div className="MatchCreateFieldIcon" aria-hidden="true">
                  <MapPinIcon />
                </div>
                <div className="MatchCreateFieldBody">
                  <div className="MatchCreateFieldLabel">MIEJSCE</div>
                  <input
                    className="MatchCreateInput"
                    value={etLocation}
                    onChange={(e) => setEtLocation(e.target.value)}
                    placeholder="Wymagane"
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div className="MatchCreateField">
                <div className="MatchCreateFieldIcon" aria-hidden="true">
                  <UserIcon />
                </div>
                <div className="MatchCreateFieldBody">
                  <div className="MatchCreateFieldLabel">TRENERZY</div>
                  <div className="MatchCreateFieldHint">Automatycznie z grupy</div>
                </div>
              </div>

              <div className="MatchCreateField">
                <div className="MatchCreateFieldIcon" aria-hidden="true">
                  <ChatIcon />
                </div>
                <div className="MatchCreateFieldBody">
                  <div className="MatchCreateFieldLabel">OPIS OPCJONALNY</div>
                  <textarea
                    className="MatchCreateTextArea"
                    rows={3}
                    value={etDescription}
                    onChange={(e) => setEtDescription(e.target.value)}
                    placeholder="Dodatkowe informacje"
                    disabled={isLoading}
                  />
                </div>
              </div>

              {details?.type === 'TRAINING' && details.series_id && (
                <label className="MatchCreateSwitchRow" aria-label="Zastosuj do cyklu treningów">
                  <span className="MatchCreateSwitchText">
                    <span className="MatchCreateSwitchTitle">ZASTOSUJ DO CYKLU</span>
                    <span className="MatchCreateSwitchSub">Zmiany od tego treningu (włącznie)</span>
                  </span>
                  <span className="Switch">
                    <input
                      className="SwitchInput"
                      type="checkbox"
                      checked={etApplyToSeries}
                      onChange={(e) => setEtApplyToSeries(e.target.checked)}
                      disabled={isLoading}
                    />
                    <span className="SwitchTrack" aria-hidden="true">
                      <span className="SwitchThumb" aria-hidden="true" />
                    </span>
                  </span>
                </label>
              )}
            </div>

            <div className="MatchCreateActions">
              <div className="SheetActionsRow">
                <PrimaryButton
                  onClick={onEditTrainingSubmit}
                  disabled={isLoading || !etTitle.trim() || !etStartLocal.trim() || !etLocation.trim()}
                  type="button"
                >
                  Zapisz trening
                </PrimaryButton>
                <SecondaryButton onClick={() => setIsEditTrainingOpen(false)} disabled={isLoading} type="button">
                  Anuluj
                </SecondaryButton>
              </div>
            </div>
          </div>
        </div>
      )}


      {isManageMatchOpen && (props.role === 'COACH' || props.role === 'ADMIN') && details?.type === 'MATCH' && (
        <div
          className="SheetOverlay"
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => {
            if (isLoading) return;
            if (e.target === e.currentTarget) setIsManageMatchOpen(false);
          }}
        >
          <div className="Sheet">
            <div className="SheetHeaderRow SheetHeaderRow--sticky">
              <div className="SheetTitle">MECZ: ODWOŁAJ LUB USUŃ</div>
              <button className="SheetCloseButton" type="button" aria-label="Zamknij" onClick={() => setIsManageMatchOpen(false)} disabled={isLoading}>
                ×
              </button>
            </div>
            <div className="Muted">
              Odwołanie pozostawia mecz w historii (status ODWOŁANY). Usunięcie usuwa mecz bezpowrotnie.
            </div>
            <PrimaryButton
              onClick={() => {
                setIsManageMatchOpen(false);
                setIsCancelMatchConfirmOpen(true);
              }}
              disabled={isLoading || !details.can_take_action}
              type="button"
            >
              Odwołaj mecz
            </PrimaryButton>
            <SecondaryButton
              onClick={() => {
                setIsManageMatchOpen(false);
                setIsDeleteEventConfirmOpen(true);
              }}
              disabled={isLoading}
              type="button"
            >
              Usuń mecz
            </SecondaryButton>
            <SecondaryButton onClick={() => setIsManageMatchOpen(false)} disabled={isLoading} type="button">
              Anuluj
            </SecondaryButton>
          </div>
        </div>
      )}

      {isCancelMatchConfirmOpen && (props.role === 'COACH' || props.role === 'ADMIN') && (
        <div
          className="SheetOverlay"
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => {
            if (isLoading) return;
            if (e.target === e.currentTarget) setIsCancelMatchConfirmOpen(false);
          }}
        >
          <div className="Sheet">
            <div className="SheetHeaderRow SheetHeaderRow--sticky">
              <div className="SheetTitle">ODWOŁAĆ MECZ?</div>
              <button
                className="SheetCloseButton"
                type="button"
                aria-label="Zamknij"
                onClick={() => setIsCancelMatchConfirmOpen(false)}
                disabled={isLoading}
              >
                ×
              </button>
            </div>
            <div className="Muted">Mecz zostanie oznaczony jako ODWOŁANY i pozostanie w historii.</div>
            <PrimaryButton onClick={onCancelMatch} disabled={isLoading} type="button">
              Odwołaj mecz
            </PrimaryButton>
            <SecondaryButton onClick={() => setIsCancelMatchConfirmOpen(false)} disabled={isLoading} type="button">
              Anuluj
            </SecondaryButton>
          </div>
        </div>
      )}

      {isCancelTrainingConfirmOpen && (props.role === 'COACH' || props.role === 'ADMIN') && (
        <div
          className="SheetOverlay"
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => {
            if (isLoading) return;
            if (e.target === e.currentTarget) setIsCancelTrainingConfirmOpen(false);
          }}
        >
          <div className="Sheet">
            <div className="SheetHeaderRow SheetHeaderRow--sticky">
              <div className="SheetTitle">ODWOŁAĆ TRENING?</div>
              <button
                className="SheetCloseButton"
                type="button"
                aria-label="Zamknij"
                onClick={() => setIsCancelTrainingConfirmOpen(false)}
                disabled={isLoading}
              >
                ×
              </button>
            </div>
            <div className="Muted">Trening zostanie oznaczony jako ODWOŁANY i pozostanie w historii.</div>
            <PrimaryButton onClick={onCancelTraining} disabled={isLoading} type="button">
              Odwołaj trening
            </PrimaryButton>
            <SecondaryButton onClick={() => setIsCancelTrainingConfirmOpen(false)} disabled={isLoading} type="button">
              Anuluj
            </SecondaryButton>
          </div>
        </div>
      )}

      {isDeleteEventConfirmOpen && (props.role === 'COACH' || props.role === 'ADMIN') && details && (
        <div
          className="SheetOverlay"
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => {
            if (isLoading) return;
            if (e.target === e.currentTarget) setIsDeleteEventConfirmOpen(false);
          }}
        >
          <div className="Sheet">
            <div className="SheetHeaderRow SheetHeaderRow--sticky">
              <div className="SheetTitle">{(details.type === 'MATCH' ? 'USUNĄĆ MECZ?' : 'USUNĄĆ TRENING?').toUpperCase()}</div>
              <button
                className="SheetCloseButton"
                type="button"
                aria-label="Zamknij"
                onClick={() => setIsDeleteEventConfirmOpen(false)}
                disabled={isLoading}
              >
                ×
              </button>
            </div>
            <div className="Muted">To działanie jest nieodwracalne. Znikną też powiązane wpisy systemowe w Aktualnościach.</div>
            <PrimaryButton onClick={onDeleteEvent} disabled={isLoading} type="button">
              Usuń
            </PrimaryButton>
            <SecondaryButton onClick={() => setIsDeleteEventConfirmOpen(false)} disabled={isLoading} type="button">
              Anuluj
            </SecondaryButton>
          </div>
        </div>
      )}

      {callupEventId && (props.role === 'COACH' || props.role === 'ADMIN') && (
        <div
          className="SheetOverlay"
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => {
            if (isLoading) return;
            if (e.target === e.currentTarget) setCallupEventId(null);
          }}
        >
          <div className="Sheet Sheet--callups">
            <div className="CallupHeader">
              <div className="SheetHeaderRow SheetHeaderRow--sticky">
                <div className="CallupTitle">POWOŁANIA NA MECZ</div>
                <button
                  className="SheetCloseButton"
                  type="button"
                  onClick={() => setCallupEventId(null)}
                  disabled={isLoading}
                  aria-label="Zamknij"
                >
                  ×
                </button>
              </div>
              {details && (
                <div className="CallupMeta">
                  <div className="CallupMetaLine">{details.title}</div>
                  <div className="CallupMetaLine is-sub">{`${formatDateTime(details.start_at)} • ${details.location}`}</div>
                </div>
              )}
            </div>

            <div className="CallupFilters">
              <button
                className={`Pill ${callupFilter === 'ALL' ? 'is-active' : ''}`}
                onClick={() => setCallupFilter('ALL')}
                disabled={isLoading}
                type="button"
              >
                {`Zawodnicy (${callupStats.all})`}
              </button>
              <button
                className={`Pill ${callupFilter === 'CALLED_UP' ? 'is-active' : ''}`}
                onClick={() => setCallupFilter('CALLED_UP')}
                disabled={isLoading}
                type="button"
              >
                {`Powołani (${callupStats.called})`}
              </button>
              <button
                className={`Pill ${callupFilter === 'PENDING' ? 'is-active' : ''}`}
                onClick={() => setCallupFilter('PENDING')}
                disabled={isLoading}
                type="button"
              >
                {`Oczekuje (${callupStats.pending})`}
              </button>
              <button
                className={`Pill ${callupFilter === 'ACCEPTED' ? 'is-active' : ''}`}
                onClick={() => setCallupFilter('ACCEPTED')}
                disabled={isLoading}
                type="button"
              >
                {`Potwierdzone (${callupStats.accepted})`}
              </button>
              <button
                className={`Pill ${callupFilter === 'DECLINED' ? 'is-active' : ''}`}
                onClick={() => setCallupFilter('DECLINED')}
                disabled={isLoading}
                type="button"
              >
                {`Nieobecny (${callupStats.declined})`}
              </button>
              <button
                className="Pill is-cta"
                onClick={onCallupSelectAll}
                disabled={isLoading || callupRoster.length === 0}
                type="button"
              >
                Powołaj wszystkich
              </button>
            </div>
            {callupInfoText && <div className="InfoBanner">{callupInfoText}</div>}

            <div className="CallupList" role="list">
              {callupRoster.length === 0 && <div className="Muted">Brak zawodników w grupie.</div>}
              {filteredCallupRoster.map((c) => {
                const eventTs = (() => {
                  const dt = details?.start_at ? new Date(details.start_at) : null;
                  return dt && !Number.isNaN(dt.getTime()) ? dt.getTime() : Date.now();
                })();
                const isCalledUp = !!callupSelected[c.id];
                const s = details?.callup_rsvps?.[c.id] ?? 'PENDING';
                const reason = details?.callup_rsvp_reasons?.[c.id] ?? null;
                const initials = `${(c.first_name || '').slice(0, 1)}${(c.last_initial || '').slice(0, 1)}`.toUpperCase();
                const photo = callupPhotoById[c.id] ?? null;
                const unavailableKind = (c.unavailability_kind ?? '').trim();
                const isUnavailable = (() => {
                  if (!unavailableKind) return false;
                  const fromRaw = (c.unavailability_from ?? '').trim();
                  const untilRaw = (c.unavailability_until ?? '').trim();
                  const fromKey = fromRaw ? fromRaw.slice(0, 10) : '';
                  const untilKey = untilRaw ? untilRaw.slice(0, 10) : '';
                  const fromMs = fromKey ? Date.parse(`${fromKey}T00:00:00Z`) : Number.NEGATIVE_INFINITY;
                  const untilMs = untilKey ? Date.parse(`${untilKey}T23:59:59.999Z`) : Number.POSITIVE_INFINITY;
                  if (Number.isNaN(fromMs) || Number.isNaN(untilMs)) return true;
                  return eventTs >= fromMs && eventTs <= untilMs;
                })();
                const unavailableLabel = unavailableKind === 'KONTUZJA' ? 'kontuzja' : unavailableKind ? 'nb' : '';
                const absBadge = isUnavailable ? callupAbsenceBadgeLabel(c) : null;
                return (
                  <div key={c.id} className="CallupRow" role="listitem">
                    <div className={['CallupAvatar', isUnavailable ? 'is-unavailable' : ''].filter(Boolean).join(' ')} aria-hidden="true">
                      {photo ? <img src={photo} alt="" /> : initials || '•'}
                      {isUnavailable && unavailableLabel && <div className="CallupAvatarBadge">{unavailableLabel}</div>}
                    </div>
                    <div className="CallupMain">
                      <div className="CallupName">{`${c.first_name} ${c.last_initial}`}</div>
                      {absBadge ? (
                        <div className="CallupBadges">
                          <div className="CallupAbsenceBadge">{absBadge}</div>
                        </div>
                      ) : null}
                      {isCalledUp && (
                        <div className="CallupSub">
                          <span className={`CallupStatus is-${s.toLowerCase()}`}>
                            {s === 'ACCEPTED' ? 'POTWIERDZONE' : s === 'DECLINED' ? 'NIEOBECNY' : 'OCZEKUJE'}
                          </span>
                          {s === 'DECLINED' && reason && reason.trim() ? <span className="CallupReason">{reason.trim()}</span> : null}
                        </div>
                      )}
                    </div>
                    <label className="Switch" aria-label={`Powołaj: ${c.first_name} ${c.last_initial}`}>
                      <input
                        className="SwitchInput"
                        type="checkbox"
                        checked={isCalledUp}
                        onChange={(e) => setCallupSelected((prev) => ({ ...prev, [c.id]: e.target.checked }))}
                        disabled={isLoading || isUnavailable}
                      />
                      <span className="SwitchTrack" aria-hidden="true">
                        <span className="SwitchThumb" aria-hidden="true" />
                      </span>
                    </label>
                  </div>
                );
              })}
            </div>

            <div className="SheetActions" aria-label="Akcje powołań">
              <div className="Muted">{`Wybrani zawodnicy: ${callupStats.called}/${callupStats.all}`}</div>
              <div className="SheetActionsRow">
                <PrimaryButton onClick={onSaveCallups} disabled={isLoading} type="button">
                  Zapisz i wyślij
                </PrimaryButton>
                <SecondaryButton onClick={() => setCallupEventId(null)} disabled={isLoading} type="button">
                  Zamknij
                </SecondaryButton>
              </div>
            </div>
          </div>
        </div>
      )}

      {!selectedId && (
        <>
          <UpcomingMatchCard
            token={props.token}
            childId={isChildScoped ? props.childId ?? null : null}
            groupId={props.role === 'COACH' || props.role === 'ADMIN' ? props.activeGroupId : null}
            events={events}
            disabled={isLoading}
            sticky={isLiveSticky}
            onManageCallups={
              props.role === 'COACH' || props.role === 'ADMIN' ? (isCoachChildCalendar ? undefined : openCallupsForEvent) : undefined
            }
          />
          {(props.role === 'COACH' || props.role === 'ADMIN') && (
            <section className="CoachCalendarManage" aria-label="Zarządzanie kalendarzem" ref={manageSectionRef}>
              {isCoachChildCalendar ? (
                <>
                  <div className="CoachManageHeader">ZARZĄDZANIE PODOPIECZNYM</div>
                  <div className="CoachManageActions">
                    <button
                      className="InlineCta"
                      onClick={() =>
                        openCreateMatch({ groupId: coachFocusChild?.group_id ?? props.activeGroupId, targetChildId: coachFocusChildId })
                      }
                      disabled={isLoading || !(coachFocusChild?.group_id ?? props.activeGroupId)}
                      type="button"
                    >
                      DODAJ DODATKOWY MECZ
                    </button>
                    <button
                      className="CoachPillButton"
                      onClick={() =>
                        openCreateTraining({ groupId: coachFocusChild?.group_id ?? props.activeGroupId, targetChildId: coachFocusChildId })
                      }
                      disabled={isLoading || !(coachFocusChild?.group_id ?? props.activeGroupId)}
                      type="button"
                    >
                      DODAJ TRENING INDYW.
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {isCoachIndividualMode ? (
                    <>
                      <div className="CoachManageHeader">ZARZĄDZANIE PODOPIECZNYMI</div>
                      <div className="CoachManageActions">
                        <button
                          className="InlineCta"
                          onClick={() => {
                            setCalendarPickChildReq({ type: 'MATCH' });
                            setCalendarPickChildQuery('');
                          }}
                          disabled={isLoading || (props.coachChildren ?? []).length === 0}
                          type="button"
                        >
                          DODAJ MECZ INDYW.
                        </button>
                        <button
                          className="CoachPillButton"
                          onClick={() => {
                            setCalendarPickChildReq({ type: 'TRAINING' });
                            setCalendarPickChildQuery('');
                          }}
                          disabled={isLoading || (props.coachChildren ?? []).length === 0}
                          type="button"
                        >
                          DODAJ TRENING INDYW.
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="CoachManageHeader">ZARZĄDZANIE DRUŻYNĄ</div>
                      <div className="CoachManageActions">
                        <button className="InlineCta" onClick={() => openCreateMatch()} disabled={isLoading || coachGroups.length === 0} type="button">
                          DODAJ NOWY MECZ
                        </button>
                        <button className="CoachPillButton" onClick={() => openCreateTraining()} disabled={isLoading || coachGroups.length === 0} type="button">
                          DODAJ TRENING
                        </button>
                      </div>
                    </>
                  )}
                </>
              )}
            </section>
          )}
          {(props.role === 'COACH' || props.role === 'ADMIN') && (
            <div className="CalendarFabGroup" role="region" aria-label="Szybkie akcje kalendarza">
              <button
                type="button"
                className={['CalendarFab', 'is-planner'].join(' ')}
                onClick={() => setPlannerOpen(true)}
                disabled={isLoading}
                aria-label="Plan dnia (widok godzinowy)"
              >
                <ClockIcon className="CalendarFabIcon" />
              </button>
              <button
                type="button"
                className={['CalendarFab', 'is-match'].filter(Boolean).join(' ')}
                onClick={() => {
                  if (isCoachChildCalendar) {
                    openCreateMatch({ groupId: coachFocusChild?.group_id ?? props.activeGroupId, targetChildId: coachFocusChildId });
                    return;
                  }
                  if (isCoachIndividualMode) {
                    setCalendarPickChildReq({ type: 'MATCH' });
                    setCalendarPickChildQuery('');
                    return;
                  }
                  openCreateMatch();
                }}
                disabled={
                  isLoading ||
                  (isCoachChildCalendar && !(coachFocusChild?.group_id ?? props.activeGroupId)) ||
                  (!isCoachChildCalendar &&
                    (isCoachIndividualMode
                      ? (props.coachChildren ?? []).length === 0
                      : coachGroups.length === 0))
                }
                aria-label="Dodaj mecz / wydarzenie"
              >
                <SoccerIcon className="CalendarFabIcon" />
              </button>
              <button
                type="button"
                className={['CalendarFab', 'is-training'].filter(Boolean).join(' ')}
                onClick={() => {
                  if (isCoachChildCalendar) {
                    openCreateTraining({ groupId: coachFocusChild?.group_id ?? props.activeGroupId, targetChildId: coachFocusChildId });
                    return;
                  }
                  if (isCoachIndividualMode) {
                    setCalendarPickChildReq({ type: 'TRAINING' });
                    setCalendarPickChildQuery('');
                    return;
                  }
                  openCreateTraining();
                }}
                disabled={
                  isLoading ||
                  (isCoachChildCalendar && !(coachFocusChild?.group_id ?? props.activeGroupId)) ||
                  (!isCoachChildCalendar &&
                    (isCoachIndividualMode
                      ? !(props.coachChildren ?? []).some((c) => (c.group_id ?? '').trim())
                      : coachGroups.length === 0))
                }
                aria-label="Dodaj trening"
              >
                <TrainingIcon className="CalendarFabIcon" />
              </button>
            </div>
          )}
          {(props.role !== 'COACH' && props.role !== 'ADMIN') && (
            <div className="CalendarFabGroup" role="region" aria-label="Szybkie akcje kalendarza">
              <button
                type="button"
                className={['CalendarFab', 'is-planner'].join(' ')}
                onClick={() => setPlannerOpen(true)}
                disabled={isLoading}
                aria-label="Plan dnia (widok godzinowy)"
              >
                <ClockIcon className="CalendarFabIcon" />
              </button>
            </div>
          )}
          <MonthCard
            monthDate={monthDate}
            selectedKey={selectedDayKey}
            markers={markers}
            onSelect={(k) => {
              setSelectedDayKey(k);
              const d = parseDateKey(k);
              setMonthCursor(new Date(d.getFullYear(), d.getMonth(), 1));
            }}
            onPrevMonth={() => shiftMonth(-1)}
            onNextMonth={() => shiftMonth(1)}
          />
          <div className="DayHeading">{formatDayLabel(selectedDayKey)}</div>
          <div className="CoachSectionTitle">WYDARZENIA W WYBRANYM DNIU</div>
          {selectedDayEventsByRole.length === 0 ? (
            <div className="Muted">Brak wydarzeń w tym dniu.</div>
          ) : (
            <div className="CoachMatchList">
              {selectedDayEventsByRole.map((e) => {
                const isIndividual = !!e.is_individual;
                const openDisabled = isLoading || (isCoachIndividualMode && !!coachFocusChildId && !isIndividual);
                return props.role === 'COACH' || props.role === 'ADMIN' ? (
                  e.type === 'MATCH' ? (
                    isCoachChildCalendar && !isIndividual ? (
                      <ParentMatchBanner key={e.id} e={e} onOpen={() => openEvent(e.id)} disabled={openDisabled} />
                    ) : (
                      <CoachMatchBanner
                        key={e.id}
                        e={e}
                        need={matchNeedById[e.id]}
                        onOpen={() => openEvent(e.id)}
                        onManageCallups={() => openCallupsForEvent(e.id)}
                        disabled={openDisabled}
                      />
                    )
                  ) : (
                    <CoachTrainingBanner
                      key={e.id}
                      e={e}
                      onOpen={() => openEvent(e.id)}
                      disabled={openDisabled}
                      resolveChildLabel={resolveCoachChildLabel}
                      resolveChildPhotoUrl={resolveChildPhotoUrl}
                    />
                  )
                ) : e.type === 'MATCH' ? (
                  <ParentMatchBanner key={e.id} e={e} onOpen={() => openEvent(e.id)} disabled={isLoading} />
                ) : (
                  <CoachTrainingBanner
                    key={e.id}
                    e={e}
                    onOpen={() => openEvent(e.id)}
                    disabled={isLoading}
                    resolveChildLabel={resolveCoachChildLabel}
                    resolveChildPhotoUrl={resolveChildPhotoUrl}
                  />
                );
              })}
            </div>
          )}

          <div className="CoachSectionTitle">MECZE W TYM TYGODNIU</div>
          <div className="CoachMatchList">
            {props.role === 'COACH' || props.role === 'ADMIN' ? (
              <>
                {otherWeekCoachMatches.length === 0 && <div className="Muted">Brak meczów w tym tygodniu.</div>}
                {otherWeekCoachMatches.map((e) => {
                  const isIndividual = !!e.is_individual;
                  const openDisabled = isLoading || (isCoachIndividualMode && !!coachFocusChildId && !isIndividual);
                  return isCoachChildCalendar && !isIndividual ? (
                    <ParentMatchBanner key={e.id} e={e} onOpen={() => openEvent(e.id)} disabled={openDisabled} />
                  ) : (
                    <CoachMatchBanner
                      key={e.id}
                      e={e}
                      need={matchNeedById[e.id]}
                      onOpen={() => openEvent(e.id)}
                      onManageCallups={() => openCallupsForEvent(e.id)}
                      disabled={openDisabled}
                    />
                  );
                })}
              </>
            ) : (
              <>
                {otherWeekParentMatches.length === 0 && <div className="Muted">Brak meczów w tym tygodniu.</div>}
                {otherWeekParentMatches.map((e) => (
                  <ParentMatchBanner key={e.id} e={e} onOpen={() => openEvent(e.id)} disabled={isLoading} />
                ))}
              </>
            )}
          </div>

          <div className="CoachSectionTitle">TRENINGI W TYM TYGODNIU</div>
          <div className="CoachMatchList">
            {props.role === 'COACH' || props.role === 'ADMIN' ? (
              <>
                {otherWeekCoachTrainings.length === 0 && <div className="Muted">Brak treningów w tym tygodniu.</div>}
                {otherWeekCoachTrainings.map((e) => (
                  <CoachTrainingBanner
                    key={e.id}
                    e={e}
                    onOpen={() => openEvent(e.id)}
                    disabled={isLoading || (isCoachIndividualMode && !!coachFocusChildId && !e.is_individual)}
                    resolveChildLabel={resolveCoachChildLabel}
                    resolveChildPhotoUrl={resolveChildPhotoUrl}
                  />
                ))}
              </>
            ) : (
              <>
                {otherWeekParentTrainings.length === 0 && <div className="Muted">Brak treningów w tym tygodniu.</div>}
                {otherWeekParentTrainings.map((e) => (
                  <CoachTrainingBanner
                    key={e.id}
                    e={e}
                    onOpen={() => openEvent(e.id)}
                    disabled={isLoading}
                    resolveChildLabel={resolveCoachChildLabel}
                    resolveChildPhotoUrl={resolveChildPhotoUrl}
                  />
                ))}
              </>
            )}
          </div>
        </>
      )}

      {selectedId && details && (
        <>
          <Card title={details.title}>
            {details.is_individual === true && (
              <div className="StatusPills" aria-label="Typ wydarzenia">
                <div
                  className={[
                    'StatusPill',
                    'StatusPill--individual',
                    details.is_individual_coach_event ? 'StatusPill--individualCoach' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <span aria-hidden="true">🎯</span>
                  {details.type === 'MATCH' ? 'DODATKOWY MECZ' : 'TRENING INDYWIDUALNY'}
                </div>
              </div>
            )}
            {details.type === 'TRAINING' &&
              details.is_individual === true &&
              (() => {
                const direct = (details.target_child_label ?? '').trim();
                if (direct) return <div className="Muted">{`Podopieczny: ${direct}`}</div>;
                const fromId = (details.target_child_id ?? '').trim();
                if (fromId) {
                  const r = resolveCoachChildLabel(fromId);
                  if (r) return <div className="Muted">{`Podopieczny: ${r}`}</div>;
                }
                if (isCoachIndividualMode) {
                  const focusId = (coachFocusChildId ?? '').trim();
                  if (focusId) {
                    const r = resolveCoachChildLabel(focusId);
                    if (r) return <div className="Muted">{`Podopieczny: ${r}`}</div>;
                  }
                  if (Array.isArray(props.coachChildren) && props.coachChildren.length === 1) {
                    const only = props.coachChildren[0];
                    const r = only ? childLabel(only).trim() : '';
                    if (r) return <div className="Muted">{`Podopieczny: ${r}`}</div>;
                  }
                }
                return null;
              })()}
            {(() => {
              if (props.role !== 'COACH' && props.role !== 'ADMIN') return null;
              if (details.type !== 'MATCH') return null;
              if (details.status === 'CANCELLED') return null;
              const callupsCount = Math.max(0, Number(details.called_up_child_ids?.length ?? 0) || 0);
              if (callupsCount > 0) return null;
              const start = new Date(details.start_at);
              if (Number.isNaN(start.getTime())) return null;
              const now = Date.now();
              if (start.getTime() <= now) return null;
              const msDay = 24 * 60 * 60 * 1000;
              const daysLeft = Math.ceil((start.getTime() - now) / msDay);
              if (daysLeft > 2) return null;
              return (
                <div className="ErrorBanner">
                  {daysLeft <= 1
                    ? 'PILNE: mecz jutro/dziś, a nadal nie wysłano powołań.'
                    : 'Uwaga: zostały 2 dni, a nadal nie wysłano powołań.'}
                </div>
              );
            })()}
            {(() => {
              if (props.role !== 'COACH' && props.role !== 'ADMIN') return null;
              if (details.type !== 'TRAINING') return null;
              if (typeof details.training_absent_count !== 'number') return null;
              const absent = Math.max(0, Number(details.training_absent_count) || 0);
              return (
                <>
                  {absent > 4 && <div className="ErrorBanner">{`Uwaga: zgłoszono ${absent} nieobecności — na treningu będzie mniej dzieci.`}</div>}
                  <div className="Muted">{`Nieobecni: ${absent}`}</div>
                </>
              );
            })()}
            {(() => {
              if (props.role !== 'COACH' && props.role !== 'ADMIN') return null;
              if (details.type !== 'MATCH') return null;
              const meta = parseMatchMeta(details.description);
              if (!meta.minPlayers) return null;
              const confirmed = Object.values(details.callup_rsvps ?? {}).filter((s) => s === 'ACCEPTED').length;
              if (confirmed >= meta.minPlayers) return null;
              return (
                <div className="Muted">
                  Za mało potwierdzonych na {meta.formula ?? 'mecz'}: {confirmed}/{meta.minPlayers}. Powołaj więcej lub przypomnij.
                </div>
              );
            })()}
            {(props.role === 'COACH' || props.role === 'ADMIN') && details.type === 'MATCH' && (
              <div className="StatusPills" aria-label="Status meczu">
                {details.is_tbd === true && (
                  <div className="StatusPill StatusPill--warning">
                    <ClockIcon className="StatusPillIcon" />
                    DO USTALENIA
                  </div>
                )}
                {(() => {
                  const meta = parseMatchMeta(details.description);
                  return meta.formula ? (
                    <div className="StatusPill">
                      <SoccerIcon className="StatusPillIcon" />
                      {`FORMUŁA: ${meta.formula}`}
                    </div>
                  ) : null;
                })()}
                {(() => {
                  const meta = parseMatchMeta(details.description);
                  if (!meta.minPlayers) return null;
                  const confirmed = Object.values(details.callup_rsvps ?? {}).filter((s) => s === 'ACCEPTED').length;
                  const ok = confirmed >= meta.minPlayers;
                  return (
                    <div className={['StatusPill', ok ? 'StatusPill--success' : 'StatusPill--danger'].join(' ')}>
                      <UserIcon className="StatusPillIcon" />
                      {`POTRZEBA: ${confirmed}/${meta.minPlayers}`}
                    </div>
                  );
                })()}
                {Array.isArray(details.called_up_child_ids) && details.called_up_child_ids.length === 0 && (
                  <div className="StatusPill StatusPill--danger">
                    <ExclamationIcon className="StatusPillIcon" />
                    BRAK POWOŁAŃ
                  </div>
                )}
                {details.match_deadline_at && (
                  <div className="StatusPill">
                    <ClockIcon className="StatusPillIcon" />
                    DEADLINE: {formatDateTime(details.match_deadline_at)}
                  </div>
                )}
                {Array.isArray(details.called_up_child_ids) && details.called_up_child_ids.length > 0 && (
                  <div className="StatusPill">
                    <UserIcon className="StatusPillIcon" />
                    {`WYSŁANO: ${details.called_up_child_ids.length}`}
                  </div>
                )}
              </div>
            )}
            {isChildScoped &&
              details.type === 'MATCH' &&
              details.status !== 'CANCELLED' &&
              (() => {
                const total =
                  typeof details.callup_total_count === 'number'
                    ? details.callup_total_count
                    : Object.keys(details.callup_rsvps ?? {}).length;
                const confirmed =
                  typeof details.callup_confirmed_count === 'number'
                    ? details.callup_confirmed_count
                    : Object.values(details.callup_rsvps ?? {}).filter((s) => s === 'ACCEPTED').length;
                if (!Number.isFinite(total) || total <= 0) return null;
                const ok = confirmed > 0;
                return (
                  <div className="StatusPills" aria-label="Potwierdzenia meczu">
                    <div className={['StatusPill', ok ? 'StatusPill--success' : 'StatusPill--danger'].join(' ')}>
                      <UserIcon className="StatusPillIcon" />
                      {`${confirmed}/${total}`}
                    </div>
                  </div>
                );
              })()}
            {isChildScoped && details.type === 'MATCH' && details.is_tbd === true && <div className="Muted">Termin: do ustalenia</div>}
            {(props.role === 'COACH' || props.role === 'ADMIN') && (
              <div className="EventTopActions">
                <button type="button" className="FeedMoreLink" onClick={onRepublishEventToFeed} disabled={isLoading}>
                  Ponów publikację na feed
                </button>
              </div>
            )}
            <div className="KeyValue">
              <div className="Key">Start</div>
              <div className="Value">{formatDateTime(details.start_at)}</div>
            </div>
            {details.type === 'MATCH' && details.match_meet_at && (
              <div className="KeyValue">
                <div className="Key">Zbiórka</div>
                <div className="Value">{formatDateTime(details.match_meet_at)}</div>
              </div>
            )}
            {details.end_at && (
              <div className="KeyValue">
                <div className="Key">Koniec</div>
                <div className="Value">{formatDateTime(details.end_at)}</div>
              </div>
            )}
            <div className="KeyValue">
              <div className="Key">Miejsce</div>
              <div className="Value">{details.location}</div>
            </div>
            {details.type === 'MATCH' && (
              <div className="KeyValue">
                <div className="Key">Przeciwnik</div>
                <div className="Value">{details.opponent_name || opponentLabelFromMatchTitle(details.title || '') || '—'}</div>
              </div>
            )}
            {(details.type === 'TRAINING' || details.type === 'MATCH') && details.training_coaches && (
              <div className="KeyValue">
                <div className="Key">Trenerzy</div>
                <div className="Value">
                  {(details.training_coaches ?? []).length > 0
                    ? (details.training_coaches ?? [])
                        .map((c) =>
                          c.is_absent ? `${prettyUserLabelFromBackendLabel(c.email)} (nieobecny)` : prettyUserLabelFromBackendLabel(c.email)
                        )
                        .join(', ')
                    : '—'}
                </div>
              </div>
            )}
            {details.status === 'CANCELLED' && <div className="Muted">Status: ODWOŁANE</div>}
            {details.type === 'MATCH' && (() => {
              const meta = parseMatchMeta(details.description);
              return meta.visible ? <div className="Muted">{meta.visible}</div> : null;
            })()}
            {details.type !== 'MATCH' && details.description && <div className="Muted">{details.description}</div>}
            {details.type === 'MATCH' && details.match_deadline_at && (
              <div className="Muted">Deadline: {formatDateTime(details.match_deadline_at)}</div>
            )}
            {isChildScoped &&
              details.type === 'MATCH' &&
              details.match_deadline_at &&
              (() => {
                const dt = new Date(details.match_deadline_at);
                if (Number.isNaN(dt.getTime())) return false;
                return dt.getTime() < Date.now();
              })() && <div className="Muted">Deadline minął — odpowiedź zapisze się jako po terminie.</div>}
          {isChildScoped && details.type === 'MATCH' && (
            <div className="Muted">
              {details.can_match_rsvp === false
                ? 'Brak powołania dla wybranego dziecka — tylko podgląd.'
                : `Status odpowiedzi: ${
                    details.match_rsvp === 'ACCEPTED' ? 'POTWIERDZONO' : details.match_rsvp === 'DECLINED' ? 'NIEOBECNY' : 'OCZEKUJE'
                  }`}
            </div>
          )}
          {isChildScoped && details.type === 'MATCH' && typeof details.called_up === 'boolean' && (
            <div className="Muted">{details.called_up ? 'Powołanie: POWOŁANY' : 'Powołanie: NIEPOWOŁANY'}</div>
          )}
          {isChildScoped &&
            details.type === 'MATCH' &&
            Array.isArray(details.called_up_child_ids) &&
            details.called_up_child_ids.length > 0 && (
              <div className="KeyValue">
                <div className="Key">Powołani</div>
                <div className="Value">
                  <button
                    type="button"
                    className="FeedMoreLink"
                    onClick={() => {
                      setCallupListFilter('ALL');
                      setIsCallupListOpen(true);
                    }}
                    disabled={isLoading}
                  >
                    {`Pełna lista (${details.called_up_child_ids.length})`}
                  </button>
                </div>
              </div>
            )}
            {(props.role === 'COACH' || props.role === 'ADMIN') &&
              details.type === 'MATCH' &&
              Array.isArray(details.called_up_child_ids) &&
              details.called_up_child_ids.length === 0 && (
                <div className="ErrorBanner">Brak powołań — ustaw zawodników w sekcji „Powołania”.</div>
              )}
            {(props.role === 'COACH' || props.role === 'ADMIN') &&
              details.type === 'MATCH' &&
              Array.isArray(details.called_up_child_ids) &&
              details.called_up_child_ids.length > 0 && (
                <div className="KeyValue">
                  <div className="Key">Powołani</div>
                  <div className="Value">
                    <button
                      type="button"
                      className="FeedMoreLink"
                      onClick={() => {
                        setCallupListFilter('ALL');
                        setIsCallupListOpen(true);
                      }}
                      disabled={isLoading}
                    >
                      {`Pełna lista (${details.called_up_child_ids.length})`}
                    </button>
                  </div>
                </div>
              )}
            {(props.role === 'COACH' || props.role === 'ADMIN') && details.type === 'MATCH' && details.callup_rsvps && (
              <div className="Muted">
                Potwierdzeni:{' '}
                {Object.values(details.callup_rsvps).filter((s) => s === 'ACCEPTED').length} • Odmowy:{' '}
                {Object.values(details.callup_rsvps).filter((s) => s === 'DECLINED').length} • Oczekuje:{' '}
                {Object.values(details.callup_rsvps).filter((s) => s === 'PENDING').length}
              </div>
            )}
          </Card>

          {(props.role === 'PARENT' || (props.role === 'PLAYER' && details.type === 'MATCH')) && (
            <div className="ActionBar" role="region" aria-label="Akcje">
              <div className="ActionBarInner">
                {details.type === 'TRAINING' && (
                  <>
                    <div className="ParentMatchActions" role="group" aria-label="Akcje treningu">
                      <button
                        className={['ParentMatchActionBtn', details.attendance_status === 'IN' ? 'is-done' : ''].filter(Boolean).join(' ')}
                        onClick={onOptIn}
                        disabled={isLoading || !details.can_take_action || details.attendance_status === 'IN'}
                        type="button"
                        aria-label={details.attendance_status === 'IN' ? 'OK' : 'Jestem'}
                      >
                        <CheckIcon className="ParentMatchActionIcon" />
                        <div className="ParentMatchActionLabel">{details.attendance_status === 'IN' ? 'OK' : 'Jestem'}</div>
                      </button>
                      <button
                        className={[
                          'ParentMatchActionBtn',
                          details.attendance_status === 'OUT'
                            ? 'is-undo is-done'
                            : details.attendance_status === 'LATE'
                              ? 'is-late is-done'
                              : 'is-negative',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        onClick={onTrainingAbsenceClick}
                        disabled={isLoading || !details.can_take_action}
                        type="button"
                        aria-label={
                          details.attendance_status === 'OUT'
                            ? 'Powrót do obecności'
                            : details.attendance_status === 'LATE'
                              ? 'Cofnij spóźnienie'
                              : 'Brak'
                        }
                      >
                        {details.attendance_status === 'OUT' ? (
                          <UndoIcon className="ParentMatchActionIcon" />
                        ) : details.attendance_status === 'LATE' ? (
                          <ClockIcon className="ParentMatchActionIcon" />
                        ) : (
                          <XIcon className="ParentMatchActionIcon" />
                        )}
                        <div className="ParentMatchActionLabel">
                          {details.attendance_status === 'OUT'
                            ? 'NB'
                            : details.attendance_status === 'LATE'
                              ? 'Spóźnię'
                              : 'Brak'}
                        </div>
                      </button>
                      <button
                        className={['ParentMatchActionBtn', 'is-map'].filter(Boolean).join(' ')}
                        onClick={openMaps}
                        disabled={isLoading}
                        type="button"
                        aria-label="Dojazd"
                      >
                        <MapIcon className="ParentMatchActionIcon" />
                        <div className="ParentMatchActionLabel">Dojazd</div>
                      </button>
                      <button
                        className={['ParentMatchActionBtn', 'is-transport'].filter(Boolean).join(' ')}
                        onClick={() => openCarpoolFromEventId(details.id)}
                        disabled={isLoading}
                        type="button"
                        aria-label="Transport"
                      >
                        <CarIcon className="ParentMatchActionIcon" />
                        <div className="ParentMatchActionLabel">Transport</div>
                      </button>
                    </div>
                    <button
                      type="button"
                      className="FeedMoreLink"
                      onClick={() => {
                        if (!details || details.type !== 'TRAINING') return;
                        if (!safeChildId) {
                          setErrorText('Wybierz dziecko, aby wykonać tę akcję.');
                          return;
                        }
                        setTrainingAbsenceSheet('PICK');
                      }}
                      disabled={isLoading || !details.can_take_action}
                    >
                      Opcje nieobecności
                    </button>
                  </>
                )}

                {details.type === 'MATCH' && (
                  <>
                    <div className="ParentMatchActions" role="group" aria-label="Akcje meczu">
                      <button
                        className={['ParentMatchActionBtn', details.match_rsvp === 'ACCEPTED' ? 'is-done' : ''].filter(Boolean).join(' ')}
                        onClick={onMatchAccept}
                        disabled={
                          isLoading ||
                          !details.can_take_action ||
                          (isChildScoped && !safeChildId) ||
                          details.can_match_rsvp === false ||
                          details.match_rsvp === 'ACCEPTED'
                        }
                        type="button"
                        aria-label={details.match_rsvp === 'ACCEPTED' ? 'OK' : 'Jestem'}
                      >
                        <CheckIcon className="ParentMatchActionIcon" />
                        <div className="ParentMatchActionLabel">{details.match_rsvp === 'ACCEPTED' ? 'OK' : 'Jestem'}</div>
                      </button>
                      <button
                        className={[
                          'ParentMatchActionBtn',
                          details.match_rsvp === 'DECLINED' ? 'is-undo is-done' : 'is-negative',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        onClick={details.match_rsvp === 'DECLINED' ? onMatchAccept : onMatchDecline}
                        disabled={
                          isLoading ||
                          !details.can_take_action ||
                          (isChildScoped && !safeChildId) ||
                          details.can_match_rsvp === false
                        }
                        type="button"
                        aria-label={details.match_rsvp === 'DECLINED' ? 'Powrót do obecności' : 'Brak'}
                      >
                        {details.match_rsvp === 'DECLINED' ? (
                          <UndoIcon className="ParentMatchActionIcon" />
                        ) : (
                          <XIcon className="ParentMatchActionIcon" />
                        )}
                        <div className="ParentMatchActionLabel">{details.match_rsvp === 'DECLINED' ? 'NB' : 'Brak'}</div>
                      </button>
                      <button
                        className={['ParentMatchActionBtn', 'is-map'].filter(Boolean).join(' ')}
                        onClick={openMaps}
                        disabled={isLoading}
                        type="button"
                        aria-label="Dojazd"
                      >
                        <MapIcon className="ParentMatchActionIcon" />
                        <div className="ParentMatchActionLabel">Dojazd</div>
                      </button>
                      <button
                        className={['ParentMatchActionBtn', 'is-transport'].filter(Boolean).join(' ')}
                        onClick={() => openCarpoolFromEventId(details.id)}
                        disabled={isLoading}
                        type="button"
                        aria-label="Transport"
                      >
                        <CarIcon className="ParentMatchActionIcon" />
                        <div className="ParentMatchActionLabel">Transport</div>
                      </button>
                    </div>
                  </>
                )}
                {isChildScoped && !safeChildId && <div className="ErrorBanner">Najpierw wybierz dziecko w Profilu.</div>}
                {parentActionInfoText && <div className="InfoBanner">{parentActionInfoText}</div>}
                {errorText && selectedId && <div className="ErrorBanner">{errorText}</div>}
              </div>
            </div>
          )}

          {(props.role === 'COACH' || props.role === 'ADMIN') && details.type === 'MATCH' && (
            <div className="ActionBar" role="region" aria-label="Akcje">
              <div className="ActionBarInner">
                <div className="ParentMatchActions" role="group" aria-label="Akcje meczu (trener)">
                  {!details.is_individual && (
                    <button
                      className={['ParentMatchActionBtn', 'is-callups'].filter(Boolean).join(' ')}
                      onClick={openCallups}
                      disabled={isLoading || !details.can_take_action}
                      type="button"
                      aria-label="Powołaj"
                    >
                      <UserIcon className="ParentMatchActionIcon" />
                      <div className="ParentMatchActionLabel">Powołaj</div>
                    </button>
                  )}
                  <button
                    className={[
                      'ParentMatchActionBtn',
                      details.my_coach_absence ? 'is-undo is-done' : 'is-coach-nb',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => {
                      if (!details.can_mark_coach_absence) return;
                      onSetMyCoachAbsence(!details.my_coach_absence);
                    }}
                    disabled={isLoading || !details.can_take_action || !details.can_mark_coach_absence}
                    type="button"
                    aria-label="NB Trenera"
                  >
                    {details.my_coach_absence ? (
                      <UndoIcon className="ParentMatchActionIcon" />
                    ) : (
                      <XIcon className="ParentMatchActionIcon" />
                    )}
                    <div className="ParentMatchActionLabel">NB Trenera</div>
                  </button>
                  <button
                    className={['ParentMatchActionBtn', 'is-map'].filter(Boolean).join(' ')}
                    onClick={openEditMatch}
                    disabled={isLoading || !details.can_take_action}
                    type="button"
                    aria-label="Edytuj"
                  >
                    <SlidersIcon className="ParentMatchActionIcon" />
                    <div className="ParentMatchActionLabel">Edytuj</div>
                  </button>
                  <button
                    className={['ParentMatchActionBtn', 'is-delete'].filter(Boolean).join(' ')}
                    onClick={() => setIsManageMatchOpen(true)}
                    disabled={isLoading}
                    type="button"
                    aria-label="Odwołaj/Usuń"
                  >
                    <TrashIcon className="ParentMatchActionIcon" />
                    <div className="ParentMatchActionLabel">Odwołaj/Usuń</div>
                  </button>
                </div>
              </div>
            </div>
          )}

          {(props.role === 'COACH' || props.role === 'ADMIN') && details.type === 'TRAINING' && (
            <div className="ActionBar" role="region" aria-label="Akcje">
              <div className="ActionBarInner">
                <div className="ParentMatchActions" role="group" aria-label="Akcje treningu (trener)">
                  <button
                    className={['ParentMatchActionBtn', 'is-map'].filter(Boolean).join(' ')}
                    onClick={openEditTraining}
                    disabled={isLoading || !details.can_take_action}
                    type="button"
                    aria-label="Edytuj"
                  >
                    <SlidersIcon className="ParentMatchActionIcon" />
                    <div className="ParentMatchActionLabel">Edytuj</div>
                  </button>
                  <button
                    className={['ParentMatchActionBtn', details.my_coach_absence ? 'is-undo is-done' : 'is-coach-nb'].filter(Boolean).join(' ')}
                    onClick={() => {
                      if (!details.can_mark_coach_absence) return;
                      onSetMyCoachAbsence(!details.my_coach_absence);
                    }}
                    disabled={isLoading || !details.can_take_action || !details.can_mark_coach_absence}
                    type="button"
                    aria-label="NB trenera"
                  >
                    {details.my_coach_absence ? <UndoIcon className="ParentMatchActionIcon" /> : <XIcon className="ParentMatchActionIcon" />}
                    <div className="ParentMatchActionLabel">NB Trenera</div>
                  </button>
                  <button
                    className={['ParentMatchActionBtn', 'is-negative'].filter(Boolean).join(' ')}
                    onClick={() => setIsCancelTrainingConfirmOpen(true)}
                    disabled={isLoading || !details.can_take_action}
                    type="button"
                    aria-label="Odwołaj trening"
                  >
                    <XIcon className="ParentMatchActionIcon" />
                    <div className="ParentMatchActionLabel">Odwołaj</div>
                  </button>
                  <button
                    className={['ParentMatchActionBtn', 'is-delete'].filter(Boolean).join(' ')}
                    onClick={() => setIsDeleteEventConfirmOpen(true)}
                    disabled={isLoading}
                    type="button"
                    aria-label="Usuń trening"
                  >
                    <TrashIcon className="ParentMatchActionIcon" />
                    <div className="ParentMatchActionLabel">Usuń</div>
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function PodopieczniPage(props: { me: Me; token: string; coachChildren: ChildItem[] }) {
  const list = props.coachChildren.slice().sort((a, b) => childLabel(a).localeCompare(childLabel(b)));
  return (
    <div className="Page">
      <div className="PageHeader">
        <div className="PageTitle">Podopieczni</div>
        <div className="Muted">Dzieci przypisane przez Admina (trener indywidualny)</div>
      </div>
      {props.me.role !== 'COACH' ? (
        <div className="Muted">Tylko dla kont trenera.</div>
      ) : list.length === 0 ? (
        <div className="Muted">Brak przypisanych podopiecznych.</div>
      ) : (
        <div className="List" aria-label="Lista podopiecznych">
          {list.map((c) => {
            const gid = (c.group_id ?? '').trim() || '';
            return (
              <div key={c.id} className="ListItem">
                <div className="ListItemTop">
                  <div className="ListItemTitle">{childLabel(c)}</div>
                </div>
                <div className="NotificationActions" aria-label="Akcje podopiecznego">
                  <SecondaryButton onClick={() => navigate('kalendarz', { cc: c.id })} type="button">
                    Kalendarz
                  </SecondaryButton>
                  <SecondaryButton
                    onClick={() => navigate('kalendarz', { ic: c.id, it: 'TRAINING', ig: gid || null })}
                    type="button"
                    disabled={!gid}
                  >
                    Dodaj trening
                  </SecondaryButton>
                  <SecondaryButton
                    onClick={() => navigate('kalendarz', { ic: c.id, it: 'MATCH', ig: gid || null })}
                    type="button"
                    disabled={!gid}
                  >
                    Dodaj mecz
                  </SecondaryButton>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function DruzynaPage(props: {
  me: Me;
  token: string;
  selectedChild: ChildItem | null;
  onReloadChildren: () => Promise<void>;
  coachChildrenOverride?: ChildItem[] | null;
  onSetCoachChildrenOverride?: (next: ChildItem[]) => void;
}) {
  const isIndividualCoachRoster = props.me.role === 'COACH' && Array.isArray(props.coachChildrenOverride);
  const [tab, setTab] = React.useState<'rozgrywki' | 'zawodnicy' | 'trenerzy'>('zawodnicy');
  const [q, setQ] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);
  const [errorText, setErrorText] = React.useState<string | null>(null);
  const [roster, setRoster] = React.useState<ChildItem[]>([]);
  const [coaches, setCoaches] = React.useState<CoachItem[]>([]);
  const [isCoachesLoading, setIsCoachesLoading] = React.useState(false);
  const [isChatStarting, setIsChatStarting] = React.useState(false);
  const [contactCoachOpen, setContactCoachOpen] = React.useState(false);
  const [coachGroups, setCoachGroups] = React.useState<GroupItem[]>([]);
  const [selectedGroupId, setSelectedGroupId] = React.useState<string>('');
  const [rosterPhotoById, setRosterPhotoById] = React.useState<Record<string, string | null>>({});
  const [profileChild, setProfileChild] = React.useState<ChildItem | null>(null);
  const [profileRangeDays, setProfileRangeDays] = React.useState<30 | 90 | 365>(30);
  const [profileEvents, setProfileEvents] = React.useState<EventListItem[]>([]);
  const [profileCoaches, setProfileCoaches] = React.useState<CoachItem[]>([]);
  const [isProfileLoading, setIsProfileLoading] = React.useState(false);
  const [profileError, setProfileError] = React.useState<string | null>(null);
  const [profilePhotoUrl, setProfilePhotoUrl] = React.useState<string | null>(null);
  const [isProfilePhotoUploading, setIsProfilePhotoUploading] = React.useState(false);
  const [playerEditFirstName, setPlayerEditFirstName] = React.useState('');
  const [playerEditLastInitial, setPlayerEditLastInitial] = React.useState('');
  const [playerEditBirthYear, setPlayerEditBirthYear] = React.useState('');
  const [playerEditShirtNumber, setPlayerEditShirtNumber] = React.useState('');
  const [playerEditError, setPlayerEditError] = React.useState<string | null>(null);
  const [playerEditInfo, setPlayerEditInfo] = React.useState<string | null>(null);
  const [isPlayerEditSaving, setIsPlayerEditSaving] = React.useState(false);
  const profilePhotoInputRef = React.useRef<HTMLInputElement | null>(null);
  const [photoCropReq, setPhotoCropReq] = React.useState<PhotoCropRequest | null>(null);
  const [teamMyChildEvents, setTeamMyChildEvents] = React.useState<EventListItem[]>([]);
  const [isTeamMyChildEventsLoading, setIsTeamMyChildEventsLoading] = React.useState(false);
  const [teamStatHint, setTeamStatHint] = React.useState<'trainings' | 'matches' | 'attendance' | null>(null);
  const teamStatHintTimerRef = React.useRef<number | null>(null);
  const [longAbsReason, setLongAbsReason] = React.useState<'CHOROBA' | 'KONTUZJA' | 'INNE'>('CHOROBA');
  const [longAbsFrom, setLongAbsFrom] = React.useState('');
  const [longAbsUntil, setLongAbsUntil] = React.useState('');
  const [longAbsNote, setLongAbsNote] = React.useState('');
  const [longAbsError, setLongAbsError] = React.useState<string | null>(null);
  const [longAbsInfo, setLongAbsInfo] = React.useState<string | null>(null);
  const [isLongAbsSaving, setIsLongAbsSaving] = React.useState(false);
  const longAbsInfoTimeoutRef = React.useRef<number | null>(null);
  const [coachNoteText, setCoachNoteText] = React.useState('');
  const [coachNoteVisibility, setCoachNoteVisibility] = React.useState<CoachNoteVisibility>('PRIVATE');
  const [coachNoteUpdatedAt, setCoachNoteUpdatedAt] = React.useState<string | null>(null);
  const [coachNoteError, setCoachNoteError] = React.useState<string | null>(null);
  const [coachNoteInfo, setCoachNoteInfo] = React.useState<string | null>(null);
  const [isCoachNoteLoading, setIsCoachNoteLoading] = React.useState(false);
  const [isCoachNoteSaving, setIsCoachNoteSaving] = React.useState(false);
  const coachNoteInfoTimeoutRef = React.useRef<number | null>(null);
  const [quickReactions, setQuickReactions] = React.useState<QuickReactionItem[]>([]);
  const [quickReactionsTtlDays, setQuickReactionsTtlDays] = React.useState<number>(7);
  const [quickReactionText, setQuickReactionText] = React.useState('');
  const [quickReactionVisibility, setQuickReactionVisibility] = React.useState<QuickReactionVisibility>('PARENT');
  const [quickReactionError, setQuickReactionError] = React.useState<string | null>(null);
  const [quickReactionInfo, setQuickReactionInfo] = React.useState<string | null>(null);
  const [isQuickReactionsLoading, setIsQuickReactionsLoading] = React.useState(false);
  const [isQuickReactionsSaving, setIsQuickReactionsSaving] = React.useState(false);
  const quickReactionInfoTimeoutRef = React.useRef<number | null>(null);
  const [coachNewChildFirstName, setCoachNewChildFirstName] = React.useState('');
  const [coachNewChildLastInitial, setCoachNewChildLastInitial] = React.useState('');
  const [coachNewChildBirthYear, setCoachNewChildBirthYear] = React.useState('');
  const [coachNewChildShirtNumber, setCoachNewChildShirtNumber] = React.useState('');
  const [coachCreateChildError, setCoachCreateChildError] = React.useState<string | null>(null);
  const [isCoachCreateChildLoading, setIsCoachCreateChildLoading] = React.useState(false);
  const [isCoachAddChildOpen, setIsCoachAddChildOpen] = React.useState(false);
  const [unassigningChildId, setUnassigningChildId] = React.useState<string | null>(null);
  const [rozgrywkiEvents, setRozgrywkiEvents] = React.useState<EventListItem[]>([]);
  const [isRozgrywkiLoading, setIsRozgrywkiLoading] = React.useState(false);
  const [rozgrywkiError, setRozgrywkiError] = React.useState<string | null>(null);
  const [resultEditOpen, setResultEditOpen] = React.useState(false);
  const [resultEditEvent, setResultEditEvent] = React.useState<EventListItem | null>(null);
  const [matchRoster, setMatchRoster] = React.useState<ChildItem[]>([]);
  const [matchRosterGroupId, setMatchRosterGroupId] = React.useState<string>('');
  const [resultHomeScore, setResultHomeScore] = React.useState<number>(0);
  const [resultAwayScore, setResultAwayScore] = React.useState<number>(0);
  const [resultFinalized, setResultFinalized] = React.useState<boolean>(false);
  const [isResultSaving, setIsResultSaving] = React.useState(false);
  const [resultError, setResultError] = React.useState<string | null>(null);
  const [resultInfo, setResultInfo] = React.useState<string | null>(null);
  const [draftGoals, setDraftGoals] = React.useState<Array<{ uid: string } & MatchGoalBase & { id?: string | null }>>([]);
  const [resultDeletedGoalIds, setResultDeletedGoalIds] = React.useState<string[]>([]);
  const [allowManualScoreOverride, setAllowManualScoreOverride] = React.useState<boolean>(false);
  const [debugBackClicked, setDebugBackClicked] = React.useState<boolean>(false);
  const closeLiveMatchNow = React.useCallback(() => {
    try { setGoalEditorOpen(false); } catch(_){}
    try { setGoalEditorUid(null); } catch(_){}
    try { setMatchClockState('IDLE'); } catch(_){}
    try { setMatchClockElapsedSec(0); } catch(_){}
    try { if (matchClockIntervalRef.current) { window.clearInterval(matchClockIntervalRef.current); matchClockIntervalRef.current = null; } } catch(_){}
    try { setResultEditEvent(null); } catch(_){}
    try { setResultEditOpen(false); } catch(_){}
  }, []);
  React.useEffect(() => { (window as any).__acCloseLive = closeLiveMatchNow; return () => { delete (window as any).__acCloseLive; }; }, [closeLiveMatchNow]);
  React.useEffect(() => {
    if (!debugBackClicked) return;
    const t = window.setTimeout(() => setDebugBackClicked(false), 1000);
    return () => window.clearTimeout(t);
  }, [debugBackClicked]);

  const [matchClockState, setMatchClockState] = React.useState<'IDLE' | 'RUNNING' | 'PAUSED' | 'FINISHED'>('IDLE');
  const [matchClockElapsedSec, setMatchClockElapsedSec] = React.useState<number>(0);
  const matchClockIntervalRef = React.useRef<number | null>(null);

  const handleClockStart = React.useCallback(() => {
    if (matchClockState === 'FINISHED') return;
    if (matchClockState === 'RUNNING') return;
    setMatchClockState('RUNNING');
    if (matchClockIntervalRef.current != null) {
      (typeof window !== 'undefined' ? window : (globalThis as any)).clearInterval(matchClockIntervalRef.current);
    }
    const id = (typeof window !== 'undefined' ? window : (globalThis as any)).setInterval(() => {
      setMatchClockElapsedSec((prev) => Math.max(0, Math.min(120 * 60 + 900, prev + 1)));
    }, 1000);
    matchClockIntervalRef.current = id as number;
  }, [matchClockState]);
  const handleClockPause = React.useCallback(() => {
    if (matchClockState !== 'RUNNING') return;
    setMatchClockState('PAUSED');
    if (matchClockIntervalRef.current != null) {
      (typeof window !== 'undefined' ? window : (globalThis as any)).clearInterval(matchClockIntervalRef.current);
      matchClockIntervalRef.current = null;
    }
  }, [matchClockState]);
  const handleClockEnd = React.useCallback(() => {
    if (matchClockState === 'FINISHED') return;
    setMatchClockState('FINISHED');
    setResultFinalized(true);
    if (matchClockIntervalRef.current != null) {
      (typeof window !== 'undefined' ? window : (globalThis as any)).clearInterval(matchClockIntervalRef.current);
      matchClockIntervalRef.current = null;
    }
  }, [matchClockState]);
  const getMatchMinute = React.useCallback((): number => {
    return Math.max(0, Math.min(120, Math.floor(matchClockElapsedSec / 60)));
  }, [matchClockElapsedSec]);

  React.useEffect(() => {
    if (!resultEditOpen) {
      if (matchClockIntervalRef.current != null) {
        (typeof window !== 'undefined' ? window : (globalThis as any)).clearInterval(matchClockIntervalRef.current);
        matchClockIntervalRef.current = null;
      }
      setMatchClockState('IDLE');
      setMatchClockElapsedSec(0);
      setGoalEditorOpen(false);
      setGoalEditorUid(null);
      try { setResultEditEvent(null); } catch(_){}
      try { setResultEditOpen(false); } catch(_){}
    }
  }, [resultEditOpen]);

  const [goalEditorOpen, setGoalEditorOpen] = React.useState(false);
  const [goalEditorUid, setGoalEditorUid] = React.useState<string | null>(null);
  const [goalEditorSide, setGoalEditorSide] = React.useState<'HOME' | 'AWAY'>('HOME');

  const isEdit = !!goalEditorUid;
  const existingGoal = isEdit ? draftGoals.find((g) => g.uid === goalEditorUid) : null;
  const [localSide, setLocalSide] = React.useState<'HOME' | 'AWAY'>('HOME');
  const [localMinute, setLocalMinute] = React.useState<number>(0);
  const [localScorerId, setLocalScorerId] = React.useState<string | null>(null);
  const [localScorerText, setLocalScorerText] = React.useState<string>('');
  const [localAssistId, setLocalAssistId] = React.useState<string | null>(null);
  const [localOg, setLocalOg] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (!goalEditorOpen) return;
    if (existingGoal) {
      setLocalSide(existingGoal.team_side);
      setLocalMinute(existingGoal.minute ?? getMatchMinute());
      if (existingGoal.team_side === 'HOME') {
        setLocalScorerId(existingGoal.scorer_child_id ?? null);
        setLocalScorerText(existingGoal.scorer_display_name ?? '');
      } else {
        setLocalScorerId(null);
        setLocalScorerText(existingGoal.scorer_display_name ?? '');
      }
      setLocalAssistId(existingGoal.assist_child_id ?? null);
      setLocalOg(!!existingGoal.is_own_goal);
    } else {
      setLocalSide(goalEditorSide);
      setLocalMinute(getMatchMinute());
      setLocalScorerId(null);
      setLocalScorerText('');
      setLocalAssistId(null);
      setLocalOg(false);
    }
  }, [goalEditorOpen, goalEditorUid, goalEditorSide, existingGoal, getMatchMinute]);

  const canSave = React.useMemo(() => {
    if (localSide === 'HOME') return !!localScorerId || localScorerText.trim().length > 0;
    return localScorerText.trim().length > 0;
  }, [localSide, localScorerId, localScorerText]);

  const mergedGoals = React.useMemo(() => {
    return [...draftGoals]
      .map((g, origIndex) => ({ g, origIndex }))
      .sort((a, b) => {
        const ma = Number(a.g.minute ?? 0);
        const mb = Number(b.g.minute ?? 0);
        if (mb !== ma) return mb - ma;
        return b.origIndex - a.origIndex;
      })
      .map((x) => x.g);
  }, [draftGoals]);

  const homeDisplayName = (childId: string | null): string => {
    if (!childId) return '';
    const p = matchRoster.find((c) => c.id === childId);
    if (!p) return '';
    const base = `${p.first_name ?? ''} ${p.last_initial ?? ''}`.trim();
    return base;
  };

  const handleSaveGoal = () => {
    const side = localSide;
    const minute = Math.max(0, Math.min(120, Math.floor(localMinute)));
    const og = !!localOg;
    const effScorerId = side === 'HOME' ? localScorerId : null;
    const effScorerDisplay = (() => {
      if (side === 'HOME') {
        return homeDisplayName(localScorerId) || localScorerText.trim();
      }
      return localScorerText.trim();
    })();
    const effAssistId = side === 'HOME' ? localAssistId : null;
    const effAssistDisplay = (side === 'HOME' && localAssistId) ? homeDisplayName(localAssistId) : '';
    const fields: Partial<MatchGoalBase & { id?: string | null; uid?: string }> = {
      team_side: side,
      minute,
      scorer_child_id: effScorerId ?? null,
      scorer_display_name: effScorerDisplay,
      assist_child_id: effAssistId ?? null,
      assist_display_name: effAssistDisplay,
      is_own_goal: og,
    };
    if (isEdit && existingGoal) {
      updateDraftGoal(existingGoal.uid, fields);
    } else {
      const empty: { uid: string; id?: string | null } & MatchGoalBase = {
        uid: `draft_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        id: null,
        team_side: side,
        minute,
        scorer_child_id: effScorerId ?? null,
        scorer_display_name: effScorerDisplay,
        assist_child_id: effAssistId ?? null,
        assist_display_name: effAssistDisplay,
        is_own_goal: og,
        is_penalty: false,
        note: '',
      };
      setDraftGoals((prev) => [empty, ...prev]);
    }
    setGoalEditorOpen(false);
    setGoalEditorUid(null);
  };

  React.useEffect(() => {
    if (allowManualScoreOverride) return;
    let home = 0;
    let away = 0;
    for (const g of draftGoals) {
      const sideOg = !!g.is_own_goal;
      if (g.team_side === 'HOME') {
        if (sideOg) away += 1; else home += 1;
      } else if (g.team_side === 'AWAY') {
        if (sideOg) home += 1; else away += 1;
      }
    }
    setResultHomeScore(home);
    setResultAwayScore(away);
  }, [draftGoals, allowManualScoreOverride]);
  const [fitnessItems, setFitnessItems] = React.useState<FitnessTestItem[]>([]);
  const [isFitnessLoading, setIsFitnessLoading] = React.useState(false);
  const [fitnessError, setFitnessError] = React.useState<string | null>(null);
  const [fitnessModalOpen, setFitnessModalOpen] = React.useState(false);
  const [fitnessEditing, setFitnessEditing] = React.useState<FitnessTestItem | null>(null);
  const [ftType, setFtType] = React.useState<string>('');
  const [ftDate, setFtDate] = React.useState<string>('');
  const [ftValue, setFtValue] = React.useState<number>(0);
  const [ftUnit, setFtUnit] = React.useState<string>('');
  const [ftNote, setFtNote] = React.useState<string>('');
  const [isFitnessSaving, setIsFitnessSaving] = React.useState(false);
  const [fitnessInfo, setFitnessInfo] = React.useState<string | null>(null);
  const [fitnessModalError, setFitnessModalError] = React.useState<string | null>(null);

  const groupId = React.useMemo(() => {
    if (isIndividualCoachRoster) return null;
    if (props.me.role === 'PARENT') return props.selectedChild?.group_id ?? null;
    if (props.me.role === 'COACH' || props.me.role === 'ADMIN') return selectedGroupId ? selectedGroupId : null;
    return null;
  }, [isIndividualCoachRoster, props.me.role, props.selectedChild?.group_id, selectedGroupId]);

  const isMyChild = !!profileChild && props.me.role === 'PARENT' && !!props.selectedChild?.id && props.selectedChild.id === profileChild.id;

  React.useEffect(() => {
    if (!groupId || tab !== 'rozgrywki') {
      setRozgrywkiEvents([]);
      setRozgrywkiError(null);
      setIsRozgrywkiLoading(false);
      return;
    }
    if (props.me.status !== 'ACTIVE') return;
    let cancelled = false;
    (async () => {
      setIsRozgrywkiLoading(true);
      setRozgrywkiError(null);
      try {
        const rawList = props.me.role === 'COACH' ? await apiCoachListMyEvents(props.token) : await apiListEvents(props.token, props.me.role === 'PARENT' ? props.selectedChild?.id ?? null : null);
        if (cancelled) return;
        const out = rawList
          .filter((e) => e.group_id === groupId && e.type === 'MATCH')
          .slice()
          .sort((a, b) => b.start_at.localeCompare(a.start_at));
        setRozgrywkiEvents(out);
      } catch (e) {
        if (!cancelled) setRozgrywkiError(adminActionError('Nie udało się pobrać rozgrywek.', e));
      } finally {
        if (!cancelled) setIsRozgrywkiLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [groupId, tab, props.me.role, props.me.status, props.selectedChild?.id, props.token]);

  const openResultEdit = React.useCallback(async (ev: EventListItem) => {
    setResultEditEvent(ev);
    setResultHomeScore(Number.isFinite(ev.home_score as number) ? Number(ev.home_score) : 0);
    setResultAwayScore(Number.isFinite(ev.away_score as number) ? Number(ev.away_score) : 0);
    setResultFinalized(!!ev.match_finalized);
    setResultError(null);
    setResultInfo(null);
    setIsResultSaving(false);
    setDraftGoals([]);
    setResultDeletedGoalIds([]);
    setAllowManualScoreOverride(false);
    setResultEditOpen(true);
    const evGroupId = (ev.group_id ?? '').trim();
    if (evGroupId && evGroupId !== matchRosterGroupId) {
      setMatchRosterGroupId(evGroupId);
      setMatchRoster([]);
      try {
        const list = await apiGroupChildren(props.token, evGroupId, null);
        setMatchRoster(list);
      } catch (e) {
        setMatchRoster(roster);
      }
    } else if (!evGroupId) {
      setMatchRosterGroupId('');
      setMatchRoster(roster);
    }
    try {
      const items = await apiListMatchGoals(props.token, ev.id);
      setDraftGoals(items.map((it) => ({
        uid: it.id,
        id: it.id,
        team_side: it.team_side as MatchGoalSide,
        scorer_child_id: it.scorer_child_id ?? null,
        scorer_display_name: it.scorer_display_name ?? '',
        minute: it.minute ?? null,
        is_own_goal: !!it.is_own_goal,
        is_penalty: !!it.is_penalty,
        note: it.note ?? null,
        assist_child_id: it.assist_child_id ?? null,
        assist_display_name: it.assist_display_name ?? null,
      })));
    } catch (e) {
      // non-critical: list goals failed; result edit still works
    }
  }, [props.token, matchRosterGroupId, roster]);

  React.useEffect(() => {
    (window as any).__acOpenResultEdit = openResultEdit;
    return () => { delete (window as any).__acOpenResultEdit; };
  }, [openResultEdit]);

  React.useEffect(() => {
    const pending = (window as any).__acPendingResultEditEvent;
    if (pending && typeof pending === 'object' && !!pending.id && pending.type === 'MATCH') {
      (window as any).__acPendingResultEditEvent = null;
      if (tab !== 'rozgrywki') { setTab('rozgrywki'); }
      const to: any = pending;
      openResultEdit(to);
    }
  }, [openResultEdit, tab]);

  const saveResultEdit = React.useCallback(async () => {
    if (!resultEditEvent || props.me.status !== 'ACTIVE') return;
    if (props.me.role !== 'ADMIN' && props.me.role !== 'COACH' && props.me.role !== 'COORDINATOR') return;
    const hs = Math.max(0, Math.min(99, Math.floor(Number.isFinite(resultHomeScore) ? resultHomeScore : 0)));
    const as = Math.max(0, Math.min(99, Math.floor(Number.isFinite(resultAwayScore) ? resultAwayScore : 0)));
    setIsResultSaving(true);
    setResultError(null);
    setResultInfo(null);
    const evId = resultEditEvent.id;
    try {
      const next = await apiSetMatchResult(props.token, evId, {
        home_score: hs,
        away_score: as,
        match_finalized: !!resultFinalized,
      });
      setRozgrywkiEvents((prev) => prev.map((x) => (x.id === next.id ? {
        ...x,
        home_score: (next as unknown as { home_score?: number | null }).home_score ?? null,
        away_score: (next as unknown as { away_score?: number | null }).away_score ?? null,
        match_finalized: (next as unknown as { match_finalized?: boolean | null }).match_finalized ?? null,
      } as EventListItem : x)));

      // Goals deletions first
      const toDelete = Array.from(new Set(resultDeletedGoalIds)).filter(Boolean);
      for (const gid of toDelete) {
        try {
          await apiDeleteMatchGoal(props.token, evId, gid);
        } catch (e) {
          // ignore individual delete errors after initial pass
        }
      }

      // Goals updates & creates
      let finalList: MatchGoalItem[] = [];
      const draftsSorted = draftGoals
        .slice()
        .sort((a, b) => (Number(a.minute ?? 999) - Number(b.minute ?? 999)));
      for (const g of draftsSorted) {
        if (g.id && resultDeletedGoalIds.includes(String(g.id))) continue;
        if (!g.scorer_display_name.trim()) continue;
        const bodyBase: MatchGoalBase = {
          team_side: g.team_side as MatchGoalSide,
          scorer_child_id: (g.scorer_child_id ?? null) || null,
          scorer_display_name: g.scorer_display_name.trim(),
          minute: g.minute ? Math.max(0, Math.min(120, Math.floor(Number(g.minute)))) : null,
          is_own_goal: !!g.is_own_goal,
          is_penalty: !!g.is_penalty,
          note: (g.note ?? '').trim() || null,
          assist_child_id: (g.assist_child_id ?? null) || null,
          assist_display_name: (g.assist_display_name ?? '').trim() || null,
        };
        try {
          if (g.id) {
            finalList = await apiUpdateMatchGoal(props.token, evId, String(g.id), bodyBase);
          } else {
            finalList = await apiAddMatchGoal(props.token, evId, bodyBase);
          }
        } catch (e) {
          // ignore per-goal errors to keep saveResult atomic for score
        }
      }

      // Refresh draftGoals state with finalList (DB-assigned IDs, canonical fields)
      if (finalList.length) {
        setDraftGoals(finalList.map((it) => ({
          uid: it.id,
          id: it.id,
          team_side: it.team_side as MatchGoalSide,
          scorer_child_id: it.scorer_child_id ?? null,
          scorer_display_name: it.scorer_display_name ?? '',
          minute: it.minute ?? null,
          is_own_goal: !!it.is_own_goal,
          is_penalty: !!it.is_penalty,
          note: it.note ?? null,
          assist_child_id: it.assist_child_id ?? null,
          assist_display_name: it.assist_display_name ?? null,
        })));
      } else {
        // No goals remain: delete any leftovers locally, drop deleted list
        setDraftGoals((prev) => prev.filter((d) => d.id && !resultDeletedGoalIds.includes(String(d.id))));
      }
      setResultDeletedGoalIds([]);
      setResultInfo('Wynik meczu oraz bramki zapisane.');
      setResultHomeScore(hs);
      setResultAwayScore(as);
    } catch (e) {
      setResultError(adminActionError('Nie udało się zapisać wyniku.', e));
    } finally {
      setIsResultSaving(false);
      try { closeLiveMatchNow(); } catch(_){}
    }
  }, [props.me.role, props.me.status, props.token, resultAwayScore, resultDeletedGoalIds, draftGoals, resultEditEvent, resultFinalized, resultHomeScore, closeLiveMatchNow]);

  const addDraftGoal = React.useCallback((side: 'HOME' | 'AWAY') => {
    const uid = `draft-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const empty: MatchGoalBase & { uid: string; id?: string | null } = {
      uid,
      id: null,
      team_side: side,
      scorer_child_id: null,
      scorer_display_name: '',
      minute: null,
      is_own_goal: false,
      is_penalty: false,
      note: null,
      assist_child_id: null,
      assist_display_name: null,
    };
    setDraftGoals((prev) => [empty, ...prev]);
  }, []);

  const updateDraftGoal = React.useCallback((uid: string, patch: Partial<MatchGoalBase>) => {
    setDraftGoals((prev) => prev.map((g) => (g.uid === uid ? { ...g, ...patch } : g)));
  }, []);

  const removeDraftGoal = React.useCallback((uid: string) => {
    setDraftGoals((prev) => {
      const match = prev.find((g) => g.uid === uid);
      if (!match) return prev;
      if (match.id) {
        setResultDeletedGoalIds((ids) => Array.from(new Set([...ids, String(match.id!)])));
      }
      return prev.filter((g) => g.uid !== uid);
    });
  }, []);

  const playerLabel = (p: { first_name?: string | null; last_initial?: string | null; shirt_number?: number | string | null } | null | undefined): string => {
    if (!p) return '';
    const base = `${String(p.first_name ?? '').trim()} ${String(p.last_initial ?? '').trim()}`.trim();
    const num = p.shirt_number;
    if (num != null && String(num).trim() !== '') return `${num}. ${base}`;
    return base;
  };

  const opponentInitials = (name: string | null | undefined): string => {
    if (!name) return 'OPP';
    const parts = String(name).trim().split(/[\s-]+/).filter(Boolean);
    const take = parts.slice(0, 3).map((s) => s[0]?.toUpperCase() ?? '').join('');
    return take || 'OPP';
  };



  const V6_COLORS = {
    home: {
      bg: '#16a34a',
      accent: '#22c55e',
      soft: 'color-mix(in srgb, #22c55e 18%, var(--surface) 82%)',
      ring: 'color-mix(in srgb, #22c55e 55%, var(--border) 45%)',
      text: '#fff',
    },
    away: {
      bg: '#ea580c',
      accent: '#f97316',
      soft: 'color-mix(in srgb, #f97316 18%, var(--surface) 82%)',
      ring: 'color-mix(in srgb, #f97316 55%, var(--border) 45%)',
      text: '#fff',
    },
  };

  const v6BadgeStyle = (size: 'xs' | 'sm' | 'md' | 'lg', team: 'HOME' | 'AWAY') => {
    const palette = team === 'HOME' ? V6_COLORS.home : V6_COLORS.away;
    const dims = { xs: 18, sm: 20, md: 26, lg: 48 } as const;
    const d = dims[size];
    const fs = { xs: 9, sm: 10, md: 12, lg: 16 } as const;
    return {
      width: d, height: d, minWidth: d,
      borderRadius: 9999,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      background: team === 'HOME' ? '#fff' : palette.soft,
      color: team === 'HOME' ? palette.bg : 'var(--text)',
      fontWeight: 900, fontSize: fs[size],
      border: team === 'HOME' ? `3px solid ${palette.accent}` : `1px solid ${palette.ring}`,
      letterSpacing: 0.2, userSelect: 'none' as const,
    };
  };

  const v6ToggleStyle = (checked: boolean, accent: 'green' | 'orange' | 'blue' = 'green') => {
    const accentColor = accent === 'green' ? V6_COLORS.home.accent : accent === 'orange' ? V6_COLORS.away.accent : 'var(--accent-blue)';
    return {
      position: 'relative' as const, display: 'inline-block',
      width: 50, height: 30, flexShrink: 0,
      borderRadius: 9999,
      background: checked ? accentColor : 'color-mix(in srgb, var(--border) 80%, var(--surface) 20%)',
      cursor: 'pointer',
      transition: 'background 200ms ease',
      '::after': undefined as unknown as undefined,
    } as React.CSSProperties;
  };

  const v6ToggleKnob = (checked: boolean): React.CSSProperties => {
    return {
      position: 'absolute',
      top: 3,
      left: checked ? 23 : 3,
      width: 24, height: 24,
      borderRadius: 9999,
      background: '#fff',
      boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
      transition: 'left 220ms cubic-bezier(.2,.8,.2,1)',
    };
  };

  const renderGoalEditorBottomSheet = () => {
    if (!goalEditorOpen || !resultEditEvent) return null;

    const opponentName = resultEditEvent.opponent_name || 'Przeciwnik';
    const awayInit = opponentInitials(opponentName);

    return (
      <div
        style={{
          position: 'fixed', inset: 0, zIndex: 950,
          display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
          background: 'rgba(5,12,28,0.62)',
          backdropFilter: 'blur(6px)',
          animation: 'none',
        }}
        onClick={(e) => { if (e.target === e.currentTarget) { setGoalEditorOpen(false); setGoalEditorUid(null); } }}
        role="dialog" aria-modal="true" aria-labelledby="goalEditTitle"
      >
        <div style={{
          width: '100%', maxWidth: 720,
          maxHeight: '90dvh',
          background: 'var(--bg)',
          borderRadius: '24px 24px 0 0',
          borderTop: '1px solid var(--border)',
          boxShadow: '0 -20px 60px rgba(0,0,0,0.5)',
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
          transform: 'translateY(0)',
        }}>
          <div style={{ padding: '10px 0 2px', display: 'flex', justifyContent: 'center' }}>
            <div style={{ width: 36, height: 4, borderRadius: 9999, background: 'color-mix(in srgb, var(--border) 70%, var(--text) 30%)', opacity: 0.6 }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 20px 14px' }}>
            <div id="goalEditTitle" style={{ fontSize: 26, fontWeight: 900, letterSpacing: -0.3 }}>
              {isEdit ? 'Edytuj gola' : 'Dodaj gola'}
            </div>
            <button type="button"
              onClick={() => { setGoalEditorOpen(false); setGoalEditorUid(null); }}
              style={{
                border: 'none', background: 'transparent', color: 'var(--text)',
                fontSize: 28, fontWeight: 300, cursor: 'pointer',
                width: 40, height: 40, padding: 0,
              }} aria-label="Zamknij">×</button>
          </div>

          <div style={{ flexGrow: 1, overflowY: 'auto', padding: '0 20px 160px', display: 'flex', flexDirection: 'column', gap: 22 }}>
            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 1fr',
              borderRadius: 14, padding: 4,
              background: 'color-mix(in srgb, var(--surface) 70%, transparent 30%)',
              border: '1px solid var(--border)',
            }}>
              {(['HOME', 'AWAY'] as const).map((s) => {
                const active = localSide === s;
                const palette = s === 'HOME' ? V6_COLORS.home : V6_COLORS.away;
                const label = s === 'HOME' ? 'Błonianka' : opponentName;
                return (
                  <button key={s} type="button" onClick={() => setLocalSide(s)}
                    style={{
                      padding: '12px 8px', borderRadius: 12,
                      border: 'none',
                      background: active ? palette.accent : 'transparent',
                      color: active ? '#fff' : 'var(--text)',
                      fontSize: 15, fontWeight: active ? 900 : 700,
                      cursor: 'pointer',
                    }}>
                    {label}
                  </button>
                );
              })}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: 1.2, textTransform: 'uppercase', opacity: 0.7 }}>Minuta</div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '48px 1fr 48px auto', gap: 10, alignItems: 'center' }}>
                <button type="button"
                  onClick={() => setLocalMinute((m) => Math.max(0, Math.floor(m) - 1))}
                  style={{
                    width: 48, height: 56, borderRadius: 14,
                    border: '1px solid var(--border)', background: 'var(--surface)',
                    fontSize: 26, fontWeight: 700, color: 'var(--text)', cursor: 'pointer',
                  }}>−</button>
                <div style={{
                  height: 56, borderRadius: 14,
                  border: '1px solid var(--border)',
                  background: 'color-mix(in srgb, var(--surface) 70%, var(--bg) 30%)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 30, fontWeight: 900, letterSpacing: -0.5,
                }}>
                  {Math.max(0, Math.min(120, Math.floor(localMinute)))}′
                </div>
                <button type="button"
                  onClick={() => setLocalMinute((m) => Math.max(0, Math.min(120, Math.floor(m) + 1)))}
                  style={{
                    width: 48, height: 56, borderRadius: 14,
                    border: '1px solid var(--border)', background: 'var(--surface)',
                    fontSize: 26, fontWeight: 700, color: 'var(--text)', cursor: 'pointer',
                  }}>＋</button>
                <button type="button"
                  onClick={() => setLocalMinute(getMatchMinute())}
                  style={{
                    padding: '0 16px', height: 56, borderRadius: 14,
                    border: `1.5px solid ${V6_COLORS.home.ring}`,
                    background: V6_COLORS.home.soft,
                    color: V6_COLORS.home.accent,
                    fontSize: 14, fontWeight: 900, cursor: 'pointer',
                  }}>Teraz</button>
              </div>
            </div>

            {localSide === 'HOME' ? (
              <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: 1.2, textTransform: 'uppercase', opacity: 0.7 }}>Strzelec</div>
                    <div style={{ fontSize: 13, fontWeight: 800, color: V6_COLORS.home.accent }}>Wszyscy ({matchRoster.length})</div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
                    {matchRoster.map((p) => {
                      const active = localScorerId === p.id;
                      return (
                        <button key={p.id} type="button" onClick={() => { setLocalScorerId(active ? null : p.id); }}
                          style={{
                            padding: '8px 6px',
                            minHeight: 72,
                            borderRadius: 14,
                            border: `2px solid ${active ? V6_COLORS.home.accent : 'var(--border)'}`,
                            background: active ? V6_COLORS.home.soft : 'var(--surface)',
                            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2,
                            color: 'var(--text)',
                            cursor: 'pointer',
                          }}>
                          <div style={{ fontSize: 22, fontWeight: 900, lineHeight: 1 }}>{p.shirt_number ?? '—'}</div>
                          <div style={{ fontSize: 13, fontWeight: 800, textAlign: 'center' }}>{playerLabel(p).replace(/^[0-9]+\.\s*/, '')}</div>
                        </button>
                      );
                    })}
                    <button type="button" onClick={() => { setLocalScorerId(null); }}
                      style={{
                        padding: '8px 6px', minHeight: 72, borderRadius: 14,
                        border: '2px dashed var(--border)',
                        background: 'transparent',
                        color: 'var(--text)',
                        fontSize: 13, fontWeight: 800, cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>Nieznany</button>
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: 1.2, textTransform: 'uppercase', opacity: 0.7 }}>Asysta</div>
                    <div style={{ fontSize: 13, fontWeight: 800, color: V6_COLORS.home.accent }}>Wszyscy ({matchRoster.length})</div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
                    <button type="button"
                      onClick={() => setLocalAssistId(null)}
                      style={{
                        padding: '8px 6px', minHeight: 72, borderRadius: 14,
                        border: `2px dashed ${localAssistId == null ? V6_COLORS.home.accent : 'var(--border)'}`,
                        background: localAssistId == null ? V6_COLORS.home.soft : 'transparent',
                        color: 'var(--text)',
                        fontSize: 13, fontWeight: localAssistId == null ? 900 : 800,
                        cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>Brak asysty</button>
                    {matchRoster.map((p) => {
                      const active = localAssistId === p.id;
                      return (
                        <button key={`a-${p.id}`} type="button" onClick={() => setLocalAssistId(active ? null : p.id)}
                          style={{
                            padding: '8px 6px',
                            minHeight: 72,
                            borderRadius: 14,
                            border: `2px solid ${active ? V6_COLORS.home.accent : 'var(--border)'}`,
                            background: active ? V6_COLORS.home.soft : 'var(--surface)',
                            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2,
                            color: 'var(--text)',
                            cursor: 'pointer',
                          }}>
                          <div style={{ fontSize: 22, fontWeight: 900, lineHeight: 1 }}>{p.shirt_number ?? '—'}</div>
                          <div style={{ fontSize: 13, fontWeight: 800, textAlign: 'center' }}>{playerLabel(p).replace(/^[0-9]+\.\s*/, '')}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: 1.2, textTransform: 'uppercase', opacity: 0.7 }}>Strzelec ({opponentName})</div>
                <input className="TextInput"
                  placeholder="Np. Jan Kowalski"
                  value={localScorerText}
                  onChange={(e) => setLocalScorerText(e.target.value)}
                  style={{ fontSize: 16, height: 52, fontWeight: 700 }} />
              </div>
            )}

            <div style={{
              padding: 14, borderRadius: 16,
              border: '1px solid var(--border)',
              background: 'color-mix(in srgb, var(--surface) 70%, transparent 30%)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14,
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ fontSize: 15, fontWeight: 800 }}>Samobój (OG)</div>
                <div className="Muted" style={{ fontSize: 12 }}>Gol zostanie zaliczony drużynie przeciwnej</div>
              </div>
              <div style={v6ToggleStyle(localOg, 'green')}
                onClick={() => setLocalOg((v) => !v)}
                role="switch" aria-checked={localOg} tabIndex={0}>
                <div style={v6ToggleKnob(localOg)} />
              </div>
            </div>
          </div>

          <div style={{
            position: 'sticky', bottom: 0, flexShrink: 0,
            padding: '10px 20px 22px',
            borderTop: '1px solid var(--border)',
            background: 'color-mix(in srgb, var(--bg) 92%, rgba(0,0,0,0.04) 8%)',
          }}>
            <button type="button" onClick={handleSaveGoal} disabled={!canSave}
              style={{
                width: '100%',
                padding: '18px 16px',
                borderRadius: 18,
                border: 'none',
                background: canSave ? V6_COLORS.home.accent : 'color-mix(in srgb, var(--border) 50%, var(--surface) 50%)',
                color: '#fff',
                fontSize: 18, fontWeight: 900, letterSpacing: 0.2,
                cursor: canSave ? 'pointer' : 'not-allowed',
                boxShadow: canSave ? '0 10px 30px color-mix(in srgb, #22c55e 45%, transparent 55%)' : 'none',
              }}>
              {isEdit ? `Zapisz zmiany · ${Math.max(0, Math.min(120, Math.floor(localMinute)))}′` : `Dodaj gola · ${Math.max(0, Math.min(120, Math.floor(localMinute)))}′`}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderLiveMatchEditorV6 = () => {
    if (!resultEditOpen) return null;
    if (!resultEditEvent) return null;
    const ev = resultEditEvent;
    const homeTeam = 'Błonianka';
    const awayTeam = ev.opponent_name || 'Przeciwnik';
    const awayInit = opponentInitials(awayTeam);

    const clockMinutes = Math.floor(matchClockElapsedSec / 60);
    const clockSeconds = matchClockElapsedSec % 60;
    const clockMMSS = `${String(clockMinutes).padStart(2, '0')}:${String(clockSeconds).padStart(2, '0')}`;

    const statusBadge = (() => {
      if (matchClockState === 'RUNNING') return { color: V6_COLORS.home.accent, label: '● W trakcie' };
      if (matchClockState === 'PAUSED') return { color: '#eab308', label: '⏸ Wstrzymany' };
      if (matchClockState === 'FINISHED') return { color: V6_COLORS.away.accent, label: 'Zakończony' };
      return { color: 'var(--text-muted, #64748b)', label: 'Nie rozpoczęty' };
    })();

    const matchBadgeColor = resultFinalized ? V6_COLORS.away.accent : statusBadge.color;
    const matchBadgeLabel = resultFinalized ? 'Zakończony' : statusBadge.label;

    return (
      <div className="ModalBackdrop" role="dialog" aria-modal="true" aria-labelledby="liveTitle"
        style={{ zIndex: 900, background: 'rgba(5,12,28,0.96)' }}>
        <div style={{
          width: '100%', maxWidth: 720, height: '100dvh', maxHeight: '100dvh',
          display: 'flex', flexDirection: 'column',
          background: 'var(--bg)', borderRadius: 0,
          boxShadow: '0 0 60px rgba(0,0,0,0.5)',
        }}>
          {/* HEADER */}
          <div style={{
            flexShrink: 0, padding: '14px 14px 10px',
            borderBottom: '1px solid var(--border)',
            background: 'color-mix(in srgb, var(--surface) 70%, transparent 30%)',
            display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', gap: 10,
            position: 'relative',
            minHeight: 72,
          }}>
            {/* ABSOLUTE OVERLAY ← (nad wszystkim, nie blokuje go nic) */}
            <div style={{
              position: 'absolute', top: 14, left: 14,
              width: 44, height: 44, zIndex: 2147483647,
              pointerEvents: 'auto',
            }}>
              <button type="button"
                onClick={() => {
                  try { setDebugBackClicked(true); } catch(_){}
                  try {
                    if (goalEditorOpen) {
                      setGoalEditorOpen(false);
                      setGoalEditorUid(null);
                      return;
                    }
                  } catch(_){}
                  try { closeLiveMatchNow(); } catch(_a){
                    try { (window as any).__acCloseLive && (window as any).__acCloseLive(); } catch(_b){}
                    try { setResultEditOpen(false); } catch(_c){}
                  }
                }}
                style={{
                  width: '100%', height: '100%', padding: 0,
                  borderRadius: 12,
                  border: `2.5px solid ${debugBackClicked ? '#dc2626' : 'var(--border)'}`,
                  background: debugBackClicked ? '#dc2626' : 'var(--bg)',
                  color: debugBackClicked ? '#fff' : 'var(--text)',
                  fontSize: 22, fontWeight: 900,
                  cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                  opacity: 1,
                  position: 'relative',
                  pointerEvents: 'auto',
                  touchAction: 'manipulation',
                  WebkitTapHighlightColor: 'transparent',
                  userSelect: 'none',
                  boxShadow: debugBackClicked
                    ? '0 0 0 8px rgba(220,38,38,0.22), 0 0 40px rgba(220,38,38,0.6)'
                    : '0 4px 14px rgba(0,0,0,0.25)',
                  transition: 'all 120ms ease-out',
                }} aria-label="Wróć">{debugBackClicked ? '✓' : '←'}</button>
            </div>

            {/* Tytuł (środek), margin 44px po obu stronach dla symetrii */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, paddingTop: 4, marginLeft: 44, marginRight: 44 }}>
              <div id="liveTitle" style={{ fontSize: 22, fontWeight: 800, letterSpacing: -0.2 }}>Wynik meczu</div>
              <div className="Muted" style={{ fontSize: 13 }}>{ev.title || 'Mecz ligowy'} · {formatDateTime(ev.start_at)}</div>
            </div>

            {/* BACKUP PRZYCISK ZAMKNIJ ✕ (prawy górny róg, też jako overlay) */}
            <div style={{
              position: 'absolute', top: 14, right: 14,
              width: 44, height: 44, zIndex: 2147483647,
              pointerEvents: 'auto',
            }}>
              <button type="button"
                onClick={() => {
                  try { setDebugBackClicked(true); } catch(_){}
                  try { closeLiveMatchNow(); } catch(_a){
                    try { (window as any).__acCloseLive && (window as any).__acCloseLive(); } catch(_b){}
                    try { setResultEditOpen(false); } catch(_c){}
                  }
                }}
                style={{
                  width: '100%', height: '100%', padding: 0,
                  borderRadius: 12,
                  border: `2.5px solid ${debugBackClicked ? '#dc2626' : 'var(--border)'}`,
                  background: debugBackClicked ? '#dc2626' : 'var(--bg)',
                  color: debugBackClicked ? '#fff' : 'var(--text)',
                  fontSize: 22, fontWeight: 900,
                  cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  pointerEvents: 'auto',
                  touchAction: 'manipulation',
                  WebkitTapHighlightColor: 'transparent',
                  userSelect: 'none',
                  boxShadow: '0 4px 14px rgba(0,0,0,0.25)',
                  transition: 'all 120ms ease-out',
                }} aria-label="Zamknij">✕</button>
            </div>
          </div>

          {/* BODY */}
          <div style={{
            flexGrow: 1, overflowY: 'auto',
            padding: '14px 14px 140px', display: 'flex', flexDirection: 'column', gap: 18,
          }}>
            {resultError && <div className="ErrorBanner">{resultError}</div>}
            {resultInfo && <div className="InfoBanner">{resultInfo}</div>}

            {/* SCOREBOARD */}
            <div style={{
              padding: '18px 14px 16px',
              borderRadius: 24,
              border: '1px solid var(--border)',
              background: 'color-mix(in srgb, var(--surface) 70%, transparent 30%)',
              display: 'flex', flexDirection: 'column', gap: 16,
            }}>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <div style={{
                  padding: '6px 16px', borderRadius: 9999,
                  background: `color-mix(in srgb, ${matchBadgeColor} 22%, var(--surface) 78%)`,
                  color: matchBadgeColor,
                  fontSize: 13, fontWeight: 900, letterSpacing: 0.3,
                  border: `1px solid color-mix(in srgb, ${matchBadgeColor} 40%, var(--border) 60%)`,
                }}>{matchBadgeLabel}</div>
              </div>

              <div style={{
                display: 'grid', gridTemplateColumns: '1fr auto 1fr',
                alignItems: 'center', gap: 12,
              }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                  <div style={v6BadgeStyle('lg', 'HOME')}>KS</div>
                  <div style={{ fontSize: 18, fontWeight: 800, textAlign: 'center' }}>{homeTeam}</div>
                  <div className="Muted" style={{ fontSize: 12 }}>Gospodarze</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 6 }}>
                  <span style={{
                    fontSize: 64, fontWeight: 900, lineHeight: 1, letterSpacing: -2,
                    color: V6_COLORS.home.accent, fontVariantNumeric: 'tabular-nums',
                  }}>{resultHomeScore}</span>
                  <span style={{ fontSize: 28, fontWeight: 700, opacity: 0.35 }}>:</span>
                  <span style={{
                    fontSize: 64, fontWeight: 900, lineHeight: 1, letterSpacing: -2,
                    color: V6_COLORS.away.accent, fontVariantNumeric: 'tabular-nums',
                  }}>{resultAwayScore}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                  <div style={v6BadgeStyle('lg', 'AWAY')}>{awayInit}</div>
                  <div style={{ fontSize: 18, fontWeight: 800, textAlign: 'center' }}>{awayTeam}</div>
                  <div className="Muted" style={{ fontSize: 12 }}>Goście</div>
                </div>
              </div>

              {/* TIMER ROW */}
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                padding: '10px 12px',
                borderRadius: 14,
                border: '1px solid var(--border)',
                background: 'color-mix(in srgb, var(--bg) 60%, var(--surface) 40%)',
              }}>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button"
                    onClick={handleClockStart}
                    disabled={matchClockState === 'RUNNING' || matchClockState === 'FINISHED'}
                    style={{
                      width: 44, height: 44, borderRadius: 12,
                      border: `1.5px solid ${V6_COLORS.home.ring}`,
                      background: V6_COLORS.home.soft,
                      color: V6_COLORS.home.accent,
                      fontSize: 18, fontWeight: 900, cursor: matchClockState === 'RUNNING' || matchClockState === 'FINISHED' ? 'not-allowed' : 'pointer',
                      opacity: matchClockState === 'RUNNING' || matchClockState === 'FINISHED' ? 0.55 : 1,
                    }} title="Start">▶</button>
                  <button type="button"
                    onClick={handleClockPause}
                    disabled={matchClockState !== 'RUNNING'}
                    style={{
                      width: 44, height: 44, borderRadius: 12,
                      border: '1.5px solid color-mix(in srgb, #eab308 50%, var(--border) 50%)',
                      background: 'color-mix(in srgb, #eab308 20%, var(--surface) 80%)',
                      color: '#ca8a04',
                      fontSize: 18, fontWeight: 900, cursor: matchClockState !== 'RUNNING' ? 'not-allowed' : 'pointer',
                      opacity: matchClockState !== 'RUNNING' ? 0.55 : 1,
                    }} title="Pauza">⏸</button>
                  <button type="button"
                    onClick={handleClockEnd}
                    disabled={matchClockState === 'FINISHED'}
                    style={{
                      width: 44, height: 44, borderRadius: 12,
                      border: `1.5px solid ${V6_COLORS.away.ring}`,
                      background: V6_COLORS.away.soft,
                      color: V6_COLORS.away.accent,
                      fontSize: 18, fontWeight: 900, cursor: matchClockState === 'FINISHED' ? 'not-allowed' : 'pointer',
                      opacity: matchClockState === 'FINISHED' ? 0.55 : 1,
                    }} title="Koniec">⏹</button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                  <div style={{
                    fontSize: 22, fontWeight: 900, letterSpacing: 0.3,
                    fontVariantNumeric: 'tabular-nums',
                    color: 'var(--text)',
                  }}>{clockMMSS}</div>
                  <div className="Muted" style={{ fontSize: 12 }}>{clockMinutes}′ bieżąca</div>
                </div>
              </div>

              {/* + Gol */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <button type="button"
                  onClick={() => { setGoalEditorSide('HOME'); setGoalEditorUid(null); setGoalEditorOpen(true); }}
                  disabled={isResultSaving}
                  style={{
                    padding: '18px 10px',
                    borderRadius: 18,
                    border: `2px solid ${V6_COLORS.home.ring}`,
                    background: V6_COLORS.home.soft,
                    color: V6_COLORS.home.accent,
                    fontSize: 22, fontWeight: 900, cursor: isResultSaving ? 'not-allowed' : 'pointer',
                  }}>+ Gol</button>
                <button type="button"
                  onClick={() => { setGoalEditorSide('AWAY'); setGoalEditorUid(null); setGoalEditorOpen(true); }}
                  disabled={isResultSaving}
                  style={{
                    padding: '18px 10px',
                    borderRadius: 18,
                    border: `2px solid ${V6_COLORS.away.ring}`,
                    background: V6_COLORS.away.soft,
                    color: V6_COLORS.away.accent,
                    fontSize: 22, fontWeight: 900, cursor: isResultSaving ? 'not-allowed' : 'pointer',
                  }}>+ Gol</button>
              </div>
            </div>

            {/* TIMELINE PRZEBIEG */}
            <div>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
                <div style={{ fontSize: 21, fontWeight: 800, letterSpacing: -0.2 }}>Przebieg meczu</div>
                <div className="Muted" style={{ fontSize: 14 }}>{mergedGoals.length} bramek · najnowsze u góry</div>
              </div>
              <div style={{
                borderRadius: 22,
                border: '1px solid var(--border)',
                background: 'color-mix(in srgb, var(--surface) 60%, transparent 40%)',
                overflow: 'hidden',
              }}>
                {mergedGoals.length === 0 ? (
                  <div style={{ padding: 24, textAlign: 'center' }}>
                    <div className="Muted" style={{ fontSize: 14 }}>
                      Brak bramek. Kliknij <strong style={{ color: 'var(--text)' }}>+ Gol</strong> powyżej — nowa bramka pojawi się NA GÓRZE.
                    </div>
                  </div>
                ) : (
                  <div>
                    {mergedGoals.map((g, idx) => {
                      const isHome = g.team_side === 'HOME';
                      const pal = isHome ? V6_COLORS.home : V6_COLORS.away;
                      const min = g.minute ?? 0;
                      const title = (() => {
                        if (g.is_own_goal) return `Bramka samobójcza${isHome ? ` · ${homeTeam}` : ` · ${awayTeam}`}`;
                        if (isHome) {
                          const p = g.scorer_child_id ? roster.find((c) => c.id === g.scorer_child_id) : null;
                          const num = p?.shirt_number;
                          const display = (g.scorer_display_name || (p ? playerLabel(p).replace(/^[0-9]+\.\s*/, '') : 'Nieznany'));
                          return `${display}${num ? ` #${num}` : ''}`;
                        }
                        return g.scorer_display_name?.trim() ? g.scorer_display_name : 'Bramka przeciwnika';
                      })();
                      const sub = (() => {
                        if (g.is_own_goal) return isHome ? 'Liczy się dla gości' : 'Liczy się dla gospodarzy';
                        if (isHome) {
                          if (g.assist_child_id || g.assist_display_name?.trim()) {
                            const p = g.assist_child_id ? roster.find((c) => c.id === g.assist_child_id) : null;
                            return `Asysta: ${g.assist_display_name?.trim() || (p ? playerLabel(p).replace(/^[0-9]+\.\s*/, '') : 'Nieznany')}`;
                          }
                          return 'Bez asysty';
                        }
                        return awayTeam;
                      })();
                      const last = idx === mergedGoals.length - 1;
                      return (
                        <div key={g.uid} style={{
                          display: 'grid', gridTemplateColumns: '72px 16px 1fr auto',
                          alignItems: 'center', gap: 12,
                          padding: '14px 14px',
                          borderBottom: last ? 'none' : '1px solid var(--border)',
                        }}>
                          <div style={{
                            fontSize: 32, fontWeight: 900, lineHeight: 1,
                            fontVariantNumeric: 'tabular-nums',
                            textAlign: 'right',
                            color: 'var(--text)',
                          }}>{min}′</div>
                          <div style={{
                            position: 'relative', alignSelf: 'stretch',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            <div style={{
                              position: 'absolute', top: -14, bottom: -14, left: '50%', transform: 'translateX(-50%)',
                              width: 3, background: pal.soft,
                              opacity: 0.9,
                            }} />
                            <div style={{
                              position: 'relative', width: 12, height: 12, borderRadius: 9999,
                              background: pal.accent,
                              boxShadow: `0 0 0 4px color-mix(in srgb, ${pal.accent} 20%, transparent 80%)`,
                            }} />
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <div style={{
                              fontSize: 17, fontWeight: 800, lineHeight: 1.2,
                              color: g.is_own_goal ? (isHome ? V6_COLORS.away.accent : V6_COLORS.home.accent) : 'var(--text)',
                              marginBottom: 2,
                            }}>{title}</div>
                            <div className="Muted" style={{ fontSize: 13 }}>{sub}</div>
                          </div>
                          <button type="button"
                            onClick={() => { setGoalEditorUid(g.uid); setGoalEditorOpen(true); }}
                            style={{
                              width: 42, height: 42,
                              borderRadius: 12,
                              border: '1.5px solid var(--border)',
                              background: 'var(--bg)',
                              color: 'var(--text)',
                              fontSize: 16, cursor: 'pointer',
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                              flexShrink: 0,
                            }} title="Edytuj gola" aria-label="Edytuj">✏</button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* TOGGLES KARTA */}
            <div style={{
              borderRadius: 22,
              border: '1px solid var(--border)',
              background: 'color-mix(in srgb, var(--surface) 60%, transparent 40%)',
              overflow: 'hidden',
            }}>
              {[
                {
                  key: 'ft',
                  title: 'Mecz zakończony (FT)',
                  sub: 'Wynik stanie się ostateczny',
                  checked: resultFinalized,
                  accent: 'green' as const,
                  onChange: (v: boolean) => { setResultFinalized(v); if (v) handleClockEnd(); },
                },
                {
                  key: 'man',
                  title: 'Wpisz wynik ręcznie',
                  sub: 'Tylko gdy bramki się nie zgadzają',
                  checked: allowManualScoreOverride,
                  accent: 'green' as const,
                  onChange: (v: boolean) => setAllowManualScoreOverride(v),
                },
              ].map((row, idx, arr) => (
                <div key={row.key} style={{
                  padding: '14px 16px',
                  borderBottom: idx === arr.length - 1 ? 'none' : '1px solid var(--border)',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14,
                }}>
                  <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <div style={{ fontSize: 16, fontWeight: 800 }}>{row.title}</div>
                    <div className="Muted" style={{ fontSize: 13 }}>{row.sub}</div>
                    {row.key === 'man' && row.checked && (
                      <div style={{
                        marginTop: 8,
                        display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 8, alignItems: 'center',
                      }}>
                        <input type="number" className="TextInput" min={0} max={99} step={1}
                          value={String(resultHomeScore)}
                          onChange={(e) => { setAllowManualScoreOverride(true); setResultHomeScore(Math.max(0, Math.min(99, Math.floor(Number(e.target.value || 0))))); }}
                          disabled={isResultSaving || !allowManualScoreOverride}
                          style={{ fontWeight: 800, fontSize: 18, textAlign: 'center', height: 44 }} />
                        <div style={{ textAlign: 'center', fontWeight: 900, opacity: 0.4 }}>:</div>
                        <input type="number" className="TextInput" min={0} max={99} step={1}
                          value={String(resultAwayScore)}
                          onChange={(e) => { setAllowManualScoreOverride(true); setResultAwayScore(Math.max(0, Math.min(99, Math.floor(Number(e.target.value || 0))))); }}
                          disabled={isResultSaving || !allowManualScoreOverride}
                          style={{ fontWeight: 800, fontSize: 18, textAlign: 'center', height: 44 }} />
                      </div>
                    )}
                  </div>
                  <div style={v6ToggleStyle(row.checked, row.accent)}
                    onClick={() => row.onChange(!row.checked)}
                    role="switch" aria-checked={row.checked} tabIndex={0}>
                    <div style={v6ToggleKnob(row.checked)} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* FOOTER ZAPISZ */}
          <div style={{
            flexShrink: 0,
            padding: '10px 14px 18px',
            borderTop: '1px solid var(--border)',
            background: 'color-mix(in srgb, var(--bg) 92%, rgba(0,0,0,0.04) 8%)',
            backdropFilter: 'blur(10px)',
          }}>
            <button type="button" onClick={() => void saveResultEdit()}
              disabled={isResultSaving}
              style={{
                width: '100%',
                padding: '18px 18px',
                borderRadius: 18,
                border: 'none',
                background: isResultSaving ? 'color-mix(in srgb, #22c55e 40%, var(--surface) 60%)' : V6_COLORS.home.accent,
                color: '#fff',
                fontSize: 19, fontWeight: 900, letterSpacing: 0.3,
                cursor: isResultSaving ? 'not-allowed' : 'pointer',
                boxShadow: '0 12px 30px color-mix(in srgb, #22c55e 45%, transparent 55%)',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 10,
              }}>
              <span style={{ fontSize: 22 }}>✓</span>
              {isResultSaving ? 'ZAPISYWANIE…' : 'Zapisz wynik'}
            </button>
          </div>
        </div>
        {renderGoalEditorBottomSheet()}
      </div>
    );
  };

  React.useEffect(() => {
    if (!resultEditOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isResultSaving) {
        try { setDebugBackClicked(true); } catch(_){}
        try { closeLiveMatchNow(); } catch(_a){
          try { setResultEditEvent(null); } catch(_b){}
          try { setResultEditOpen(false); } catch(_c){}
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [closeLiveMatchNow, isResultSaving, resultEditOpen]);

  React.useEffect(() => {
    if (!profileChild) {
      setFitnessItems([]);
      setFitnessError(null);
      setIsFitnessLoading(false);
      return;
    }
    if (props.me.status !== 'ACTIVE') return;
    const canSeeFitness = isMyChild || props.me.role === 'ADMIN' || props.me.role === 'COACH' || props.me.role === 'COORDINATOR';
    if (!canSeeFitness) {
      setFitnessItems([]);
      setFitnessError(null);
      setIsFitnessLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setIsFitnessLoading(true);
      setFitnessError(null);
      try {
        const items = await apiListFitnessTests(props.token, profileChild.id);
        if (cancelled) return;
        setFitnessItems(items.slice().sort((a, b) => b.test_date.localeCompare(a.test_date)));
      } catch (e) {
        if (!cancelled) setFitnessError(coachActionError('Nie udało się pobrać testów sprawnościowych.', e));
      } finally {
        if (!cancelled) setIsFitnessLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isMyChild, profileChild, props.me.role, props.me.status, props.token]);

  const openFitnessAdd = React.useCallback(() => {
    if (!profileChild) return;
    if (props.me.status !== 'ACTIVE') return;
    if (props.me.role !== 'ADMIN' && props.me.role !== 'COACH' && props.me.role !== 'COORDINATOR') return;
    setFitnessEditing(null);
    setFtType('');
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    setFtDate(`${yyyy}-${mm}-${dd}`);
    setFtValue(0);
    setFtUnit('');
    setFtNote('');
    setFitnessInfo(null);
    setFitnessModalError(null);
    setIsFitnessSaving(false);
    setFitnessModalOpen(true);
  }, [profileChild, props.me.role, props.me.status]);

  const openFitnessEdit = React.useCallback((item: FitnessTestItem) => {
    if (!profileChild) return;
    if (props.me.status !== 'ACTIVE') return;
    if (props.me.role !== 'ADMIN' && props.me.role !== 'COACH' && props.me.role !== 'COORDINATOR') return;
    setFitnessEditing(item);
    setFtType(item.test_type);
    setFtDate(item.test_date);
    setFtValue(Number.isFinite(item.value) ? item.value : 0);
    setFtUnit(item.unit);
    setFtNote(item.note ?? '');
    setFitnessInfo(null);
    setFitnessModalError(null);
    setIsFitnessSaving(false);
    setFitnessModalOpen(true);
  }, [profileChild, props.me.role, props.me.status]);

  const saveFitness = React.useCallback(async () => {
    if (!profileChild) return;
    if (props.me.status !== 'ACTIVE') return;
    if (props.me.role !== 'ADMIN' && props.me.role !== 'COACH' && props.me.role !== 'COORDINATOR') return;
    const typeClean = ftType.trim();
    const unitClean = ftUnit.trim();
    const dateClean = ftDate.trim();
    const noteClean = ftNote.trim();
    const valueClean = Number(ftValue);
    if (!typeClean) {
      setFitnessModalError('Podaj typ testu sprawnościowego.');
      return;
    }
    if (!unitClean) {
      setFitnessModalError('Podaj jednostkę (np. cm, s, kg, m).');
      return;
    }
    if (!dateClean) {
      setFitnessModalError('Podaj datę testu.');
      return;
    }
    if (!Number.isFinite(valueClean)) {
      setFitnessModalError('Wartość musi być liczbą.');
      return;
    }
    if (noteClean.length > 200) {
      setFitnessModalError('Notatka za długa (max 200 znaków).');
      return;
    }
    setIsFitnessSaving(true);
    setFitnessModalError(null);
    setFitnessInfo(null);
    try {
      const body = {
        test_type: typeClean,
        test_date: dateClean,
        value: valueClean,
        unit: unitClean,
        note: noteClean || null,
      };
      const next = fitnessEditing
        ? await apiUpdateFitnessTest(props.token, fitnessEditing.id, body)
        : await apiCreateFitnessTest(props.token, profileChild.id, body);
      setFitnessItems((prev) => {
        const without = fitnessEditing ? prev.filter((x) => x.id !== fitnessEditing.id) : prev;
        return [next, ...without].slice().sort((a, b) => b.test_date.localeCompare(a.test_date));
      });
      setFitnessInfo(fitnessEditing ? 'Zapisano zmiany w teście.' : 'Dodano nowy test sprawnościowy.');
    } catch (e) {
      setFitnessModalError(coachActionError('Nie udało się zapisać testu sprawnościowego.', e));
    } finally {
      setIsFitnessSaving(false);
    }
  }, [fitnessEditing, ftDate, ftNote, ftType, ftUnit, ftValue, profileChild, props.me.role, props.me.status, props.token]);

  const deleteFitness = React.useCallback(async (item: FitnessTestItem) => {
    if (!profileChild) return;
    if (props.me.status !== 'ACTIVE') return;
    if (props.me.role !== 'ADMIN' && props.me.role !== 'COACH' && props.me.role !== 'COORDINATOR') return;
    if (!window.confirm('Usunąć ten test sprawnościowy?')) return;
    setIsFitnessSaving(true);
    setFitnessError(null);
    try {
      await apiDeleteFitnessTest(props.token, item.id);
      setFitnessItems((prev) => prev.filter((x) => x.id !== item.id));
    } catch (e) {
      setFitnessError(coachActionError('Nie udało się usunąć testu.', e));
    } finally {
      setIsFitnessSaving(false);
    }
  }, [profileChild, props.me.role, props.me.status, props.token]);

  React.useEffect(() => {
    if (!fitnessModalOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isFitnessSaving) setFitnessModalOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [fitnessModalOpen, isFitnessSaving]);

  React.useEffect(() => {
    if (!isIndividualCoachRoster) return;
    if (tab !== 'zawodnicy') setTab('zawodnicy');
  }, [isIndividualCoachRoster, tab]);

  React.useEffect(() => {
    if (!isIndividualCoachRoster) setIsCoachAddChildOpen(false);
  }, [isIndividualCoachRoster]);

  React.useEffect(() => {
    if (isIndividualCoachRoster) return;
    if (props.me.role !== 'COACH' && props.me.role !== 'ADMIN') return;
    if (props.me.status !== 'ACTIVE') return;
    let cancelled = false;
    (async () => {
      try {
        const groups = props.me.role === 'COACH' ? await apiMyGroups(props.token) : await apiAdminListGroups(props.token);
        if (cancelled) return;
        setCoachGroups(groups);
        if (!selectedGroupId && groups[0]) setSelectedGroupId(groups[0].id);
      } catch (e) {
        if (!cancelled) setErrorText('Nie udało się pobrać listy grup.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isIndividualCoachRoster, props.me.role, props.me.status, props.token, selectedGroupId]);

  React.useEffect(() => {
    setRosterPhotoById({});
  }, [groupId]);

  const groupLabel = React.useMemo(() => {
    if (isIndividualCoachRoster) return 'Podopieczni';
    if (props.me.role === 'PARENT') {
      const g = props.selectedChild?.group_name;
      const y = props.selectedChild?.group_year;
      return g ? `${g}${y ? ` (${y})` : ''}` : 'Brak grupy';
    }
    const g = coachGroups.find((x) => x.id === selectedGroupId);
    if (!g) return coachGroups.length === 0 ? 'Brak grupy' : 'Wybierz grupę';
    return `${g.name}${g.year ? ` (${g.year})` : ''}`;
  }, [coachGroups, props.me.role, props.selectedChild?.group_name, props.selectedChild?.group_year, selectedGroupId]);

  React.useEffect(() => {
    if (!profileChild) {
      setProfileEvents([]);
      setProfileCoaches([]);
      setProfileError(null);
      setIsProfileLoading(false);
      setProfilePhotoUrl(null);
      return;
    }
    if (props.me.status !== 'ACTIVE') return;
    const gid = profileChild.group_id ?? null;
    if (!gid) return;

    let cancelled = false;
    (async () => {
      setIsProfileLoading(true);
      setProfileError(null);
      const isMyChild = props.me.role === 'PARENT' && !!props.selectedChild?.id && props.selectedChild.id === profileChild.id;
      const coachesChildId = props.me.role === 'PARENT' ? props.selectedChild?.id ?? null : null;
      try {
        const [photoRes, coachesRes, eventsRes] = await Promise.all([
          (async () => {
            try {
              return await apiGetChildPhoto(props.token, profileChild.id);
            } catch {
              return { child_id: profileChild.id, data_url: null } satisfies ChildPhotoResponse;
            }
          })(),
          (async () => {
            try {
              return await apiGroupCoaches(props.token, gid, coachesChildId);
            } catch {
              return [] as CoachItem[];
            }
          })(),
          (async () => {
            if (!isMyChild) return [] as EventListItem[];
            try {
              return await apiListEvents(props.token, profileChild.id);
            } catch {
              if (!cancelled) setProfileError('Nie udało się pobrać statystyk (dostępne tylko dla własnego dziecka).');
              return [] as EventListItem[];
            }
          })(),
        ]);
        if (cancelled) return;
        setProfilePhotoUrl(photoRes.data_url ?? null);
        setProfileCoaches(coachesRes);
        setProfileEvents(eventsRes);
      } finally {
        if (!cancelled) setIsProfileLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [profileChild, props.me.role, props.me.status, props.selectedChild?.id, props.token]);

  const profileStats = React.useMemo(() => {
    const nowTs = Date.now();
    const fromTs = nowTs - profileRangeDays * 24 * 60 * 60 * 1000;

    const relevant = profileEvents.filter((e) => {
      if (e.status === 'CANCELLED') return false;
      const ts = new Date(e.start_at).getTime();
      if (Number.isNaN(ts)) return false;
      return ts >= fromTs && ts <= nowTs;
    });

    const trainings = relevant.filter((e) => e.type === 'TRAINING');
    const matches = relevant.filter((e) => e.type === 'MATCH');

    const trainingsOut = trainings.filter((e) => e.attendance_status === 'OUT').length;
    const trainingsLate = trainings.filter((e) => e.attendance_status === 'LATE').length;
    const trainingsIn = trainings.length - trainingsOut - trainingsLate;
    const attendancePct = trainings.length > 0 ? Math.round((trainingsIn / trainings.length) * 100) : 0;

    const matchesAccepted = matches.filter((e) => e.match_rsvp === 'ACCEPTED').length;
    const matchesDeclined = matches.filter((e) => e.match_rsvp === 'DECLINED').length;
    const matchesPending = matches.length - matchesAccepted - matchesDeclined;

    return {
      trainingsTotal: trainings.length,
      trainingsIn,
      trainingsOut,
      trainingsLate,
      attendancePct,
      matchesTotal: matches.length,
      matchesAccepted,
      matchesDeclined,
      matchesPending,
    };
  }, [profileEvents, profileRangeDays]);

  const uploadProfilePhoto = React.useCallback(
    async (file: File) => {
      if (!profileChild) return;
      if (props.me.status !== 'ACTIVE') return;
      if (props.me.role === 'PARENT') {
        if (!props.selectedChild?.id || props.selectedChild.id !== profileChild.id) return;
      } else if (props.me.role !== 'ADMIN' && props.me.role !== 'COACH' && props.me.role !== 'COORDINATOR') {
        return;
      }
      setIsProfilePhotoUploading(true);
      setProfileError(null);
      try {
        const r = await apiSetChildPhoto(props.token, profileChild.id, file);
        setProfilePhotoUrl(r.data_url ?? null);
      } catch (e) {
        setProfileError(adminActionError('Nie udało się zapisać zdjęcia.', e));
        throw e;
      } finally {
        setIsProfilePhotoUploading(false);
      }
    },
    [profileChild, props.me.role, props.me.status, props.token]
  );

  const openProfilePhotoCrop = React.useCallback(
    (file: File) => {
      if (!profileChild) return;
      if (props.me.status !== 'ACTIVE') return;
      if (props.me.role === 'PARENT') {
        if (!props.selectedChild?.id || props.selectedChild.id !== profileChild.id) return;
      } else if (props.me.role !== 'ADMIN' && props.me.role !== 'COACH' && props.me.role !== 'COORDINATOR') {
        return;
      }
      setPhotoCropReq({
        title: 'Ustaw zdjęcie profilu',
        file,
        onConfirm: async (cropped) => {
          await uploadProfilePhoto(cropped);
        },
      });
    },
    [profileChild, props.me.role, props.me.status, uploadProfilePhoto]
  );

  const clearProfilePhoto = React.useCallback(async () => {
    if (!profileChild) return;
    if (props.me.status !== 'ACTIVE') return;
    if (props.me.role === 'PARENT') {
      if (!props.selectedChild?.id || props.selectedChild.id !== profileChild.id) return;
    } else if (props.me.role !== 'ADMIN' && props.me.role !== 'COACH' && props.me.role !== 'COORDINATOR') {
      return;
    }
    setIsProfilePhotoUploading(true);
    setProfileError(null);
    try {
      await apiClearChildPhoto(props.token, profileChild.id);
      setProfilePhotoUrl(null);
    } catch (e) {
      setProfileError(adminActionError('Nie udało się usunąć zdjęcia.', e));
    } finally {
      setIsProfilePhotoUploading(false);
    }
  }, [profileChild, props.me.role, props.me.status, props.token]);

  const canManageProfileChild =
    !!profileChild && props.me.status === 'ACTIVE' && (props.me.role === 'ADMIN' || props.me.role === 'COACH' || props.me.role === 'COORDINATOR');
  const canManageProfileChildPhoto = canManageProfileChild || isMyChild;

  React.useEffect(() => {
    if (!profileChild) {
      setPlayerEditFirstName('');
      setPlayerEditLastInitial('');
      setPlayerEditBirthYear('');
      setPlayerEditShirtNumber('');
      setPlayerEditError(null);
      setPlayerEditInfo(null);
      setIsPlayerEditSaving(false);
      return;
    }
    setPlayerEditFirstName(profileChild.first_name ?? '');
    setPlayerEditLastInitial(profileChild.last_initial ?? '');
    setPlayerEditBirthYear(Number.isFinite(profileChild.birth_year as number) ? String(profileChild.birth_year) : '');
    setPlayerEditShirtNumber(Number.isFinite(profileChild.shirt_number as number) ? String(profileChild.shirt_number) : '');
    setPlayerEditError(null);
    setPlayerEditInfo(null);
    setIsPlayerEditSaving(false);
  }, [profileChild?.id]);

  const saveProfileChild = React.useCallback(async () => {
    if (!profileChild || !canManageProfileChild) return;
    const first_name = playerEditFirstName.trim();
    const last_initial = playerEditLastInitial.trim();
    if (!first_name || !last_initial) {
      setPlayerEditError('Uzupełnij imię i inicjał nazwiska.');
      return;
    }

    const birthYearRaw = playerEditBirthYear.trim();
    const shirtNumberRaw = playerEditShirtNumber.trim();
    const parsedBirthYear = birthYearRaw ? Number(birthYearRaw) : null;
    const parsedShirtNumber = shirtNumberRaw ? Number(shirtNumberRaw) : null;
    if (
      birthYearRaw &&
      (parsedBirthYear === null || !Number.isInteger(parsedBirthYear) || parsedBirthYear < 2000 || parsedBirthYear > 2100)
    ) {
      setPlayerEditError('Rocznik musi być liczbą z zakresu 2000-2100.');
      return;
    }
    if (
      shirtNumberRaw &&
      (parsedShirtNumber === null || !Number.isInteger(parsedShirtNumber) || parsedShirtNumber < 1 || parsedShirtNumber > 99)
    ) {
      setPlayerEditError('Numer na koszulce musi być liczbą z zakresu 1-99.');
      return;
    }
    const birth_year = parsedBirthYear;
    const shirt_number = parsedShirtNumber;

    setIsPlayerEditSaving(true);
    setPlayerEditError(null);
    setPlayerEditInfo(null);
    try {
      const body = { first_name, last_initial, birth_year, shirt_number };
      const next =
        props.me.role === 'ADMIN' || props.me.role === 'COORDINATOR'
          ? await apiAdminUpdateChild(props.token, profileChild.id, body)
          : await apiUpdateChild(props.token, profileChild.id, body);
      setProfileChild(next);
      setRoster((prev) => prev.map((item) => (item.id === next.id ? { ...item, ...next } : item)));
      if (Array.isArray(props.coachChildrenOverride)) {
        props.onSetCoachChildrenOverride?.(props.coachChildrenOverride.map((item) => (item.id === next.id ? { ...item, ...next } : item)));
      }
      setPlayerEditInfo('Zapisano dane zawodnika.');
    } catch (e) {
      setPlayerEditError(adminActionError('Nie udało się zapisać danych zawodnika.', e));
    } finally {
      setIsPlayerEditSaving(false);
    }
  }, [
    canManageProfileChild,
    playerEditBirthYear,
    playerEditFirstName,
    playerEditLastInitial,
    playerEditShirtNumber,
    profileChild,
    props.coachChildrenOverride,
    props.me.role,
    props.onSetCoachChildrenOverride,
    props.token,
  ]);

  React.useEffect(() => {
    if (longAbsInfoTimeoutRef.current) window.clearTimeout(longAbsInfoTimeoutRef.current);
    longAbsInfoTimeoutRef.current = null;
    setLongAbsInfo(null);
  }, [profileChild?.id]);

  React.useEffect(() => {
    if (coachNoteInfoTimeoutRef.current) window.clearTimeout(coachNoteInfoTimeoutRef.current);
    coachNoteInfoTimeoutRef.current = null;
    setCoachNoteInfo(null);
  }, [profileChild?.id]);

  React.useEffect(() => {
    if (quickReactionInfoTimeoutRef.current) window.clearTimeout(quickReactionInfoTimeoutRef.current);
    quickReactionInfoTimeoutRef.current = null;
    setQuickReactionInfo(null);
  }, [profileChild?.id]);

  React.useEffect(() => {
    if (!contactCoachOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (isChatStarting) return;
      e.preventDefault();
      setContactCoachOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [contactCoachOpen, isChatStarting]);

  React.useEffect(() => {
    if (!profileChild) setContactCoachOpen(false);
  }, [profileChild]);

  React.useEffect(() => {
    if (!profileChild) {
      setCoachNoteText('');
      setCoachNoteVisibility('PRIVATE');
      setCoachNoteUpdatedAt(null);
      setCoachNoteError(null);
      setIsCoachNoteLoading(false);
      setIsCoachNoteSaving(false);
      return;
    }
    if (props.me.status !== 'ACTIVE') return;
    const canLoad = props.me.role === 'COACH' || props.me.role === 'ADMIN' || isMyChild;
    if (!canLoad) {
      setCoachNoteText('');
      setCoachNoteVisibility('PRIVATE');
      setCoachNoteUpdatedAt(null);
      setCoachNoteError(null);
      setIsCoachNoteLoading(false);
      setIsCoachNoteSaving(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setIsCoachNoteLoading(true);
      setCoachNoteError(null);
      try {
        const r = await apiGetChildCoachNote(props.token, profileChild.id);
        if (cancelled) return;
        const txt = (r.text ?? '').trim();
        const vis = r.visibility === 'PARENT' || r.visibility === 'PRIVATE' ? r.visibility : null;
        if (props.me.role === 'PARENT') {
          setCoachNoteText(vis === 'PARENT' ? txt : '');
          setCoachNoteVisibility('PRIVATE');
          setCoachNoteUpdatedAt(null);
        } else {
          setCoachNoteText(txt);
          setCoachNoteVisibility((vis ?? 'PRIVATE') as CoachNoteVisibility);
          setCoachNoteUpdatedAt((r.updated_at ?? null) as string | null);
        }
      } catch (e) {
        if (!cancelled) setCoachNoteError(humanizeHttpError(e, 'Nie udało się pobrać notatki trenera.'));
      } finally {
        if (!cancelled) setIsCoachNoteLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isMyChild, profileChild, props.me.role, props.me.status, props.token]);

  React.useEffect(() => {
    if (!profileChild) {
      setQuickReactions([]);
      setQuickReactionsTtlDays(7);
      setQuickReactionText('');
      setQuickReactionVisibility('PARENT');
      setQuickReactionError(null);
      setIsQuickReactionsLoading(false);
      setIsQuickReactionsSaving(false);
      return;
    }
    if (props.me.status !== 'ACTIVE') return;
    const canLoad = props.me.role === 'COACH' || props.me.role === 'ADMIN' || isMyChild;
    if (!canLoad) {
      setQuickReactions([]);
      setQuickReactionText('');
      setQuickReactionVisibility('PARENT');
      setQuickReactionError(null);
      setIsQuickReactionsLoading(false);
      setIsQuickReactionsSaving(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setIsQuickReactionsLoading(true);
      setQuickReactionError(null);
      try {
        const r = await apiGetQuickReactions(props.token, profileChild.id);
        if (cancelled) return;
        setQuickReactionsTtlDays(r.ttl_days);
        setQuickReactions(r.items);
        if (props.me.role === 'COACH') setQuickReactionVisibility('PARENT');
      } catch (e) {
        if (cancelled) return;
        let extra = '';
        try {
          const h = await apiHealth(props.token);
          extra = ` (Backend: ${h.build ? h.build : 'legacy-no-build'})`;
        } catch {
          extra = ' (Backend: unreachable)';
        }
        setQuickReactionError(`${coachActionError('Nie udało się pobrać szybkich reakcji.', e)}${extra}`);
      } finally {
        if (!cancelled) setIsQuickReactionsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isIndividualCoachRoster, isMyChild, profileChild, props.me.role, props.me.status, props.token]);

  const onAddQuickReaction = React.useCallback(
    async (text: string) => {
      if (!profileChild) return;
      if (props.me.status !== 'ACTIVE') return;
      if (props.me.role !== 'COACH' && props.me.role !== 'ADMIN') return;
      const t = text.trim();
      if (!t || t.length > 80) return;
      if (quickReactionInfoTimeoutRef.current) window.clearTimeout(quickReactionInfoTimeoutRef.current);
      quickReactionInfoTimeoutRef.current = null;
      setQuickReactionInfo(null);
      setIsQuickReactionsSaving(true);
      setQuickReactionError(null);
      try {
        const r = await apiCreateQuickReaction(
          props.token,
          profileChild.id,
          t,
          props.me.role === 'COACH' ? quickReactionVisibility : undefined
        );
        setQuickReactionsTtlDays(r.ttl_days);
        setQuickReactions(r.items);
        setQuickReactionText('');
        setQuickReactionInfo(`Dodano reakcję (${quickReactionVisibility === 'COACH_PRIVATE' ? 'tylko dla Ciebie' : 'dla rodzica'}, ${r.ttl_days} dni).`);
        quickReactionInfoTimeoutRef.current = window.setTimeout(() => setQuickReactionInfo(null), 4200);
      } catch (e) {
        let extra = '';
        try {
          const h = await apiHealth(props.token);
          extra = ` (Backend: ${h.build ? h.build : 'legacy-no-build'})`;
        } catch {
          extra = ' (Backend: unreachable)';
        }
        setQuickReactionError(`${coachActionError('Nie udało się dodać reakcji.', e)}${extra}`);
      } finally {
        setIsQuickReactionsSaving(false);
      }
    },
    [profileChild, props.me.role, props.me.status, props.token, quickReactionVisibility]
  );

  const onDeleteQuickReaction = React.useCallback(
    async (reactionId: string) => {
      if (!profileChild) return;
      if (props.me.status !== 'ACTIVE') return;
      if (props.me.role !== 'COACH' && props.me.role !== 'ADMIN') return;
      setIsQuickReactionsSaving(true);
      setQuickReactionError(null);
      try {
        const r = await apiDeleteQuickReaction(props.token, profileChild.id, reactionId);
        setQuickReactionsTtlDays(r.ttl_days);
        setQuickReactions(r.items);
      } catch (e) {
        let extra = '';
        try {
          const h = await apiHealth(props.token);
          extra = ` (Backend: ${h.build ? h.build : 'legacy-no-build'})`;
        } catch {
          extra = ' (Backend: unreachable)';
        }
        setQuickReactionError(`${coachActionError('Nie udało się usunąć reakcji.', e)}${extra}`);
      } finally {
        setIsQuickReactionsSaving(false);
      }
    },
    [profileChild, props.me.role, props.me.status, props.token]
  );

  const quickReactionEmojiForText = (text: string): string => {
    const t = (text || '').toLowerCase();
    if (/zaangaż|zaangaz|walka|ambic/.test(t)) return '🔥';
    if (/progres|postęp|postep|rozwój|rozwoj/.test(t)) return '📈';
    if (/frekwenc|obecno/.test(t)) return '✅';
    if (/koleżeń|kolezen|zesp[oó]ł|drużyn|druzyn/.test(t)) return '🤝';
    if (/zachowan|fair|kultura/.test(t)) return '🏅';
    if (/postaw|forma|moc|si[łl]a/.test(t)) return '💪';
    return '⭐';
  };

  React.useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);
    if (!profileChild || !isMyChild) {
      setLongAbsReason('CHOROBA');
      setLongAbsFrom(today);
      setLongAbsUntil(today);
      setLongAbsNote('');
      setLongAbsError(null);
      return;
    }
    const kind = (profileChild.unavailability_kind ?? '').trim();
    const existingReason = (profileChild.unavailability_reason ?? '').trim();
    const inferred =
      existingReason === 'CHOROBA' || existingReason === 'KONTUZJA' || existingReason === 'INNE'
        ? existingReason
        : kind === 'KONTUZJA'
          ? 'KONTUZJA'
          : 'INNE';
    setLongAbsReason(inferred);
    const f = (profileChild.unavailability_from ?? '').trim();
    const u = (profileChild.unavailability_until ?? '').trim();
    setLongAbsFrom((f ? f.slice(0, 10) : today) || today);
    setLongAbsUntil((u ? u.slice(0, 10) : today) || today);
    setLongAbsNote((profileChild.unavailability_note ?? '').trim());
    setLongAbsError(null);
  }, [isMyChild, profileChild]);

  const onSaveLongAbsence = React.useCallback(async () => {
    if (!profileChild) return;
    if (props.me.role !== 'PARENT' || props.me.status !== 'ACTIVE') return;
    if (!props.selectedChild?.id || props.selectedChild.id !== profileChild.id) return;
    const from = longAbsFrom.trim();
    const until = longAbsUntil.trim();
    if (!from || !until) return;
    if (until < from) return;
    const note = longAbsNote.trim();
    if (note.length > 240) return;
    if (longAbsInfoTimeoutRef.current) window.clearTimeout(longAbsInfoTimeoutRef.current);
    longAbsInfoTimeoutRef.current = null;
    setLongAbsInfo(null);
    setIsLongAbsSaving(true);
    setLongAbsError(null);
    try {
      const updated = await apiSetChildUnavailability(props.token, profileChild.id, {
        reason: longAbsReason,
        from_date: from,
        until_date: until,
        note: note ? note : undefined,
      });
      setProfileChild(updated);
      await props.onReloadChildren();
      setLongAbsInfo('Ustawiono długą nieobecność.');
      longAbsInfoTimeoutRef.current = window.setTimeout(() => setLongAbsInfo(null), 4200);
    } catch (e) {
      setLongAbsError(humanizeHttpError(e, 'Nie udało się zapisać długiej nieobecności.'));
    } finally {
      setIsLongAbsSaving(false);
    }
  }, [longAbsFrom, longAbsNote, longAbsReason, longAbsUntil, profileChild, props, props.onReloadChildren]);

  const onClearLongAbsence = React.useCallback(async () => {
    if (!profileChild) return;
    if (props.me.role !== 'PARENT' || props.me.status !== 'ACTIVE') return;
    if (!props.selectedChild?.id || props.selectedChild.id !== profileChild.id) return;
    if (longAbsInfoTimeoutRef.current) window.clearTimeout(longAbsInfoTimeoutRef.current);
    longAbsInfoTimeoutRef.current = null;
    setLongAbsInfo(null);
    setIsLongAbsSaving(true);
    setLongAbsError(null);
    try {
      const updated = await apiClearChildUnavailability(props.token, profileChild.id);
      setProfileChild(updated);
      await props.onReloadChildren();
      setLongAbsInfo('Usunięto długą nieobecność.');
      longAbsInfoTimeoutRef.current = window.setTimeout(() => setLongAbsInfo(null), 4200);
    } catch (e) {
      setLongAbsError(humanizeHttpError(e, 'Nie udało się usunąć długiej nieobecności.'));
    } finally {
      setIsLongAbsSaving(false);
    }
  }, [profileChild, props, props.onReloadChildren]);

  const onSaveCoachNote = React.useCallback(
    async (forceClear?: boolean) => {
      if (!profileChild) return;
      if (props.me.status !== 'ACTIVE') return;
      if (props.me.role !== 'COACH' && props.me.role !== 'ADMIN') return;
      const txt = forceClear ? '' : coachNoteText.trim();
      if (!forceClear && txt.length > 2000) return;
      if (coachNoteInfoTimeoutRef.current) window.clearTimeout(coachNoteInfoTimeoutRef.current);
      coachNoteInfoTimeoutRef.current = null;
      setCoachNoteInfo(null);
      setIsCoachNoteSaving(true);
      setCoachNoteError(null);
      try {
        const r = await apiUpsertChildCoachNote(props.token, profileChild.id, {
          text: txt,
          visibility: txt ? coachNoteVisibility : null,
        });
        setCoachNoteText((r.text ?? '').trim());
        setCoachNoteVisibility(((r.visibility ?? 'PRIVATE') as CoachNoteVisibility) || 'PRIVATE');
        setCoachNoteUpdatedAt((r.updated_at ?? null) as string | null);
        setCoachNoteInfo(txt ? 'Zapisano notatkę.' : 'Usunięto notatkę.');
        coachNoteInfoTimeoutRef.current = window.setTimeout(() => setCoachNoteInfo(null), 4200);
      } catch (e) {
        setCoachNoteError(humanizeHttpError(e, 'Nie udało się zapisać notatki.'));
      } finally {
        setIsCoachNoteSaving(false);
      }
    },
    [coachNoteText, coachNoteVisibility, profileChild, props.me.role, props.me.status, props.token]
  );

  const profileHistory = React.useMemo(() => {
    if (!isMyChild) return [] as EventListItem[];

    const nowTs = Date.now();
    const fromTs = nowTs - profileRangeDays * 24 * 60 * 60 * 1000;
    const relevant = profileEvents.filter((e) => {
      if (e.status === 'CANCELLED') return false;
      const ts = new Date(e.start_at).getTime();
      if (Number.isNaN(ts)) return false;
      return ts >= fromTs && ts <= nowTs;
    });
    relevant.sort((a, b) => new Date(b.start_at).getTime() - new Date(a.start_at).getTime());
    return relevant.slice(0, 20);
  }, [isMyChild, profileEvents, profileRangeDays]);

  React.useEffect(() => {
    if (props.me.status !== 'ACTIVE') return;
    if (tab !== 'zawodnicy') return;
    if (props.me.role !== 'PARENT') return;
    const childId = props.selectedChild?.id ?? null;
    if (!childId) return;
    let cancelled = false;
    (async () => {
      setIsTeamMyChildEventsLoading(true);
      try {
        const list = await apiListEvents(props.token, childId);
        if (!cancelled) setTeamMyChildEvents(list);
      } catch {
        if (!cancelled) setTeamMyChildEvents([]);
      } finally {
        if (!cancelled) setIsTeamMyChildEventsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [props.me.role, props.me.status, props.selectedChild?.id, props.token, tab]);

  const teamMyChildStats = React.useMemo(() => {
    const nowTs = Date.now();
    const fromTs = nowTs - 30 * 24 * 60 * 60 * 1000;

    const relevant = teamMyChildEvents.filter((e) => {
      if (e.status === 'CANCELLED') return false;
      const ts = new Date(e.start_at).getTime();
      if (Number.isNaN(ts)) return false;
      return ts >= fromTs && ts <= nowTs;
    });

    const trainings = relevant.filter((e) => e.type === 'TRAINING');
    const matches = relevant.filter((e) => e.type === 'MATCH');

    const trainingsOut = trainings.filter((e) => e.attendance_status === 'OUT').length;
    const trainingsLate = trainings.filter((e) => e.attendance_status === 'LATE').length;
    const trainingsIn = trainings.length - trainingsOut - trainingsLate;
    const attendancePct = trainings.length > 0 ? Math.round((trainingsIn / trainings.length) * 100) : 0;

    return {
      trainingsTotal: trainings.length,
      matchesTotal: matches.length,
      attendancePct,
    };
  }, [teamMyChildEvents]);

  const onTeamStatHintStart = React.useCallback((key: 'trainings' | 'matches' | 'attendance') => {
    if (teamStatHintTimerRef.current) window.clearTimeout(teamStatHintTimerRef.current);
    teamStatHintTimerRef.current = window.setTimeout(() => setTeamStatHint(key), 450);
  }, []);

  const onTeamStatHintEnd = React.useCallback(() => {
    if (teamStatHintTimerRef.current) window.clearTimeout(teamStatHintTimerRef.current);
    teamStatHintTimerRef.current = null;
    setTeamStatHint(null);
  }, []);

  React.useEffect(() => {
    if (props.me.status !== 'ACTIVE') return;
    if (tab !== 'zawodnicy') return;
    if (props.me.role === 'PARENT' && !props.selectedChild?.id) return;
    if (roster.length === 0) return;
    if (!groupId && !isIndividualCoachRoster) return;

    let cancelled = false;
    const missing = roster.map((c) => c.id).filter((id) => !(id in rosterPhotoById));
    if (missing.length === 0) return;

    (async () => {
      const limit = 4;
      const results: Record<string, string | null> = {};
      for (let i = 0; i < missing.length; i += limit) {
        const batch = missing.slice(i, i + limit);
        const settled = await Promise.allSettled(batch.map((id) => apiGetChildPhoto(props.token, id)));
        for (let j = 0; j < batch.length; j += 1) {
          const id = batch[j];
          const r = settled[j];
          if (r.status === 'fulfilled') results[id] = r.value.data_url ?? null;
          else results[id] = null;
        }
        if (cancelled) return;
        setRosterPhotoById((prev) => ({ ...prev, ...results }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [groupId, isIndividualCoachRoster, props.me.role, props.me.status, props.selectedChild?.id, props.token, roster, rosterPhotoById, tab]);

  React.useEffect(() => {
    if (props.me.status !== 'ACTIVE') return;
    if (tab !== 'zawodnicy') return;
    if (isIndividualCoachRoster) {
      setRoster((props.coachChildrenOverride ?? []).slice());
      return;
    }
    if (!groupId) {
      setRoster([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      setErrorText(null);
      try {
        const list = await apiGroupChildren(
          props.token,
          groupId,
          props.me.role === 'PARENT' ? props.selectedChild?.id ?? null : null
        );
        if (!cancelled) setRoster(list);
      } catch (e) {
        if (!cancelled) setErrorText('Nie udało się pobrać listy zawodników.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [groupId, isIndividualCoachRoster, props.coachChildrenOverride, props.me.role, props.me.status, props.selectedChild?.id, props.token, tab]);

  React.useEffect(() => {
    if (props.me.status !== 'ACTIVE') return;
    if (tab !== 'trenerzy' && tab !== 'zawodnicy') return;
    if (isIndividualCoachRoster) {
      setCoaches([]);
      return;
    }
    if (!groupId) {
      setCoaches([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setIsCoachesLoading(true);
      setErrorText(null);
      try {
        const list = await apiGroupCoaches(
          props.token,
          groupId,
          props.me.role === 'PARENT' ? props.selectedChild?.id ?? null : null
        );
        if (!cancelled) setCoaches(list);
      } catch (e) {
        if (!cancelled) setErrorText('Nie udało się pobrać listy trenerów.');
      } finally {
        if (!cancelled) setIsCoachesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [groupId, props.me.role, props.me.status, props.selectedChild?.id, props.token, tab]);

  const startChatWithCoach = React.useCallback(
    async (c: CoachItem) => {
      if (props.me.role !== 'PARENT') return;
      const childId = props.selectedChild?.id ?? '';
      if (!childId) return;
      setIsChatStarting(true);
      setErrorText(null);
      try {
        const th = await apiChatOpenThread(props.token, { recipient_user_id: c.id, child_id: childId });
        navigate('wiadomosci', { t: th.id });
      } catch (e) {
        setErrorText(adminActionError('Nie udało się rozpocząć rozmowy.', e));
      } finally {
        setIsChatStarting(false);
      }
    },
    [props.me.role, props.selectedChild?.id, props.token]
  );

  const filtered = React.useMemo(() => {
    const s = q.trim().toLowerCase();
    const base = !s
      ? roster
      : roster.filter((p) => {
          const name = `${p.first_name} ${p.last_initial}`.toLowerCase();
          const y = p.birth_year ? String(p.birth_year) : '';
          return name.includes(s) || y.includes(s);
        });
    return base
      .slice()
      .sort((a, b) => {
        const aLast = (a.last_initial ?? '').trim().toLowerCase();
        const bLast = (b.last_initial ?? '').trim().toLowerCase();
        const lastCmp = aLast.localeCompare(bLast, 'pl');
        if (lastCmp !== 0) return lastCmp;
        const aFirst = (a.first_name ?? '').trim().toLowerCase();
        const bFirst = (b.first_name ?? '').trim().toLowerCase();
        const firstCmp = aFirst.localeCompare(bFirst, 'pl');
        if (firstCmp !== 0) return firstCmp;
        const aY = Number(a.birth_year || 0);
        const bY = Number(b.birth_year || 0);
        return aY - bY;
      });
  }, [q, roster]);

  const TEAM_UI_VARIANT: 'v2' | 'legacy' = 'v2';

  const dmFmt = React.useMemo(() => new Intl.DateTimeFormat('pl-PL', { day: '2-digit', month: '2-digit' }), []);

  const rosterAbsenceLabel = React.useCallback(
    (p: ChildItem): string | null => {
      const kind = p.unavailability_kind ?? null;
      if (!kind) return null;
      const rawReason = String(p.unavailability_reason ?? '')
        .trim()
        .toUpperCase();
      const reason =
        kind === 'KONTUZJA'
          ? 'KONTUZJA'
          : rawReason.includes('CHOROBA')
            ? 'CHOROBA'
            : rawReason.includes('INNE')
              ? 'INNE'
              : 'NIEOBECNY';
      const fromKey = p.unavailability_from ? p.unavailability_from.slice(0, 10) : '';
      const untilKey = p.unavailability_until ? p.unavailability_until.slice(0, 10) : '';
      const fromLabel = fromKey ? dmFmt.format(parseDateKey(fromKey)) : '';
      const untilLabel = untilKey ? dmFmt.format(parseDateKey(untilKey)) : '';
      const range =
        fromLabel && untilLabel ? `${fromLabel}–${untilLabel}` : fromLabel ? `OD ${fromLabel}` : untilLabel ? `DO ${untilLabel}` : '';
      return range ? `${reason} ${range}` : reason;
    },
    [dmFmt]
  );

  const coachInitialsFromEmail = React.useCallback((email: string): string => {
    const label = prettyUserLabelFromBackendLabel(email);
    const parts = label
      .split(/[ ._-]+/g)
      .map((x) => x.trim())
      .filter(Boolean);
    const a = parts[0]?.slice(0, 1) ?? '';
    const b = (parts[1]?.slice(0, 1) ?? parts[0]?.slice(1, 2) ?? '').trim();
    return `${a}${b}`.toUpperCase() || '•';
  }, []);

  const renderLegacyGroupBox = () => (
    <>
      {(props.me.role === 'COACH' || props.me.role === 'ADMIN') && (
        <Card title="Grupa">
          {errorText && <div className="ErrorBanner">{errorText}</div>}
          {coachGroups.length === 0 ? (
            <div className="Muted">Brak przypisanych grup. Poproś Admina o przypisanie.</div>
          ) : (
            <select className="TextInput" value={selectedGroupId} onChange={(e) => setSelectedGroupId(e.target.value)}>
              {coachGroups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                  {g.year ? ` ${g.year}` : ''}
                </option>
              ))}
            </select>
          )}
        </Card>
      )}

      {props.me.role === 'PARENT' && (!props.selectedChild || !props.selectedChild.group_id) && (
        <Card title="Brak grupy">
          <div className="Muted">Wybrane dziecko nie ma przypisanej grupy. Poproś Admina o przypisanie.</div>
        </Card>
      )}
    </>
  );

  const renderLegacy = () => (
    <>
      <UpcomingMatchCard
        token={props.token}
        childId={props.me.role === 'PARENT' ? props.selectedChild?.id ?? null : null}
        groupId={groupId}
        disabled={isLoading || isChatStarting || isCoachesLoading}
        sticky
      />

      <div className="SectionTabs" role="tablist" aria-label="Drużyna">
        <button
          type="button"
          className={['SectionTab', tab === 'rozgrywki' ? 'is-active' : ''].filter(Boolean).join(' ')}
          onClick={() => setTab('rozgrywki')}
          aria-selected={tab === 'rozgrywki'}
        >
          ROZGRYWKI
        </button>
        <button
          type="button"
          className={['SectionTab', tab === 'zawodnicy' ? 'is-active' : ''].filter(Boolean).join(' ')}
          onClick={() => setTab('zawodnicy')}
          aria-selected={tab === 'zawodnicy'}
        >
          ZAWODNICY
        </button>
        <button
          type="button"
          className={['SectionTab', tab === 'trenerzy' ? 'is-active' : ''].filter(Boolean).join(' ')}
          onClick={() => setTab('trenerzy')}
          aria-selected={tab === 'trenerzy'}
        >
          TRENERZY
        </button>
      </div>

      {tab === 'zawodnicy' && (
        <>
          <div className="PageHeroTitle">ZAWODNICY</div>
          {renderLegacyGroupBox()}

          <div className="SearchRow">
            <div className="SearchInputWrap">
              <SearchIcon className="SearchIcon" />
              <input
                className="SearchInput"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="SZUKAJ ZAWODNIKA"
              />
            </div>
            <button className="IconSquare" type="button" aria-label="Filtry" disabled>
              <SlidersIcon />
            </button>
          </div>

          <div className="ListMeta">
            <div className="ListMetaLeft">{groupLabel.toUpperCase()}</div>
            <div className="ListMetaRight">{filtered.length} ZAWODNIKÓW</div>
          </div>

          <div className="PlayerList">
            {errorText && <div className="ErrorBanner">{errorText}</div>}
            {isLoading && roster.length === 0 && <div className="Muted">Ładowanie…</div>}
            {!isLoading && groupId && filtered.length === 0 && <div className="Muted">Brak zawodników w tej grupie.</div>}
            {filtered.map((p, idx) => (
              <button
                key={p.id}
                className={[
                  'PlayerCard',
                  idx % 2 === 0 ? 'PlayerCard--green' : 'PlayerCard--blue',
                  p.unavailability_kind ? 'is-unavailable' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                type="button"
                onClick={() => {
                  setProfileChild(p);
                  setProfileRangeDays(30);
                }}
              >
                <div className="PlayerLeft">
                  <div
                    className={['PlayerAvatar', p.unavailability_kind ? 'is-unavailable' : ''].filter(Boolean).join(' ')}
                    aria-hidden="true"
                  >
                    {rosterPhotoById[p.id] ? (
                      <img src={rosterPhotoById[p.id] as string} alt="" />
                    ) : (
                      <div className="PlayerAvatarFallback">
                        {`${(p.first_name || '').slice(0, 1)}${(p.last_initial || '').slice(0, 1)}`.toUpperCase() || '•'}
                      </div>
                    )}
                  </div>
                  {(() => {
                    const abs = rosterAbsenceLabel(p);
                    return p.shirt_number || abs ? (
                      <div className="PlayerBadges">
                        {p.shirt_number ? (
                          <div className="PlayerJersey" aria-label={`No. ${p.shirt_number}`}>
                            <div className="PlayerJerseyLabel">NO.</div>
                            <div className="PlayerJerseyValue">{p.shirt_number}</div>
                          </div>
                        ) : null}
                        {abs ? <div className="PlayerAbsence">{abs}</div> : null}
                      </div>
                    ) : null;
                  })()}
                  <div className="PlayerInfo">
                    <div className="PlayerName">{`${p.first_name} ${p.last_initial}`.toUpperCase()}</div>
                    <div className="PlayerSub">{p.birth_year ? `ROCZNIK ${p.birth_year}` : 'ZAWODNIK'}</div>
                  </div>
                </div>
                <div className="PlayerRight">
                  <div className="PlayerStats">
                    <div>{p.group_name ? p.group_name.toUpperCase() : 'GRUPA'}</div>
                    <div>{p.group_year ? `U${new Date().getFullYear() - p.group_year}` : ''}</div>
                  </div>
                  <UserIcon className="PlayerIcon" />
                </div>
              </button>
            ))}
          </div>
        </>
      )}

      {tab === 'rozgrywki' && (
        <>
          <div className="PageHeroTitle">ROZGRYWKI</div>
          {renderLegacyGroupBox()}
          <Card title={groupLabel}>
            {rozgrywkiError && <div className="ErrorBanner">{rozgrywkiError}</div>}
            {isRozgrywkiLoading && rozgrywkiEvents.length === 0 && <div className="Muted">Ładowanie…</div>}
            {!isRozgrywkiLoading && rozgrywkiEvents.length === 0 && <div className="Muted">Brak rozgrywek w tej grupie.</div>}
            {rozgrywkiEvents.length > 0 && (
              <div className="List" role="list" aria-label="Rozgrywki grupy">
                {rozgrywkiEvents.map((ev) => {
                  const opponentLabel = ev.opponent_name || opponentLabelFromMatchTitle(ev.title || '');
                  const isFinalized = !!ev.match_finalized;
                  const scoreText = isFinalized
                    ? `${Number(ev.home_score ?? 0)}:${Number(ev.away_score ?? 0)}`
                    : null;
                  const canEditResult = props.me.status === 'ACTIVE' && (props.me.role === 'ADMIN' || props.me.role === 'COACH' || props.me.role === 'COORDINATOR');
                  return (
                    <div key={ev.id} className="ListItem" role="listitem">
                      <div className="ListItemTop">
                        <div className="ListItemTitle">
                          {ev.status === 'CANCELLED' ? '❌ ODWOŁANY • ' : ''}
                          {`Błonianka vs ${opponentLabel || 'Przeciwnik'}`}
                        </div>
                        {isFinalized ? (
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                            <span
                              className="ListItemBadge"
                              style={{ background: 'var(--color-primary-50, #e8eaf6)', color: 'var(--color-primary-700, #1a237e)', fontWeight: 800, fontSize: 14 }}
                            >
                              🏁 {scoreText}
                            </span>
                            {(ev.external_source ?? '').trim() === 'LNP' && (
                              <span className="LnpBadge"><span className="LnpBadgeDot"></span>LNP</span>
                            )}
                          </div>
                        ) : (
                          <span className="ListItemBadge">{formatDateTime(ev.start_at)}</span>
                        )}
                      </div>
                      <div style={{ marginTop: 6, fontSize: 13, color: 'var(--color-neutral-600, #666)' }}>
                        📍 {ev.location || 'Brak lokalizacji'}
                        {ev.match_kind ? ` • ${matchKindShort(ev.match_kind)}` : ''}
                        {!isFinalized && ev.status !== 'CANCELLED' && new Date(ev.start_at).getTime() < Date.now() ? ' • W TRAKCIE' : ''}
                      </div>
                      {canEditResult && (
                        <div style={{ marginTop: 10, display: 'flex', justifyContent: 'flex-end' }}>
                          <SecondaryButton onClick={() => openResultEdit(ev)} disabled={isResultSaving || isRozgrywkiLoading} type="button">
                            Edytuj wynik
                          </SecondaryButton>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </Card>

          {resultEditOpen && resultEditEvent && renderLiveMatchEditorV6()}
        </>
      )}

      {tab === 'trenerzy' && (
        <>
          <div className="PageHeroTitle">TRENERZY</div>
          {renderLegacyGroupBox()}

          <Card title={groupLabel}>
            {errorText && <div className="ErrorBanner">{errorText}</div>}
            {isCoachesLoading && coaches.length === 0 && <div className="Muted">Ładowanie…</div>}
            {!isCoachesLoading && coaches.length === 0 && <div className="Muted">Brak przypisanych trenerów.</div>}
            {coaches.length > 0 && (
              <div className="List">
                {coaches.map((c) => (
                  <div key={c.id} className="ListItem">
                    <div className="ListItemTop">
                      <div className="ListItemTitle">{prettyUserLabelFromBackendLabel(c.email)}</div>
                      <div className="ListItemBadge">TRENER</div>
                    </div>
                    {props.me.role === 'PARENT' && (
                      <PrimaryButton
                        onClick={() => startChatWithCoach(c)}
                        disabled={isChatStarting || props.me.status !== 'ACTIVE' || !props.selectedChild?.id}
                        type="button"
                      >
                        Napisz
                      </PrimaryButton>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>
        </>
      )}
    </>
  );

  const renderV2 = () => (
    <>
      <UpcomingMatchCard
        token={props.token}
        childId={props.me.role === 'PARENT' ? props.selectedChild?.id ?? null : null}
        groupId={groupId}
        disabled={isLoading || isChatStarting || isCoachesLoading}
        sticky
      />

      <div className="TeamHero">
        <div className="TeamHeroTitle">{groupLabel.toUpperCase()}</div>
        <div className="TeamHeroSubtitle">KADRA</div>
        {!isIndividualCoachRoster && (props.me.role === 'COACH' || props.me.role === 'ADMIN') && (
          <div className="TeamHeroControls">
            {coachGroups.length === 0 ? (
              <div className="TeamHeroHint">Brak przypisanych grup. Poproś Admina o przypisanie.</div>
            ) : (
              <select className="TextInput TeamGroupSelect" value={selectedGroupId} onChange={(e) => setSelectedGroupId(e.target.value)}>
                {coachGroups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                    {g.year ? ` ${g.year}` : ''}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}
        {props.me.role === 'PARENT' && (!props.selectedChild || !props.selectedChild.group_id) && (
          <div className="TeamHeroHint">Wybrane dziecko nie ma przypisanej grupy. Poproś Admina o przypisanie.</div>
        )}
      </div>

      <div className="SectionTabs" role="tablist" aria-label="Drużyna">
        <button
          type="button"
          className={['SectionTab', tab === 'zawodnicy' ? 'is-active' : ''].filter(Boolean).join(' ')}
          onClick={() => setTab('zawodnicy')}
          aria-selected={tab === 'zawodnicy'}
        >
          {isIndividualCoachRoster ? 'PODOPIECZNI' : 'ZAWODNICY'}
        </button>
        {!isIndividualCoachRoster && (
          <>
            <button
              type="button"
              className={['SectionTab', tab === 'trenerzy' ? 'is-active' : ''].filter(Boolean).join(' ')}
              onClick={() => setTab('trenerzy')}
              aria-selected={tab === 'trenerzy'}
            >
              TRENERZY
            </button>
            <button
              type="button"
              className={['SectionTab', tab === 'rozgrywki' ? 'is-active' : ''].filter(Boolean).join(' ')}
              onClick={() => setTab('rozgrywki')}
              aria-selected={tab === 'rozgrywki'}
            >
              ROZGRYWKI
            </button>
          </>
        )}
      </div>

      {tab === 'zawodnicy' && (
        <>
          <div className="SearchRow">
            <div className="SearchInputWrap">
              <SearchIcon className="SearchIcon" />
              <input
                className="SearchInput"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="SZUKAJ ZAWODNIKA"
              />
            </div>
            <button className="IconSquare" type="button" aria-label="Filtry" disabled>
              <SlidersIcon />
            </button>
          </div>

          {isIndividualCoachRoster && !isCoachAddChildOpen && (
            <div className="SheetActionsRow">
              <PrimaryButton
                type="button"
                disabled={isCoachCreateChildLoading}
                onClick={() => {
                  setCoachCreateChildError(null);
                  setIsCoachAddChildOpen(true);
                }}
              >
                Dodaj podopiecznego
              </PrimaryButton>
            </div>
          )}

          {isIndividualCoachRoster && isCoachAddChildOpen && (
            <Card title="Dodaj podopiecznego">
              {coachCreateChildError && <div className="ErrorBanner">{coachCreateChildError}</div>}
              <div className="Muted">Utwórz dziecko od razu w swoich podopiecznych. Usunięcie podopiecznego usuwa tylko z Twojej listy.</div>
              <input
                className="TextInput"
                value={coachNewChildFirstName}
                onChange={(e) => setCoachNewChildFirstName(e.target.value)}
                placeholder="Imię"
                disabled={isCoachCreateChildLoading}
              />
              <input
                className="TextInput"
                value={coachNewChildLastInitial}
                onChange={(e) => setCoachNewChildLastInitial(e.target.value)}
                placeholder="Inicjał nazwiska (np. K.)"
                disabled={isCoachCreateChildLoading}
              />
              <input
                className="TextInput"
                value={coachNewChildBirthYear}
                onChange={(e) => setCoachNewChildBirthYear(e.target.value)}
                placeholder="Rok urodzenia (opcjonalnie)"
                inputMode="numeric"
                disabled={isCoachCreateChildLoading}
              />
              <input
                className="TextInput"
                value={coachNewChildShirtNumber}
                onChange={(e) => setCoachNewChildShirtNumber(e.target.value)}
                placeholder="No. (opcjonalnie)"
                inputMode="numeric"
                disabled={isCoachCreateChildLoading}
              />
              <div className="SheetActionsRow">
                <PrimaryButton
                  type="button"
                  disabled={isCoachCreateChildLoading || coachNewChildFirstName.trim() === '' || coachNewChildLastInitial.trim() === ''}
                  onClick={async () => {
                    if (props.me.role !== 'COACH' || props.me.status !== 'ACTIVE') return;
                    setIsCoachCreateChildLoading(true);
                    setCoachCreateChildError(null);
                    try {
                      const by = coachNewChildBirthYear.trim() ? Number(coachNewChildBirthYear.trim()) : null;
                      const sn = coachNewChildShirtNumber.trim() ? Number(coachNewChildShirtNumber.trim()) : null;
                      const next = await apiCoachCreateChild(props.token, {
                        first_name: coachNewChildFirstName.trim(),
                        last_initial: coachNewChildLastInitial.trim(),
                        birth_year: Number.isFinite(by as any) ? (by as any) : null,
                        shirt_number: Number.isFinite(sn as any) ? (sn as any) : null,
                      });
                      props.onSetCoachChildrenOverride?.(next);
                      setCoachNewChildFirstName('');
                      setCoachNewChildLastInitial('');
                      setCoachNewChildBirthYear('');
                      setCoachNewChildShirtNumber('');
                      setIsCoachAddChildOpen(false);
                    } catch (e) {
                      setCoachCreateChildError(humanizeHttpError(e, 'Nie udało się dodać podopiecznego.'));
                    } finally {
                      setIsCoachCreateChildLoading(false);
                    }
                  }}
                >
                  Dodaj dziecko
                </PrimaryButton>
                <SecondaryButton
                  type="button"
                  disabled={isCoachCreateChildLoading}
                  onClick={() => {
                    setIsCoachAddChildOpen(false);
                  }}
                >
                  Anuluj
                </SecondaryButton>
              </div>
            </Card>
          )}

          {!isIndividualCoachRoster && (
            <>
              <div className="TeamSectionHeader">
                <div className="TeamSectionTitle">TRENERZY</div>
              </div>

              {errorText && <div className="ErrorBanner">{errorText}</div>}
              {isCoachesLoading && coaches.length === 0 ? (
                <div className="Muted">Ładowanie…</div>
              ) : coaches.length === 0 ? (
                <div className="Muted">Brak przypisanych trenerów.</div>
              ) : (
                <div className="TeamCoachRow" aria-label="Trenerzy">
                  {coaches.map((c) => (
                    <div key={c.id} className="TeamCoachCard">
                      <div className="TeamAvatarRing" aria-hidden="true">
                        <div className="TeamAvatarFallback">{coachInitialsFromEmail(c.email)}</div>
                      </div>
                      <div className="TeamCoachText">
                        <div className="TeamCoachName">{prettyUserLabelFromBackendLabel(c.email).toUpperCase()}</div>
                        <div className="TeamCoachRole">TRENER</div>
                      </div>
                      {props.me.role === 'PARENT' && (
                        <button
                          type="button"
                          className="TeamCoachAction"
                          onClick={() => startChatWithCoach(c)}
                          disabled={isChatStarting || props.me.status !== 'ACTIVE' || !props.selectedChild?.id}
                          aria-label="Napisz do trenera"
                        >
                          <PlusIcon />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          <div className="TeamSectionHeader">
            <div className="TeamSectionTitle">{isIndividualCoachRoster ? 'PODOPIECZNI' : 'ZAWODNICY'}</div>
            <div className="TeamSectionMeta">{filtered.length} {isIndividualCoachRoster ? 'PODOPIECZNYCH' : 'ZAWODNIKÓW'}</div>
          </div>

          {isLoading && roster.length === 0 && <div className="Muted">Ładowanie…</div>}
          {!isLoading && groupId && filtered.length === 0 && <div className="Muted">Brak zawodników w tej grupie.</div>}

          <div className="TeamRosterGrid" aria-label="Zawodnicy">
            {filtered.map((p) => {
              const initials = `${(p.first_name || '').slice(0, 1)}${(p.last_initial || '').slice(0, 1)}`.toUpperCase() || '•';
              const isMyChildCard = props.me.role === 'PARENT' && !!props.selectedChild?.id && props.selectedChild.id === p.id;
              const trainingsValue = isMyChildCard && !isTeamMyChildEventsLoading ? String(teamMyChildStats.trainingsTotal) : '—';
              const matchesValue = isMyChildCard && !isTeamMyChildEventsLoading ? String(teamMyChildStats.matchesTotal) : '—';
              const attendanceValue = isMyChildCard && !isTeamMyChildEventsLoading ? `${teamMyChildStats.attendancePct}%` : '—';
              const abs = rosterAbsenceLabel(p);
              const canAddIndividual = props.me.status === 'ACTIVE';
              const openProfile = () => {
                setProfileChild(p);
                setProfileRangeDays(30);
              };
              return (
                <div
                  key={p.id}
                  role="button"
                  tabIndex={0}
                  className={['TeamPlayerCard', p.unavailability_kind ? 'is-unavailable' : ''].filter(Boolean).join(' ')}
                  onClick={openProfile}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') openProfile();
                  }}
                >
                  <div className="TeamPlayerTop">
                    <div className="TeamPlayerHeader">
                      <div
                        className={['TeamAvatarRing', p.unavailability_kind ? 'is-unavailable' : ''].filter(Boolean).join(' ')}
                        aria-hidden="true"
                      >
                        {rosterPhotoById[p.id] ? (
                          <img className="TeamAvatarImg" src={rosterPhotoById[p.id] as string} alt="" />
                        ) : (
                          <div className="TeamAvatarFallback">{initials}</div>
                        )}
                      </div>
                      {p.shirt_number || abs || isIndividualCoachRoster ? (
                        <div className="TeamPlayerBadges">
                          {p.shirt_number ? (
                            <div className="TeamJersey" aria-label={`No. ${p.shirt_number}`}>
                              <div className="TeamJerseyLabel">NO.</div>
                              <div className="TeamJerseyValue">{p.shirt_number}</div>
                            </div>
                          ) : null}
                          {abs ? <div className="TeamAbsence">{abs}</div> : null}
                        </div>
                      ) : null}
                    </div>
                    <div className="TeamPlayerName">{`${p.first_name} ${p.last_initial}`.toUpperCase()}</div>
                    <div className="TeamPlayerMeta">{p.birth_year ? `ROCZNIK ${p.birth_year}` : 'ZAWODNIK'}</div>
                  </div>

                  {isIndividualCoachRoster ? (
                    <div className="TeamPlayerStatsRow TeamPlayerStatsRow--shortcuts" aria-label="Skróty">
                      <button
                        type="button"
                        className="TeamShortcutBtn"
                        aria-label="Kalendarz podopiecznego"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate('kalendarz', { cc: p.id });
                        }}
                      >
                        <ClockIcon className="UpcomingMatchActionIcon" />
                        <div className="TeamShortcutLabel">Kalendarz</div>
                      </button>
                      <button
                        type="button"
                        className="TeamShortcutBtn"
                        aria-label="Dodaj trening indywidualny"
                        disabled={!canAddIndividual}
                        onClick={(e) => {
                          e.stopPropagation();
                          void (async () => {
                            if (props.me.status !== 'ACTIVE') return;
                            let gid = (p.group_id ?? '').trim();
                            if (!gid) {
                              try {
                                const next = await apiCoachMyChildren(props.token);
                                props.onSetCoachChildrenOverride?.(next);
                                const updated = next.find((x) => x.id === p.id) ?? null;
                                gid = (updated?.group_id ?? '').trim();
                              } catch {
                                gid = '';
                              }
                            }
                            navigate('kalendarz', { ic: p.id, it: 'TRAINING', ig: (gid || 'individual').trim() });
                          })();
                        }}
                      >
                        <TrainingIcon className="UpcomingMatchActionIcon" />
                        <div className="TeamShortcutLabel">Trening</div>
                      </button>
                    </div>
                  ) : (
                    <div className="TeamPlayerStatsRow" aria-hidden="true">
                      <div className="TeamStat">
                        <div
                          className="TeamStatLabel"
                          aria-label="Treningi"
                          title="Treningi"
                          onPointerDown={() => onTeamStatHintStart('trainings')}
                          onPointerUp={onTeamStatHintEnd}
                          onPointerCancel={onTeamStatHintEnd}
                          onPointerLeave={onTeamStatHintEnd}
                        >
                          <TrainingIcon className="TeamStatIcon" />
                          {teamStatHint === 'trainings' && <div className="TeamStatTooltip">TRENINGI</div>}
                        </div>
                        <div className="TeamStatValue">{trainingsValue}</div>
                      </div>
                      <div className="TeamStat">
                        <div
                          className="TeamStatLabel"
                          aria-label="Mecze"
                          title="Mecze"
                          onPointerDown={() => onTeamStatHintStart('matches')}
                          onPointerUp={onTeamStatHintEnd}
                          onPointerCancel={onTeamStatHintEnd}
                          onPointerLeave={onTeamStatHintEnd}
                        >
                          <SoccerIcon className="TeamStatIcon" />
                          {teamStatHint === 'matches' && <div className="TeamStatTooltip">MECZE</div>}
                        </div>
                        <div className="TeamStatValue">{matchesValue}</div>
                      </div>
                      <div className="TeamStat">
                        <div
                          className="TeamStatLabel"
                          aria-label="Obecność"
                          title="Obecność"
                          onPointerDown={() => onTeamStatHintStart('attendance')}
                          onPointerUp={onTeamStatHintEnd}
                          onPointerCancel={onTeamStatHintEnd}
                          onPointerLeave={onTeamStatHintEnd}
                        >
                          <CheckIcon className="TeamStatIcon" />
                          {teamStatHint === 'attendance' && <div className="TeamStatTooltip">OBECNOŚĆ</div>}
                        </div>
                        <div className="TeamStatValue">{attendanceValue}</div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {tab === 'trenerzy' && (
        <>
          {errorText && <div className="ErrorBanner">{errorText}</div>}
          {isCoachesLoading && coaches.length === 0 ? (
            <div className="Muted">Ładowanie…</div>
          ) : coaches.length === 0 ? (
            <div className="Muted">Brak przypisanych trenerów.</div>
          ) : (
            <div className="TeamCoachGrid" aria-label="Trenerzy">
              {coaches.map((c) => (
                <div key={c.id} className="TeamCoachCard TeamCoachCard--grid">
                  <div className="TeamAvatarRing" aria-hidden="true">
                    <div className="TeamAvatarFallback">{coachInitialsFromEmail(c.email)}</div>
                  </div>
                  <div className="TeamCoachText">
                    <div className="TeamCoachName">{prettyUserLabelFromBackendLabel(c.email).toUpperCase()}</div>
                    <div className="TeamCoachRole">TRENER</div>
                  </div>
                  {props.me.role === 'PARENT' && (
                    <PrimaryButton
                      onClick={() => startChatWithCoach(c)}
                      disabled={isChatStarting || props.me.status !== 'ACTIVE' || !props.selectedChild?.id}
                      type="button"
                    >
                      Napisz
                    </PrimaryButton>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {tab === 'rozgrywki' && (
        <>
          <Card title={`Rozgrywki • ${groupLabel}`}>
            {rozgrywkiError && <div className="ErrorBanner">{rozgrywkiError}</div>}
            {isRozgrywkiLoading && rozgrywkiEvents.length === 0 && <div className="Muted">Ładowanie…</div>}
            {!isRozgrywkiLoading && rozgrywkiEvents.length === 0 && <div className="Muted">Brak rozgrywek w tej grupie.</div>}
            {rozgrywkiEvents.length > 0 && (
              <div className="List" role="list" aria-label="Rozgrywki grupy">
                {rozgrywkiEvents.map((ev) => {
                  const opponentLabel = ev.opponent_name || opponentLabelFromMatchTitle(ev.title || '');
                  const isFinalized = !!ev.match_finalized;
                  const scoreText = isFinalized
                    ? `${Number(ev.home_score ?? 0)}:${Number(ev.away_score ?? 0)}`
                    : null;
                  const canEditResult = props.me.status === 'ACTIVE' && (props.me.role === 'ADMIN' || props.me.role === 'COACH' || props.me.role === 'COORDINATOR');
                  return (
                    <div key={ev.id} className="ListItem" role="listitem">
                      <div className="ListItemTop">
                        <div className="ListItemTitle">
                          {ev.status === 'CANCELLED' ? '❌ ODWOŁANY • ' : ''}
                          {`Błonianka vs ${opponentLabel || 'Przeciwnik'}`}
                        </div>
                        {isFinalized ? (
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                            <span
                              className="ListItemBadge"
                              style={{ background: 'var(--color-primary-50, #e8eaf6)', color: 'var(--color-primary-700, #1a237e)', fontWeight: 800, fontSize: 14 }}
                            >
                              🏁 {scoreText}
                            </span>
                            {(ev.external_source ?? '').trim() === 'LNP' && (
                              <span className="LnpBadge"><span className="LnpBadgeDot"></span>LNP</span>
                            )}
                          </div>
                        ) : (
                          <span className="ListItemBadge">{formatDateTime(ev.start_at)}</span>
                        )}
                      </div>
                      <div style={{ marginTop: 6, fontSize: 13, color: 'var(--color-neutral-600, #666)' }}>
                        📍 {ev.location || 'Brak lokalizacji'}
                        {ev.match_kind ? ` • ${matchKindShort(ev.match_kind)}` : ''}
                        {!isFinalized && ev.status !== 'CANCELLED' && new Date(ev.start_at).getTime() < Date.now() ? ' • W TRAKCIE' : ''}
                      </div>
                      {canEditResult && (
                        <div style={{ marginTop: 10, display: 'flex', justifyContent: 'flex-end' }}>
                          <SecondaryButton onClick={() => openResultEdit(ev)} disabled={isResultSaving || isRozgrywkiLoading} type="button">
                            Edytuj wynik
                          </SecondaryButton>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </Card>

          {resultEditOpen && resultEditEvent && renderLiveMatchEditorV6()}
        </>
      )}
    </>
  );

  return (
    <div className="Page">
      {TEAM_UI_VARIANT === 'v2' ? renderV2() : renderLegacy()}

      {profileChild && (
        <div
          className="SheetOverlay"
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setProfileChild(null);
          }}
        >
          <div className="Sheet Sheet--playerProfile">
            <div className="MatchCreateHeader">
              <div className="MatchCreateHeaderTitle">PROFIL ZAWODNIKA</div>
              <button className="IconSquare" type="button" aria-label="Zamknij" onClick={() => setProfileChild(null)}>
                <XIcon />
              </button>
            </div>

            <div className="PlayerProfileHero">
              <div className="PlayerProfileHeroTop">
                <div className="PlayerProfileClub">
                  <div className="PlayerProfileClubMark" aria-hidden="true">
                    <SoccerIcon />
                  </div>
                  <div className="PlayerProfileClubText">
                    <div className="PlayerProfileClubName">{(profileChild.group_name || 'AKADEMIA').toUpperCase()}</div>
                    <div className="PlayerProfileClubSub">PROFIL</div>
                  </div>
                </div>
              </div>

              {(() => {
                const initials = `${(profileChild.first_name || '').slice(0, 1)}${(profileChild.last_initial || '').slice(0, 1)}`.toUpperCase();
                const abs = rosterAbsenceLabel(profileChild);
                return (
                  <div className="PlayerProfileIdentity">
                    <div className="PlayerProfileIdentityLeft">
                      <div className="PlayerProfileAvatarWrap">
                        {profilePhotoUrl ? (
                          <img className="PlayerProfileAvatarImg" src={profilePhotoUrl} alt="" />
                        ) : (
                          <div className="PlayerProfileAvatar">{initials || '•'}</div>
                        )}
                        {canManageProfileChildPhoto && (
                          <>
                            <input
                              ref={profilePhotoInputRef}
                              type="file"
                              accept="image/jpeg,image/png,image/webp"
                              onChange={(e) => {
                                const f = e.target.files?.[0];
                                e.target.value = '';
                                if (!f) return;
                                openProfilePhotoCrop(f);
                              }}
                              disabled={isProfilePhotoUploading || isProfileLoading}
                              className="HiddenInput"
                            />
                            <button
                              type="button"
                              className="PlayerProfileAvatarAction"
                              onClick={() => profilePhotoInputRef.current?.click()}
                              disabled={isProfilePhotoUploading || isProfileLoading}
                              aria-label="Zmień zdjęcie"
                            >
                              <PlusIcon />
                            </button>
                          </>
                        )}
                      </div>
                      <div className="PlayerProfileIdentityText">
                        <div className="PlayerProfileName">{`${profileChild.first_name} ${profileChild.last_initial}`.toUpperCase()}</div>
                        <div className="PlayerProfileMeta">
                          {profileChild.birth_year ? `ROCZNIK ${profileChild.birth_year}` : 'ZAWODNIK'}
                          {profileChild.group_year ? ` • U${new Date().getFullYear() - profileChild.group_year}` : ''}
                        </div>
                      </div>
                    </div>
                    {profileChild.shirt_number || abs ? (
                      <div className="PlayerProfileIdentityRight">
                        {profileChild.shirt_number ? (
                          <div className="PlayerProfileJersey" aria-label={`No. ${profileChild.shirt_number}`}>
                            <div className="PlayerProfileJerseyLabel">NO.</div>
                            <div className="PlayerProfileJerseyValue">{profileChild.shirt_number}</div>
                          </div>
                        ) : null}
                        {abs ? <div className="PlayerProfileAbsence">{abs}</div> : null}
                      </div>
                    ) : null}
                  </div>
                );
              })()}

              {quickReactions.length > 0 && (
                <div className="PlayerProfileReactions" aria-label="Szybkie reakcje">
                  {quickReactions.slice(0, 6).map((r) => (
                    <div key={r.id} className="PlayerProfileReactionBadge" aria-label={r.text}>
                      <div className="PlayerProfileReactionIcon" aria-hidden="true">
                        {quickReactionEmojiForText(r.text)}
                      </div>
                      <div className="PlayerProfileReactionText">{r.text}</div>
                    </div>
                  ))}
                  {quickReactions.length > 6 && <div className="PlayerProfileReactionMore">{`+${quickReactions.length - 6}`}</div>}
                </div>
              )}

              {props.me.role === 'PARENT' && props.selectedChild?.id === profileChild.id ? (
                <>
                  <div className="PlayerProfileQuickTitle">SZYBKI PRZEGLĄD</div>
                  <div className="PlayerProfileQuickGrid">
                    <div className="PlayerProfileQuickTile">
                      <TrainingIcon className="PlayerProfileQuickIcon" />
                      <div className="PlayerProfileQuickLabel">TRENINGI</div>
                      <div className="PlayerProfileQuickValue">{isProfileLoading ? '—' : profileStats.trainingsTotal}</div>
                    </div>
                    <div className="PlayerProfileQuickTile">
                      <SoccerIcon className="PlayerProfileQuickIcon" />
                      <div className="PlayerProfileQuickLabel">MECZE</div>
                      <div className="PlayerProfileQuickValue">{isProfileLoading ? '—' : profileStats.matchesTotal}</div>
                    </div>
                    <div className="PlayerProfileQuickTile">
                      <CheckIcon className="PlayerProfileQuickIcon" />
                      <div className="PlayerProfileQuickLabel">OBECNOŚĆ</div>
                      <div className="PlayerProfileQuickValue">{isProfileLoading ? '—' : `${profileStats.attendancePct}%`}</div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="PlayerProfileQuickTitle">{isIndividualCoachRoster ? 'SKRÓTY' : 'PODGLĄD'}</div>
                  {isIndividualCoachRoster && (
                    <div className="PlayerProfileQuickGrid" aria-label="Skróty podopiecznego">
                      <button
                        type="button"
                        className="PlayerProfileQuickTile"
                        onClick={() => navigate('kalendarz', { cc: profileChild.id })}
                        disabled={isProfileLoading}
                      >
                        <ClockIcon className="PlayerProfileQuickIcon" />
                        <div className="PlayerProfileQuickLabel">KALENDARZ</div>
                        <div className="PlayerProfileQuickValue">Otwórz</div>
                      </button>
                      <button
                        type="button"
                        className="PlayerProfileQuickTile"
                        onClick={() => {
                          if (!profileChild.group_id) return;
                          navigate('kalendarz', { ic: profileChild.id, it: 'TRAINING', ig: profileChild.group_id });
                          setProfileChild(null);
                        }}
                        disabled={isProfileLoading || !profileChild.group_id}
                      >
                        <TrainingIcon className="PlayerProfileQuickIcon" />
                        <div className="PlayerProfileQuickLabel">TRENING INDYW.</div>
                        <div className="PlayerProfileQuickValue">Dodaj</div>
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="PlayerProfileBody">
              {profileError && <div className="ErrorBanner">{profileError}</div>}

              {canManageProfileChild && (
                <Card title="Edycja zawodnika">
                  <div className="Muted">Trener lub administrator może poprawić podstawowe dane zawodnika bezpośrednio z profilu.</div>
                  {playerEditInfo && <div className="InfoBanner">{playerEditInfo}</div>}
                  {playerEditError && <div className="ErrorBanner">{playerEditError}</div>}
                  <div className="MatchCreateGrid2">
                    <input
                      className="TextInput"
                      value={playerEditFirstName}
                      onChange={(e) => setPlayerEditFirstName(e.target.value)}
                      placeholder="Imię"
                      disabled={isPlayerEditSaving || isProfileLoading}
                    />
                    <input
                      className="TextInput"
                      value={playerEditLastInitial}
                      onChange={(e) => setPlayerEditLastInitial(e.target.value)}
                      placeholder="Inicjał nazwiska"
                      maxLength={3}
                      disabled={isPlayerEditSaving || isProfileLoading}
                    />
                    <input
                      className="TextInput"
                      inputMode="numeric"
                      value={playerEditBirthYear}
                      onChange={(e) => setPlayerEditBirthYear(e.target.value)}
                      placeholder="Rocznik"
                      disabled={isPlayerEditSaving || isProfileLoading}
                    />
                    <input
                      className="TextInput"
                      inputMode="numeric"
                      value={playerEditShirtNumber}
                      onChange={(e) => setPlayerEditShirtNumber(e.target.value)}
                      placeholder="Numer na koszulce"
                      disabled={isPlayerEditSaving || isProfileLoading}
                    />
                  </div>
                  <div className="SheetActionsRow">
                    <PrimaryButton type="button" disabled={isPlayerEditSaving || isProfileLoading} onClick={saveProfileChild}>
                      {isPlayerEditSaving ? 'Zapisywanie…' : 'Zapisz dane'}
                    </PrimaryButton>
                    <SecondaryButton
                      type="button"
                      disabled={isPlayerEditSaving || isProfileLoading}
                      onClick={() => {
                        if (!profileChild) return;
                        setPlayerEditFirstName(profileChild.first_name ?? '');
                        setPlayerEditLastInitial(profileChild.last_initial ?? '');
                        setPlayerEditBirthYear(Number.isFinite(profileChild.birth_year as number) ? String(profileChild.birth_year) : '');
                        setPlayerEditShirtNumber(Number.isFinite(profileChild.shirt_number as number) ? String(profileChild.shirt_number) : '');
                        setPlayerEditError(null);
                        setPlayerEditInfo(null);
                      }}
                    >
                      Cofnij zmiany
                    </SecondaryButton>
                  </div>
                </Card>
              )}

              {canManageProfileChildPhoto && profilePhotoUrl && (
                <div className="PlayerProfilePhotoActions">
                  <SecondaryButton type="button" disabled={isProfilePhotoUploading || isProfileLoading} onClick={clearProfilePhoto}>
                    Usuń zdjęcie
                  </SecondaryButton>
                </div>
              )}

              {isIndividualCoachRoster && props.me.role === 'COACH' && props.me.status === 'ACTIVE' && (
                <Card title="Podopieczny">
                  <div className="Muted">Usuń tego zawodnika z Twojej listy podopiecznych.</div>
                  <div className="SheetActionsRow">
                    <button
                      type="button"
                      className="SecondaryButton TeamUnassignBtn"
                      disabled={isProfileLoading || unassigningChildId === profileChild.id}
                      onClick={async () => {
                        const childId = profileChild.id;
                        if (!window.confirm('Usunąć tego podopiecznego z Twojej listy?')) return;
                        setUnassigningChildId(childId);
                        try {
                          const next = await apiCoachRemoveChild(props.token, childId);
                          props.onSetCoachChildrenOverride?.(next);
                          setProfileChild(null);
                        } catch (err) {
                          setErrorText(humanizeHttpError(err, 'Nie udało się usunąć podopiecznego.'));
                        } finally {
                          setUnassigningChildId(null);
                        }
                      }}
                      aria-label="Usuń z podopiecznych"
                    >
                      Usuń z podopiecznych
                    </button>
                  </div>
                </Card>
              )}

              {props.me.role === 'PARENT' && props.selectedChild?.id === profileChild.id && (
                <Card title="Trening prywatny">
                  <div className="Muted">Dodaj trening indywidualny widoczny tylko dla Ciebie. Pojawi się w kalendarzu i w Aktualnościach.</div>
                  <div className="SheetActionsRow">
                    <PrimaryButton
                      type="button"
                      disabled={isProfileLoading}
                      onClick={() => {
                        navigate('kalendarz', { ptc: profileChild.id, pt: '1' });
                        setProfileChild(null);
                      }}
                    >
                      Dodaj trening prywatny
                    </PrimaryButton>
                  </div>
                </Card>
              )}

              {(props.me.role === 'COACH' || props.me.role === 'ADMIN') && !isIndividualCoachRoster && (
                <Card title="Wydarzenie indywidualne">
                  <div className="Muted">Dodaj trening lub mecz widoczny tylko dla tego zawodnika (rodzica) oraz trenerów/admina.</div>
                  <div className="SheetActionsRow">
                    <PrimaryButton
                      type="button"
                      disabled={isProfileLoading || !profileChild.group_id}
                      onClick={() => {
                        if (!profileChild.group_id) return;
                        navigate('kalendarz', { ic: profileChild.id, it: 'TRAINING', ig: profileChild.group_id });
                        setProfileChild(null);
                      }}
                    >
                      Dodaj trening
                    </PrimaryButton>
                    <SecondaryButton
                      type="button"
                      disabled={isProfileLoading || !profileChild.group_id}
                      onClick={() => {
                        if (!profileChild.group_id) return;
                        navigate('kalendarz', { ic: profileChild.id, it: 'MATCH', ig: profileChild.group_id });
                        setProfileChild(null);
                      }}
                    >
                      Dodaj mecz
                    </SecondaryButton>
                  </div>
                </Card>
              )}

              {props.me.role === 'PARENT' && props.selectedChild?.id === profileChild.id && (
                <div className="PlayerProfileRange">
                  <div className="Muted">Okres</div>
                  <div className="NotificationActions" aria-label="Okres statystyk">
                    {([
                      { d: 30 as const, label: '30 dni' },
                      { d: 90 as const, label: '90 dni' },
                      { d: 365 as const, label: 'Rok' },
                    ] as const).map((x) => (
                      <button
                        key={x.d}
                        type="button"
                        className={`Pill ${profileRangeDays === x.d ? 'is-active' : ''}`}
                        onClick={() => setProfileRangeDays(x.d)}
                        disabled={isProfileLoading}
                      >
                        {x.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {isMyChild && (
                <Card title="Długa nieobecność">
                  <div className="Muted">Ustaw zakres dat dla dziecka (wpływa na powołania i treningi).</div>
                  {longAbsInfo && <div className="InfoBanner">{longAbsInfo}</div>}
                  {longAbsError && <div className="ErrorBanner">{longAbsError}</div>}

                  <div className="Muted">Powód</div>
                  <div className="NotificationActions" aria-label="Powód długiej nieobecności">
                    {([
                      { k: 'CHOROBA' as const, label: 'Choroba' },
                      { k: 'KONTUZJA' as const, label: 'Kontuzja' },
                      { k: 'INNE' as const, label: 'Inne' },
                    ] as const).map((x) => (
                      <button
                        key={x.k}
                        type="button"
                        className={`Pill ${longAbsReason === x.k ? 'is-active' : ''}`}
                        onClick={() => setLongAbsReason(x.k)}
                        disabled={isProfileLoading || isLongAbsSaving}
                      >
                        {x.label}
                      </button>
                    ))}
                  </div>

                  <div className="MatchCreateGrid2">
                    <div className="MatchCreateField MatchCreateField--compact">
                      <div className="MatchCreateFieldIcon" aria-hidden="true">
                        <ClockIcon />
                      </div>
                      <div className="MatchCreateFieldBody">
                        <div className="MatchCreateFieldLabel">OD</div>
                        <input
                          className="MatchCreateInput"
                          type="date"
                          value={longAbsFrom}
                          onChange={(e) => setLongAbsFrom(e.target.value)}
                          disabled={isProfileLoading || isLongAbsSaving}
                        />
                      </div>
                    </div>

                    <div className="MatchCreateField MatchCreateField--compact">
                      <div className="MatchCreateFieldIcon" aria-hidden="true">
                        <ClockIcon />
                      </div>
                      <div className="MatchCreateFieldBody">
                        <div className="MatchCreateFieldLabel">DO</div>
                        <input
                          className="MatchCreateInput"
                          type="date"
                          value={longAbsUntil}
                          onChange={(e) => setLongAbsUntil(e.target.value)}
                          disabled={isProfileLoading || isLongAbsSaving}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="MatchCreateField">
                    <div className="MatchCreateFieldIcon" aria-hidden="true">
                      <ChatIcon />
                    </div>
                    <div className="MatchCreateFieldBody">
                      <div className="MatchCreateFieldLabel">OPIS (OPCJONALNIE)</div>
                      <textarea
                        className="MatchCreateTextArea"
                        rows={3}
                        value={longAbsNote}
                        onChange={(e) => setLongAbsNote(e.target.value)}
                        placeholder="Np. L4 do piątku, wyjazd rodzinny…"
                        disabled={isProfileLoading || isLongAbsSaving}
                      />
                    </div>
                  </div>

                  <PrimaryButton
                    onClick={onSaveLongAbsence}
                    disabled={
                      isProfileLoading ||
                      isLongAbsSaving ||
                      !longAbsFrom.trim() ||
                      !longAbsUntil.trim() ||
                      longAbsUntil.trim() < longAbsFrom.trim() ||
                      longAbsNote.trim().length > 240
                    }
                    type="button"
                  >
                    Zapisz
                  </PrimaryButton>
                  {((profileChild?.unavailability_kind ?? '').trim() || '').length > 0 && (
                    <SecondaryButton onClick={onClearLongAbsence} disabled={isProfileLoading || isLongAbsSaving} type="button">
                      Usuń długą nieobecność
                    </SecondaryButton>
                  )}
                </Card>
              )}

              {((props.me.role === 'PARENT' && props.selectedChild?.id === profileChild.id) || (props.me.role === 'COACH' && !isIndividualCoachRoster)) && (
                <Card title="Frekwencja">
                  <div className="KeyValue">
                    <div className="Key">Treningi: obecności</div>
                    <div className="Value">{isProfileLoading ? '—' : profileStats.trainingsIn}</div>
                  </div>
                  <div className="KeyValue">
                    <div className="Key">Treningi: nieobecności</div>
                    <div className="Value">{isProfileLoading ? '—' : profileStats.trainingsOut}</div>
                  </div>
                  <div className="KeyValue">
                    <div className="Key">Treningi: spóźnienia</div>
                    <div className="Value">{isProfileLoading ? '—' : profileStats.trainingsLate}</div>
                  </div>
                  <div className="KeyValue">
                    <div className="Key">Mecze: potwierdzone</div>
                    <div className="Value">{isProfileLoading ? '—' : profileStats.matchesAccepted}</div>
                  </div>
                  <div className="KeyValue">
                    <div className="Key">Mecze: odmowy</div>
                    <div className="Value">{isProfileLoading ? '—' : profileStats.matchesDeclined}</div>
                  </div>
                  <div className="KeyValue">
                    <div className="Key">Mecze: brak odpowiedzi</div>
                    <div className="Value">{isProfileLoading ? '—' : profileStats.matchesPending}</div>
                  </div>
                </Card>
              )}

              <Card title="Historia">
                {!isMyChild ? (
                  <div className="Muted">Dostępne tylko dla własnego dziecka.</div>
                ) : isProfileLoading && profileHistory.length === 0 ? (
                  <div className="Muted">Ładowanie…</div>
                ) : profileHistory.length === 0 ? (
                  <div className="Muted">Brak wydarzeń w wybranym okresie.</div>
                ) : (
                  <div className="List">
                    {profileHistory.map((e) => {
                      const kindLabel = e.type === 'MATCH' ? 'MECZ' : 'TRENING';
                      const title =
                        e.type === 'MATCH'
                          ? (e.opponent_name || opponentLabelFromMatchTitle(e.title || '') || 'Mecz').toUpperCase()
                          : (e.title || 'Trening').toUpperCase();

                      const statusLabel =
                        e.type === 'MATCH'
                          ? e.match_rsvp === 'ACCEPTED'
                            ? 'POTWIERDZONE'
                            : e.match_rsvp === 'DECLINED'
                              ? 'NIEOBECNY'
                              : 'BRAK ODPOWIEDZI'
                          : e.attendance_status === 'OUT'
                            ? 'NIEOBECNY'
                            : e.attendance_status === 'LATE'
                              ? 'SPÓŹNIONY'
                              : 'OBECNY';

                      return (
                        <div key={e.id} className="ListItem">
                          <div className="ListItemTop">
                            <div className="ListItemTitle">{title}</div>
                            <div className="ListItemBadge">{kindLabel}</div>
                          </div>
                          <div className="Muted">{`${formatDateTime(e.start_at)} • ${e.location || ''}`.trim()}</div>
                          <div className="Muted">{statusLabel}</div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </Card>

              <Card title="Testy sprawnościowe">
                {fitnessError && <div className="ErrorBanner">{fitnessError}</div>}
                {isMyChild || props.me.role === 'ADMIN' || props.me.role === 'COACH' || props.me.role === 'COORDINATOR' ? (
                  <>
                    {((props.me.role === 'ADMIN' || props.me.role === 'COACH' || props.me.role === 'COORDINATOR') && props.me.status === 'ACTIVE') && (
                      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
                        <PrimaryButton onClick={openFitnessAdd} disabled={isFitnessLoading || isFitnessSaving} type="button">
                          + Dodaj test
                        </PrimaryButton>
                      </div>
                    )}
                    {isFitnessLoading && fitnessItems.length === 0 ? (
                      <div className="Muted">Ładowanie testów…</div>
                    ) : fitnessItems.length === 0 ? (
                      <div className="Muted">Brak testów sprawnościowych. Trener lub Administrator może dodać pierwszy wynik.</div>
                    ) : (
                      <div className="List" role="list" aria-label="Testy sprawnościowe zawodnika">
                        {fitnessItems.map((ft) => {
                          const canEditFitness = props.me.status === 'ACTIVE' && (props.me.role === 'ADMIN' || props.me.role === 'COACH' || props.me.role === 'COORDINATOR');
                          return (
                            <div key={ft.id} className="ListItem" role="listitem">
                              <div className="ListItemTop">
                                <div className="ListItemTitle">{ft.test_type.toUpperCase()}</div>
                                <div
                                  className="ListItemBadge"
                                  style={{ fontWeight: 800, fontSize: 15 }}
                                >
                                  {`${ft.value} ${ft.unit}`}
                                </div>
                              </div>
                              <div style={{ marginTop: 6, fontSize: 13, color: 'var(--color-neutral-600, #666)' }}>
                                📅 {ft.test_date}
                                {ft.created_by_email ? ` • ${prettyUserLabelFromBackendLabel(ft.created_by_email)}` : ''}
                              </div>
                              {ft.note ? <div style={{ marginTop: 6, fontSize: 13 }}>{ft.note}</div> : null}
                              {canEditFitness && (
                                <div style={{ marginTop: 10, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                                  <SecondaryButton onClick={() => openFitnessEdit(ft)} disabled={isFitnessSaving || isFitnessLoading} type="button">
                                    Edytuj
                                  </SecondaryButton>
                                  <SecondaryButton onClick={() => void deleteFitness(ft)} disabled={isFitnessSaving || isFitnessLoading} type="button">
                                    Usuń
                                  </SecondaryButton>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="Muted">Dostępne tylko dla rodzica tego dziecka, Trenera lub Administratora.</div>
                )}
              </Card>

              {(props.me.role === 'COACH' || props.me.role === 'ADMIN') && (
                <Card title={`Szybkie reakcje (${quickReactionsTtlDays} dni)`}>
                  {quickReactionError && <div className="ErrorBanner">{quickReactionError}</div>}
                  {quickReactionInfo && <div className="InfoBanner">{quickReactionInfo}</div>}

                  {props.me.role === 'COACH' && (
                    <>
                      <div className="Muted">Widoczność</div>
                      <div className="NotificationActions" aria-label="Widoczność reakcji">
                        {([
                          { k: 'COACH_PRIVATE' as const, label: 'Tylko dla mnie' },
                          { k: 'PARENT' as const, label: 'Dla rodzica' },
                        ] as const).map((x) => (
                          <button
                            key={x.k}
                            type="button"
                            className={`Pill ${quickReactionVisibility === x.k ? 'is-active' : ''}`}
                            onClick={() => setQuickReactionVisibility(x.k)}
                            disabled={isProfileLoading || isQuickReactionsLoading || isQuickReactionsSaving}
                          >
                            {x.label}
                          </button>
                        ))}
                      </div>
                    </>
                  )}

                  <div className="Muted">Skróty</div>
                  <div className="NotificationActions" aria-label="Szybkie reakcje: skróty">
                    {[
                      'Super zaangażowanie',
                      'Świetna postawa',
                      'Brawa za frekwencję',
                      'Mega progres',
                      'Wzorowe zachowanie',
                    ].map((t) => (
                      <button
                        key={t}
                        type="button"
                        className="Pill"
                        onClick={() => onAddQuickReaction(t)}
                        disabled={isProfileLoading || isQuickReactionsLoading || isQuickReactionsSaving}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                  <div className="Muted">Własna</div>
                  <div className="NotificationActions" aria-label="Szybkie reakcje: dodaj">
                    <input
                      className="TextInput"
                      value={quickReactionText}
                      onChange={(e) => setQuickReactionText(e.target.value)}
                      placeholder="Np. Super walka w obronie"
                      disabled={isProfileLoading || isQuickReactionsLoading || isQuickReactionsSaving}
                    />
                    <PrimaryButton
                      type="button"
                      onClick={() => onAddQuickReaction(quickReactionText)}
                      disabled={
                        isProfileLoading ||
                        isQuickReactionsLoading ||
                        isQuickReactionsSaving ||
                        quickReactionText.trim().length === 0 ||
                        quickReactionText.trim().length > 80
                      }
                    >
                      Dodaj
                    </PrimaryButton>
                  </div>

                  {isQuickReactionsLoading && quickReactions.length === 0 ? (
                    <div className="Muted">Ładowanie…</div>
                  ) : quickReactions.length === 0 ? (
                    <div className="Muted">Brak reakcji.</div>
                  ) : (
                    <div className="QuickReactionsGrid" aria-label="Lista reakcji">
                      {quickReactions.map((r) => (
                        <div key={r.id} className="QuickReactionChip">
                          <div className="QuickReactionChipIcon" aria-hidden="true">
                            {quickReactionEmojiForText(r.text)}
                          </div>
                          <div className="QuickReactionChipText">{r.text}</div>
                          <button
                            className="QuickReactionChipRemove"
                            type="button"
                            onClick={() => onDeleteQuickReaction(r.id)}
                            disabled={isProfileLoading || isQuickReactionsSaving}
                            aria-label="Usuń reakcję"
                          >
                            <XIcon />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              )}

              <Card title="Notatka trenera">
                {coachNoteError && <div className="ErrorBanner">{coachNoteError}</div>}
                {coachNoteInfo && <div className="InfoBanner">{coachNoteInfo}</div>}

                {props.me.role === 'COACH' || props.me.role === 'ADMIN' ? (
                  <>
                    <div className="Muted">Widoczność</div>
                    <div className="NotificationActions" aria-label="Widoczność notatki">
                      {([
                        { k: 'PRIVATE' as const, label: 'Tylko dla mnie' },
                        { k: 'PARENT' as const, label: 'Dla rodzica' },
                      ] as const).map((x) => (
                        <button
                          key={x.k}
                          type="button"
                          className={`Pill ${coachNoteVisibility === x.k ? 'is-active' : ''}`}
                          onClick={() => setCoachNoteVisibility(x.k)}
                          disabled={isProfileLoading || isCoachNoteLoading || isCoachNoteSaving}
                        >
                          {x.label}
                        </button>
                      ))}
                    </div>
                    <textarea
                      className="TextArea"
                      rows={5}
                      value={coachNoteText}
                      onChange={(e) => setCoachNoteText(e.target.value)}
                      placeholder="Wpisz notatkę…"
                      disabled={isProfileLoading || isCoachNoteLoading || isCoachNoteSaving}
                    />
                    {coachNoteUpdatedAt && <div className="Muted">{`Ostatnia zmiana: ${formatDateTime(coachNoteUpdatedAt)}`}</div>}
                    <div className="NotificationActions" aria-label="Akcje notatki">
                      <PrimaryButton
                        onClick={() => onSaveCoachNote()}
                        disabled={
                          isProfileLoading ||
                          isCoachNoteLoading ||
                          isCoachNoteSaving ||
                          coachNoteText.trim().length === 0 ||
                          coachNoteText.trim().length > 2000
                        }
                        type="button"
                      >
                        Zapisz
                      </PrimaryButton>
                      {coachNoteText.trim().length > 0 && (
                        <SecondaryButton
                          onClick={() => onSaveCoachNote(true)}
                          disabled={isProfileLoading || isCoachNoteLoading || isCoachNoteSaving}
                          type="button"
                        >
                          Usuń
                        </SecondaryButton>
                      )}
                    </div>
                  </>
                ) : !isMyChild ? (
                  <div className="Muted">Dostępne tylko dla własnego dziecka.</div>
                ) : isCoachNoteLoading ? (
                  <div className="Muted">Ładowanie…</div>
                ) : coachNoteText.trim().length > 0 ? (
                  <div className="CoachNoteBody">{coachNoteText}</div>
                ) : (
                  <div className="Muted">Brak notatek dla rodzica.</div>
                )}
              </Card>
            </div>

            {props.me.role === 'PARENT' && (
              <div className="PlayerProfileStickyCta">
                <PrimaryButton
                  type="button"
                  disabled={isChatStarting || isProfileLoading || isProfilePhotoUploading}
                  onClick={async () => {
                    if (profileCoaches.length === 1) {
                      await startChatWithCoach(profileCoaches[0]);
                      setProfileChild(null);
                      return;
                    }
                    if (profileCoaches.length > 1) {
                      setContactCoachOpen(true);
                      return;
                    }
                    setProfileChild(null);
                    setTab('trenerzy');
                  }}
                >
                  SKONTAKTUJ SIĘ Z TRENEREM
                </PrimaryButton>
              </div>
            )}

            {contactCoachOpen && (
              <div
                className="SheetOverlay"
                role="dialog"
                aria-modal="true"
                onMouseDown={(e) => {
                  if (isChatStarting) return;
                  if (e.target === e.currentTarget) setContactCoachOpen(false);
                }}
              >
                <div className="Sheet">
                  <div className="SheetHeaderRow SheetHeaderRow--sticky">
                    <div className="SheetTitle">WYBIERZ TRENERA</div>
                    <button
                      className="SheetCloseButton"
                      type="button"
                      aria-label="Zamknij"
                      onClick={() => setContactCoachOpen(false)}
                      disabled={isChatStarting}
                    >
                      ×
                    </button>
                  </div>
                  {profileCoaches.length === 0 ? (
                    <div className="Muted">Brak przypisanych trenerów.</div>
                  ) : (
                    <div className="List">
                      {profileCoaches.map((c) => (
                        <button
                          key={c.id}
                          className="ListItem ListItemButton"
                          type="button"
                          disabled={isChatStarting || isProfileLoading || isProfilePhotoUploading}
                          onClick={async () => {
                            setContactCoachOpen(false);
                            await startChatWithCoach(c);
                            setProfileChild(null);
                          }}
                        >
                          <div className="ListItemTop">
                            <div className="ListItemTitle">{prettyUserLabelFromBackendLabel(c.email)}</div>
                            <div className="ListItemBadge">TRENER</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {fitnessModalOpen && (
        <div className="ModalBackdrop" role="dialog" aria-modal="true" aria-labelledby="fitnessModalTitle">
          <div className="Sheet" style={{ maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
            <div className="SheetHeader" style={{ flexShrink: 0 }}>
              <div id="fitnessModalTitle" className="SheetTitle">
                {fitnessEditing ? 'Edytuj test sprawnościowy' : 'Dodaj test sprawnościowy'}
              </div>
              <button
                className="SheetClose"
                type="button"
                aria-label="Zamknij"
                onClick={() => !isFitnessSaving && setFitnessModalOpen(false)}
                disabled={isFitnessSaving}
              >
                ×
              </button>
            </div>
            <div className="SheetBody" style={{ flexGrow: 1, overflowY: 'auto' }}>
              {fitnessModalError && <div className="ErrorBanner">{fitnessModalError}</div>}
              {fitnessInfo && <div className="InfoBanner">{fitnessInfo}</div>}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>
                  <div className="Label">Typ testu</div>
                  <input
                    className="TextInput"
                    list="fitnessTypes"
                    value={ftType}
                    onChange={(e) => setFtType(e.target.value)}
                    disabled={isFitnessSaving}
                    placeholder="np. Skok wzwyż, Sprint 20m, Brzuszki, Siła, Pływanie 50m…"
                  />
                  <datalist id="fitnessTypes">
                    <option value="Skok wzwyż (CMJ)" />
                    <option value="Sprint 20m" />
                    <option value="Sprint 30m" />
                    <option value="Brzuszki 1 min" />
                    <option value="Przysiady" />
                    <option value="Pompki" />
                    <option value="Bieg 1km" />
                    <option value="Rzut piłką palant" />
                    <option value="Wysk długi" />
                    <option value="Pływanie 50m" />
                    <option value="Siła" />
                    <option value="Zwinność" />
                  </datalist>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <div className="Label">Data testu</div>
                    <input
                      type="date"
                      className="TextInput"
                      value={ftDate}
                      onChange={(e) => setFtDate(e.target.value)}
                      disabled={isFitnessSaving}
                    />
                  </div>
                  <div>
                    <div className="Label">Wartość + jednostka</div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <input
                        type="number"
                        className="TextInput"
                        step="any"
                        value={String(ftValue)}
                        onChange={(e) => setFtValue(Number(e.target.value))}
                        disabled={isFitnessSaving}
                        placeholder="np. 45"
                        style={{ width: '40%' }}
                      />
                      <input
                        className="TextInput"
                        list="fitnessUnits"
                        value={ftUnit}
                        onChange={(e) => setFtUnit(e.target.value)}
                        disabled={isFitnessSaving}
                        placeholder="cm / s / kg / powt"
                        style={{ width: '60%' }}
                      />
                      <datalist id="fitnessUnits">
                        <option value="cm" />
                        <option value="m" />
                        <option value="s" />
                        <option value="min" />
                        <option value="kg" />
                        <option value="powt" />
                        <option value="pkt" />
                      </datalist>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="Label">Notatka (opcjonalnie)</div>
                  <textarea
                    className="TextInput"
                    rows={3}
                    value={ftNote}
                    onChange={(e) => setFtNote(e.target.value)}
                    disabled={isFitnessSaving}
                    placeholder="np. najlepszy wynik w roku"
                    maxLength={200}
                  />
                </div>
              </div>
            </div>
            <div className="SheetFooter" style={{ flexShrink: 0, display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <SecondaryButton onClick={() => setFitnessModalOpen(false)} disabled={isFitnessSaving} type="button">
                Zamknij
              </SecondaryButton>
              <PrimaryButton onClick={() => void saveFitness()} disabled={isFitnessSaving} type="button">
                {isFitnessSaving ? 'ZAPISYWANIE…' : fitnessEditing ? 'ZAPISZ ZMIANY' : 'DODAJ TEST'}
              </PrimaryButton>
            </div>
          </div>
        </div>
      )}

      <PhotoCropperSheet
        req={photoCropReq}
        onClose={() => setPhotoCropReq(null)}
        onDone={() => setPhotoCropReq(null)}
      />
    </div>
  );
}

function PhotoCropperSheet(props: { req: PhotoCropRequest | null; onClose: () => void; onDone: () => void }) {
  const viewportPx = 260;
  const [zoom, setZoom] = React.useState(1.1);
  const [offsetX, setOffsetX] = React.useState(0);
  const [offsetY, setOffsetY] = React.useState(0);
  const [imgDim, setImgDim] = React.useState<{ w: number; h: number } | null>(null);
  const [errText, setErrText] = React.useState<string | null>(null);
  const [isSaving, setIsSaving] = React.useState(false);
  const dragRef = React.useRef<{ startX: number; startY: number; ox: number; oy: number } | null>(null);

  const objectUrl = React.useMemo(() => {
    if (!props.req) return null;
    return URL.createObjectURL(props.req.file);
  }, [props.req]);

  React.useEffect(() => {
    if (!objectUrl) return;
    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [objectUrl]);

  React.useEffect(() => {
    if (!objectUrl) return;
    let alive = true;
    const img = new Image();
    img.src = objectUrl;
    const done = () => {
      if (!alive) return;
      const w = img.naturalWidth || img.width;
      const h = img.naturalHeight || img.height;
      if (!w || !h) {
        setErrText('Nie udało się odczytać zdjęcia.');
        setImgDim(null);
      } else {
        setImgDim({ w, h });
        setErrText(null);
      }
    };
    img.onload = done;
    img.onerror = () => {
      if (!alive) return;
      setErrText('Nie udało się odczytać zdjęcia.');
      setImgDim(null);
    };
    return () => {
      alive = false;
    };
  }, [objectUrl]);

  React.useEffect(() => {
    setZoom(1.1);
    setOffsetX(0);
    setOffsetY(0);
    setErrText(null);
    setIsSaving(false);
  }, [props.req?.file]);

  const clampOffsets = React.useCallback(
    (next: { ox: number; oy: number; z: number }) => {
      if (!imgDim) return { ox: next.ox, oy: next.oy };
      const baseScale = Math.max(viewportPx / imgDim.w, viewportPx / imgDim.h);
      const w0 = imgDim.w * baseScale;
      const h0 = imgDim.h * baseScale;
      const w = w0 * next.z;
      const h = h0 * next.z;
      const maxX = Math.max(0, (w - viewportPx) / 2);
      const maxY = Math.max(0, (h - viewportPx) / 2);
      const ox = Math.max(-maxX, Math.min(maxX, next.ox));
      const oy = Math.max(-maxY, Math.min(maxY, next.oy));
      return { ox, oy };
    },
    [imgDim, viewportPx]
  );

  React.useEffect(() => {
    const c = clampOffsets({ ox: offsetX, oy: offsetY, z: zoom });
    if (c.ox !== offsetX) setOffsetX(c.ox);
    if (c.oy !== offsetY) setOffsetY(c.oy);
  }, [clampOffsets, offsetX, offsetY, zoom]);

  if (!props.req || !objectUrl) return null;

  const baseScale = imgDim ? Math.max(viewportPx / imgDim.w, viewportPx / imgDim.h) : 1;
  const baseW = imgDim ? imgDim.w * baseScale : viewportPx;
  const baseH = imgDim ? imgDim.h * baseScale : viewportPx;

  return (
    <div
      className="SheetOverlay"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (isSaving) return;
        if (e.target === e.currentTarget) props.onClose();
      }}
    >
      <div className="Sheet Sheet--photoCrop">
        <div className="SheetHeaderRow SheetHeaderRow--sticky">
          <div className="SheetTitle">{props.req.title}</div>
          <button className="SheetCloseButton" type="button" onClick={props.onClose} aria-label="Zamknij" disabled={isSaving}>
            ×
          </button>
        </div>

        {errText && <div className="ErrorBanner">{errText}</div>}

        <div
          className="PhotoCropViewport"
          style={{ width: viewportPx, height: viewportPx }}
          onPointerDown={(e) => {
            if (isSaving) return;
            (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
            dragRef.current = { startX: e.clientX, startY: e.clientY, ox: offsetX, oy: offsetY };
          }}
          onPointerMove={(e) => {
            if (!dragRef.current) return;
            const dx = e.clientX - dragRef.current.startX;
            const dy = e.clientY - dragRef.current.startY;
            const c = clampOffsets({ ox: dragRef.current.ox + dx, oy: dragRef.current.oy + dy, z: zoom });
            setOffsetX(c.ox);
            setOffsetY(c.oy);
          }}
          onPointerUp={() => {
            dragRef.current = null;
          }}
          onPointerCancel={() => {
            dragRef.current = null;
          }}
        >
          <div className="PhotoCropGrid" aria-hidden="true" />
          <img
            className="PhotoCropImage"
            src={objectUrl}
            alt=""
            style={{
              width: baseW,
              height: baseH,
              transform: `translate(-50%, -50%) translate(${offsetX}px, ${offsetY}px) scale(${zoom})`,
            }}
            draggable={false}
          />
        </div>

        <div className="PhotoCropControls">
          <div className="Muted">Powiększenie</div>
          <input
            className="RangeInput"
            type="range"
            min={1}
            max={3}
            step={0.01}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            disabled={isSaving || !imgDim}
            aria-label="Powiększenie"
          />
          <div className="PhotoCropActions">
            <SecondaryButton type="button" onClick={props.onClose} disabled={isSaving}>
              Anuluj
            </SecondaryButton>
            <PrimaryButton
              type="button"
              disabled={isSaving || !imgDim}
              onClick={async () => {
                if (!imgDim) return;
                setIsSaving(true);
                setErrText(null);
                try {
                  const cropped = await cropAndCompressProfilePhoto({
                    file: props.req!.file,
                    crop: { offsetX, offsetY, zoom, viewportPx },
                    targetMaxBytes: 600_000,
                  });
                  await props.req!.onConfirm(cropped);
                  props.onDone();
                } catch (e) {
                  setErrText('Nie udało się zapisać zdjęcia.');
                } finally {
                  setIsSaving(false);
                }
              }}
            >
              Zapisz zdjęcie
            </PrimaryButton>
          </div>
        </div>
      </div>
    </div>
  );
}

function renderChatBody(body: string): React.ReactNode {
  if (!body) return null;
  const re = /(\B@[\w\-.@ąćęłńóśźżĄĆĘŁŃÓŚŹŻ ]{1,40})/g;
  const parts = body.split(re);
  return parts.map((part, idx) => {
    if (part && part.length > 1 && part.charAt(0) === '@') {
      return (
        <span key={idx} className="ChatBubbleMention">
          {part}
        </span>
      );
    }
    return <React.Fragment key={idx}>{part}</React.Fragment>;
  });
}

function CzatPage(props: { me: Me; token: string; selectedChild: ChildItem | null }) {
  const [groups, setGroups] = React.useState<GroupItem[]>([]);
  const [selectedGroupId, setSelectedGroupId] = React.useState<string>('');
  const [messages, setMessages] = React.useState<LoungeMessageItem[]>([]);
  const [composer, setComposer] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);
  const [errorText, setErrorText] = React.useState<string | null>(null);
  const [autorefreshEnabled, setAutorefreshEnabled] = React.useState<boolean>(true);
  const intervalTimerRef = React.useRef<number | null>(null);
  const visibilityRef = React.useRef<boolean>(typeof document !== 'undefined' ? document.visibilityState === 'visible' : true);

  const groupLabel = React.useMemo(() => {
    if (props.me.role === 'PARENT') {
      const g = props.selectedChild?.group_name ?? '';
      const y = props.selectedChild?.group_year ?? null;
      const base = g ? g : 'GRUPA';
      return y ? `${base} (${y})` : base;
    }
    const g = groups.find((x) => x.id === selectedGroupId) ?? null;
    if (!g) return 'GRUPA';
    return g.year ? `${g.name} (${g.year})` : g.name;
  }, [groups, props.me.role, props.selectedChild?.group_name, props.selectedChild?.group_year, selectedGroupId]);

  React.useEffect(() => {
    if (props.me.status !== 'ACTIVE') return;
    if (props.me.role === 'COACH') {
      apiMyGroups(props.token)
        .then((list) => {
          setGroups(list);
          if (list.length > 0 && !selectedGroupId) setSelectedGroupId(list[0].id);
        })
        .catch(() => setGroups([]));
    }
    if (props.me.role === 'ADMIN' || props.me.role === 'COORDINATOR') {
      apiAdminListGroups(props.token)
        .then((list) => {
          setGroups(list);
          if (list.length > 0 && !selectedGroupId) setSelectedGroupId(list[0].id);
        })
        .catch(() => setGroups([]));
    }
  }, [props.me.role, props.me.status, props.token, selectedGroupId]);

  const load = React.useCallback(async () => {
    setIsLoading(true);
    setErrorText(null);
    try {
      if (props.me.role === 'PARENT') {
        const childId = props.selectedChild?.id ?? null;
        const groupId = props.selectedChild?.group_id ?? null;
        if (!childId || !groupId) {
          setMessages([]);
          return;
        }
        const list = await apiLoungeListMessages(props.token, { childId });
        setMessages(list);
        return;
      }

      const gid = selectedGroupId || null;
      if (!gid) {
        setMessages([]);
        return;
      }
      const list = await apiLoungeListMessages(props.token, { groupId: gid });
      setMessages(list);
    } catch (e) {
      setErrorText(adminActionError('Nie udało się pobrać czatu.', e));
    } finally {
      setIsLoading(false);
    }
  }, [props.me.role, props.selectedChild?.group_id, props.selectedChild?.id, props.token, selectedGroupId]);

  React.useEffect(() => {
    load();
  }, [load]);

  React.useEffect(() => {
    if (!autorefreshEnabled) {
      if (intervalTimerRef.current !== null) {
        window.clearInterval(intervalTimerRef.current);
        intervalTimerRef.current = null;
      }
      return;
    }
    const tick = () => {
      if (!visibilityRef.current) return;
      load();
    };
    intervalTimerRef.current = window.setInterval(tick, 15000);
    const onVis = () => {
      const nowVis = document.visibilityState === 'visible';
      const wasVis = visibilityRef.current;
      visibilityRef.current = nowVis;
      if (nowVis && !wasVis) {
        load();
      }
    };
    if (typeof document !== 'undefined') {
      visibilityRef.current = document.visibilityState === 'visible';
      document.addEventListener('visibilitychange', onVis);
    }
    return () => {
      if (intervalTimerRef.current !== null) {
        window.clearInterval(intervalTimerRef.current);
        intervalTimerRef.current = null;
      }
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVis);
      }
    };
  }, [autorefreshEnabled, load]);

  const send = React.useCallback(async () => {
    const text = composer.trim();
    if (!text) return;
    setComposer('');
    setErrorText(null);
    try {
      if (props.me.role === 'PARENT') {
        const childId = props.selectedChild?.id ?? null;
        const gid = props.selectedChild?.group_id ?? null;
        if (!childId || !gid) return;
        const msg = await apiLoungeSendMessage(props.token, { group_id: gid, body: text }, childId);
        setMessages((prev) => [...prev, msg]);
        window.setTimeout(() => load(), 300);
        return;
      }
      const gid = selectedGroupId || '';
      if (!gid) return;
      const msg = await apiLoungeSendMessage(props.token, { group_id: gid, body: text });
      setMessages((prev) => [...prev, msg]);
      window.setTimeout(() => load(), 300);
    } catch (e) {
      setErrorText(adminActionError('Nie udało się wysłać wiadomości.', e));
    }
  }, [composer, props.me.role, props.selectedChild?.group_id, props.selectedChild?.id, props.token, selectedGroupId, load]);

  const canWrite = props.me.status === 'ACTIVE' && (props.me.role !== 'PARENT' || !!props.selectedChild?.group_id);

  return (
    <div className="Page PageHasActionBar">
      <div className="PageHeroTitle">CZAT</div>
      {errorText && <div className="ErrorBanner">{errorText}</div>}

      {(props.me.role === 'COACH' || props.me.role === 'ADMIN' || props.me.role === 'COORDINATOR') && (
        <Card title="Grupa">
          {groups.length === 0 ? (
            <div className="Muted">Brak grup.</div>
          ) : (
            <select className="TextInput" value={selectedGroupId} onChange={(e) => setSelectedGroupId(e.target.value)} disabled={isLoading}>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                  {g.year ? ` (${g.year})` : ''}
                </option>
              ))}
            </select>
          )}
        </Card>
      )}

      {props.me.role === 'PARENT' && (!props.selectedChild || !props.selectedChild.group_id) && (
        <Card title="Brak grupy">
          <div className="Muted">Wybrane dziecko nie ma przypisanej grupy. Poproś Admina o przypisanie.</div>
        </Card>
      )}

      <Card title={groupLabel}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span
            className="Badge"
            style={{
              background: autorefreshEnabled ? 'var(--color-success-50, #e8f5e9)' : 'var(--color-neutral-50, #f5f5f5)',
              color: autorefreshEnabled ? 'var(--color-success-700, #2e7d32)' : 'var(--color-neutral-600, #757575)',
              border: `1px solid ${autorefreshEnabled ? 'var(--color-success-200, #a5d6a7)' : 'var(--color-neutral-200, #e0e0e0)'}`,
              padding: '4px 10px',
              borderRadius: 999,
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            {autorefreshEnabled ? '🔄 AUTO WŁ (15s)' : '⏸ AUTO WYŁ'}
          </span>
          <SecondaryButton onClick={load} disabled={isLoading} type="button">
            Odśwież
          </SecondaryButton>
          <SecondaryButton onClick={() => setAutorefreshEnabled((prev) => !prev)} type="button">
            {autorefreshEnabled ? 'WYŁĄCZ AUTO' : 'WŁĄCZ AUTO'}
          </SecondaryButton>
        </div>
      </Card>

      <div className="ChatMessages" role="log" aria-label="Czat ogólny">
        {isLoading && messages.length === 0 && <div className="Muted">Ładowanie…</div>}
        {!isLoading && messages.length === 0 && <div className="Muted">Brak wiadomości. Napisz pierwszą.</div>}
        {messages.map((m) => (
          <div key={m.id} className={['ChatBubble', m.is_me ? 'ChatBubble--me' : ''].filter(Boolean).join(' ')}>
            {!m.is_me && <div className="ChatBubbleMeta">{m.sender_email}</div>}
            <div className="ChatBubbleBody">{renderChatBody(m.body)}</div>
            <div className="ChatBubbleMeta">{formatTime(m.created_at)}</div>
          </div>
        ))}
      </div>

      <div className="ActionBar" aria-label="Nowa wiadomość">
        <div className="ActionBarInner">
          <div className="ChatComposerRow">
            <input
              className="TextInput"
              value={composer}
              onChange={(e) => setComposer(e.target.value)}
              placeholder="Napisz wiadomość…"
              disabled={isLoading || !canWrite}
            />
            <PrimaryButton onClick={send} disabled={isLoading || !canWrite || composer.trim() === ''} type="button">
              Wyślij
            </PrimaryButton>
          </div>
        </div>
      </div>
    </div>
  );
}

function WiadomosciPage(props: { me: Me; token: string; selectedChildId?: string | null; onRefreshNotifications?: () => void }) {
  const [q, setQ] = React.useState('');
  const [threads, setThreads] = React.useState<ChatThreadItem[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [errorText, setErrorText] = React.useState<string | null>(null);
  const [selectedThreadId, setSelectedThreadId] = React.useState<string | null>(null);
  const [messages, setMessages] = React.useState<ChatMessageItem[]>([]);
  const [composer, setComposer] = React.useState('');
  const [targetsOpen, setTargetsOpen] = React.useState(false);
  const [targets, setTargets] = React.useState<ChatTargetItem[]>([]);
  const [isTargetsLoading, setIsTargetsLoading] = React.useState(false);
  const [hashThreadId, setHashThreadId] = React.useState<string | null>(() => {
    const t = getHashParts().params.get('t');
    return t && t.trim() ? t : null;
  });

  const selectedThread = React.useMemo(
    () => (selectedThreadId ? threads.find((t) => t.id === selectedThreadId) ?? null : null),
    [selectedThreadId, threads]
  );

  const loadThreads = React.useCallback(async () => {
    setIsLoading(true);
    setErrorText(null);
    try {
      const list = await apiChatListThreads(props.token);
      setThreads(list);
    } catch (e) {
      setErrorText(adminActionError('Nie udało się pobrać wątków.', e));
    } finally {
      setIsLoading(false);
    }
  }, [props.token]);

  React.useEffect(() => {
    loadThreads();
  }, [loadThreads]);

  React.useEffect(() => {
    const onHashChange = () => {
      const t = getHashParts().params.get('t');
      setHashThreadId(t && t.trim() ? t : null);
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  React.useEffect(() => {
    if (!targetsOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (isLoading) return;
      e.preventDefault();
      setTargetsOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isLoading, targetsOpen]);

  const filtered = React.useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return threads;
    return threads.filter((t) => t.title.toLowerCase().includes(s) || (t.last_message_preview ?? '').toLowerCase().includes(s));
  }, [q, threads]);

  const openThread = React.useCallback(
    async (threadId: string, updateHash: boolean = true) => {
      if (updateHash) navigate('wiadomosci', { t: threadId });
      setSelectedThreadId(threadId);
      setIsLoading(true);
      setErrorText(null);
      try {
        const list = await apiChatListMessages(props.token, threadId);
        setMessages(list);
        await apiChatMarkRead(props.token, threadId);
        setThreads((prev) => prev.map((t) => (t.id === threadId ? { ...t, unread_count: 0 } : t)));
        props.onRefreshNotifications?.();
      } catch (e) {
        setErrorText(adminActionError('Nie udało się pobrać wiadomości.', e));
      } finally {
        setIsLoading(false);
      }
    },
    [props.onRefreshNotifications, props.token]
  );

  React.useEffect(() => {
    if (!hashThreadId) return;
    if (selectedThreadId === hashThreadId) return;
    if (isLoading && threads.length === 0) return;
    openThread(hashThreadId, false);
  }, [hashThreadId, isLoading, openThread, selectedThreadId, threads.length]);

  const loadTargets = React.useCallback(async () => {
    setIsTargetsLoading(true);
    setErrorText(null);
    try {
      const list = await apiChatTargets(props.token, props.selectedChildId ?? null);
      setTargets(list);
    } catch (e) {
      setErrorText(adminActionError('Nie udało się pobrać listy kontaktów.', e));
    } finally {
      setIsTargetsLoading(false);
    }
  }, [props.selectedChildId, props.token]);

  const startNewThread = React.useCallback(
    async (t: ChatTargetItem) => {
      const childId = t.child_id ?? props.selectedChildId ?? null;
      if (!childId) return;
      setTargetsOpen(false);
      setIsLoading(true);
      setErrorText(null);
      try {
        const th = await apiChatOpenThread(props.token, { recipient_user_id: t.recipient_user_id, child_id: childId });
        setThreads((prev) => {
          const rest = prev.filter((x) => x.id !== th.id);
          return [th, ...rest];
        });
        await openThread(th.id);
      } catch (e) {
        setErrorText(adminActionError('Nie udało się utworzyć/otworzyć wątku.', e));
      } finally {
        setIsLoading(false);
      }
    },
    [openThread, props.selectedChildId, props.token]
  );

  const send = React.useCallback(async () => {
    if (!selectedThreadId) return;
    const text = composer.trim();
    if (!text) return;
    setComposer('');
    try {
      const msg = await apiChatSendMessage(props.token, selectedThreadId, { body: text });
      setMessages((prev) => [...prev, msg]);
      setThreads((prev) =>
        prev.map((t) =>
          t.id === selectedThreadId
            ? { ...t, last_message_at: msg.created_at, last_message_preview: msg.body.slice(0, 80), unread_count: 0 }
            : t
        )
      );
    } catch (e) {
      setErrorText(adminActionError('Nie udało się wysłać wiadomości.', e));
    }
  }, [composer, props.token, selectedThreadId]);

  if (selectedThread) {
    return (
      <div className="Page PageHasActionBar">
        <UpcomingMatchCard token={props.token} childId={props.me.role === 'PARENT' ? props.selectedChildId ?? null : null} disabled={isLoading} sticky />
        <div className="ChatHeader">
          <SecondaryButton
            onClick={() => {
              setSelectedThreadId(null);
              setMessages([]);
              navigate('wiadomosci');
            }}
            type="button"
          >
            Wróć
          </SecondaryButton>
          <div className="ChatHeaderMain">
            <div className="ChatHeaderTitle">{selectedThread.title}</div>
            <div className="ChatHeaderSub">{selectedThread.child_label}</div>
          </div>
        </div>

        {errorText && <div className="ErrorBanner">{errorText}</div>}

        <div className="ChatMessages" role="log" aria-label="Wiadomości">
          {isLoading && messages.length === 0 && <div className="Muted">Ładowanie…</div>}
          {!isLoading && messages.length === 0 && <div className="Muted">Brak wiadomości. Napisz pierwszą.</div>}
          {messages.map((m) => (
            <div key={m.id} className={['ChatBubble', m.is_me ? 'ChatBubble--me' : ''].filter(Boolean).join(' ')}>
              <div className="ChatBubbleBody">{renderChatBody(m.body)}</div>
              <div className="ChatBubbleMeta">{formatTime(m.created_at)}</div>
            </div>
          ))}
        </div>

        <div className="ActionBar" aria-label="Nowa wiadomość">
          <div className="ActionBarInner">
            <div className="ChatComposerRow">
              <input
                className="TextInput"
                value={composer}
                onChange={(e) => setComposer(e.target.value)}
                placeholder="Napisz wiadomość…"
                disabled={isLoading}
              />
              <PrimaryButton onClick={send} disabled={isLoading || composer.trim() === ''} type="button">
                Wyślij
              </PrimaryButton>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="Page">
      <UpcomingMatchCard
        token={props.token}
        childId={props.me.role === 'PARENT' ? props.selectedChildId ?? null : null}
        disabled={isTargetsLoading || isLoading}
        sticky
      />
      <div className="PageHeroTitle">WIADOMOŚCI</div>

      {errorText && <div className="ErrorBanner">{errorText}</div>}

      <div className="SearchRow">
        <div className="SearchInputWrap">
          <SearchIcon className="SearchIcon" />
          <input
            className="SearchInput"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Szukaj konwersacji lub osób"
          />
        </div>
        <SecondaryButton
          onClick={() => {
            setTargetsOpen(true);
            loadTargets();
          }}
          disabled={isTargetsLoading || isLoading || (props.me.role === 'PARENT' && !props.selectedChildId)}
          type="button"
        >
          Nowa
        </SecondaryButton>
      </div>

      <div className="ThreadList">
        {isLoading && threads.length === 0 && <div className="Muted">Ładowanie…</div>}
        {!isLoading && filtered.length === 0 && <div className="Muted">Brak wątków.</div>}
        {filtered.map((t) => (
          <button
            key={t.id}
            className="ThreadCard ThreadCardButton"
            type="button"
            onClick={() => openThread(t.id, true)}
            disabled={isLoading}
          >
            <div className="Avatar Avatar--logo" aria-hidden="true">
              <img src={clubLogo} alt="" />
            </div>
            <div className="ThreadMain">
              <div className="ThreadTop">
                <div className="ThreadTitle">{t.title}</div>
                <div className="ThreadTime">{t.last_message_at ? formatTime(t.last_message_at) : ''}</div>
              </div>
              <div className="ThreadPreview">{t.last_message_preview ?? ''}</div>
              <div className="ThreadPreview ThreadPreview--sub">{t.child_label}</div>
            </div>
            {t.unread_count > 0 && <div className="ThreadUnread">{t.unread_count}</div>}
          </button>
        ))}
      </div>

      {targetsOpen && (
        <div
          className="SheetOverlay"
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => {
            if (isLoading) return;
            if (e.target === e.currentTarget) setTargetsOpen(false);
          }}
        >
          <div className="Sheet">
            <div className="SheetHeaderRow SheetHeaderRow--sticky">
              <div className="SheetTitle">NOWA ROZMOWA</div>
              <button className="SheetCloseButton" type="button" aria-label="Zamknij" onClick={() => setTargetsOpen(false)} disabled={isLoading}>
                ×
              </button>
            </div>
            {props.me.role === 'PARENT' && !props.selectedChildId && <div className="Muted">Najpierw wybierz dziecko w Profilu.</div>}
            {isTargetsLoading ? (
              <div className="Muted">Ładowanie…</div>
            ) : targets.length === 0 ? (
              <div className="Muted">Brak dostępnych kontaktów.</div>
            ) : (
              <div className="List">
                {targets.map((t) => (
                  <button
                    key={`${t.recipient_user_id}:${t.child_id ?? ''}`}
                    className="ListItem ListItemButton"
                    type="button"
                    onClick={() => startNewThread(t)}
                    disabled={isLoading || (props.me.role === 'PARENT' && !props.selectedChildId)}
                  >
                    <div className="ListItemTop">
                      <div className="ListItemTitle">{t.recipient_email}</div>
                      <div className="ListItemBadge">{t.child_label ?? ''}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ProfilPage(props: {
  me: Me;
  token: string;
  selectedChild: ChildItem | null;
  children: ChildItem[];
  selectedChildId: string | null;
  onSelectChildId: (childId: string) => void;
  workGroups: GroupItem[];
  activeGroupId: string;
  onChangeActiveGroupId: (groupId: string) => void;
  coachViewMode: CoachViewMode;
  onChangeCoachViewMode: (mode: CoachViewMode) => void;
  coachChildren: ChildItem[];
  onChangeChild: () => void;
  onLogout: () => void;
  onRefreshMe?: () => void;
  onSwitchRole?: (role: Role) => Promise<void> | void;
  isRoleSwitchLoading?: boolean;
}) {
  const pushSupport = React.useMemo(() => {
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
    const isIos = /iPad|iPhone|iPod/i.test(ua);
    const isAndroid = /Android/i.test(ua);
    const isWebView = /\bwv\b/i.test(ua) || /FBAN|FBAV|Instagram|Line|Twitter/i.test(ua);
    const isStandalone =
      (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
      (!!(navigator as unknown as { standalone?: boolean }).standalone);
    const hasNotification = typeof window !== 'undefined' && 'Notification' in window;
    const hasServiceWorker = typeof navigator !== 'undefined' && 'serviceWorker' in navigator;
    const hasPushManager = typeof window !== 'undefined' && 'PushManager' in window;
    const isSecure = typeof window !== 'undefined' ? window.isSecureContext : false;

    if (!hasNotification) return { supported: false, reason: 'Brak obsługi powiadomień w tej przeglądarce.' };
    if (isIos && !isStandalone)
      return {
        supported: false,
        reason: 'Na iOS Web Push działa dopiero po dodaniu aplikacji do ekranu głównego (PWA) i uruchomieniu z ikonki.',
      };
    if (!isSecure)
      return {
        supported: false,
        reason: 'Web Push wymaga HTTPS (wyjątek: localhost). Na telefonie otwórz aplikację przez HTTPS, nie po samym IP/HTTP.',
      };
    if (isWebView)
      return {
        supported: false,
        reason: 'Wygląda na przeglądarkę wbudowaną w aplikację. Otwórz link w Safari (iOS) lub Chrome (Android).',
      };
    if (!hasServiceWorker)
      return { supported: false, reason: 'Brak Service Worker w tej przeglądarce/trybie.' };
    if (!hasPushManager) {
      if (isIos)
        return {
          supported: false,
          reason:
            'Na iOS Web Push działa tylko w PWA (z ikonki) i wymaga zaufanego HTTPS. Na VPS użyj certyfikatu zaufanego (np. Let’s Encrypt) — pobieranie CA dotyczy tylko lokalnego mkcert.',
        };
      if (isAndroid) return { supported: false, reason: 'Na Androidzie Web Push działa w Chrome (nie w każdej przeglądarce).' };
      return { supported: false, reason: 'Brak Push API w tej przeglądarce/trybie.' };
    }
    return { supported: true, reason: '' };
  }, []);
  const isAndroidUa = React.useMemo(() => {
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
    return /Android/i.test(ua);
  }, []);
  const isIOS = React.useMemo(() => {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent || '';
    const isAppleMobile = /iPhone|iPad|iPod/i.test(ua);
    const isOldIe = typeof window !== 'undefined' && !!(window as unknown as { MSStream?: unknown }).MSStream;
    return isAppleMobile && !isOldIe;
  }, []);
  const isIOSStandalone = React.useMemo(() => {
    if (typeof window === 'undefined') return false;
    try {
      const navStandalone = !!(navigator as unknown as { standalone?: boolean }).standalone;
      const mediaStandalone = !!(window.matchMedia && window.matchMedia('(display-mode: standalone)').matches);
      return navStandalone || mediaStandalone;
    } catch {
      return false;
    }
  }, []);
  const iosVersion = React.useMemo(() => {
    if (typeof navigator === 'undefined') return 0;
    try {
      const m = /OS (\d+)_(\d+)/.exec(navigator.userAgent || '');
      if (!m) return 0;
      return Number(`${m[1]}.${m[2]}`);
    } catch {
      return 0;
    }
  }, []);
  const [, setIosPollTick] = React.useState<number>(0);
  React.useEffect(() => {
    if (!isIOS || iosVersion < 16.4) return;
    let rounds = 0;
    let cancelled = false;
    const id = window.setInterval(() => {
      if (cancelled) return;
      rounds += 1;
      if (rounds > 60) {
        window.clearInterval(id);
        return;
      }
      try {
        const navStandalone = !!(navigator as unknown as { standalone?: boolean }).standalone;
        const mediaStandalone = !!(window.matchMedia && window.matchMedia('(display-mode: standalone)').matches);
        if (navStandalone || mediaStandalone) {
          window.clearInterval(id);
          window.location.reload();
          return;
        }
      } catch {
        /* ignore */
      }
      setIosPollTick(rounds);
    }, 2000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [isIOS, iosVersion]);
  const isAdminLike = props.me.role === 'ADMIN' || props.me.role === 'COORDINATOR';
  const isFullAdmin = props.me.role === 'ADMIN';

  type AccessFlags = { can_parent: boolean; can_coach: boolean; can_admin: boolean; can_coordinator: boolean; can_player: boolean };
  const enabledRolesFromAccess = React.useCallback((a: AccessFlags): Role[] => {
    const out: Role[] = [];
    if (a.can_coordinator) out.push('COORDINATOR');
    if (a.can_player) out.push('PLAYER');
    if (a.can_parent) out.push('PARENT');
    if (a.can_coach) out.push('COACH');
    if (a.can_admin) out.push('ADMIN');
    return out;
  }, []);
  const pickDefaultRoleFromAccess = React.useCallback(
    (a: AccessFlags): Role => {
      const enabled = enabledRolesFromAccess(a);
      return enabled[0] ?? 'PARENT';
    },
    [enabledRolesFromAccess]
  );
  const normalizeRoleForAccess = React.useCallback(
    (r: Role, a: AccessFlags): Role => {
      const enabled = enabledRolesFromAccess(a);
      return enabled.includes(r) ? r : pickDefaultRoleFromAccess(a);
    },
    [enabledRolesFromAccess, pickDefaultRoleFromAccess]
  );
  const accessFromUser = React.useCallback((u: AdminUserItem): AccessFlags => {
    const a: AccessFlags = {
      can_parent: !!u.can_parent,
      can_coach: !!u.can_coach,
      can_admin: !!u.can_admin,
      can_coordinator: !!u.can_coordinator || u.role === 'COORDINATOR',
      can_player: !!u.can_player || u.role === 'PLAYER',
    };
    if (a.can_parent || a.can_coach || a.can_admin || a.can_coordinator || a.can_player) return a;
    const legacy: Role =
      u.role === 'ADMIN' || u.role === 'COORDINATOR' || u.role === 'COACH' || u.role === 'PARENT' || u.role === 'PLAYER'
        ? (u.role as Role)
        : 'PARENT';
    return {
      can_player: legacy === 'PLAYER',
      can_parent: legacy === 'PARENT',
      can_coach: legacy === 'COACH',
      can_admin: legacy === 'ADMIN',
      can_coordinator: legacy === 'COORDINATOR',
    };
  }, []);
  const [pending, setPending] = React.useState<AdminUserItem[]>([]);
  const [pendingRoleById, setPendingRoleById] = React.useState<Record<string, Role>>({});
  const [pendingAccessById, setPendingAccessById] = React.useState<Record<string, AccessFlags>>({});
  const [users, setUsers] = React.useState<AdminUserItem[]>([]);
  const [usersStatusFilter, setUsersStatusFilter] = React.useState<'ALL' | 'PENDING' | 'ACTIVE' | 'REJECTED'>('ALL');
  const [usersQuery, setUsersQuery] = React.useState('');
  const [usersOffset, setUsersOffset] = React.useState(0);
  const [usersHasMore, setUsersHasMore] = React.useState(false);
  const [usersTotal, setUsersTotal] = React.useState<number | null>(null);
  const [userRoleById, setUserRoleById] = React.useState<Record<string, Role>>({});
  const [userAccessById, setUserAccessById] = React.useState<Record<string, AccessFlags>>({});
  const [userStatusById, setUserStatusById] = React.useState<Record<string, 'ACTIVE' | 'PENDING' | 'REJECTED'>>({});
  const [isUsersLoading, setIsUsersLoading] = React.useState(false);
  const [deleteUser, setDeleteUser] = React.useState<{ id: string; email: string } | null>(null);
  const [editUserEmail, setEditUserEmail] = React.useState<{ id: string; label: string } | null>(null);
  const [editUserEmailValue, setEditUserEmailValue] = React.useState('');
  const [editUserEmailError, setEditUserEmailError] = React.useState<string | null>(null);
  const [resetUserPassword, setResetUserPassword] = React.useState<{ id: string; label: string } | null>(null);
  const [resetUserPasswordValue, setResetUserPasswordValue] = React.useState('');
  const [resetUserPasswordDone, setResetUserPasswordDone] = React.useState<{ password: string; label: string } | null>(null);
  const [createUserEmail, setCreateUserEmail] = React.useState('');
  const [createUserRole, setCreateUserRole] = React.useState<Role>('PARENT');
  const [createUserAccess, setCreateUserAccess] = React.useState<AccessFlags>({
    can_parent: true,
    can_coach: false,
    can_admin: false,
    can_coordinator: false,
    can_player: false,
  });
  const [createUserStatus, setCreateUserStatus] = React.useState<'ACTIVE' | 'PENDING'>('ACTIVE');
  const [createUserPassword, setCreateUserPassword] = React.useState('');
  const [createdUserPassword, setCreatedUserPassword] = React.useState<string | null>(null);
  const [createdUserEmail, setCreatedUserEmail] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [errorText, setErrorText] = React.useState<string | null>(null);
  const [isPushLoading, setIsPushLoading] = React.useState(false);
  const [pushError, setPushError] = React.useState<string | null>(null);
  const [pushPermission, setPushPermission] = React.useState<NotificationPermission>('default');
  const [pushEnabled, setPushEnabled] = React.useState(false);
  const [pushEndpoint, setPushEndpoint] = React.useState<string | null>(null);
  const [notificationPrefs, setNotificationPrefs] = React.useState<NotificationPrefs | null>(null);
  const [prefsLoading, setPrefsLoading] = React.useState(false);
  const [prefsError, setPrefsError] = React.useState<string | null>(null);
  const [savingPrefs, setSavingPrefs] = React.useState(false);
  const [dbConfirm, setDbConfirm] = React.useState('');
  const [dbWithBackup, setDbWithBackup] = React.useState(true);
  const [dbInfo, setDbInfo] = React.useState<string | null>(null);
  const [dbError, setDbError] = React.useState<string | null>(null);
  const [isDbLoading, setIsDbLoading] = React.useState(false);
  const [userReportsTab, setUserReportsTab] = React.useState<'panel' | 'raporty'>('panel');
  const [userReports, setUserReports] = React.useState<AttendanceReportListItem[]>([]);
  const [isUserReportsLoading, setIsUserReportsLoading] = React.useState(false);
  const [userReportsError, setUserReportsError] = React.useState<string | null>(null);
  const [downloadingReportId, setDownloadingReportId] = React.useState<string | null>(null);
  const [children, setChildren] = React.useState<ChildItem[]>([]);
  const [adminChildrenOpen, setAdminChildrenOpen] = React.useState(false);
  const [adminChildrenTab, setAdminChildrenTab] = React.useState<'list' | 'add' | 'assign'>('list');
  const [adminChildQuery, setAdminChildQuery] = React.useState('');
  const [adminChildGroupFilter, setAdminChildGroupFilter] = React.useState<string>('ALL');
  const [adminChildYearFilter, setAdminChildYearFilter] = React.useState<string>('ALL');
  const [adminChildBirthYearFilter, setAdminChildBirthYearFilter] = React.useState<string>('ALL');
  const [adminChildrenList, setAdminChildrenList] = React.useState<ChildItem[]>([]);
  const [adminChildrenListOffset, setAdminChildrenListOffset] = React.useState(0);
  const [adminChildrenListHasMore, setAdminChildrenListHasMore] = React.useState(false);
  const [adminChildrenListTotal, setAdminChildrenListTotal] = React.useState<number | null>(null);
  const [isAdminChildrenListLoading, setIsAdminChildrenListLoading] = React.useState(false);
  const [statsRangeDays, setStatsRangeDays] = React.useState<30 | 90 | 365>(30);
  const [statsEvents, setStatsEvents] = React.useState<EventListItem[]>([]);
  const [isStatsLoading, setIsStatsLoading] = React.useState(false);
  const [statsError, setStatsError] = React.useState<string | null>(null);
  const [editChild, setEditChild] = React.useState<{
    id: string;
    first_name: string;
    last_initial: string;
    birth_year: string;
    shirt_number: string;
  } | null>(null);
  const [editChildPhotoUrl, setEditChildPhotoUrl] = React.useState<string | null>(null);
  const [isEditChildPhotoUploading, setIsEditChildPhotoUploading] = React.useState(false);
  const [editChildPhotoError, setEditChildPhotoError] = React.useState<string | null>(null);
  const editChildPhotoInputRef = React.useRef<HTMLInputElement | null>(null);
  const [editChildPhotoCropReq, setEditChildPhotoCropReq] = React.useState<PhotoCropRequest | null>(null);
  const [deleteChild, setDeleteChild] = React.useState<{ id: string; label: string } | null>(null);
  const [newChildFirstName, setNewChildFirstName] = React.useState('');
  const [newChildLastInitial, setNewChildLastInitial] = React.useState('');
  const [newChildBirthYear, setNewChildBirthYear] = React.useState('');
  const [newChildShirtNumber, setNewChildShirtNumber] = React.useState('');
  const [assignParentEmail, setAssignParentEmail] = React.useState('');
  const [assignChildId, setAssignChildId] = React.useState<string>('');
  const [assignIndividualCoachEmail, setAssignIndividualCoachEmail] = React.useState('');
  const [individualCoaches, setIndividualCoaches] = React.useState<CoachItem[]>([]);
  const [assignPlayerEmail, setAssignPlayerEmail] = React.useState('');
  const [childPlayers, setChildPlayers] = React.useState<PlayerAccountItem[]>([]);
  const [groups, setGroups] = React.useState<GroupItem[]>([]);
  const [attendanceReportOpen, setAttendanceReportOpen] = React.useState(false);
  const [attendanceReportGroupId, setAttendanceReportGroupId] = React.useState('');
  const [attendanceReportRange, setAttendanceReportRange] = React.useState<AttendanceReportRange>('month');
  const [attendanceReportYear, setAttendanceReportYear] = React.useState(() => String(new Date().getFullYear()));
  const [attendanceReportMonth, setAttendanceReportMonth] = React.useState(() => String(new Date().getMonth() + 1));
  const [attendanceReports, setAttendanceReports] = React.useState<AttendanceReportListItem[]>([]);
  const [isAttendanceReportsLoading, setIsAttendanceReportsLoading] = React.useState(false);
  const [newGroupName, setNewGroupName] = React.useState('');
  const [newGroupYear, setNewGroupYear] = React.useState('');
  const [setGroupGroupId, setSetGroupGroupId] = React.useState<string>('');
  const [adminGroupConfigTab, setAdminGroupConfigTab] = React.useState<'groups' | 'assign-children' | 'assign-coaches'>('groups');
  const [groupAssignQuery, setGroupAssignQuery] = React.useState('');
  const [groupAssignGroupFilter, setGroupAssignGroupFilter] = React.useState<string>('ALL');
  const [groupAssignYearFilter, setGroupAssignYearFilter] = React.useState<string>('ALL');
  const [groupAssignBirthYearFilter, setGroupAssignBirthYearFilter] = React.useState<string>('ALL');
  const [groupAssignSort, setGroupAssignSort] = React.useState<'name-asc' | 'name-desc' | 'birth-asc' | 'birth-desc' | 'group-asc' | 'group-desc'>(
    'name-asc'
  );
  const [groupAssignList, setGroupAssignList] = React.useState<ChildItem[]>([]);
  const [groupAssignListOffset, setGroupAssignListOffset] = React.useState(0);
  const [groupAssignListHasMore, setGroupAssignListHasMore] = React.useState(false);
  const [groupAssignListTotal, setGroupAssignListTotal] = React.useState<number | null>(null);
  const [isGroupAssignListLoading, setIsGroupAssignListLoading] = React.useState(false);
  const [groupAssignTargetByChildId, setGroupAssignTargetByChildId] = React.useState<Record<string, string>>({});
  const [adminGroupsOpen, setAdminGroupsOpen] = React.useState(false);
  const [assignCoachGroupId, setAssignCoachGroupId] = React.useState<string>('');
  const [assignCoachEmail, setAssignCoachEmail] = React.useState<string>('');
  const [isOpponentTeamsOpen, setIsOpponentTeamsOpen] = React.useState(false);
  const [opponentTeams, setOpponentTeams] = React.useState<OpponentTeamItem[]>([]);
  const [opponentTeamsQuery, setOpponentTeamsQuery] = React.useState('');
  const [newOpponentTeamName, setNewOpponentTeamName] = React.useState('');
  const [opponentTeamsError, setOpponentTeamsError] = React.useState<string | null>(null);
  const [isOpponentTeamsLoading, setIsOpponentTeamsLoading] = React.useState(false);
  const [editOpponentTeam, setEditOpponentTeam] = React.useState<OpponentTeamItem | null>(null);
  const [editOpponentName, setEditOpponentName] = React.useState('');
  const [editOpponentLogoUrl, setEditOpponentLogoUrl] = React.useState('');
  const [isEditOpponentLogoUploading, setIsEditOpponentLogoUploading] = React.useState(false);
  const editOpponentLogoInputRef = React.useRef<HTMLInputElement | null>(null);
  const [editOpponentLogoCropReq, setEditOpponentLogoCropReq] = React.useState<PhotoCropRequest | null>(null);
  const [deleteOpponentTeam, setDeleteOpponentTeam] = React.useState<OpponentTeamItem | null>(null);
  const [isUsageOpen, setIsUsageOpen] = React.useState(false);
  const [usageUsers, setUsageUsers] = React.useState<AdminUsageUserItem[]>([]);
  const [usageQuery, setUsageQuery] = React.useState('');
  const [isUsageLoading, setIsUsageLoading] = React.useState(false);
  const [usageError, setUsageError] = React.useState<string | null>(null);
  const [quickTtlDays, setQuickTtlDays] = React.useState<number>(7);
  const [quickTtlInput, setQuickTtlInput] = React.useState('7');
  const [isQuickTtlLoading, setIsQuickTtlLoading] = React.useState(false);
  const [quickTtlError, setQuickTtlError] = React.useState<string | null>(null);
  const [quickTtlInfo, setQuickTtlInfo] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!editChild) {
      setEditChildPhotoUrl(null);
      setEditChildPhotoError(null);
      setIsEditChildPhotoUploading(false);
      return;
    }
    if (!isAdminLike || props.me.status !== 'ACTIVE') return;
    let cancelled = false;
    (async () => {
      try {
        const r = await apiGetChildPhoto(props.token, editChild.id);
        if (cancelled) return;
        setEditChildPhotoUrl(r.data_url ?? null);
        setEditChildPhotoError(null);
      } catch (e) {
        if (cancelled) return;
        setEditChildPhotoUrl(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [editChild, isAdminLike, props.me.role, props.me.status, props.token]);

  React.useEffect(() => {
    if (props.me.role !== 'ADMIN' || props.me.status !== 'ACTIVE') return;
    let cancelled = false;
    (async () => {
      setIsQuickTtlLoading(true);
      setQuickTtlError(null);
      setQuickTtlInfo(null);
      try {
        const r = await apiAdminGetQuickReactionsConfig(props.token);
        if (cancelled) return;
        setQuickTtlDays(r.ttl_days);
        setQuickTtlInput(String(r.ttl_days));
      } catch (e) {
        if (!cancelled) setQuickTtlError(adminActionError('Nie udało się pobrać ustawień szybkich reakcji.', e));
      } finally {
        if (!cancelled) setIsQuickTtlLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [props.me.role, props.me.status, props.token]);

  const saveQuickTtl = React.useCallback(async () => {
    if (props.me.role !== 'ADMIN' || props.me.status !== 'ACTIVE') return;
    const n = Number(quickTtlInput.trim());
    if (!Number.isFinite(n)) return;
    if (n < 1 || n > 30) return;
    setIsQuickTtlLoading(true);
    setQuickTtlError(null);
    setQuickTtlInfo(null);
    try {
      const r = await apiAdminSetQuickReactionsConfig(props.token, n);
      setQuickTtlDays(r.ttl_days);
      setQuickTtlInput(String(r.ttl_days));
      setQuickTtlInfo('Zapisano ustawienie.');
    } catch (e) {
      setQuickTtlError(adminActionError('Nie udało się zapisać ustawień.', e));
    } finally {
      setIsQuickTtlLoading(false);
    }
  }, [props.me.role, props.me.status, props.token, quickTtlInput]);

  const loadStatsEvents = React.useCallback(async () => {
    if (props.me.role !== 'PARENT') return;
    if (props.me.status !== 'ACTIVE') return;
    const cid = props.selectedChild?.id ?? null;
    if (!cid) {
      setStatsEvents([]);
      return;
    }
    setIsStatsLoading(true);
    setStatsError(null);
    try {
      const list = await apiListEvents(props.token, cid);
      setStatsEvents(list);
    } catch (e) {
      setStatsError('Nie udało się pobrać wydarzeń do statystyk.');
    } finally {
      setIsStatsLoading(false);
    }
  }, [props.me.role, props.me.status, props.selectedChild?.id, props.token]);

  React.useEffect(() => {
    loadStatsEvents();
  }, [loadStatsEvents]);

  const attendanceStats = React.useMemo(() => {
    const nowTs = Date.now();
    const fromTs = nowTs - statsRangeDays * 24 * 60 * 60 * 1000;

    const relevant = statsEvents.filter((e) => {
      if (e.status === 'CANCELLED') return false;
      const ts = new Date(e.start_at).getTime();
      if (Number.isNaN(ts)) return false;
      return ts >= fromTs && ts <= nowTs;
    });

    const trainings = relevant.filter((e) => e.type === 'TRAINING');
    const matches = relevant.filter((e) => e.type === 'MATCH');

    const trainingsOut = trainings.filter((e) => e.attendance_status === 'OUT').length;
    const trainingsLate = trainings.filter((e) => e.attendance_status === 'LATE').length;
    const trainingsIn = trainings.length - trainingsOut - trainingsLate;

    const matchesAccepted = matches.filter((e) => e.match_rsvp === 'ACCEPTED').length;
    const matchesDeclined = matches.filter((e) => e.match_rsvp === 'DECLINED').length;
    const matchesPending = matches.length - matchesAccepted - matchesDeclined;
    const matchesRespondedLate = matches.filter((e) => !!e.match_responded_late).length;

    return {
      trainingsTotal: trainings.length,
      trainingsIn,
      trainingsOut,
      trainingsLate,
      matchesTotal: matches.length,
      matchesAccepted,
      matchesDeclined,
      matchesPending,
      matchesRespondedLate,
    };
  }, [statsEvents, statsRangeDays]);

  const loadOpponentTeams = React.useCallback(async () => {
    if (props.me.role !== 'COACH' && props.me.role !== 'ADMIN') return;
    setIsOpponentTeamsLoading(true);
    setOpponentTeamsError(null);
    try {
      const list = await apiListOpponentTeams(props.token);
      setOpponentTeams(list);
    } catch (e) {
      setOpponentTeamsError('Nie udało się pobrać listy drużyn.');
    } finally {
      setIsOpponentTeamsLoading(false);
    }
  }, [props.me.role, props.token]);

  const userReportAllowedGroup = React.useMemo<string | null>(() => {
    const role = props.me.role;
    if (role === 'ADMIN' || role === 'COORDINATOR') return null;
    if (role === 'PARENT') return props.selectedChild?.group_id ?? null;
    if (role === 'PLAYER') return props.selectedChild?.group_id ?? null;
    if (role === 'COACH') return props.activeGroupId ?? null;
    return null;
  }, [props.me.role, props.selectedChild?.group_id, props.activeGroupId]);

  const loadUserReports = React.useCallback(async () => {
    if (props.me.status !== 'ACTIVE') return;
    setIsUserReportsLoading(true);
    setUserReportsError(null);
    try {
      const list = await apiUserListAttendanceReports(props.token, userReportAllowedGroup ?? undefined);
      setUserReports(list);
    } catch (e) {
      setUserReportsError('Nie udało się załadować listy raportów.');
      setUserReports([]);
    } finally {
      setIsUserReportsLoading(false);
    }
  }, [props.me.status, props.token, userReportAllowedGroup]);

  const downloadUserReport = React.useCallback(async (report: AttendanceReportListItem) => {
    if (downloadingReportId) return;
    setDownloadingReportId(report.id);
    try {
      const r = await apiUserDownloadAttendanceReport(props.token, report.id);
      const url = URL.createObjectURL(r.blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = r.filename || report.filename;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        URL.revokeObjectURL(url);
        a.remove();
      }, 500);
    } catch (e) {
      window.alert('Nie udało się pobrać raportu.');
    } finally {
      setDownloadingReportId(null);
    }
  }, [downloadingReportId, props.token]);

  React.useEffect(() => {
    if (userReportsTab !== 'raporty') return;
    void loadUserReports();
  }, [userReportsTab, loadUserReports]);

  React.useEffect(() => {
    const syncTabFromHash = () => {
      try {
        const { params } = getHashParts();
        const t = params.get('tab');
        if (t === 'raporty') setUserReportsTab('raporty');
      } catch {}
    };
    syncTabFromHash();
    window.addEventListener('hashchange', syncTabFromHash);
    return () => window.removeEventListener('hashchange', syncTabFromHash);
  }, []);

  React.useEffect(() => {
    if (!isOpponentTeamsOpen) return;
    loadOpponentTeams();
  }, [isOpponentTeamsOpen, loadOpponentTeams]);

  const filteredOpponentTeams = React.useMemo(() => {
    const q = opponentTeamsQuery.trim().toLowerCase();
    if (!q) return opponentTeams;
    return opponentTeams.filter((t) => (t.name || '').toLowerCase().includes(q));
  }, [opponentTeams, opponentTeamsQuery]);

  const loadUsageUsers = React.useCallback(async () => {
    if (props.me.role !== 'ADMIN' || props.me.status !== 'ACTIVE') return;
    setIsUsageLoading(true);
    setUsageError(null);
    try {
      const list = await apiAdminUsageUsers(props.token);
      setUsageUsers(list);
    } catch {
      setUsageError('Nie udało się pobrać statystyk korzystania.');
    } finally {
      setIsUsageLoading(false);
    }
  }, [props.me.role, props.me.status, props.token]);

  React.useEffect(() => {
    if (!isUsageOpen) return;
    loadUsageUsers();
  }, [isUsageOpen, loadUsageUsers]);

  const filteredUsageUsers = React.useMemo(() => {
    const q = usageQuery.trim().toLowerCase();
    if (!q) return usageUsers;
    return usageUsers.filter((u) => (u.email || '').toLowerCase().includes(q) || (u.role || '').toLowerCase().includes(q));
  }, [usageQuery, usageUsers]);

  const onAddOpponentTeam = async () => {
    if (props.me.role !== 'COACH' && props.me.role !== 'ADMIN') return;
    const name = newOpponentTeamName.trim().replace(/\s+/g, ' ');
    if (!name) return;
    setIsOpponentTeamsLoading(true);
    setOpponentTeamsError(null);
    try {
      await apiCoachCreateOpponentTeam(props.token, { name });
      setNewOpponentTeamName('');
      await loadOpponentTeams();
    } catch (e) {
      setOpponentTeamsError('Nie udało się dodać drużyny.');
    } finally {
      setIsOpponentTeamsLoading(false);
    }
  };

  const onSaveOpponentTeam = async () => {
    if (props.me.role !== 'COACH' && props.me.role !== 'ADMIN') return;
    if (!editOpponentTeam) return;
    const name = editOpponentName.trim().replace(/\s+/g, ' ');
    const logo = editOpponentLogoUrl.trim();
    if (!name) return;
    setIsOpponentTeamsLoading(true);
    setOpponentTeamsError(null);
    try {
      await apiCoachUpdateOpponentTeam(props.token, editOpponentTeam.id, { name, logo_url: logo ? logo : null });
      setEditOpponentTeam(null);
      await loadOpponentTeams();
    } catch (e) {
      const st = getHttpStatusFromError(e);
      if (st === 422) setOpponentTeamsError('Taka drużyna już istnieje lub dane są nieprawidłowe.');
      else setOpponentTeamsError('Nie udało się zapisać zmian.');
    } finally {
      setIsOpponentTeamsLoading(false);
    }
  };

  const openEditOpponentLogoCrop = (file: File) => {
    if (!editOpponentTeam) return;
    setEditOpponentLogoCropReq({
      title: 'Kadruj logo',
      file,
      onConfirm: async (cropped) => {
        setIsEditOpponentLogoUploading(true);
        setOpponentTeamsError(null);
        try {
          const dataUrl = await fileToDataUrl(cropped);
          setEditOpponentLogoUrl(dataUrl);
        } catch (e) {
          setOpponentTeamsError('Nie udało się przetworzyć zdjęcia.');
        } finally {
          setIsEditOpponentLogoUploading(false);
        }
      },
    });
  };

  const clearEditOpponentLogo = () => {
    setEditOpponentLogoUrl('');
  };

  const onDeleteOpponentTeamConfirm = async () => {
    if (props.me.role !== 'COACH' && props.me.role !== 'ADMIN') return;
    if (!deleteOpponentTeam) return;
    setIsOpponentTeamsLoading(true);
    setOpponentTeamsError(null);
    try {
      await apiCoachDeleteOpponentTeam(props.token, deleteOpponentTeam.id);
      setDeleteOpponentTeam(null);
      await loadOpponentTeams();
    } catch (e) {
      const st = getHttpStatusFromError(e);
      if (st === 422) setOpponentTeamsError('Nie można usunąć drużyny, która jest już użyta w meczach.');
      else setOpponentTeamsError('Nie udało się usunąć drużyny.');
    } finally {
      setIsOpponentTeamsLoading(false);
    }
  };

  const loadPending = React.useCallback(async () => {
    setIsLoading(true);
    setErrorText(null);
    try {
      const r = await apiAdminListUsers(props.token, { status_filter: 'PENDING', limit: 200, offset: 0 });
      setPending(r.items);
      setPendingAccessById((prev) => {
        const next: Record<string, AccessFlags> = { ...prev };
        r.items.forEach((u) => {
          next[u.id] = prev[u.id] ?? accessFromUser(u);
        });
        return next;
      });
      setPendingRoleById((prev) => {
        const next: Record<string, Role> = {};
        r.items.forEach((u) => {
          const role: Role =
            u.role === 'ADMIN' || u.role === 'COORDINATOR' || u.role === 'COACH' || u.role === 'PARENT' ? (u.role as Role) : 'PARENT';
          const access = accessFromUser(u);
          next[u.id] = normalizeRoleForAccess((prev[u.id] ?? role) as Role, access);
        });
        return next;
      });
    } catch (e) {
      setErrorText('Nie udało się pobrać listy kont do akceptacji.');
    } finally {
      setIsLoading(false);
    }
  }, [accessFromUser, normalizeRoleForAccess, props.token]);

  React.useEffect(() => {
    if (!isAdminLike) return;
    loadPending();
  }, [isAdminLike, loadPending, props.me.role]);

  React.useEffect(() => {
    if (!deleteUser && !editUserEmail && !editChild && !deleteChild && !resetUserPassword) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (isLoading) return;
      e.preventDefault();
      if (resetUserPassword) {
        setResetUserPassword(null);
        setResetUserPasswordValue('');
        setResetUserPasswordDone(null);
        return;
      }
      if (deleteChild) {
        setDeleteChild(null);
        return;
      }
      if (editUserEmail) {
        setEditUserEmail(null);
        setEditUserEmailValue('');
        setEditUserEmailError(null);
        return;
      }
      if (editChild) {
        setEditChild(null);
        return;
      }
      if (deleteUser) setDeleteUser(null);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [deleteChild, deleteUser, editChild, editUserEmail, resetUserPassword, isLoading]);

  React.useEffect(() => {
    if (!isOpponentTeamsOpen && !editOpponentTeam && !deleteOpponentTeam) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (isOpponentTeamsLoading) return;
      e.preventDefault();
      if (deleteOpponentTeam) {
        setDeleteOpponentTeam(null);
        return;
      }
      if (editOpponentTeam) {
        setEditOpponentTeam(null);
        return;
      }
      if (isOpponentTeamsOpen) setIsOpponentTeamsOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [deleteOpponentTeam, editOpponentTeam, isOpponentTeamsLoading, isOpponentTeamsOpen]);

  const loadUsers = React.useCallback(async () => {
    if (!isAdminLike) return;
    const limit = 50;
    setIsUsersLoading(true);
    setErrorText(null);
    try {
      const status = usersStatusFilter === 'ALL' ? undefined : usersStatusFilter;
      const q = usersQuery.trim();
      const r = await apiAdminListUsers(props.token, {
        status_filter: status,
        q: q ? q : undefined,
        limit,
        offset: 0,
      });
      setUsers(r.items);
      setUsersTotal(r.total);
      setUsersOffset(r.items.length);
      setUsersHasMore(r.total !== null ? r.items.length < r.total : r.items.length === limit);
      setUserAccessById((prev) => {
        const next: Record<string, AccessFlags> = { ...prev };
        r.items.forEach((u) => {
          next[u.id] = prev[u.id] ?? accessFromUser(u);
        });
        return next;
      });
      setUserRoleById((prev) => {
        const next: Record<string, Role> = {};
        r.items.forEach((u) => {
          const role: Role =
            u.role === 'ADMIN' || u.role === 'COORDINATOR' || u.role === 'COACH' || u.role === 'PARENT' ? (u.role as Role) : 'PARENT';
          const access = accessFromUser(u);
          next[u.id] = normalizeRoleForAccess((prev[u.id] ?? role) as Role, access);
        });
        return next;
      });
      setUserStatusById((prev) => {
        const next: Record<string, 'ACTIVE' | 'PENDING' | 'REJECTED'> = {};
        r.items.forEach((u) => {
          const st: 'ACTIVE' | 'PENDING' | 'REJECTED' =
            u.status === 'ACTIVE' || u.status === 'PENDING' || u.status === 'REJECTED' ? u.status : 'PENDING';
          next[u.id] = prev[u.id] ?? st;
        });
        return next;
      });
    } catch (e) {
      setErrorText('Nie udało się pobrać listy kont.');
    } finally {
      setIsUsersLoading(false);
    }
  }, [accessFromUser, isAdminLike, normalizeRoleForAccess, props.me.role, props.token, usersQuery, usersStatusFilter]);

  const loadMoreUsers = React.useCallback(async () => {
    if (!isAdminLike) return;
    const limit = 50;
    setIsUsersLoading(true);
    setErrorText(null);
    try {
      const status = usersStatusFilter === 'ALL' ? undefined : usersStatusFilter;
      const q = usersQuery.trim();
      const r = await apiAdminListUsers(props.token, {
        status_filter: status,
        q: q ? q : undefined,
        limit,
        offset: usersOffset,
      });
      setUsers((prev) => prev.concat(r.items));
      setUsersTotal(r.total);
      setUsersOffset((prev) => prev + r.items.length);
      setUsersHasMore(r.total !== null ? usersOffset + r.items.length < r.total : r.items.length === limit);
      setUserAccessById((prev) => {
        const next: Record<string, AccessFlags> = { ...prev };
        r.items.forEach((u) => {
          next[u.id] = prev[u.id] ?? accessFromUser(u);
        });
        return next;
      });
      setUserRoleById((prev) => {
        const next: Record<string, Role> = { ...prev };
        r.items.forEach((u) => {
          const role: Role =
            u.role === 'ADMIN' || u.role === 'COORDINATOR' || u.role === 'COACH' || u.role === 'PARENT' ? (u.role as Role) : 'PARENT';
          const access = accessFromUser(u);
          next[u.id] = normalizeRoleForAccess((prev[u.id] ?? role) as Role, access);
        });
        return next;
      });
      setUserStatusById((prev) => {
        const next: Record<string, 'ACTIVE' | 'PENDING' | 'REJECTED'> = { ...prev };
        r.items.forEach((u) => {
          const st: 'ACTIVE' | 'PENDING' | 'REJECTED' =
            u.status === 'ACTIVE' || u.status === 'PENDING' || u.status === 'REJECTED' ? u.status : 'PENDING';
          next[u.id] = prev[u.id] ?? st;
        });
        return next;
      });
    } catch (e) {
      setErrorText('Nie udało się pobrać kolejnych kont.');
    } finally {
      setIsUsersLoading(false);
    }
  }, [accessFromUser, isAdminLike, normalizeRoleForAccess, props.token, usersOffset, usersQuery, usersStatusFilter]);

  React.useEffect(() => {
    if (!isAdminLike) return;
    loadUsers();
  }, [isAdminLike, props.me.role]);

  const loadChildren = React.useCallback(async () => {
    if (!isAdminLike) return;
    setIsLoading(true);
    setErrorText(null);
    try {
      const limit = 200;
      let offset = 0;
      let out: ChildItem[] = [];
      let total: number | null = null;
      while (true) {
        const r = await apiAdminListChildren(props.token, { limit, offset });
        total = r.total;
        out = out.concat(r.items);
        offset += r.items.length;
        if (r.items.length < limit) break;
        if (total !== null && offset >= total) break;
        if (offset > 5000) break;
      }
      setChildren(out);
      if (!assignChildId && out[0]) setAssignChildId(out[0].id);
    } catch (e) {
      setErrorText('Nie udało się pobrać listy dzieci.');
    } finally {
      setIsLoading(false);
    }
  }, [assignChildId, isAdminLike, props.me.role, props.token]);

  const loadAdminChildrenList = React.useCallback(
    async (mode: 'reset' | 'more') => {
      if (!isAdminLike) return;
      const limit = 50;
      setIsAdminChildrenListLoading(true);
      setErrorText(null);
      try {
        const q = adminChildQuery.trim();
        const baseOffset = mode === 'more' ? adminChildrenListOffset : 0;
        const r = await apiAdminListChildren(props.token, {
          group_id: adminChildGroupFilter === 'ALL' ? undefined : adminChildGroupFilter,
          group_year: adminChildYearFilter === 'ALL' ? undefined : Number(adminChildYearFilter),
          birth_year: adminChildBirthYearFilter === 'ALL' ? undefined : Number(adminChildBirthYearFilter),
          q: q ? q : undefined,
          limit,
          offset: baseOffset,
        });
        setAdminChildrenListTotal(r.total);
        if (mode === 'more') {
          setAdminChildrenList((prev) => prev.concat(r.items));
          setAdminChildrenListOffset((prev) => prev + r.items.length);
          setAdminChildrenListHasMore(r.total !== null ? baseOffset + r.items.length < r.total : r.items.length === limit);
        } else {
          setAdminChildrenList(r.items);
          setAdminChildrenListOffset(r.items.length);
          setAdminChildrenListHasMore(r.total !== null ? r.items.length < r.total : r.items.length === limit);
        }
      } catch (e) {
        setErrorText('Nie udało się pobrać listy dzieci.');
      } finally {
        setIsAdminChildrenListLoading(false);
      }
    },
    [
      adminChildBirthYearFilter,
      adminChildGroupFilter,
      adminChildQuery,
      adminChildYearFilter,
      adminChildrenListOffset,
      isAdminLike,
      props.token,
    ]
  );

  React.useEffect(() => {
    if (!isAdminLike) return;
    loadChildren();
  }, [isAdminLike, loadChildren, props.me.role]);

  React.useEffect(() => {
    if (!isAdminLike) return;
    if (!adminChildrenOpen) return;
    if (adminChildrenTab !== 'list') return;
    loadAdminChildrenList('reset');
  }, [adminChildrenOpen, adminChildrenTab, isAdminLike, loadAdminChildrenList]);

  React.useEffect(() => {
    if (!isAdminLike) return;
    if (!adminChildrenOpen) return;
    if (adminChildrenTab !== 'list') return;
    const t = window.setTimeout(() => {
      loadAdminChildrenList('reset');
    }, 240);
    return () => window.clearTimeout(t);
  }, [
    adminChildBirthYearFilter,
    adminChildGroupFilter,
    adminChildQuery,
    adminChildYearFilter,
    adminChildrenOpen,
    adminChildrenTab,
    isAdminLike,
    loadAdminChildrenList,
  ]);

  React.useEffect(() => {
    if (!isAdminLike) return;
    if (!assignChildId) {
      setIndividualCoaches([]);
      setChildPlayers([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [coaches, players] = await Promise.all([
          apiAdminListIndividualCoaches(props.token, assignChildId),
          apiAdminListChildPlayers(props.token, assignChildId),
        ]);
        if (!cancelled) {
          setIndividualCoaches(coaches);
          setChildPlayers(players);
        }
      } catch {
        if (!cancelled) {
          setIndividualCoaches([]);
          setChildPlayers([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [assignChildId, isAdminLike, props.me.role, props.token]);

  const loadGroups = React.useCallback(async () => {
    if (!isAdminLike) return;
    setIsLoading(true);
    setErrorText(null);
    try {
      const list = await apiAdminListGroups(props.token);
      setGroups(list);
      if (!setGroupGroupId && list[0]) setSetGroupGroupId(list[0].id);
      if (!assignCoachGroupId && list[0]) setAssignCoachGroupId(list[0].id);
    } catch (e) {
      setErrorText('Nie udało się pobrać listy grup.');
    } finally {
      setIsLoading(false);
    }
  }, [assignCoachGroupId, isAdminLike, props.me.role, props.token, setGroupGroupId]);

  const loadGroupAssignList = React.useCallback(
    async (mode: 'reset' | 'more') => {
      if (!isAdminLike) return;
      const limit = 50;
      setIsGroupAssignListLoading(true);
      setErrorText(null);
      try {
        const q = groupAssignQuery.trim();
        const baseOffset = mode === 'more' ? groupAssignListOffset : 0;
        const r = await apiAdminListChildren(props.token, {
          group_id: groupAssignGroupFilter === 'ALL' ? undefined : groupAssignGroupFilter,
          group_year: groupAssignYearFilter === 'ALL' ? undefined : Number(groupAssignYearFilter),
          birth_year: groupAssignBirthYearFilter === 'ALL' ? undefined : Number(groupAssignBirthYearFilter),
          q: q ? q : undefined,
          limit,
          offset: baseOffset,
        });
        setGroupAssignListTotal(r.total);
        setGroupAssignTargetByChildId((prev) => {
          const next = mode === 'more' ? { ...prev } : {};
          for (const item of r.items) next[item.id] = item.group_id ?? '';
          return next;
        });
        if (mode === 'more') {
          setGroupAssignList((prev) => prev.concat(r.items));
          setGroupAssignListOffset((prev) => prev + r.items.length);
          setGroupAssignListHasMore(r.total !== null ? baseOffset + r.items.length < r.total : r.items.length === limit);
        } else {
          setGroupAssignList(r.items);
          setGroupAssignListOffset(r.items.length);
          setGroupAssignListHasMore(r.total !== null ? r.items.length < r.total : r.items.length === limit);
        }
      } catch (e) {
        setErrorText('Nie udało się pobrać listy dzieci do przypisania.');
      } finally {
        setIsGroupAssignListLoading(false);
      }
    },
    [
      groupAssignBirthYearFilter,
      groupAssignGroupFilter,
      groupAssignListOffset,
      groupAssignQuery,
      groupAssignYearFilter,
      isAdminLike,
      props.token,
    ]
  );

  const loadAttendanceReports = React.useCallback(async () => {
    if (!isAdminLike) return;
    const gid = attendanceReportGroupId.trim();
    setIsAttendanceReportsLoading(true);
    try {
      const list = await apiAdminListAttendanceReports(props.token, gid || undefined);
      setAttendanceReports(list);
    } catch {
      setAttendanceReports([]);
    } finally {
      setIsAttendanceReportsLoading(false);
    }
  }, [attendanceReportGroupId, isAdminLike, props.me.role, props.token]);

  React.useEffect(() => {
    if (!isAdminLike) return;
    loadGroups();
  }, [isAdminLike, loadGroups, props.me.role]);

  React.useEffect(() => {
    if (!isAdminLike) return;
    if (!adminGroupsOpen) return;
    if (adminGroupConfigTab !== 'assign-children') return;
    loadGroupAssignList('reset');
  }, [adminGroupConfigTab, adminGroupsOpen, isAdminLike, loadGroupAssignList]);

  React.useEffect(() => {
    if (!isAdminLike) return;
    if (!adminGroupsOpen) return;
    if (adminGroupConfigTab !== 'assign-children') return;
    const t = window.setTimeout(() => {
      loadGroupAssignList('reset');
    }, 240);
    return () => window.clearTimeout(t);
  }, [
    adminGroupConfigTab,
    adminGroupsOpen,
    groupAssignBirthYearFilter,
    groupAssignGroupFilter,
    groupAssignQuery,
    groupAssignYearFilter,
    isAdminLike,
    loadGroupAssignList,
  ]);

  React.useEffect(() => {
    if (!attendanceReportOpen) return;
    if (attendanceReportGroupId) return;
    if (groups[0]) setAttendanceReportGroupId(groups[0].id);
  }, [attendanceReportGroupId, attendanceReportOpen, groups]);

  React.useEffect(() => {
    if (!attendanceReportOpen) return;
    loadAttendanceReports();
  }, [attendanceReportGroupId, attendanceReportOpen, loadAttendanceReports]);

  const toApiAccess = (a: AccessFlags) => ({
    can_parent: a.can_parent,
    can_coach: a.can_coach,
    can_admin: a.can_admin,
    can_coordinator: a.can_coordinator,
    can_player: a.can_player,
  });

  const approve = async (userId: string) => {
    setIsLoading(true);
    setErrorText(null);
    try {
      const access = pendingAccessById[userId] ?? { can_parent: true, can_coach: false, can_admin: false, can_coordinator: false, can_player: false };
      const role = normalizeRoleForAccess(pendingRoleById[userId] ?? 'PARENT', access);
      const safeRole = !isFullAdmin && (role === 'ADMIN' || role === 'COORDINATOR') ? 'PARENT' : role;
      const safeAccess = !isFullAdmin ? { ...access, can_admin: false, can_coordinator: false } : access;
      await apiAdminApproveUser(props.token, userId, { role: safeRole, ...toApiAccess(safeAccess) });
      await loadPending();
      await loadUsers();
    } catch (e) {
      setErrorText(adminActionError('Nie udało się zaakceptować konta.', e));
    } finally {
      setIsLoading(false);
    }
  };

  const createUser = async () => {
    if (!isAdminLike) return;
    const email = createUserEmail.trim();
    if (!email) return;
    const password = createUserPassword.trim();
    setIsLoading(true);
    setErrorText(null);
    setCreatedUserPassword(null);
    setCreatedUserEmail(null);
    try {
      const role = normalizeRoleForAccess(createUserRole, createUserAccess);
      const safeRole = !isFullAdmin && (role === 'ADMIN' || role === 'COORDINATOR') ? 'PARENT' : role;
      const safeAccess = !isFullAdmin ? { ...createUserAccess, can_admin: false, can_coordinator: false } : createUserAccess;
      const res = await apiAdminCreateUser(props.token, {
        email,
        role: safeRole,
        ...toApiAccess(safeAccess),
        status: createUserStatus,
        ...(password ? { password } : {}),
      });
      setCreatedUserEmail(res.user.email);
      setCreatedUserPassword(res.password);
      setCreateUserEmail('');
      setCreateUserPassword('');
      await loadUsers();
      await loadPending();
    } catch (e) {
      setErrorText(adminActionError('Nie udało się utworzyć konta.', e));
    } finally {
      setIsLoading(false);
    }
  };

  const updateUser = async (userId: string) => {
    if (userId === props.me.id) return;
    setIsLoading(true);
    setErrorText(null);
    try {
      const access = userAccessById[userId] ?? { can_parent: true, can_coach: false, can_admin: false, can_coordinator: false, can_player: false };
      const role = normalizeRoleForAccess(userRoleById[userId] ?? 'PARENT', access);
      const st = userStatusById[userId] ?? 'PENDING';
      const safeRole = !isFullAdmin && (role === 'ADMIN' || role === 'COORDINATOR') ? 'PARENT' : role;
      const safeAccess = !isFullAdmin ? { ...access, can_admin: false, can_coordinator: false } : access;
      await apiAdminUpdateUser(props.token, userId, { role: safeRole, ...toApiAccess(safeAccess), status: st });
      await loadUsers();
      await loadPending();
    } catch (e) {
      setErrorText(adminActionError('Nie udało się zapisać zmian konta.', e));
    } finally {
      setIsLoading(false);
    }
  };

  const openEditUserEmail = (u: AdminUserItem) => {
    if (u.id === props.me.id) return;
    setEditUserEmail({ id: u.id, label: userLabelWithEmail(u) });
    setEditUserEmailValue(u.email);
    setEditUserEmailError(null);
  };

  const saveUserEmail = async () => {
    if (!editUserEmail) return;
    if (editUserEmail.id === props.me.id) return;
    const email = editUserEmailValue.trim();
    if (!email) return;
    setIsLoading(true);
    setEditUserEmailError(null);
    try {
      await apiAdminSetUserEmail(props.token, editUserEmail.id, email);
      setEditUserEmail(null);
      setEditUserEmailValue('');
      await loadUsers();
      await loadPending();
    } catch (e) {
      const code = parseHttpStatus(e);
      if (code === 409) setEditUserEmailError('Ten email jest już zajęty.');
      else if (code === 422) setEditUserEmailError('Podaj poprawny email.');
      else setEditUserEmailError(adminActionError('Nie udało się zmienić emaila.', e));
    } finally {
      setIsLoading(false);
    }
  };

  const deleteUserConfirm = async () => {
    if (!deleteUser) return;
    if (deleteUser.id === props.me.id) return;
    setIsLoading(true);
    setErrorText(null);
    try {
      await apiAdminDeleteUser(props.token, deleteUser.id);
      setDeleteUser(null);
      await loadUsers();
      await loadPending();
    } catch (e) {
      setErrorText(adminActionError('Nie udało się usunąć konta.', e));
    } finally {
      setIsLoading(false);
    }
  };

  const openResetUserPassword = (u: AdminUserItem) => {
    if (u.id === props.me.id) return;
    setResetUserPassword({ id: u.id, label: userLabelWithEmail(u) });
    setResetUserPasswordValue('');
    setResetUserPasswordDone(null);
  };

  const saveResetUserPassword = async () => {
    if (!resetUserPassword) return;
    if (resetUserPassword.id === props.me.id) return;
    const pwd = resetUserPasswordValue.trim() || undefined;
    if (pwd && pwd.length < 8) {
      setResetUserPasswordDone(null);
      setErrorText('Hasło musi mieć min. 8 znaków. Zostaw puste aby wygenerować automatycznie.');
      return;
    }
    setIsLoading(true);
    setErrorText(null);
    try {
      const r = await apiAdminResetPassword(props.token, resetUserPassword.id, pwd);
      setResetUserPasswordDone({ password: r.password, label: resetUserPassword.label });
      await loadUsers();
      await loadPending();
    } catch (e) {
      setResetUserPasswordDone(null);
      setErrorText(adminActionError('Nie udało się zresetować hasła.', e));
    } finally {
      setIsLoading(false);
    }
  };

  const createChild = async () => {
    if (!isAdminLike) return;
    const first_name = newChildFirstName.trim();
    const last_initial = newChildLastInitial.trim();
    const birth_year = newChildBirthYear.trim() ? Number(newChildBirthYear.trim()) : null;
    const shirt_number = newChildShirtNumber.trim() ? Number(newChildShirtNumber.trim()) : null;
    setIsLoading(true);
    setErrorText(null);
    try {
      await apiAdminCreateChild(props.token, { first_name, last_initial, birth_year, shirt_number });
      setNewChildFirstName('');
      setNewChildLastInitial('');
      setNewChildBirthYear('');
      setNewChildShirtNumber('');
      await loadChildren();
    } catch (e) {
      setErrorText('Nie udało się dodać dziecka (sprawdź pola).');
    } finally {
      setIsLoading(false);
    }
  };

  const assignParent = async () => {
    if (!isAdminLike) return;
    const email = assignParentEmail.trim();
    if (!email || !assignChildId) return;
    setIsLoading(true);
    setErrorText(null);
    try {
      await apiAdminAssignParent(props.token, assignChildId, email);
      setAssignParentEmail('');
    } catch (e) {
      setErrorText('Nie udało się przypisać rodzica (sprawdź email i dziecko).');
    } finally {
      setIsLoading(false);
    }
  };

  const assignIndividualCoach = async () => {
    if (!isAdminLike) return;
    const email = assignIndividualCoachEmail.trim();
    if (!email || !assignChildId) return;
    setIsLoading(true);
    setErrorText(null);
    try {
      const list = await apiAdminAssignIndividualCoach(props.token, assignChildId, email);
      setIndividualCoaches(list);
      setAssignIndividualCoachEmail('');
    } catch (e) {
      setErrorText('Nie udało się przypisać trenera indywidualnego (konto musi być ACTIVE i mieć dostęp Trener).');
    } finally {
      setIsLoading(false);
    }
  };

  const unassignIndividualCoach = async (coachUserId: string) => {
    if (!isAdminLike) return;
    if (!assignChildId) return;
    setIsLoading(true);
    setErrorText(null);
    try {
      const list = await apiAdminUnassignIndividualCoach(props.token, assignChildId, coachUserId);
      setIndividualCoaches(list);
    } catch (e) {
      setErrorText('Nie udało się usunąć przypisania trenera indywidualnego.');
    } finally {
      setIsLoading(false);
    }
  };

  const assignPlayer = async () => {
    if (!isAdminLike) return;
    const email = assignPlayerEmail.trim();
    if (!email || !assignChildId) return;
    setIsLoading(true);
    setErrorText(null);
    try {
      const list = await apiAdminAssignPlayer(props.token, assignChildId, email);
      setChildPlayers(list);
      setAssignPlayerEmail('');
    } catch (e) {
      setErrorText('Nie udało się przypisać konta zawodnika (konto musi być ACTIVE i mieć dostęp Zawodnik).');
    } finally {
      setIsLoading(false);
    }
  };

  const unassignPlayer = async (playerUserId: string) => {
    if (!isAdminLike) return;
    if (!assignChildId) return;
    setIsLoading(true);
    setErrorText(null);
    try {
      const list = await apiAdminUnassignPlayer(props.token, assignChildId, playerUserId);
      setChildPlayers(list);
    } catch (e) {
      setErrorText('Nie udało się usunąć przypisania konta zawodnika.');
    } finally {
      setIsLoading(false);
    }
  };

  const createGroup = async () => {
    if (!isAdminLike) return;
    const name = newGroupName.trim();
    const year = newGroupYear.trim() ? Number(newGroupYear.trim()) : null;
    if (!name) return;
    setIsLoading(true);
    setErrorText(null);
    try {
      await apiAdminCreateGroup(props.token, { name, year });
      setNewGroupName('');
      setNewGroupYear('');
      await loadGroups();
    } catch (e) {
      setErrorText('Nie udało się dodać grupy (sprawdź pola).');
    } finally {
      setIsLoading(false);
    }
  };

  const setChildGroup = async (childId: string, nextGroupId?: string | null) => {
    if (!isAdminLike) return;
    const safeChildId = childId.trim();
    if (!safeChildId) return;
    const targetGroupId = typeof nextGroupId === 'string' ? nextGroupId : groupAssignTargetByChildId[safeChildId] ?? '';
    const gid = targetGroupId ? targetGroupId : null;
    setIsLoading(true);
    setErrorText(null);
    try {
      const updated = await apiAdminSetChildGroup(props.token, safeChildId, gid);
      setGroupAssignTargetByChildId((prev) => ({ ...prev, [safeChildId]: updated.group_id ?? '' }));
      setGroupAssignList((prev) => prev.map((item) => (item.id === updated.id ? { ...item, ...updated } : item)));
      await loadChildren();
    } catch (e) {
      setErrorText('Nie udało się przypisać grupy do dziecka.');
    } finally {
      setIsLoading(false);
    }
  };

  const assignCoach = async () => {
    if (!isAdminLike) return;
    const email = assignCoachEmail.trim();
    if (!assignCoachGroupId || !email) return;
    setIsLoading(true);
    setErrorText(null);
    try {
      await apiAdminAssignCoachToGroup(props.token, assignCoachGroupId, email);
      setAssignCoachEmail('');
    } catch (e) {
      setErrorText('Nie udało się przypisać trenera (konto musi być ACTIVE i mieć dostęp Trener).');
    } finally {
      setIsLoading(false);
    }
  };

  const updateChild = async () => {
    if (!isAdminLike) return;
    if (!editChild) return;
    const first_name = editChild.first_name.trim();
    const last_initial = editChild.last_initial.trim();
    const birth_year = editChild.birth_year.trim() ? Number(editChild.birth_year.trim()) : null;
    const shirt_number = editChild.shirt_number.trim() ? Number(editChild.shirt_number.trim()) : null;
    setIsLoading(true);
    setErrorText(null);
    try {
      await apiAdminUpdateChild(props.token, editChild.id, { first_name, last_initial, birth_year, shirt_number });
      setEditChild(null);
      await loadChildren();
    } catch (e) {
      setErrorText(adminActionError('Nie udało się zapisać zmian dziecka.', e));
    } finally {
      setIsLoading(false);
    }
  };

  const uploadEditChildPhoto = async (file: File) => {
    if (!isAdminLike) return;
    if (props.me.status !== 'ACTIVE') return;
    if (!editChild) return;
    setIsEditChildPhotoUploading(true);
    setEditChildPhotoError(null);
    try {
      const r = await apiSetChildPhoto(props.token, editChild.id, file);
      setEditChildPhotoUrl(r.data_url ?? null);
    } catch (e) {
      setEditChildPhotoError(adminActionError('Nie udało się zapisać zdjęcia.', e));
      throw e;
    } finally {
      setIsEditChildPhotoUploading(false);
    }
  };

  const openEditChildPhotoCrop = (file: File) => {
    if (!isAdminLike) return;
    if (props.me.status !== 'ACTIVE') return;
    if (!editChild) return;
    setEditChildPhotoCropReq({
      title: 'Ustaw zdjęcie dziecka',
      file,
      onConfirm: async (cropped) => {
        await uploadEditChildPhoto(cropped);
      },
    });
  };

  const clearEditChildPhoto = async () => {
    if (!isAdminLike) return;
    if (props.me.status !== 'ACTIVE') return;
    if (!editChild) return;
    setIsEditChildPhotoUploading(true);
    setEditChildPhotoError(null);
    try {
      await apiClearChildPhoto(props.token, editChild.id);
      setEditChildPhotoUrl(null);
    } catch (e) {
      setEditChildPhotoError(adminActionError('Nie udało się usunąć zdjęcia.', e));
    } finally {
      setIsEditChildPhotoUploading(false);
    }
  };

  const deleteChildConfirm = async () => {
    if (!isAdminLike) return;
    if (!deleteChild) return;
    setIsLoading(true);
    setErrorText(null);
    try {
      await apiAdminDeleteChild(props.token, deleteChild.id);
      setDeleteChild(null);
      await loadChildren();
    } catch (e) {
      setErrorText(adminActionError('Nie udało się usunąć dziecka.', e));
    } finally {
      setIsLoading(false);
    }
  };

  const filteredUsers = React.useMemo(() => {
    const q = usersQuery.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => {
      const email = (u.email || '').toLowerCase();
      const dn = (u.display_name || '').toLowerCase();
      const nick = (u.nickname || '').toLowerCase();
      return email.includes(q) || dn.includes(q) || nick.includes(q) || u.role.toLowerCase().includes(q) || u.status.toLowerCase().includes(q);
    });
  }, [users, usersQuery]);

  const describeErr = React.useCallback((e: unknown): string => {
    if (e && typeof e === 'object' && 'message' in e && typeof (e as { message: unknown }).message === 'string') {
      return (e as { message: string }).message;
    }
    try {
      return String(e);
    } catch {
      return 'unknown';
    }
  }, []);

  const adminDownloadDbBackup = React.useCallback(async () => {
    if (props.me.role !== 'ADMIN' || props.me.status !== 'ACTIVE') return;
    setIsDbLoading(true);
    setDbError(null);
    setDbInfo(null);
    try {
      const r = await apiAdminDbBackup(props.token);
      const url = URL.createObjectURL(r.blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = r.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setDbInfo(`Backup pobrany: ${r.filename}`);
    } catch (e) {
      setDbError(adminActionError('Nie udało się pobrać backupu bazy.', e));
    } finally {
      setIsDbLoading(false);
    }
  }, [props.me.role, props.me.status, props.token]);

  const adminDownloadAttendanceReportSamplePdf = React.useCallback(async () => {
    if (!isAdminLike || props.me.status !== 'ACTIVE') return;
    setIsLoading(true);
    setErrorText(null);
    try {
      const r = await apiAdminAttendanceReportSamplePdf(props.token);
      const url = URL.createObjectURL(r.blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = r.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErrorText(adminActionError('Nie udało się pobrać raportu PDF.', e));
    } finally {
      setIsLoading(false);
    }
  }, [isAdminLike, props.me.status, props.token]);

  const adminDownloadAttendanceReportPdf = React.useCallback(async () => {
    if (!isAdminLike || props.me.status !== 'ACTIVE') return;
    const gid = attendanceReportGroupId.trim();
    const y = Number(attendanceReportYear);
    const m = Number(attendanceReportMonth);
    if (!gid) {
      setErrorText('Wybierz grupę.');
      return;
    }
    if (!Number.isFinite(y) || y < 2000 || y > 2100) {
      setErrorText('Nieprawidłowy rok.');
      return;
    }
    if (!Number.isFinite(m) || m < 1 || m > 12) {
      setErrorText('Nieprawidłowy miesiąc.');
      return;
    }
    setIsLoading(true);
    setErrorText(null);
    try {
      const r = await apiAdminAttendanceReportPdf(props.token, { group_id: gid, range: attendanceReportRange, year: y, month: m });
      const url = URL.createObjectURL(r.blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = r.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      loadAttendanceReports();
    } catch (e) {
      setErrorText(adminActionError('Nie udało się pobrać raportu PDF.', e));
    } finally {
      setIsLoading(false);
    }
  }, [
    attendanceReportGroupId,
    attendanceReportMonth,
    attendanceReportRange,
    attendanceReportYear,
    isAdminLike,
    loadAttendanceReports,
    props.me.status,
    props.token,
  ]);

  const adminBulkGenerateAttendanceReportsPrevMonth = React.useCallback(async () => {
    if (props.me.role !== 'ADMIN' || props.me.status !== 'ACTIVE') return;
    if (!window.confirm('Wygenerować raporty miesięczne (poprzedni miesiąc) dla wszystkich grup? Istniejące raporty dla tego okresu zostaną nadpisane w archiwum.'))
      return;
    setIsLoading(true);
    setErrorText(null);
    try {
      const r = await apiAdminBulkGenerateAttendanceReports(props.token, { range: 'month' });
      const ym = `${r.year}-${String(r.month).padStart(2, '0')}`;
      const errors = r.results.filter((x) => !x.ok);
      const top = errors.slice(0, 5).map((x) => `${x.group_id}: ${x.error || 'ERROR'}`);
      const details = top.length > 0 ? `\n\nBłędy (pierwsze ${top.length}):\n${top.join('\n')}${errors.length > top.length ? `\n... (${errors.length - top.length} więcej)` : ''}` : '';
      window.alert(`Raporty wygenerowane.\nOkres: ${ym}\nOK: ${r.ok_count}/${r.total_groups}\nBłędy: ${r.error_count}${details}`);
      loadAttendanceReports();
    } catch (e) {
      setErrorText(adminActionError('Nie udało się wygenerować raportów zbiorczo.', e));
    } finally {
      setIsLoading(false);
    }
  }, [loadAttendanceReports, props.me.role, props.me.status, props.token]);

  const adminDownloadSavedAttendanceReport = React.useCallback(
    async (reportId: string) => {
      if (!isAdminLike || props.me.status !== 'ACTIVE') return;
      const rid = reportId.trim();
      if (!rid) return;
      setIsLoading(true);
      setErrorText(null);
      try {
        const r = await apiAdminDownloadAttendanceReport(props.token, rid);
        const url = URL.createObjectURL(r.blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = r.filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      } catch (e) {
        setErrorText(adminActionError('Nie udało się pobrać raportu PDF.', e));
      } finally {
        setIsLoading(false);
      }
    },
    [isAdminLike, props.me.status, props.token]
  );

  const adminResetStatsCalendar = React.useCallback(async () => {
    if (props.me.role !== 'ADMIN' || props.me.status !== 'ACTIVE') return;
    setIsDbLoading(true);
    setDbError(null);
    setDbInfo(null);
    try {
      const r = await apiAdminDbResetStatsCalendar(props.token, { confirm: dbConfirm, with_backup: dbWithBackup });
      const note = r.backup_filename ? ` (backup: ${r.backup_filename})` : '';
      setDbInfo(`Wyczyszczono statystyki i kalendarz.${note}`);
      setDbConfirm('');
    } catch (e) {
      setDbError(adminActionError('Nie udało się wyczyścić statystyk i kalendarza.', e));
    } finally {
      setIsDbLoading(false);
    }
  }, [dbConfirm, dbWithBackup, props.me.role, props.me.status, props.token]);

  const ensureSw = React.useCallback(async (): Promise<ServiceWorkerRegistration> => {
    let reg = await navigator.serviceWorker.getRegistration();
    if (reg) {
      if (!reg.active) {
        reg = await navigator.serviceWorker.ready;
      }
      await reg.update().catch(() => {});
      return reg;
    }

    const res = await fetch('/sw.js', { cache: 'no-store' });
    if (!res.ok) throw new Error(`SW_HTTP_${res.status}`);
    const ct = (res.headers.get('content-type') || '').toLowerCase();
    if (!ct.includes('javascript') && !ct.includes('ecmascript')) {
      throw new Error(`SW_BAD_CONTENT_TYPE_${ct || 'unknown'}`);
    }

    await navigator.serviceWorker.register('/sw.js', { scope: '/' });
    reg = await navigator.serviceWorker.ready;
    await reg.update().catch(() => {});
    return reg;
  }, []);

  const refreshPush = React.useCallback(async () => {
    setIsPushLoading(true);
    setPushError(null);
    try {
      if (!pushSupport.supported) {
        setPushPermission('default');
        setPushEnabled(false);
        setPushEndpoint(null);
        return;
      }
      setPushPermission(Notification.permission);
      const reg = await ensureSw();
      const sub = await reg.pushManager.getSubscription();
      setPushEnabled(!!sub);
      setPushEndpoint(sub?.endpoint ?? null);
    } catch (e) {
      setPushError(`Nie udało się uruchomić Service Workera: ${describeErr(e)}`);
    } finally {
      setIsPushLoading(false);
    }
  }, [describeErr, ensureSw, pushSupport.supported]);

  React.useEffect(() => {
    if (props.me.status !== 'ACTIVE') return;
    refreshPush();
  }, [props.me.status, refreshPush]);

  const enablePush = React.useCallback(async () => {
    if (!pushSupport.supported) return;
    setIsPushLoading(true);
    setPushError(null);
    try {
      const perm = await Notification.requestPermission();
      setPushPermission(perm);
      if (perm !== 'granted') {
        setPushEnabled(false);
        return;
      }

      const key = await apiPushPublicKey(props.token);
      if (!key.public_key) {
        setPushError('Brak skonfigurowanego klucza VAPID na backendzie.');
        return;
      }

      const reg = await ensureSw();
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(key.public_key) as unknown as BufferSource,
      });
      const json = sub.toJSON() as unknown as { endpoint?: string; keys?: { p256dh?: string; auth?: string } };
      const endpoint = (json.endpoint ?? '').trim();
      const p256dh = (json.keys?.p256dh ?? '').trim();
      const auth = (json.keys?.auth ?? '').trim();
      if (!endpoint || !p256dh || !auth) {
        setPushError('Nie udało się odczytać danych subskrypcji z przeglądarki.');
        return;
      }
      await apiPushSubscribe(props.token, { endpoint, keys: { p256dh, auth } });
      await refreshPush();
    } catch (e) {
      const err = describeErr(e);
      if (/push service error/i.test(err)) {
        setPushError(
          `Nie udało się włączyć powiadomień push: ${err}. Najczęściej pomaga: uruchom w Chrome, zaktualizuj Chrome + Usługi Google Play, wyłącz oszczędzanie baterii dla Chrome, usuń dane strony (Ustawienia strony -> Pamięć) i spróbuj ponownie.`
        );
        return;
      }
      setPushError(`Nie udało się włączyć powiadomień push: ${err}`);
    } finally {
      setIsPushLoading(false);
    }
  }, [describeErr, ensureSw, props.token, pushSupport.supported, refreshPush]);

  const disablePush = React.useCallback(async () => {
    if (!pushSupport.supported) return;
    setIsPushLoading(true);
    setPushError(null);
    try {
      const reg = await ensureSw();
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await apiPushUnsubscribe(props.token, { endpoint: sub.endpoint }).catch(() => {});
        await sub.unsubscribe().catch(() => {});
      }
      await refreshPush();
    } catch (e) {
      setPushError(`Nie udało się wyłączyć powiadomień push: ${describeErr(e)}`);
    } finally {
      setIsPushLoading(false);
    }
  }, [describeErr, ensureSw, props.token, pushSupport.supported, refreshPush]);

  const recheckPushPermission = React.useCallback(async () => {
    if (!pushSupport.supported) return;
    setIsPushLoading(true);
    setPushError(null);
    try {
      const perm = Notification.permission;
      setPushPermission(perm);
      if (perm === 'granted') {
        const key = await apiPushPublicKey(props.token);
        if (key.public_key) {
          const reg = await ensureSw();
          let sub = await reg.pushManager.getSubscription();
          if (!sub) {
            sub = await reg.pushManager.subscribe({
              userVisibleOnly: true,
              applicationServerKey: urlBase64ToUint8Array(key.public_key) as unknown as BufferSource,
            });
            const json = sub.toJSON() as unknown as { endpoint?: string; keys?: { p256dh?: string; auth?: string } };
            const endpoint = (json.endpoint ?? '').trim();
            const p256dh = (json.keys?.p256dh ?? '').trim();
            const auth = (json.keys?.auth ?? '').trim();
            if (endpoint && p256dh && auth) {
              await apiPushSubscribe(props.token, { endpoint, keys: { p256dh, auth } });
            }
          }
          window.location.reload();
          return;
        }
        await refreshPush();
        return;
      }
      if (perm === 'default') {
        await enablePush();
      }
    } catch (e) {
      setPushError(`Nie udało się ponownie sprawdzić uprawnień: ${describeErr(e)}`);
    } finally {
      setIsPushLoading(false);
    }
  }, [describeErr, enablePush, ensureSw, props.token, pushSupport.supported, refreshPush]);

  React.useEffect(() => {
    if (pushPermission !== 'denied') return;
    if (typeof Notification === 'undefined') return;
    let cancelled = false;
    let rounds = 0;
    const MAX_ROUNDS = 60;
    const INTERVAL_MS = 2000;
    const timer = window.setInterval(() => {
      if (cancelled) return;
      rounds += 1;
      try {
        const perm = Notification.permission;
        if (perm === 'granted') {
          window.clearInterval(timer);
          window.location.reload();
          return;
        }
        if (perm !== pushPermission) {
          setPushPermission(perm);
        }
      } catch {
        // ignore
      }
      if (rounds >= MAX_ROUNDS) {
        window.clearInterval(timer);
      }
    }, INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [pushPermission]);

  const sendTestPush = React.useCallback(async () => {
    if (!pushSupport.supported) return;
    setIsPushLoading(true);
    setPushError(null);
    try {
      await apiPushTest(props.token);
    } catch (e) {
      setPushError('Nie udało się wysłać testu push (sprawdź VAPID na backendzie i subskrypcję).');
    } finally {
      setIsPushLoading(false);
    }
  }, [props.token, pushSupport.supported]);

  const loadNotificationPrefs = React.useCallback(async () => {
    if (!props.token || props.me.status !== 'ACTIVE') return;
    setPrefsLoading(true);
    setPrefsError(null);
    try {
      const prefs = await apiUserGetNotificationPrefs(props.token);
      setNotificationPrefs(prefs);
    } catch (e) {
      setPrefsError(`Nie udało się załadować ustawień powiadomień: ${describeErr(e)}`);
      setNotificationPrefs(null);
    } finally {
      setPrefsLoading(false);
    }
  }, [describeErr, props.me.status, props.token]);

  const saveNotificationPrefs = React.useCallback(async () => {
    if (!props.token || props.me.status !== 'ACTIVE' || !notificationPrefs) return;
    setSavingPrefs(true);
    setPrefsError(null);
    try {
      const body: Partial<{
        silent_from: number;
        silent_until: number;
        channels: Record<string, boolean>;
        injury_alerts: 0 | 1;
      }> = {
        silent_from: notificationPrefs.silent_from,
        silent_until: notificationPrefs.silent_until,
        channels: notificationPrefs.channels,
        injury_alerts: notificationPrefs.injury_alerts,
      };
      const updated = await apiUserPutNotificationPrefs(props.token, body);
      setNotificationPrefs(updated);
      try {
        alert('Ustawienia powiadomień zapisane.');
      } catch {
        /* ignore */
      }
    } catch (e) {
      const err = `Nie udało się zapisać ustawień powiadomień: ${describeErr(e)}`;
      setPrefsError(err);
    } finally {
      setSavingPrefs(false);
    }
  }, [describeErr, notificationPrefs, props.me.status, props.token]);

  React.useEffect(() => {
    if (props.me.status !== 'ACTIVE') return;
    loadNotificationPrefs();
  }, [props.me.status, loadNotificationPrefs]);

  const downloadMkcertRootCa = React.useCallback(async () => {
    setIsPushLoading(true);
    setPushError(null);
    try {
      const blob = await apiDevMkcertRootCa(props.token);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'rootCA.pem';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setPushError(`Nie udało się pobrać certyfikatu CA: ${describeErr(e)}`);
    } finally {
      setIsPushLoading(false);
    }
  }, [describeErr, props.token]);

  const [profile, setProfile] = React.useState<UserProfile | null>(null);
  const [profileDisplayName, setProfileDisplayName] = React.useState('');
  const [profileNickname, setProfileNickname] = React.useState('');
  const [profileError, setProfileError] = React.useState<string | null>(null);
  const [isProfileLoading, setIsProfileLoading] = React.useState(false);
  const [passwordCurrent, setPasswordCurrent] = React.useState('');
  const [passwordNext, setPasswordNext] = React.useState('');
  const [passwordNext2, setPasswordNext2] = React.useState('');
  const [passwordError, setPasswordError] = React.useState<string | null>(null);
  const [passwordOk, setPasswordOk] = React.useState(false);
  const [isPasswordLoading, setIsPasswordLoading] = React.useState(false);
  const [rodoAcceptance, setRodoAcceptance] = React.useState<RodoAcceptance | null>(null);
  const [rodoLoading, setRodoLoading] = React.useState(false);
  const [rodoError, setRodoError] = React.useState<string | null>(null);
  const [rodoAcceptLoading, setRodoAcceptLoading] = React.useState(false);
  const [rodoSheetOpen, setRodoSheetOpen] = React.useState(false);
  const [rodoSheetAccepted, setRodoSheetAccepted] = React.useState(false);

  const loadProfile = React.useCallback(async () => {
    if (props.me.status !== 'ACTIVE') return;
    setIsProfileLoading(true);
    setProfileError(null);
    try {
      const p = await apiGetProfile(props.token);
      setProfile(p);
      setProfileDisplayName(p.display_name ?? '');
      setProfileNickname(p.nickname ?? '');
    } catch {
      setProfileError('Nie udało się pobrać profilu.');
    } finally {
      setIsProfileLoading(false);
    }
  }, [props.me.status, props.token]);

  const loadRodo = React.useCallback(async () => {
    if (props.me.status !== 'ACTIVE') return;
    setRodoLoading(true);
    setRodoError(null);
    try {
      const r = await apiGetRodo(props.token);
      setRodoAcceptance(r);
    } catch {
      setRodoError('Nie udało się pobrać regulaminu RODO.');
    } finally {
      setRodoLoading(false);
    }
  }, [props.me.status, props.token]);

  const acceptRodo = React.useCallback(async () => {
    if (!rodoAcceptance) return;
    setRodoAcceptLoading(true);
    setRodoError(null);
    try {
      const r = await apiAcceptRodo(props.token, rodoAcceptance.latest_version);
      setRodoAcceptance(r);
      setRodoSheetAccepted(true);
      if (props.onRefreshMe) props.onRefreshMe();
    } catch {
      setRodoError('Nie udało się zaakceptować regulaminu RODO.');
    } finally {
      setRodoAcceptLoading(false);
    }
  }, [props, rodoAcceptance]);

  React.useEffect(() => {
    loadProfile();
    loadRodo();
  }, [loadProfile, loadRodo]);

  const saveProfile = React.useCallback(async () => {
    if (props.me.status !== 'ACTIVE') return;
    setIsProfileLoading(true);
    setProfileError(null);
    try {
      const p = await apiUpdateProfile(props.token, {
        display_name: profileDisplayName.trim() ? profileDisplayName : null,
        nickname: profileNickname.trim() ? profileNickname : null,
      });
      setProfile(p);
      setProfileDisplayName(p.display_name ?? '');
      setProfileNickname(p.nickname ?? '');
    } catch {
      setProfileError('Nie udało się zapisać profilu.');
    } finally {
      setIsProfileLoading(false);
    }
  }, [profileDisplayName, profileNickname, props.me.status, props.token]);

  const canChangePassword =
    passwordCurrent.trim() !== '' &&
    passwordNext.trim() !== '' &&
    passwordNext2.trim() !== '' &&
    passwordNext.trim() === passwordNext2.trim() &&
    passwordNext.trim().length >= 8 &&
    !isPasswordLoading;

  const changePassword = React.useCallback(async () => {
    const cur = passwordCurrent.trim();
    const next = passwordNext.trim();
    const next2 = passwordNext2.trim();
    if (!cur || !next || !next2) return;
    if (next !== next2) {
      setPasswordError('Nowe hasła nie są takie same.');
      return;
    }
    if (next.length < 8) {
      setPasswordError('Hasło musi mieć co najmniej 8 znaków.');
      return;
    }
    setIsPasswordLoading(true);
    setPasswordError(null);
    setPasswordOk(false);
    try {
      await apiChangePassword(props.token, { current_password: cur, new_password: next });
      setPasswordCurrent('');
      setPasswordNext('');
      setPasswordNext2('');
      setPasswordOk(true);
    } catch (e) {
      const code = parseHttpStatus(e);
      if (code === 401) setPasswordError('Nieprawidłowe obecne hasło.');
      else if (code === 422) setPasswordError('Hasło musi mieć co najmniej 8 znaków.');
      else setPasswordError('Nie udało się zmienić hasła.');
    } finally {
      setIsPasswordLoading(false);
    }
  }, [passwordCurrent, passwordNext, passwordNext2, props.token]);

  const [coachEditOpen, setCoachEditOpen] = React.useState(false);
  const [passwordSheetOpen, setPasswordSheetOpen] = React.useState(false);
  const [adminPanelTab, setAdminPanelTab] = React.useState<'manage' | 'settings' | 'tools'>('manage');
  const [adminAccountsOpen, setAdminAccountsOpen] = React.useState(false);
  const [adminAccountsTab, setAdminAccountsTab] = React.useState<'pending' | 'all'>('pending');
  const adminUsersReloadTimeoutRef = React.useRef<number | null>(null);
  const [adminCreateUserOpen, setAdminCreateUserOpen] = React.useState(false);
  const [adminDbToolsOpen, setAdminDbToolsOpen] = React.useState(false);
  const [adminQuickReactionsOpen, setAdminQuickReactionsOpen] = React.useState(false);
  const [coachPickChildSheet, setCoachPickChildSheet] = React.useState<{ type: 'TRAINING' | 'MATCH' } | null>(null);
  const [coachPickChildQuery, setCoachPickChildQuery] = React.useState('');

  const [isDesktop, setIsDesktop] = React.useState(() => {
    try {
      if (typeof window === 'undefined') return false;
      return window.matchMedia && window.matchMedia('(min-width: 1024px)').matches;
    } catch (e) {
      return false;
    }
  });
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!window.matchMedia) return;
    const mql = window.matchMedia('(min-width: 1024px)');
    const onChange = (e: MediaQueryListEvent | MediaQueryList) => {
      const matched = 'matches' in e ? !!e.matches : false;
      setIsDesktop(matched);
    };
    onChange(mql);
    let detached = false;
    try {
      if (typeof (mql as any).addEventListener === 'function') {
        (mql as any).addEventListener('change', onChange);
      } else if (typeof (mql as any).addListener === 'function') {
        (mql as any).addListener(onChange);
      }
    } catch (e) {}
    return () => {
      detached = true;
      try {
        if (typeof (mql as any).removeEventListener === 'function') {
          (mql as any).removeEventListener('change', onChange);
        } else if (typeof (mql as any).removeListener === 'function') {
          (mql as any).removeListener(onChange);
        }
      } catch (e) {}
    };
  }, []);

  type DesktopAdminSection = 'users' | 'groups' | 'children' | 'manage' | 'settings' | 'tools';
  const [desktopAdminSection, setDesktopAdminSection] = React.useState<DesktopAdminSection>('users');

  const [dtUsersPage, setDtUsersPage] = React.useState(1);
  const [dtUsersPerPage, setDtUsersPerPage] = React.useState(25);
  const [dtUsersSearch, setDtUsersSearch] = React.useState('');
  const [dtUsersStatus, setDtUsersStatus] = React.useState<'ALL' | 'ACTIVE' | 'PENDING' | 'REJECTED'>('ALL');
  const [dtUsersData, setDtUsersData] = React.useState<{ data: AdminUserItem[]; total: number; page: number; per_page: number; total_pages: number } | null>(null);
  const [dtUsersLoading, setDtUsersLoading] = React.useState(false);
  const dtUsersDebounceRef = React.useRef<number | null>(null);

  const [dtChildrenPage, setDtChildrenPage] = React.useState(1);
  const [dtChildrenPerPage, setDtChildrenPerPage] = React.useState(25);
  const [dtChildrenSearch, setDtChildrenSearch] = React.useState('');
  const [dtChildrenGroup, setDtChildrenGroup] = React.useState('ALL');
  const [dtChildrenData, setDtChildrenData] = React.useState<{ data: ChildItem[]; total: number; page: number; per_page: number; total_pages: number } | null>(null);
  const [dtChildrenLoading, setDtChildrenLoading] = React.useState(false);
  const dtChildrenDebounceRef = React.useRef<number | null>(null);

  const [dtGroupsPage, setDtGroupsPage] = React.useState(1);
  const [dtGroupsPerPage, setDtGroupsPerPage] = React.useState(25);
  const [dtGroupsSearch, setDtGroupsSearch] = React.useState('');
  const [dtGroupsData, setDtGroupsData] = React.useState<{ data: GroupItem[]; total: number; page: number; per_page: number; total_pages: number } | null>(null);
  const [dtGroupsLoading, setDtGroupsLoading] = React.useState(false);
  const dtGroupsDebounceRef = React.useRef<number | null>(null);

  type LnpRowStatus = 'MATCHED' | 'NO_MATCH' | 'DUPLICATE' | 'ERROR' | 'PENDING';
  type LnpParsedRowFE = {
    id?: string | null;
    date: string;
    opponent: string;
    score_home: number | null;
    score_away: number | null;
    side: 'HOME' | 'AWAY' | 'UNKNOWN';
    scorers?: string | null;
    status?: LnpRowStatus;
    match_event_id?: string | null;
    note?: string | null;
  };
  const [lnpOpen, setLnpOpen] = React.useState(false);
  const [lnpStep, setLnpStep] = React.useState<1 | 2 | 3>(1);
  const [lnpCsvText, setLnpCsvText] = React.useState('');
  const [lnpParsedRows, setLnpParsedRows] = React.useState<LnpParsedRowFE[]>([]);
  const [lnpRowChecked, setLnpRowChecked] = React.useState<Record<number, boolean>>({});
  const [lnpApplySummary, setLnpApplySummary] = React.useState<LnpApplyResponse | null>(null);
  const [lnpLoading, setLnpLoading] = React.useState(false);
  const [lnpError, setLnpError] = React.useState<string | null>(null);
  const [lnpHistory, setLnpHistory] = React.useState<any[]>([]);
  const coachName = React.useMemo(() => {
    const v = (profile?.display_name ?? profileDisplayName ?? '').trim();
    if (v) return v;
    const em = (profile?.email ?? '').trim();
    if (!em) return 'TRENER';
    const base = em.split('@')[0] ?? '';
    return base ? base.toUpperCase() : 'TRENER';
  }, [profile?.display_name, profile?.email, profileDisplayName]);
  const coachSubtitle = React.useMemo(() => {
    const group = props.workGroups.find((g) => g.id === props.activeGroupId) ?? null;
    const groupLabel = group ? `${group.name}${group.year ? ` (${group.year})` : ''}` : '';
    const modeLabel = props.coachViewMode === 'INDIVIDUAL' ? 'Trener indywidualny' : 'Trener grupowy';
    return groupLabel ? `${modeLabel} • ${groupLabel}` : modeLabel;
  }, [props.activeGroupId, props.coachViewMode, props.workGroups]);
  const coachInitials = React.useMemo(() => {
    const parts = coachName
      .split(/\s+/g)
      .map((p) => p.trim())
      .filter(Boolean);
    const a = parts[0]?.slice(0, 1) ?? '';
    const b = parts.length > 1 ? parts[parts.length - 1]?.slice(0, 1) ?? '' : '';
    const out = `${a}${b}`.toUpperCase();
    return out || 'T';
  }, [coachName]);
  const todayLabel = React.useMemo(() => {
    const fmt = new Intl.DateTimeFormat('pl-PL', { day: '2-digit', month: 'long' });
    return `DZISIAJ, ${fmt.format(new Date()).toUpperCase()}`;
  }, []);
  const coachNicknameLabel = React.useMemo(() => {
    const v = (profile?.nickname ?? profileNickname ?? '').trim();
    return v ? v : '—';
  }, [profile?.nickname, profileNickname]);

  React.useEffect(() => {
    if (!isAdminLike) return;
    if (!adminAccountsOpen) return;
    if (adminAccountsTab !== 'all') return;
    if (adminUsersReloadTimeoutRef.current) window.clearTimeout(adminUsersReloadTimeoutRef.current);
    adminUsersReloadTimeoutRef.current = window.setTimeout(() => {
      loadUsers();
    }, 240);
    return () => {
      if (adminUsersReloadTimeoutRef.current) window.clearTimeout(adminUsersReloadTimeoutRef.current);
    };
  }, [adminAccountsOpen, adminAccountsTab, adminUsersReloadTimeoutRef, isAdminLike, loadUsers, usersQuery, usersStatusFilter]);
  const parentSubtitle = React.useMemo(() => {
    if (props.me.role !== 'PARENT') return '';
    const labels = props.children
      .map((c) => (c.group_name ?? '').trim())
      .filter(Boolean);
    const uniq = Array.from(new Set(labels));
    const head = uniq.slice(0, 2).join(' & ');
    const tail = uniq.length > 2 ? ` +${uniq.length - 2}` : '';
    return head ? `Rodzic • ${head}${tail}` : 'Rodzic';
  }, [props.children, props.me.role]);
  const adminSubtitle = React.useMemo(() => {
    if (!isAdminLike) return '';
    if (props.me.role === 'ADMIN') return 'Administrator systemu • Superużytkownik';
    return 'Koordynator • Panel administracyjny';
  }, [isAdminLike, props.me.role]);
  const switchableRoles = React.useMemo(() => (props.me.roles ?? []).filter((r) => r !== props.me.role), [props.me.role, props.me.roles]);

  const dtApiGet = async <T extends any>(url: string, token: string): Promise<T> => {
    const res = await fetch(url, { headers: { Accept: 'application/json', Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new Error(`HTTP_${res.status}`);
    return res.json();
  };

  const loadDesktopUsers = React.useCallback(async () => {
    if (!isAdminLike) return;
    setDtUsersLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', String(dtUsersPage));
      params.set('per_page', String(dtUsersPerPage));
      if (dtUsersSearch.trim()) params.set('search', dtUsersSearch.trim());
      if (dtUsersStatus !== 'ALL') params.set('status_filter', dtUsersStatus);
      const url = `/v1/admin/users?${params.toString()}`;
      const r = await dtApiGet<{ data: AdminUserItem[]; total: number; page: number; per_page: number; total_pages: number }>(url, props.token);
      if (r && Array.isArray((r as any).data)) {
        setDtUsersData(r as any);
      } else if (Array.isArray(r as any)) {
        const arr = (r as any) as AdminUserItem[];
        setDtUsersData({ data: arr, total: arr.length, page: dtUsersPage, per_page: dtUsersPerPage, total_pages: 1 });
      }
    } catch (e) {
      setDtUsersData(null);
    } finally {
      setDtUsersLoading(false);
    }
  }, [dtUsersPage, dtUsersPerPage, dtUsersSearch, dtUsersStatus, isAdminLike, props.token]);

  const loadDesktopGroups = React.useCallback(async () => {
    if (!isAdminLike) return;
    setDtGroupsLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', String(dtGroupsPage));
      params.set('per_page', String(dtGroupsPerPage));
      if (dtGroupsSearch.trim()) params.set('search', dtGroupsSearch.trim());
      const url = `/v1/admin/groups?${params.toString()}`;
      const r = await dtApiGet<{ data: GroupItem[]; total: number; page: number; per_page: number; total_pages: number }>(url, props.token);
      if (r && Array.isArray((r as any).data)) {
        setDtGroupsData(r as any);
      } else if (Array.isArray(r as any)) {
        const arr = (r as any) as GroupItem[];
        setDtGroupsData({ data: arr, total: arr.length, page: dtGroupsPage, per_page: dtGroupsPerPage, total_pages: 1 });
      }
    } catch (e) {
      setDtGroupsData(null);
    } finally {
      setDtGroupsLoading(false);
    }
  }, [dtGroupsPage, dtGroupsPerPage, dtGroupsSearch, isAdminLike, props.token]);

  const loadDesktopChildren = React.useCallback(async () => {
    if (!isAdminLike) return;
    setDtChildrenLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', String(dtChildrenPage));
      params.set('per_page', String(dtChildrenPerPage));
      if (dtChildrenSearch.trim()) params.set('search', dtChildrenSearch.trim());
      if (dtChildrenGroup !== 'ALL') params.set('group_id', dtChildrenGroup);
      const url = `/v1/admin/children?${params.toString()}`;
      const r = await dtApiGet<{ data: ChildItem[]; total: number; page: number; per_page: number; total_pages: number }>(url, props.token);
      if (r && Array.isArray((r as any).data)) {
        setDtChildrenData(r as any);
      } else if (Array.isArray(r as any)) {
        const arr = (r as any) as ChildItem[];
        setDtChildrenData({ data: arr, total: arr.length, page: dtChildrenPage, per_page: dtChildrenPerPage, total_pages: 1 });
      }
    } catch (e) {
      setDtChildrenData(null);
    } finally {
      setDtChildrenLoading(false);
    }
  }, [dtChildrenPage, dtChildrenPerPage, dtChildrenSearch, dtChildrenGroup, isAdminLike, props.token]);

  React.useEffect(() => { if (!isAdminLike) return; loadDesktopUsers(); }, [loadDesktopUsers, isAdminLike]);
  React.useEffect(() => { if (!isAdminLike) return; loadDesktopGroups(); }, [loadDesktopGroups, isAdminLike]);
  React.useEffect(() => { if (!isAdminLike) return; loadDesktopChildren(); }, [loadDesktopChildren, isAdminLike]);

  React.useEffect(() => {
    if (!isAdminLike) return;
    if (dtUsersDebounceRef.current) window.clearTimeout(dtUsersDebounceRef.current);
    dtUsersDebounceRef.current = window.setTimeout(() => { loadDesktopUsers(); }, 260);
    return () => { if (dtUsersDebounceRef.current) window.clearTimeout(dtUsersDebounceRef.current); };
  }, [dtUsersSearch, dtUsersStatus, isAdminLike, loadDesktopUsers]);
  React.useEffect(() => {
    if (!isAdminLike) return;
    if (dtGroupsDebounceRef.current) window.clearTimeout(dtGroupsDebounceRef.current);
    dtGroupsDebounceRef.current = window.setTimeout(() => { loadDesktopGroups(); }, 260);
    return () => { if (dtGroupsDebounceRef.current) window.clearTimeout(dtGroupsDebounceRef.current); };
  }, [dtGroupsSearch, isAdminLike, loadDesktopGroups]);
  React.useEffect(() => {
    if (!isAdminLike) return;
    if (dtChildrenDebounceRef.current) window.clearTimeout(dtChildrenDebounceRef.current);
    dtChildrenDebounceRef.current = window.setTimeout(() => { loadDesktopChildren(); }, 260);
    return () => { if (dtChildrenDebounceRef.current) window.clearTimeout(dtChildrenDebounceRef.current); };
  }, [dtChildrenSearch, dtChildrenGroup, isAdminLike, loadDesktopChildren]);

  const roleBadgeClass = (r: string): string => {
    if (r === 'ADMIN') return 'AdminRolePill AdminRolePill--A';
    if (r === 'COORDINATOR') return 'AdminRolePill AdminRolePill--C';
    if (r === 'COACH') return 'AdminRolePill AdminRolePill--H';
    if (r === 'PARENT') return 'AdminRolePill AdminRolePill--R';
    if (r === 'PLAYER') return 'AdminRolePill AdminRolePill--P';
    return 'AdminRolePill';
  };
  const statusBadgeClass = (s: string): string => {
    if (s === 'ACTIVE') return 'AdminTableBadge AdminTableBadge--ok';
    if (s === 'PENDING') return 'AdminTableBadge AdminTableBadge--warn';
    if (s === 'REJECTED') return 'AdminTableBadge AdminTableBadge--danger';
    return 'AdminTableBadge AdminTableBadge--info';
  };
  const lnpStatusBadge = (s?: string): { cls: string; txt: string } => {
    if (s === 'MATCHED') return { cls: 'AdminTableBadge AdminTableBadge--ok', txt: 'MATCHED' };
    if (s === 'NO_MATCH') return { cls: 'AdminTableBadge AdminTableBadge--warn', txt: 'NO MATCH' };
    if (s === 'DUPLICATE') return { cls: 'AdminTableBadge AdminTableBadge--warn', txt: 'DUPLIKAT' };
    if (s === 'ERROR') return { cls: 'AdminTableBadge AdminTableBadge--danger', txt: 'BŁĄD' };
    if (s === 'IMPORTED') return { cls: 'AdminTableBadge AdminTableBadge--ok', txt: 'ZAIMPORTOWANO' };
    return { cls: 'AdminTableBadge AdminTableBadge--info', txt: 'OCZEKUJE' };
  };

  const profileChildScoped = isChildScopedRole(props.me.role);
  const roleSwitchSection =
    switchableRoles.length > 0 ? (
      <section className="CoachProfileSection" aria-label="Przełącz profil">
        <div className="CoachProfileSectionTitle">PRZEŁĄCZ PROFIL</div>
        <div className="Muted">To samo konto może działać w kilku kontekstach. Przełączasz widok bez wylogowania.</div>
        <div className="CoachProfilePills" aria-label="Dostępne profile">
          {[props.me.role, ...switchableRoles].map((r) => {
            const isCurrent = r === props.me.role;
            return (
              <button
                key={r}
                type="button"
                className={['Pill', isCurrent ? 'is-active' : ''].filter(Boolean).join(' ')}
                onClick={() => {
                  if (isCurrent || !props.onSwitchRole) return;
                  props.onSwitchRole(r);
                }}
                disabled={!!props.isRoleSwitchLoading}
                aria-pressed={isCurrent}
              >
                {isCurrent ? `${roleLabelPl(r)} • aktywny` : `Przełącz na ${roleLabelPl(r)}`}
              </button>
            );
          })}
        </div>
        <div className="Muted">{roleContextHint(props.me.role)}</div>
        {profileChildScoped && props.selectedChild && <div className="Muted">{`Aktywne dziecko: ${childLabel(props.selectedChild)}`}</div>}
        {props.me.role === 'PARENT' && !props.selectedChild && props.children.length > 1 && (
          <div className="Muted">Po wejściu w profil rodzica wybierasz dziecko, dla którego chcesz działać.</div>
        )}
      </section>
    ) : null;

  const adminGroupYearById = React.useMemo(() => {
    const out = new Map<string, number>();
    for (const g of groups) {
      const id = (g.id ?? '').trim();
      if (!id) continue;
      if (Number.isFinite(g.year as number)) out.set(id, g.year as number);
    }
    return out;
  }, [groups]);

  const adminChildGroupOptions = React.useMemo(() => {
    const out: { id: string; label: string }[] = [];
    for (const g of groups) {
      const id = (g.id ?? '').trim();
      if (!id) continue;
      const name = (g.name ?? '').trim();
      const label = `${name}${g.year ? ` (${g.year})` : ''}`.trim();
      out.push({ id, label });
    }
    out.sort((a, b) => a.label.localeCompare(b.label, 'pl-PL'));
    return out;
  }, [groups]);

  const adminChildGroupYearOptions = React.useMemo(() => {
    const set = new Set<number>();
    for (const g of groups) {
      if (Number.isFinite(g.year as number)) set.add(g.year as number);
    }
    const out = Array.from(set);
    out.sort((a, b) => a - b);
    return out;
  }, [groups]);

  const adminChildBirthYearOptions = React.useMemo(() => {
    const set = new Set<number>();
    for (const c of children) {
      if (Number.isFinite(c.birth_year as number)) set.add(c.birth_year as number);
    }
    const out = Array.from(set);
    out.sort((a, b) => a - b);
    return out;
  }, [children]);

  const groupAssignDisplayList = React.useMemo(() => {
    const out = [...groupAssignList];
    const getName = (c: ChildItem) => `${(c.first_name ?? '').trim()} ${(c.last_initial ?? '').trim()}`.trim();
    const getGroup = (c: ChildItem) => `${(c.group_name ?? '').trim()} ${c.group_year ?? ''}`.trim();
    out.sort((a, b) => {
      if (groupAssignSort === 'birth-asc') return (a.birth_year ?? 9999) - (b.birth_year ?? 9999) || getName(a).localeCompare(getName(b), 'pl-PL');
      if (groupAssignSort === 'birth-desc') return (b.birth_year ?? -1) - (a.birth_year ?? -1) || getName(a).localeCompare(getName(b), 'pl-PL');
      if (groupAssignSort === 'group-asc') return getGroup(a).localeCompare(getGroup(b), 'pl-PL') || getName(a).localeCompare(getName(b), 'pl-PL');
      if (groupAssignSort === 'group-desc') return getGroup(b).localeCompare(getGroup(a), 'pl-PL') || getName(a).localeCompare(getName(b), 'pl-PL');
      if (groupAssignSort === 'name-desc') return getName(b).localeCompare(getName(a), 'pl-PL');
      return getName(a).localeCompare(getName(b), 'pl-PL');
    });
    return out;
  }, [groupAssignList, groupAssignSort]);

  if (props.me.role === 'COACH') {
    return (
      <div className="Page CoachProfilePage">
        <div className="CoachProfileTopBar" aria-label="Nagłówek profilu trenera">
          <div className="CoachProfileBrand">
            <img className="CoachProfileLogo" src={clubLogo} alt="" aria-hidden="true" />
            <div className="CoachProfileBrandText">
              <div className="CoachProfileBrandTitle">AP BŁONIANKA</div>
              <div className="CoachProfileBrandSub">AKADEMIA</div>
            </div>
          </div>
          <div className="CoachProfileToday">{todayLabel}</div>
        </div>

        <section className="CoachProfileHero" aria-label="Profil trenera">
          <div className="CoachProfileHeroTop">
            <div className="CoachProfileAvatar" aria-hidden="true">
              {coachInitials}
            </div>
            <div className="CoachProfileHeroMain">
              <div className="CoachProfileName">{coachName}</div>
              <div className="CoachProfileRole">{coachSubtitle}</div>
              <div className="CoachProfileStatusRow">
                <span className={['CoachProfileStatusDot', props.me.status === 'ACTIVE' ? 'is-active' : ''].filter(Boolean).join(' ')} />
                <span className="CoachProfileStatusLabel">{props.me.status}</span>
              </div>
            </div>
            <button className="CoachProfileGear" type="button" onClick={() => setCoachEditOpen((v) => !v)} aria-label="Ustawienia profilu">
              ⚙
            </button>
          </div>
          <div className="CoachProfileHeroBottom">
            <div className="CoachProfileHeroHint">Zarządzaj swoim profilem i uprawnieniami</div>
            <button className="CoachProfileEditBtn" type="button" onClick={() => setCoachEditOpen((v) => !v)} disabled={isProfileLoading}>
              ✎ EDYCJA
            </button>
          </div>
        </section>

        {roleSwitchSection}

        <div className="SectionTabs" role="tablist" aria-label="Sekcje panelu administracyjnego">
          <button
            className={['SectionTab', adminPanelTab === 'manage' ? 'is-active' : ''].filter(Boolean).join(' ')}
            type="button"
            onClick={() => setAdminPanelTab('manage')}
            aria-label="Zarządzanie"
          >
            Zarządzanie
          </button>
          <button
            className={['SectionTab', adminPanelTab === 'settings' ? 'is-active' : ''].filter(Boolean).join(' ')}
            type="button"
            onClick={() => setAdminPanelTab('settings')}
            aria-label="Ustawienia"
          >
            Ustawienia
          </button>
          <button
            className={['SectionTab', adminPanelTab === 'tools' ? 'is-active' : ''].filter(Boolean).join(' ')}
            type="button"
            onClick={() => setAdminPanelTab('tools')}
            aria-label="Narzędzia"
          >
            Narzędzia
          </button>
        </div>

        {coachEditOpen && (
          <section className="CoachProfileSection" aria-label="Edycja profilu">
            {profileError && <div className="ErrorBanner">{profileError}</div>}
            <div className="CoachProfileSectionTitle">EDYCJA</div>
            {profile?.email && (
              <div className="CoachProfileRow">
                <div className="CoachProfileRowKey">E-mail / Login</div>
                <div className="CoachProfileRowVal">{profile.email}</div>
              </div>
            )}
            <div className="CoachProfileForm">
              <input
                className="TextInput"
                value={profileDisplayName}
                onChange={(e) => setProfileDisplayName(e.target.value)}
                placeholder="Imię i nazwisko"
                disabled={isProfileLoading}
              />
              <input
                className="TextInput"
                value={profileNickname}
                onChange={(e) => setProfileNickname(e.target.value)}
                placeholder="Nick / opis"
                disabled={isProfileLoading}
              />
              <div className="CoachProfileActions">
                <PrimaryButton onClick={saveProfile} disabled={isProfileLoading} type="button">
                  Zapisz
                </PrimaryButton>
                <SecondaryButton onClick={() => setCoachEditOpen(false)} disabled={isProfileLoading} type="button">
                  Zamknij
                </SecondaryButton>
              </div>
            </div>
          </section>
        )}

        <section className="CoachProfileSection" aria-label="Zarządzanie kontem">
          <div className="CoachProfileSectionTitle">ZARZĄDZANIE KONTEM</div>
          <div className="CoachProfileManageRows">
            <div className="CoachProfileManageRow">
              <div className="CoachProfileManageLeft">
                <div className="CoachProfileManageLabel">E-mail / Login:</div>
                <div className="CoachProfileManageValue">{profile?.email ?? '—'}</div>
              </div>
              <div className="CoachProfileManageRight">
                <span className="CoachProfileInlineIcon" aria-hidden="true">
                  🔒
                </span>
                <button
                  className="CoachProfileInlineBtn"
                  type="button"
                  onClick={() => setPasswordSheetOpen(true)}
                  disabled={isPasswordLoading || props.me.status !== 'ACTIVE'}
                >
                  🔑 Zmień hasło
                </button>
              </div>
            </div>
            <div className="CoachProfileManageRow">
              <div className="CoachProfileManageLeft">
                <div className="CoachProfileManageLabel">Nick / Opis:</div>
                <div className="CoachProfileManageValue">{coachNicknameLabel}</div>
              </div>
              <div className="CoachProfileManageRight">
                <button
                  className="CoachProfileInlineIconBtn"
                  type="button"
                  onClick={() => setCoachEditOpen(true)}
                  aria-label="Edytuj nick / opis"
                  disabled={isProfileLoading}
                >
                  ✎
                </button>
              </div>
            </div>
          </div>
          <div className="Muted">Zmianę emaila może wykonać tylko Admin.</div>
        </section>

        <section className="CoachProfileSection" aria-label="Moje drużyny">
          <div className="CoachProfileSectionTitle">MOJE DRUŻYNY</div>
          {props.workGroups.length === 0 ? (
            <div className="Muted">Brak grup.</div>
          ) : (
            <div className="CoachProfileGroupGrid" aria-label="Lista grup">
              {props.workGroups.map((g) => {
                const active = g.id === props.activeGroupId;
                return (
                  <button
                    key={g.id}
                    type="button"
                    className={['CoachProfileGroupCard', active ? 'is-active' : ''].filter(Boolean).join(' ')}
                    onClick={() => props.onChangeActiveGroupId(g.id)}
                  >
                    <img className="CoachProfileGroupLogo" src={clubLogo} alt="" aria-hidden="true" />
                    <span className="CoachProfileGroupCardText">{`${g.name}${g.year ? ` (${g.year})` : ''}`}</span>
                    {active && (
                      <span className="CoachProfileGroupCheck" aria-hidden="true">
                        <CheckIcon className="CoachProfileGroupPillCheck" />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </section>

        <section className="CoachProfileSection" aria-label="Trener indywidualny">
          <div className="CoachProfileSectionTitle">TRENER INDYWIDUALNY (ROLA WARUNKOWA)</div>
          <div
            className="CoachProfileIndividualCard"
            role="button"
            tabIndex={0}
            onClick={() => navigate('druzyna')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') navigate('druzyna');
            }}
          >
            <div className="CoachProfileIndividualAvatar" aria-hidden="true">
              {coachInitials}
            </div>
            <div className="CoachProfileIndividualMain">
              <div className="CoachProfileIndividualTitle">Zarządzaj Podopiecznymi</div>
              <div className="CoachProfileIndividualSub">{`Podopieczni: ${props.coachChildren.length}`}</div>
            </div>
            <div className="CoachProfileIndividualEnd" aria-hidden="true">
              ›
            </div>
          </div>
          <div className="CoachProfilePills" aria-label="Tryb trenera">
            {(['GROUP', 'INDIVIDUAL'] as const).map((m) => (
              <button
                key={m}
                type="button"
                className={['CoachProfileModePill', props.coachViewMode === m ? 'is-active' : ''].filter(Boolean).join(' ')}
                onClick={() => props.onChangeCoachViewMode(m)}
              >
                {m === 'GROUP' ? 'Grupowy' : 'Indywidualny'}
              </button>
            ))}
          </div>
          {props.coachViewMode === 'INDIVIDUAL' && (
            <>
              {props.coachChildren.length === 0 ? (
                <div className="Muted">Brak podopiecznych — poproś Admina o przypisanie zawodnika.</div>
              ) : (
                <div className="CoachManageActions" aria-label="Dodawanie wydarzeń dla podopiecznego">
                  <button className="InlineCta" type="button" onClick={() => setCoachPickChildSheet({ type: 'MATCH' })} disabled={isProfileLoading}>
                    DODAJ MECZ
                  </button>
                  <button
                    className="CoachPillButton"
                    type="button"
                    onClick={() => setCoachPickChildSheet({ type: 'TRAINING' })}
                    disabled={isProfileLoading}
                  >
                    DODAJ TRENING
                  </button>
                </div>
              )}
            </>
          )}
        </section>

        <section className="CoachProfileSection" aria-label="Ustawienia powiadomień">
          <div className="CoachProfileSectionTitle">USTAWIENIA POWIADOMIEŃ</div>
          {isIOS && iosVersion > 0 && iosVersion < 16.4 && (
            <div className="WarningBanner" role="note" style={{ padding: '12px 14px', borderRadius: '8px', background: '#fff1f0', border: '1px solid #ffa39e', margin: '12px 0' }}>
              <div style={{ fontWeight: 600, marginBottom: '6px' }}>🔴 Wersja iOS poniżej 16.4</div>
              <div style={{ lineHeight: 1.55 }}>Wersja iOS <strong>{String(iosVersion)}</strong> nie wspiera powiadomień Web Push. Zaktualizuj iPhone do co najmniej <strong>iOS 16.4</strong>: <em>Ustawienia → Ogólne → Aktualizacja oprogramowania</em>.</div>
            </div>
          )}
          {isIOS && iosVersion >= 16.4 && !isIOSStandalone && pushPermission !== 'granted' && (
            <div className="WarningBanner" role="note" style={{ padding: '12px 14px', borderRadius: '8px', background: '#fffbe6', border: '1px solid #ffe58f', margin: '12px 0' }}>
              <div style={{ fontWeight: 600, marginBottom: '8px' }}>🟡 Dodaj FootDesk do ekranu głównego, aby włączyć powiadomienia iOS PWA</div>
              <ol style={{ margin: 0, paddingLeft: '20px', lineHeight: 1.7 }}>
                <li>🔗 <strong>Udostępnij</strong>: Stuknij ikonkę udostępniania ↗️ w pasku Safari (dół ekranu).</li>
                <li>➕ <strong>Dodaj do ekranu głównego</strong>: Przewiń w dół menu i wybierz „Dodaj do ekranu głównego”.</li>
                <li>✏️ <strong>Nazwij</strong>: Wpisz „FootDesk” jako nazwę, stuknij „Dodaj” w prawym górnym rogu.</li>
                <li>🏠 <strong>Otwórz z ikonki</strong>: Zamknij Safari, otwórz FootDesk z ikony na ekranie głównym iPhone.</li>
                <li>🔔 <strong>Włącz powiadomienia</strong>: Przejdź do Profilu → Ustawienia → stuknij „Włącz powiadomienia” poniżej. Zezwól w oknie dialogowym Safari.</li>
              </ol>
              <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
                <SecondaryButton onClick={() => window.location.reload()} disabled={isPushLoading} type="button">
                  🔄 SPRAWDŹ PONOWNIE TRYB STANDALONE
                </SecondaryButton>
              </div>
            </div>
          )}
          {isIOS && pushPermission === 'denied' && (
            <div className="WarningBanner" role="note" style={{ padding: '12px 14px', borderRadius: '8px', background: '#fff1f0', border: '1px solid #ffa39e', margin: '12px 0' }}>
              <div style={{ fontWeight: 600, marginBottom: '6px' }}>🔴 Powiadomienia są wyłączone w systemie iOS</div>
              <div style={{ lineHeight: 1.55 }}>Otwórz: <strong>Ustawienia → FootDesk → Powiadomienia</strong> i przełącz <strong>„Zezwalaj na powiadomienia”</strong> na WŁ. Następnie zamknij i otwórz ponownie aplikację FootDesk z ikony na ekranie głównym.</div>
            </div>
          )}
          {!pushSupport.supported ? (
            <div className="Muted">{pushSupport.reason || 'Ta przeglądarka nie obsługuje Web Push.'}</div>
          ) : (
            <>
              {pushError && <div className="ErrorBanner">{pushError}</div>}
              <div className="CoachProfileRow">
                <div className="CoachProfileRowKey">Powiadomienia Web Push</div>
                <div className="CoachProfileRowVal">
                  <label className="Switch" aria-label="Powiadomienia Web Push">
                    <input
                      className="SwitchInput"
                      type="checkbox"
                      checked={pushEnabled}
                      onChange={(e) => {
                        if (isPushLoading) return;
                        if (e.target.checked) enablePush();
                        else disablePush();
                      }}
                      disabled={isPushLoading}
                    />
                    <span className="SwitchTrack" aria-hidden="true">
                      <span className="SwitchThumb" aria-hidden="true" />
                    </span>
                  </label>
                </div>
              </div>
              <div className="CoachProfileRow">
                <div className="CoachProfileRowKey">Zgoda przeglądarki:</div>
                <div className="CoachProfileRowVal">
                  {pushPermission === 'granted' ? (
                    <span className="Badge Badge--ok" role="status">WŁĄCZONE</span>
                  ) : pushPermission === 'denied' ? (
                    <span className="Badge Badge--warn" role="alert">ZABLOKOWANE</span>
                  ) : (
                    <>
                      <PrimaryButton onClick={enablePush} disabled={isPushLoading} type="button">
                        Włącz powiadomienia
                      </PrimaryButton>
                    </>
                  )}
                </div>
              </div>
              {pushPermission === 'denied' && (
                <div className="WarningBanner" role="note" style={{ padding: '12px 14px', borderRadius: '8px', background: '#fff7e6', border: '1px solid #ffe1a8', margin: '12px 0' }}>
                  <div style={{ fontWeight: 600, marginBottom: '8px' }}>⚠️ Powiadomienia są zablokowane w tej przeglądarce. Włącz je ręcznie:</div>
                  {isAndroidUa ? (
                    <ol style={{ margin: 0, paddingLeft: '20px', lineHeight: 1.6 }}>
                      <li>Kliknij ikonę 🔒 obok adresu URL w pasku przeglądarki.</li>
                      <li>Wybierz „Powiadomienia” → przełącz na „Włączone” / „Dozwolone”.</li>
                      <li>W Ustawieniach systemu: Aplikacje → Chrome → Powiadomienia → włącz.</li>
                      <li>Zrestartuj przeglądarkę (zamknij i otwórz ponownie) i odśwież aplikację.</li>
                      <li><strong>Krok 5 (ANDROID):</strong> Jeśli kroki 1-4 nie pomogły → <em>Kliknij kłódkę 🔒 na pasku adresu → Uprawnienia → Powiadomienia → WŁĄCZ</em>, a następnie <strong>ZAMKNIJ i Otwórz ponownie kartę</strong> lub zrestartuj Chrome.</li>
                    </ol>
                  ) : isIOS ? (
                    <div className="Muted">Zobacz instrukcję iOS wyżej 👆 (Ustawienia → FootDesk → Powiadomienia → Zezwalaj na powiadomienia → WŁ, a następnie zamknij i otwórz ponownie aplikację z ikony na ekranie głównym).</div>
                  ) : (
                    <ol style={{ margin: 0, paddingLeft: '20px', lineHeight: 1.6 }}>
                      <li>Kliknij ikonę 🔒 / 🔵 obok adresu URL w pasku przeglądarki.</li>
                      <li>Wybierz „Powiadomienia” → przełącz na „Włączone” / „Dozwolone”.</li>
                      <li>Odśwież kartę aplikacji (F5 lub zamknij i otwórz ponownie).</li>
                    </ol>
                  )}
                  <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
                    <SecondaryButton onClick={() => void recheckPushPermission()} disabled={isPushLoading} type="button">
                      🔄 Sprawdź ponownie uprawnienia
                    </SecondaryButton>
                  </div>
                </div>
              )}
              {prefsError && <div className="ErrorBanner">{prefsError}</div>}
              {prefsLoading && <div className="Muted">Ładowanie ustawień powiadomień…</div>}
              {!prefsLoading && notificationPrefs && (
                <>
                  <div className="CoachProfileQuietRow" aria-label="Godziny ciszy">
                    <div className="CoachProfileRowKey">Godziny ciszy:</div>
                    <div className="CoachProfileQuietInputs" style={{ alignItems: 'center', gap: '8px' }}>
                      <select
                        className="TextInput"
                        style={{ maxWidth: '90px' }}
                        value={String(notificationPrefs.silent_from)}
                        onChange={(e) => {
                          const v = parseInt(e.target.value, 10);
                          setNotificationPrefs({ ...notificationPrefs, silent_from: Number.isFinite(v) ? v : 22 });
                        }}
                      >
                        {Array.from({ length: 24 }).map((_, h) => (
                          <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>
                        ))}
                      </select>
                      <span className="CoachProfileQuietDash" aria-hidden="true">–</span>
                      <select
                        className="TextInput"
                        style={{ maxWidth: '90px' }}
                        value={String(notificationPrefs.silent_until)}
                        onChange={(e) => {
                          const v = parseInt(e.target.value, 10);
                          setNotificationPrefs({ ...notificationPrefs, silent_until: Number.isFinite(v) ? v : 6 });
                        }}
                      >
                        {Array.from({ length: 24 }).map((_, h) => (
                          <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>
                        ))}
                      </select>
                      <label className="CoachProfileChk" style={{ marginLeft: '8px', whiteSpace: 'nowrap' }}>
                        <input
                          type="checkbox"
                          checked={notificationPrefs.silent_from === notificationPrefs.silent_until}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setNotificationPrefs({ ...notificationPrefs, silent_from: notificationPrefs.silent_until });
                            }
                          }}
                        />
                        <span>Wyłącz</span>
                      </label>
                    </div>
                  </div>
                  <div className="CoachProfileNotifGrid" aria-label="Rodzaje powiadomień">
                    {(['match_rsvp', 'match_tbd', 'attendance_report_ready', 'lounge', 'lounge_mention', 'direct_message', 'infeed_important', 'injury_reported', 'match_cancelled', 'match_critical_change'] as const).map((k) => {
                      if (k === 'injury_reported' && props.me.role !== 'COACH') {
                        return null;
                      }
                      const labels: Record<string, string> = {
                        match_rsvp: 'Powołania: Potwierdź udział',
                        match_tbd: 'Mecze: Do ustalenia (TBD)',
                        attendance_report_ready: 'Raporty: Frekwencja gotowa',
                        lounge: 'Czat drużyny: nowe wiadomości',
                        lounge_mention: 'Czat drużyny: wzmianki @Mnie',
                        direct_message: 'Prywatne wiadomości',
                        infeed_important: 'Feed: Ważne ogłoszenia',
                        injury_reported: 'Alerty: nieobecności i kontuzje moich zawodników',
                        match_cancelled: 'Mecze: Odwołane',
                        match_critical_change: 'Mecze: Krytyczne zmiany',
                      };
                      const disabled = false;
                      const title = '';
                      return (
                        <label key={k} className="CoachProfileChk" title={title} style={{ opacity: 1 }}>
                          <input
                            type="checkbox"
                            checked={!!notificationPrefs.channels?.[k]}
                            disabled={disabled}
                            onChange={(e) => {
                              setNotificationPrefs({
                                ...notificationPrefs,
                                channels: { ...(notificationPrefs.channels || {}), [k]: e.target.checked },
                              });
                            }}
                          />
                          <span>{labels[k] ?? k}</span>
                        </label>
                      );
                    })}
                  </div>
                  <div className="CoachProfileActions">
                    <PrimaryButton onClick={saveNotificationPrefs} disabled={savingPrefs || prefsLoading} type="button">
                      {savingPrefs ? 'Zapisywanie…' : 'Zapisz ustawienia'}
                    </PrimaryButton>
                  </div>
                </>
              )}
              <div className="CoachProfileActions">
                <SecondaryButton onClick={refreshPush} disabled={isPushLoading} type="button">
                  Odśwież
                </SecondaryButton>
                <SecondaryButton onClick={sendTestPush} disabled={isPushLoading || !pushEnabled} type="button">
                  Wyślij test
                </SecondaryButton>
                {IS_DEV && isAndroidUa && (
                  <SecondaryButton onClick={downloadMkcertRootCa} disabled={isPushLoading} type="button">
                    Pobierz certyfikat CA
                  </SecondaryButton>
                )}
              </div>
            </>
          )}
        </section>

        <section className="CoachProfileSection" aria-label="Inne">
          <div className="CoachProfileSectionTitle">INNE</div>
          <div className="CoachProfileRow">
            <div className="CoachProfileRowKey">RODO i audyt</div>
            <div className="CoachProfileRowVal">W przygotowaniu</div>
          </div>
          <div className="CoachProfileActions">
            <SecondaryButton onClick={() => setIsOpponentTeamsOpen(true)} type="button">
              Drużyny przeciwnika
            </SecondaryButton>
            <SecondaryButton onClick={props.onLogout} type="button">
              Wyloguj
            </SecondaryButton>
          </div>
        </section>

        {isOpponentTeamsOpen && (
          <div
            className="SheetOverlay"
            role="dialog"
            aria-modal="true"
            onMouseDown={(e) => {
              if (isOpponentTeamsLoading) return;
              if (e.target === e.currentTarget) setIsOpponentTeamsOpen(false);
            }}
          >
            <div className="Sheet">
              <div className="SheetHeaderRow SheetHeaderRow--sticky">
                <div className="SheetTitle">DRUŻYNY PRZECIWNIKA</div>
                <button
                  className="SheetCloseButton"
                  type="button"
                  aria-label="Zamknij"
                  onClick={() => setIsOpponentTeamsOpen(false)}
                  disabled={isOpponentTeamsLoading}
                >
                  ×
                </button>
              </div>
              {opponentTeamsError && <div className="ErrorBanner">{opponentTeamsError}</div>}
              {isOpponentTeamsLoading && <div className="Muted">Ładowanie…</div>}

              <input
                className="TextInput"
                value={opponentTeamsQuery}
                onChange={(e) => setOpponentTeamsQuery(e.target.value)}
                placeholder="Szukaj"
                disabled={isOpponentTeamsLoading}
              />

              <div className="SearchRow">
                <input
                  className="TextInput"
                  value={newOpponentTeamName}
                  onChange={(e) => setNewOpponentTeamName(e.target.value)}
                  placeholder="Nowa drużyna"
                  disabled={isOpponentTeamsLoading}
                />
                <PrimaryButton onClick={onAddOpponentTeam} disabled={isOpponentTeamsLoading || newOpponentTeamName.trim() === ''} type="button">
                  Dodaj
                </PrimaryButton>
              </div>

              <div className="List">
                {filteredOpponentTeams.length === 0 ? (
                  <div className="Muted">Brak drużyn.</div>
                ) : (
                  filteredOpponentTeams.map((t) => (
                    <div key={t.id} className="OpponentTeamRow">
                      <div className="OpponentTeamMain">
                        <div className="OpponentTeamTitle">{t.name}</div>
                        <div className="OpponentTeamMeta">{t.logo_url ? 'Logo ustawione' : 'Brak logo'}</div>
                      </div>
                      <div className="OpponentTeamActions">
                        <button
                          className="OpponentTeamIconBtn"
                          type="button"
                          onClick={() => {
                            setEditOpponentTeam(t);
                            setEditOpponentName(t.name);
                            setEditOpponentLogoUrl(t.logo_url ?? '');
                          }}
                          disabled={isOpponentTeamsLoading}
                          aria-label="Edytuj drużynę"
                        >
                          ✎
                        </button>
                        <button
                          className="OpponentTeamIconBtn is-danger"
                          type="button"
                          onClick={() => setDeleteOpponentTeam(t)}
                          disabled={isOpponentTeamsLoading}
                          aria-label="Usuń drużynę"
                        >
                          🗑
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {editOpponentTeam && (
          <div
            className="SheetOverlay"
            role="dialog"
            aria-modal="true"
            onMouseDown={(e) => {
              if (isOpponentTeamsLoading) return;
              if (e.target === e.currentTarget) setEditOpponentTeam(null);
            }}
          >
            <div className="Sheet">
              <div className="SheetHeaderRow SheetHeaderRow--sticky">
                <div className="SheetTitle">EDYTUJ DRUŻYNĘ</div>
                <button
                  className="SheetCloseButton"
                  type="button"
                  aria-label="Zamknij"
                  onClick={() => setEditOpponentTeam(null)}
                  disabled={isOpponentTeamsLoading || isEditOpponentLogoUploading}
                >
                  ×
                </button>
              </div>
              {opponentTeamsError && <div className="ErrorBanner">{opponentTeamsError}</div>}
              <input
                className="TextInput"
                value={editOpponentName}
                onChange={(e) => setEditOpponentName(e.target.value)}
                placeholder="Nazwa"
                disabled={isOpponentTeamsLoading}
              />
              <div className="ChildPhotoRow">
                <div className="ChildPhotoPreview" aria-hidden="true">
                  {editOpponentLogoUrl.trim() ? (
                    <img className="ChildPhotoPreviewImg" src={editOpponentLogoUrl.trim()} alt="" />
                  ) : (
                    <div className="ChildPhotoPreviewFallback">•</div>
                  )}
                </div>
                <div className="ChildPhotoActions">
                  <input
                    ref={editOpponentLogoInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      e.target.value = '';
                      if (!f) return;
                      openEditOpponentLogoCrop(f);
                    }}
                    disabled={isOpponentTeamsLoading || isEditOpponentLogoUploading}
                    className="HiddenInput"
                  />
                  <SecondaryButton
                    type="button"
                    onClick={() => editOpponentLogoInputRef.current?.click()}
                    disabled={isOpponentTeamsLoading || isEditOpponentLogoUploading}
                  >
                    Ustaw logo
                  </SecondaryButton>
                  {editOpponentLogoUrl.trim() && (
                    <SecondaryButton
                      type="button"
                      onClick={clearEditOpponentLogo}
                      disabled={isOpponentTeamsLoading || isEditOpponentLogoUploading}
                    >
                      Usuń logo
                    </SecondaryButton>
                  )}
                </div>
              </div>
              <PrimaryButton onClick={onSaveOpponentTeam} disabled={isOpponentTeamsLoading || editOpponentName.trim() === ''} type="button">
                Zapisz
              </PrimaryButton>
              <SecondaryButton onClick={() => setEditOpponentTeam(null)} disabled={isOpponentTeamsLoading} type="button">
                Anuluj
              </SecondaryButton>
            </div>
          </div>
        )}

        {deleteOpponentTeam && (
          <div
            className="SheetOverlay"
            role="dialog"
            aria-modal="true"
            onMouseDown={(e) => {
              if (isOpponentTeamsLoading) return;
              if (e.target === e.currentTarget) setDeleteOpponentTeam(null);
            }}
          >
            <div className="Sheet">
              <div className="SheetHeaderRow SheetHeaderRow--sticky">
                <div className="SheetTitle">USUNĄĆ DRUŻYNĘ?</div>
                <button
                  className="SheetCloseButton"
                  type="button"
                  aria-label="Zamknij"
                  onClick={() => setDeleteOpponentTeam(null)}
                  disabled={isOpponentTeamsLoading}
                >
                  ×
                </button>
              </div>
              <div className="Muted">{deleteOpponentTeam.name}</div>
              <PrimaryButton onClick={onDeleteOpponentTeamConfirm} disabled={isOpponentTeamsLoading} type="button">
                Usuń
              </PrimaryButton>
              <SecondaryButton onClick={() => setDeleteOpponentTeam(null)} disabled={isOpponentTeamsLoading} type="button">
                Anuluj
              </SecondaryButton>
            </div>
          </div>
        )}

        {passwordSheetOpen && (
          <div
            className="SheetOverlay"
            role="dialog"
            aria-modal="true"
            onMouseDown={(e) => {
              if (isPasswordLoading) return;
              if (e.target === e.currentTarget) setPasswordSheetOpen(false);
            }}
          >
            <div className="Sheet">
              <div className="SheetHeaderRow SheetHeaderRow--sticky">
                <div className="SheetTitle">ZMIEŃ HASŁO</div>
                <button
                  className="SheetCloseButton"
                  type="button"
                  aria-label="Zamknij"
                  onClick={() => setPasswordSheetOpen(false)}
                  disabled={isPasswordLoading}
                >
                  ×
                </button>
              </div>
              {passwordError && <div className="ErrorBanner">{passwordError}</div>}
              {passwordOk && <div className="InfoBanner">Hasło zmienione.</div>}
              <div className="CoachProfileForm">
                <input
                  className="TextInput"
                  value={passwordCurrent}
                  onChange={(e) => setPasswordCurrent(e.target.value)}
                  placeholder="Obecne hasło"
                  type="password"
                  disabled={isPasswordLoading}
                />
                <input
                  className="TextInput"
                  value={passwordNext}
                  onChange={(e) => setPasswordNext(e.target.value)}
                  placeholder="Nowe hasło (min. 8 znaków)"
                  type="password"
                  disabled={isPasswordLoading}
                />
                <input
                  className="TextInput"
                  value={passwordNext2}
                  onChange={(e) => setPasswordNext2(e.target.value)}
                  placeholder="Powtórz nowe hasło"
                  type="password"
                  disabled={isPasswordLoading}
                />
              </div>
              <div className="CoachProfileActions">
                <PrimaryButton onClick={changePassword} disabled={isPasswordLoading || !canChangePassword} type="button">
                  Zmień hasło
                </PrimaryButton>
                <SecondaryButton onClick={() => setPasswordSheetOpen(false)} disabled={isPasswordLoading} type="button">
                  Anuluj
                </SecondaryButton>
              </div>
            </div>
          </div>
        )}

        {coachPickChildSheet && (
          <div
            className="SheetOverlay"
            role="dialog"
            aria-modal="true"
            onMouseDown={(e) => {
              if (isProfileLoading) return;
              if (e.target === e.currentTarget) {
                setCoachPickChildSheet(null);
                setCoachPickChildQuery('');
              }
            }}
          >
            <div className="Sheet">
              <div className="SheetHeaderRow SheetHeaderRow--sticky">
                <div className="SheetTitle">
                  {coachPickChildSheet.type === 'MATCH' ? 'DODAJ MECZ — WYBIERZ PODOPIECZNEGO' : 'DODAJ TRENING — WYBIERZ PODOPIECZNEGO'}
                </div>
                <button
                  className="SheetCloseButton"
                  type="button"
                  aria-label="Zamknij"
                  onClick={() => {
                    setCoachPickChildSheet(null);
                    setCoachPickChildQuery('');
                  }}
                  disabled={isProfileLoading}
                >
                  ×
                </button>
              </div>
              <input
                className="TextInput"
                value={coachPickChildQuery}
                onChange={(e) => setCoachPickChildQuery(e.target.value)}
                placeholder="Szukaj podopiecznego..."
                disabled={isProfileLoading}
              />
              <div className="List">
                {props.coachChildren
                  .slice()
                  .sort((a, b) => {
                    const al = String(a.last_initial ?? '').trim().toLowerCase();
                    const bl = String(b.last_initial ?? '').trim().toLowerCase();
                    const af = String(a.first_name ?? '').trim().toLowerCase();
                    const bf = String(b.first_name ?? '').trim().toLowerCase();
                    const byLast = al.localeCompare(bl, 'pl', { sensitivity: 'base' });
                    if (byLast !== 0) return byLast;
                    return af.localeCompare(bf, 'pl', { sensitivity: 'base' });
                  })
                  .filter((c) => {
                    const q = coachPickChildQuery.trim().toLowerCase();
                    if (!q) return true;
                    const full = `${c.first_name} ${c.last_initial}`.trim().toLowerCase();
                    const group = (c.group_name ?? '').trim().toLowerCase();
                    return full.includes(q) || group.includes(q);
                  })
                  .map((c) => (
                    <button
                      key={c.id}
                      className="ListItem"
                      type="button"
                      onClick={() => {
                        void (async () => {
                          if (isProfileLoading) return;
                          let ig = (c.group_id ?? '').trim();
                          if (!ig) {
                            try {
                              const next = await apiCoachMyChildren(props.token);
                              const updated = next.find((x) => x.id === c.id) ?? null;
                              ig = (updated?.group_id ?? '').trim();
                            } catch {
                              ig = '';
                            }
                          }
                          const it = coachPickChildSheet.type;
                          setCoachPickChildSheet(null);
                          setCoachPickChildQuery('');
                          navigate('kalendarz', { ic: c.id, it, ig: (ig || 'individual').trim() });
                        })();
                      }}
                      disabled={isProfileLoading}
                    >
                      <div className="ListItemTop">
                        <div className="ListItemTitle">{`${c.first_name} ${c.last_initial}`.toUpperCase()}</div>
                      </div>
                      <div className="Muted">
                        {(c.group_name ?? '').trim() ? `${c.group_name}${c.group_year ? ` (${c.group_year})` : ''}` : '—'}
                      </div>
                    </button>
                  ))}
              </div>
            </div>
          </div>
        )}

        <PhotoCropperSheet req={editOpponentLogoCropReq} onClose={() => setEditOpponentLogoCropReq(null)} onDone={() => setEditOpponentLogoCropReq(null)} />
      </div>
    );
  }

  if (isAdminLike) {
    return (
      <>
        {isDesktop ? (
          <div className="AdminDesktopRoot">
            <aside className="AdminSidebar" aria-label="Panel administracyjny desktop">
              <div className="AdminSidebarBrand">
                <img src={clubLogo} alt="" aria-hidden="true" />
                <div>
                  <div className="AdminSidebarBrandTitle">AP BŁONIANKA</div>
                  <div className="AdminSidebarBrandSub">Panel administracyjny</div>
                </div>
              </div>
              <div className="AdminSidebarGreeting">
                <div className="AdminSidebarGreetingName">Cześć, {profileDisplayName || coachName || 'Admin'}</div>
                <div className="AdminSidebarGreetingSub">
                  <span className={['CoachProfileStatusDot', props.me.status === 'ACTIVE' ? 'is-active' : ''].filter(Boolean).join(' ')} />
                  {props.me.role === 'ADMIN' ? 'Administrator' : 'Koordynator'} • {props.me.status}
                </div>
              </div>
              <div className="AdminSidebarSection" aria-label="Zarządzanie">
                <div className="AdminSidebarSectionTitle">ZARZĄDZANIE</div>
                <button type="button" className={['AdminSidebarNavBtn', desktopAdminSection === 'users' ? 'is-active' : ''].filter(Boolean).join(' ')} onClick={() => setDesktopAdminSection('users')}>
                  <span aria-hidden="true">👥</span><span>Użytkownicy</span>
                </button>
                <button type="button" className={['AdminSidebarNavBtn', desktopAdminSection === 'groups' ? 'is-active' : ''].filter(Boolean).join(' ')} onClick={() => { setDesktopAdminSection('groups'); loadDesktopGroups(); }}>
                  <span aria-hidden="true">⚙️</span><span>Grupy</span>
                </button>
                <button type="button" className={['AdminSidebarNavBtn', desktopAdminSection === 'children' ? 'is-active' : ''].filter(Boolean).join(' ')} onClick={() => { setDesktopAdminSection('children'); loadDesktopChildren(); }}>
                  <span aria-hidden="true">👨‍👩‍👧</span><span>Dzieci</span>
                </button>
              </div>
              <div className="AdminSidebarSection" aria-label="Narzędzia">
                <div className="AdminSidebarSectionTitle">NARZĘDZIA</div>
                <button type="button" className="AdminSidebarNavBtn" onClick={() => setLnpOpen(true)}>
                  <span aria-hidden="true">🟨</span><span>Import LNP</span>
                </button>
                <button type="button" className="AdminSidebarNavBtn" onClick={() => { setAttendanceReportOpen(true); loadGroups(); }}>
                  <span aria-hidden="true">📄</span><span>Raporty PDF</span>
                </button>
                <button type="button" className="AdminSidebarNavBtn" onClick={() => { if (window.confirm('Wyczyścić pamięć podręczną interfejsu?')) window.location.reload(); }}>
                  <span aria-hidden="true">🗑️</span><span>Wyczyść cache</span>
                </button>
              </div>
              <div className="AdminSidebarSection" aria-label="Ustawienia">
                <div className="AdminSidebarSectionTitle">USTAWIENIA</div>
                <button type="button" className="AdminSidebarNavBtn" onClick={() => setCoachEditOpen(true)}>
                  <span aria-hidden="true">👤</span><span>Profil</span>
                </button>
                <button type="button" className="AdminSidebarNavBtn" onClick={() => setPasswordSheetOpen(true)}>
                  <span aria-hidden="true">🔔</span><span>Powiadomienia</span>
                </button>
              </div>
              <div className="AdminSidebarFooter">
                <button type="button" className="AdminSidebarLogoutBtn" onClick={() => { if (window.confirm('Wylogować się?')) props.onLogout(); }}>
                  <span aria-hidden="true">🚪</span><span>Wyloguj</span>
                </button>
                <div style={{ marginTop: 12, padding: '10px 12px', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, background: 'rgba(255,255,255,0.03)', color: 'rgba(255,255,255,0.75)', fontSize: 12, lineHeight: 1.5 }} aria-label="Wersja aplikacji">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontWeight: 700 }}>v{APP_FE_VERSION} · {APP_FE_RELEASE}</span>
                    <span style={{ padding: '1px 6px', borderRadius: 4, background: '#22c55e33', color: '#22c55e', fontSize: 11 }}>Faza 6</span>
                  </div>
                  <div style={{ marginTop: 4, fontFamily: 'ui-monospace, monospace' }}>
                    FE build: <span style={{ fontWeight: 700 }}>{APP_FE_BUILD_ISO ? APP_FE_BUILD_ISO.slice(0, 8) : 'local'}</span>
                  </div>
                  <div style={{ marginTop: 4 }}>
                    <a href="#profil" onClick={(e) => { e.preventDefault(); try { window.location.hash = '#profil'; } catch {} }} style={{ color: '#93c5fd', textDecoration: 'underline' }}>
                      Szczegóły wersji w Profilu ›
                    </a>
                  </div>
                </div>
              </div>
            </aside>
            <main className="AdminDesktopMain">
              <section className="AdminDesktopHero">
                <div>
                  <h1 className="AdminDesktopHeroTitle">Panel Administracyjny</h1>
                  <p className="AdminDesktopHeroSub">Zarządzaj użytkownikami, grupami, dziećmi i importuj wyniki rozgrywek z Łączy Nas Piłka.</p>
                </div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <PrimaryButton type="button" onClick={() => setLnpOpen(true)}>🟨 Import LNP (CSV/Paste)</PrimaryButton>
                  <SecondaryButton type="button" onClick={() => setAdminCreateUserOpen(true)}>＋ Utwórz konto</SecondaryButton>
                </div>
              </section>
              <div className="AdminGrid">
                <div className="AdminCard AdminCard--span8" aria-label="Użytkownicy">
                  <div className="AdminCardHeader">
                    <div className="AdminCardTitle">👥 Użytkownicy</div>
                    <div className="AdminCardFilters" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <select className="TextInput" style={{ width: 150, padding: '6px 10px' }} value={dtUsersStatus} onChange={(e) => setDtUsersStatus(e.target.value as 'ALL' | 'ACTIVE' | 'PENDING' | 'REJECTED')}>
                        <option value="ALL">Wszystkie statusy</option>
                        <option value="ACTIVE">Aktywne</option>
                        <option value="PENDING">Oczekujące</option>
                        <option value="REJECTED">Odrzucone</option>
                      </select>
                      <input className="TextInput" style={{ minWidth: 220 }} placeholder="🔍 Szukaj (e-mail, imię, nazwisko)…" value={dtUsersSearch} onChange={(e) => setDtUsersSearch(e.target.value)} />
                    </div>
                  </div>
                  {dtUsersLoading && <div className="Muted" style={{ padding: 20 }}>Ładowanie użytkowników…</div>}
                  {!dtUsersLoading && (
                    <>
                      <div className="AdminTableWrap" role="region" aria-label="Tabela użytkowników">
                        <table className="AdminTable">
                          <thead>
                            <tr>
                              <th style={{ width: 180 }}>Użytkownik</th>
                              <th style={{ width: 100 }}>Rola</th>
                              <th style={{ width: 110 }}>Status</th>
                              <th>E-mail</th>
                              <th style={{ width: 130 }}>Utworzono</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(Array.isArray(dtUsersData) ? dtUsersData : (dtUsersData?.data ?? [])).map((u: any) => (
                              <tr key={u.id}>
                                <td><div style={{ fontWeight: 600 }}>{u.display_name || u.nickname || '—'}</div></td>
                                <td><span className={roleBadgeClass(u.role)}>{u.role}</span></td>
                                <td><span className={statusBadgeClass(u.status)}>{u.status}</span></td>
                                <td style={{ fontFamily: 'ui-monospace, monospace', fontSize: 13 }}>{u.email}</td>
                                <td className="Muted">{u.created_at ? new Date(u.created_at).toLocaleDateString('pl-PL') : '—'}</td>
                              </tr>
                            ))}
                            {((Array.isArray(dtUsersData) ? dtUsersData : (dtUsersData?.data ?? [])).length === 0) && (
                              <tr><td colSpan={5} className="Muted" style={{ textAlign: 'center', padding: 24 }}>Brak użytkowników w tym zakresie.</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                      <div className="AdminPagination" aria-label="Paginacja użytkowników">
                        <div className="AdminPaginationLeft">Razem: <strong>{dtUsersData?.total ?? (Array.isArray(dtUsersData) ? dtUsersData.length : 0)}</strong></div>
                        <div className="AdminPaginationRight">
                          <SecondaryButton type="button" disabled={dtUsersPage <= 1 || dtUsersLoading} onClick={() => setDtUsersPage((p: number) => Math.max(1, p - 1))}>‹ Poprzednia</SecondaryButton>
                          <span className="AdminPaginationPage">Strona {dtUsersPage} / {dtUsersData?.total_pages ?? 1}</span>
                          <SecondaryButton type="button" disabled={dtUsersLoading || dtUsersPage >= (dtUsersData?.total_pages ?? 1)} onClick={() => setDtUsersPage((p: number) => p + 1)}>Następna ›</SecondaryButton>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                <div className="AdminCard AdminCard--span4" aria-label="Grupy">
                  <div className="AdminCardHeader">
                    <div className="AdminCardTitle">⚙️ Grupy</div>
                    <input className="TextInput" style={{ width: 180 }} placeholder="🔍 Szukaj grupy…" value={dtGroupsSearch} onChange={(e) => setDtGroupsSearch(e.target.value)} />
                  </div>
                  {dtGroupsLoading && <div className="Muted" style={{ padding: 20 }}>Ładowanie grup…</div>}
                  {!dtGroupsLoading && (
                    <>
                      <div className="AdminTableWrap" role="region" aria-label="Tabela grup">
                        <table className="AdminTable">
                          <thead>
                            <tr>
                              <th>Nazwa</th>
                              <th style={{ width: 90 }}>Rocznik</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(Array.isArray(dtGroupsData) ? dtGroupsData : (dtGroupsData?.data ?? [])).map((g: any) => (
                              <tr key={g.id}>
                                <td style={{ fontWeight: 600 }}>{g.name}</td>
                                <td className="Muted">{g.year || '—'}</td>
                              </tr>
                            ))}
                            {((Array.isArray(dtGroupsData) ? dtGroupsData : (dtGroupsData?.data ?? [])).length === 0) && (
                              <tr><td colSpan={2} className="Muted" style={{ textAlign: 'center', padding: 20 }}>Brak grup.</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                      <div className="AdminPagination" aria-label="Paginacja grup">
                        <div className="AdminPaginationLeft">Razem: <strong>{dtGroupsData?.total ?? (Array.isArray(dtGroupsData) ? dtGroupsData.length : 0)}</strong></div>
                        <div className="AdminPaginationRight">
                          <SecondaryButton type="button" disabled={dtGroupsPage <= 1 || dtGroupsLoading} onClick={() => setDtGroupsPage((p: number) => Math.max(1, p - 1))}>‹</SecondaryButton>
                          <span className="AdminPaginationPage">{dtGroupsPage} / {dtGroupsData?.total_pages ?? 1}</span>
                          <SecondaryButton type="button" disabled={dtGroupsLoading || dtGroupsPage >= (dtGroupsData?.total_pages ?? 1)} onClick={() => setDtGroupsPage((p: number) => p + 1)}>›</SecondaryButton>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                <div className="AdminCard AdminCard--span12" aria-label="Dzieci">
                  <div className="AdminCardHeader">
                    <div className="AdminCardTitle">👨‍👩‍👧 Dzieci</div>
                    <div className="AdminCardFilters" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <select className="TextInput" style={{ width: 160, padding: '6px 10px' }} value={dtChildrenGroup} onChange={(e) => setDtChildrenGroup(e.target.value)}>
                        <option value="ALL">Wszystkie grupy</option>
                        {(Array.isArray(dtGroupsData) ? dtGroupsData : (dtGroupsData?.data ?? [])).map((g: any) => (
                          <option key={g.id} value={g.id}>{g.name}</option>
                        ))}
                      </select>
                      <input className="TextInput" style={{ minWidth: 240 }} placeholder="🔍 Szukaj (imię, inicjał, nr koszulki)…" value={dtChildrenSearch} onChange={(e) => setDtChildrenSearch(e.target.value)} />
                    </div>
                  </div>
                  {dtChildrenLoading && <div className="Muted" style={{ padding: 20 }}>Ładowanie dzieci…</div>}
                  {!dtChildrenLoading && (
                    <>
                      <div className="AdminTableWrap" role="region" aria-label="Tabela dzieci">
                        <table className="AdminTable">
                          <thead>
                            <tr>
                              <th style={{ width: 180 }}>Dziecko</th>
                              <th style={{ width: 80 }}>Nr</th>
                              <th style={{ width: 110 }}>Grupa</th>
                              <th style={{ width: 100 }}>Rocznik</th>
                              <th style={{ width: 110 }}>Status</th>
                              <th>Rodzice (e-mail)</th>
                              <th style={{ width: 130 }}>Utworzono</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(Array.isArray(dtChildrenData) ? dtChildrenData : (dtChildrenData?.data ?? [])).map((c: any) => (
                              <tr key={c.id}>
                                <td><div style={{ fontWeight: 600 }}>{c.first_name} {c.last_initial}</div></td>
                                <td className="Muted">{Number.isFinite(Number(c.shirt_number)) ? c.shirt_number : '—'}</td>
                                <td>{c.group_name || c.group_id || '—'}</td>
                                <td className="Muted">{c.birth_year || '—'}</td>
                                <td>
                                  {(c.unavailability_kind || '').trim() ? (
                                    <span className="Badge Badge--warn">{c.unavailability_kind === 'KONTUZJA' ? 'Kontuzja' : 'NB'}</span>
                                  ) : (
                                    <span className="Badge Badge--ok">AKTYWNY</span>
                                  )}
                                </td>
                                <td style={{ fontFamily: 'ui-monospace, monospace', fontSize: 13 }}>{c.parent_emails || c.parent_email || '—'}</td>
                                <td className="Muted">{c.created_at ? new Date(c.created_at).toLocaleDateString('pl-PL') : '—'}</td>
                              </tr>
                            ))}
                            {((Array.isArray(dtChildrenData) ? dtChildrenData : (dtChildrenData?.data ?? [])).length === 0) && (
                              <tr><td colSpan={7} className="Muted" style={{ textAlign: 'center', padding: 24 }}>Brak dzieci w tym zakresie.</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                      <div className="AdminPagination" aria-label="Paginacja dzieci">
                        <div className="AdminPaginationLeft">Razem: <strong>{dtChildrenData?.total ?? (Array.isArray(dtChildrenData) ? dtChildrenData.length : 0)}</strong></div>
                        <div className="AdminPaginationRight">
                          <SecondaryButton type="button" disabled={dtChildrenPage <= 1 || dtChildrenLoading} onClick={() => setDtChildrenPage((p: number) => Math.max(1, p - 1))}>‹ Poprzednia</SecondaryButton>
                          <span className="AdminPaginationPage">Strona {dtChildrenPage} / {dtChildrenData?.total_pages ?? 1}</span>
                          <SecondaryButton type="button" disabled={dtChildrenLoading || dtChildrenPage >= (dtChildrenData?.total_pages ?? 1)} onClick={() => setDtChildrenPage((p: number) => p + 1)}>Następna ›</SecondaryButton>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </main>
          </div>
        ) : (
          <div className="AdminMobileOnly">
            <div className="Page AdminProfilePage">
        <section className="CoachProfileHero" aria-label="Panel administratora">
          <div className="CoachProfileHeroTop">
            <div className="CoachProfileAvatar" aria-hidden="true">
              {coachInitials}
            </div>
            <div className="CoachProfileHeroMain">
              <div className="CoachProfileName">{coachName}</div>
              <div className="CoachProfileRole">{adminSubtitle}</div>
              <div className="CoachProfileStatusRow">
                <span className="AdminRolePill">{props.me.role === 'ADMIN' ? 'ADMIN' : 'KOORDYNATOR'}</span>
                <span className={['CoachProfileStatusDot', props.me.status === 'ACTIVE' ? 'is-active' : ''].filter(Boolean).join(' ')} />
                <span className="CoachProfileStatusLabel">{props.me.status}</span>
              </div>
            </div>
            <button
              className="CoachProfileGear"
              type="button"
              onClick={() => {
                setAdminPanelTab('settings');
                setCoachEditOpen((v) => !v);
              }}
              aria-label="Ustawienia profilu"
            >
              ⚙
            </button>
          </div>
          <div className="CoachProfileHeroBottom">
            <div className="CoachProfileHeroHint">Zarządzanie systemem, użytkownikami i grupami</div>
            <button
              className="CoachProfileEditBtn"
              type="button"
              onClick={() => {
                setAdminPanelTab('settings');
                setCoachEditOpen((v) => !v);
              }}
              disabled={isProfileLoading}
            >
              ✎ EDYCJA
            </button>
          </div>
        </section>

        {coachEditOpen && (
          <section className="CoachProfileSection" aria-label="Edycja profilu">
            {profileError && <div className="ErrorBanner">{profileError}</div>}
            <div className="CoachProfileSectionTitle">EDYCJA</div>
            {profile?.email && (
              <div className="CoachProfileRow">
                <div className="CoachProfileRowKey">E-mail / Login</div>
                <div className="CoachProfileRowVal">{profile.email}</div>
              </div>
            )}
            <div className="CoachProfileForm">
              <input
                className="TextInput"
                value={profileDisplayName}
                onChange={(e) => setProfileDisplayName(e.target.value)}
                placeholder="Imię i nazwisko"
                disabled={isProfileLoading}
              />
              <input
                className="TextInput"
                value={profileNickname}
                onChange={(e) => setProfileNickname(e.target.value)}
                placeholder="Nick / opis"
                disabled={isProfileLoading}
              />
              <div className="CoachProfileActions">
                <PrimaryButton onClick={saveProfile} disabled={isProfileLoading} type="button">
                  Zapisz
                </PrimaryButton>
                <SecondaryButton onClick={() => setCoachEditOpen(false)} disabled={isProfileLoading} type="button">
                  Zamknij
                </SecondaryButton>
              </div>
            </div>
          </section>
        )}

        {adminPanelTab === 'settings' && (
          <section className="CoachProfileSection" aria-label="Moje konto">
            <div className="CoachProfileSectionTitle">MOJE KONTO</div>
            <div className="CoachProfileManageRows">
              <div className="CoachProfileManageRow">
                <div className="CoachProfileManageLeft">
                  <div className="CoachProfileManageLabel">E-mail / Login:</div>
                  <div className="CoachProfileManageValue">{profile?.email ?? '—'}</div>
                </div>
                <div className="CoachProfileManageRight">
                  <span className="CoachProfileInlineIcon" aria-hidden="true">
                    🔒
                  </span>
                  <button
                    className="CoachProfileInlineBtn"
                    type="button"
                    onClick={() => setPasswordSheetOpen(true)}
                    disabled={isPasswordLoading || props.me.status !== 'ACTIVE'}
                  >
                    🔑 Zmień hasło
                  </button>
                </div>
              </div>
              <div className="CoachProfileManageRow">
                <div className="CoachProfileManageLeft">
                  <div className="CoachProfileManageLabel">Nick / Opis:</div>
                  <div className="CoachProfileManageValue">{coachNicknameLabel}</div>
                </div>
                <div className="CoachProfileManageRight">
                  <button
                    className="CoachProfileInlineIconBtn"
                    type="button"
                    onClick={() => setCoachEditOpen(true)}
                    aria-label="Edytuj nick / opis"
                    disabled={isProfileLoading}
                  >
                    ✎
                  </button>
                </div>
              </div>
            </div>
          </section>
        )}

        {adminPanelTab === 'manage' && (
          <section className="CoachProfileSection" aria-label="Panel zarządzania">
            <div className="CoachProfileSectionTitle">PANEL ZARZĄDZANIA</div>
            {errorText && <div className="ErrorBanner">{errorText}</div>}
            <div className="AdminActionGrid" aria-label="Akcje administratora">
              <button
                className="AdminActionTile"
                type="button"
                onClick={() => {
                  setAdminAccountsTab('pending');
                  setAdminAccountsOpen(true);
                  loadPending();
                }}
                disabled={isLoading}
              >
                <span className="AdminActionIcon" aria-hidden="true">
                  👤
                </span>
                <span className="AdminActionText">
                  <span className="AdminActionTitle">Zarządzaj kontami</span>
                  <span className="AdminActionSub">Akceptacje, role, statusy</span>
                </span>
                <span className="AdminActionChevron" aria-hidden="true">
                  ›
                </span>
              </button>

            <button
              className="AdminActionTile"
              type="button"
              onClick={() => setAdminCreateUserOpen(true)}
              disabled={isLoading}
            >
              <span className="AdminActionIcon" aria-hidden="true">
                ＋
              </span>
              <span className="AdminActionText">
                <span className="AdminActionTitle">Utwórz nowe konto</span>
                <span className="AdminActionSub">{isFullAdmin ? 'Rodzic / Trener / Admin / Koordynator' : 'Rodzic / Trener'}</span>
              </span>
              <span className="AdminActionChevron" aria-hidden="true">
                ›
              </span>
            </button>

              <button
                className="AdminActionTile"
                type="button"
                onClick={() => {
                  setAdminChildrenTab('list');
                  setAdminChildGroupFilter('ALL');
                  setAdminChildYearFilter('ALL');
                  setAdminChildBirthYearFilter('ALL');
                  setAdminChildrenOpen(true);
                  loadGroups();
                  loadChildren();
                }}
                disabled={isLoading}
              >
              <span className="AdminActionIcon" aria-hidden="true">
                👥
              </span>
              <span className="AdminActionText">
                <span className="AdminActionTitle">Zarządzaj dziećmi</span>
                <span className="AdminActionSub">Edycja, rodzice, trenerzy</span>
              </span>
              <span className="AdminActionChevron" aria-hidden="true">
                ›
              </span>
              </button>

            <button
              className="AdminActionTile"
              type="button"
              onClick={() => {
                setAdminGroupsOpen(true);
                loadGroups();
              }}
              disabled={isLoading}
            >
              <span className="AdminActionIcon" aria-hidden="true">
                ⚙
              </span>
              <span className="AdminActionText">
                <span className="AdminActionTitle">Konfiguracja grup</span>
                <span className="AdminActionSub">Grupy, roczniki, trenerzy</span>
              </span>
              <span className="AdminActionChevron" aria-hidden="true">
                ›
              </span>
            </button>

            <button
              className="AdminActionTile"
              type="button"
              onClick={() => {
                setAttendanceReportOpen(true);
                loadGroups();
              }}
              disabled={isLoading}
            >
              <span className="AdminActionIcon" aria-hidden="true">
                📄
              </span>
              <span className="AdminActionText">
                <span className="AdminActionTitle">Raport frekwencji (PDF)</span>
                <span className="AdminActionSub">Miesięczny / kwartalny / półroczny</span>
              </span>
              <span className="AdminActionChevron" aria-hidden="true">
                ⬇
              </span>
            </button>
            </div>
          </section>
        )}

        {adminPanelTab === 'settings' && isFullAdmin && (
          <section className="CoachProfileSection" aria-label="Ustawienia globalne">
            <div className="CoachProfileSectionTitle">USTAWIENIA GLOBALNE</div>
            <button
              className="AdminRowBtn"
              type="button"
              onClick={() => {
                setAdminQuickReactionsOpen(true);
              }}
              disabled={isQuickTtlLoading}
            >
              <span className="AdminRowLeft">
                <span className="AdminRowTitle">Szybkie reakcje (MVP)</span>
                <span className="AdminRowSub">{`Aktualnie: ${quickTtlDays} dni`}</span>
              </span>
              <span className="AdminRowRight" aria-hidden="true">
                ✎
              </span>
            </button>

            <div className="AdminActionGrid" aria-label="Ustawienia systemu">
              <button className="AdminActionTile" type="button" disabled>
                <span className="AdminActionIcon" aria-hidden="true">
                  📄
                </span>
                <span className="AdminActionText">
                  <span className="AdminActionTitle">Polityka danych i audyt</span>
                  <span className="AdminActionSub">W przygotowaniu</span>
                </span>
                <span className="AdminActionChevron" aria-hidden="true">
                  ›
                </span>
              </button>
              <button className="AdminActionTile" type="button" disabled>
                <span className="AdminActionIcon" aria-hidden="true">
                  🔔
                </span>
                <span className="AdminActionText">
                  <span className="AdminActionTitle">Logika powiadomień</span>
                  <span className="AdminActionSub">W przygotowaniu</span>
                </span>
                <span className="AdminActionChevron" aria-hidden="true">
                  ›
                </span>
              </button>
            </div>
          </section>
        )}

        {adminPanelTab === 'tools' && (
          <section className="CoachProfileSection" aria-label="Narzędzia systemowe">
            <div className="CoachProfileSectionTitle">NARZĘDZIA SYSTEMOWE</div>
            {isFullAdmin && (
              <>
                {dbError && <div className="ErrorBanner">{dbError}</div>}
                {dbInfo && <div className="Muted">{dbInfo}</div>}
                <div className="AdminToolsRow">
                  <button className="AdminToolBtn" type="button" onClick={adminDownloadDbBackup} disabled={isDbLoading}>
                    ⬇ Utwórz backup
                  </button>
                  <button className="AdminToolBtn is-danger" type="button" onClick={() => setAdminDbToolsOpen(true)} disabled={isDbLoading}>
                    🗑 Wyczyść statystyki i kalendarz
                  </button>
                </div>
              </>
            )}
            <button className="AdminRowBtn" type="button" onClick={() => setIsUsageOpen(true)}>
              <span className="AdminRowLeft">
                <span className="AdminRowTitle">Statystyki portalu</span>
                <span className="AdminRowSub">Ostatnia aktywność i logowania</span>
              </span>
              <span className="AdminRowRight" aria-hidden="true">
                ›
              </span>
            </button>
            {isFullAdmin && (
              <button className="AdminRowBtn" type="button" onClick={() => setIsOpponentTeamsOpen(true)}>
                <span className="AdminRowLeft">
                  <span className="AdminRowTitle">Drużyny przeciwnika</span>
                  <span className="AdminRowSub">Podpowiedzi w formularzu meczu</span>
                </span>
                <span className="AdminRowRight" aria-hidden="true">
                  ›
                </span>
              </button>
            )}
          </section>
        )}

        {adminPanelTab === 'settings' && (
          <section className="CoachProfileSection" aria-label="Powiadomienia Web Push">
            <div className="CoachProfileSectionTitle">POWIADOMIENIA WEB PUSH</div>
            {!pushSupport.supported ? (
              <div className="Muted">{pushSupport.reason || 'Ta przeglądarka nie obsługuje Web Push.'}</div>
            ) : (
              <>
                {pushError && <div className="ErrorBanner">{pushError}</div>}
                <div className="CoachProfileRow">
                  <div className="CoachProfileRowKey">Powiadomienia Web Push</div>
                  <div className="CoachProfileRowVal">
                    <label className="Switch" aria-label="Powiadomienia Web Push">
                      <input
                        className="SwitchInput"
                        type="checkbox"
                        checked={pushEnabled}
                        onChange={(e) => {
                          if (isPushLoading) return;
                          if (e.target.checked) enablePush();
                          else disablePush();
                        }}
                        disabled={isPushLoading}
                      />
                      <span className="SwitchTrack" aria-hidden="true">
                        <span className="SwitchThumb" aria-hidden="true" />
                      </span>
                    </label>
                  </div>
                </div>
                <div className="CoachProfileActions">
                  <SecondaryButton onClick={refreshPush} disabled={isPushLoading} type="button">
                    Odśwież
                  </SecondaryButton>
                  <SecondaryButton onClick={sendTestPush} disabled={isPushLoading || !pushEnabled} type="button">
                    Wyślij testowy push
                  </SecondaryButton>
                  {IS_DEV && isAndroidUa && (
                    <SecondaryButton onClick={downloadMkcertRootCa} disabled={isPushLoading} type="button">
                      Pobierz certyfikat CA
                    </SecondaryButton>
                  )}
                </div>
              </>
            )}
          </section>
        )}

        {adminPanelTab === 'settings' && (
          <section className="CoachProfileSection" aria-label="Inne">
            <div className="CoachProfileSectionTitle">INNE</div>
            <div className="CoachProfileRow">
              <div className="CoachProfileRowKey">Ostatnia aktywność</div>
              <div className="CoachProfileRowVal">—</div>
            </div>
            <div className="CoachProfileActions">
              <SecondaryButton onClick={props.onLogout} type="button">
                Wyloguj
              </SecondaryButton>
            </div>
          </section>
        )}
            </div>
          </div>
        )}

        {attendanceReportOpen && (
          <div
            className="SheetOverlay"
            role="dialog"
            aria-modal="true"
            onMouseDown={(e) => {
              if (isLoading) return;
              if (e.target === e.currentTarget) setAttendanceReportOpen(false);
            }}
          >
            <div className="Sheet">
              <div className="SheetHeaderRow SheetHeaderRow--sticky">
                <div className="SheetTitle">RAPORT FREKWENCJI (PDF)</div>
                <button className="SheetCloseButton" type="button" aria-label="Zamknij" onClick={() => setAttendanceReportOpen(false)} disabled={isLoading}>
                  ×
                </button>
              </div>
              <Card title="Parametry">
                <div className="CoachProfileForm">
                  <select
                    className="TextInput"
                    value={attendanceReportGroupId}
                    onChange={(e) => setAttendanceReportGroupId(e.target.value)}
                    disabled={isLoading}
                  >
                    {groups.map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.name}
                        {g.year ? ` ${g.year}` : ''}
                      </option>
                    ))}
                  </select>
                  <select
                    className="TextInput"
                    value={attendanceReportRange}
                    onChange={(e) => setAttendanceReportRange(e.target.value as AttendanceReportRange)}
                    disabled={isLoading}
                  >
                    <option value="month">Miesięczny</option>
                    <option value="quarter">Kwartalny</option>
                    <option value="halfyear">Półroczny</option>
                  </select>
                  <div className="CoachProfileRow">
                    <div className="CoachProfileRowKey">Rok</div>
                    <input
                      className="TextInput"
                      value={attendanceReportYear}
                      onChange={(e) => setAttendanceReportYear(e.target.value)}
                      inputMode="numeric"
                      disabled={isLoading}
                    />
                  </div>
                  <div className="CoachProfileRow">
                    <div className="CoachProfileRowKey">Miesiąc</div>
                    <select
                      className="TextInput"
                      value={attendanceReportMonth}
                      onChange={(e) => setAttendanceReportMonth(e.target.value)}
                      disabled={isLoading}
                    >
                      {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                        <option key={m} value={String(m)}>
                          {m}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </Card>
              <Card title="Archiwum">
                <div className="CoachProfileActions">
                  <SecondaryButton onClick={loadAttendanceReports} disabled={isLoading || isAttendanceReportsLoading} type="button">
                    Odśwież listę
                  </SecondaryButton>
                </div>
                {isAttendanceReportsLoading ? (
                  <div className="Muted">Ładowanie…</div>
                ) : attendanceReports.length === 0 ? (
                  <div className="Muted">Brak zapisanych raportów dla tej grupy.</div>
                ) : (
                  <div className="List">
                    {attendanceReports.slice(0, 10).map((r) => (
                      <div key={r.id} className="ListItem">
                        <div className="ListItemTop">
                          <div className="ListItemTitle">
                            {r.year}-{String(r.month).padStart(2, '0')} • {r.range}
                          </div>
                          <div className="ListItemBadge">PDF</div>
                        </div>
                        <div className="ListItemMeta">{r.created_at}</div>
                        <div className="CoachProfileActions">
                          <SecondaryButton onClick={() => adminDownloadSavedAttendanceReport(r.id)} disabled={isLoading} type="button">
                            Pobierz
                          </SecondaryButton>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
              <div className="SheetActions">
                <PrimaryButton onClick={adminDownloadAttendanceReportPdf} disabled={isLoading || !attendanceReportGroupId.trim()} type="button">
                  Generuj i pobierz PDF
                </PrimaryButton>
                {isFullAdmin && (
                  <SecondaryButton onClick={adminBulkGenerateAttendanceReportsPrevMonth} disabled={isLoading} type="button">
                    Generuj dla wszystkich (poprzedni miesiąc)
                  </SecondaryButton>
                )}
                <SecondaryButton onClick={adminDownloadAttendanceReportSamplePdf} disabled={isLoading} type="button">
                  Pobierz szablon
                </SecondaryButton>
                <SecondaryButton onClick={() => setAttendanceReportOpen(false)} disabled={isLoading} type="button">
                  Zamknij
                </SecondaryButton>
              </div>
            </div>
          </div>
        )}

        {adminAccountsOpen && (
          <div
            className="SheetOverlay"
            role="dialog"
            aria-modal="true"
            onMouseDown={(e) => {
              if (isLoading) return;
              if (e.target === e.currentTarget) setAdminAccountsOpen(false);
            }}
          >
            <div className="Sheet">
              <div className="SheetHeaderRow SheetHeaderRow--sticky">
                <div className="SheetTitle">ZARZĄDZAJ KONTAMI</div>
                <button className="SheetCloseButton" type="button" aria-label="Zamknij" onClick={() => setAdminAccountsOpen(false)} disabled={isLoading}>
                  ×
                </button>
              </div>
              <div className={['SectionTabs', 'SectionTabs--2'].join(' ')} role="tablist" aria-label="Zakładki zarządzania kontami">
                <button
                  className={['SectionTab', adminAccountsTab === 'pending' ? 'is-active' : ''].filter(Boolean).join(' ')}
                  type="button"
                  onClick={() => {
                    setAdminAccountsTab('pending');
                    loadPending();
                  }}
                  disabled={isLoading}
                >
                  Akceptacje
                </button>
                <button
                  className={['SectionTab', adminAccountsTab === 'all' ? 'is-active' : ''].filter(Boolean).join(' ')}
                  type="button"
                  onClick={() => {
                    setAdminAccountsTab('all');
                  }}
                  disabled={isLoading}
                >
                  Wszystkie
                </button>
              </div>

              {adminAccountsTab === 'pending' && (
                <Card title="Akceptacja kont">
                {errorText && <div className="ErrorBanner">{errorText}</div>}
                <div className="List">
                  {pending.length === 0 ? (
                    <div className="Muted">Brak kont oczekujących.</div>
                  ) : (
                    pending.map((u) => (
                      <div key={u.id} className="ListItem">
                        <div className="ListItemTop">
                          <div className="ListItemTitle">{userLabelWithEmail(u)}</div>
                          <div className="ListItemBadge">{u.status}</div>
                        </div>
                        <div className="ListItemMeta">{u.created_at}</div>
                        {(() => {
                          const r = (u.signup_role ?? '').trim().toUpperCase();
                          const isParent = r === 'PARENT';
                          const isCoach = r === 'COACH';
                          const roleLabel = isParent ? 'Rodzic' : isCoach ? 'Trener' : '';
                          const cf = (u.signup_child_first_name ?? '').trim();
                          const cl = (u.signup_child_last_initial ?? '').trim();
                          const by = Number.isFinite(u.signup_child_birth_year as number) ? String(u.signup_child_birth_year) : '';
                          const group = (u.signup_group_label ?? '').trim();
                          const childLabel = isParent && (cf || cl || by) ? `${[cf, cl].filter(Boolean).join(' ')}${by ? ` (${by})` : ''}`.trim() : '';
                          const parts = [roleLabel, childLabel, group ? `Grupa: ${group}` : ''].filter(Boolean);
                          if (parts.length === 0) return null;
                          return <div className="Muted">{`Zgłoszenie: ${parts.join(' • ')}`}</div>;
                        })()}
                        <div className="Muted">Dostępy</div>
                        <div className="NotificationActions" aria-label="Dostępy konta">
                          <button
                            type="button"
                            className={`Pill ${(pendingAccessById[u.id]?.can_parent ?? u.can_parent) ? 'is-active' : ''}`}
                            onClick={() => {
                              setPendingAccessById((prev) => {
                                const cur = prev[u.id] ?? accessFromUser(u);
                                const next = { ...cur, can_parent: !cur.can_parent, can_coordinator: false };
                                if (!next.can_parent && !next.can_coach && !next.can_admin && !next.can_coordinator) return prev;
                                const nextRole = normalizeRoleForAccess(pendingRoleById[u.id] ?? 'PARENT', next);
                                if (nextRole !== (pendingRoleById[u.id] ?? 'PARENT')) {
                                  setPendingRoleById((rprev) => ({ ...rprev, [u.id]: nextRole }));
                                }
                                return { ...prev, [u.id]: next };
                              });
                            }}
                            disabled={isLoading}
                          >
                            Rodzic
                          </button>
                          <button
                            type="button"
                            className={`Pill ${(pendingAccessById[u.id]?.can_coach ?? u.can_coach) ? 'is-active' : ''}`}
                            onClick={() => {
                              setPendingAccessById((prev) => {
                                const cur = prev[u.id] ?? accessFromUser(u);
                                const next = { ...cur, can_coach: !cur.can_coach, can_coordinator: false };
                                if (!next.can_parent && !next.can_coach && !next.can_admin && !next.can_coordinator) return prev;
                                const nextRole = normalizeRoleForAccess(pendingRoleById[u.id] ?? 'PARENT', next);
                                if (nextRole !== (pendingRoleById[u.id] ?? 'PARENT')) {
                                  setPendingRoleById((rprev) => ({ ...rprev, [u.id]: nextRole }));
                                }
                                return { ...prev, [u.id]: next };
                              });
                            }}
                            disabled={isLoading}
                          >
                            Trener
                          </button>
                          {isFullAdmin && (
                            <>
                              <button
                                type="button"
                                className={`Pill ${(pendingAccessById[u.id]?.can_admin ?? u.can_admin) ? 'is-active' : ''}`}
                                onClick={() => {
                                  setPendingAccessById((prev) => {
                                    const cur = prev[u.id] ?? accessFromUser(u);
                                    const next = { ...cur, can_admin: !cur.can_admin };
                                    if (next.can_admin) {
                                      next.can_parent = false;
                                      next.can_coach = false;
                                      next.can_coordinator = false;
                                    }
                                    if (!next.can_parent && !next.can_coach && !next.can_admin && !next.can_coordinator) return prev;
                                    const nextRole = normalizeRoleForAccess(pendingRoleById[u.id] ?? 'PARENT', next);
                                    if (nextRole !== (pendingRoleById[u.id] ?? 'PARENT')) {
                                      setPendingRoleById((rprev) => ({ ...rprev, [u.id]: nextRole }));
                                    }
                                    return { ...prev, [u.id]: next };
                                  });
                                }}
                                disabled={isLoading}
                              >
                                Admin
                              </button>
                              <button
                                type="button"
                                className={`Pill ${(pendingAccessById[u.id]?.can_coordinator ?? u.role === 'COORDINATOR') ? 'is-active' : ''}`}
                                onClick={() => {
                                  setPendingAccessById((prev) => {
                                    const cur = prev[u.id] ?? accessFromUser(u);
                                    const next = { ...cur, can_coordinator: !cur.can_coordinator, can_admin: false };
                                    if (next.can_coordinator) {
                                      next.can_parent = false;
                                      next.can_coach = false;
                                    }
                                    if (!next.can_parent && !next.can_coach && !next.can_admin && !next.can_coordinator) return prev;
                                    const nextRole = normalizeRoleForAccess(pendingRoleById[u.id] ?? 'PARENT', next);
                                    if (nextRole !== (pendingRoleById[u.id] ?? 'PARENT')) {
                                      setPendingRoleById((rprev) => ({ ...rprev, [u.id]: nextRole }));
                                    }
                                    return { ...prev, [u.id]: next };
                                  });
                                }}
                                disabled={isLoading}
                              >
                                Koord.
                              </button>
                            </>
                          )}
                        </div>
                        <select
                          className="TextInput"
                          value={pendingRoleById[u.id] ?? normalizeRoleForAccess('PARENT', pendingAccessById[u.id] ?? accessFromUser(u))}
                          onChange={(e) => setPendingRoleById((prev) => ({ ...prev, [u.id]: e.target.value as Role }))}
                          disabled={isLoading}
                          aria-label="Tryb domyślny"
                        >
                          {enabledRolesFromAccess(pendingAccessById[u.id] ?? accessFromUser(u)).includes('PARENT') && (
                            <option value="PARENT">Rodzic</option>
                          )}
                          {enabledRolesFromAccess(pendingAccessById[u.id] ?? accessFromUser(u)).includes('COACH') && (
                            <option value="COACH">Trener</option>
                          )}
                          {isFullAdmin && enabledRolesFromAccess(pendingAccessById[u.id] ?? accessFromUser(u)).includes('ADMIN') && (
                            <option value="ADMIN">Admin</option>
                          )}
                          {isFullAdmin && enabledRolesFromAccess(pendingAccessById[u.id] ?? accessFromUser(u)).includes('COORDINATOR') && (
                            <option value="COORDINATOR">Koordynator</option>
                          )}
                        </select>
                        <PrimaryButton onClick={() => approve(u.id)} disabled={isLoading} type="button">
                          Akceptuj
                        </PrimaryButton>
                      </div>
                    ))
                  )}
                </div>
                <SecondaryButton onClick={loadPending} disabled={isLoading} type="button">
                  Odśwież
                </SecondaryButton>
                </Card>
              )}

              {adminAccountsTab === 'all' && (
                <Card title="Konta (wszystkie)">
                {errorText && <div className="ErrorBanner">{errorText}</div>}
                <div className="SearchRow">
                  <div className="SearchInputWrap">
                    <SearchIcon className="SearchIcon" />
                    <input
                      className="SearchInput"
                      value={usersQuery}
                      onChange={(e) => setUsersQuery(e.target.value)}
                      placeholder="Szukaj email/rola/status"
                    />
                  </div>
                </div>
                <select
                  className="TextInput"
                  value={usersStatusFilter}
                  onChange={(e) => setUsersStatusFilter(e.target.value as 'ALL' | 'PENDING' | 'ACTIVE' | 'REJECTED')}
                  disabled={isUsersLoading || isLoading}
                  aria-label="Filtr statusu"
                >
                  <option value="ALL">Wszystkie</option>
                  <option value="PENDING">PENDING</option>
                  <option value="ACTIVE">ACTIVE</option>
                  <option value="REJECTED">REJECTED</option>
                </select>

                <div className="List">
                  {isUsersLoading && users.length === 0 ? (
                    <div className="Muted">Ładowanie…</div>
                  ) : filteredUsers.length === 0 ? (
                    <div className="Muted">Brak kont.</div>
                  ) : (
                    filteredUsers.map((u) => {
                      const isSelf = u.id === props.me.id;
                      const isProtected = !isFullAdmin && (u.role === 'ADMIN' || u.role === 'COORDINATOR');
                      const disableEdit = isLoading || isSelf || isProtected;
                      const access = userAccessById[u.id] ?? accessFromUser(u);
                      return (
                        <div key={u.id} className="ListItem">
                          <div className="ListItemTop">
                            <div className="ListItemTitle">{userLabelWithEmail(u)}</div>
                            <div className="ListItemBadge">{u.status}</div>
                          </div>
                          <div className="ListItemMeta">{u.created_at}</div>
                          {isSelf && <div className="Muted">To Twoje konto — nie można go edytować ani usunąć.</div>}
                          {!isSelf && isProtected && <div className="Muted">Koordynator nie może edytować kont Admina ani Koordynatora.</div>}
                          <div className="Muted">Dostępy</div>
                          <div className="NotificationActions" aria-label="Dostępy konta">
                            <button
                              type="button"
                              className={`Pill ${access.can_parent ? 'is-active' : ''}`}
                              onClick={() => {
                                setUserAccessById((prev) => {
                                  const cur = prev[u.id] ?? accessFromUser(u);
                                  const next = { ...cur, can_parent: !cur.can_parent, can_coordinator: false };
                                  if (!next.can_parent && !next.can_coach && !next.can_admin && !next.can_coordinator) return prev;
                                  const nextRole = normalizeRoleForAccess(userRoleById[u.id] ?? 'PARENT', next);
                                  if (nextRole !== (userRoleById[u.id] ?? 'PARENT')) {
                                    setUserRoleById((rprev) => ({ ...rprev, [u.id]: nextRole }));
                                  }
                                  return { ...prev, [u.id]: next };
                                });
                              }}
                              disabled={disableEdit}
                            >
                              Rodzic
                            </button>
                            <button
                              type="button"
                              className={`Pill ${access.can_coach ? 'is-active' : ''}`}
                              onClick={() => {
                                setUserAccessById((prev) => {
                                  const cur = prev[u.id] ?? accessFromUser(u);
                                  const next = { ...cur, can_coach: !cur.can_coach, can_coordinator: false };
                                  if (!next.can_parent && !next.can_coach && !next.can_admin && !next.can_coordinator) return prev;
                                  const nextRole = normalizeRoleForAccess(userRoleById[u.id] ?? 'PARENT', next);
                                  if (nextRole !== (userRoleById[u.id] ?? 'PARENT')) {
                                    setUserRoleById((rprev) => ({ ...rprev, [u.id]: nextRole }));
                                  }
                                  return { ...prev, [u.id]: next };
                                });
                              }}
                              disabled={disableEdit}
                            >
                              Trener
                            </button>
                            {isFullAdmin && (
                              <>
                                <button
                                  type="button"
                                  className={`Pill ${access.can_admin ? 'is-active' : ''}`}
                                  onClick={() => {
                                    setUserAccessById((prev) => {
                                      const cur = prev[u.id] ?? accessFromUser(u);
                                      const next = { ...cur, can_admin: !cur.can_admin };
                                      if (next.can_admin) {
                                        next.can_parent = false;
                                        next.can_coach = false;
                                        next.can_coordinator = false;
                                      }
                                      if (!next.can_parent && !next.can_coach && !next.can_admin && !next.can_coordinator) return prev;
                                      const nextRole = normalizeRoleForAccess(userRoleById[u.id] ?? 'PARENT', next);
                                      if (nextRole !== (userRoleById[u.id] ?? 'PARENT')) {
                                        setUserRoleById((rprev) => ({ ...rprev, [u.id]: nextRole }));
                                      }
                                      return { ...prev, [u.id]: next };
                                    });
                                  }}
                                  disabled={disableEdit}
                                >
                                  Admin
                                </button>
                                <button
                                  type="button"
                                  className={`Pill ${access.can_coordinator ? 'is-active' : ''}`}
                                  onClick={() => {
                                    setUserAccessById((prev) => {
                                      const cur = prev[u.id] ?? accessFromUser(u);
                                      const next = { ...cur, can_coordinator: !cur.can_coordinator, can_admin: false };
                                      if (next.can_coordinator) {
                                        next.can_parent = false;
                                        next.can_coach = false;
                                      }
                                      if (!next.can_parent && !next.can_coach && !next.can_admin && !next.can_coordinator) return prev;
                                      const nextRole = normalizeRoleForAccess(userRoleById[u.id] ?? 'PARENT', next);
                                      if (nextRole !== (userRoleById[u.id] ?? 'PARENT')) {
                                        setUserRoleById((rprev) => ({ ...rprev, [u.id]: nextRole }));
                                      }
                                      return { ...prev, [u.id]: next };
                                    });
                                  }}
                                  disabled={disableEdit}
                                >
                                  Koord.
                                </button>
                              </>
                            )}
                          </div>
                          <select
                            className="TextInput"
                            value={userRoleById[u.id] ?? normalizeRoleForAccess('PARENT', access)}
                            onChange={(e) => setUserRoleById((prev) => ({ ...prev, [u.id]: e.target.value as Role }))}
                            disabled={disableEdit}
                            aria-label="Tryb domyślny"
                          >
                            {enabledRolesFromAccess(access).includes('PARENT') && <option value="PARENT">Rodzic</option>}
                            {enabledRolesFromAccess(access).includes('COACH') && <option value="COACH">Trener</option>}
                            {isFullAdmin && enabledRolesFromAccess(access).includes('ADMIN') && <option value="ADMIN">Admin</option>}
                            {isFullAdmin && enabledRolesFromAccess(access).includes('COORDINATOR') && <option value="COORDINATOR">Koordynator</option>}
                          </select>
                          <select
                            className="TextInput"
                            value={userStatusById[u.id] ?? 'PENDING'}
                            onChange={(e) =>
                              setUserStatusById((prev) => ({ ...prev, [u.id]: e.target.value as 'ACTIVE' | 'PENDING' | 'REJECTED' }))
                            }
                            disabled={disableEdit}
                            aria-label="Status"
                          >
                            <option value="PENDING">PENDING</option>
                            <option value="ACTIVE">ACTIVE</option>
                            <option value="REJECTED">REJECTED</option>
                          </select>
                          <PrimaryButton onClick={() => updateUser(u.id)} disabled={disableEdit} type="button">
                            Zapisz zmiany
                          </PrimaryButton>
                          <SecondaryButton onClick={() => openEditUserEmail(u)} disabled={disableEdit} type="button">
                            Zmień email
                          </SecondaryButton>
                          <SecondaryButton onClick={() => openResetUserPassword(u)} disabled={disableEdit} type="button">
                            Resetuj hasło
                          </SecondaryButton>
                          <SecondaryButton
                            onClick={() => setDeleteUser({ id: u.id, email: userLabelWithEmail(u) })}
                            disabled={disableEdit}
                            type="button"
                          >
                            Usuń konto
                          </SecondaryButton>
                        </div>
                      );
                    })
                  )}
                </div>
                <div className="ListMeta">
                  <div>{`Wyniki: ${users.length}${usersTotal !== null ? ` / ${usersTotal}` : ''}`}</div>
                  <div>{usersHasMore ? 'Możesz wczytać więcej' : 'Koniec listy'}</div>
                </div>
                {usersHasMore && (
                  <SecondaryButton onClick={loadMoreUsers} disabled={isUsersLoading || isLoading} type="button">
                    Załaduj więcej
                  </SecondaryButton>
                )}
                <SecondaryButton onClick={loadUsers} disabled={isUsersLoading || isLoading} type="button">
                  Odśwież
                </SecondaryButton>
                </Card>
              )}
            </div>
          </div>
        )}

        {adminCreateUserOpen && (
          <div
            className="SheetOverlay"
            role="dialog"
            aria-modal="true"
            onMouseDown={(e) => {
              if (isLoading) return;
              if (e.target === e.currentTarget) setAdminCreateUserOpen(false);
            }}
          >
            <div className="Sheet">
              <div className="SheetHeaderRow SheetHeaderRow--sticky">
                <div className="SheetTitle">UTWÓRZ NOWE KONTO</div>
                <button
                  className="SheetCloseButton"
                  type="button"
                  aria-label="Zamknij"
                  onClick={() => setAdminCreateUserOpen(false)}
                  disabled={isLoading}
                >
                  ×
                </button>
              </div>
              {errorText && <div className="ErrorBanner">{errorText}</div>}
              <div className="Muted">Jeśli hasło zostawisz puste, zostanie wygenerowane automatycznie.</div>
              <input
                className="TextInput"
                value={createUserEmail}
                onChange={(e) => setCreateUserEmail(e.target.value)}
                placeholder="Email użytkownika"
                inputMode="email"
                autoCapitalize="none"
                autoCorrect="off"
                disabled={isLoading}
              />
              <div className="Muted">Dostępy</div>
              <div className="NotificationActions" aria-label="Dostępy konta">
                <button
                  type="button"
                  className={`Pill ${createUserAccess.can_parent ? 'is-active' : ''}`}
                  onClick={() => {
                    setCreateUserAccess((prev) => {
                      const next = { ...prev, can_parent: !prev.can_parent, can_coordinator: false };
                      if (!next.can_parent && !next.can_coach && !next.can_admin && !next.can_coordinator) return prev;
                      const nextRole = normalizeRoleForAccess(createUserRole, next);
                      if (nextRole !== createUserRole) setCreateUserRole(nextRole);
                      return next;
                    });
                  }}
                  disabled={isLoading}
                >
                  Rodzic
                </button>
                <button
                  type="button"
                  className={`Pill ${createUserAccess.can_coach ? 'is-active' : ''}`}
                  onClick={() => {
                    setCreateUserAccess((prev) => {
                      const next = { ...prev, can_coach: !prev.can_coach, can_coordinator: false };
                      if (!next.can_parent && !next.can_coach && !next.can_admin && !next.can_coordinator) return prev;
                      const nextRole = normalizeRoleForAccess(createUserRole, next);
                      if (nextRole !== createUserRole) setCreateUserRole(nextRole);
                      return next;
                    });
                  }}
                  disabled={isLoading}
                >
                  Trener
                </button>
                {isFullAdmin && (
                  <>
                    <button
                      type="button"
                      className={`Pill ${createUserAccess.can_admin ? 'is-active' : ''}`}
                      onClick={() => {
                        setCreateUserAccess((prev) => {
                          const next = { ...prev, can_admin: !prev.can_admin };
                          if (next.can_admin) {
                            next.can_parent = false;
                            next.can_coach = false;
                            next.can_coordinator = false;
                          }
                          if (!next.can_parent && !next.can_coach && !next.can_admin && !next.can_coordinator) return prev;
                          const nextRole = normalizeRoleForAccess(createUserRole, next);
                          if (nextRole !== createUserRole) setCreateUserRole(nextRole);
                          return next;
                        });
                      }}
                      disabled={isLoading}
                    >
                      Admin
                    </button>
                    <button
                      type="button"
                      className={`Pill ${createUserAccess.can_coordinator ? 'is-active' : ''}`}
                      onClick={() => {
                        setCreateUserAccess((prev) => {
                          const next = { ...prev, can_coordinator: !prev.can_coordinator, can_admin: false };
                          if (next.can_coordinator) {
                            next.can_parent = false;
                            next.can_coach = false;
                          }
                          if (!next.can_parent && !next.can_coach && !next.can_admin && !next.can_coordinator) return prev;
                          const nextRole = normalizeRoleForAccess(createUserRole, next);
                          if (nextRole !== createUserRole) setCreateUserRole(nextRole);
                          return next;
                        });
                      }}
                      disabled={isLoading}
                    >
                      Koord.
                    </button>
                  </>
                )}
              </div>
              <select
                className="TextInput"
                value={createUserRole}
                onChange={(e) => {
                  const nextRole = e.target.value as Role;
                  setCreateUserRole(nextRole);
                  setCreateUserAccess((prev) => {
                    if (nextRole === 'COORDINATOR') {
                      return { can_parent: false, can_coach: false, can_admin: false, can_coordinator: true, can_player: false };
                    }
                    const next = {
                      ...prev,
                      can_coordinator: false,
                      can_parent: prev.can_parent || nextRole === 'PARENT',
                      can_coach: prev.can_coach || nextRole === 'COACH',
                      can_admin: prev.can_admin || nextRole === 'ADMIN',
                    };
                    return next;
                  });
                }}
                disabled={isLoading}
                aria-label="Tryb domyślny"
              >
                {enabledRolesFromAccess(createUserAccess).includes('PARENT') && <option value="PARENT">Rodzic</option>}
                {enabledRolesFromAccess(createUserAccess).includes('COACH') && <option value="COACH">Trener</option>}
                {isFullAdmin && enabledRolesFromAccess(createUserAccess).includes('ADMIN') && <option value="ADMIN">Admin</option>}
                {isFullAdmin && enabledRolesFromAccess(createUserAccess).includes('COORDINATOR') && (
                  <option value="COORDINATOR">Koordynator</option>
                )}
              </select>
              <select
                className="TextInput"
                value={createUserStatus}
                onChange={(e) => setCreateUserStatus(e.target.value as 'ACTIVE' | 'PENDING')}
                disabled={isLoading}
                aria-label="Status konta"
              >
                <option value="ACTIVE">ACTIVE</option>
                <option value="PENDING">PENDING</option>
              </select>
              <input
                className="TextInput"
                value={createUserPassword}
                onChange={(e) => setCreateUserPassword(e.target.value)}
                placeholder="Hasło (opcjonalnie)"
                type="password"
                disabled={isLoading}
              />
              <PrimaryButton onClick={createUser} disabled={isLoading || createUserEmail.trim() === ''} type="button">
                Utwórz konto
              </PrimaryButton>
              {createdUserPassword && createdUserEmail && (
                <div className="KeyValue">
                  <div className="Key">Hasło</div>
                  <div className="Value">
                    {createdUserEmail}: {createdUserPassword}
                  </div>
                </div>
              )}
              <SecondaryButton onClick={() => setAdminCreateUserOpen(false)} disabled={isLoading} type="button">
                Zamknij
              </SecondaryButton>
            </div>
          </div>
        )}

        {adminChildrenOpen && (
          <div
            className="SheetOverlay"
            role="dialog"
            aria-modal="true"
            onMouseDown={(e) => {
              if (isLoading) return;
              if (e.target === e.currentTarget) setAdminChildrenOpen(false);
            }}
          >
            <div className="Sheet">
              <div className="SheetHeaderRow SheetHeaderRow--sticky">
                <div className="SheetTitle">ZARZĄDZAJ DZIEĆMI</div>
                <button className="SheetCloseButton" type="button" aria-label="Zamknij" onClick={() => setAdminChildrenOpen(false)}>
                  ×
                </button>
              </div>
              {errorText && <div className="ErrorBanner">{errorText}</div>}
              <div className="SectionTabs" role="tablist" aria-label="Zakładki zarządzania dziećmi">
                <button
                  className={['SectionTab', adminChildrenTab === 'list' ? 'is-active' : ''].filter(Boolean).join(' ')}
                  type="button"
                  onClick={() => setAdminChildrenTab('list')}
                  disabled={isLoading}
                >
                  Lista
                </button>
                <button
                  className={['SectionTab', adminChildrenTab === 'add' ? 'is-active' : ''].filter(Boolean).join(' ')}
                  type="button"
                  onClick={() => setAdminChildrenTab('add')}
                  disabled={isLoading}
                >
                  Dodaj
                </button>
                <button
                  className={['SectionTab', adminChildrenTab === 'assign' ? 'is-active' : ''].filter(Boolean).join(' ')}
                  type="button"
                  onClick={() => setAdminChildrenTab('assign')}
                  disabled={isLoading}
                >
                  Przypisz
                </button>
              </div>

              {adminChildrenTab === 'list' && (
                <Card title="Lista dzieci (edycja/usuwanie)">
                  <div className="SearchRow">
                    <div className="SearchInputWrap">
                      <SearchIcon className="SearchIcon" />
                      <input
                        className="SearchInput"
                        value={adminChildQuery}
                        onChange={(e) => setAdminChildQuery(e.target.value)}
                        placeholder="Szukaj: imię/nazwisko, grupa, id, numer"
                        disabled={isLoading || isAdminChildrenListLoading}
                      />
                    </div>
                  </div>
                  <div className="AdminFormGrid2">
                    <select
                      className="TextInput"
                      value={adminChildGroupFilter}
                      onChange={(e) => setAdminChildGroupFilter(e.target.value)}
                      disabled={isLoading || isAdminChildrenListLoading}
                      aria-label="Filtr grupy"
                    >
                      <option value="ALL">Wszystkie grupy</option>
                      <option value="NO_GROUP">Brak grupy</option>
                      {adminChildGroupOptions.map((g) => (
                        <option key={g.id} value={g.id}>
                          {g.label}
                        </option>
                      ))}
                    </select>
                    <select
                      className="TextInput"
                      value={adminChildYearFilter}
                      onChange={(e) => setAdminChildYearFilter(e.target.value)}
                      disabled={isLoading || isAdminChildrenListLoading}
                      aria-label="Filtr rocznika"
                    >
                      <option value="ALL">Rocznik grupy (wszystkie)</option>
                      {adminChildGroupYearOptions.map((y) => (
                        <option key={y} value={String(y)}>
                          {y}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="AdminFormGrid2">
                    <div className="Muted">Rok urodzenia dziecka</div>
                    <select
                      className="TextInput"
                      value={adminChildBirthYearFilter}
                      onChange={(e) => setAdminChildBirthYearFilter(e.target.value)}
                      disabled={isLoading || isAdminChildrenListLoading}
                      aria-label="Filtr roku urodzenia dziecka"
                    >
                      <option value="ALL">Wszystkie</option>
                      {adminChildBirthYearOptions.map((y) => (
                        <option key={y} value={String(y)}>
                          {y}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="ListMeta">
                    <div>{`Wyniki: ${adminChildrenList.length}${adminChildrenListTotal !== null ? ` / ${adminChildrenListTotal}` : ''}`}</div>
                    <div>
                      {adminChildGroupFilter === 'ALL' &&
                      adminChildYearFilter === 'ALL' &&
                      adminChildBirthYearFilter === 'ALL' &&
                      !adminChildQuery.trim()
                        ? 'Brak filtrów'
                        : 'Filtry aktywne'}
                    </div>
                  </div>
                  <div className="List">
                    {isAdminChildrenListLoading && adminChildrenList.length === 0 ? (
                      <div className="Muted">Ładowanie…</div>
                    ) : adminChildrenListTotal === 0 ? (
                      <div className="Muted">Brak dzieci.</div>
                    ) : adminChildrenList.length === 0 ? (
                      <div className="Muted">Brak wyników.</div>
                    ) : (
                      adminChildrenList.map((c) => {
                        const initials =
                          `${(c.first_name ?? '').trim().slice(0, 1)}${(c.last_initial ?? '').trim().slice(0, 1)}`.toUpperCase() || 'D';
                        const group = (c.group_name ?? '').trim() || 'Brak grupy';
                        const shirt = Number.isFinite(c.shirt_number as number) ? `Nr ${String(c.shirt_number)}` : '';
                        const open = () =>
                          setEditChild({
                            id: c.id,
                            first_name: c.first_name,
                            last_initial: c.last_initial,
                            birth_year: c.birth_year ? String(c.birth_year) : '',
                            shirt_number: c.shirt_number ? String(c.shirt_number) : '',
                          });
                        return (
                          <div
                            key={c.id}
                            className="AdminChildRow"
                            role="button"
                            tabIndex={0}
                            onClick={open}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') open();
                            }}
                          >
                            <div className="AdminChildAvatar" aria-hidden="true">
                              {initials}
                            </div>
                            <div className="AdminChildMain">
                              <div className="AdminChildTitle">{childLabel(c)}</div>
                              <div className="AdminChildMeta">
                                {group}
                                {shirt ? ` • ${shirt}` : ''}
                                {` • ${c.id}`}
                              </div>
                            </div>
                            <div className="AdminChildActions">
                              <button
                                className="AdminChildIconBtn"
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  open();
                                }}
                                disabled={isLoading}
                                aria-label="Edytuj dziecko"
                              >
                                ✎
                              </button>
                              <button
                                className="AdminChildIconBtn is-danger"
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeleteChild({ id: c.id, label: childLabel(c) });
                                }}
                                disabled={isLoading}
                                aria-label="Usuń dziecko"
                              >
                                🗑
                              </button>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                  {adminChildrenListHasMore && (
                    <SecondaryButton
                      onClick={() => loadAdminChildrenList('more')}
                      disabled={isLoading || isAdminChildrenListLoading}
                      type="button"
                    >
                      Załaduj więcej
                    </SecondaryButton>
                  )}
                  <SecondaryButton onClick={() => loadAdminChildrenList('reset')} disabled={isLoading || isAdminChildrenListLoading} type="button">
                    Odśwież
                  </SecondaryButton>
                </Card>
              )}

              {adminChildrenTab === 'add' && (
                <Card title="Dodaj dziecko">
                  <div className="Muted">Nowe dziecko trafi do bazy, potem możesz przypisać rodzica i trenera.</div>
                  <div className="AdminFormGrid2">
                    <input
                      className="TextInput"
                      value={newChildFirstName}
                      onChange={(e) => setNewChildFirstName(e.target.value)}
                      placeholder="Imię"
                      disabled={isLoading}
                    />
                    <input
                      className="TextInput"
                      value={newChildLastInitial}
                      onChange={(e) => setNewChildLastInitial(e.target.value)}
                      placeholder="Inicjał nazwiska (np. K.)"
                      disabled={isLoading}
                    />
                  </div>
                  <div className="AdminFormGrid2">
                    <input
                      className="TextInput"
                      value={newChildBirthYear}
                      onChange={(e) => setNewChildBirthYear(e.target.value)}
                      placeholder="Rok ur. (opcjonalnie)"
                      inputMode="numeric"
                      disabled={isLoading}
                    />
                    <input
                      className="TextInput"
                      value={newChildShirtNumber}
                      onChange={(e) => setNewChildShirtNumber(e.target.value)}
                      placeholder="Nr (opcjonalnie)"
                      inputMode="numeric"
                      disabled={isLoading}
                    />
                  </div>
                  <PrimaryButton
                    onClick={createChild}
                    disabled={isLoading || newChildFirstName.trim() === '' || newChildLastInitial.trim() === ''}
                    type="button"
                  >
                    Dodaj dziecko
                  </PrimaryButton>
                </Card>
              )}

              {adminChildrenTab === 'assign' && (
                <Card title="Przypisania">
                  <details className="AdminAccordion" open>
                    <summary className="AdminAccordionSummary">Przypisz rodzica</summary>
                    <div className="AdminAccordionBody">
                      <div className="AdminFormGrid2">
                        <select className="TextInput" value={assignChildId} onChange={(e) => setAssignChildId(e.target.value)} disabled={isLoading}>
                          {children.map((c) => (
                            <option key={c.id} value={c.id}>
                              {childLabel(c)}
                            </option>
                          ))}
                        </select>
                        <input
                          className="TextInput"
                          value={assignParentEmail}
                          onChange={(e) => setAssignParentEmail(e.target.value)}
                          placeholder="Email rodzica"
                          inputMode="email"
                          autoCapitalize="none"
                          autoCorrect="off"
                          disabled={isLoading}
                        />
                      </div>
                      <PrimaryButton onClick={assignParent} disabled={isLoading || !assignChildId || assignParentEmail.trim() === ''} type="button">
                        Przypisz rodzica
                      </PrimaryButton>
                    </div>
                  </details>

                  <details className="AdminAccordion">
                    <summary className="AdminAccordionSummary">Przypisz trenera indywidualnego</summary>
                    <div className="AdminAccordionBody">
                      <div className="Muted">Dla wybranego dziecka.</div>
                      <select className="TextInput" value={assignChildId} onChange={(e) => setAssignChildId(e.target.value)} disabled={isLoading}>
                        {children.map((c) => (
                          <option key={c.id} value={c.id}>
                            {childLabel(c)}
                          </option>
                        ))}
                      </select>

                      {individualCoaches.length > 0 ? (
                        <div className="MatchCreateSuggestions" aria-label="Trenerzy indywidualni">
                          {individualCoaches.map((c) => (
                            <button
                              key={c.id}
                              type="button"
                              className="Pill"
                              onClick={() => unassignIndividualCoach(c.id)}
                              disabled={isLoading}
                              aria-label={`Usuń trenera: ${c.email}`}
                            >
                              {c.email}
                              <span aria-hidden="true"> ×</span>
                            </button>
                          ))}
                        </div>
                      ) : (
                        <div className="Muted">Brak.</div>
                      )}
                      <div className="AdminFormGrid2">
                        <input
                          className="TextInput"
                          value={assignIndividualCoachEmail}
                          onChange={(e) => setAssignIndividualCoachEmail(e.target.value)}
                          placeholder="Email trenera (indywidualnego)"
                          inputMode="email"
                          autoCapitalize="none"
                          autoCorrect="off"
                          disabled={isLoading}
                        />
                        <PrimaryButton
                          onClick={assignIndividualCoach}
                          disabled={isLoading || !assignChildId || assignIndividualCoachEmail.trim() === ''}
                          type="button"
                        >
                          Przypisz
                        </PrimaryButton>
                      </div>
                    </div>
                  </details>

                  <details className="AdminAccordion">
                    <summary className="AdminAccordionSummary">Przypisz konto zawodnika</summary>
                    <div className="AdminAccordionBody">
                      <div className="Muted">Jedno konto zawodnika jest przypięte do jednego dziecka.</div>
                      <select className="TextInput" value={assignChildId} onChange={(e) => setAssignChildId(e.target.value)} disabled={isLoading}>
                        {children.map((c) => (
                          <option key={c.id} value={c.id}>
                            {childLabel(c)}
                          </option>
                        ))}
                      </select>

                      {childPlayers.length > 0 ? (
                        <div className="MatchCreateSuggestions" aria-label="Konta zawodników">
                          {childPlayers.map((p) => (
                            <button
                              key={p.id}
                              type="button"
                              className="Pill"
                              onClick={() => unassignPlayer(p.id)}
                              disabled={isLoading}
                              aria-label={`Usuń konto zawodnika: ${p.email}`}
                            >
                              {p.email}
                              <span aria-hidden="true"> ×</span>
                            </button>
                          ))}
                        </div>
                      ) : (
                        <div className="Muted">Brak przypiętego konta zawodnika.</div>
                      )}

                      <div className="AdminFormGrid2">
                        <input
                          className="TextInput"
                          value={assignPlayerEmail}
                          onChange={(e) => setAssignPlayerEmail(e.target.value)}
                          placeholder="Email konta zawodnika"
                          inputMode="email"
                          autoCapitalize="none"
                          autoCorrect="off"
                          disabled={isLoading}
                        />
                        <PrimaryButton onClick={assignPlayer} disabled={isLoading || !assignChildId || assignPlayerEmail.trim() === ''} type="button">
                          Przypisz
                        </PrimaryButton>
                      </div>
                    </div>
                  </details>
                </Card>
              )}

            </div>
          </div>
        )}

        {adminGroupsOpen && (
          <div
            className="SheetOverlay"
            role="dialog"
            aria-modal="true"
            onMouseDown={(e) => {
              if (isLoading) return;
              if (e.target === e.currentTarget) setAdminGroupsOpen(false);
            }}
          >
            <div className="Sheet">
              <div className="SheetHeaderRow SheetHeaderRow--sticky">
                <div className="SheetTitle">KONFIGURACJA GRUP</div>
                <button className="SheetCloseButton" type="button" aria-label="Zamknij" onClick={() => setAdminGroupsOpen(false)} disabled={isLoading}>
                  ×
                </button>
              </div>
              {errorText && <div className="ErrorBanner">{errorText}</div>}
              <div className="SectionTabs">
                <button
                  type="button"
                  className={['SectionTab', adminGroupConfigTab === 'groups' ? 'is-active' : ''].filter(Boolean).join(' ')}
                  onClick={() => setAdminGroupConfigTab('groups')}
                >
                  Grupy
                </button>
                <button
                  type="button"
                  className={['SectionTab', adminGroupConfigTab === 'assign-children' ? 'is-active' : ''].filter(Boolean).join(' ')}
                  onClick={() => setAdminGroupConfigTab('assign-children')}
                >
                  Dzieci
                </button>
                <button
                  type="button"
                  className={['SectionTab', adminGroupConfigTab === 'assign-coaches' ? 'is-active' : ''].filter(Boolean).join(' ')}
                  onClick={() => setAdminGroupConfigTab('assign-coaches')}
                >
                  Trenerzy
                </button>
              </div>

              {adminGroupConfigTab === 'groups' && (
                <Card title="Grupy">
                  <div className="Muted">Najpierw utwórz grupy, potem przypisuj do nich dzieci i trenerów.</div>
                  <input
                    className="TextInput"
                    value={newGroupName}
                    onChange={(e) => setNewGroupName(e.target.value)}
                    placeholder="Nazwa grupy (np. Biała)"
                    disabled={isLoading}
                  />
                  <input
                    className="TextInput"
                    value={newGroupYear}
                    onChange={(e) => setNewGroupYear(e.target.value)}
                    placeholder="Rocznik grupy (opcjonalnie)"
                    inputMode="numeric"
                    disabled={isLoading}
                  />
                  <PrimaryButton onClick={createGroup} disabled={isLoading || newGroupName.trim() === ''} type="button">
                    Dodaj grupę
                  </PrimaryButton>
                  <SecondaryButton onClick={loadGroups} disabled={isLoading} type="button">
                    Odśwież listę grup
                  </SecondaryButton>
                </Card>
              )}

              {adminGroupConfigTab === 'assign-children' && (
                <Card title="Przypisywanie dzieci do grup">
                  <div className="Muted">Osobny widok do szybkiego filtrowania, sortowania i zmiany grupy dla konkretnego dziecka.</div>
                  <div className="SearchRow">
                    <div className="SearchInputWrap">
                      <SearchIcon className="SearchIcon" />
                      <input
                        className="SearchInput"
                        value={groupAssignQuery}
                        onChange={(e) => setGroupAssignQuery(e.target.value)}
                        placeholder="Szukaj: imię, inicjał, grupa, numer, id"
                        disabled={isLoading || isGroupAssignListLoading}
                      />
                    </div>
                  </div>
                  <div className="AdminFormGrid2">
                    <select
                      className="TextInput"
                      value={groupAssignGroupFilter}
                      onChange={(e) => setGroupAssignGroupFilter(e.target.value)}
                      disabled={isLoading || isGroupAssignListLoading}
                    >
                      <option value="ALL">Wszystkie grupy</option>
                      <option value="NO_GROUP">Brak grupy</option>
                      {adminChildGroupOptions.map((g) => (
                        <option key={g.id} value={g.id}>
                          {g.label}
                        </option>
                      ))}
                    </select>
                    <select
                      className="TextInput"
                      value={groupAssignYearFilter}
                      onChange={(e) => setGroupAssignYearFilter(e.target.value)}
                      disabled={isLoading || isGroupAssignListLoading}
                    >
                      <option value="ALL">Rocznik grupy (wszystkie)</option>
                      {adminChildGroupYearOptions.map((y) => (
                        <option key={y} value={String(y)}>
                          {y}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="AdminFormGrid2">
                    <select
                      className="TextInput"
                      value={groupAssignBirthYearFilter}
                      onChange={(e) => setGroupAssignBirthYearFilter(e.target.value)}
                      disabled={isLoading || isGroupAssignListLoading}
                    >
                      <option value="ALL">Rok urodzenia dziecka (wszystkie)</option>
                      {adminChildBirthYearOptions.map((y) => (
                        <option key={y} value={String(y)}>
                          {y}
                        </option>
                      ))}
                    </select>
                    <select
                      className="TextInput"
                      value={groupAssignSort}
                      onChange={(e) => setGroupAssignSort(e.target.value as typeof groupAssignSort)}
                      disabled={isLoading || isGroupAssignListLoading}
                    >
                      <option value="name-asc">Sortuj: imię A-Z</option>
                      <option value="name-desc">Sortuj: imię Z-A</option>
                      <option value="birth-asc">Sortuj: rok rosnąco</option>
                      <option value="birth-desc">Sortuj: rok malejąco</option>
                      <option value="group-asc">Sortuj: grupa A-Z</option>
                      <option value="group-desc">Sortuj: grupa Z-A</option>
                    </select>
                  </div>
                  <div className="ListMeta">
                    <div>{`Wyniki: ${groupAssignDisplayList.length}${groupAssignListTotal !== null ? ` / ${groupAssignListTotal}` : ''}`}</div>
                    <div>{isGroupAssignListLoading ? 'Ładowanie…' : 'Gotowe'}</div>
                  </div>
                  <div className="List">
                    {isGroupAssignListLoading && groupAssignDisplayList.length === 0 ? (
                      <div className="Muted">Ładowanie listy dzieci…</div>
                    ) : groupAssignListTotal === 0 ? (
                      <div className="Muted">Brak dzieci.</div>
                    ) : groupAssignDisplayList.length === 0 ? (
                      <div className="Muted">Brak wyników dla wybranych filtrów.</div>
                    ) : (
                      groupAssignDisplayList.map((c) => {
                        const initials = `${(c.first_name ?? '').trim().slice(0, 1)}${(c.last_initial ?? '').trim().slice(0, 1)}`.toUpperCase() || 'D';
                        const groupMeta = (c.group_name ?? '').trim() || 'Brak grupy';
                        const birthMeta = c.birth_year ? `Rocznik ${c.birth_year}` : 'Rok urodzenia —';
                        const shirtMeta = Number.isFinite(c.shirt_number as number) ? `Nr ${String(c.shirt_number)}` : 'Nr —';
                        return (
                          <div key={c.id} className="AdminChildRow">
                            <div className="AdminChildAvatar" aria-hidden="true">
                              {initials}
                            </div>
                            <div className="AdminChildMain">
                              <div className="AdminChildTitle">{`${c.first_name} ${c.last_initial}`}</div>
                              <div className="AdminChildMeta">{`${groupMeta} • ${birthMeta} • ${shirtMeta}`}</div>
                              <select
                                className="TextInput"
                                value={groupAssignTargetByChildId[c.id] ?? c.group_id ?? ''}
                                onChange={(e) =>
                                  setGroupAssignTargetByChildId((prev) => ({
                                    ...prev,
                                    [c.id]: e.target.value,
                                  }))
                                }
                                disabled={isLoading}
                              >
                                <option value="">— Brak grupy —</option>
                                {adminChildGroupOptions.map((g) => (
                                  <option key={g.id} value={g.id}>
                                    {g.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="AdminChildActions">
                              <PrimaryButton
                                onClick={() => setChildGroup(c.id)}
                                disabled={isLoading || (groupAssignTargetByChildId[c.id] ?? c.group_id ?? '') === (c.group_id ?? '')}
                                type="button"
                              >
                                Zapisz
                              </PrimaryButton>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                  {groupAssignListHasMore && (
                    <SecondaryButton onClick={() => loadGroupAssignList('more')} disabled={isLoading || isGroupAssignListLoading} type="button">
                      Załaduj więcej
                    </SecondaryButton>
                  )}
                </Card>
              )}

              {adminGroupConfigTab === 'assign-coaches' && (
                <Card title="Przypisz trener → grupa">
                  <div className="Muted">Przypisz aktywne konto trenera do wybranej grupy.</div>
                  <select className="TextInput" value={assignCoachGroupId} onChange={(e) => setAssignCoachGroupId(e.target.value)} disabled={isLoading}>
                    {groups.map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.name}
                        {g.year ? ` (${g.year})` : ''}
                      </option>
                    ))}
                  </select>
                  <input
                    className="TextInput"
                    value={assignCoachEmail}
                    onChange={(e) => setAssignCoachEmail(e.target.value)}
                    placeholder="Email trenera (konto ACTIVE, rola COACH)"
                    inputMode="email"
                    autoCapitalize="none"
                    autoCorrect="off"
                    disabled={isLoading}
                  />
                  <PrimaryButton onClick={assignCoach} disabled={isLoading || assignCoachGroupId === '' || assignCoachEmail.trim() === ''} type="button">
                    Przypisz trenera
                  </PrimaryButton>
                  <SecondaryButton onClick={loadGroups} disabled={isLoading} type="button">
                    Odśwież listę grup
                  </SecondaryButton>
                </Card>
              )}
            </div>
          </div>
        )}

        {adminQuickReactionsOpen && (
          <div
            className="SheetOverlay"
            role="dialog"
            aria-modal="true"
            onMouseDown={(e) => {
              if (isQuickTtlLoading) return;
              if (e.target === e.currentTarget) setAdminQuickReactionsOpen(false);
            }}
          >
            <div className="Sheet">
              <div className="SheetHeaderRow SheetHeaderRow--sticky">
                <div className="SheetTitle">SZYBKIE REAKCJE</div>
                <button
                  className="SheetCloseButton"
                  type="button"
                  aria-label="Zamknij"
                  onClick={() => setAdminQuickReactionsOpen(false)}
                  disabled={isQuickTtlLoading}
                >
                  ×
                </button>
              </div>
              {quickTtlError && <div className="ErrorBanner">{quickTtlError}</div>}
              {quickTtlInfo && <div className="InfoBanner">{quickTtlInfo}</div>}
              <div className="Muted">Czas widoczności reakcji na profilu zawodnika.</div>
              <div className="KeyValue">
                <div className="Key">Aktualnie</div>
                <div className="Value">{`${quickTtlDays} dni`}</div>
              </div>
              <input
                className="TextInput"
                value={quickTtlInput}
                onChange={(e) => setQuickTtlInput(e.target.value)}
                placeholder="1–30"
                inputMode="numeric"
                disabled={isQuickTtlLoading}
              />
              <PrimaryButton
                onClick={saveQuickTtl}
                disabled={
                  isQuickTtlLoading ||
                  !Number.isFinite(Number(quickTtlInput.trim())) ||
                  Number(quickTtlInput.trim()) < 1 ||
                  Number(quickTtlInput.trim()) > 30
                }
                type="button"
              >
                Zapisz
              </PrimaryButton>
            </div>
          </div>
        )}

        {adminDbToolsOpen && (
          <div
            className="SheetOverlay"
            role="dialog"
            aria-modal="true"
            onMouseDown={(e) => {
              if (isDbLoading) return;
              if (e.target === e.currentTarget) setAdminDbToolsOpen(false);
            }}
          >
            <div className="Sheet">
              <div className="SheetHeaderRow SheetHeaderRow--sticky">
                <div className="SheetTitle">BACKUP & CZYSZCZENIE</div>
                <button className="SheetCloseButton" type="button" aria-label="Zamknij" onClick={() => setAdminDbToolsOpen(false)} disabled={isDbLoading}>
                  ×
                </button>
              </div>
              {dbError && <div className="ErrorBanner">{dbError}</div>}
              {dbInfo && <div className="Muted">{dbInfo}</div>}
              <div className="Muted">
                Reset czyści kalendarz (wydarzenia) i statystyki (obecności, powołania, RSVP). Nie usuwa kont, dzieci, grup, feedu ani wiadomości.
              </div>
              <SecondaryButton onClick={adminDownloadDbBackup} disabled={isDbLoading} type="button">
                Pobierz backup bazy
              </SecondaryButton>
              <label className="MatchCreateSwitchRow" aria-label="Wykonaj backup przed resetem">
                <span className="MatchCreateSwitchText">
                  <span className="MatchCreateSwitchTitle">BACKUP PRZED RESETEM</span>
                  <span className="MatchCreateSwitchSub">Zapisze kopię do pliku przed wyczyszczeniem (SQLite)</span>
                </span>
                <span className="MatchCreateSwitchControl">
                  <span className="Switch">
                    <input
                      className="SwitchInput"
                      type="checkbox"
                      checked={dbWithBackup}
                      onChange={(e) => setDbWithBackup(e.target.checked)}
                      disabled={isDbLoading}
                    />
                    <span className="SwitchTrack">
                      <span className="SwitchThumb" />
                    </span>
                  </span>
                </span>
              </label>
              <input
                className="TextInput"
                value={dbConfirm}
                onChange={(e) => setDbConfirm(e.target.value)}
                placeholder="Wpisz: RESET_STATS_CALENDAR"
                disabled={isDbLoading}
              />
              <PrimaryButton
                onClick={adminResetStatsCalendar}
                disabled={isDbLoading || dbConfirm.trim().toUpperCase() !== 'RESET_STATS_CALENDAR'}
                type="button"
              >
                Wyczyść statystyki i kalendarz
              </PrimaryButton>
            </div>
          </div>
        )}

        {isUsageOpen && (
          <div
            className="SheetOverlay"
            role="dialog"
            aria-modal="true"
            onMouseDown={(e) => {
              if (isUsageLoading) return;
              if (e.target === e.currentTarget) setIsUsageOpen(false);
            }}
          >
            <div className="Sheet">
              <div className="SheetHeaderRow SheetHeaderRow--sticky">
                <div className="SheetTitle">STATYSTYKI KORZYSTANIA</div>
                <button className="SheetCloseButton" type="button" aria-label="Zamknij" onClick={() => setIsUsageOpen(false)} disabled={isUsageLoading}>
                  ×
                </button>
              </div>
              {usageError && <div className="ErrorBanner">{usageError}</div>}
              {isUsageLoading && <div className="Muted">Ładowanie…</div>}

              <div className="SearchRow">
                <input
                  className="TextInput"
                  value={usageQuery}
                  onChange={(e) => setUsageQuery(e.target.value)}
                  placeholder="Szukaj (email/rola)"
                  disabled={isUsageLoading}
                />
                <SecondaryButton onClick={loadUsageUsers} disabled={isUsageLoading} type="button">
                  Odśwież
                </SecondaryButton>
              </div>

              <div className="List">
                {filteredUsageUsers.length === 0 ? (
                  <div className="Muted">Brak danych.</div>
                ) : (
                  filteredUsageUsers.map((u) => (
                    <div key={u.id} className="ListItem">
                      <div className="ListItemTop">
                        <div className="ListItemTitle">{u.email}</div>
                        <div className="ListItemBadge">{u.role}</div>
                      </div>
                      <div className="ListItemMeta">
                        {`Status: ${u.status} • Logowania: ${u.login_count || 0} • Ostatnie logowanie: ${
                          u.last_login_at ? formatDateTime(u.last_login_at) : '—'
                        } • Ostatnia aktywność: ${u.last_seen_at ? formatDateTime(u.last_seen_at) : '—'}`}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {isOpponentTeamsOpen && (
          <div
            className="SheetOverlay"
            role="dialog"
            aria-modal="true"
            onMouseDown={(e) => {
              if (isOpponentTeamsLoading) return;
              if (e.target === e.currentTarget) setIsOpponentTeamsOpen(false);
            }}
          >
            <div className="Sheet">
              <div className="SheetHeaderRow SheetHeaderRow--sticky">
                <div className="SheetTitle">DRUŻYNY PRZECIWNIKA</div>
                <button
                  className="SheetCloseButton"
                  type="button"
                  aria-label="Zamknij"
                  onClick={() => setIsOpponentTeamsOpen(false)}
                  disabled={isOpponentTeamsLoading}
                >
                  ×
                </button>
              </div>
              {opponentTeamsError && <div className="ErrorBanner">{opponentTeamsError}</div>}
              {isOpponentTeamsLoading && <div className="Muted">Ładowanie…</div>}

              <input
                className="TextInput"
                value={opponentTeamsQuery}
                onChange={(e) => setOpponentTeamsQuery(e.target.value)}
                placeholder="Szukaj"
                disabled={isOpponentTeamsLoading}
              />

              <div className="SearchRow">
                <input
                  className="TextInput"
                  value={newOpponentTeamName}
                  onChange={(e) => setNewOpponentTeamName(e.target.value)}
                  placeholder="Nowa drużyna"
                  disabled={isOpponentTeamsLoading}
                />
                <PrimaryButton onClick={onAddOpponentTeam} disabled={isOpponentTeamsLoading || newOpponentTeamName.trim() === ''} type="button">
                  Dodaj
                </PrimaryButton>
              </div>

              <div className="List">
                {filteredOpponentTeams.length === 0 ? (
                  <div className="Muted">Brak drużyn.</div>
                ) : (
                  filteredOpponentTeams.map((t) => (
                    <div key={t.id} className="OpponentTeamRow">
                      <div className="OpponentTeamMain">
                        <div className="OpponentTeamTitle">{t.name}</div>
                        <div className="OpponentTeamMeta">{t.logo_url ? 'Logo ustawione' : 'Brak logo'}</div>
                      </div>
                      <div className="OpponentTeamActions">
                        <button
                          className="OpponentTeamIconBtn"
                          type="button"
                          onClick={() => {
                            setEditOpponentTeam(t);
                            setEditOpponentName(t.name);
                            setEditOpponentLogoUrl(t.logo_url ?? '');
                          }}
                          disabled={isOpponentTeamsLoading}
                          aria-label="Edytuj drużynę"
                        >
                          ✎
                        </button>
                        <button
                          className="OpponentTeamIconBtn is-danger"
                          type="button"
                          onClick={() => setDeleteOpponentTeam(t)}
                          disabled={isOpponentTeamsLoading}
                          aria-label="Usuń drużynę"
                        >
                          🗑
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {editOpponentTeam && (
          <div
            className="SheetOverlay"
            role="dialog"
            aria-modal="true"
            onMouseDown={(e) => {
              if (isOpponentTeamsLoading) return;
              if (e.target === e.currentTarget) setEditOpponentTeam(null);
            }}
          >
            <div className="Sheet">
              <div className="SheetHeaderRow SheetHeaderRow--sticky">
                <div className="SheetTitle">EDYTUJ DRUŻYNĘ</div>
                <button
                  className="SheetCloseButton"
                  type="button"
                  aria-label="Zamknij"
                  onClick={() => setEditOpponentTeam(null)}
                  disabled={isOpponentTeamsLoading || isEditOpponentLogoUploading}
                >
                  ×
                </button>
              </div>
              {opponentTeamsError && <div className="ErrorBanner">{opponentTeamsError}</div>}
              <input
                className="TextInput"
                value={editOpponentName}
                onChange={(e) => setEditOpponentName(e.target.value)}
                placeholder="Nazwa"
                disabled={isOpponentTeamsLoading}
              />
              <div className="ChildPhotoRow">
                <div className="ChildPhotoPreview" aria-hidden="true">
                  {editOpponentLogoUrl.trim() ? (
                    <img className="ChildPhotoPreviewImg" src={editOpponentLogoUrl.trim()} alt="" />
                  ) : (
                    <div className="ChildPhotoPreviewFallback">•</div>
                  )}
                </div>
                <div className="ChildPhotoActions">
                  <input
                    ref={editOpponentLogoInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      e.target.value = '';
                      if (!f) return;
                      openEditOpponentLogoCrop(f);
                    }}
                    disabled={isOpponentTeamsLoading || isEditOpponentLogoUploading}
                    className="HiddenInput"
                  />
                  <SecondaryButton
                    type="button"
                    onClick={() => editOpponentLogoInputRef.current?.click()}
                    disabled={isOpponentTeamsLoading || isEditOpponentLogoUploading}
                  >
                    Ustaw logo
                  </SecondaryButton>
                  {editOpponentLogoUrl.trim() && (
                    <SecondaryButton
                      type="button"
                      onClick={clearEditOpponentLogo}
                      disabled={isOpponentTeamsLoading || isEditOpponentLogoUploading}
                    >
                      Usuń logo
                    </SecondaryButton>
                  )}
                </div>
              </div>
              <PrimaryButton onClick={onSaveOpponentTeam} disabled={isOpponentTeamsLoading || editOpponentName.trim() === ''} type="button">
                Zapisz
              </PrimaryButton>
              <SecondaryButton onClick={() => setEditOpponentTeam(null)} disabled={isOpponentTeamsLoading} type="button">
                Anuluj
              </SecondaryButton>
            </div>
          </div>
        )}

        {deleteOpponentTeam && (
          <div
            className="SheetOverlay"
            role="dialog"
            aria-modal="true"
            onMouseDown={(e) => {
              if (isOpponentTeamsLoading) return;
              if (e.target === e.currentTarget) setDeleteOpponentTeam(null);
            }}
          >
            <div className="Sheet">
              <div className="SheetHeaderRow SheetHeaderRow--sticky">
                <div className="SheetTitle">USUNĄĆ DRUŻYNĘ?</div>
                <button
                  className="SheetCloseButton"
                  type="button"
                  aria-label="Zamknij"
                  onClick={() => setDeleteOpponentTeam(null)}
                  disabled={isOpponentTeamsLoading}
                >
                  ×
                </button>
              </div>
              <div className="Muted">{deleteOpponentTeam.name}</div>
              <PrimaryButton onClick={onDeleteOpponentTeamConfirm} disabled={isOpponentTeamsLoading} type="button">
                Usuń
              </PrimaryButton>
              <SecondaryButton onClick={() => setDeleteOpponentTeam(null)} disabled={isOpponentTeamsLoading} type="button">
                Anuluj
              </SecondaryButton>
            </div>
          </div>
        )}

        {passwordSheetOpen && (
          <div
            className="SheetOverlay"
            role="dialog"
            aria-modal="true"
            onMouseDown={(e) => {
              if (isPasswordLoading) return;
              if (e.target === e.currentTarget) setPasswordSheetOpen(false);
            }}
          >
            <div className="Sheet">
              <div className="SheetHeaderRow SheetHeaderRow--sticky">
                <div className="SheetTitle">ZMIEŃ HASŁO</div>
                <button
                  className="SheetCloseButton"
                  type="button"
                  aria-label="Zamknij"
                  onClick={() => setPasswordSheetOpen(false)}
                  disabled={isPasswordLoading}
                >
                  ×
                </button>
              </div>
              {passwordError && <div className="ErrorBanner">{passwordError}</div>}
              {passwordOk && <div className="InfoBanner">Hasło zmienione.</div>}
              <div className="CoachProfileForm">
                <input
                  className="TextInput"
                  value={passwordCurrent}
                  onChange={(e) => setPasswordCurrent(e.target.value)}
                  placeholder="Obecne hasło"
                  type="password"
                  disabled={isPasswordLoading}
                />
                <input
                  className="TextInput"
                  value={passwordNext}
                  onChange={(e) => setPasswordNext(e.target.value)}
                  placeholder="Nowe hasło (min. 8 znaków)"
                  type="password"
                  disabled={isPasswordLoading}
                />
                <input
                  className="TextInput"
                  value={passwordNext2}
                  onChange={(e) => setPasswordNext2(e.target.value)}
                  placeholder="Powtórz nowe hasło"
                  type="password"
                  disabled={isPasswordLoading}
                />
              </div>
              <div className="CoachProfileActions">
                <PrimaryButton onClick={changePassword} disabled={isPasswordLoading || !canChangePassword} type="button">
                  Zmień hasło
                </PrimaryButton>
                <SecondaryButton onClick={() => setPasswordSheetOpen(false)} disabled={isPasswordLoading} type="button">
                  Anuluj
                </SecondaryButton>
              </div>
            </div>
          </div>
        )}

        {deleteUser && (
          <div
            className="SheetOverlay"
            role="dialog"
            aria-modal="true"
            onMouseDown={(e) => {
              if (isLoading) return;
              if (e.target === e.currentTarget) setDeleteUser(null);
            }}
          >
            <div className="Sheet">
              <div className="SheetHeaderRow SheetHeaderRow--sticky">
                <div className="SheetTitle">USUNĄĆ KONTO?</div>
                <button className="SheetCloseButton" type="button" aria-label="Zamknij" onClick={() => setDeleteUser(null)} disabled={isLoading}>
                  ×
                </button>
              </div>
              <div className="Muted">{deleteUser.email}</div>
              <PrimaryButton onClick={deleteUserConfirm} disabled={isLoading} type="button">
                Usuń
              </PrimaryButton>
              <SecondaryButton onClick={() => setDeleteUser(null)} disabled={isLoading} type="button">
                Anuluj
              </SecondaryButton>
            </div>
          </div>
        )}

        {editUserEmail && (
          <div
            className="SheetOverlay"
            role="dialog"
            aria-modal="true"
            onMouseDown={(e) => {
              if (isLoading) return;
              if (e.target === e.currentTarget) {
                setEditUserEmail(null);
                setEditUserEmailValue('');
                setEditUserEmailError(null);
              }
            }}
          >
            <div className="Sheet">
              <div className="SheetHeaderRow SheetHeaderRow--sticky">
                <div className="SheetTitle">ZMIEŃ EMAIL</div>
                <button
                  className="SheetCloseButton"
                  type="button"
                  aria-label="Zamknij"
                  onClick={() => {
                    setEditUserEmail(null);
                    setEditUserEmailValue('');
                    setEditUserEmailError(null);
                  }}
                  disabled={isLoading}
                >
                  ×
                </button>
              </div>
              <div className="Muted">{editUserEmail.label}</div>
              {editUserEmailError && <div className="ErrorBanner">{editUserEmailError}</div>}
              <input
                className="TextInput"
                value={editUserEmailValue}
                onChange={(e) => setEditUserEmailValue(e.target.value)}
                inputMode="email"
                autoCapitalize="none"
                autoCorrect="off"
                placeholder="Email"
                disabled={isLoading}
              />
              <PrimaryButton onClick={saveUserEmail} disabled={isLoading || editUserEmailValue.trim() === ''} type="button">
                Zapisz
              </PrimaryButton>
              <SecondaryButton
                onClick={() => {
                  setEditUserEmail(null);
                  setEditUserEmailValue('');
                  setEditUserEmailError(null);
                }}
                disabled={isLoading}
                type="button"
              >
                Anuluj
              </SecondaryButton>
            </div>
          </div>
        )}

        {resetUserPassword && (
          <div
            className="SheetOverlay"
            role="dialog"
            aria-modal="true"
            onMouseDown={(e) => {
              if (isLoading) return;
              if (e.target === e.currentTarget) {
                setResetUserPassword(null);
                setResetUserPasswordValue('');
                setResetUserPasswordDone(null);
              }
            }}
          >
            <div className="Sheet">
              <div className="SheetHeaderRow SheetHeaderRow--sticky">
                <div className="SheetTitle">RESET HASŁA</div>
                <button
                  className="SheetCloseButton"
                  type="button"
                  aria-label="Zamknij"
                  onClick={() => {
                    setResetUserPassword(null);
                    setResetUserPasswordValue('');
                    setResetUserPasswordDone(null);
                  }}
                  disabled={isLoading}
                >
                  ×
                </button>
              </div>
              <div className="Muted">{resetUserPassword.label}</div>
              {resetUserPasswordDone ? (
                <>
                  <div className="OkBanner">
                    ✅ Hasło zresetowane pomyślnie. Wszystkie istniejące sesje tego użytkownika zostały unieważnione (został wylogowany na wszystkich urządzeniach).
                  </div>
                  <div className="PasswordResetOutput" aria-label="Nowe hasło użytkownika">
                    <div className="Muted" style={{ fontSize: '13px', opacity: 0.8 }}>Nowe hasło (skopiuj i podaj użytkownikowi w bezpieczny sposób):</div>
                    <div className="PasswordResetValue" style={{ fontWeight: 700, fontSize: '18px', letterSpacing: '0.3px', padding: '10px 12px', borderRadius: '12px', background: '#FFF7ED', color: '#9A3412', border: '1px solid #FDBA74', userSelect: 'all', wordBreak: 'break-all' }}>
                      {resetUserPasswordDone.password}
                    </div>
                    <div className="Muted" style={{ marginTop: '6px', fontSize: '13px', opacity: 0.75 }}>
                      Użytkownik może to hasło zmienić później w Profilu → Zmień hasło.
                    </div>
                  </div>
                  <PrimaryButton
                    onClick={() => {
                      setResetUserPassword(null);
                      setResetUserPasswordValue('');
                      setResetUserPasswordDone(null);
                    }}
                    type="button"
                  >
                    Gotowe
                  </PrimaryButton>
                </>
              ) : (
                <>
                  <input
                    className="TextInput"
                    value={resetUserPasswordValue}
                    onChange={(e) => setResetUserPasswordValue(e.target.value)}
                    placeholder="Nowe hasło (zostaw puste → wygeneruje automatycznie, min. 8 znaków)"
                    autoCapitalize="none"
                    autoCorrect="off"
                    disabled={isLoading}
                  />
                  <PrimaryButton onClick={saveResetUserPassword} disabled={isLoading} type="button">
                    Zresetuj hasło
                  </PrimaryButton>
                  <SecondaryButton
                    onClick={() => {
                      setResetUserPassword(null);
                      setResetUserPasswordValue('');
                      setResetUserPasswordDone(null);
                    }}
                    disabled={isLoading}
                    type="button"
                  >
                    Anuluj
                  </SecondaryButton>
                </>
              )}
            </div>
          </div>
        )}

        {editChild && (
          <div
            className="SheetOverlay"
            role="dialog"
            aria-modal="true"
            onMouseDown={(e) => {
              if (isLoading) return;
              if (e.target === e.currentTarget) setEditChild(null);
            }}
          >
            <div className="Sheet">
              <div className="SheetHeaderRow SheetHeaderRow--sticky">
                <div className="SheetTitle">EDYTUJ DZIECKO</div>
                <button className="SheetCloseButton" type="button" aria-label="Zamknij" onClick={() => setEditChild(null)} disabled={isLoading}>
                  ×
                </button>
              </div>
              <input
                className="TextInput"
                value={editChild.first_name}
                onChange={(e) => setEditChild((p) => (p ? { ...p, first_name: e.target.value } : p))}
                placeholder="Imię"
                disabled={isLoading}
              />
              <input
                className="TextInput"
                value={editChild.last_initial}
                onChange={(e) => setEditChild((p) => (p ? { ...p, last_initial: e.target.value } : p))}
                placeholder="Inicjał nazwiska (np. K.)"
                disabled={isLoading}
              />
              <input
                className="TextInput"
                value={editChild.birth_year}
                onChange={(e) => setEditChild((p) => (p ? { ...p, birth_year: e.target.value } : p))}
                placeholder="Rok urodzenia (opcjonalnie)"
                inputMode="numeric"
                disabled={isLoading}
              />
              <input
                className="TextInput"
                value={editChild.shirt_number}
                onChange={(e) => setEditChild((p) => (p ? { ...p, shirt_number: e.target.value } : p))}
                placeholder="No. (opcjonalnie)"
                inputMode="numeric"
                disabled={isLoading}
              />
              {editChildPhotoError && <div className="ErrorBanner">{editChildPhotoError}</div>}
              <div className="ChildPhotoRow">
                <div className="ChildPhotoPreview" aria-hidden="true">
                  {editChildPhotoUrl ? (
                    <img className="ChildPhotoPreviewImg" src={editChildPhotoUrl} alt="" />
                  ) : (
                    <div className="ChildPhotoPreviewFallback">
                      {`${(editChild.first_name || '').slice(0, 1)}${(editChild.last_initial || '').slice(0, 1)}`.toUpperCase() || '•'}
                    </div>
                  )}
                </div>
                <div className="ChildPhotoActions">
                  <input
                    ref={editChildPhotoInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      e.target.value = '';
                      if (!f) return;
                      openEditChildPhotoCrop(f);
                    }}
                    disabled={isLoading || isEditChildPhotoUploading}
                    className="HiddenInput"
                  />
                  <SecondaryButton type="button" onClick={() => editChildPhotoInputRef.current?.click()} disabled={isLoading || isEditChildPhotoUploading}>
                    Zmień zdjęcie
                  </SecondaryButton>
                  {editChildPhotoUrl && (
                    <SecondaryButton type="button" onClick={clearEditChildPhoto} disabled={isLoading || isEditChildPhotoUploading}>
                      Usuń zdjęcie
                    </SecondaryButton>
                  )}
                </div>
              </div>
              <PrimaryButton
                onClick={updateChild}
                disabled={isLoading || editChild.first_name.trim() === '' || editChild.last_initial.trim() === ''}
                type="button"
              >
                Zapisz
              </PrimaryButton>
              <SecondaryButton onClick={() => setEditChild(null)} disabled={isLoading} type="button">
                Anuluj
              </SecondaryButton>
            </div>
          </div>
        )}

        <PhotoCropperSheet req={editChildPhotoCropReq} onClose={() => setEditChildPhotoCropReq(null)} onDone={() => setEditChildPhotoCropReq(null)} />
        <PhotoCropperSheet req={editOpponentLogoCropReq} onClose={() => setEditOpponentLogoCropReq(null)} onDone={() => setEditOpponentLogoCropReq(null)} />

        {deleteChild && (
          <div
            className="SheetOverlay"
            role="dialog"
            aria-modal="true"
            onMouseDown={(e) => {
              if (isLoading) return;
              if (e.target === e.currentTarget) setDeleteChild(null);
            }}
          >
            <div className="Sheet">
              <div className="SheetHeaderRow SheetHeaderRow--sticky">
                <div className="SheetTitle">USUNĄĆ DZIECKO?</div>
                <button className="SheetCloseButton" type="button" aria-label="Zamknij" onClick={() => setDeleteChild(null)} disabled={isLoading}>
                  ×
                </button>
              </div>
              <div className="Muted">{deleteChild.label}</div>
              <PrimaryButton onClick={deleteChildConfirm} disabled={isLoading} type="button">
                Usuń
              </PrimaryButton>
              <SecondaryButton onClick={() => setDeleteChild(null)} disabled={isLoading} type="button">
                Anuluj
              </SecondaryButton>
            </div>
          </div>
        )}

        {lnpOpen && (
          <div
            className="ModalBackdrop"
            role="dialog"
            aria-modal="true"
            onMouseDown={(e) => {
              if (lnpLoading) return;
              if (e.target === e.currentTarget) {
                setLnpOpen(false);
                setLnpStep(1);
                setLnpCsvText('');
                setLnpParsedRows([]);
                setLnpRowChecked({});
                setLnpApplySummary(null);
                setLnpError(null);
              }
            }}
          >
            <div className="Sheet LnpWizardSheet">
              <div className="SheetHeaderRow SheetHeaderRow--sticky">
                <div className="SheetTitle">🟨 IMPORT Z ŁĄCZY NAS PIŁKA</div>
                <button
                  className="SheetCloseButton"
                  type="button"
                  aria-label="Zamknij"
                  onClick={() => {
                    if (lnpLoading) return;
                    setLnpOpen(false);
                    setLnpStep(1);
                    setLnpCsvText('');
                    setLnpParsedRows([]);
                    setLnpRowChecked({});
                    setLnpApplySummary(null);
                    setLnpError(null);
                  }}
                  disabled={lnpLoading}
                >
                  ×
                </button>
              </div>
              <div className="LnpWizardSteps" aria-label="Kroki importu">
                <div className={['LnpWizardStep', lnpStep >= 1 ? 'is-active' : '', lnpStep > 1 ? 'is-done' : ''].filter(Boolean).join(' ')}>
                  <div className="LnpWizardStepNum">1</div>
                  <div className="LnpWizardStepLabel">Wklej CSV</div>
                </div>
                <div className="LnpWizardStepLine" aria-hidden="true" />
                <div className={['LnpWizardStep', lnpStep >= 2 ? 'is-active' : '', lnpStep > 2 ? 'is-done' : ''].filter(Boolean).join(' ')}>
                  <div className="LnpWizardStepNum">2</div>
                  <div className="LnpWizardStepLabel">Podgląd i wybór</div>
                </div>
                <div className="LnpWizardStepLine" aria-hidden="true" />
                <div className={['LnpWizardStep', lnpStep >= 3 ? 'is-active' : ''].filter(Boolean).join(' ')}>
                  <div className="LnpWizardStepNum">3</div>
                  <div className="LnpWizardStepLabel">Gotowe</div>
                </div>
              </div>
              {lnpStep === 1 && (
                <div style={{ padding: '4px 0 16px' }}>
                  <div style={{ fontSize: 14, lineHeight: 1.6, marginBottom: 14, color: 'var(--color-neutral-700, #444)' }}>
                    <strong style={{ color: 'var(--color-primary-700, #1a237e)' }}>Jak skopiować dane z Łączy Nas Piłka:</strong>
                    <ol style={{ margin: '8px 0 0 20px', padding: 0 }}>
                      <li>Wejdź na stronę LNP → sekcja z wynikami Twojej drużyny.</li>
                      <li>Zaznacz i skopiuj (Ctrl+C) wiersze tabeli z wynikami lub wyeksportuj do CSV.</li>
                      <li>Wklej (Ctrl+V) zawartość poniżej. Obsługiwane kolumny: <strong>data</strong>, <strong>przeciwnik</strong>, <strong>wynik</strong> (np. 3:2), <strong>dom/wyjazd</strong>, <strong>strzelcy</strong>.</li>
                    </ol>
                  </div>
                  <textarea
                    className="LnpWizardTextarea"
                    placeholder="Data;Przeciwnik;Wynik;Dom/Wyjazd;Strzelcy&#10;15.03.2026;AP Błonianka Błonie;3:2;D;Kowalski 12', Nowak 45' PK"
                    value={lnpCsvText}
                    onChange={(e) => setLnpCsvText(e.target.value)}
                    rows={12}
                    disabled={lnpLoading}
                  />
                  {lnpError && <div className="ErrorBanner" style={{ marginTop: 12 }}>{lnpError}</div>}
                  <div className="CoachProfileActions" style={{ justifyContent: 'flex-end', marginTop: 16 }}>
                    <PrimaryButton
                      type="button"
                      disabled={lnpLoading || lnpCsvText.trim() === ''}
                      onClick={async () => {
                        try {
                          setLnpLoading(true);
                          setLnpError(null);
                          const r = await apiAdminLnpParse(props.token, { csv_text: lnpCsvText, source_kind: lnpCsvText.trim().includes('\n') ? 'PASTE' : 'CSV' });
                          const rows = r?.rows || [];
                          setLnpParsedRows(rows);
                          const checked: Record<number, boolean> = {};
                          rows.forEach((row: any, idx: number) => { if (row?.status === 'MATCHED') checked[idx] = true; });
                          setLnpRowChecked(checked);
                          setLnpStep(2);
                        } catch (e: any) {
                          setLnpError(`Nie udało się sparsować danych: ${e?.message || 'sprawdź format CSV.'}`);
                        } finally {
                          setLnpLoading(false);
                        }
                      }}
                    >
                      {lnpLoading ? 'Sparsowywanie…' : 'Dalej →'}
                    </PrimaryButton>
                  </div>
                </div>
              )}
              {lnpStep === 2 && (
                <div style={{ padding: '4px 0 16px' }}>
                  <div className="LnpWizardSummary" role="group" aria-label="Podsumowanie parsowania">
                    <div className="LnpWizardSummaryItem">
                      <div className="LnpWizardSummaryNum">{lnpParsedRows.length}</div>
                      <div className="LnpWizardSummaryLabel">Wszystkie</div>
                    </div>
                    <div className="LnpWizardSummaryItem LnpWizardSummaryItem--ok">
                      <div className="LnpWizardSummaryNum">{lnpParsedRows.filter((r: any) => r?.status === 'MATCHED').length}</div>
                      <div className="LnpWizardSummaryLabel">Dopasowane</div>
                    </div>
                    <div className="LnpWizardSummaryItem LnpWizardSummaryItem--warn">
                      <div className="LnpWizardSummaryNum">{lnpParsedRows.filter((r: any) => r?.status === 'NO_MATCH').length}</div>
                      <div className="LnpWizardSummaryLabel">Bez dopasowania</div>
                    </div>
                    <div className="LnpWizardSummaryItem LnpWizardSummaryItem--danger">
                      <div className="LnpWizardSummaryNum">{lnpParsedRows.filter((r: any) => r?.status === 'DUPLICATE' || r?.status === 'ERROR').length}</div>
                      <div className="LnpWizardSummaryLabel">Pominięte</div>
                    </div>
                  </div>
                  <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <SecondaryButton
                        type="button"
                        disabled={lnpLoading}
                        onClick={() => {
                          const all: Record<number, boolean> = {};
                          lnpParsedRows.forEach((_: any, idx: number) => { all[idx] = true; });
                          setLnpRowChecked(all);
                        }}
                      >
                        Zaznacz wszystkie
                      </SecondaryButton>
                      <SecondaryButton
                        type="button"
                        disabled={lnpLoading}
                        onClick={() => {
                          const onlyMatched: Record<number, boolean> = {};
                          lnpParsedRows.forEach((row: any, idx: number) => { if (row?.status === 'MATCHED') onlyMatched[idx] = true; });
                          setLnpRowChecked(onlyMatched);
                        }}
                      >
                        Tylko dopasowane
                      </SecondaryButton>
                      <SecondaryButton type="button" disabled={lnpLoading} onClick={() => setLnpRowChecked({})}>Wyczyść</SecondaryButton>
                    </div>
                  </div>
                  <div className="AdminTableWrap" style={{ marginTop: 16 }} role="region" aria-label="Tabela wierszy do importu">
                    <table className="AdminTable">
                      <thead>
                        <tr>
                          <th style={{ width: 44 }}><input type="checkbox" className="LnpRowChk" aria-label="Zaznacz wszystkie" checked={lnpParsedRows.length > 0 && lnpParsedRows.every((_: any, idx: number) => lnpRowChecked[idx])} onChange={(e) => { const c = e.target.checked; const next: Record<number, boolean> = {}; if (c) lnpParsedRows.forEach((_: any, i: number) => next[i] = true); setLnpRowChecked(next); }} /></th>
                          <th style={{ width: 120 }}>Status</th>
                          <th style={{ width: 110 }}>Data</th>
                          <th>Przeciwnik</th>
                          <th style={{ width: 90 }}>Wynik</th>
                          <th style={{ width: 80 }}>Dom</th>
                          <th>Uwagi</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lnpParsedRows.map((row: any, idx: number) => (
                          <tr key={idx} className={lnpRowChecked[idx] ? 'is-selected' : ''}>
                            <td><input type="checkbox" className="LnpRowChk" checked={!!lnpRowChecked[idx]} aria-label={`Zaznacz wiersz ${idx + 1}`} onChange={(e) => setLnpRowChecked((prev) => ({ ...prev, [idx]: e.target.checked }))} disabled={lnpLoading || row?.status === 'ERROR'} /></td>
                            <td><span className={lnpStatusBadge(row?.status).cls}>{lnpStatusBadge(row?.status).txt}</span></td>
                            <td className="Muted">{row?.date_iso ? new Date(row.date_iso).toLocaleDateString('pl-PL') : (row?.raw_date || '—')}</td>
                            <td style={{ fontWeight: 600 }}>{row?.opponent || row?.raw_opponent || '—'}</td>
                            <td style={{ fontWeight: 800 }}>{row?.home_score ?? '?'}:{row?.away_score ?? '?'}</td>
                            <td>{row?.is_home ? '🏠 Dom' : '✈️ Wyjazd'}</td>
                            <td className="Muted" style={{ fontSize: 12 }}>{row?.note || (row?.match_event_id ? `#${row.match_event_id}` : '')}</td>
                          </tr>
                        ))}
                        {lnpParsedRows.length === 0 && (
                          <tr><td colSpan={7} className="Muted" style={{ textAlign: 'center', padding: 24 }}>Brak danych do podglądu.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  {lnpError && <div className="ErrorBanner" style={{ marginTop: 12 }}>{lnpError}</div>}
                  <div className="CoachProfileActions" style={{ justifyContent: 'space-between', marginTop: 18 }}>
                    <SecondaryButton type="button" disabled={lnpLoading} onClick={() => { setLnpStep(1); setLnpError(null); }}>← Wstecz</SecondaryButton>
                    <PrimaryButton
                      type="button"
                      disabled={lnpLoading || Object.keys(lnpRowChecked).filter((k) => lnpRowChecked[Number(k)]).length === 0}
                      onClick={async () => {
                        try {
                          setLnpLoading(true);
                          setLnpError(null);
                          const selectedIds = Object.keys(lnpRowChecked).filter((k) => lnpRowChecked[Number(k)]).map((k) => Number(k));
                          const selectedRows = selectedIds.map((i) => lnpParsedRows[i]).filter(Boolean);
                          const r = await apiAdminLnpApply(props.token, { row_ids: selectedIds, rows: selectedRows, source_kind: 'PASTE', source_text: lnpCsvText });
                          setLnpApplySummary(r);
                          setLnpStep(3);
                        } catch (e: any) {
                          setLnpError(`Nie udało się zaimportować: ${e?.message || 'spróbuj ponownie.'}`);
                        } finally {
                          setLnpLoading(false);
                        }
                      }}
                    >
                      {lnpLoading ? 'Importowanie…' : `✅ Zaimportuj zaznaczone (${Object.keys(lnpRowChecked).filter((k) => lnpRowChecked[Number(k)]).length})`}
                    </PrimaryButton>
                  </div>
                </div>
              )}
              {lnpStep === 3 && (
                <div style={{ padding: '20px 0 16px', textAlign: 'center' }}>
                  <div style={{ fontSize: 56, marginBottom: 10 }}>✅</div>
                  <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 6, color: 'var(--color-success-700, #1b5e20)' }}>Import zakończony sukcesem</div>
                  {lnpApplySummary && (
                    <div className="LnpWizardSummary" style={{ marginTop: 18, justifyContent: 'center' }}>
                      <div className="LnpWizardSummaryItem">
                        <div className="LnpWizardSummaryNum">{lnpApplySummary.summary?.row_count ?? 0}</div>
                        <div className="LnpWizardSummaryLabel">Wierszy</div>
                      </div>
                      <div className="LnpWizardSummaryItem LnpWizardSummaryItem--ok">
                        <div className="LnpWizardSummaryNum">{lnpApplySummary.summary?.created_count ?? 0}</div>
                        <div className="LnpWizardSummaryLabel">Zaktualizowano</div>
                      </div>
                      <div className="LnpWizardSummaryItem LnpWizardSummaryItem--warn">
                        <div className="LnpWizardSummaryNum">{lnpApplySummary.summary?.matched_count ?? 0}</div>
                        <div className="LnpWizardSummaryLabel">Dopasowano</div>
                      </div>
                      <div className="LnpWizardSummaryItem LnpWizardSummaryItem--danger">
                        <div className="LnpWizardSummaryNum">{lnpApplySummary.summary?.skipped_count ?? 0}</div>
                        <div className="LnpWizardSummaryLabel">Pominięto</div>
                      </div>
                    </div>
                  )}
                  <div className="CoachProfileActions" style={{ justifyContent: 'center', marginTop: 22, gap: 12 }}>
                    <SecondaryButton
                      type="button"
                      onClick={() => {
                        setLnpStep(1);
                        setLnpCsvText('');
                        setLnpParsedRows([]);
                        setLnpRowChecked({});
                        setLnpApplySummary(null);
                        setLnpError(null);
                      }}
                    >
                      🔄 Nowy import
                    </SecondaryButton>
                    <PrimaryButton
                      type="button"
                      onClick={() => {
                        setLnpOpen(false);
                        setLnpStep(1);
                        setLnpCsvText('');
                        setLnpParsedRows([]);
                        setLnpRowChecked({});
                        setLnpApplySummary(null);
                        setLnpError(null);
                        window.location.reload();
                      }}
                    >
                      Zamknij i odśwież
                    </PrimaryButton>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </>
    );
  }

  if (props.me.role === 'PARENT') {
    return (
      <div className="Page ParentProfilePage">
        <section className="CoachProfileHero" aria-label="Profil rodzica">
          <div className="CoachProfileHeroTop">
            <div className="CoachProfileAvatar" aria-hidden="true">
              {coachInitials}
            </div>
            <div className="CoachProfileHeroMain">
              <div className="CoachProfileName">{coachName}</div>
              <div className="CoachProfileRole">{parentSubtitle}</div>
              <div className="CoachProfileStatusRow">
                <span className={['CoachProfileStatusDot', props.me.status === 'ACTIVE' ? 'is-active' : ''].filter(Boolean).join(' ')} />
                <span className="CoachProfileStatusLabel">{props.me.status}</span>
              </div>
            </div>
            <button className="CoachProfileGear" type="button" onClick={() => setCoachEditOpen((v) => !v)} aria-label="Ustawienia profilu">
              ⚙
            </button>
          </div>
          <div className="CoachProfileHeroBottom">
            <div className="CoachProfileHeroHint">Zarządzaj kontem i profilami swoich dzieci</div>
            <button className="CoachProfileEditBtn" type="button" onClick={() => setCoachEditOpen((v) => !v)} disabled={isProfileLoading}>
              ✎ EDYCJA
            </button>
          </div>
        </section>

        {coachEditOpen && (
          <section className="CoachProfileSection" aria-label="Edycja profilu">
            {profileError && <div className="ErrorBanner">{profileError}</div>}
            <div className="CoachProfileSectionTitle">EDYCJA</div>
            {profile?.email && (
              <div className="CoachProfileRow">
                <div className="CoachProfileRowKey">E-mail / Login</div>
                <div className="CoachProfileRowVal">{profile.email}</div>
              </div>
            )}
            <div className="CoachProfileForm">
              <input
                className="TextInput"
                value={profileDisplayName}
                onChange={(e) => setProfileDisplayName(e.target.value)}
                placeholder="Imię i nazwisko"
                disabled={isProfileLoading}
              />
              <input
                className="TextInput"
                value={profileNickname}
                onChange={(e) => setProfileNickname(e.target.value)}
                placeholder="Nick / opis"
                disabled={isProfileLoading}
              />
              <div className="CoachProfileActions">
                <PrimaryButton onClick={saveProfile} disabled={isProfileLoading} type="button">
                  Zapisz
                </PrimaryButton>
                <SecondaryButton onClick={() => setCoachEditOpen(false)} disabled={isProfileLoading} type="button">
                  Zamknij
                </SecondaryButton>
              </div>
            </div>
          </section>
        )}

        {roleSwitchSection}

        <section className="CoachProfileSection" aria-label="Moje dzieci">
          <div className="CoachProfileSectionTitle">MOJE DZIECI</div>
          {props.children.length === 0 ? (
            <div className="Muted">Brak przypisanego dziecka.</div>
          ) : (
            <div className="ParentChildrenList">
              {props.children.map((c) => {
                const isActive = c.id === props.selectedChildId;
                const initials = `${(c.first_name ?? '').trim().slice(0, 1)}${(c.last_initial ?? '').trim().slice(0, 1)}`.toUpperCase() || 'D';
                const group = (c.group_name ?? '').trim();
                const shirt = Number.isFinite(c.shirt_number as number) ? String(c.shirt_number) : '—';
                const unavailableKind = (c.unavailability_kind ?? '').trim();
                const unavailableLabel = unavailableKind === 'KONTUZJA' ? 'Kontuzja' : unavailableKind ? 'NB' : '';
                return (
                  <div
                    key={c.id}
                    className={['ParentChildCard', isActive ? 'is-active' : '', unavailableKind ? 'is-unavailable' : ''].filter(Boolean).join(' ')}
                    role="button"
                    tabIndex={0}
                    onClick={() => props.onSelectChildId(c.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') props.onSelectChildId(c.id);
                    }}
                  >
                    <div className="ParentChildTop">
                      <div className="ParentChildAvatar" aria-hidden="true">
                        {initials}
                      </div>
                      <div className="ParentChildMain">
                        <div className="ParentChildName">
                          {c.first_name} {c.last_initial}
                          {group ? ` (${group})` : ''}
                        </div>
                        <div className="ParentChildMeta">
                          {`Nr ${shirt}`}
                          {unavailableLabel ? ` • ${unavailableLabel}` : ''}
                        </div>
                      </div>
                      {isActive && (
                        <div className="ParentChildCheck" aria-hidden="true">
                          <CheckIcon />
                        </div>
                      )}
                    </div>
                    <div className="ParentChildActions">
                      <button
                        className="ParentChildLinkBtn"
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          props.onSelectChildId(c.id);
                          navigate('druzyna');
                        }}
                      >
                        Zobacz Profil Dziecka ›
                      </button>
                      <button
                        className="ParentChildNbBtn"
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          props.onSelectChildId(c.id);
                          navigate('kalendarz', { abs: 'LONG', c: c.id });
                        }}
                        disabled={props.me.status !== 'ACTIVE'}
                      >
                        Zgłoś NB/Kontuzję
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <div className="CoachProfileActions">
            <SecondaryButton onClick={props.onChangeChild} type="button" disabled={props.children.length === 0}>
              Zmień wybrane dziecko
            </SecondaryButton>
          </div>
        </section>

        <section className="CoachProfileSection" aria-label="Moje konto">
          <div className="CoachProfileSectionTitle">MOJE KONTO</div>
          <div className="CoachProfileManageRows">
            <div className="CoachProfileManageRow">
              <div className="CoachProfileManageLeft">
                <div className="CoachProfileManageLabel">E-mail / Login:</div>
                <div className="CoachProfileManageValue">{profile?.email ?? '—'}</div>
              </div>
              <div className="CoachProfileManageRight">
                <span className="CoachProfileInlineIcon" aria-hidden="true">
                  🔒
                </span>
                <button
                  className="CoachProfileInlineBtn"
                  type="button"
                  onClick={() => setPasswordSheetOpen(true)}
                  disabled={isPasswordLoading || props.me.status !== 'ACTIVE'}
                >
                  🔑 Zmień hasło
                </button>
              </div>
            </div>
            <div className="CoachProfileManageRow">
              <div className="CoachProfileManageLeft">
                <div className="CoachProfileManageLabel">Nick / Opis:</div>
                <div className="CoachProfileManageValue">{coachNicknameLabel}</div>
              </div>
              <div className="CoachProfileManageRight">
                <button
                  className="CoachProfileInlineIconBtn"
                  type="button"
                  onClick={() => setCoachEditOpen(true)}
                  aria-label="Edytuj nick / opis"
                  disabled={isProfileLoading}
                >
                  ✎
                </button>
              </div>
            </div>
          </div>
          <div className="Muted">Zmianę emaila może wykonać tylko Admin.</div>
        </section>

        <section className="CoachProfileSection" aria-label="Ustawienia powiadomień i akcje">
          <div className="CoachProfileSectionTitle">USTAWIENIA POWIADOMIEŃ & AKCJE</div>
          {isIOS && iosVersion > 0 && iosVersion < 16.4 && (
            <div className="WarningBanner" role="note" style={{ padding: '12px 14px', borderRadius: '8px', background: '#fff1f0', border: '1px solid #ffa39e', margin: '12px 0' }}>
              <div style={{ fontWeight: 600, marginBottom: '6px' }}>🔴 Wersja iOS poniżej 16.4</div>
              <div style={{ lineHeight: 1.55 }}>Wersja iOS <strong>{String(iosVersion)}</strong> nie wspiera powiadomień Web Push. Zaktualizuj iPhone do co najmniej <strong>iOS 16.4</strong>: <em>Ustawienia → Ogólne → Aktualizacja oprogramowania</em>.</div>
            </div>
          )}
          {isIOS && iosVersion >= 16.4 && !isIOSStandalone && pushPermission !== 'granted' && (
            <div className="WarningBanner" role="note" style={{ padding: '12px 14px', borderRadius: '8px', background: '#fffbe6', border: '1px solid #ffe58f', margin: '12px 0' }}>
              <div style={{ fontWeight: 600, marginBottom: '8px' }}>🟡 Dodaj FootDesk do ekranu głównego, aby włączyć powiadomienia iOS PWA</div>
              <ol style={{ margin: 0, paddingLeft: '20px', lineHeight: 1.7 }}>
                <li>🔗 <strong>Udostępnij</strong>: Stuknij ikonkę udostępniania ↗️ w pasku Safari (dół ekranu).</li>
                <li>➕ <strong>Dodaj do ekranu głównego</strong>: Przewiń w dół menu i wybierz „Dodaj do ekranu głównego”.</li>
                <li>✏️ <strong>Nazwij</strong>: Wpisz „FootDesk” jako nazwę, stuknij „Dodaj” w prawym górnym rogu.</li>
                <li>🏠 <strong>Otwórz z ikonki</strong>: Zamknij Safari, otwórz FootDesk z ikony na ekranie głównym iPhone.</li>
                <li>🔔 <strong>Włącz powiadomienia</strong>: Przejdź do Profilu → Ustawienia → stuknij „Włącz powiadomienia” poniżej. Zezwól w oknie dialogowym Safari.</li>
              </ol>
              <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
                <SecondaryButton onClick={() => window.location.reload()} disabled={isPushLoading} type="button">
                  🔄 SPRAWDŹ PONOWNIE TRYB STANDALONE
                </SecondaryButton>
              </div>
            </div>
          )}
          {isIOS && pushPermission === 'denied' && (
            <div className="WarningBanner" role="note" style={{ padding: '12px 14px', borderRadius: '8px', background: '#fff1f0', border: '1px solid #ffa39e', margin: '12px 0' }}>
              <div style={{ fontWeight: 600, marginBottom: '6px' }}>🔴 Powiadomienia są wyłączone w systemie iOS</div>
              <div style={{ lineHeight: 1.55 }}>Otwórz: <strong>Ustawienia → FootDesk → Powiadomienia</strong> i przełącz <strong>„Zezwalaj na powiadomienia”</strong> na WŁ. Następnie zamknij i otwórz ponownie aplikację FootDesk z ikony na ekranie głównym.</div>
            </div>
          )}
          {!pushSupport.supported ? (
            <div className="Muted">{pushSupport.reason || 'Ta przeglądarka nie obsługuje Web Push.'}</div>
          ) : (
            <>
              {pushError && <div className="ErrorBanner">{pushError}</div>}
              <div className="CoachProfileRow">
                <div className="CoachProfileRowKey">Powiadomienia Web Push</div>
                <div className="CoachProfileRowVal">
                  <label className="Switch" aria-label="Powiadomienia Web Push">
                    <input
                      className="SwitchInput"
                      type="checkbox"
                      checked={pushEnabled}
                      onChange={(e) => {
                        if (isPushLoading) return;
                        if (e.target.checked) enablePush();
                        else disablePush();
                      }}
                      disabled={isPushLoading}
                    />
                    <span className="SwitchTrack" aria-hidden="true">
                      <span className="SwitchThumb" aria-hidden="true" />
                    </span>
                  </label>
                </div>
              </div>
              <div className="CoachProfileRow">
                <div className="CoachProfileRowKey">Zgoda przeglądarki:</div>
                <div className="CoachProfileRowVal">
                  {pushPermission === 'granted' ? (
                    <span className="Badge Badge--ok" role="status">WŁĄCZONE</span>
                  ) : pushPermission === 'denied' ? (
                    <span className="Badge Badge--warn" role="alert">ZABLOKOWANE</span>
                  ) : (
                    <PrimaryButton onClick={enablePush} disabled={isPushLoading} type="button">
                      Włącz powiadomienia
                    </PrimaryButton>
                  )}
                </div>
              </div>
              {pushPermission === 'denied' && (
                <div className="WarningBanner" role="note" style={{ padding: '12px 14px', borderRadius: '8px', background: '#fff7e6', border: '1px solid #ffe1a8', margin: '12px 0' }}>
                  <div style={{ fontWeight: 600, marginBottom: '8px' }}>⚠️ Powiadomienia są zablokowane w tej przeglądarce. Włącz je ręcznie:</div>
                  {isAndroidUa ? (
                    <ol style={{ margin: 0, paddingLeft: '20px', lineHeight: 1.6 }}>
                      <li>Kliknij ikonę 🔒 obok adresu URL w pasku przeglądarki.</li>
                      <li>Wybierz „Powiadomienia” → przełącz na „Włączone” / „Dozwolone”.</li>
                      <li>W Ustawieniach systemu: Aplikacje → Chrome → Powiadomienia → włącz.</li>
                      <li>Zrestartuj przeglądarkę (zamknij i otwórz ponownie) i odśwież aplikację.</li>
                      <li><strong>Krok 5 (ANDROID):</strong> Jeśli kroki 1-4 nie pomogły → <em>Kliknij kłódkę 🔒 na pasku adresu → Uprawnienia → Powiadomienia → WŁĄCZ</em>, a następnie <strong>ZAMKNIJ i Otwórz ponownie kartę</strong> lub zrestartuj Chrome.</li>
                    </ol>
                  ) : isIOS ? (
                    <div className="Muted">Zobacz instrukcję iOS wyżej 👆 (Ustawienia → FootDesk → Powiadomienia → Zezwalaj na powiadomienia → WŁ, a następnie zamknij i otwórz ponownie aplikację z ikony na ekranie głównym).</div>
                  ) : (
                    <ol style={{ margin: 0, paddingLeft: '20px', lineHeight: 1.6 }}>
                      <li>Kliknij ikonę 🔒 / 🔵 obok adresu URL w pasku przeglądarki.</li>
                      <li>Wybierz „Powiadomienia” → przełącz na „Włączone” / „Dozwolone”.</li>
                      <li>Odśwież kartę aplikacji (F5 lub zamknij i otwórz ponownie).</li>
                    </ol>
                  )}
                  <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
                    <SecondaryButton onClick={() => void recheckPushPermission()} disabled={isPushLoading} type="button">
                      🔄 Sprawdź ponownie uprawnienia
                    </SecondaryButton>
                  </div>
                </div>
              )}
              {prefsError && <div className="ErrorBanner">{prefsError}</div>}
              {prefsLoading && <div className="Muted">Ładowanie ustawień powiadomień…</div>}
              {!prefsLoading && notificationPrefs && (
                <>
                  <div className="CoachProfileQuietRow" aria-label="Godziny ciszy">
                    <div className="CoachProfileRowKey">Godziny ciszy:</div>
                    <div className="CoachProfileQuietInputs" style={{ alignItems: 'center', gap: '8px' }}>
                      <select
                        className="TextInput"
                        style={{ maxWidth: '90px' }}
                        value={String(notificationPrefs.silent_from)}
                        onChange={(e) => {
                          const v = parseInt(e.target.value, 10);
                          setNotificationPrefs({ ...notificationPrefs, silent_from: Number.isFinite(v) ? v : 22 });
                        }}
                      >
                        {Array.from({ length: 24 }).map((_, h) => (
                          <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>
                        ))}
                      </select>
                      <span className="CoachProfileQuietDash" aria-hidden="true">–</span>
                      <select
                        className="TextInput"
                        style={{ maxWidth: '90px' }}
                        value={String(notificationPrefs.silent_until)}
                        onChange={(e) => {
                          const v = parseInt(e.target.value, 10);
                          setNotificationPrefs({ ...notificationPrefs, silent_until: Number.isFinite(v) ? v : 6 });
                        }}
                      >
                        {Array.from({ length: 24 }).map((_, h) => (
                          <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>
                        ))}
                      </select>
                      <label className="CoachProfileChk" style={{ marginLeft: '8px', whiteSpace: 'nowrap' }}>
                        <input
                          type="checkbox"
                          checked={notificationPrefs.silent_from === notificationPrefs.silent_until}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setNotificationPrefs({ ...notificationPrefs, silent_from: notificationPrefs.silent_until });
                            }
                          }}
                        />
                        <span>Wyłącz</span>
                      </label>
                    </div>
                  </div>
                  <div className="CoachProfileNotifGrid" aria-label="Rodzaje powiadomień">
                    {(['match_rsvp', 'match_tbd', 'attendance_report_ready', 'lounge', 'lounge_mention', 'direct_message', 'infeed_important', 'match_cancelled', 'match_critical_change'] as const).map((k) => {
                      const labels: Record<string, string> = {
                        match_rsvp: 'Powołania: Potwierdź udział',
                        match_tbd: 'Mecze: Do ustalenia (TBD)',
                        attendance_report_ready: 'Raporty: Frekwencja gotowa',
                        lounge: 'Czat drużyny: nowe wiadomości',
                        lounge_mention: 'Czat drużyny: wzmianki @Mnie',
                        direct_message: 'Prywatne wiadomości',
                        infeed_important: 'Feed: Ważne ogłoszenia',
                        match_cancelled: 'Mecze: Odwołane',
                        match_critical_change: 'Mecze: Krytyczne zmiany',
                      };
                      const disabled = false;
                      const title = '';
                      return (
                        <label key={k} className="CoachProfileChk" title={title} style={{ opacity: 1 }}>
                          <input
                            type="checkbox"
                            checked={!!notificationPrefs.channels?.[k]}
                            disabled={disabled}
                            onChange={(e) => {
                              setNotificationPrefs({
                                ...notificationPrefs,
                                channels: { ...(notificationPrefs.channels || {}), [k]: e.target.checked },
                              });
                            }}
                          />
                          <span>{labels[k] ?? k}</span>
                        </label>
                      );
                    })}
                  </div>
                  <div className="CoachProfileActions">
                    <PrimaryButton onClick={saveNotificationPrefs} disabled={savingPrefs || prefsLoading} type="button">
                      {savingPrefs ? 'Zapisywanie…' : 'Zapisz ustawienia'}
                    </PrimaryButton>
                  </div>
                </>
              )}
              <div className="CoachProfileActions">
                <button
                  className="CoachProfilePrimaryAction"
                  type="button"
                  onClick={() => {
                    navigate('kalendarz');
                  }}
                >
                  Zarządzaj powołaniami ›
                </button>
                <SecondaryButton onClick={refreshPush} disabled={isPushLoading} type="button">
                  Odśwież
                </SecondaryButton>
              </div>
            </>
          )}
        </section>

        <section className="CoachProfileSection" aria-label="Inne i RODO">
          <div className="CoachProfileSectionTitle">INNE I RODO</div>
          <div className="CoachProfileActions">
            <button className="ParentSecondaryTile" type="button" disabled>
              Polityka danych i audyt
            </button>
            <button className="ParentSecondaryTile" type="button" disabled>
              Pokaż dojazd i stadion
            </button>
          </div>
          <div className="CoachProfileActions">
            <SecondaryButton onClick={props.onLogout} type="button">
              Wyloguj
            </SecondaryButton>
          </div>
        </section>

        {passwordSheetOpen && (
          <div
            className="SheetOverlay"
            role="dialog"
            aria-modal="true"
            onMouseDown={(e) => {
              if (isPasswordLoading) return;
              if (e.target === e.currentTarget) setPasswordSheetOpen(false);
            }}
          >
            <div className="Sheet">
              <div className="SheetHeaderRow SheetHeaderRow--sticky">
                <div className="SheetTitle">ZMIEŃ HASŁO</div>
                <button
                  className="SheetCloseButton"
                  type="button"
                  aria-label="Zamknij"
                  onClick={() => setPasswordSheetOpen(false)}
                  disabled={isPasswordLoading}
                >
                  ×
                </button>
              </div>
              {passwordError && <div className="ErrorBanner">{passwordError}</div>}
              {passwordOk && <div className="InfoBanner">Hasło zmienione.</div>}
              <div className="CoachProfileForm">
                <input
                  className="TextInput"
                  value={passwordCurrent}
                  onChange={(e) => setPasswordCurrent(e.target.value)}
                  placeholder="Obecne hasło"
                  type="password"
                  disabled={isPasswordLoading}
                />
                <input
                  className="TextInput"
                  value={passwordNext}
                  onChange={(e) => setPasswordNext(e.target.value)}
                  placeholder="Nowe hasło (min. 8 znaków)"
                  type="password"
                  disabled={isPasswordLoading}
                />
                <input
                  className="TextInput"
                  value={passwordNext2}
                  onChange={(e) => setPasswordNext2(e.target.value)}
                  placeholder="Powtórz nowe hasło"
                  type="password"
                  disabled={isPasswordLoading}
                />
              </div>
              <div className="CoachProfileActions">
                <PrimaryButton onClick={changePassword} disabled={isPasswordLoading || !canChangePassword} type="button">
                  Zmień hasło
                </PrimaryButton>
                <SecondaryButton onClick={() => setPasswordSheetOpen(false)} disabled={isPasswordLoading} type="button">
                  Anuluj
                </SecondaryButton>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="Page">
      {props.me.role === 'PLAYER' && props.selectedChild && (
        <Card title="Mój Profil Zawodnika">
          <div className="KeyValue">
            <div className="Key">Zawodnik</div>
            <div className="Value">{childLabel(props.selectedChild)}</div>
          </div>
          <div className="KeyValue">
            <div className="Key">Grupa</div>
            <div className="Value">
              {props.selectedChild.group_name
                ? `${props.selectedChild.group_name}${props.selectedChild.group_year ? ` (${props.selectedChild.group_year})` : ''}`
                : 'Brak grupy'}
            </div>
          </div>
          <div className="KeyValue">
            <div className="Key">Nr na koszulce</div>
            <div className="Value">{Number.isFinite(props.selectedChild.shirt_number as number) ? props.selectedChild.shirt_number : '—'}</div>
          </div>
          <div className="Muted">To konto działa w kontekście jednego przypisanego zawodnika.</div>
        </Card>
      )}

      <Card title="Moje konto i raporty">
        <div className="KeyValue">
          <div className="Key">Tryb konta</div>
          <div className="Value">{roleLabelPl(props.me.role)}</div>
        </div>
        <div className="KeyValue">
          <div className="Key">Dostępy</div>
          <div className="Value">{props.me.roles.map((r) => roleLabelPl(r)).join(', ')}</div>
        </div>
        <div className="KeyValue">
          <div className="Key">Status</div>
          <div className="Value">{props.me.status}</div>
        </div>
        {roleSwitchSection}
        <div style={{ display: 'flex', gap: 8, margin: '14px 0 8px' }}>
          <button
            className={userReportsTab === 'panel' ? 'PrimaryButton' : 'SecondaryButton'}
            type="button"
            onClick={() => {
              setUserReportsTab('panel');
              navigate('profil');
            }}
          >
            Panel
          </button>
          <button
            className={userReportsTab === 'raporty' ? 'PrimaryButton' : 'SecondaryButton'}
            type="button"
            onClick={() => {
              setUserReportsTab('raporty');
              navigate('profil', { tab: 'raporty' });
            }}
          >
            Raporty
          </button>
        </div>
        {userReportsTab === 'raporty' && (
          <>
            <div className="KeyValue">
              <div className="Key">Dla grupy</div>
              <div className="Value">
                {userReportAllowedGroup
                  ? userReportAllowedGroup
                  : 'Wszystkie (dostęp administratora / koordynatora)'}
              </div>
            </div>
            {isUserReportsLoading ? (
              <div className="Muted">Ładowanie listy raportów…</div>
            ) : userReportsError ? (
              <div className="ErrorBanner">{userReportsError}</div>
            ) : userReports.length === 0 ? (
              <div className="InfoBanner">Brak dostępnych raportów miesięcznych. Raporty pojawiają się automatycznie 1 dnia każdego miesiąca dla poprzedniego okresu.</div>
            ) : (
              <div className="List">
                {userReports.map((r) => {
                  const groupName = (() => {
                    try {
                      if (!r.group_id) return '';
                      if (isAdminLike) {
                        const found = groups.find((g) => g.id === r.group_id);
                        if (found) return found.name;
                      }
                      if (props.me.role === 'PARENT' || props.me.role === 'PLAYER') {
                        if (props.selectedChild && props.selectedChild.group_id === r.group_id) {
                          return props.selectedChild.group_name
                            ? `${props.selectedChild.group_name}${props.selectedChild.group_year ? ` (${props.selectedChild.group_year})` : ''}`
                            : r.group_id;
                        }
                      }
                      return r.group_id;
                    } catch {
                      return r.group_id;
                    }
                  })();
                  const rangeLabel =
                    r.range === 'month'
                      ? `miesięczny · ${String(r.month).padStart(2, '0')}/${r.year}`
                      : r.range === 'quarter'
                      ? `kwartalny · ${r.year}`
                      : r.range === 'halfyear'
                      ? `półroczny · ${r.year}`
                      : `${r.year}`;
                  const isDownloading = downloadingReportId === r.id;
                  return (
                    <div key={r.id} className="ListItem">
                      <div className="ListItemTop">
                        <div className="ListItemTitle">
                          Raport frekwencji · {rangeLabel}
                        </div>
                      </div>
                      <div className="ListItemMeta">
                        {groupName ? `${groupName} · ` : ''}
                        Utworzono: {new Date(r.created_at).toLocaleString('pl-PL')}
                      </div>
                      <div className="NotificationActions">
                        <PrimaryButton
                          onClick={() => void downloadUserReport(r)}
                          disabled={isDownloading}
                          type="button"
                        >
                          {isDownloading ? 'Pobieranie…' : 'Pobierz PDF'}
                        </PrimaryButton>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <div style={{ marginTop: 12 }}>
              <SecondaryButton
                onClick={() => void loadUserReports()}
                disabled={isUserReportsLoading}
                type="button"
              >
                Odśwież listę
              </SecondaryButton>
            </div>
          </>
        )}
        {userReportsTab === 'panel' && (
          <>
            {profileError && <div className="ErrorBanner">{profileError}</div>}
            {profile?.email && (
              <div className="KeyValue">
                <div className="Key">Email</div>
                <div className="Value">{profile.email}</div>
              </div>
            )}
            <input
              className="TextInput"
              value={profileDisplayName}
              onChange={(e) => setProfileDisplayName(e.target.value)}
              placeholder="Nazwa (np. Jan Kowalski)"
              disabled={isProfileLoading}
            />
            <input
              className="TextInput"
              value={profileNickname}
              onChange={(e) => setProfileNickname(e.target.value)}
              placeholder="Nick (np. trenerJ)"
              disabled={isProfileLoading}
            />
            <PrimaryButton onClick={saveProfile} disabled={isProfileLoading} type="button">
              Zapisz
            </PrimaryButton>
          </>
        )}
      </Card>

      <Card title="Bezpieczeństwo">
        {passwordError && <div className="ErrorBanner">{passwordError}</div>}
        {passwordOk && <div className="InfoBanner">Hasło zmienione.</div>}
        <input
          className="TextInput"
          value={passwordCurrent}
          onChange={(e) => setPasswordCurrent(e.target.value)}
          placeholder="Obecne hasło"
          type="password"
          disabled={isPasswordLoading}
        />
        <input
          className="TextInput"
          value={passwordNext}
          onChange={(e) => setPasswordNext(e.target.value)}
          placeholder="Nowe hasło (min. 8 znaków)"
          type="password"
          disabled={isPasswordLoading}
        />
        <input
          className="TextInput"
          value={passwordNext2}
          onChange={(e) => setPasswordNext2(e.target.value)}
          placeholder="Powtórz nowe hasło"
          type="password"
          disabled={isPasswordLoading}
        />
        <PrimaryButton onClick={changePassword} disabled={!canChangePassword} type="button">
          Zmień hasło
        </PrimaryButton>
        <div className="Muted">Zmianę emaila może wykonać tylko Admin.</div>
      </Card>

      <Card title="Powiadomienia push">
        {!pushSupport.supported ? (
          <div className="Muted">{pushSupport.reason || 'Ta przeglądarka nie obsługuje Web Push.'}</div>
        ) : (
          <>
            {pushError && <div className="ErrorBanner">{pushError}</div>}
            {isPushLoading && <div className="Muted">Sprawdzanie…</div>}
            <div className="KeyValue">
              <div className="Key">Uprawnienie</div>
              <div className="Value">{pushPermission}</div>
            </div>
            <div className="KeyValue">
              <div className="Key">Status</div>
              <div className="Value">{pushEnabled ? 'Włączone' : 'Wyłączone'}</div>
            </div>
            {pushEnabled && pushEndpoint && <div className="Muted">{pushEndpoint}</div>}
            {pushEnabled ? (
              <PrimaryButton onClick={disablePush} disabled={isPushLoading} type="button">
                Wyłącz
              </PrimaryButton>
            ) : (
              <PrimaryButton onClick={enablePush} disabled={isPushLoading} type="button">
                Włącz
              </PrimaryButton>
            )}
            <SecondaryButton onClick={refreshPush} disabled={isPushLoading} type="button">
              Odśwież
            </SecondaryButton>
            <SecondaryButton onClick={sendTestPush} disabled={isPushLoading || !pushEnabled} type="button">
              Wyślij test
            </SecondaryButton>
            {IS_DEV && isAndroidUa && (
              <SecondaryButton onClick={downloadMkcertRootCa} disabled={isPushLoading} type="button">
                Pobierz certyfikat CA
              </SecondaryButton>
            )}
          </>
        )}
      </Card>

      <Card title="Regulamin RODO">
        {rodoError && <div className="ErrorBanner">{rodoError}</div>}
        {rodoLoading && <div className="Muted">Ładowanie regulaminu…</div>}
        {!rodoLoading && rodoAcceptance && (
          <>
            <div className="KeyValue">
              <div className="Key">Wersja regulaminu</div>
              <div className="Value">{rodoAcceptance.policy.version} ({rodoAcceptance.policy.published_at})</div>
            </div>
            <div className="KeyValue">
              <div className="Key">Status akceptacji</div>
              <div className="Value">
                {rodoAcceptance.accepted_latest ? 'Zaakceptowano najnowszą wersję' : 'Brak akceptacji najnowszej wersji'}
              </div>
            </div>
            {rodoAcceptance.accepted_at && (
              <div className="KeyValue">
                <div className="Key">Data akceptacji</div>
                <div className="Value">{new Date(rodoAcceptance.accepted_at).toLocaleString('pl-PL')}</div>
              </div>
            )}
            {rodoAcceptance.accepted_version && rodoAcceptance.accepted_version !== rodoAcceptance.latest_version && (
              <div className="InfoBanner">
                Zaakceptowałeś wersję {rodoAcceptance.accepted_version}. Najnowsza wersja to {rodoAcceptance.latest_version}.
              </div>
            )}
            <PrimaryButton
              onClick={() => {
                setRodoSheetAccepted(rodoAcceptance.accepted_latest);
                setRodoSheetOpen(true);
              }}
              type="button"
            >
              Przeczytaj regulamin RODO
            </PrimaryButton>
          </>
        )}
      </Card>

      <AppVersionBadge token={props.token} />

      {rodoSheetOpen && rodoAcceptance && (
        <div className="ModalBackdrop" role="dialog" aria-modal="true" aria-labelledby="rodoSheetTitle">
          <div className="Sheet Sheet--large" style={{ maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
            <div className="SheetHeader" style={{ flexShrink: 0 }}>
              <div id="rodoSheetTitle" className="SheetTitle">Regulamin RODO — wersja {rodoAcceptance.policy.version}</div>
              <button
                className="SheetClose"
                type="button"
                aria-label="Zamknij"
                onClick={() => setRodoSheetOpen(false)}
              >
                ×
              </button>
            </div>
            <div className="SheetBody" style={{ flexGrow: 1, overflowY: 'auto' }}>
              {rodoAcceptance.policy.sections.map((s) => (
                <div key={s.title} style={{ marginBottom: 18 }}>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>{s.title}</div>
                  <div className="Muted" style={{ lineHeight: 1.6 }}>{s.body}</div>
                </div>
              ))}
              <div className="RodoAcceptSection" style={{ marginTop: 20 }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    style={{ marginTop: 4 }}
                    checked={rodoSheetAccepted}
                    onChange={(e) => setRodoSheetAccepted(e.target.checked)}
                  />
                  <span>
                    Oświadczam, że zapoznałem(am) się z treścią regulaminu RODO w wersji {rodoAcceptance.policy.version} i akceptuję jego warunki.
                  </span>
                </label>
              </div>
            </div>
            <div className="SheetFooter" style={{ flexShrink: 0, display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <SecondaryButton type="button" onClick={() => setRodoSheetOpen(false)} disabled={rodoAcceptLoading}>
                {rodoAcceptance.accepted_latest ? 'Zamknij' : 'Anuluj'}
              </SecondaryButton>
              <PrimaryButton
                type="button"
                onClick={acceptRodo}
                disabled={rodoAcceptLoading || !rodoSheetAccepted || rodoAcceptance.accepted_latest}
              >
                {rodoAcceptLoading ? 'Zapisywanie…' : rodoAcceptance.accepted_latest ? 'Już zaakceptowano' : 'Akceptuję regulamin'}
              </PrimaryButton>
            </div>
          </div>
        </div>
      )}

      {props.me.role === 'ADMIN' && (
        <Card title="Drużyny przeciwnika">
          <div className="Muted">Baza drużyn do podpowiedzi w formularzu „Dodaj mecz”.</div>
          <SecondaryButton onClick={() => setIsOpponentTeamsOpen(true)} type="button">
            Zarządzaj
          </SecondaryButton>
        </Card>
      )}

      {props.me.role === 'ADMIN' && (
        <Card title="Statystyki korzystania">
          <div className="Muted">Ostatnia aktywność i liczba logowań dla kont.</div>
          <SecondaryButton onClick={() => setIsUsageOpen(true)} type="button">
            Otwórz
          </SecondaryButton>
        </Card>
      )}

      {props.me.role === 'ADMIN' && (
        <Card title="Szybkie reakcje">
          {quickTtlError && <div className="ErrorBanner">{quickTtlError}</div>}
          {quickTtlInfo && <div className="InfoBanner">{quickTtlInfo}</div>}
          <div className="Muted">Czas widoczności reakcji na profilu zawodnika.</div>
          <div className="KeyValue">
            <div className="Key">Aktualnie</div>
            <div className="Value">{`${quickTtlDays} dni`}</div>
          </div>
          <input
            className="TextInput"
            value={quickTtlInput}
            onChange={(e) => setQuickTtlInput(e.target.value)}
            placeholder="1–30"
            inputMode="numeric"
            disabled={isQuickTtlLoading}
          />
          <PrimaryButton
            onClick={saveQuickTtl}
            disabled={
              isQuickTtlLoading ||
              !Number.isFinite(Number(quickTtlInput.trim())) ||
              Number(quickTtlInput.trim()) < 1 ||
              Number(quickTtlInput.trim()) > 30
            }
            type="button"
          >
            Zapisz
          </PrimaryButton>
        </Card>
      )}

      {(props.me.role as Role) === 'PARENT' && (
        <>
          <Card title="Dziecko">
            {props.selectedChild ? (
              <div className="KeyValue">
                <div className="Key">Wybrane</div>
                <div className="Value">{childLabel(props.selectedChild)}</div>
              </div>
            ) : (
              <div className="Muted">Nie wybrano dziecka.</div>
            )}
            <SecondaryButton onClick={props.onChangeChild} type="button">
              Zmień
            </SecondaryButton>
          </Card>

          <Card title="Frekwencja">
            {statsError && <div className="ErrorBanner">{statsError}</div>}
            {!props.selectedChild ? (
              <div className="Muted">Wybierz dziecko, aby zobaczyć statystyki.</div>
            ) : (
              <>
                <div className="Muted">Okres</div>
                <div className="NotificationActions" aria-label="Okres statystyk">
                  {([
                    { d: 30 as const, label: '30 dni' },
                    { d: 90 as const, label: '90 dni' },
                    { d: 365 as const, label: 'Rok' },
                  ] as const).map((x) => (
                    <button
                      key={x.d}
                      type="button"
                      className={`Pill ${statsRangeDays === x.d ? 'is-active' : ''}`}
                      onClick={() => setStatsRangeDays(x.d)}
                      disabled={isStatsLoading}
                    >
                      {x.label}
                    </button>
                  ))}
                </div>

                {isStatsLoading ? (
                  <div className="Muted">Ładowanie…</div>
                ) : (
                  <>
                    <div className="KeyValue">
                      <div className="Key">Treningi</div>
                      <div className="Value">{attendanceStats.trainingsTotal}</div>
                    </div>
                    <div className="KeyValue">
                      <div className="Key">Obecności</div>
                      <div className="Value">{attendanceStats.trainingsIn}</div>
                    </div>
                    <div className="KeyValue">
                      <div className="Key">Nieobecności</div>
                      <div className="Value">{attendanceStats.trainingsOut}</div>
                    </div>
                    <div className="KeyValue">
                      <div className="Key">Spóźnienia</div>
                      <div className="Value">{attendanceStats.trainingsLate}</div>
                    </div>

                    <div className="KeyValue">
                      <div className="Key">Mecze</div>
                      <div className="Value">{attendanceStats.matchesTotal}</div>
                    </div>
                    <div className="KeyValue">
                      <div className="Key">Potwierdzone</div>
                      <div className="Value">{attendanceStats.matchesAccepted}</div>
                    </div>
                    <div className="KeyValue">
                      <div className="Key">Odmowy</div>
                      <div className="Value">{attendanceStats.matchesDeclined}</div>
                    </div>
                    <div className="KeyValue">
                      <div className="Key">Brak odpowiedzi</div>
                      <div className="Value">{attendanceStats.matchesPending}</div>
                    </div>
                    <div className="KeyValue">
                      <div className="Key">Odpowiedzi po terminie</div>
                      <div className="Value">{attendanceStats.matchesRespondedLate}</div>
                    </div>
                  </>
                )}
              </>
            )}
          </Card>
        </>
      )}

      {props.me.role === 'ADMIN' && (
        <Card title="Utwórz konto (Admin)">
          {errorText && <div className="ErrorBanner">{errorText}</div>}
          <div className="Muted">Jeśli hasło zostawisz puste, zostanie wygenerowane automatycznie.</div>
          <input
            className="TextInput"
            value={createUserEmail}
            onChange={(e) => setCreateUserEmail(e.target.value)}
            placeholder="Email użytkownika"
            inputMode="email"
            autoCapitalize="none"
            autoCorrect="off"
            disabled={isLoading}
          />
          <div className="Muted">Dostępy</div>
          <div className="NotificationActions" aria-label="Dostępy konta">
            <button
              type="button"
              className={`Pill ${createUserAccess.can_parent ? 'is-active' : ''}`}
              onClick={() => {
                setCreateUserAccess((prev) => {
                  const next = { ...prev, can_parent: !prev.can_parent };
                  if (!next.can_parent && !next.can_coach && !next.can_admin) return prev;
                  const nextRole = normalizeRoleForAccess(createUserRole, next);
                  if (nextRole !== createUserRole) setCreateUserRole(nextRole);
                  return next;
                });
              }}
              disabled={isLoading}
            >
              Rodzic
            </button>
            <button
              type="button"
              className={`Pill ${createUserAccess.can_coach ? 'is-active' : ''}`}
              onClick={() => {
                setCreateUserAccess((prev) => {
                  const next = { ...prev, can_coach: !prev.can_coach };
                  if (!next.can_parent && !next.can_coach && !next.can_admin) return prev;
                  const nextRole = normalizeRoleForAccess(createUserRole, next);
                  if (nextRole !== createUserRole) setCreateUserRole(nextRole);
                  return next;
                });
              }}
              disabled={isLoading}
            >
              Trener
            </button>
            <button
              type="button"
              className={`Pill ${createUserAccess.can_admin ? 'is-active' : ''}`}
              onClick={() => {
                setCreateUserAccess((prev) => {
                  const next = { ...prev, can_admin: !prev.can_admin };
                  if (!next.can_parent && !next.can_coach && !next.can_admin) return prev;
                  const nextRole = normalizeRoleForAccess(createUserRole, next);
                  if (nextRole !== createUserRole) setCreateUserRole(nextRole);
                  return next;
                });
              }}
              disabled={isLoading}
            >
              Admin
            </button>
          </div>
          <select
            className="TextInput"
            value={createUserRole}
            onChange={(e) => {
              const nextRole = e.target.value as Role;
              setCreateUserRole(nextRole);
              setCreateUserAccess((prev) => {
                const next = {
                  ...prev,
                  can_parent: prev.can_parent || nextRole === 'PARENT',
                  can_coach: prev.can_coach || nextRole === 'COACH',
                  can_admin: prev.can_admin || nextRole === 'ADMIN',
                };
                return next;
              });
            }}
            disabled={isLoading}
            aria-label="Tryb domyślny"
          >
            {enabledRolesFromAccess(createUserAccess).includes('PARENT') && <option value="PARENT">Rodzic</option>}
            {enabledRolesFromAccess(createUserAccess).includes('COACH') && <option value="COACH">Trener</option>}
            {enabledRolesFromAccess(createUserAccess).includes('ADMIN') && <option value="ADMIN">Admin</option>}
          </select>
          <select
            className="TextInput"
            value={createUserStatus}
            onChange={(e) => setCreateUserStatus(e.target.value as 'ACTIVE' | 'PENDING')}
            disabled={isLoading}
            aria-label="Status konta"
          >
            <option value="ACTIVE">ACTIVE</option>
            <option value="PENDING">PENDING</option>
          </select>
          <input
            className="TextInput"
            value={createUserPassword}
            onChange={(e) => setCreateUserPassword(e.target.value)}
            placeholder="Hasło (opcjonalnie)"
            type="password"
            disabled={isLoading}
          />
          <PrimaryButton onClick={createUser} disabled={isLoading || createUserEmail.trim() === ''} type="button">
            Utwórz konto
          </PrimaryButton>
          {createdUserPassword && createdUserEmail && (
            <div className="KeyValue">
              <div className="Key">Hasło</div>
              <div className="Value">
                {createdUserEmail}: {createdUserPassword}
              </div>
            </div>
          )}
        </Card>
      )}

      {isUsageOpen && (
        <div
          className="SheetOverlay"
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => {
            if (isUsageLoading) return;
            if (e.target === e.currentTarget) setIsUsageOpen(false);
          }}
        >
          <div className="Sheet">
            <div className="SheetTitle">Statystyki korzystania</div>
            {usageError && <div className="ErrorBanner">{usageError}</div>}
            {isUsageLoading && <div className="Muted">Ładowanie…</div>}

            <div className="SearchRow">
              <input
                className="TextInput"
                value={usageQuery}
                onChange={(e) => setUsageQuery(e.target.value)}
                placeholder="Szukaj (email/rola)"
                disabled={isUsageLoading}
              />
              <SecondaryButton onClick={loadUsageUsers} disabled={isUsageLoading} type="button">
                Odśwież
              </SecondaryButton>
            </div>

            <div className="List">
              {filteredUsageUsers.length === 0 ? (
                <div className="Muted">Brak danych.</div>
              ) : (
                filteredUsageUsers.map((u) => (
                  <div key={u.id} className="ListItem">
                    <div className="ListItemTop">
                      <div className="ListItemTitle">{u.email}</div>
                      <div className="ListItemBadge">{u.role}</div>
                    </div>
                    <div className="ListItemMeta">
                      {`Status: ${u.status} • Logowania: ${u.login_count || 0} • Ostatnie logowanie: ${
                        u.last_login_at ? formatDateTime(u.last_login_at) : '—'
                      } • Ostatnia aktywność: ${u.last_seen_at ? formatDateTime(u.last_seen_at) : '—'}`}
                    </div>
                  </div>
                ))
              )}
            </div>

            <SecondaryButton onClick={() => setIsUsageOpen(false)} disabled={isUsageLoading} type="button">
              Zamknij
            </SecondaryButton>
          </div>
        </div>
      )}

      {isOpponentTeamsOpen && (
        <div
          className="SheetOverlay"
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => {
            if (isOpponentTeamsLoading) return;
            if (e.target === e.currentTarget) setIsOpponentTeamsOpen(false);
          }}
        >
          <div className="Sheet">
            <div className="SheetTitle">Drużyny przeciwnika</div>
            {opponentTeamsError && <div className="ErrorBanner">{opponentTeamsError}</div>}
            {isOpponentTeamsLoading && <div className="Muted">Ładowanie…</div>}

            <input
              className="TextInput"
              value={opponentTeamsQuery}
              onChange={(e) => setOpponentTeamsQuery(e.target.value)}
              placeholder="Szukaj"
              disabled={isOpponentTeamsLoading}
            />

            <div className="SearchRow">
              <input
                className="TextInput"
                value={newOpponentTeamName}
                onChange={(e) => setNewOpponentTeamName(e.target.value)}
                placeholder="Nowa drużyna"
                disabled={isOpponentTeamsLoading}
              />
              <PrimaryButton
                onClick={onAddOpponentTeam}
                disabled={isOpponentTeamsLoading || newOpponentTeamName.trim() === ''}
                type="button"
              >
                Dodaj
              </PrimaryButton>
            </div>

            <div className="List">
              {filteredOpponentTeams.length === 0 ? (
                <div className="Muted">Brak drużyn.</div>
              ) : (
                filteredOpponentTeams.map((t) => (
                  <div key={t.id} className="ListItem">
                    <div className="ListItemTop">
                      <div className="ListItemTitle">{t.name}</div>
                    </div>
                    {t.logo_url && <div className="ListItemMeta">Logo ustawione</div>}
                    <div className="CoachManageActions">
                      <SecondaryButton
                        onClick={() => {
                          setEditOpponentTeam(t);
                          setEditOpponentName(t.name);
                          setEditOpponentLogoUrl(t.logo_url ?? '');
                        }}
                        disabled={isOpponentTeamsLoading}
                        type="button"
                      >
                        Edytuj
                      </SecondaryButton>
                      <SecondaryButton
                        onClick={() => setDeleteOpponentTeam(t)}
                        disabled={isOpponentTeamsLoading}
                        type="button"
                      >
                        Usuń
                      </SecondaryButton>
                    </div>
                  </div>
                ))
              )}
            </div>

            <SecondaryButton onClick={() => setIsOpponentTeamsOpen(false)} disabled={isOpponentTeamsLoading} type="button">
              Zamknij
            </SecondaryButton>
          </div>
        </div>
      )}

      {editOpponentTeam && (
        <div
          className="SheetOverlay"
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => {
            if (isOpponentTeamsLoading) return;
            if (e.target === e.currentTarget) setEditOpponentTeam(null);
          }}
        >
          <div className="Sheet">
            <div className="SheetTitle">Edytuj drużynę</div>
            {opponentTeamsError && <div className="ErrorBanner">{opponentTeamsError}</div>}
            <input
              className="TextInput"
              value={editOpponentName}
              onChange={(e) => setEditOpponentName(e.target.value)}
              placeholder="Nazwa"
              disabled={isOpponentTeamsLoading}
            />
            <div className="ChildPhotoRow">
              <div className="ChildPhotoPreview" aria-hidden="true">
                {editOpponentLogoUrl.trim() ? (
                  <img className="ChildPhotoPreviewImg" src={editOpponentLogoUrl.trim()} alt="" />
                ) : (
                  <div className="ChildPhotoPreviewFallback">•</div>
                )}
              </div>
              <div className="ChildPhotoActions">
                <input
                  ref={editOpponentLogoInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = '';
                    if (!f) return;
                    openEditOpponentLogoCrop(f);
                  }}
                  disabled={isOpponentTeamsLoading || isEditOpponentLogoUploading}
                  className="HiddenInput"
                />
                <SecondaryButton
                  type="button"
                  onClick={() => editOpponentLogoInputRef.current?.click()}
                  disabled={isOpponentTeamsLoading || isEditOpponentLogoUploading}
                >
                  Ustaw logo
                </SecondaryButton>
                {editOpponentLogoUrl.trim() && (
                  <SecondaryButton
                    type="button"
                    onClick={clearEditOpponentLogo}
                    disabled={isOpponentTeamsLoading || isEditOpponentLogoUploading}
                  >
                    Usuń logo
                  </SecondaryButton>
                )}
              </div>
            </div>
            <PrimaryButton
              onClick={onSaveOpponentTeam}
              disabled={isOpponentTeamsLoading || editOpponentName.trim() === ''}
              type="button"
            >
              Zapisz
            </PrimaryButton>
            <SecondaryButton onClick={() => setEditOpponentTeam(null)} disabled={isOpponentTeamsLoading} type="button">
              Anuluj
            </SecondaryButton>
          </div>
        </div>
      )}

      {deleteOpponentTeam && (
        <div
          className="SheetOverlay"
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => {
            if (isOpponentTeamsLoading) return;
            if (e.target === e.currentTarget) setDeleteOpponentTeam(null);
          }}
        >
          <div className="Sheet">
            <div className="SheetTitle">Usunąć drużynę?</div>
            <div className="Muted">{deleteOpponentTeam.name}</div>
            <PrimaryButton onClick={onDeleteOpponentTeamConfirm} disabled={isOpponentTeamsLoading} type="button">
              Usuń
            </PrimaryButton>
            <SecondaryButton onClick={() => setDeleteOpponentTeam(null)} disabled={isOpponentTeamsLoading} type="button">
              Anuluj
            </SecondaryButton>
          </div>
        </div>
      )}

      {deleteUser && (
        <div
          className="SheetOverlay"
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => {
            if (isLoading) return;
            if (e.target === e.currentTarget) setDeleteUser(null);
          }}
        >
          <div className="Sheet">
            <div className="SheetTitle">Usunąć konto?</div>
            <div className="Muted">{deleteUser.email}</div>
            <PrimaryButton onClick={deleteUserConfirm} disabled={isLoading} type="button">
              Usuń
            </PrimaryButton>
            <SecondaryButton onClick={() => setDeleteUser(null)} disabled={isLoading} type="button">
              Anuluj
            </SecondaryButton>
          </div>
        </div>
      )}

      {editUserEmail && (
        <div
          className="SheetOverlay"
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => {
            if (isLoading) return;
            if (e.target === e.currentTarget) {
              setEditUserEmail(null);
              setEditUserEmailValue('');
              setEditUserEmailError(null);
            }
          }}
        >
          <div className="Sheet">
            <div className="SheetTitle">Zmień email</div>
            <div className="Muted">{editUserEmail.label}</div>
            {editUserEmailError && <div className="ErrorBanner">{editUserEmailError}</div>}
            <input
              className="TextInput"
              value={editUserEmailValue}
              onChange={(e) => setEditUserEmailValue(e.target.value)}
              inputMode="email"
              autoCapitalize="none"
              autoCorrect="off"
              placeholder="Email"
              disabled={isLoading}
            />
            <PrimaryButton onClick={saveUserEmail} disabled={isLoading || editUserEmailValue.trim() === ''} type="button">
              Zapisz
            </PrimaryButton>
            <SecondaryButton
              onClick={() => {
                setEditUserEmail(null);
                setEditUserEmailValue('');
                setEditUserEmailError(null);
              }}
              disabled={isLoading}
              type="button"
            >
              Anuluj
            </SecondaryButton>
          </div>
        </div>
      )}

      {editChild && (
        <div
          className="SheetOverlay"
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => {
            if (isLoading) return;
            if (e.target === e.currentTarget) setEditChild(null);
          }}
        >
          <div className="Sheet">
            <div className="SheetTitle">Edytuj dziecko</div>
            <input
              className="TextInput"
              value={editChild.first_name}
              onChange={(e) => setEditChild((p) => (p ? { ...p, first_name: e.target.value } : p))}
              placeholder="Imię"
              disabled={isLoading}
            />
            <input
              className="TextInput"
              value={editChild.last_initial}
              onChange={(e) => setEditChild((p) => (p ? { ...p, last_initial: e.target.value } : p))}
              placeholder="Inicjał nazwiska (np. K.)"
              disabled={isLoading}
            />
            <input
              className="TextInput"
              value={editChild.birth_year}
              onChange={(e) => setEditChild((p) => (p ? { ...p, birth_year: e.target.value } : p))}
              placeholder="Rok urodzenia (opcjonalnie)"
              inputMode="numeric"
              disabled={isLoading}
            />
            <input
              className="TextInput"
              value={editChild.shirt_number}
              onChange={(e) => setEditChild((p) => (p ? { ...p, shirt_number: e.target.value } : p))}
              placeholder="No. (opcjonalnie)"
              inputMode="numeric"
              disabled={isLoading}
            />
            {editChildPhotoError && <div className="ErrorBanner">{editChildPhotoError}</div>}
            <div className="ChildPhotoRow">
              <div className="ChildPhotoPreview" aria-hidden="true">
                {editChildPhotoUrl ? (
                  <img className="ChildPhotoPreviewImg" src={editChildPhotoUrl} alt="" />
                ) : (
                  <div className="ChildPhotoPreviewFallback">
                    {`${(editChild.first_name || '').slice(0, 1)}${(editChild.last_initial || '').slice(0, 1)}`.toUpperCase() || '•'}
                  </div>
                )}
              </div>
              <div className="ChildPhotoActions">
                <input
                  ref={editChildPhotoInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = '';
                    if (!f) return;
                    openEditChildPhotoCrop(f);
                  }}
                  disabled={isLoading || isEditChildPhotoUploading}
                  className="HiddenInput"
                />
                <SecondaryButton
                  type="button"
                  onClick={() => editChildPhotoInputRef.current?.click()}
                  disabled={isLoading || isEditChildPhotoUploading}
                >
                  Zmień zdjęcie
                </SecondaryButton>
                {editChildPhotoUrl && (
                  <SecondaryButton type="button" onClick={clearEditChildPhoto} disabled={isLoading || isEditChildPhotoUploading}>
                    Usuń zdjęcie
                  </SecondaryButton>
                )}
              </div>
            </div>
            <PrimaryButton
              onClick={updateChild}
              disabled={isLoading || editChild.first_name.trim() === '' || editChild.last_initial.trim() === ''}
              type="button"
            >
              Zapisz
            </PrimaryButton>
            <SecondaryButton onClick={() => setEditChild(null)} disabled={isLoading} type="button">
              Anuluj
            </SecondaryButton>
          </div>
        </div>
      )}

      <PhotoCropperSheet
        req={editChildPhotoCropReq}
        onClose={() => setEditChildPhotoCropReq(null)}
        onDone={() => setEditChildPhotoCropReq(null)}
      />

      <PhotoCropperSheet
        req={editOpponentLogoCropReq}
        onClose={() => setEditOpponentLogoCropReq(null)}
        onDone={() => setEditOpponentLogoCropReq(null)}
      />

      {deleteChild && (
        <div
          className="SheetOverlay"
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => {
            if (isLoading) return;
            if (e.target === e.currentTarget) setDeleteChild(null);
          }}
        >
          <div className="Sheet">
            <div className="SheetTitle">Usunąć dziecko?</div>
            <div className="Muted">{deleteChild.label}</div>
            <PrimaryButton onClick={deleteChildConfirm} disabled={isLoading} type="button">
              Usuń
            </PrimaryButton>
            <SecondaryButton onClick={() => setDeleteChild(null)} disabled={isLoading} type="button">
              Anuluj
            </SecondaryButton>
          </div>
        </div>
      )}

      {isAdminLike && (
        <Card title="Akceptacja kont">
          {errorText && <div className="ErrorBanner">{errorText}</div>}
          <div className="List">
            {pending.length === 0 ? (
              <div className="Muted">Brak kont oczekujących.</div>
            ) : (
              pending.map((u) => (
                <div key={u.id} className="ListItem">
                  <div className="ListItemTop">
                    <div className="ListItemTitle">{userLabelWithEmail(u)}</div>
                    <div className="ListItemBadge">{u.status}</div>
                  </div>
                  <div className="ListItemMeta">{u.created_at}</div>
                  {(() => {
                    const r = (u.signup_role ?? '').trim().toUpperCase();
                    const isParent = r === 'PARENT';
                    const isCoach = r === 'COACH';
                    const roleLabel = isParent ? 'Rodzic' : isCoach ? 'Trener' : '';
                    const cf = (u.signup_child_first_name ?? '').trim();
                    const cl = (u.signup_child_last_initial ?? '').trim();
                    const by = Number.isFinite(u.signup_child_birth_year as number) ? String(u.signup_child_birth_year) : '';
                    const group = (u.signup_group_label ?? '').trim();
                    const childLabel = isParent && (cf || cl || by) ? `${[cf, cl].filter(Boolean).join(' ')}${by ? ` (${by})` : ''}`.trim() : '';
                    const parts = [roleLabel, childLabel, group ? `Grupa: ${group}` : ''].filter(Boolean);
                    if (parts.length === 0) return null;
                    return <div className="Muted">{`Zgłoszenie: ${parts.join(' • ')}`}</div>;
                  })()}
                  <div className="Muted">Dostępy</div>
                  <div className="NotificationActions" aria-label="Dostępy konta">
                    <button
                      type="button"
                      className={`Pill ${(pendingAccessById[u.id]?.can_parent ?? u.can_parent) ? 'is-active' : ''}`}
                      onClick={() => {
                        setPendingAccessById((prev) => {
                          const cur = prev[u.id] ?? accessFromUser(u);
                          const next = { ...cur, can_parent: !cur.can_parent };
                          if (!next.can_parent && !next.can_coach && !next.can_admin) return prev;
                          const nextRole = normalizeRoleForAccess(pendingRoleById[u.id] ?? 'PARENT', next);
                          if (nextRole !== (pendingRoleById[u.id] ?? 'PARENT')) {
                            setPendingRoleById((rprev) => ({ ...rprev, [u.id]: nextRole }));
                          }
                          return { ...prev, [u.id]: next };
                        });
                      }}
                      disabled={isLoading}
                    >
                      Rodzic
                    </button>
                    <button
                      type="button"
                      className={`Pill ${(pendingAccessById[u.id]?.can_coach ?? u.can_coach) ? 'is-active' : ''}`}
                      onClick={() => {
                        setPendingAccessById((prev) => {
                          const cur = prev[u.id] ?? accessFromUser(u);
                          const next = { ...cur, can_coach: !cur.can_coach };
                          if (!next.can_parent && !next.can_coach && !next.can_admin) return prev;
                          const nextRole = normalizeRoleForAccess(pendingRoleById[u.id] ?? 'PARENT', next);
                          if (nextRole !== (pendingRoleById[u.id] ?? 'PARENT')) {
                            setPendingRoleById((rprev) => ({ ...rprev, [u.id]: nextRole }));
                          }
                          return { ...prev, [u.id]: next };
                        });
                      }}
                      disabled={isLoading}
                    >
                      Trener
                    </button>
                    <button
                      type="button"
                      className={`Pill ${(pendingAccessById[u.id]?.can_admin ?? u.can_admin) ? 'is-active' : ''}`}
                      onClick={() => {
                        setPendingAccessById((prev) => {
                          const cur = prev[u.id] ?? accessFromUser(u);
                          const next = { ...cur, can_admin: !cur.can_admin };
                          if (!next.can_parent && !next.can_coach && !next.can_admin) return prev;
                          const nextRole = normalizeRoleForAccess(pendingRoleById[u.id] ?? 'PARENT', next);
                          if (nextRole !== (pendingRoleById[u.id] ?? 'PARENT')) {
                            setPendingRoleById((rprev) => ({ ...rprev, [u.id]: nextRole }));
                          }
                          return { ...prev, [u.id]: next };
                        });
                      }}
                      disabled={isLoading}
                    >
                      Admin
                    </button>
                  </div>
                  <select
                    className="TextInput"
                    value={pendingRoleById[u.id] ?? normalizeRoleForAccess('PARENT', pendingAccessById[u.id] ?? accessFromUser(u))}
                    onChange={(e) => setPendingRoleById((prev) => ({ ...prev, [u.id]: e.target.value as Role }))}
                    disabled={isLoading}
                    aria-label="Tryb domyślny"
                  >
                    {enabledRolesFromAccess(pendingAccessById[u.id] ?? accessFromUser(u)).includes('PARENT') && (
                      <option value="PARENT">Rodzic</option>
                    )}
                    {enabledRolesFromAccess(pendingAccessById[u.id] ?? accessFromUser(u)).includes('COACH') && (
                      <option value="COACH">Trener</option>
                    )}
                    {enabledRolesFromAccess(pendingAccessById[u.id] ?? accessFromUser(u)).includes('ADMIN') && (
                      <option value="ADMIN">Admin</option>
                    )}
                  </select>
                  <PrimaryButton onClick={() => approve(u.id)} disabled={isLoading} type="button">
                    Akceptuj
                  </PrimaryButton>
                </div>
              ))
            )}
          </div>
          <SecondaryButton onClick={loadPending} disabled={isLoading} type="button">
            Odśwież
          </SecondaryButton>
        </Card>
      )}

      {props.me.role === 'ADMIN' && (
        <Card title="Konta (wszystkie)">
          {errorText && <div className="ErrorBanner">{errorText}</div>}
          <div className="SearchRow">
            <div className="SearchInputWrap">
              <SearchIcon className="SearchIcon" />
              <input
                className="SearchInput"
                value={usersQuery}
                onChange={(e) => setUsersQuery(e.target.value)}
                placeholder="Szukaj email/rola/status"
              />
            </div>
          </div>
          <select
            className="TextInput"
            value={usersStatusFilter}
            onChange={(e) => setUsersStatusFilter(e.target.value as 'ALL' | 'PENDING' | 'ACTIVE' | 'REJECTED')}
            disabled={isUsersLoading || isLoading}
            aria-label="Filtr statusu"
          >
            <option value="ALL">Wszystkie</option>
            <option value="PENDING">PENDING</option>
            <option value="ACTIVE">ACTIVE</option>
            <option value="REJECTED">REJECTED</option>
          </select>

          <div className="List">
            {isUsersLoading && users.length === 0 ? (
              <div className="Muted">Ładowanie…</div>
            ) : filteredUsers.length === 0 ? (
              <div className="Muted">Brak kont.</div>
            ) : (
              filteredUsers.map((u) => {
                const isSelf = u.id === props.me.id;
                const access = userAccessById[u.id] ?? accessFromUser(u);
                return (
                  <div key={u.id} className="ListItem">
                    <div className="ListItemTop">
                      <div className="ListItemTitle">{userLabelWithEmail(u)}</div>
                      <div className="ListItemBadge">{u.status}</div>
                    </div>
                    <div className="ListItemMeta">{u.created_at}</div>
                    {isSelf && <div className="Muted">To Twoje konto — nie można go edytować ani usunąć.</div>}
                    <div className="Muted">Dostępy</div>
                    <div className="NotificationActions" aria-label="Dostępy konta">
                      <button
                        type="button"
                        className={`Pill ${access.can_parent ? 'is-active' : ''}`}
                        onClick={() => {
                          setUserAccessById((prev) => {
                            const cur = prev[u.id] ?? accessFromUser(u);
                            const next = { ...cur, can_parent: !cur.can_parent };
                            if (!next.can_parent && !next.can_coach && !next.can_admin) return prev;
                            const nextRole = normalizeRoleForAccess(userRoleById[u.id] ?? 'PARENT', next);
                            if (nextRole !== (userRoleById[u.id] ?? 'PARENT')) {
                              setUserRoleById((rprev) => ({ ...rprev, [u.id]: nextRole }));
                            }
                            return { ...prev, [u.id]: next };
                          });
                        }}
                        disabled={isLoading || isSelf}
                      >
                        Rodzic
                      </button>
                      <button
                        type="button"
                        className={`Pill ${access.can_coach ? 'is-active' : ''}`}
                        onClick={() => {
                          setUserAccessById((prev) => {
                            const cur = prev[u.id] ?? accessFromUser(u);
                            const next = { ...cur, can_coach: !cur.can_coach };
                            if (!next.can_parent && !next.can_coach && !next.can_admin) return prev;
                            const nextRole = normalizeRoleForAccess(userRoleById[u.id] ?? 'PARENT', next);
                            if (nextRole !== (userRoleById[u.id] ?? 'PARENT')) {
                              setUserRoleById((rprev) => ({ ...rprev, [u.id]: nextRole }));
                            }
                            return { ...prev, [u.id]: next };
                          });
                        }}
                        disabled={isLoading || isSelf}
                      >
                        Trener
                      </button>
                      <button
                        type="button"
                        className={`Pill ${access.can_admin ? 'is-active' : ''}`}
                        onClick={() => {
                          setUserAccessById((prev) => {
                            const cur = prev[u.id] ?? accessFromUser(u);
                            const next = { ...cur, can_admin: !cur.can_admin };
                            if (!next.can_parent && !next.can_coach && !next.can_admin) return prev;
                            const nextRole = normalizeRoleForAccess(userRoleById[u.id] ?? 'PARENT', next);
                            if (nextRole !== (userRoleById[u.id] ?? 'PARENT')) {
                              setUserRoleById((rprev) => ({ ...rprev, [u.id]: nextRole }));
                            }
                            return { ...prev, [u.id]: next };
                          });
                        }}
                        disabled={isLoading || isSelf}
                      >
                        Admin
                      </button>
                    </div>
                    <select
                      className="TextInput"
                      value={userRoleById[u.id] ?? normalizeRoleForAccess('PARENT', access)}
                      onChange={(e) => setUserRoleById((prev) => ({ ...prev, [u.id]: e.target.value as Role }))}
                      disabled={isLoading || isSelf}
                      aria-label="Tryb domyślny"
                    >
                      {enabledRolesFromAccess(access).includes('PARENT') && <option value="PARENT">Rodzic</option>}
                      {enabledRolesFromAccess(access).includes('COACH') && <option value="COACH">Trener</option>}
                      {enabledRolesFromAccess(access).includes('ADMIN') && <option value="ADMIN">Admin</option>}
                    </select>
                    <select
                      className="TextInput"
                      value={userStatusById[u.id] ?? 'PENDING'}
                      onChange={(e) =>
                        setUserStatusById((prev) => ({ ...prev, [u.id]: e.target.value as 'ACTIVE' | 'PENDING' | 'REJECTED' }))
                      }
                      disabled={isLoading || isSelf}
                      aria-label="Status"
                    >
                      <option value="PENDING">PENDING</option>
                      <option value="ACTIVE">ACTIVE</option>
                      <option value="REJECTED">REJECTED</option>
                    </select>
                    <PrimaryButton onClick={() => updateUser(u.id)} disabled={isLoading || isSelf} type="button">
                      Zapisz zmiany
                    </PrimaryButton>
                    <SecondaryButton onClick={() => openEditUserEmail(u)} disabled={isLoading || isSelf} type="button">
                      Zmień email
                    </SecondaryButton>
                    <SecondaryButton
                      onClick={() => setDeleteUser({ id: u.id, email: userLabelWithEmail(u) })}
                      disabled={isLoading || isSelf}
                      type="button"
                    >
                      Usuń konto
                    </SecondaryButton>
                  </div>
                );
              })
            )}
          </div>
          <SecondaryButton onClick={loadUsers} disabled={isUsersLoading || isLoading} type="button">
            Odśwież
          </SecondaryButton>
        </Card>
      )}

      {props.me.role === 'ADMIN' && (
        <Card title="Baza danych: backup/reset">
          {dbError && <div className="ErrorBanner">{dbError}</div>}
          {dbInfo && <div className="Muted">{dbInfo}</div>}
          <div className="Muted">
            Reset czyści kalendarz (wydarzenia) i statystyki (obecności, powołania, RSVP). Nie usuwa kont, dzieci, grup, feedu ani wiadomości.
          </div>
          <SecondaryButton onClick={adminDownloadDbBackup} disabled={isDbLoading} type="button">
            Pobierz backup bazy
          </SecondaryButton>

          <label className="MatchCreateSwitchRow" aria-label="Wykonaj backup przed resetem">
            <span className="MatchCreateSwitchText">
              <span className="MatchCreateSwitchTitle">BACKUP PRZED RESETEM</span>
              <span className="MatchCreateSwitchSub">Zapisze kopię do pliku przed wyczyszczeniem (SQLite)</span>
            </span>
            <span className="MatchCreateSwitchControl">
              <span className="Switch">
                <input
                  className="SwitchInput"
                  type="checkbox"
                  checked={dbWithBackup}
                  onChange={(e) => setDbWithBackup(e.target.checked)}
                  disabled={isDbLoading}
                />
                <span className="SwitchTrack">
                  <span className="SwitchThumb" />
                </span>
              </span>
            </span>
          </label>

          <input
            className="TextInput"
            value={dbConfirm}
            onChange={(e) => setDbConfirm(e.target.value)}
            placeholder="Wpisz: RESET_STATS_CALENDAR"
            disabled={isDbLoading}
          />
          <PrimaryButton
            onClick={adminResetStatsCalendar}
            disabled={isDbLoading || dbConfirm.trim().toUpperCase() !== 'RESET_STATS_CALENDAR'}
            type="button"
          >
            Wyczyść statystyki i kalendarz
          </PrimaryButton>
        </Card>
      )}

      {props.me.role === 'ADMIN' && (
        <Card title="Lista dzieci (edycja/usuwanie)">
          {errorText && <div className="ErrorBanner">{errorText}</div>}
          <div className="List">
            {children.length === 0 ? (
              <div className="Muted">Brak dzieci.</div>
            ) : (
              children.map((c) => (
                <div key={c.id} className="ListItem">
                  <div className="ListItemTop">
                    <div className="ListItemTitle">{childLabel(c)}</div>
                    <div className="ListItemBadge">{c.group_name ? c.group_name : 'Brak grupy'}</div>
                  </div>
                  <div className="ListItemMeta">{c.id}</div>
                  <PrimaryButton
                    onClick={() =>
                      setEditChild({
                        id: c.id,
                        first_name: c.first_name,
                        last_initial: c.last_initial,
                        birth_year: c.birth_year ? String(c.birth_year) : '',
                        shirt_number: c.shirt_number ? String(c.shirt_number) : '',
                      })
                    }
                    disabled={isLoading}
                    type="button"
                  >
                    Edytuj
                  </PrimaryButton>
                  <SecondaryButton
                    onClick={() => setDeleteChild({ id: c.id, label: childLabel(c) })}
                    disabled={isLoading}
                    type="button"
                  >
                    Usuń
                  </SecondaryButton>
                </div>
              ))
            )}
          </div>
          <SecondaryButton onClick={loadChildren} disabled={isLoading} type="button">
            Odśwież
          </SecondaryButton>
        </Card>
      )}

      {props.me.role === 'ADMIN' && (
        <Card title="Dzieci">
          {errorText && <div className="ErrorBanner">{errorText}</div>}
          <div className="Muted">Dodaj dziecko i przypisz do rodzica po emailu.</div>
          <input
            className="TextInput"
            value={newChildFirstName}
            onChange={(e) => setNewChildFirstName(e.target.value)}
            placeholder="Imię"
            disabled={isLoading}
          />
          <input
            className="TextInput"
            value={newChildLastInitial}
            onChange={(e) => setNewChildLastInitial(e.target.value)}
            placeholder="Inicjał nazwiska (np. K.)"
            disabled={isLoading}
          />
          <input
            className="TextInput"
            value={newChildBirthYear}
            onChange={(e) => setNewChildBirthYear(e.target.value)}
            placeholder="Rok urodzenia (opcjonalnie)"
            inputMode="numeric"
            disabled={isLoading}
          />
          <input
            className="TextInput"
            value={newChildShirtNumber}
            onChange={(e) => setNewChildShirtNumber(e.target.value)}
            placeholder="No. (opcjonalnie)"
            inputMode="numeric"
            disabled={isLoading}
          />
          <PrimaryButton
            onClick={createChild}
            disabled={isLoading || newChildFirstName.trim() === '' || newChildLastInitial.trim() === ''}
            type="button"
          >
            Dodaj dziecko
          </PrimaryButton>

          <div className="Muted">Przypisanie:</div>
          <select
            className="TextInput"
            value={assignChildId}
            onChange={(e) => setAssignChildId(e.target.value)}
            disabled={isLoading}
          >
            {children.map((c) => (
              <option key={c.id} value={c.id}>
                {childLabel(c)}
              </option>
            ))}
          </select>
          <input
            className="TextInput"
            value={assignParentEmail}
            onChange={(e) => setAssignParentEmail(e.target.value)}
            placeholder="Email rodzica"
            inputMode="email"
            autoCapitalize="none"
            autoCorrect="off"
            disabled={isLoading}
          />
          <PrimaryButton onClick={assignParent} disabled={isLoading || !assignChildId || assignParentEmail.trim() === ''} type="button">
            Przypisz rodzica
          </PrimaryButton>

          <div className="Muted">Trener indywidualny (dla tego dziecka):</div>
          {individualCoaches.length > 0 ? (
            <div className="MatchCreateSuggestions" aria-label="Trenerzy indywidualni">
              {individualCoaches.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className="Pill"
                  onClick={() => unassignIndividualCoach(c.id)}
                  disabled={isLoading}
                  aria-label={`Usuń trenera: ${c.email}`}
                >
                  {c.email}
                  <span aria-hidden="true"> ×</span>
                </button>
              ))}
            </div>
          ) : (
            <div className="Muted">Brak.</div>
          )}
          <input
            className="TextInput"
            value={assignIndividualCoachEmail}
            onChange={(e) => setAssignIndividualCoachEmail(e.target.value)}
            placeholder="Email trenera (indywidualnego)"
            inputMode="email"
            autoCapitalize="none"
            autoCorrect="off"
            disabled={isLoading}
          />
          <PrimaryButton
            onClick={assignIndividualCoach}
            disabled={isLoading || !assignChildId || assignIndividualCoachEmail.trim() === ''}
            type="button"
          >
            Przypisz trenera indywidualnego
          </PrimaryButton>

          <div className="Muted">Konto zawodnika (dla tego dziecka):</div>
          {childPlayers.length > 0 ? (
            <div className="MatchCreateSuggestions" aria-label="Konta zawodników">
              {childPlayers.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className="Pill"
                  onClick={() => unassignPlayer(p.id)}
                  disabled={isLoading}
                  aria-label={`Usuń konto zawodnika: ${p.email}`}
                >
                  {p.email}
                  <span aria-hidden="true"> ×</span>
                </button>
              ))}
            </div>
          ) : (
            <div className="Muted">Brak.</div>
          )}
          <input
            className="TextInput"
            value={assignPlayerEmail}
            onChange={(e) => setAssignPlayerEmail(e.target.value)}
            placeholder="Email konta zawodnika"
            inputMode="email"
            autoCapitalize="none"
            autoCorrect="off"
            disabled={isLoading}
          />
          <PrimaryButton onClick={assignPlayer} disabled={isLoading || !assignChildId || assignPlayerEmail.trim() === ''} type="button">
            Przypisz konto zawodnika
          </PrimaryButton>

          <SecondaryButton onClick={loadChildren} disabled={isLoading} type="button">
            Odśwież listę dzieci
          </SecondaryButton>
        </Card>
      )}

      {props.me.role === 'ADMIN' && (
        <Card title="Grupy">
          {errorText && <div className="ErrorBanner">{errorText}</div>}
          <div className="Muted">Utwórz grupę tutaj, a przypisywanie dzieci i trenerów wykonuj w osobnym widoku „Konfiguracja grup”.</div>
          <input
            className="TextInput"
            value={newGroupName}
            onChange={(e) => setNewGroupName(e.target.value)}
            placeholder="Nazwa grupy (np. U12)"
            disabled={isLoading}
          />
          <input
            className="TextInput"
            value={newGroupYear}
            onChange={(e) => setNewGroupYear(e.target.value)}
            placeholder="Rocznik (opcjonalnie)"
            inputMode="numeric"
            disabled={isLoading}
          />
          <PrimaryButton onClick={createGroup} disabled={isLoading || newGroupName.trim() === ''} type="button">
            Dodaj grupę
          </PrimaryButton>

          <div className="Muted">Przypisz trener → grupa:</div>
          <select className="TextInput" value={assignCoachGroupId} onChange={(e) => setAssignCoachGroupId(e.target.value)} disabled={isLoading}>
            {groups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
                {g.year ? ` (${g.year})` : ''}
              </option>
            ))}
          </select>
          <input
            className="TextInput"
            value={assignCoachEmail}
            onChange={(e) => setAssignCoachEmail(e.target.value)}
            placeholder="Email trenera (konto ACTIVE, rola COACH)"
            inputMode="email"
            autoCapitalize="none"
            autoCorrect="off"
            disabled={isLoading}
          />
          <PrimaryButton
            onClick={assignCoach}
            disabled={isLoading || assignCoachGroupId === '' || assignCoachEmail.trim() === ''}
            type="button"
          >
            Przypisz trenera
          </PrimaryButton>
          <SecondaryButton onClick={loadGroups} disabled={isLoading} type="button">
            Odśwież listę grup
          </SecondaryButton>
        </Card>
      )}

      <SecondaryButton onClick={props.onLogout}>Wyloguj</SecondaryButton>
    </div>
  );
}

function BottomNav(props: {
  tab: Tab;
  tabs: Tab[];
  unreadThreads: number;
  labelPartsByTab?: Partial<Record<Tab, string[]>>;
  onChange: (tab: Tab) => void;
}) {
  return (
    <nav className="BottomNav" aria-label="Główna nawigacja">
      {props.tabs.map((t) => {
        const isActive = t === props.tab;
        const unread = t === 'wiadomosci' && !isActive ? Math.max(0, Number(props.unreadThreads) || 0) : 0;
        const labelParts = props.labelPartsByTab?.[t] ?? TAB_LABEL_PARTS[t] ?? [TAB_LABEL[t]];
        return (
          <button
            key={t}
            className={['BottomNavItem', isActive ? 'is-active' : ''].filter(Boolean).join(' ')}
            onClick={() => props.onChange(t)}
            aria-current={isActive ? 'page' : undefined}
            type="button"
          >
            <span className="BottomNavIconWrap" aria-hidden="true">
              <TabIcon tab={t} className="BottomNavIcon" />
              {unread > 0 && <span className="BottomNavBadge">{unread}</span>}
            </span>
            <span className="BottomNavLabel">
              {labelParts.map((p, idx) => (
                <span key={`${t}-${idx}`} className="BottomNavLabelLine">
                  {p}
                </span>
              ))}
            </span>
          </button>
        );
      })}
    </nav>
  );
}

function App() {
  const tab = useTab();
  const [token, setToken] = useToken();
  const { me, isLoading, isUnauthorized, setMeExternal, refreshMe } = useMe(token);
  const role: Role = me?.role ?? 'PARENT';
  const tabs = React.useMemo(() => getTabsForRole(role), [role]);
  const bottomTabs = React.useMemo(() => getBottomTabsForRole(role), [role]);
  const [selectedChildId, setSelectedChildId] = useSelectedChildId();

  const appIsIOS = React.useMemo(() => {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent || '';
    const isAppleMobile = /iPhone|iPad|iPod/i.test(ua);
    const isOldIe = typeof window !== 'undefined' && !!(window as unknown as { MSStream?: unknown }).MSStream;
    return isAppleMobile && !isOldIe;
  }, []);
  const appIosVersion = React.useMemo(() => {
    if (typeof navigator === 'undefined') return 0;
    try {
      const m = /OS (\d+)_(\d+)/.exec(navigator.userAgent || '');
      if (!m) return 0;
      return Number(`${m[1]}.${m[2]}`);
    } catch {
      return 0;
    }
  }, []);
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!('serviceWorker' in navigator)) return;
    const loc = window.location;
    const isSecure = loc.protocol === 'https:' || loc.hostname === '127.0.0.1' || loc.hostname === 'localhost';
    if (!isSecure) return;
    if (appIsIOS && appIosVersion > 0 && appIosVersion < 16.4) {
      console.log('[SW] skipped: iOS version < 16.4 (no Web Push support)');
      return;
    }
    navigator.serviceWorker
      .register('/sw.js')
      .then(function (reg) {
        console.log('[SW] registered, scope:', (reg || {}).scope || '');
      })
      .catch(function (err) {
        console.warn('[SW] register failed:', err);
      });
  }, [appIsIOS, appIosVersion]);

  const [menuOpen, setMenuOpen] = React.useState(false);
  const [workGroups, setWorkGroups] = React.useState<GroupItem[]>([]);
  const [activeGroupId, setActiveGroupId] = React.useState<string>(() => localStorage.getItem(ACTIVE_GROUP_KEY) ?? '');
  const [coachViewMode, setCoachViewMode] = React.useState<CoachViewMode>(() => {
    const v = (localStorage.getItem(COACH_VIEW_MODE_KEY) ?? '').trim().toUpperCase();
    return v === 'INDIVIDUAL' ? 'INDIVIDUAL' : 'GROUP';
  });
  const [coachChildren, setCoachChildren] = React.useState<ChildItem[]>([]);
  const [notificationsOpen, setNotificationsOpen] = React.useState(false);
  const [notifSummary, setNotifSummary] = React.useState<{
    importantToRead: number;
    pendingMatchRsvp: number;
    unreadThreads: number;
    pendingApprovals: number;
    missingCallups: number;
    missingCallupsLevel: 'INFO' | 'CRITICAL' | null;
    missingCallupsMatch: { id: string; title: string; start_at: string } | null;
    tbdMatches: number;
    tbdMatch: { id: string; title: string; start_at: string } | null;
    pendingMatch: { id: string; title: string; start_at: string; location: string } | null;
    importantPost: { id: string; title: string; body: string } | null;
    unreadThread: { id: string; title: string; preview: string; unread_count: number } | null;
    pendingApproval: { id: string; email: string } | null;
  }>({
    importantToRead: 0,
    pendingMatchRsvp: 0,
    unreadThreads: 0,
    pendingApprovals: 0,
    missingCallups: 0,
    missingCallupsLevel: null,
    missingCallupsMatch: null,
    tbdMatches: 0,
    tbdMatch: null,
    pendingMatch: null,
    importantPost: null,
    unreadThread: null,
    pendingApproval: null,
  });
  const [isNotifLoading, setIsNotifLoading] = React.useState(false);
  const [notifError, setNotifError] = React.useState<string | null>(null);
  const [children, setChildren] = React.useState<ChildItem[]>([]);
  const [isChildrenLoading, setIsChildrenLoading] = React.useState(false);
  const [childrenError, setChildrenError] = React.useState<string | null>(null);
  const [isRoleSwitchLoading, setIsRoleSwitchLoading] = React.useState(false);
  const [rodoMandatory, setRodoMandatory] = React.useState<RodoAcceptance | null>(null);
  const [rodoMandatoryLoading, setRodoMandatoryLoading] = React.useState(false);
  const [rodoMandatoryError, setRodoMandatoryError] = React.useState<string | null>(null);
  const [rodoMandatoryAccepting, setRodoMandatoryAccepting] = React.useState(false);
  const [rodoMandatoryChecked, setRodoMandatoryChecked] = React.useState(false);
  const isChildScoped = isChildScopedRole(role);

  const loadRodoMandatory = React.useCallback(async () => {
    if (!token) return;
    if (!me || me.status !== 'ACTIVE') return;
    if (me.rodo_accepted_latest) {
      setRodoMandatory(null);
      return;
    }
    setRodoMandatoryLoading(true);
    setRodoMandatoryError(null);
    try {
      const r = await apiGetRodo(token);
      if (!r.accepted_latest) {
        setRodoMandatory(r);
        setRodoMandatoryChecked(false);
      } else {
        setRodoMandatory(null);
      }
    } catch {
      setRodoMandatoryError('Nie udało się załadować regulaminu RODO.');
    } finally {
      setRodoMandatoryLoading(false);
    }
  }, [me, token]);

  React.useEffect(() => {
    loadRodoMandatory();
  }, [loadRodoMandatory]);

  const handleLogout = React.useCallback(async () => {
    let currentToken = token;
    try {
      const saved = window.localStorage.getItem(TOKEN_KEY);
      if (saved && !currentToken) currentToken = saved;
    } catch {
    }
    try {
      await apiLogout(currentToken);
    } catch {
    }
    setToken(null);
    setSelectedChildId(null);
    setMeExternal(null);
    try {
      window.location.hash = '#/';
    } catch {
    }
  }, [token, setToken, setSelectedChildId, setMeExternal]);

  const acceptRodoMandatory = React.useCallback(async () => {
    if (!token || !rodoMandatory) return;
    setRodoMandatoryAccepting(true);
    setRodoMandatoryError(null);
    try {
      const r = await apiAcceptRodo(token, rodoMandatory.latest_version);
      setRodoMandatory(r.accepted_latest ? null : r);
      refreshMe();
    } catch {
      setRodoMandatoryError('Nie udało się zaakceptować regulaminu RODO. Spróbuj ponownie.');
    } finally {
      setRodoMandatoryAccepting(false);
    }
  }, [refreshMe, rodoMandatory, token]);

  React.useEffect(() => {
    if (isUnauthorized && token) setToken(null);
  }, [isUnauthorized, setToken, token]);

  const [reloginMessage, setReloginMessage] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    let intervalHandle: number | null = null;
    let reloginTimer: number | null = null;

    const isOnLoginPage = (): boolean => {
      try {
        const { path } = getHashParts();
        return path === 'login';
      } catch {
        return false;
      }
    };

    const checkAppVersion = async (): Promise<void> => {
      if (cancelled) return;
      if (!token) return;
      if (isOnLoginPage()) return;
      try {
        const [lastCache, alreadyShown, lastAppliedForceIso] = (() => {
          try {
            return [
              window.localStorage.getItem(CLIENT_CACHE_KEY) ?? null,
              (window.localStorage.getItem(FORCE_RELOGIN_MSG_KEY) ?? '') === '1',
              window.localStorage.getItem(LAST_FORCE_RELOGIN_ISO_KEY) ?? null,
            ];
          } catch {
            return [null, false, null];
          }
        })();
        const resp = await fetch('/health', { cache: 'no-store', headers: { Accept: 'application/json' } });
        if (!resp.ok) return;
        const data = (await resp.json()) as {
          client_cache_buster?: string | null;
          force_relogin_before_iso?: string | null;
          build_epoch?: number | null;
        };
        const serverCache = (data.client_cache_buster ?? String(data.build_epoch ?? '')).trim() || '';
        if (!serverCache) return;

        if (!lastCache) {
          try {
            window.localStorage.setItem(CLIENT_CACHE_KEY, serverCache);
          } catch {
            // ignore
          }
          return;
        }

        const cacheMismatch = lastCache !== serverCache;
        const serverForceIso = (data.force_relogin_before_iso ?? '').trim() || null;
        const forceByServer = !!serverForceIso && serverForceIso !== lastAppliedForceIso;

        if (!cacheMismatch && !forceByServer) return;
        if (alreadyShown && cacheMismatch && !forceByServer) return;

        if (forceByServer && serverForceIso) {
          try {
            window.localStorage.setItem(CLIENT_CACHE_KEY, serverCache);
            window.localStorage.setItem(LAST_FORCE_RELOGIN_ISO_KEY, serverForceIso);
            window.localStorage.removeItem(FORCE_RELOGIN_MSG_KEY);
          } catch {
            // ignore
          }
          setReloginMessage('Wymagane ponowne zalogowanie po aktualizacji systemowej.');
        } else {
          try {
            window.localStorage.setItem(FORCE_RELOGIN_MSG_KEY, '1');
          } catch {
            // ignore
          }
          setReloginMessage('Dostępna nowa wersja aplikacji. Za chwilę zostaniesz przekierowany do ekranu logowania, aby załadować aktualizację.');
        }

        if (reloginTimer) window.clearTimeout(reloginTimer);
        reloginTimer = window.setTimeout(() => {
          if (cancelled) return;
          try {
            window.localStorage.setItem(CLIENT_CACHE_KEY, serverCache);
            if (serverForceIso) {
              window.localStorage.setItem(LAST_FORCE_RELOGIN_ISO_KEY, serverForceIso);
            }
            window.localStorage.removeItem(FORCE_RELOGIN_MSG_KEY);
          } catch {
            // ignore
          }
          if (!token) {
            try {
              window.location.assign('/#/login');
            } catch {
              window.location.href = '/#/login';
            }
            return;
          }
          setToken(null);
          try {
            window.location.assign('/#/login');
            window.location.reload();
          } catch {
            window.location.href = '/#/login';
          }
        }, 2500);
      } catch {
        // ignore
      }
    };

    void checkAppVersion();
    try {
      intervalHandle = window.setInterval(() => {
        void checkAppVersion();
      }, 60000);
    } catch {
      // ignore
    }

    return () => {
      cancelled = true;
      if (intervalHandle !== null) {
        try {
          window.clearInterval(intervalHandle);
        } catch {
          // ignore
        }
      }
      if (reloginTimer !== null) {
        try {
          window.clearTimeout(reloginTimer);
        } catch {
          // ignore
        }
      }
    };
  }, [setToken, token]);

  React.useEffect(() => {
    if (!reloginMessage) return;
  }, [reloginMessage]);

  React.useEffect(() => {
    localStorage.setItem(ACTIVE_GROUP_KEY, activeGroupId);
  }, [activeGroupId]);

  React.useEffect(() => {
    try {
      localStorage.setItem(COACH_VIEW_MODE_KEY, coachViewMode);
    } catch {}
  }, [coachViewMode]);

  React.useEffect(() => {
    if (!menuOpen && !notificationsOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (isNotifLoading) return;
      e.preventDefault();
      if (notificationsOpen) {
        setNotificationsOpen(false);
        return;
      }
      if (menuOpen) setMenuOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isNotifLoading, menuOpen, notificationsOpen]);

  React.useEffect(() => {
    if (!token) {
      setWorkGroups([]);
      setActiveGroupId('');
      setCoachChildren([]);
      setCoachViewMode('GROUP');
      return;
    }
    if (isLoading || !me) return;
    if (me.status !== 'ACTIVE') return;
    if (me.role !== 'COACH' && me.role !== 'ADMIN') {
      setWorkGroups([]);
      setActiveGroupId('');
      setCoachChildren([]);
      setCoachViewMode('GROUP');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const list = me.role === 'COACH' ? await apiMyGroups(token) : await apiAdminListGroups(token);
        if (cancelled) return;
        setWorkGroups(list);
        if (me.role === 'COACH' && list.length === 0) {
          setActiveGroupId('');
          setCoachViewMode('INDIVIDUAL');
          return;
        }
        const exists = activeGroupId && list.some((g) => g.id === activeGroupId);
        if (!exists) setActiveGroupId(list[0]?.id ?? '');
      } catch {
        if (cancelled) return;
        setWorkGroups([]);
        setActiveGroupId('');
        if (me.role === 'COACH') setCoachViewMode('INDIVIDUAL');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeGroupId, isLoading, me, token]);

  React.useEffect(() => {
    if (!token) return;
    if (isLoading || !me) return;
    if (me.status !== 'ACTIVE') return;
    if (me.role !== 'COACH') {
      setCoachChildren([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const list = await apiCoachMyChildren(token);
        if (cancelled) return;
        setCoachChildren(list);
      } catch {
        if (cancelled) return;
        setCoachChildren([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [coachViewMode, isLoading, me, token]);

  React.useEffect(() => {
    if (!token) return;
    if (isLoading || !me) return;
    if (me.status !== 'ACTIVE') return;
    if (!tabs.includes(tab)) navigate(tabs[0]);
  }, [isLoading, me, tab, tabs, token]);

  const loadChildren = React.useCallback(async () => {
    if (!token) return;
    if (!me || !isChildScopedRole(me.role) || me.status !== 'ACTIVE') return;
    setIsChildrenLoading(true);
    setChildrenError(null);
    try {
      const list = await apiMyChildren(token);
      setChildren(list);
      if (list.length > 0) {
        if (list.length === 1) {
          if (selectedChildId !== list[0].id) setSelectedChildId(list[0].id);
          return;
        }
        const exists = selectedChildId ? list.some((c) => c.id === selectedChildId) : false;
        if (!exists) setSelectedChildId(null);
      } else {
        setSelectedChildId(null);
      }
    } catch (e) {
      setChildrenError('Nie udało się pobrać listy dzieci.');
    } finally {
      setIsChildrenLoading(false);
    }
  }, [me, selectedChildId, setSelectedChildId, token]);

  React.useEffect(() => {
    loadChildren();
  }, [loadChildren]);

  const loadNotifications = React.useCallback(async () => {
    if (!token) return;
    if (!me || me.status !== 'ACTIVE') return;
    if (isChildScopedRole(me.role) && !selectedChildId) return;
    setIsNotifLoading(true);
    setNotifError(null);
    try {
      const res = await apiNotifications(token, isChildScopedRole(me.role) ? selectedChildId : undefined);

      let importantToRead = 0;
      let pendingMatchRsvp = 0;
      let unreadThreads = 0;
      let pendingApprovals = 0;
      let missingCallups = 0;
      let missingCallupsLevel: 'INFO' | 'CRITICAL' | null = null;
      let missingCallupsMatch: { id: string; title: string; start_at: string } | null = null;
      let tbdMatches = 0;
      let tbdMatch: { id: string; title: string; start_at: string } | null = null;
      let pendingMatch: { id: string; title: string; start_at: string; location: string } | null = null;
      let importantPost: { id: string; title: string; body: string } | null = null;
      let unreadThread: { id: string; title: string; preview: string; unread_count: number } | null = null;
      let pendingApproval: { id: string; email: string } | null = null;

      const isCoachOrAdmin = me.role === 'COACH' || me.role === 'ADMIN';

      for (const it of res.items) {
        if ((it.kind === 'CALLUPS_MISSING_CRITICAL' || it.kind === 'CALLUPS_MISSING_INFO') && isCoachOrAdmin) {
          missingCallups += it.count;
          if (!missingCallupsMatch && it.ref_event_id) {
            missingCallupsLevel = it.kind === 'CALLUPS_MISSING_CRITICAL' ? 'CRITICAL' : 'INFO';
            missingCallupsMatch = {
              id: it.ref_event_id,
              title: (it.preview ?? '').trim() || 'Mecz',
              start_at: (it.subtitle ?? '').trim(),
            };
          }
        }
        if (it.kind === 'MATCH_TBD' && isCoachOrAdmin) {
          tbdMatches += it.count;
          if (!tbdMatch && it.ref_event_id) {
            tbdMatch = {
              id: it.ref_event_id,
              title: (it.preview ?? '').trim() || 'Mecz',
              start_at: (it.subtitle ?? '').trim(),
            };
          }
        }
        if (it.kind === 'MATCH_RSVP') {
          pendingMatchRsvp += it.count;
          if (!pendingMatch && it.ref_event_id) {
            const subtitle = it.subtitle ?? '';
            const parts = subtitle.split(' • ');
            const startAt = (parts[0] ?? '').trim();
            const location = (parts[1] ?? '').trim();
            pendingMatch = {
              id: it.ref_event_id,
              title: (it.preview ?? '').trim() || 'Mecz',
              start_at: startAt,
              location,
            };
          }
        }
        if (it.kind === 'FEED_IMPORTANT') {
          importantToRead += it.count;
          if (!importantPost && it.ref_post_id) {
            const preview = (it.preview ?? '').trim();
            const idx = preview.indexOf(' — ');
            const title = idx >= 0 ? preview.slice(0, idx) : preview;
            const body = idx >= 0 ? preview.slice(idx + 3) : '';
            importantPost = { id: it.ref_post_id, title: title.trim(), body: body.trim() };
          }
        }
        if (it.kind === 'CHAT_UNREAD') {
          unreadThreads += it.count;
          if (!unreadThread && it.ref_thread_id) {
            const preview = (it.preview ?? '').trim();
            const idx = preview.indexOf(' — ');
            const title = idx >= 0 ? preview.slice(0, idx) : preview;
            const msg = idx >= 0 ? preview.slice(idx + 3) : '';
            unreadThread = { id: it.ref_thread_id, title: title.trim() || 'Wiadomości', preview: msg.trim(), unread_count: it.count };
          }
        }
        if (it.kind === 'ADMIN_APPROVAL') {
          pendingApprovals += it.count;
          if (!pendingApproval && it.ref_user_id) {
            pendingApproval = { id: it.ref_user_id, email: (it.preview ?? '').trim() };
          }
        }
      }

      setNotifSummary({
        importantToRead,
        pendingMatchRsvp,
        unreadThreads,
        pendingApprovals,
        missingCallups,
        missingCallupsLevel,
        missingCallupsMatch,
        tbdMatches,
        tbdMatch,
        pendingMatch,
        importantPost,
        unreadThread,
        pendingApproval,
      });
    } catch (e) {
      setNotifError('Nie udało się pobrać powiadomień.');
    } finally {
      setIsNotifLoading(false);
    }
  }, [me, selectedChildId, token]);

  React.useEffect(() => {
    if (!token) return;
    if (!me || me.status !== 'ACTIVE') return;
    let cancelled = false;
    const run = async () => {
      if (cancelled) return;
      await loadNotifications();
    };
    run();
    const id = window.setInterval(run, 30000);
    window.addEventListener('focus', run);
    return () => {
      cancelled = true;
      window.clearInterval(id);
      window.removeEventListener('focus', run);
    };
  }, [loadNotifications, me, token]);

  if (!token) {
    return <LoginScreen onLogin={(t) => setToken(t)} />;
  }

  if (isLoading || !me) {
    return (
      <div className="Page PageCentered">
        <div className="Spinner" aria-label="Ładowanie" />
        <div className="Muted">Ładowanie…</div>
      </div>
    );
  }

  if (me.status === 'PENDING') {
    return <PendingScreen onLogout={handleLogout} />;
  }

  if (isChildScoped) {
    if (isChildrenLoading && children.length === 0) {
      return (
        <div className="Page PageCentered">
          <div className="Spinner" aria-label="Ładowanie" />
          <div className="Muted">Ładowanie…</div>
        </div>
      );
    }
    if (childrenError) {
      return (
        <div className="Page">
          <div className="ErrorBanner">{childrenError}</div>
          <SecondaryButton onClick={loadChildren} type="button">
            Spróbuj ponownie
          </SecondaryButton>
          <SecondaryButton onClick={handleLogout} type="button">
            Wyloguj
          </SecondaryButton>
        </div>
      );
    }
    if (children.length === 0) {
      return (
        <div className="Page">
          <Card title="Brak przypisanego dziecka">
            <div className="Muted">Poproś Admina o przypisanie dziecka do Twojego konta.</div>
          </Card>
          <SecondaryButton onClick={loadChildren} disabled={isChildrenLoading} type="button">
            Odśwież
          </SecondaryButton>
          <SecondaryButton onClick={handleLogout} type="button">
            Wyloguj
          </SecondaryButton>
        </div>
      );
    }
    if (!selectedChildId) {
      return (
        <SelectChildScreen
          token={token}
          children={children}
          onSelect={(id) => setSelectedChildId(id)}
          onReload={loadChildren}
        />
      );
    }
  }

  const selectedChild = isChildScoped ? children.find((c) => c.id === selectedChildId) ?? null : null;
  const topBarDate = formatTopBarDateParts(new Date());
  const notifTotal =
    notifSummary.importantToRead +
    notifSummary.pendingMatchRsvp +
    notifSummary.unreadThreads +
    notifSummary.pendingApprovals +
    notifSummary.missingCallups +
    notifSummary.tbdMatches;
  const notifBadgeText = notifTotal > 99 ? '99+' : notifTotal > 9 ? '9+' : String(notifTotal);

  const notifAcceptMatch = async () => {
    if (!token) return;
    if (!me || !isChildScopedRole(me.role) || me.status !== 'ACTIVE') return;
    if (!selectedChildId) return;
    const m = notifSummary.pendingMatch;
    if (!m) return;
    setIsNotifLoading(true);
    try {
      await apiMatchRsvpRequest(token, m.id, { response: 'ACCEPT' }, selectedChildId);
      await loadNotifications();
      setNotificationsOpen(false);
      navigate('kalendarz', { e: m.id });
    } catch (e) {
    } finally {
      setIsNotifLoading(false);
    }
  };

  const notifConfirmPost = async () => {
    if (!token) return;
    if (!me || me.role !== 'PARENT' || me.status !== 'ACTIVE') return;
    if (!selectedChildId) return;
    const p = notifSummary.importantPost;
    if (!p) return;
    setIsNotifLoading(true);
    try {
      await apiFeedConfirm(token, p.id, selectedChildId);
      await loadNotifications();
    } catch (e) {
    } finally {
      setIsNotifLoading(false);
    }
  };

  return (
    <div className="AppShell">
      <style>
        {`
          .ChatBubbleMention {
            color: #0a66c2;
            font-weight: 600;
            cursor: default;
          }
        `}
      </style>
      <header className="TopBar">
        <div className="TopBarInner">
          <div className="Brand" aria-label="Klub">
            <img className="BrandLogo" src={clubLogo} alt="Błonianka Błonie" />
            <div className="BrandText">
              <div className="BrandTitle">Błonianka Błonie</div>
              <div className="BrandSubtitle">Akademia</div>
            </div>
          </div>
          <div className="TopBarCenter">
            <div className="TopBarMeta" aria-label="Aktualna data">
              <div className="TopBarMetaLine">{topBarDate.line1}</div>
              <div className="TopBarMetaLine">{topBarDate.line2}</div>
            </div>
          </div>
          <div className="TopBarIconWrap">
            <button
              className={['TopBarIconButton', notifTotal > 0 ? 'is-active' : ''].filter(Boolean).join(' ')}
              type="button"
              aria-label={notifTotal > 0 ? `Powiadomienia (${notifTotal})` : 'Powiadomienia'}
              onClick={() => {
                setNotificationsOpen(true);
                loadNotifications();
              }}
            >
              <BellIcon />
              {notifTotal > 0 && <span className="TopBarDot">{notifBadgeText}</span>}
            </button>
            <button className="TopBarIconButton" type="button" aria-label="Menu" onClick={() => setMenuOpen(true)}>
              <MenuIcon />
            </button>
          </div>
        </div>
      </header>
      <main className="Content" role="main">
        {tab === 'feed' && (
          <FeedPage
            me={me}
            token={token}
            selectedChild={selectedChild}
            workGroups={workGroups}
            activeGroupId={activeGroupId}
            onChangeActiveGroupId={setActiveGroupId}
          />
        )}
        {tab === 'kalendarz' && (
          <KalendarzPage
            me={me}
            role={me.role}
            token={token}
            childId={selectedChildId}
            parentChildren={me.role === 'PARENT' ? children : undefined}
            workGroups={workGroups}
            activeGroupId={activeGroupId}
            coachViewMode={coachViewMode}
            coachChildren={coachChildren}
          />
        )}
        {tab === 'druzyna' && tabs.includes('druzyna') && (
          <>
            {me.role === 'COACH' && coachViewMode === 'INDIVIDUAL' ? (
              <DruzynaPage
                me={me}
                token={token}
                selectedChild={selectedChild}
                onReloadChildren={loadChildren}
                coachChildrenOverride={coachChildren}
                onSetCoachChildrenOverride={(next) => {
                  setCoachChildren(next);
                  if (next.length === 0) setCoachViewMode('GROUP');
                }}
              />
            ) : (
              <DruzynaPage me={me} token={token} selectedChild={selectedChild} onReloadChildren={loadChildren} />
            )}
          </>
        )}
        {tab === 'wiadomosci' && <WiadomosciPage me={me} token={token} selectedChildId={selectedChildId} onRefreshNotifications={loadNotifications} />}
        {tab === 'czat' && <CzatPage me={me} token={token} selectedChild={selectedChild} />}
        {tab === 'profil' && (
          <ProfilPage
            me={me}
            token={token}
            selectedChild={selectedChild}
            children={children}
            selectedChildId={selectedChildId}
            onSelectChildId={(id) => setSelectedChildId(id)}
            workGroups={workGroups}
            activeGroupId={activeGroupId}
            onChangeActiveGroupId={setActiveGroupId}
            coachViewMode={coachViewMode}
            onChangeCoachViewMode={setCoachViewMode}
            coachChildren={coachChildren}
            onChangeChild={() => setSelectedChildId(null)}
            onLogout={handleLogout}
            onRefreshMe={refreshMe}
            onSwitchRole={async (nextRole) => {
              if (!token) return;
              if (nextRole === me.role) return;
              setIsRoleSwitchLoading(true);
              try {
                const nextMe = await apiAuthSwitchRole(token, nextRole);
                setMeExternal(nextMe);
              } catch (e) {
                window.alert(humanizeHttpError(e, 'Nie udało się przełączyć profilu.'));
              } finally {
                setIsRoleSwitchLoading(false);
              }
            }}
            isRoleSwitchLoading={isRoleSwitchLoading}
          />
        )}
      </main>
      {reloginMessage && !(() => {
        try {
          const { path } = getHashParts();
          return path === 'login';
        } catch {
          return false;
        }
      })() && (
        <div className="AutoReloginOverlay" role="dialog" aria-modal="true" aria-label="Aktualizacja aplikacji">
          <div className="AutoReloginCard" role="document">
            <div className="AutoReloginIcon" aria-hidden="true">
              <span>🔄</span>
            </div>
            <div className="AutoReloginTitle">AKTUALIZACJA APLIKACJI</div>
            <div className="AutoReloginBody">{reloginMessage}</div>
            <div className="AutoReloginHint">Za chwilę nastąpi przekierowanie do ekranu logowania…</div>
            <div className="AutoReloginSpinner" aria-hidden="true" />
          </div>
        </div>
      )}
      {rodoMandatory && (
        <div className="RodoMandatoryOverlay" role="dialog" aria-modal="true" aria-labelledby="rodoMandatoryTitle">
          <div className="RodoMandatoryCard">
            <div className="RodoMandatoryHeader">
              <div id="rodoMandatoryTitle" className="RodoMandatoryTitle">Wymagana akceptacja regulaminu RODO</div>
              <div className="RodoMandatorySubtitle">Wersja {rodoMandatory.policy.version} z {rodoMandatory.policy.published_at}</div>
            </div>
            {rodoMandatoryLoading ? (
              <div className="RodoMandatoryBody">
                <div className="Muted">Ładowanie treści regulaminu…</div>
              </div>
            ) : (
              <div className="RodoMandatoryBody">
                {rodoMandatoryError && <div className="ErrorBanner">{rodoMandatoryError}</div>}
                <div className="RodoMandatoryIntro">
                  Przed pierwszym użyciem aplikacji wymagane jest zapoznanie się i akceptacja regulaminu przetwarzania danych osobowych (RODO).
                  Możesz go przeczytać w całości poniżej lub w każdej chwili ponownie otworzyć w swoim profilu.
                </div>
                <div className="RodoMandatoryPolicyScroll">
                  {rodoMandatory.policy.sections.map((s) => (
                    <div key={s.title} className="RodoMandatorySection">
                      <div className="RodoMandatorySectionTitle">{s.title}</div>
                      <div className="RodoMandatorySectionBody">{s.body}</div>
                    </div>
                  ))}
                </div>
                <label className="RodoMandatoryAccept">
                  <input
                    type="checkbox"
                    checked={rodoMandatoryChecked}
                    onChange={(e) => setRodoMandatoryChecked(e.target.checked)}
                  />
                  <span>
                    Oświadczam, że zapoznałem(am) się z treścią regulaminu RODO w wersji {rodoMandatory.policy.version}
                    i akceptuję jego warunki.
                  </span>
                </label>
              </div>
            )}
            <div className="RodoMandatoryFooter">
              <SecondaryButton
                type="button"
                onClick={handleLogout}
                disabled={rodoMandatoryAccepting}
              >
                Wyloguj się
              </SecondaryButton>
              <PrimaryButton
                type="button"
                onClick={acceptRodoMandatory}
                disabled={rodoMandatoryLoading || rodoMandatoryAccepting || !rodoMandatoryChecked}
              >
                {rodoMandatoryAccepting ? 'Zapisywanie akceptacji…' : 'Akceptuję regulamin i przechodzę do aplikacji'}
              </PrimaryButton>
            </div>
          </div>
        </div>
      )}
      <BottomNav
        tab={tab}
        tabs={bottomTabs}
        unreadThreads={notifSummary.unreadThreads}
        labelPartsByTab={
          me.role === 'COACH' && coachViewMode === 'INDIVIDUAL' ? { druzyna: ['PODO', 'PIECZNI'] } : undefined
        }
        onChange={(t) => navigate(t)}
      />
      {menuOpen && (
        <div
          className="SheetOverlay"
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setMenuOpen(false);
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setMenuOpen(false);
          }}
        >
          <div className="Sheet">
            <div className="SheetHeaderRow SheetHeaderRow--sticky">
              <div className="SheetTitle">MENU</div>
              <button className="SheetCloseButton" type="button" aria-label="Zamknij" onClick={() => setMenuOpen(false)}>
                ×
              </button>
            </div>
            {(me.roles?.length ?? 0) > 1 && (
              <>
                <div className="Muted">Tryb konta</div>
                <select
                  className="TextInput"
                  value={me.role}
                  onChange={async (e) => {
                    if (!token) return;
                    const nextRole = (e.target.value as Role) || me.role;
                    if (nextRole === me.role) return;
                    setIsRoleSwitchLoading(true);
                    try {
                      const nextMe = await apiAuthSwitchRole(token, nextRole);
                      setMeExternal(nextMe);
                      setMenuOpen(false);
                    } catch {}
                    setIsRoleSwitchLoading(false);
                  }}
                  disabled={isRoleSwitchLoading}
                  aria-label="Tryb konta"
                >
                  {(me.roles ?? []).map((r) => (
                    <option key={r} value={r}>
                      {roleLabelPl(r)}
                    </option>
                  ))}
                </select>
              </>
            )}
            {(me.role === 'COACH' || me.role === 'ADMIN') && (
              <>
                <div className="Muted">Grupa robocza</div>
                {workGroups.length === 0 ? (
                  <div className="Muted">Brak grup.</div>
                ) : (
                  <select className="TextInput" value={activeGroupId} onChange={(e) => setActiveGroupId(e.target.value)}>
                    {workGroups.map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.name}
                        {g.year ? ` (${g.year})` : ''}
                      </option>
                    ))}
                  </select>
                )}
                {me.role === 'COACH' && (
                  <>
                    <div className="Muted">Tryb trenera</div>
                    <select
                      className="TextInput"
                      value={coachViewMode}
                      onChange={(e) => setCoachViewMode((e.target.value as CoachViewMode) || 'GROUP')}
                    >
                      <option value="GROUP">Grupowy</option>
                      <option value="INDIVIDUAL">Indywidualny</option>
                    </select>
                  </>
                )}
              </>
            )}
            <PrimaryButton
              onClick={() => {
                setMenuOpen(false);
                navigate('profil');
              }}
              type="button"
            >
              Profil / Panel
            </PrimaryButton>
            {me.role === 'PARENT' && (
              <SecondaryButton
                onClick={() => {
                  setMenuOpen(false);
                  setSelectedChildId(null);
                }}
                type="button"
              >
                Zmień dziecko
              </SecondaryButton>
            )}
            <SecondaryButton
              onClick={() => {
                setMenuOpen(false);
                void handleLogout();
              }}
              type="button"
            >
              Wyloguj
            </SecondaryButton>
          </div>
        </div>
      )}
      {notificationsOpen && (
        <div
          className="SheetOverlay"
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => {
            if (isNotifLoading) return;
            if (e.target === e.currentTarget) setNotificationsOpen(false);
          }}
        >
          <div className="Sheet">
            <div className="SheetHeaderRow SheetHeaderRow--sticky">
              <div className="SheetTitle">POWIADOMIENIA</div>
              <button
                className="SheetCloseButton"
                type="button"
                aria-label="Zamknij"
                onClick={() => setNotificationsOpen(false)}
                disabled={isNotifLoading}
              >
                ×
              </button>
            </div>
            {notifError && <div className="ErrorBanner">{notifError}</div>}
            {notifTotal === 0 ? (
              <div className="Muted">{isNotifLoading ? 'Ładowanie…' : 'Brak nowych powiadomień.'}</div>
            ) : (
              <div className="List">
                {(me.role === 'COACH' || me.role === 'ADMIN') && notifSummary.tbdMatches > 0 && notifSummary.tbdMatch && (
                  <div
                    className="ListItem NotificationItem"
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      setNotificationsOpen(false);
                      navigate('kalendarz', { e: notifSummary.tbdMatch?.id });
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        setNotificationsOpen(false);
                        navigate('kalendarz', { e: notifSummary.tbdMatch?.id });
                      }
                    }}
                  >
                    <div className="ListItemTop">
                      <div className="ListItemTitle">Termin do ustalenia (2 dni)</div>
                      <div className="ListItemBadge">{notifSummary.tbdMatches}</div>
                    </div>
                    <div className="NotificationPreview">
                      <div className="NotificationPreviewTitle">{notifSummary.tbdMatch.title}</div>
                      <div className="NotificationPreviewMeta">{formatDateTime(notifSummary.tbdMatch.start_at)}</div>
                    </div>
                    <div className="NotificationActions">
                      <SecondaryButton
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setNotificationsOpen(false);
                          navigate('kalendarz', { e: notifSummary.tbdMatch?.id });
                        }}
                        disabled={isNotifLoading}
                        type="button"
                      >
                        Otwórz
                      </SecondaryButton>
                    </div>
                  </div>
                )}
                {me.role === 'COACH' && notifSummary.missingCallups > 0 && notifSummary.missingCallupsMatch && (
                  <div
                    className="ListItem NotificationItem"
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      setNotificationsOpen(false);
                      navigate('kalendarz', { e: notifSummary.missingCallupsMatch?.id });
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        setNotificationsOpen(false);
                        navigate('kalendarz', { e: notifSummary.missingCallupsMatch?.id });
                      }
                    }}
                  >
                    <div className="ListItemTop">
                      <div className="ListItemTitle">
                        {notifSummary.missingCallupsLevel === 'CRITICAL' ? 'Brak powołań (1 dzień)' : 'Brak powołań (2 dni)'}
                      </div>
                      <div className="ListItemBadge">{notifSummary.missingCallups}</div>
                    </div>
                    <div className="NotificationPreview">
                      <div className="NotificationPreviewTitle">{notifSummary.missingCallupsMatch.title}</div>
                      <div className="NotificationPreviewMeta">{formatDateTime(notifSummary.missingCallupsMatch.start_at)}</div>
                    </div>
                    <div className="NotificationActions">
                      <SecondaryButton
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setNotificationsOpen(false);
                          navigate('kalendarz', { e: notifSummary.missingCallupsMatch?.id });
                        }}
                        disabled={isNotifLoading}
                        type="button"
                      >
                        Otwórz
                      </SecondaryButton>
                    </div>
                  </div>
                )}
                {isChildScoped && notifSummary.pendingMatchRsvp > 0 && notifSummary.pendingMatch && (
                  <div
                    className="ListItem NotificationItem"
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      setNotificationsOpen(false);
                      navigate('kalendarz', { e: notifSummary.pendingMatch?.id });
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        setNotificationsOpen(false);
                        navigate('kalendarz', { e: notifSummary.pendingMatch?.id });
                      }
                    }}
                  >
                    <div className="ListItemTop">
                      <div className="ListItemTitle">Mecz do potwierdzenia</div>
                      <div className="ListItemBadge">{notifSummary.pendingMatchRsvp}</div>
                    </div>
                    <div className="NotificationPreview">
                      <div className="NotificationPreviewTitle">{notifSummary.pendingMatch.title}</div>
                      <div className="NotificationPreviewMeta">
                        {formatDateTime(notifSummary.pendingMatch.start_at)} • {notifSummary.pendingMatch.location}
                      </div>
                    </div>
                    <div className="NotificationActions">
                      <PrimaryButton
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          notifAcceptMatch();
                        }}
                        disabled={isNotifLoading}
                        type="button"
                      >
                        Potwierdź
                      </PrimaryButton>
                      <SecondaryButton
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setNotificationsOpen(false);
                          navigate('kalendarz', { e: notifSummary.pendingMatch?.id });
                        }}
                        disabled={isNotifLoading}
                        type="button"
                      >
                        Otwórz
                      </SecondaryButton>
                    </div>
                  </div>
                )}

                {me.role === 'PARENT' && notifSummary.importantToRead > 0 && notifSummary.importantPost && (
                  <div
                    className="ListItem NotificationItem"
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      setNotificationsOpen(false);
                      navigate('feed', { p: notifSummary.importantPost?.id });
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        setNotificationsOpen(false);
                        navigate('feed', { p: notifSummary.importantPost?.id });
                      }
                    }}
                  >
                    <div className="ListItemTop">
                      <div className="ListItemTitle">Ważne do przeczytania</div>
                      <div className="ListItemBadge">{notifSummary.importantToRead}</div>
                    </div>
                    <div className="NotificationPreview">
                      <div className="NotificationPreviewTitle">{notifSummary.importantPost.title}</div>
                      <div className="NotificationPreviewMeta">{notifSummary.importantPost.body}</div>
                    </div>
                    <div className="NotificationActions">
                      <PrimaryButton
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          notifConfirmPost();
                        }}
                        disabled={isNotifLoading}
                        type="button"
                      >
                        Przeczytałem
                      </PrimaryButton>
                      <SecondaryButton
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setNotificationsOpen(false);
                          navigate('feed', { p: notifSummary.importantPost?.id });
                        }}
                        disabled={isNotifLoading}
                        type="button"
                      >
                        Otwórz
                      </SecondaryButton>
                    </div>
                  </div>
                )}

                {notifSummary.unreadThreads > 0 && notifSummary.unreadThread && (
                  <div
                    className="ListItem NotificationItem"
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      setNotificationsOpen(false);
                      navigate('wiadomosci', { t: notifSummary.unreadThread?.id });
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        setNotificationsOpen(false);
                        navigate('wiadomosci', { t: notifSummary.unreadThread?.id });
                      }
                    }}
                  >
                    <div className="ListItemTop">
                      <div className="ListItemTitle">Nowa wiadomość</div>
                      <div className="ListItemBadge">{notifSummary.unreadThreads}</div>
                    </div>
                    <div className="NotificationPreview">
                      <div className="NotificationPreviewTitle">{notifSummary.unreadThread.title}</div>
                      <div className="NotificationPreviewMeta">{notifSummary.unreadThread.preview}</div>
                    </div>
                    <div className="NotificationActions">
                      <PrimaryButton
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setNotificationsOpen(false);
                          navigate('wiadomosci', { t: notifSummary.unreadThread?.id });
                        }}
                        disabled={isNotifLoading}
                        type="button"
                      >
                        Otwórz
                      </PrimaryButton>
                    </div>
                  </div>
                )}

                {(me.role === 'ADMIN' || me.role === 'COORDINATOR') && notifSummary.pendingApprovals > 0 && (
                  <div
                    className="ListItem NotificationItem"
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      setNotificationsOpen(false);
                      navigate('profil');
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        setNotificationsOpen(false);
                        navigate('profil');
                      }
                    }}
                  >
                    <div className="ListItemTop">
                      <div className="ListItemTitle">Konta do akceptacji</div>
                      <div className="ListItemBadge">{notifSummary.pendingApprovals}</div>
                    </div>
                    {notifSummary.pendingApproval && (
                      <div className="NotificationPreview">
                        <div className="NotificationPreviewTitle">{notifSummary.pendingApproval.email}</div>
                        <div className="NotificationPreviewMeta">Kliknij, aby przejść do panelu</div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            <PrimaryButton onClick={loadNotifications} disabled={isNotifLoading} type="button">
              Odśwież
            </PrimaryButton>
            <SecondaryButton onClick={() => setNotificationsOpen(false)} type="button">
              Zamknij
            </SecondaryButton>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
